// ----- js/core/dom-utils.js -----
// 通用 DOM / 字符串小工具。
//
// escapeHtml 原先定义在 tools/low-fan.js 里。web/js/** 是 classic script，靠全局裸名互相
// 引用（构建期由 vite-plugin-load-order-bundle.mjs 按 load-order.json 顺序拼成一个 chunk），
// 顶层 function 声明会成为全局属性，所以"定义在业务模块里"平时也能用。但它实际被 16 个文件
// 调用了 460 次（viral/library.js、auth/session.js、rewrite/drafts.js 是调用大户）——任何一次
// 「精简低粉模块」或调整加载顺序，都会让登录、账单、知识库整片 ReferenceError 白屏。
//
// 迁到 core 并排到清单最前面：本模块不依赖任何东西，只被依赖。改这里只需保证输出与原来
// 逐字一致（转义 & < > " 四个字符）。
//
// 注意：这里只放"全站通用"的函数。只有一个业务模块自己用的私有 helper 别搬过来。

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ----- js/core/select-menu.js -----
// Keep native selects in place for labels, form submission, and programmatic value updates.
// Only their picker is replaced, including selects rendered after page load.
function studioSelectMenuBounds(rect, viewport, contentHeight, alignment = "start") {
  const edge = 12;
  const gap = 6;
  const leftEdge = viewport.left + edge;
  const topEdge = viewport.top + edge;
  const rightEdge = viewport.left + viewport.width - edge;
  const bottomEdge = viewport.top + viewport.height - edge;
  const width = Math.min(Math.max(rect.width, 200), Math.max(0, viewport.width - edge * 2));
  const below = Math.max(0, bottomEdge - rect.bottom - gap);
  const above = Math.max(0, rect.top - gap - topEdge);
  const upward = below < Math.min(contentHeight, 180) && above > below;
  const height = Math.min(contentHeight, 280, upward ? above : below, Math.max(0, viewport.height - edge * 2));
  const preferredLeft = alignment === "center" ? rect.left + (rect.width - width) / 2 : rect.left;
  return {
    width,
    height,
    left: Math.max(leftEdge, Math.min(preferredLeft, rightEdge - width)),
    top: Math.max(topEdge, Math.min(upward ? rect.top - gap - height : rect.bottom + gap, bottomEdge - height)),
  };
}

function bindStudioSelectMenus() {
  if (typeof HTMLElement.prototype.showPopover !== "function") return;
  const menu = document.createElement("div");
  menu.id = "studioSelectMenu";
  menu.className = "studio-select-menu";
  menu.setAttribute("role", "listbox");
  menu.setAttribute("popover", "manual");
  document.body.append(menu);
  document.documentElement.classList.add("studio-selects-enabled");
  let activeSelect = null;
  let search = "";
  let searchTime = 0;
  const selectFrom = (target) => target instanceof HTMLSelectElement && !target.multiple && target.size <= 1 ? target : null;
  const close = (restoreFocus = false) => {
    const select = activeSelect;
    activeSelect = null;
    observer.disconnect();
    menu.hidePopover();
    if (select) select.setAttribute("aria-expanded", "false");
    if (restoreFocus && select?.isConnected) select.focus({ preventScroll: true });
  };
  const observer = new MutationObserver((records) => {
    if (!activeSelect) return;
    if (!activeSelect.isConnected || activeSelect.matches(":disabled") || !activeSelect.getClientRects().length
      || records.some((record) => record.target === activeSelect || activeSelect.contains(record.target))) close();
  });
  const enabledOptions = () => [...menu.querySelectorAll("[role=option]:not(:disabled)")];
  const focusMatch = (key) => {
    const now = Date.now();
    search = now - searchTime < 700 ? search + key : key;
    searchTime = now;
    const options = enabledOptions();
    const start = options.indexOf(document.activeElement) + 1;
    const ordered = [...options.slice(start), ...options.slice(0, start)];
    const match = ordered.find((option) => option.textContent.trim().toLocaleLowerCase().startsWith(search.toLocaleLowerCase()));
    match?.focus();
  };
  const open = (select) => {
    if (select.matches(":disabled") || !select.options.length) return;
    if (activeSelect === select) return;
    close();
    activeSelect = select;
    search = "";
    select.focus({ preventScroll: true });
    select.setAttribute("aria-controls", menu.id);
    select.setAttribute("aria-expanded", "true");
    const label = select.getAttribute("aria-label") || select.labels?.[0]?.textContent.replace(select.textContent, "").trim() || select.title || "选择选项";
    menu.setAttribute("aria-label", label);
    menu.replaceChildren();
    [...select.options].forEach((option, index) => {
      if (option.hidden) return;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "studio-select-option";
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(option.selected));
      button.disabled = option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled);
      button.tabIndex = -1;
      button.textContent = option.label;
      button.addEventListener("click", () => {
        const changed = select.selectedIndex !== index;
        select.selectedIndex = index;
        close(true);
        if (changed) {
          select.dispatchEvent(new Event("input", { bubbles: true }));
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
      menu.append(button);
    });
    const visual = window.visualViewport;
    const viewport = {
      left: visual?.offsetLeft || 0,
      top: visual?.offsetTop || 0,
      width: visual?.width || window.innerWidth,
      height: visual?.height || window.innerHeight,
    };
    const rect = select.getBoundingClientRect();
    const alignment = select.id === "authOrganizationSelect" ? "center" : "start";
    menu.style.width = `${studioSelectMenuBounds(rect, viewport, 280, alignment).width}px`;
    menu.style.maxHeight = "none";
    menu.showPopover();
    const bounds = studioSelectMenuBounds(rect, viewport, menu.getBoundingClientRect().height, alignment);
    menu.style.left = `${bounds.left}px`;
    menu.style.top = `${bounds.top}px`;
    menu.style.maxHeight = `${bounds.height}px`;
    const selected = menu.querySelector('[aria-selected="true"]:not(:disabled)');
    (selected || enabledOptions()[0])?.focus({ preventScroll: true });
    if (selected) menu.scrollTop = Math.max(0, selected.offsetTop - menu.clientHeight / 2);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "hidden", "disabled"] });
  };
  // Cancel the native picker before its pointer/mouse default action, also on touch devices.
  document.addEventListener("pointerdown", (event) => {
    const select = selectFrom(event.target);
    if (select && event.button === 0) {
      event.preventDefault();
      if (activeSelect === select) close(true);
      else open(select);
    } else if (!menu.contains(event.target)) close();
  }, true);
  document.addEventListener("mousedown", (event) => {
    if (selectFrom(event.target) && event.button === 0) event.preventDefault();
  }, true);
  document.addEventListener("click", (event) => {
    const select = selectFrom(event.target);
    if (!select) return;
    event.preventDefault();
    // Labels and assistive technology can activate a field without pointer events.
    if (event.detail === 0) open(select);
  }, true);
  document.addEventListener("keydown", (event) => {
    const select = selectFrom(event.target);
    const inMenu = menu.contains(event.target);
    if (!select && !inMenu) return;
    if (event.key === "Tab") {
      if (activeSelect) close(true);
      return;
    }
    if (event.key === "Escape") {
      if (activeSelect) {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
      return;
    }
    if (event.ctrlKey || event.metaKey) return;
    if (select && ["Enter", " ", "ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      open(select);
      const options = enabledOptions();
      if (event.key === "Home") options[0]?.focus();
      if (event.key === "End") options.at(-1)?.focus();
    } else if (inMenu && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const options = enabledOptions();
      const index = options.indexOf(document.activeElement);
      const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
      options[next]?.focus();
    } else if (event.key.length === 1 && event.key !== " " && !event.altKey) {
      event.preventDefault();
      if (select) open(select);
      focusMatch(event.key);
    }
  }, true);
  document.addEventListener("scroll", (event) => {
    if (!menu.contains(event.target)) close();
  }, true);
  document.addEventListener("focusin", (event) => {
    if (activeSelect && event.target !== activeSelect && !menu.contains(event.target)) close();
  });
  window.addEventListener("resize", () => close());
  window.visualViewport?.addEventListener("resize", () => close());
  window.visualViewport?.addEventListener("scroll", () => close());
}

bindStudioSelectMenus();

// ----- js/core/api.js -----
const storageKey = "xhs-ai-workstation-v1";
const stateRecoveryStorageKey = "xhs-ai-workstation-recovery-v1";
const STATE_STORAGE_VERSION = 2;
const aiConfigStorageKey = "xhs-ai-config-v1";

// Workspace caches belong to one tenant. Two organizations opened in the same
// browser must never share drafts, publish entries or the cached AI config, so
// every key is resolved through the active organization instead of a constant.
function activeTenantStorageScope() {
  try {
    return String(globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "")
      .replace(/[^A-Za-z0-9_-]/g, "");
  } catch (_error) {
    return "";
  }
}

// 解析出「这次读写到底属于哪个租户的桶」。登录落定前 activeTenantStorageScope() 是空的，
// 此时用本浏览器上第一次登录的那个组织（::claimed 标记）——但这是个**兜底猜测**，不是事实。
// 需要判断"某段数据属于哪个租户"时必须拿它来比对（见 state.js 的恢复快照租户校验）：
// 只按当前活跃租户反推，就会让上一个组织留下的数据通过校验混进当前组织（P97）。
function resolvedTenantStorageScope(baseKey) {
  const scope = activeTenantStorageScope();
  if (scope) return scope;
  try {
    return String(localStorage.getItem(`${baseKey}::claimed`) || "").replace(/[^A-Za-z0-9_-]/g, "");
  } catch (_error) {
    return "";
  }
}

function tenantStorageKey(baseKey) {
  const scope = resolvedTenantStorageScope(baseKey);
  if (scope) return `${baseKey}::${scope}`;
  return baseKey;
}

function claimTenantStorage(baseKey, scope) {
  // One-time handover of the pre-tenant shared bucket to the first organization
  // that logs in here. Later organizations start empty instead of inheriting it.
  if (!scope) return false;
  try {
    const marker = `${baseKey}::claimed`;
    if (localStorage.getItem(marker)) return false;
    const scopedKey = `${baseKey}::${scope}`;
    const legacy = localStorage.getItem(baseKey);
    if (legacy && !localStorage.getItem(scopedKey)) localStorage.setItem(scopedKey, legacy);
    localStorage.setItem(marker, scope);
    return Boolean(legacy);
  } catch (_error) {
    return false;
  }
}
const backendBaseUrl = window.location.protocol.startsWith("http") ? window.location.origin : "http://127.0.0.1:8000";
// `adminMountUrl`（= `${backendBaseUrl}/admin`）曾在这里：SPA 早期用 iframe 内嵌 starlette-admin
// 时构造它的地址，拆掉 iframe 之后就成了全仓零引用的死常量（`/admin` 现在没有任何 UI 入口，
// 只有 session.js 的治理台权限探针还会碰那个 cookie）。已删。
// 单账号同时提交的生图页数上限。它是"一个账号能吃掉几个 heavy worker"的**唯一**旋钮：
// 服务端没有每账号在途上限（见 tests/test_ai_image_tenant_concurrency.py），而生产 heavy 池
// 只有 4 个 worker。
// 取 1 = 任一账号最多占 1/4，团队最公平：4 个账号可以完全无排队地同时出图，第 5 个才开始等。
// 代价是单人最慢：15 页从「2 并发约 8.1 分钟」变成「约 16 分钟」。每页本身耗时、排队方式、
// 以及「15 分钟/页」的客户端死线都不变，只是页面一张张陆续出现。
// 取 2 = 单人快一倍、但一个账号占掉一半；取 4 等于允许一个人占满整池。
const IMAGE_GENERATION_CONCURRENCY = 1;
const IMAGE_PAGE_TIMEOUT_RETRY_DELAYS = [2000, 5000];
const MAX_IMAGE_INPUTS = 16;
// 与后端 XHS_MAX_REQUEST_BYTES 同源：整包 JSON 请求体的硬上限。超限时服务端在读到
// 请求体前就断开连接，前端只能拿到一句"连不上后端"，所以要在发送前自己预检。
const MAX_REQUEST_BYTES = 25 * 1024 * 1024;
const MAX_LOCAL_REFERENCE_FILES = 20;
const MAX_CLOUD_REFERENCE_FILES = 1;
const MAX_REVERSE_TOPIC_FILES = 1;
const MAX_LOCAL_REFERENCE_FILE_BYTES = 100 * 1024 * 1024;
const EXECUTION_BUNDLE_VERSION = 6;
const EXECUTION_BUNDLE_TYPE = "ExecutionBundleV6";
const PIPELINE_CONTRACT = "knowledge-template-v2";
const LOCAL_FILE_SUFFIX_RE = /\.(pdf|docx|txt|md|markdown)$/i;

// 未迁移能力：后端把它作为机器可读清单（GET /api/capabilities）下发，前端按
// data-requires-capability 隐藏对应入口。取不到清单时一律按"可用"处理（fail-open）——
// 服务端本来就会拒绝，不能因为一次网络抖动把功能藏起来。
const CAPABILITIES_KEY = "__xhsCapabilities";
let capabilitiesPromise = null;

function capabilityEnabled(name) {
  const snapshot = globalThis[CAPABILITIES_KEY];
  if (!snapshot || typeof snapshot !== "object") return true;
  const entry = snapshot[name];
  if (!entry || typeof entry !== "object") return true;
  return entry.available !== false;
}

function applyCapabilityVisibility(root) {
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;
  scope.querySelectorAll("[data-requires-capability]").forEach((node) => {
    const declared = String(node.getAttribute("data-requires-capability") || "").trim();
    if (!declared) return;
    // 逗号分隔表示"这些能力都要可用"，任一不可用就隐藏该入口。
    const available = declared
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
      .every((name) => capabilityEnabled(name));
    node.classList.toggle("hidden", !available);
  });
}

async function loadCapabilities(force = false) {
  if (capabilitiesPromise && !force) return capabilitiesPromise;
  capabilitiesPromise = (async () => {
    try {
      const response = await backendFetch("/api/capabilities");
      if (!response.ok) return null;
      const data = await response.json();
      if (data && data.capabilities && typeof data.capabilities === "object") {
        globalThis[CAPABILITIES_KEY] = data.capabilities;
        applyCapabilityVisibility();
        return data.capabilities;
      }
      return null;
    } catch (_error) {
      return null;
    }
  })();
  return capabilitiesPromise;
}

// 插件包版本只有一个来源：后端读 extension/manifest.json，同一目录就是
// /extension-download.zip 打包的内容。首页不再手写版本号，避免出现"页面写
// 0.1.11、下载到的包是 0.1.12"。取不到时保留 HTML 里的静态兜底，不显示空值。
const EXTENSION_PACKAGE_KEY = "__xhsExtensionPackage";
let extensionPackagePromise = null;

function extensionPackageVersion() {
  const info = globalThis[EXTENSION_PACKAGE_KEY];
  if (!info || typeof info !== "object") return "";
  return String(info.version || "").trim();
}

function applyExtensionPackageVersion(root) {
  const version = extensionPackageVersion();
  if (!version) return;
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;
  scope.querySelectorAll("[data-extension-version]").forEach((node) => {
    node.textContent = version;
  });
}

async function loadExtensionPackage(force = false) {
  if (extensionPackagePromise && !force) return extensionPackagePromise;
  extensionPackagePromise = (async () => {
    try {
      const response = await backendFetch("/api/extension/package");
      if (!response.ok) return null;
      const data = await response.json();
      const info = data?.package;
      if (!info || typeof info !== "object") return null;
      globalThis[EXTENSION_PACKAGE_KEY] = info;
      applyExtensionPackageVersion();
      // 服务端版本填好后再探测本机版本才有比对意义（实现见 drafts.js 的桥接探测）。
      if (typeof refreshLocalExtensionStatus === "function") void refreshLocalExtensionStatus();
      return info;
    } catch (_error) {
      // 版本号取不到时保持静态兜底：读不到不该拖垮首页，也不该显示空值。
      return null;
    }
  })();
  return extensionPackagePromise;
}

// 认证就绪的等待上限：只防"启动期那次令牌交换永久挂起把整页点死"，不改变正常时序。
const AUTH_READY_WAIT_MS = 6000;

async function backendFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (globalThis.xhsSpaAuthReady) {
    // 启动期的认证链里有真实的令牌交换。后端或 Logto 恰好在那一刻重启时，那一次 fetch 会一直
    // 挂着，而这个等待原本没有上限：所有请求都卡在这一句上，界面表现是"点什么都没反应"
    // （例如「AI 配置」按钮点了不弹层——它的第一步就是等一个 /api/config）。给一个上限，
    // 超时就按"没有令牌"继续：服务端会回它真实的 401/403，用户至少看得到状态，而不是干等。
    await Promise.race([
      Promise.resolve(globalThis.xhsSpaAuthReady).catch(() => {}),
      new Promise((resolve) => { globalThis.setTimeout(resolve, AUTH_READY_WAIT_MS); }),
    ]);
  }
  let token = "";
  try {
    token = await globalThis.xhsSpaAuth?.getAccessToken?.() || "";
  } catch (_error) {
    // Unauthenticated shell requests continue to the server and receive its
    // normal 401/403 projection; no token or error payload is persisted.
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  // Bearer auth is the default credential for the shell; the admin session is
  // the one flow that also needs its Set-Cookie response to be stored, so it
  // opts into "same-origin" explicitly.
  const response = await fetch(`${backendBaseUrl}${path}`, {
    ...options,
    headers,
    credentials: options.credentials || "omit",
  });
  // 401 有两种，调用方必须能分开：
  //   ①服务端拒了我们带上去的令牌 —— 会话真的失效了；
  //   ②这次请求压根没带令牌 —— 启动早期取令牌失败、或登录回调还在换令牌时就会这样。
  // 只有①能当"会话失效"去清本地会话：②一旦也清，就会把同一份 sessionStorage 里正在换
  // 令牌的登录会话（signInSession/PKCE）一起删掉，用户看到的是"登录成功后立刻被判未登录"。
  try {
    Object.defineProperty(response, "xhsAuthAttached", { value: Boolean(token), configurable: true });
    // 令牌有没有组织作用域同样要带出去：登录回调刚换完令牌、组织还没激活时，凭据是"没组织
    // 的令牌"，后端一定回 401。那种 401 同样不能当作"会话被撤销"。
    Object.defineProperty(response, "xhsAuthOrganization", {
      value: String(globalThis.xhsSpaAuth?.getAccessTokenOrganizationId?.() || ""),
      configurable: true,
    });
    // 这次请求是什么方法，错误边界要靠它分辨"用户发起的一次动作"与"页面自己的被动读"
    // （见 core/client.js 的 handleUnauthenticatedBackendAction）：两者都可能是 401，
    // 但只有前者该把登录入口递到用户手上。
    Object.defineProperty(response, "xhsRequestMethod", {
      value: String(options.method || "GET").toUpperCase(),
      configurable: true,
    });
  } catch (_error) {
    // Response 不可扩展时退化为旧行为（宁可沿用旧的严格处置，也不要静默改成宽松）。
  }
  return response;
}

let lastLifecycleStateFlushAt = 0;


// ----- js/core/extension-status.js -----
// 浏览器插件（扩展）存在性检测的唯一来源。
//
// 为什么要有这一个模块：工作站里"抓取笔记、低粉采集、发布"三件事都得由浏览器里的
// 「薯作台小红书采集助手」扩展落地，而"这台浏览器装没装、这一页挂着的插件上下文还活着吗"
// 只能靠一次 postMessage 往返问出来（协议见 extension/workstation_bridge.js）。过去这件事
// 被问了三遍且各问各的：首页状态行问一次、抓取链路开跑前再问一次 900ms、采集链路干脆不问。
// 于是有两个真实缺陷：
//   1. 检测没完成时动作照样开跑：首页那行还写着「检测中…」，用户点了抓取，抓取自己再打一次
//      探测并当场判"插件没响应"——同一台浏览器上"首页说已连接、点抓取说不认识"。
//   2. 采集从不说"没装"：装与没装的用户看到同一句"装了会怎样、没装会怎样"，没装的人只能对着
//      一个永远加载的小红书页面干等到接管提示。
// 现在收敛成一份带状态的检测（idle / detecting / present / absent），动作入口统一走
// requireCaptureExtension()：检测还没出结果就等它（并如实说"正在等待插件连接完成"），
// 结果是"没检测到"就当场拦住并说清"未检测到插件安装"，绝不"先建任务再祈祷插件接手"。
const EXTENSION_BRIDGE_SOURCE = "shuzuotai-workstation";
const EXTENSION_NAME = "薯作台小红书采集助手";
// 桥接脚本在就不代表插件可用：它可能挂着已被判失效的上一版上下文，所以这一趟必须等
// 扩展后台的回答，900ms 是"本地消息却没人答"的兜底上限。
const EXTENSION_PROBE_TIMEOUT_MS = 900;
// 扩展后台是 MV3 service worker，浏览器闲下来就把它回收了：第一次问没人答，常常只是它正在
// 被唤醒（服务端机器上更明显）。所以问不到就多问两次，几次都没人接才判"没检测到"——
// 一次没答就报"未检测到插件安装"会把装得好好的用户拦在抓取/采集门外。
const EXTENSION_PROBE_ATTEMPTS = 3;
const EXTENSION_PROBE_RETRY_DELAY_MS = 400;
// 否定回答不能立刻定案：同一页面里可能同时挂着"失效的旧桥接"（立刻回 false）和"刚被后台
// 补注入的活桥接"（绕后台一圈才回 true）。页面只认第一个 pong，会被 false 抢走，后台那次
// 补注入就白做了。给 true 一个很短的窗口，再采信 false。
const EXTENSION_PROBE_NEGATIVE_GRACE_MS = 250;
// 一次检测结果的有效期。期内抓取/采集与首页状态行直接复用（点击到反馈之间不该多出一次
// 探测的空白），过期才重问一遍：用户装好插件后刷新本页、重载扩展后回到本页，状态都会跟着更新。
// 页面启动时那次探测（见 shell/boot.js）算出来的结论就活在这个窗口里。
const EXTENSION_STATUS_TTL_MS = 20000;
// "正在等待插件连接完成"只在等待确实发生了的时候才说。插件在的话这一趟本机消息几毫秒就回来，
// 先弹一条等待提示只会留下一句与事实无关、还要滞留几秒的话。超过这个宽限还没落地才提示。
const EXTENSION_WAITING_NOTICE_DELAY_MS = 350;
const CAPTURE_EXTENSION_INSTALL_HINT = "请到首页「浏览器插件」下载安装（支持 Chrome 和 Edge），装好后点状态行右侧的「重新检测」；"
  + "一般不用刷新整页，刚更新或重载过扩展时同理——仍连不上再刷新本页。";

// everPresent：**这一页曾经**连上过插件。装没装是"这台浏览器的事实"，而"这一页现在问不问得到"
// 只是"当前这一份页面上下文的状态"——扩展被更新/重载过、浏览器回收了后台、标签页被冻结过之后，
// 页面里挂着的那份桥接脚本就再也不回话了，可插件明明还装着。这两种情况要说不同的话，
// 所以把"曾经连上过"记在这里（只活在这一页里：刷新会把桥接脚本重新注入，一切从头验）。
const extensionDetection = { status: "idle", version: "", at: 0, promise: null, everPresent: false };

function extensionDetectionSnapshot() {
  return {
    status: extensionDetection.status,
    present: extensionDetection.status === "present",
    version: extensionDetection.version,
    everPresent: extensionDetection.everPresent,
  };
}

// 「有结果且还新鲜」才直接复用。正在检测（promise 未落地）一律算没有结果：这时复用的是
// 在飞的那一次检测本身，动作入口于是会等它，而不是再打一轮探测。
function extensionDetectionFresh(now = Date.now()) {
  if (extensionDetection.status !== "present" && extensionDetection.status !== "absent") return false;
  return now - extensionDetection.at < EXTENSION_STATUS_TTL_MS;
}

// 问桥接脚本一句"插件在不在、哪一版"。问到 pong 才算"这台浏览器里插件是活的"：桥接脚本会
// 往扩展后台打一个来回，正是把"页面挂的是上一版脚本、该刷新了"验出来的唯一办法。
function probeCaptureExtension(timeoutMs = EXTENSION_PROBE_TIMEOUT_MS) {
  return new Promise((resolve) => {
    const requestId = "probe-" + Date.now() + "-" + Math.random().toString(36).slice(2);
    let settled = false;
    let negativeTimer = 0;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(negativeTimer);
      window.removeEventListener("message", handler);
      resolve(result);
    };
    const handler = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.source !== EXTENSION_BRIDGE_SOURCE) return;
      if (data.type !== "extension-pong" || data.requestId !== requestId) return;
      const result = { present: Boolean(data.ok), version: String(data.version || "") };
      if (result.present) {
        finish(result);
        return;
      }
      // 否定回答先挂起：同一页面里可能还有一份活桥接稍后才回 true（它要绕扩展后台一圈）。
      // 立刻采信 false 就会被失效的旧桥接抢答，后台那次补注入等于白做。
      if (!negativeTimer) {
        negativeTimer = window.setTimeout(() => finish(result), EXTENSION_PROBE_NEGATIVE_GRACE_MS);
      }
    };
    window.addEventListener("message", handler);
    try {
      window.postMessage(
        { source: EXTENSION_BRIDGE_SOURCE, type: "ping-extension", requestId },
        window.location.origin,
      );
    } catch (_error) {
      finish({ present: false, version: "" });
      return;
    }
    window.setTimeout(() => finish({ present: false, version: "" }), timeoutMs);
  });
}

// 打一次检测，或加入正在进行的那一次。**同一时刻只有一份检测**：用户点得快、动作和首页那次
// 检测撞在一起时，后到的等前一份结果，而不是自己再打一轮探测（那正是"首页说已连接、点下去
// 说不认识"的来源）。force 用于明确要求重问的场景（服务端包版本刚填好，要按新版本比一次）。
function detectCaptureExtension(options = {}) {
  if (extensionDetection.promise) return extensionDetection.promise;
  if (!options.force && extensionDetectionFresh()) return Promise.resolve(extensionDetectionSnapshot());
  extensionDetection.status = "detecting";
  extensionDetection.promise = (async () => {
    let result = { present: false, version: "" };
    for (let attempt = 0; attempt < EXTENSION_PROBE_ATTEMPTS; attempt += 1) {
      if (attempt) await new Promise((resolve) => window.setTimeout(resolve, EXTENSION_PROBE_RETRY_DELAY_MS));
      try {
        result = await probeCaptureExtension(Number(options.timeoutMs) || EXTENSION_PROBE_TIMEOUT_MS);
      } catch (_error) {
        result = { present: false, version: "" };
      }
      if (result.present) break;
    }
    extensionDetection.status = result.present ? "present" : "absent";
    if (result.present) extensionDetection.everPresent = true;
    extensionDetection.version = String(result.version || "");
    extensionDetection.at = Date.now();
    extensionDetection.promise = null;
    syncExtensionDetectionIndicators();
    return extensionDetectionSnapshot();
  })();
  syncExtensionDetectionIndicators();
  return extensionDetection.promise;
}

// 首页那句"本机浏览器 …"是同一份检测结果的展示面：检测出结果就写结果，页面上原有的
// 「检测中…」兜底由 index.html 自己给。过去它只在"登录链末端那次探测"落地时更新，
// 动作入口另起的探测改不动它，两处说法会互相矛盾。
function extensionInstalledStatusText(detection) {
  if (detection.status !== "present" && detection.status !== "absent") return "检测中…";
  if (!detection.present) {
    // 先前连上过、现在问不到：**不能**写成"未检测到"——插件还装着，是这一页的上下文失效了
    // （扩展被更新/重载、后台被回收、标签页被冻结过）。午休回来"状态还写着已连接、抓取却
    // 说没装"那种自相矛盾就是这么来的。
    return detection.everPresent
      ? "已装但本页连不上（点右侧「重新检测」）"
      : "未检测到（装好后点右侧「重新检测」）";
  }
  const expected = typeof extensionPackageVersion === "function" ? extensionPackageVersion() : "";
  if (!detection.version) return "已连接";
  if (expected && expected !== detection.version) return `已装 ${detection.version}，服务端是 ${expected}，请重新安装`;
  return `已连接 ${detection.version}`;
}

function syncExtensionDetectionIndicators() {
  if (typeof document === "undefined" || !document.querySelectorAll) return;
  const nodes = document.querySelectorAll("[data-extension-installed]");
  if (!nodes.length) return;
  // 已经有结论之后再重问（结果过期、版本比对、动作前的复核）时保留上一次的结论：把显示打回
  // 「检测中…」会让刚看到「已连接」的用户以为插件掉了，而几百毫秒后结论往往一模一样。
  // 第一次检测（还没有任何结论）才写「检测中…」——那正是页面上原有的兜底文案。
  if (extensionDetection.status === "detecting" && extensionDetection.at) return;
  const text = extensionInstalledStatusText(extensionDetectionSnapshot());
  nodes.forEach((node) => {
    node.textContent = text;
  });
}

// 服务端包版本到齐后再渲染一次（首页那句要把本机版本跟服务端版本比一比，调用点见
// core/api.js）。本机装没装在页面启动时就探过了（见 shell/boot.js），这里**只在结果过期时**
// 才重问：结论已经在页面上写着，再重打一轮只会白等一次（重问期间也保留上一条结论，见上）。
async function refreshLocalExtensionStatus() {
  if (extensionDetectionFresh()) {
    syncExtensionDetectionIndicators();
    return extensionDetectionSnapshot();
  }
  return await detectCaptureExtension({ force: true });
}

// 首页「本机浏览器 …」旁边那个刷新按钮：装好或重载扩展之后，用户不必刷新整个网页，
// 点一下就**强制**重问一次——不管上一份结论是否还在有效期内。按钮此刻要如实进入
// "正在重新检测…"：手动点了一次却看不出任何动静，用户只会以为按钮坏了。
async function refreshExtensionStatusFromButton(button = null) {
  const indicators = typeof document !== "undefined" && document.querySelectorAll
    ? [...document.querySelectorAll("[data-extension-installed]")]
    : [];
  if (button) {
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
  }
  // 手动重问时明确写"正在重新检测…"：syncExtensionDetectionIndicators 会为已有结论保留
  // 上一次文案，正好不会把这句盖掉（结论落地时再统一写回结果）。
  indicators.forEach((node) => { node.textContent = "正在重新检测…"; });
  try {
    await detectCaptureExtension({ force: true });
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  }
  // detectCaptureExtension 落地时已经写过一次；这里兜底，保证这一趟一定以结果收尾。
  syncExtensionDetectionIndicators();
  return extensionDetectionSnapshot();
}

// 页面被晾在后台很久（午休、切去干别的）之后回到本页时静默重问一次：插件可能已经被重载或
// 回收，而状态行还停在几小时前的「已连接」。这里不弹任何提示（用户只是切回来，没点动作），
// 只把状态改成此刻的事实——"回来一看还写着已连接、一抓却说连不上"就是缺这一步。
function armExtensionDetectionRefresh() {
  const refresh = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    if (extensionDetectionFresh()) return;
    void detectCaptureExtension();
  };
  document.addEventListener("visibilitychange", refresh);
  window.addEventListener("focus", refresh);
}

// 给用户的说法。动作名由调用点给（"抓取"/"采集"），因为用户点的是按钮，不是插件。
function captureExtensionWaitingMessage(actionLabel = "这次操作") {
  return `正在等待插件连接完成…（${actionLabel}由浏览器里的「${EXTENSION_NAME}」扩展执行，几秒内出结果）`;
}

// 动作被拦下来时的说法，分两种——**这台浏览器里从没见过插件**（装没装都不知道），
// 与**先前连上过、这一页现在问不到了**（多半是扩展被重载/更新过，页面里挂的还是上一版脚本）。
// 合成一句会把"状态栏明明写着已连接"的用户说懵：他确实是装了的，要他做的是刷新本页。
function captureExtensionFailure(detection, actionLabel = "这次操作") {
  if (detection.everPresent) {
    return {
      heading: "插件没有响应",
      message: `插件没有响应：这一页先前连上过「${EXTENSION_NAME}」，现在问不到它了——`
        + "多半是扩展被更新或重新加载过（已打开的页面里挂的还是上一版插件脚本），或者扩展后台刚被浏览器回收还没醒。",
      hint: "先点状态行右侧的「重新检测」重试（一般无需刷新整页）；仍然连不上再刷新本页，"
      + "或到扩展页（chrome://extensions / edge://extensions）重新加载一次扩展。",
    };
  }
  return {
    heading: "未检测到插件安装",
    message: `未检测到插件安装：${actionLabel}由浏览器扩展「${EXTENSION_NAME}」执行，这台浏览器里没有检测到它。`,
    hint: CAPTURE_EXTENSION_INSTALL_HINT,
  };
}

// 带标记的错误：调用点据此换成"插件没响应/没装"的说法，而不是笼统的"导入失败/这次采集没拿到内容"。
// heading 与 hint 一起带上，是因为两种情况的标题和下一步都不一样。
function captureExtensionMissingError(failure) {
  const error = new Error(failure.message);
  error.extensionMissing = true;
  error.extensionHeading = failure.heading;
  error.extensionHint = failure.hint;
  return error;
}

// 动作入口的统一闸门：
//   · 检测还在路上（首页那次没回来，或结果过期要重问）→ 等的是**同一份**检测，等待超过宽限
//     就如实说"正在等待插件连接完成"，不再自己另打一次探测；
//   · 结果是"问不到"→ 返回 { heading, message, hint }，调用点据此拦住这一次动作；
//   · 已连接 → 返回 null，调用点照常继续。
// 返回对象而不是抛错，是因为两个调用点的呈现面不同：抓取走失败提示条，采集走提示浮层。
async function requireCaptureExtension(actionLabel = "这次操作") {
  const detection = detectCaptureExtension();
  const waitingNotice = window.setTimeout(() => {
    if (typeof toast === "function") toast(captureExtensionWaitingMessage(actionLabel), { duration: 3000 });
  }, EXTENSION_WAITING_NOTICE_DELAY_MS);
  let result;
  try {
    result = await detection;
  } finally {
    window.clearTimeout(waitingNotice);
  }
  return result.present ? null : captureExtensionFailure(result, actionLabel);
}

// ----- js/core/unfinished-surfaces.js -----
// 未完成、先把入口收起来的功能面。
//
// 云端知识库（Dify）这一块还没做完——连接、选文档、检索确认都还要再打磨——所以界面上的
// 入口先隐藏：顶部「知识库」板块、团队治理里的「知识库连接」、资料参照里的「云端资料」。
// 只收入口，不删能力：状态、接口、数据和本地参考资料全部照常。
//
// 恢复时把下面的开关改回 true，并去掉 web/index.html（脚本里有 hidden 的那几处）与
// js/knowledge/workspace.js 里同一批 hidden。
const CLOUD_KNOWLEDGE_ENABLED = false;

// 入口已收起的板块不能再被打开：旧会话里存过的 currentSection 会把页面落在空壳上。
function unfinishedSectionHidden(section = "") {
  return !CLOUD_KNOWLEDGE_ENABLED && section === "knowledge";
}

// 「资料参照还缺本地资料」时的提示。云端入口收起后不能再让人「切换到云端资料」。
function knowledgeLocalOnlyHint() {
  return CLOUD_KNOWLEDGE_ENABLED
    ? "默认仅使用本地资料，请先添加并确认本地文件；如需云端资料，请手动切换到云端资料"
    : "默认仅使用本地资料，请先添加并确认本地文件";
}

// ----- js/auth/logto-errors.js -----
// Logto 的错误码 → 中文话术。登录/注册/重置弹层与账号设置共用这一份。
//
// 为什么必须收口：Logto 对外那层 message 经常是"说了等于没说"的空话，真正的原因在别处——
//   - 用户名格式不合法 → code=guard.invalid_input，message 只有「请求中 body 无效」，
//     原因埋在 data.issues[].path 里且恒为 value，不看调用点就分不清这次验的是用户名还是密码；
//   - 密码不合规 → code=password.rejected，message 只有「密码被拒绝。请检查密码是否符合要求。」，
//     具体理由（太短 / 已泄露 / 连续序列）在 data[].code 里，不读就等于没告诉用户要求是什么。
// 映射规则来自 2026-09-14 在本机 Logto 上逐条实测：
//   a / a_b 通过；a-b、中文 → guard.invalid_input；129 位 → user.username_too_long；admin → 已占用。
//   29481736、Password1、abcdefgh → password_rejected.pwned；Kx7#mQp2Lw → 通过。

function logtoPasswordRejectionReasons(payload) {
  const items = Array.isArray(payload?.data) ? payload.data : [];
  return items
    .map((item) => {
      switch (String(item?.code || "")) {
        case "password_rejected.too_short":
          return `至少 ${Number(item?.interpolation?.min) || 8} 位`;
        case "password_rejected.pwned":
          return "不能是已在公开泄露数据里出现过的密码";
        case "password_rejected.restricted.sequence":
          return "不能是连续字符（如 12345678、abcdefgh）";
        default:
          return "";
      }
    })
    .filter(Boolean);
}

// 429 的等待时间在响应头 Retry-After 里（秒数或 HTTP 日期两种写法）。取不到就返回 0，
// 话术退化成「稍等片刻」——不要在调用点编一个数字，那比不说更误导。
function logtoRetryAfterSeconds(response) {
  const raw = String(response?.headers?.get?.("Retry-After") || "").trim();
  if (!raw) return 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds > 0) return Math.round(seconds);
  const at = Date.parse(raw);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, Math.round((at - Date.now()) / 1000));
}

// options.invalidField：这次失败的是哪个字段（"username" / "password"）。
// guard.invalid_input 的报文里认不出字段，只能由调用点告诉映射，否则只能给没有信息量的兜底。
function logtoErrorMessage(payload, status, options) {
  const settings = options || {};
  const code = String(payload?.code || "");
  if (code === "password.rejected") {
    const reasons = logtoPasswordRejectionReasons(payload);
    return reasons.length
      ? `密码不符合要求：${reasons.join("；")}。`
      : "密码不符合要求：至少 8 位，且不要使用常见或已泄露的密码。";
  }
  if (code === "user.username_too_long") return "用户名太长：最多 128 个字符。";
  if (code === "user.username_already_in_use") return "该用户名已被占用，换一个再试。";
  // 注册时最常见的那条：邮箱已经被别的账号绑过（实测 422，message 是英文的
  // "This email is associated with an existing account."）。以前没有这个分支，
  // 于是落到兜底、把裸状态码「认证服务返回 422」丢给了用户。
  if (code === "user.email_already_in_use") return "该邮箱已被其他账号使用，请换一个邮箱。";
  // 改密时把新密码填成当前密码（案例 56 实测 422）。这条会从账号设置与忘记密码两处冒出来，
  // 用户自己就能改；以前没有分支，落到底就变成「账号服务返回 422」。
  if (code === "user.same_password") return "新密码不能与当前密码相同，请换一个。";
  // 密码不满足实例的安全策略（Logto 公开 API 参考里与上面同族，无子码）。本地只校验长度，
  // 「缺数字/缺符号」这类会被本地放过去，所以这条是真会走到的。
  if (code === "user.password_policy_violation") return "密码不符合要求：至少 8 位，且不要使用常见或已泄露的密码。";
  // interaction cookie 丢了或过时（实测 400）：不是用户输入的问题，重开一次弹层即可。
  if (code === "session.not_found") return "登录会话已失效，请关闭弹层后重新打开再试。";
  // 提交时服务端找不到这次交互对应的账号标识（实测 404，message 是
  // "User identifier not found. Please go back and sign in again."）。与上一条的区别是
  // 这里连账号都没认出来，必须重新走一遍登录。
  if (code === "session.identifier_not_found") return "找不到当前登录的账号信息，请关闭弹层后重新登录一次。";
  // 交互结束时 profile 还没写全（实测 422）：能走到这一步说明前面某步没成功。
  if (code === "user.missing_profile") return "账号资料不完整，请补齐邮箱或用户名后重试。";
  if (code === "guard.invalid_input") {
    if (settings.invalidField === "username") return "用户名只能用字母、数字和下划线，长度 1–128 位。";
    if (settings.invalidField === "email") return "邮箱格式不正确，请检查后重试。";
    if (settings.invalidField === "password") return "密码不符合要求：至少 8 位，且不要使用常见或已泄露的密码。";
    // guard.invalid_input 是所有 zod 校验失败的统称，必须看字段路径再给话术，
    // 否则所有校验失败都会被说成同一个字段的问题（曾经误判过一次）。
    const issue = payload?.data?.issues?.[0] || {};
    const path = Array.isArray(issue.path) ? issue.path.join(".") : "";
    return path ? `请求参数不合法（${path}），请检查后重试。` : "请求参数不合法，请检查后重试。";
  }
  if (code === "account_center.field_not_editable") return "该字段暂不支持自行修改，请联系管理员。";
  // 邮箱验证码这几条都是实测的（2026-09-14 与 2026-09-15 两批）：码不对回
  // verification_code.not_found；注册/重置/换绑里把码填成 000000 这类"格式对但内容错"的
  // 回 verification_code.code_mismatch（案例 8/22/27/58 实测 400）；验证记录过时/被用过回
  // session.verification_session_not_found。对用户来说是同一件事（码不对），所以共用一句话，
  // 避免同一个错因出现两种说法。
  //
  // 这句话不能说"已过期"：这两条码**都不是**过期。Logto 的判序是
  // not_found（查不到/已被消费）→ expired（createdAt + 有效期 < now）→ exceed_max_try（试错次数
  // 用尽）→ code_mismatch（填错），见 packages/core/src/libraries/passcode.ts 的 verifyPasscode。
  // 把"码填错"说成"验证码已过期"，用户会以为是自己等太久，10 分钟内反复重试，越试越错（P21）。
  if (code === "verification_code.not_found" || code === "verification_code.code_mismatch") {
    return "验证码不正确或已失效，请重新获取验证码。";
  }
  // 真过期与试错超限：以前这两条没有映射，落兜底变成「认证服务暂时不可用」——明明用户重新取一次码
  // 就能继续，却被引到客服（P21）。各自的起因不同，话术分开写。
  if (code === "verification_code.expired") return "验证码已过期，请重新获取验证码。";
  if (code === "verification_code.exceed_max_try") return "验证码错误次数过多，请重新获取验证码。";
  if (code === "session.verification_session_not_found") return "验证记录已失效，请重新获取验证码。";
  // 登录失败锁定（Logto sentinel，阈值见 XHS_LOGTO_SENTINEL_*）：同一账号一小时内失败到阈值后，
  // 连正确密码也会被拒，直到锁定期过。Logto 自己的 message 是「尝试次数过多。请稍后再试 X 分钟。」，
  // 可读但没说清是账号被锁、且随实例语言变；这里给一句不依赖 locale 的话术。
  // 不映射的话会掉到最末尾的 fallback，用户看到的是「认证服务返回 400」。
  if (code === "session.verification_blocked_too_many_attempts") {
    return "登录失败次数过多，该账号已被临时锁定，请稍后再试。";
  }
  // 501 = Logto 的 ConnectorErrorCodes.NotImplemented（connector.not_found）：这个实例没装或
  // 没实例化发信通道，验证码根本发不出去。原样透传 501 只会让人来问"501 是什么"，这里说清
  // 是环境缺通道、该找谁。（实测：本机装了 SMTP 连接器时这两条发码路径都是 200。）
  if (status === 501 || code === "connector.not_found") {
    return "当前环境没有可用的邮件发送通道，验证码发不出去；请联系管理员配置邮件连接器后再试。";
  }
  // 登录用的是这条（实测 422 + session.invalid_credentials）；账号设置在它自己的入口里改口，
  // 见 account-settings.js。不依赖 Logto 的 message 本地化，实例换语言也不会漏出英文。
  if (code === "session.invalid_credentials") return "账号或密码错误，请重新输入。";
  if (code === "verification.password_invalid") return "当前密码不正确。";
  if (code === "verification_record.permission_denied") return "登录状态需要重新验证：请先退出并重新登录一次。";
  if (code === "oidc.session_not_found") return "该登录设备已失效，请刷新后重试。";
  if (status === 401 || code === "auth.unauthorized") {
    return "登录状态已过期或权限不足：请退出后重新登录一次再试。";
  }
  if (status === 403) return "当前账号没有修改该项的权限。";
  // 被限流时响应体常常解析不出 code：nginx 的 zone（xhs_auth）用自己的 429 页面，
  // 部署侧还没改成 JSON，前端 `response.json().catch(() => ({}))` 拿到 `{}`，
  // 于是落到最末尾那句「认证服务暂时不可用」——把「请求太快」说成了「服务挂了」，
  // 用户会去等一场并不存在的故障，排障侧也分不清限流与故障（P96）。
  // 按状态码分档是本文件已有的模式（上面 401/403 两行），429 补上同一档；
  // 有 Retry-After 就带上可等待时间，没有就说清「稍等片刻」。
  if (status === 429) {
    // 等待时间两个来源：响应头 Retry-After（部署侧按本仓库文档配的），或报文里的
    // retryAfterSeconds（同族网关回 JSON 时）。都没有就只说「稍等片刻」。
    const seconds = Math.round(Number(settings.retryAfterSeconds || payload?.retryAfterSeconds));
    if (!Number.isFinite(seconds) || seconds <= 0) return "请求过于频繁，请稍等片刻后再试。";
    const wait = seconds >= 60 ? `${Math.max(1, Math.round(seconds / 60))} 分钟` : `${seconds} 秒`;
    return `请求过于频繁，请等约 ${wait} 后再试。`;
  }
  // 走到这里说明这个 code 还没被映射。兜底必须由这里定死，不能让调用点给：
  //   1) 调用点顺手写的就是「认证服务返回 422」这种裸状态码，对用户既说不出原因、
  //      也给不了下一步；
  //   2) payload.message 是 Logto 那边的原文（英文/随实例语言变），透出去就是漏英文。
  // 排障需要的那两个值走控制台，不在界面上出现。
  globalThis.console?.warn?.("[logto-error] 未映射的错误码", { code, status });
  return "认证服务暂时不可用，请稍后重试或联系管理员。";
}

// ----- js/auth/session.js -----
// 组织切换失败的一句话术：切换被拒（P42）与刷新后所选组织不可用（P41）是同一种
// 事实——当前账号进不去那个组织——所以两条路径共用同一句提示，别一处说一处不说。
const ORGANIZATION_SWITCH_FAILED_MESSAGE = "组织切换失败，请确认当前账号属于该组织";

// 跨标签登录态（P40）：登出只作用在当前标签，其它标签会停在"界面已登录、接口已失效"
// 的分裂态。这里只广播"会话开始/结束"这一个事实，不传令牌；storage 事件不会回到写入
// 标签自己，所以不会自触发。存储不可用（隐私模式）时退化为 401 统一登出（core/client.js）。
const AUTH_TAB_BROADCAST_KEY = "xhs-auth-tab-broadcast-v1";
const AUTH_TAB_SIGNED_OUT = "signed-out";
const AUTH_TAB_SIGNED_IN = "signed-in";
const AUTH_TAB_SIGNED_IN_HINT = "已在另一个标签页登录，刷新本页即可同步登录状态";

function broadcastAuthSessionChange(type) {
  try {
    localStorage.setItem(AUTH_TAB_BROADCAST_KEY, JSON.stringify({ type, at: Date.now() }));
  } catch (_error) {
    /* 广播不可用时由 401 兜底 */
  }
}

// 本标签的会话从"确认没有"变成"有"时广播一次登录。初值留 null：页面刚打开就已经是登录态
// （刷新一个已登录标签）不算"别处登录了"，否则每次 F5 都会去打扰其它标签。
// D10：它记录的是"上一次观察值"，只给下面 noteLocalAuthSession 做跃迁判定（唯一的读取方就是
// 它自己，见 grep `lastObservedAuthenticated`），**不是**一份可供读取的登录态——要判断"当前
// 是否已登录"只能用 login-gate.js 的读口 signedInState() / resolveSignedInState()。
// 也正因为它是跃迁记录，删掉它跨标签的"登录方向"广播（AUTH_TAB_SIGNED_IN）就没有依据了。
let lastObservedAuthenticated = null;

function noteLocalAuthSession(authenticated) {
  const now = Boolean(authenticated);
  const was = lastObservedAuthenticated;
  lastObservedAuthenticated = now;
  if (was === false && now) broadcastAuthSessionChange(AUTH_TAB_SIGNED_IN);
}

// 换成"登录落地"这个事实本身的广播（P40 的登录方向）。登录成功要整页跳到 /callback 换码，
// 文档一换，lastObservedAuthenticated 的初值必然是 null——"本标签从无到有"的跃迁推断看不穿
// 文档边界，所以 A 登录后发不出任何广播，B 就一直停在未登录态（回归 2-D 与 RG4-R4 都实测到）。
// 跃迁推断不了跨文档的事，只能用回调落地这个信号：spa-auth 换码成功后以 reason="callback"
// 广播一次认证变化（web/spa-auth.js 的 handleCallback），那时就是"本标签刚登录"。
const AUTH_CALLBACK_REASON = "callback";

function noteAuthCallbackLanded(reason) {
  if (String(reason || "") !== AUTH_CALLBACK_REASON) return false;
  broadcastAuthSessionChange(AUTH_TAB_SIGNED_IN);
  return true;
}

// 收到"另一个标签已退出"：清掉本标签的本地会话（不跳转），登录态 UI 随
// xhs-spa-auth-changed(signout) 回退到未登录态。本来就没登录的标签不动，避免无谓提示。
async function applyRemoteSignOut() {
  const auth = globalThis.xhsSpaAuth;
  if (!auth) return;
  if (!(await auth.isAuthenticated?.().catch(() => false))) return;
  const reset = typeof auth.signOutLocal === "function"
    ? () => auth.signOutLocal()
    : (typeof auth.signOut === "function" ? () => auth.signOut() : null);
  if (!reset) return;
  await reset().catch((error) => console.error("Cross-tab sign-out failed", error));
  toast("已在另一个标签页退出登录", { duration: 4200 });
}

// 收到"另一个标签已登录"。会话按标签隔离（Logto SDK 把 refresh token 放在 sessionStorage），
// 本标签拿不到那份会话，所以**不能**凭空把自己变成登录态——那等于把"隔离"偷偷取消掉。
// 能做的、也必须做的是别停在一个让人误解的未登录界面上：告诉用户刷新即可同步。
async function applyRemoteSignIn() {
  const auth = globalThis.xhsSpaAuth;
  if (!auth) return;
  if (await auth.isAuthenticated?.().catch(() => false)) return;
  toast(AUTH_TAB_SIGNED_IN_HINT, { duration: 5200 });
}

function bindAuthTabSync() {
  if (document.documentElement.dataset.authTabSyncBound === "1") return;
  document.documentElement.dataset.authTabSyncBound = "1";
  window.addEventListener("storage", (event) => {
    if (event.key !== AUTH_TAB_BROADCAST_KEY || !event.newValue) return;
    let payload = null;
    try {
      payload = JSON.parse(event.newValue);
    } catch (_error) {
      payload = null;
    }
    const type = String(payload?.type || "");
    if (type === AUTH_TAB_SIGNED_OUT) {
      void applyRemoteSignOut();
      return;
    }
    if (type === AUTH_TAB_SIGNED_IN) void applyRemoteSignIn();
  });
}

function bindSpaAuthUi() {
  const button = document.querySelector("#authSignInBtn");
  const signOut = document.querySelector("#authSignOutBtn");
  const organization = document.querySelector("#authOrganizationSelect");
  const mobileOrganization = document.querySelector("#mobileOrganizationSelect");
  const billing = document.querySelector("#openBillingBtn");
  if (!button || !signOut || !organization) return;
  const accountMenu = document.querySelector("#accountMenu");
  const accountMenuPanel = document.querySelector("#accountMenuPanel");
  const accountSettingsEntry = document.querySelector("#accountSettingsEntry");
  const accountPromptsEntry = document.querySelector("#accountPromptsEntry");
  const myOrganizationsEntry = document.querySelector("#myOrganizationsEntry");
  // 账号菜单只对登录用户存在：未登录时里面既没有账号设置、也没有可做的操作。
  // 面板会被 events.js 挪到 body 上，所以隐藏入口时要顺手把面板一并收起。
  const setAccountMenuVisible = (visible) => {
    accountMenu?.classList.toggle("hidden", !visible);
    accountSettingsEntry?.classList.toggle("hidden", !visible);
    accountPromptsEntry?.classList.toggle("hidden", !visible);
    myOrganizationsEntry?.classList.toggle("hidden", !visible);
    if (!visible) {
      accountMenuPanel?.classList.add("hidden");
      accountMenu.querySelector("#accountMenuBtn")?.setAttribute("aria-expanded", "false");
    }
  };
  const renderOrganizationChoices = (auth) => renderOrganizationSelect(auth);
  const setBillingVisible = (visible) => billing?.classList.toggle("hidden", !visible);
  const showOrganizationSwitchFailure = () => {
    // 提示不限页面：切换失败是"这个操作没做成"，停在首页也该看得见（P42）。
    toast(ORGANIZATION_SWITCH_FAILED_MESSAGE, { kind: "error", duration: 7000 });
    authManagementState.error = ORGANIZATION_SWITCH_FAILED_MESSAGE;
    if (state.currentSection === "auth-management") renderAuthManagement();
  };
  const setAuthenticatedUiPending = () => {
    button.classList.add("hidden");
    signOut.classList.remove("hidden");
    organization.classList.add("hidden");
    mobileOrganization?.classList.add("hidden");
    setAccountMenuVisible(true);
    setBillingVisible(false);
    authManagementSetNavVisible(false);
    adminConsoleSetNavVisible(false);
  };
  const resetBillingState = () => {
    billingState.loaded = false;
    billingState.error = "";
    billingState.membership = null;
    billingState.balance = null;
    billingState.pricing = null;
    billingState.trial = null;
    billingState.lowBalance = null;
    billingState.rates = null;
    billingState.transactions = null;
    billingState.meteringEnabled = true;
    renderBillingStatus();
  };
  const closeButton = document.querySelector("#closeAuthLoginBtn");
  const modal = document.querySelector("#authLoginModal");
  if (closeButton) closeButton.onclick = closeAuthLoginModal;
  if (modal) {
    modal.onclick = (event) => {
      if (event.target === modal) closeAuthLoginModal();
    };
  }
  if (document.documentElement.dataset.authKeyboardBound !== "1") {
    document.documentElement.dataset.authKeyboardBound = "1";
    document.addEventListener("keydown", (event) => {
      const activeModal = document.querySelector("#authLoginModal:not(.hidden)");
      if (!activeModal) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeAuthLoginModal();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...activeModal.querySelectorAll("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex=\"-1\"])")]
        .filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }
  const syncAuthUi = async ({ refreshData = false, skipOrganizationRefresh = false } = {}) => {
    const auth = globalThis.xhsSpaAuth;
    if (!auth?.configured) return false;
    // 这是登录态的**权威读取**（直接问 SDK）：下面 noteLocalAuthSession / noteLoginGateSignedIn /
    // 整套 UI 三处写都出自这一次答案。别的地方要判断"当前是否已登录"，读 login-gate.js 的读口
    // signedInState()（同步缓存态）或那边的 resolveSignedInState()（权威态），不要各问一次 SDK——
    // 同一个 tick 里两个独立答案就会造出"界面已登录、接口已失效"的分裂（D10）。
    const authenticated = await auth.isAuthenticated?.().catch(() => false);
    // 本标签刚登录 → 广播一次，别让其它标签一直停在未登录的界面上（P40 的登录方向）。
    noteLocalAuthSession(authenticated);
    // 登录闸门（auth/login-gate.js）要一个**同步**的答案：未登录时各功能入口不再把"没登录"
    // 说成别的故障，而是提示登录并拉起登录弹窗。这里是登录态唯一的权威出口——启动、登录落地、
    // 登出、切组织、跨标签登出都经过它。
    if (typeof noteLoginGateSignedIn === "function") noteLoginGateSignedIn(authenticated);
    if (!authenticated) {
      resetAuthProvisioningState();
      accountSessionExtrasLoaded = false;
      adminConsoleProbeOutcome = "";
      button.classList.remove("hidden");
      signOut.classList.add("hidden");
      organization.classList.add("hidden");
      organization.value = "";
      if (mobileOrganization) {
        mobileOrganization.classList.add("hidden");
        mobileOrganization.value = "";
      }
      setAccountMenuVisible(false);
      setBillingVisible(false);
      resetBillingState();
      // 退出后不能残留上一个账号名（面板本身也收起来了，这里一并清干净）。
      setAccountNameText("");
      authManagementSetNavVisible(false);
      adminConsoleSetNavVisible(false);
      clearConsoleCapabilities();
      if (state.currentSection === "auth-management" || state.currentSection === "admin-console") setSection("home");
      if (pendingAuthInvitation && !pendingAuthInvitationPrompted) {
        pendingAuthInvitationPrompted = true;
        toast("请先登录，登录后会自动接受组织邀请", { duration: 5200 });
      }
      return false;
    }
    setAuthenticatedUiPending();
    // 账号名在登录完成时就写进 DOM：账号菜单只展示已就绪的值，不再依赖打开菜单那一刻
    // 的一次异步往返（那样会先闪一下 -- 占位）。
    void refreshAccountNameText(auth);
    if (!skipOrganizationRefresh && typeof auth.refreshOrganizations === "function") {
      await auth.refreshOrganizations().catch(() => []);
    }
    // 刷新时如果持久化的组织确实不可用，spa-auth 会回落到默认组织并留下一条待报；
    // 这里把这次回落如实告诉用户，别让组织在刷新后无声地变掉（P41）。
    const droppedOrganizationId = auth.consumeOrganizationRestoreWarning?.() || "";
    if (droppedOrganizationId) {
      toast(ORGANIZATION_SWITCH_FAILED_MESSAGE, { kind: "error", duration: 7000 });
    }
    try {
      // Every authenticated account gets a personal organization, even when
      // its claims already contain organizations received from invitations.
      await provisionAuthenticatedOrganization(auth);
    } catch (error) {
      setAuthenticatedUiPending();
      if (state.currentSection === "auth-management") setSection("home");
      await reportProvisioningFailure(error);
      return false;
    }
    try {
      await acceptPendingAuthInvitation(auth);
    } catch (error) {
      toast(`组织邀请接受失败：${error?.message || "邀请可能已过期，请重新获取"}`, { kind: "error", duration: 8000 });
    }
    // 邮件只是通知：已经登录的人应该在平台内看到邀请并一键确认，不必等那封信
    // （也不必去别处找那串邀请码）。读不到邀请不影响登录，所以这里不等它。
    void globalThis.xhsInvitationInbox?.prompt?.();
    const choices = renderOrganizationChoices(auth);
    if (!choices.length || !auth.getActiveOrganizationId?.()) {
      setAuthenticatedUiPending();
      if (state.currentSection === "auth-management") setSection("home");
      toast("组织初始化未完成：个人组织不可用", { kind: "error", duration: 8000 });
      return false;
    }
    button.classList.add("hidden");
    button.disabled = false;
    signOut.classList.remove("hidden");
    setBillingVisible(true);
    authManagementSetNavVisible(true);
    // 浏览器里的工作区缓存必须跟着组织走：登录完成/切换组织后先切到该租户的
    // 存储桶（并做一次旧数据迁移），再读取该组织的 AI 配置。
    let tenantScopeSwitched = false;
    if (typeof switchWorkspaceTenantScope === "function") {
      tenantScopeSwitched = await switchWorkspaceTenantScope()
        .catch((error) => {
          console.error("Tenant workspace switch failed", error);
          return false;
        });
    }
    // AI 配置以服务端为准：游客开机已跳过租户读；登录落定后这里补拉，切组织也走这里。
    // 必须先于治理台探测：/api/config 会带回 capabilities，普通账号据此直接跳过
    // /api/admin/session（否则登录落定会连打几次必然 403 的 session）。
    if (typeof loadAiConfigFromBackend === "function") {
      await loadAiConfigFromBackend().catch(() => {});
    }
    void refreshAdminConsoleAccess();
    // 工作区同样：游客开机不拉 /api/state；登录就绪后必须合并一次，否则新窗口任务全空。
    // 重试与失败提示由 retryWorkspaceHydration 负责（P85）。
    if (typeof mergeSavedImageLibraryFromBackend === "function") {
      await mergeSavedImageLibraryFromBackend();
    }
    // 迁移进新租户桶的内容要落库，但只能在水合把 stateRevision 对齐到服务端之后
    // 写：先写就是用新租户本地快照里的旧 revision 写盘，服务端判等失败回 409。
    if (tenantScopeSwitched && typeof persistWorkspaceAfterHydration === "function") {
      persistWorkspaceAfterHydration();
    }
    // 运营配置 / 封禁横幅 / 新设备提醒：**不能挂在 refreshData 下面**——硬刷新走的是
    // 下面的 else 分支（启动时 syncAuthUi() 不传 refreshData），挂上去就漏掉了，表现为
    // 管理端改完公告、用户刷新页面看不到（B24）。用 accountSessionExtrasLoaded 独立把关，
    // 仍保证一次登录会话只拉一轮，避免 auth-changed 连发时 Network 里刷一长串。
    if (!accountSessionExtrasLoaded) {
      accountSessionExtrasLoaded = true;
      if (typeof loadOpsConfig === "function") void loadOpsConfig();
      if (typeof loadAccountRestriction === "function") void loadAccountRestriction();
      if (typeof announceLoginDevice === "function") void announceLoginDevice();
    }
    if (refreshData) {
      resetBillingState();
      await loadBillingStatus();
      void loadCapabilities();
      // 首页插件卡片的版本号来自 /extension-download.zip 里那份 manifest。
      if (typeof loadExtensionPackage === "function") void loadExtensionPackage();
      if (state.currentSection === "auth-management") {
        authManagementState.loaded = {};
        await loadAuthManagement(true);
      }
    } else {
      void loadBillingStatus();
      void loadCapabilities();
      if (typeof loadExtensionPackage === "function") void loadExtensionPackage();
    }
    return true;
  };
  if (document.documentElement.dataset.authChangedBound !== "1") {
    document.documentElement.dataset.authChangedBound = "1";
    const handleAuthChanged = (event) => {
      const reason = event?.detail?.reason || "";
      // 整页跳转换码回来（reason="callback"）的落地要显式广播：这个文档里没有 false→true
      // 的跃迁可看，光靠 noteLocalAuthSession 是发现不了这次登录的（P40 登录方向）。
      noteAuthCallbackLanded(reason);
      if (reason === "signout" || reason === "callback" || event?.type === "xhs-mock-authenticated" || event?.type === "xhs-mock-signed-out") {
        resetAuthProvisioningState();
      }
      void syncAuthUi({
        refreshData: true,
        skipOrganizationRefresh: reason === "provisioned-organization",
      }).catch((error) => console.error("SPA auth UI refresh failed", error));
    };
    window.addEventListener("xhs-spa-auth-changed", handleAuthChanged);
    document.addEventListener("xhs-mock-authenticated", handleAuthChanged);
    document.addEventListener("xhs-mock-signed-out", handleAuthChanged);
    // 余额展示是事件驱动的，而真正扣费/放开冻结的时刻是"任务终态"：不在这里刷一次，
    // 顶栏与侧栏就会一直停在启动时读到的数字（台账 P16）。任务结束发事件是为了不让
    // core/client.js 依赖本模块的加载顺序。
    window.addEventListener("xhs-job-finished", () => {
      void loadBillingStatus();
    });
  }
  const bind = () => {
    const auth = globalThis.xhsSpaAuth;
    if (button.dataset.bound === "1") return;
    // The auth module is an asynchronously loaded ESM script. Keep polling
    // briefly when app.js runs first so the visible button never becomes inert.
    if (!auth?.configured) {
      window.setTimeout(bind, 50);
      return;
    }
    button.dataset.bound = "1";
    organization.classList.add("hidden");
    const startSignIn = async () => {
      button.disabled = true;
      try {
        await openAuthLoginModal(auth, () => syncAuthUi({ refreshData: true }));
        // The modal owns the submit state; keep the header action available
        // after the modal has been opened or dismissed.
        button.disabled = false;
      } catch (error) {
        console.error("SPA sign-in failed", error);
        toast(`登录失败：${error?.message || "认证服务暂不可用"}`);
        button.disabled = false;
      }
    };
    button.onclick = startSignIn;
    bindAuthTabSync();
    signOut.addEventListener("click", async () => {
      signOut.disabled = true;
      // 先广播再登出：完整的登出会跳转到登录提供方，页面卸载之后就再也没有机会
      // 通知其它标签了（P40）。
      broadcastAuthSessionChange(AUTH_TAB_SIGNED_OUT);
      try {
        await auth.signOut();
        button.classList.remove("hidden");
        signOut.classList.add("hidden");
        organization.classList.add("hidden");
        organization.value = "";
        if (mobileOrganization) {
          mobileOrganization.classList.add("hidden");
          mobileOrganization.value = "";
        }
        setBillingVisible(false);
        resetBillingState();
        authManagementSetNavVisible(false);
        adminConsoleSetNavVisible(false);
        if (state.currentSection === "auth-management" || state.currentSection === "admin-console") setSection("home");
      } finally {
        signOut.disabled = false;
      }
    });
    organization.addEventListener("change", async () => {
      const value = organization.value;
      const previousValue = auth.getActiveOrganizationId?.() || "";
      if (mobileOrganization && mobileOrganization.value !== value) mobileOrganization.value = value;
      // 下拉出现空值只有两种可能：本来就没有可选组织，或者 DOM 与当前组织不同步
      // （收到了不存在的组织 id）。后者不能让界面停在"未选中"，否则用户既不知道
      // 失败、也不知道自己到底在哪个组织下操作。
      if (!value) {
        if (previousValue) {
          renderOrganizationChoices(auth);
          showOrganizationSwitchFailure();
        }
        return;
      }
      organization.disabled = true;
      if (mobileOrganization) mobileOrganization.disabled = true;
      // 切走之前先算好这句提醒（它说的是"上一个组织"的事），切换成功后再显示——那时用户
      // 已经在新组织里，正好知道"刚才那张单留在哪了"。
      const pendingOrderNotice = pendingBillingOrderSwitchNotice(previousValue);
      try {
        // 切之前先把上一个组织的待写内容落库：250ms 的自动保存防抖可能在新组织
        // 生效之后才发请求，那样旧组织的内容会被写进新组织。
        if (typeof flushWorkspaceBeforeTenantSwitch === "function") {
          await flushWorkspaceBeforeTenantSwitch().catch(() => {});
        }
        await auth.switchOrganization(value);
        if (pendingOrderNotice) {
          // 收银台是上一个组织的：切过去之后轮询读不到那张单（服务端 `get_billing_order` 按
          // 当前租户判归属，会一直 404），界面却停在"等待支付中…"——那张码也不会再入账到这里。
          // 收掉它，订单留在哪由下面那句提醒说清（closeBillingPanel 会顺带停掉轮询）。
          closeBillingPanel();
          toast(pendingOrderNotice, { duration: 9000 });
        }
        // 当前组织到这一步就已经换了：给"跟着当前组织走"的其它界面一个确定的信号
        // （「我的组织」弹层靠它把"当前组织"那枚标记挪到新行）。**必须放在这里、不能挪到函数末尾**：
        // 后面的工作区落库/水合是另一个组织的数据准备，可能很慢甚至失败（本轮实测：夹具的
        // `/api/state` 契约不符，那个 await 永远回不来），而"我在哪个组织"此刻已经成立。
        document.dispatchEvent(new CustomEvent("xhs-organization-switched", { detail: { tenantId: value } }));
        let tenantScopeSwitched = false;
        if (typeof switchWorkspaceTenantScope === "function") {
          tenantScopeSwitched = await switchWorkspaceTenantScope()
            .catch((error) => {
              console.error("Tenant workspace switch failed", error);
              return false;
            });
        }
        // 新组织的工作区以服务端为准：先把草稿/发布读回来，stateRevision 才会对齐
        // 到服务端当前值，之后写盘才是合法的（见 state.js 里切租户的说明）。失败由它
        // 自己重试并提示，不在这里吞掉（P85）。
        if (typeof mergeSavedImageLibraryFromBackend === "function") {
          await mergeSavedImageLibraryFromBackend();
        }
        if (tenantScopeSwitched && typeof persistWorkspaceAfterHydration === "function") {
          persistWorkspaceAfterHydration();
        }
      } catch (error) {
        console.error("Organization switch failed", error);
        // 只有"切换本身"被拒才回到原组织。回滚要用下拉里真实存在的那个选项，任何
        // 情况下都不留一个空的选择器。
        organization.value = previousValue && [...organization.options].some((option) => option.value === previousValue)
          ? previousValue
          : "";
        if (mobileOrganization) mobileOrganization.value = organization.value;
        if (!organization.value) renderOrganizationChoices(auth);
        showOrganizationSwitchFailure();
        organization.disabled = false;
        if (mobileOrganization) mobileOrganization.disabled = false;
        return;
      }
      // 组织已经换了：之后的刷新（账单、治理成员）出错不能再把选择退回去，也不能
      // 报"切换失败"——那会把一个成功了的操作说成失败。
      try {
        resetBillingState();
        await loadBillingStatus();
        if (state.currentSection === "auth-management") {
          authManagementState.loaded = {};
          await loadAuthManagement(true);
        }
      } catch (error) {
        console.error("Organization switch refresh failed", error);
      } finally {
        organization.disabled = false;
        if (mobileOrganization) mobileOrganization.disabled = false;
      }
    });
    mobileOrganization?.addEventListener("change", () => {
      organization.value = mobileOrganization.value;
      organization.dispatchEvent(new Event("change"));
    });
    syncAuthUi().catch((error) => console.error("SPA auth UI initialization failed", error));
  };
  if (globalThis.xhsSpaAuthReady) globalThis.xhsSpaAuthReady.then(bind);
  else window.setTimeout(bind, 0);
}

let authLoginPollTimer = null;
// 账号名只有一个写入口：登录完成时（syncAuthUi）预置真实值，账号菜单打开时只做兜底刷新。
// 以前这里没有预置，值只在"打开菜单那一刻"异步去取，异步回来之前用户看到的就是 index.html
// 里那个 -- 占位；首次登录后第一次打开最容易撞上。空值不再渲染成占位符——那样会在取数
// 失败时把已就绪的真实账号名覆盖掉。
// D10：`#accountName` 曾经被怀疑是登录态的第四份副本（"自己写又自己读回来"）。已核实：
// 全仓只有写入方，没有任何地方读它的文本判登录态（grep `accountName` 只有 index.html 的元素、
// 本函数、shell/events.js:21 的写入与 tests）。所以它只是**展示输出**，不是状态副本，
// 也不需要收敛；判断登录态一律走 login-gate.js 的读口 signedInState()。
function setAccountNameText(name, node) {
  const target = node || document.querySelector("#accountName");
  if (!target) return;
  target.textContent = String(name || "").trim();
}
async function refreshAccountNameText(auth, node) {
  const name = auth?.getUserName ? await auth.getUserName().catch(() => "") : "";
  if (name) setAccountNameText(name, node);
}
let authExperienceContext = null;
// 注册用码换来的那条验证记录只在本次弹层会话里有效（和重置流程同一个道理）。放在模块级是因为
// 发码在弹层里、换记录在 registerAuthExperience 里，两边都要用同一个值。
let registerVerificationId = "";
// 验证码被 Logto 验证成功后会立即 consume（packages/core/src/libraries/passcode.ts 里
// `await consumePasscode(passcode.id)`），再次 verify 同一条 code 会得到
// `verification_code.not_found`、前端映射成"验证码不正确或已过期"。
// 而本地校验只兜"长度>=8"和"两次一致"，密码序列/pwned 这种只有 Logto 才认的规则要在 API
// 那一层才被拦——拦下来时 verify 已经白跑了一次。把 verify 成功的 recordId 缓存下来，后续
// 改密码重提交时直接复用，避免"换合规密码 → 验证码过期"这条假链路。
// 重新发码、切换模式、清弹层时必须清掉，否则会跨弹层复用死掉的 recordId。
let verifiedRegisterRecordId = "";
let verifiedResetRecordId = "";
let authLoginReturnFocus = null;
let authProvisioningPromise = null;
let authProvisioningCompleted = false;
let authProvisioningAuth = null;
const AUTH_INVITATION_STORAGE_KEY = "xhs-pending-organization-invitation-v1";
let pendingAuthInvitation = null;
let pendingAuthInvitationPrompted = false;
let pendingAuthInvitationPromise = null;

function normalizePendingAuthInvitation(value) {
  if (!value || typeof value !== "object") return null;
  const invitationId = String(value.invitationId || value.invitation_id || "").trim();
  const token = String(value.invitationToken || value.invitation_token || value.token || "").trim();
  if (!invitationId && !token) return null;
  return invitationId ? { invitationId } : { invitationToken: token };
}

function capturePendingAuthInvitation() {
  let captured = null;
  try {
    const params = new URLSearchParams(window.location.search);
    captured = normalizePendingAuthInvitation({
      invitationId: params.get("invitationId") || params.get("invitation_id") || params.get("invite"),
      invitationToken: params.get("invitationToken") || params.get("invitation_token"),
    });
    if (captured) {
      sessionStorage.setItem(AUTH_INVITATION_STORAGE_KEY, JSON.stringify(captured));
      const url = new URL(window.location.href);
      ["invitationId", "invitation_id", "invite", "invitationToken", "invitation_token"].forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      return captured;
    }
    captured = normalizePendingAuthInvitation(JSON.parse(sessionStorage.getItem(AUTH_INVITATION_STORAGE_KEY) || "null"));
  } catch (_error) {
    captured = null;
  }
  return captured;
}

function clearPendingAuthInvitation() {
  pendingAuthInvitation = null;
  try { sessionStorage.removeItem(AUTH_INVITATION_STORAGE_KEY); } catch (_error) { /* best effort */ }
}

// 这些码说明服务端再试多少次都是同一个结果（邀请没了/已经结束/不属于这个账号）：
// 继续把它留在 sessionStorage 里，只会让之后每次登录都重试并弹一次失败。
const AUTH_INVITATION_TERMINAL_CODES = new Set([
  "logto_invitation_missing",
  "logto_invitation_not_pending",
  "logto_invitation_mismatch",
  // 这条邀请的收件邮箱不是当前账号：换台设备、重登多少次都是同一个结果。
  "logto_invitation_recipient_mismatch",
  "logto_invitation_organization_missing",
  "logto_invitation_acceptance_mismatch",
  "invitation_stale",
]);

async function activateAcceptedOrganization(auth, payload, fallbackName = "") {
  const organizationId = String(payload?.organizationId || payload?.membership?.tenant_id || payload?.membership?.tenantId || "").trim();
  if (organizationId && typeof auth?.activateOrganization === "function") {
    await auth.activateOrganization(organizationId, {
      name: fallbackName || payload?.invitation?.organizationName || payload?.invitation?.organization?.name,
      roles: payload?.membership?.organization_roles || payload?.invitation?.organizationRoles || payload?.invitation?.roles,
    }, { reason: "invitation-accepted" });
  } else if (typeof auth?.refreshOrganizations === "function") {
    await auth.refreshOrganizations();
  }
  toast("已加入组织，个人组织仍会保留在组织列表中", { duration: 4200 });
}

// 邮件链接的自动接受与平台内的「我收到的邀请」走同一条路径：同一个接口，区别只在
// 后者的 invitationId 是按当前账号邮箱在平台内查到的（/api/auth/invitations/inbox）。
async function acceptOrganizationInvitation(invitationId, organizationName = "") {
  const id = String(invitationId || "").trim();
  if (!id) throw new Error("缺少邀请标识，请刷新页面后重试");
  const payload = await postBackend("/api/auth/invitations/accept", { invitationId: id });
  await activateAcceptedOrganization(globalThis.xhsSpaAuth, payload, organizationName);
  return payload;
}

async function acceptPendingAuthInvitation(auth) {
  if (!pendingAuthInvitation) return null;
  if (pendingAuthInvitationPromise) return pendingAuthInvitationPromise;
  pendingAuthInvitationPromise = (async () => {
    let payload;
    try {
      payload = await postBackend("/api/auth/invitations/accept", pendingAuthInvitation);
    } catch (error) {
      // 只有"再试也没用"的失败才把邀请清掉；网络/限流一类必须保留，
      // 否则断一次网就等于把这条邀请悄悄丢了，用户再也点不到它。
      const code = String(error?.errorCode || "");
      const status = Number(error?.status || 0);
      const terminal = AUTH_INVITATION_TERMINAL_CODES.has(code) || (status >= 400 && status < 500 && status !== 429);
      if (terminal) {
        clearPendingAuthInvitation();
        error.message = `${error.message || "邀请已失效"}（已停止自动重试，请让邀请人重新发送）`;
      }
      throw error;
    }
    clearPendingAuthInvitation();
    await activateAcceptedOrganization(auth, payload);
    return payload;
  })().finally(() => {
    pendingAuthInvitationPromise = null;
  });
  return pendingAuthInvitationPromise;
}

pendingAuthInvitation = capturePendingAuthInvitation();

function resetAuthProvisioningState() {
  authProvisioningCompleted = false;
  authProvisioningAuth = null;
}

// 组织初始化失败时先弄清"到底是谁的问题"（P131）。这一句过去一律是「组织初始化未完成：
// <后端原话>」，于是被封的账号刷新页面看到的是「组织初始化未完成：Bearer authentication
// required」——用户看不出自己是被封了，也看不出该重新登录。
// 分档：
//   ①账号受限（封禁）：按封禁话术说，带到期时间 —— 这是用户真正需要知道的事；
//   ②401/403：这个浏览器已经没有可用的会话凭据（令牌过期或被撤销、或认证层不肯给令牌），
//     那是"重新登录"的事，不是组织初始化的事；
//   ③其余（真·组织初始化失败）保留原口径，别把后端原话吞掉。
async function reportProvisioningFailure(error) {
  const restriction = await loadAccountRestriction();
  if (restriction?.banned) {
    toast(accountRestrictionText(restriction), { kind: "error", duration: 8000 });
    return;
  }
  if (error?.status === 401 || error?.status === 403) {
    toast("登录状态已失效，请重新登录", { kind: "error", duration: 8000 });
    return;
  }
  toast(`组织初始化未完成：${error?.message || "请稍后重试"}`, { kind: "error", duration: 8000 });
}

async function provisionAuthenticatedOrganization(auth) {
  if (authProvisioningPromise) return authProvisioningPromise;
  if (authProvisioningCompleted && authProvisioningAuth === auth) return null;
  authProvisioningPromise = (async () => {
    const result = await postBackend("/api/auth/provision-self", {});
    const organizationId = String(result?.organizationId || result?.organization?.id || "").trim();
    if (!organizationId) throw new Error("组织初始化未返回组织 ID");
    if (typeof auth.activateProvisionedOrganization !== "function") {
      throw new Error("组织初始化未完成：当前认证客户端不支持组织激活");
    }
    await auth.activateProvisionedOrganization(organizationId, {
      name: result?.organization?.name,
      roles: result?.roles || result?.membership?.organization_roles || result?.membership?.organizationRoles,
    });
    authProvisioningCompleted = true;
    authProvisioningAuth = auth;
    return result;
  })().finally(() => {
    authProvisioningPromise = null;
  });
  return authProvisioningPromise;
}

async function authExperienceRequest(path, options = {}) {
  const { invalidField, ...init } = options;
  const headers = { ...(init.headers || {}), "Logto-App-Id": String(globalThis.XHS_LOGTO_CONFIG?.appId || "").trim() };
  if (init.body && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  const response = await fetch(path, { ...init, headers, credentials: "include" });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = String(payload?.code || "");
    // 交互丢了或过时了：prepareAuthExperience 缓存的那个 context 已经死了。不清掉的话
    // 之后每次登录都会拿它重试、每次都撞同一个 400——而且"关掉弹层重开"没用，因为
    // 缓存在模块作用域里，只有整页刷新才重置（实测踩过：对话框反复重开都登不进去）。
    // 作废它，下一次 prepareAuthExperience 就会重新建一次交互。
    if (code === "session.not_found" || code === "session.identifier_not_found") {
      authExperienceContext = null;
    }
    // 排障用：把 Logto 真码打到控制台，界面仍走映射话术。绝不打 body（可能含密码）。
    globalThis.console?.warn?.("[auth-experience]", {
      path,
      status: response.status,
      code,
      appId: headers["Logto-App-Id"] || "",
      invalidField: invalidField || "",
    });
    // 失败原因多半不在 message 里（见 logto-errors.js 的说明），必须过映射。
    // 兜底话术也归映射模块管：调用点自己拼的「认证服务返回 422」就是本轮漏出的那句。
    throw new Error(logtoErrorMessage(payload, response.status, {
      invalidField,
      // 429 的可等待时间只在响应头里，报文里没有；不透传出去就只剩「稍等片刻」（P96）。
      retryAfterSeconds: logtoRetryAfterSeconds(response),
    }));
  }
  return payload;
}

async function prepareAuthExperience(auth, interactionEvent = "SignIn") {
  if (authExperienceContext) return authExperienceContext;
  const signInUrl = await auth.getSignInUrl?.();
  if (!signInUrl) return null;
  const parsed = new URL(signInUrl, location.origin);
  const appId = String(globalThis.XHS_LOGTO_CONFIG?.appId || parsed.searchParams.get("client_id") || "").trim();
  if (appId) {
    try { sessionStorage.setItem("app_id", appId); } catch (_error) { /* best effort */ }
  }
  // The authorization request creates Logto's client-specific interaction
  // cookie. We keep the response body out of the app and only retain cookies.
  await fetch(parsed.toString(), { credentials: "include", redirect: "manual", headers: { Accept: "text/html" } });
  await authExperienceRequest("/api/experience", { method: "PUT", body: JSON.stringify({ interactionEvent }) });
  authExperienceContext = { appId, signInUrl };
  return authExperienceContext;
}

async function registerAuthExperience(auth, { username, email, code, password }) {
  if (!auth.getSignInUrl) {
    await auth.signIn();
    return;
  }
  if (!registerVerificationId) throw new Error("请先点击「发送验证码」");
  await prepareAuthExperience(auth, "Register");
  // 注册只做用户名：Logto 实例的 signUp.identifiers 就是 ["username"]。invalidField 得在这里
  // 说明，否则 Logto 只回一句「请求中 body 无效」，用户看不出是用户名格式不合规。
  await authExperienceRequest("/api/experience/profile", {
    method: "POST",
    invalidField: "username",
    body: JSON.stringify({ type: "username", value: username }),
  });
  // 邮箱不是注册标识，但 Logto 允许在注册交互里把它绑上——代价是必须带一条验证记录：
  // 先用码换记录，再把记录连同邮箱一起提交（实测 profile 带 verificationId 返回 204）。
  // 复用已验证过的记录：Logto 在 verify 成功后立刻 consume passcode（见模块顶部注释），
  // 第二次再 verify 同一条 code 会得到 verification_code.not_found，被前端映射成"验证码
  // 不正确或已过期"。本地只挡密码长度/两次一致，序列/pwned 只有 Logto 才认——这次
  // password.rejected 之后用户改合规密码重提，要复用上次 verify 出来的 recordId。
  let recordId = verifiedRegisterRecordId;
  if (!recordId) {
    const verification = await authExperienceRequest("/api/experience/verification/verification-code/verify", {
      method: "POST",
      body: JSON.stringify({ identifier: { type: "email", value: email }, verificationId: registerVerificationId, code }),
    });
    recordId = String(verification?.verificationId || "");
    if (!recordId) throw new Error("验证码不正确或已过期，请重新获取");
    verifiedRegisterRecordId = recordId;
  }
  await authExperienceRequest("/api/experience/profile", {
    method: "POST",
    invalidField: "email",
    body: JSON.stringify({ type: "email", value: email, verificationId: recordId }),
  });
  await authExperienceRequest("/api/experience/profile", {
    method: "POST",
    invalidField: "password",
    body: JSON.stringify({ type: "password", value: password }),
  });
  await authExperienceRequest("/api/experience/identification", { method: "POST", body: JSON.stringify({}) });
  const result = await authExperienceRequest("/api/experience/submit", { method: "POST" });
  if (!result.redirectTo) throw new Error("注册完成地址缺失");
  location.assign(new URL(result.redirectTo, location.origin).toString());
}

async function submitAuthExperience(auth, identifier, password) {
  if (!auth.getSignInUrl) {
    await auth.signIn();
    return;
  }
  await prepareAuthExperience(auth);
  const type = identifier.includes("@") ? "email" : "username";
  const verification = await authExperienceRequest("/api/experience/verification/password", {
    method: "POST",
    body: JSON.stringify({ identifier: { type, value: identifier }, password }),
  });
  await authExperienceRequest("/api/experience/identification", {
    method: "POST",
    body: JSON.stringify({ verificationId: verification.verificationId }),
  });
  const result = await authExperienceRequest("/api/experience/submit", { method: "POST" });
  if (!result.redirectTo) throw new Error("认证完成地址缺失");
  location.assign(new URL(result.redirectTo, location.origin).toString());
}

// 忘记密码：验证码 → 验证记录 → 设置新密码 → 提交（Logto 完成后会带着 code 回到 /callback，
// 等于顺带把用户登进来）。路由契约对过 Logto 源码：登录方式/忘记密码的策略在
// identification 那步才校验，「设置新密码」那条路由只有 PUT（POST 会 404）。这几步还依赖
// Sign-in Experience 开着 email 登录与忘记密码（scripts/configure_account_center.py --enable-email-signin）。
async function resetAuthExperience(auth, email, code, password, verificationId) {
  if (!verificationId) throw new Error("请先点击「发送验证码」");
  if (!code) throw new Error("请填写邮箱验证码");
  if (!password) throw new Error("请填写新密码");
  // 同 registerAuthExperience：Logto verify 成功即 consume passcode（passcode.ts 里
  // `await consumePasscode(passcode.id)`），第二次再 verify 同一份 code 会得到
  // verification_code.not_found，被映射成"验证码不正确或已过期"。新密码不符合 Logto
  // 策略（序列/pwned）时，本地预校验只挡长度/两次一致，拦下时 verify 已经白跑——
  // 用户换个合规密码重提就撞这条假链路。复用已验证的 recordId。
  let recordId = verifiedResetRecordId;
  if (!recordId) {
    const verification = await authExperienceRequest("/api/experience/verification/verification-code/verify", {
      method: "POST",
      body: JSON.stringify({
        identifier: { type: "email", value: email },
        verificationId,
        code,
      }),
    });
    recordId = String(verification?.verificationId || "");
    if (!recordId) throw new Error("验证码不正确或已过期，请重新获取");
    verifiedResetRecordId = recordId;
  }
  await authExperienceRequest("/api/experience/identification", {
    method: "POST",
    body: JSON.stringify({ verificationId: recordId }),
  });
  await authExperienceRequest("/api/experience/profile/password", {
    method: "PUT",
    body: JSON.stringify({ password }),
  });
  const result = await authExperienceRequest("/api/experience/submit", { method: "POST", body: JSON.stringify({}) });
  // ForgotPassword 这条交互在 Logto 里是"写完密码就收工"：submit 更新完用户密码后直接
  // `ctx.status = 204`，既不下发登录会话、也不给 redirectTo（Logto 的
  // routes/interaction/actions/submit-interaction.ts 的 Forgot Password 分支，
  // 对比 SignIn/Register 分支都会 assignInteractionResults 再回 redirectTo）。
  // 所以"没有 redirectTo"是这条流程的正常成功形态，不是失败——密码此刻已经落库。
  // 以前这里按登录/注册的同款写法 throw「重置完成地址缺失」，把一个内部字眼糊到用户脸上，
  // 用户明明重置成功了却以为出了故障（P140）。
  const redirectTo = String(result?.redirectTo || "");
  if (!redirectTo) return { signedIn: false };
  location.assign(new URL(redirectTo, location.origin).toString());
  return { signedIn: true };
}

// 登录/注册成功后收尾必须由“成功”直接触发，而不是等那次整页跳转顺带完成。
// location.assign 会带我们离开这个文档（去 /callback 换码），本文件随文档一起消失；
// 跳转没真正发生（被拦、被取消、或根本不触发导航）时，这里就是唯一还能收尾的地方——
// 此前这段挂在 `!auth.getSignInUrl` 分支下，而本构建里 auth.getSignInUrl 存在
// （web/spa-auth.js 定义并导出）→ 条件恒不成立，于是只剩“整页跳转”一条路：
// 跳转不落地就永久停在 loading、弹窗不退（P113）。
const AUTH_NAVIGATION_STALL_MS = 8000;
let authNavigationStallTimer = null;

function finishAuthLoginSuccess(syncAuthenticatedUi) {
  closeAuthLoginModal();
  if (typeof syncAuthenticatedUi !== "function") return Promise.resolve();
  // 收尾自己出错不能反过来说成“登录失败”——登录已经成功了。
  return Promise.resolve(syncAuthenticatedUi()).catch((error) => {
    console.error("Authenticated UI sync after sign-in failed", error);
  });
}

// 跳转到底有没有发生不可观测，只能等：文档被替换时这个计时器随文档一起消失，它跑完就说明
// 那次跳转没落地（此时用户其实没登录）。给一句可见提示，别让人对着已经关掉的弹窗以为登录完成了。
function warnIfAuthNavigationStalled(auth) {
  window.clearTimeout(authNavigationStallTimer);
  authNavigationStallTimer = window.setTimeout(() => {
    if (document.hidden) return;
    Promise.resolve(auth?.isAuthenticated?.())
      .then((authenticated) => {
        if (authenticated) return;
        toast("登录未完成，请重新登录（或刷新页面后重试）", { kind: "error", duration: 8000 });
      })
      .catch(() => {});
  }, AUTH_NAVIGATION_STALL_MS);
}

async function openAuthLoginModal(auth, syncAuthenticatedUi) {
  const modal = document.querySelector("#authLoginModal");
  const form = document.querySelector("#authLoginForm");
  const identifierInput = document.querySelector("#authLoginIdentifier");
  const passwordInput = document.querySelector("#authLoginPassword");
  const submit = document.querySelector("#authLoginSubmit");
  const error = document.querySelector("#authLoginError");
  const confirmField = document.querySelector("#authLoginConfirmField");
  const confirmInput = document.querySelector("#authLoginConfirmPassword");
  const emailField = document.querySelector("#authLoginEmailField");
  const emailInput = document.querySelector("#authLoginEmail");
  const codeField = document.querySelector("#authLoginCodeField");
  const codeInput = document.querySelector("#authLoginCode");
  const sendCodeButton = document.querySelector("#authLoginSendCodeBtn");
  const forgotButton = document.querySelector("#authLoginForgot");
  const passwordLabel = document.querySelector("#authLoginPasswordLabel");
  const identifierLabel = document.querySelector("#authLoginIdentifierLabel");
  const identifierHint = document.querySelector("#authLoginIdentifierHint");
  const passwordHint = document.querySelector("#authLoginPasswordHint");
  const switchButton = document.querySelector("#authLoginSwitch");
  const title = document.querySelector("#authLoginTitle");
  const description = document.querySelector("#authLoginDescription");
  if (!modal || !form || !identifierInput || !passwordInput || !submit || !confirmField || !confirmInput || !switchButton || !title || !description) return auth.signIn();
  authExperienceContext = null;
  // 弹层重新打开就清掉上一轮的验证记录：recordId 是 Logto 这次 interaction 内的产物，
  // 下次进来是新 interaction，复用旧记录会撞 session.not_found/verification_code.not_found
  // —— 表现同样是"验证码不正确或已过期"。
  verifiedRegisterRecordId = "";
  verifiedResetRecordId = "";
  const currentFocus = document.activeElement;
  authLoginReturnFocus = currentFocus && currentFocus !== document.body && typeof currentFocus.focus === "function"
    ? currentFocus
    : document.querySelector("#authSignInBtn");
  form.reset();
  if (error) error.textContent = "";
  let mode = "signIn";
  // 重置密码要用验证码换验证记录，换来的记录只在本次弹层会话里有效。
  let resetVerificationId = "";
  const showError = (message) => { if (error) error.textContent = String(message || ""); };
  const enterMode = (next) => {
    mode = next;
    // 每次切换都要换一个新的 Logto interaction（SignIn/Register/ForgotPassword 三种事件
    // 不能共用同一个 interaction），否则会把上一步的流程带进来。
    authExperienceContext = null;
    if (next !== "reset") resetVerificationId = "";
    if (next !== "register") registerVerificationId = "";
    // 切走之后另一条流程的 recordId 也不再属于当前 interaction，一并清掉。
    if (next !== "register") verifiedRegisterRecordId = "";
    if (next !== "reset") verifiedResetRecordId = "";
    // 换模式 = 换一套交互，凭证类输入一律清空：密码框不清空的话，登录时敲的密码会原样
    // 带到注册/重置模式里（反之亦然），用户以为在填新密码，其实提交的是上一个模式的值。
    // 这里不学 renderMode 里那样按模式条件清——条件式正是上次漏掉 password 的原因。
    passwordInput.value = "";
    confirmInput.value = "";
    renderMode();
  };
  const renderMode = () => {
    const isRegister = mode === "register";
    const isReset = mode === "reset";
    title.textContent = isReset ? "重置密码" : isRegister ? "注册薯作台" : "登录薯作台";
    description.textContent = isReset
      ? "重置密码需要向绑定邮箱发送验证码；没有绑定邮箱的账号请联系管理员。"
      : isRegister
        ? "邮箱用于找回密码，注册时会发验证码给你确认。"
        : "登录后可管理团队、组织和内容权限。";
    confirmField.classList.toggle("hidden", !(isRegister || isReset));
    confirmInput.required = isRegister || isReset;
    if (emailField) emailField.classList.toggle("hidden", !isRegister);
    if (emailInput) {
      emailInput.required = isRegister;
      emailInput.value = isRegister ? emailInput.value : "";
    }
    // 注册和重置都要收邮箱验证码；注册收的是新邮箱，重置收的是已绑定邮箱。
    if (codeField) codeField.classList.toggle("hidden", !(isRegister || isReset));
    if (codeInput) {
      codeInput.required = false;
      codeInput.value = isRegister || isReset ? codeInput.value : "";
    }
    // 注册的登录标识只认用户名（邮箱另设必填项，走验证码）。这里不能写"邮箱或用户名"，
    // 否则用户会照着一个走不通的提示把邮箱填进用户名框。规则也必须露出来：Logto 对格式
    // 不合规只回一句「请求中 body 无效」，用户猜不到是不允许这些字符。
    if (identifierLabel) identifierLabel.textContent = isReset ? "绑定邮箱" : isRegister ? "用户名" : "邮箱或用户名";
    if (identifierHint) {
      identifierHint.textContent = isRegister ? "1–128 位，只能用字母、数字和下划线。" : "";
      identifierHint.classList.toggle("hidden", !isRegister);
    }
    if (passwordLabel) passwordLabel.textContent = isReset ? "新密码" : "密码";
    if (passwordHint) {
      // 重置也要设新密码，同一条策略，提示一起给。
      passwordHint.textContent = isRegister || isReset
        ? "至少 8 位，不能是已泄露的常见密码或连续字符（如 12345678）。"
        : "";
      passwordHint.classList.toggle("hidden", !(isRegister || isReset));
    }
    identifierInput.autocomplete = isReset ? "email" : "username";
    identifierInput.placeholder = isReset ? "输入绑定邮箱" : isRegister ? "输入用户名" : "输入邮箱或用户名";
    passwordInput.autocomplete = isRegister || isReset ? "new-password" : "current-password";
    // 重置流程不会顺带登录（Logto 的 ForgotPassword submit 只写密码、不下发会话），
    // 所以按钮不能承诺"并登录"——否则用户以为点完就进去了（P140）。
    submit.querySelector("span").textContent = isReset ? "重置密码" : isRegister ? "注册" : "登录";
    switchButton.textContent = isReset ? "返回登录" : isRegister ? "已有账号？返回登录" : "没有账号？注册";
    const signUpEnabled = globalThis.XHS_LOGTO_CONFIG?.signUpEnabled !== false;
    switchButton.classList.toggle("hidden", !isReset && !signUpEnabled);
    if (forgotButton) forgotButton.classList.toggle("hidden", isReset);
    showError("");
  };
  switchButton.onclick = () => enterMode(mode === "signIn" ? "register" : "signIn");
  if (forgotButton) {
    forgotButton.onclick = () => enterMode("reset");
  }
  if (sendCodeButton) {
    sendCodeButton.onclick = async () => {
      // 注册收的是新邮箱（要绑上去），重置收的是已绑定邮箱（只验证本人），两者收件人来源不同。
      const isRegisterCode = mode === "register";
      const email = String((isRegisterCode ? emailInput?.value : identifierInput.value) || "").trim();
      if (!email.includes("@")) {
        showError(isRegisterCode ? "请先填写邮箱" : "请先填写绑定邮箱");
        return;
      }
      const restore = setButtonLoading(sendCodeButton, "发送中…");
      try {
        // 发码这一步就决定了 interaction 的事件类型，提交时必须复用同一个（见 enterMode 的说明）。
        await prepareAuthExperience(auth, isRegisterCode ? "Register" : "ForgotPassword");
        const interactionEvent = isRegisterCode ? "Register" : "ForgotPassword";
        const result = await authExperienceRequest("/api/experience/verification/verification-code", {
          method: "POST",
          body: JSON.stringify({
            identifier: { type: "email", value: email },
            interactionEvent,
          }),
        });
        const verificationId = String(result?.verificationId || "");
        if (!verificationId) throw new Error("验证码发送失败，请稍后重试");
        if (isRegisterCode) registerVerificationId = verificationId;
        else resetVerificationId = verificationId;
        // 重发时上一次 verify 出来的 recordId 已经随老 passcode 一起被 consume，旧码也
        // 作废了——缓存必须清，否则下次提交会拿一份死记录去走 profile/identification。
        if (isRegisterCode) verifiedRegisterRecordId = "";
        else verifiedResetRecordId = "";
        // 重置模式无法承诺"已发送"：Logto 出于防枚举，对没绑定任何账号的邮箱同样回 200 +
        // verificationId，应用拿不到真实投递结果（实测发件箱没有那封邮件）。注册是确定发了的。
        showError(
          isRegisterCode
            ? "验证码已发送，请查看邮箱（10 分钟内有效）"
            : "已提交重置请求：若该邮箱已绑定账号，验证码会在 10 分钟内送达；长时间收不到，说明该邮箱可能没有绑定账号，请联系管理员。",
        );
      } catch (requestError) {
        if (isRegisterCode) registerVerificationId = "";
        else resetVerificationId = "";
        // 发码失败时同样要把缓存清掉：即使上一份 recordId 还在，这次失败的发送已经
        // 让 prepareAuthExperience 进入了一个不可信的 interaction 状态，继续复用缓存
        // 反而会把失败传递到下一次提交。
        if (isRegisterCode) verifiedRegisterRecordId = "";
        else verifiedResetRecordId = "";
        showError(requestError?.message || "验证码发送失败，请稍后重试");
      } finally {
        restore();
      }
    };
  }
  renderMode();
  modal.classList.remove("hidden");
  modal.setAttribute("aria-hidden", "false");
  const onSubmit = async (event) => {
    event.preventDefault();
    const identifier = String(identifierInput.value || "").trim();
    const password = String(passwordInput.value || "");
    const confirmPassword = String(confirmInput.value || "");
    const email = String(emailInput?.value || "").trim();
    const code = String(codeInput?.value || "").trim();
    if (!identifier || !password || (mode === "register" && !confirmPassword)) {
      // 文案里的账号称谓必须与上面的字段标签一致（登录是"邮箱或用户名"、注册是"用户名"、
      // 重置是"绑定邮箱"），否则用户会照着一个不存在的字段去找。
      if (error) {
        error.textContent = mode === "register"
          ? "请完整填写用户名和密码"
          : mode === "reset"
            ? "请输入绑定邮箱和密码"
            : "请输入邮箱或用户名和密码";
      }
      return;
    }
    // 能本地判的先判掉：这两类让 Logto 判只会回一句没有信息量的话（见 logto-errors.js），
    // 用户名格式错还要白跑一趟网络。字符集与长度必须分开判：合并成一条正则时，超长会先被
    // 那句"只能用字母、数字和下划线"吃掉，用户看不出真正的原因是太长。
    if (mode === "register") {
      if (!/^[A-Za-z0-9_]+$/.test(identifier)) {
        if (error) error.textContent = "用户名只能用字母、数字和下划线，长度 1–128 位";
        return;
      }
      if (identifier.length > 128) {
        // 与 logto-errors.js 的 user.username_too_long 逐字一致：同一个错误不能有两种说法。
        if (error) error.textContent = "用户名太长：最多 128 个字符。";
        return;
      }
    }
    // 注册的邮箱是必填项，且要过邮箱格式这一关——格式错的邮箱 Logto 要到发码/校验那步才回，
    // 用户看到的是一句跟邮箱没关系的话。
    if (mode === "register" && !email) {
      showError("请填写邮箱");
      return;
    }
    if (mode === "register" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError("邮箱格式不正确");
      return;
    }
    if (mode === "register" && !code) {
      showError("请填写邮箱验证码（先点「发送验证码」）");
      return;
    }
    if ((mode === "register" || mode === "reset") && password.length < 8) {
      if (error) error.textContent = "密码至少 8 位";
      return;
    }
    if ((mode === "register" || mode === "reset") && password !== confirmPassword) {
      if (error) error.textContent = "两次输入的密码不一致";
      return;
    }
    submit.disabled = true;
    submit.classList.add("is-loading");
    if (error) error.textContent = "";
    try {
      if (mode === "reset") {
        const outcome = await resetAuthExperience(auth, identifier, String(codeInput?.value || "").trim(), password, resetVerificationId);
        if (outcome && outcome.signedIn === false) {
          // 密码已经重置成功，但这条交互不会顺带登录（见 resetAuthExperience 的说明）。
          // 这里不能说"失败"，也不该走 finishAuthLoginSuccess/warnIfAuthNavigationStalled
          // ——那条路会宣布登录成功、8 秒后再报「登录未完成」。把话指向下一步动作，
          // 并把弹层切回登录模式、把刚重置的账号留在输入框里（P140）。
          if (identifierInput) identifierInput.value = identifier;
          enterMode("signIn");
          description.textContent = "密码已重置，请用新密码登录。";
          toast("密码已重置，请用新密码登录", { duration: 6000 });
          return;
        }
      } else if (mode === "register") {
        await registerAuthExperience(auth, { username: identifier, email, code, password });
      } else {
        await submitAuthExperience(auth, identifier, password);
      }
      // 成功即收尾：关弹窗 + 同步登录态 UI 都按“登录成功”直接做，不再依赖 location.assign
      // 是否真的把页面跳走（详见 finishAuthLoginSuccess 上的说明）。
      await finishAuthLoginSuccess(syncAuthenticatedUi);
      warnIfAuthNavigationStalled(auth);
    } catch (requestError) {
      if (error) error.textContent = requestError?.message || (mode === "register" ? "注册失败，请稍后重试" : "登录失败，请检查账号和密码");
    } finally {
      // 失败路径靠这里复位；成功路径的复位由 closeAuthLoginModal 与这里共同保证。
      // 按钮的复活必须在 finally —— 放在 catch 里时，任何“成功但没收尾”的路径都会永久停在 loading。
      submit.disabled = false;
      submit.classList.remove("is-loading");
    }
  };
  form.onsubmit = onSubmit;
  identifierInput.focus();
}

function closeAuthLoginModal() {
  const modal = document.querySelector("#authLoginModal");
  const form = document.querySelector("#authLoginForm");
  const button = document.querySelector("#authSignInBtn");
  const submit = document.querySelector("#authLoginSubmit");
  window.clearInterval(authLoginPollTimer);
  authLoginPollTimer = null;
  if (form) form.onsubmit = null;
  if (button) button.disabled = false;
  if (submit) {
    submit.disabled = false;
    submit.classList.remove("is-loading");
  }
  if (modal) {
    modal.classList.add("hidden");
    modal.setAttribute("aria-hidden", "true");
  }
  if (authLoginReturnFocus?.isConnected && typeof authLoginReturnFocus.focus === "function") {
    authLoginReturnFocus.focus();
  }
  authLoginReturnFocus = null;
}

const authManagementState = {
  tab: "members",
  loading: false,
  loaded: {},
  members: [],
  invitations: [],
  invitationFilter: "pending",
  error: "",
  loadError: "",
  loadErrorStatus: 0,
  invitationDraft: { email: "", role: "Viewer" },
  invitationDraftOrganizationId: "",
  notice: "",
  invitationLink: "",
  currentUserId: "",
  memberQuery: "",
  memberPage: 1,
  bulkMode: false,
  selected: new Set(),
};

const billingState = {
  loading: false,
  loaded: false,
  error: "",
  meteringEnabled: true,
  membership: null,
  balance: null,
  pricing: null,
  trial: null,
  lowBalance: null,
  rates: null,
  transactions: null,
};

function billingCreditsText(status) {
  const balance = Number(status?.balance?.availableCredits);
  return Number.isFinite(balance) ? String(balance) : "--";
}

function billingCreditsButtonText(status) {
  const balance = Number(status?.balance?.availableCredits);
  if (!Number.isFinite(balance)) return "--";
  const absolute = Math.abs(balance);
  if (absolute < 10000) return String(balance);
  const compact = absolute >= 100000000 ? balance / 100000000 : balance / 10000;
  const suffix = absolute >= 100000000 ? "亿" : "万";
  return `${Number(compact.toFixed(compact < 10 ? 1 : 0))}${suffix}`;
}

function billingMembershipLabel(status) {
  const membership = status?.membership || {};
  const statusText = String(membership.status || "").toLowerCase();
  const expiresAt = Number(membership.expiresAt);
  if (statusText === "active" && Number.isFinite(expiresAt) && expiresAt > 0) {
    return `会员 · ${billingExpiryDateText(expiresAt)} 到期`;
  }
  return statusText === "active" ? "会员" : "未开通会员";
}

// 到期日怎么显示只有这一处：秒/毫秒两种量纲都收（后端给的是秒，但夹具与旧数据见过毫秒）。
function billingExpiryDateText(expiresAt) {
  const value = Number(expiresAt);
  if (!Number.isFinite(value) || value <= 0) return "";
  const date = new Date(value > 10 ** 11 ? value : value * 1000);
  return date.toLocaleDateString("zh-CN");
}

function renderBillingStatus() {
  const creditsNode = $("#billingCredits");
  const creditsSideNode = $("#billingCreditsSide");
  const membershipNode = $("#billingMembership");
  const actionNode = $("#openBillingBtn");
  const status = billingState.membership || billingState.balance || billingState.pricing
    ? {
        membership: billingState.membership,
        balance: billingState.balance,
        pricing: billingState.pricing,
      }
    : null;
  const creditsText = billingCreditsText(status);
  if (creditsNode) creditsNode.textContent = billingCreditsButtonText(status);
  // The sidebar metric is otherwise only refreshed by renderMetrics() on section
  // switches, so without this it keeps showing "--" right after sign-in.
  if (creditsSideNode) creditsSideNode.textContent = creditsText;
  if (membershipNode) membershipNode.textContent = billingMembershipLabel(status);
  if (actionNode) {
    const actionLabel = status?.membership?.status?.toLowerCase?.() === "active" ? "充值" : "开通会员";
    actionNode.querySelector?.("span")?.replaceChildren(document.createTextNode(actionLabel));
    actionNode.title = `积分余额：${creditsText}`;
  }
}

async function loadBillingStatus() {
  if (billingState.loading) return;
  const auth = globalThis.xhsSpaAuth;
  if (!auth?.configured) {
    if (globalThis.xhsSpaAuthReady) await globalThis.xhsSpaAuthReady;
    else return;
  }
  if (!(await auth.isAuthenticated?.().catch(() => false))) return;
  const organizations = auth.organizations?.() || [];
  if (organizations.length && !auth.getActiveOrganizationId?.()) {
    billingState.error = "请先选择组织";
    renderBillingStatus();
    return;
  }
  billingState.loading = true;
  billingState.error = "";
  try {
    const data = await getBackend("/api/billing/status");
    billingState.membership = data.membership || null;
    billingState.balance = data.balance || null;
    billingState.pricing = data.pricing || null;
    billingState.trial = data.trial || null;
    billingState.lowBalance = data.lowBalance || null;
    billingState.meteringEnabled = data.meteringEnabled !== false;
    billingState.rates = data.rates || null;
    billingState.loaded = true;
  } catch (error) {
    billingState.error = error?.message || "会员信息读取失败";
  } finally {
    billingState.loading = false;
    renderBillingStatus();
  }
}

function billingPanelMarkup() {
  if (billingState.error) {
    return `
      <div class="billing-state billing-state-error" role="alert">
        <p>${escapeHtml(billingState.error || "会员信息读取失败")}</p>
        <button type="button" class="ghost-btn" data-billing-retry><span>重新加载</span></button>
      </div>
    `;
  }
  const membership = billingState.membership || {};
  const pricing = billingState.pricing || {};
  const balance = Number(billingState.balance?.availableCredits);
  const memberPrice = Number(pricing.membershipPriceRmb);
  const memberCredits = Number(pricing.membershipCredits);
  const memberDays = Number(pricing.membershipDays);
  const topupPrice = Number(pricing.topupPriceRmb);
  const topupCredits = Number(pricing.topupCredits);
  const memberActive = String(membership.status || "").toLowerCase() === "active";
  const money = (value) => (Number.isFinite(value) ? `${value} 元` : "-- 元");
  const count = (value) => (Number.isFinite(value) ? value.toLocaleString("zh-CN") : "--");
  const creditsText = Number.isFinite(balance) ? balance.toLocaleString("zh-CN") : "--";
  const memberDetail = `开通即赠 ${count(memberCredits)} 积分${Number.isFinite(memberDays) ? `，有效期 ${memberDays} 天` : ""}。`;
  const low = billingState.lowBalance || null;
  const trial = billingState.trial || null;
  const trialCredits = count(Number(trial?.credits));
  const trialDays = Number(trial?.expiresDays);
  const trialNote = trial?.enabled
    ? trial.granted
      ? `试用额度已发放：${trialCredits} 积分，有效期 ${Number.isFinite(trialDays) ? trialDays : "--"} 天。`
      : `新用户首次使用即赠 ${trialCredits} 积分，${Number.isFinite(trialDays) ? trialDays : "--"} 天内有效。`
    : "";
  // 会员与积分都归**组织**，所以这一页的主语是组织不是"我"：先写清"买给谁"，再谈钱。
  // 页面上没主语时（读不到当前组织）不能假装是自己的余额。
  const subject = billingSubject();
  // 多组织的人最容易问的就是"我到底在几个组织里、各自什么情况"——计费页只讲当前组织，
  // 所以这里给出路（入口就在主语那行，与"可在顶栏切换"说的是同一件事的两个方向）。
  const subjectLine = subject.label
    ? `<p class="billing-subject">为 <strong>${escapeHtml(subject.label)}</strong> 购买${
        subject.organizationCount > 1
          ? '<span class="billing-subject-hint">当前组织 · 可在顶栏切换</span><button type="button" class="link-btn" data-billing-open-organizations><span>我的全部组织</span></button>'
          : ""
      }</p>`
    : "";
  const poolNote = subject.label ? `<p class="billing-note">本组织成员共用这一池 积分。</p>` : "";
  // "我在别的组织能买"——只用来决定出路是"切换组织"还是"找管理员"，不点名到具体组织
  // （名字长短不一，点出来只会让这句变啰嗦；切到哪一个是用户自己的事）。
  const purchaseElsewhere = subject.purchasableElsewhere.length > 0;
  // 低额度提醒是"去充值"的召唤。不能购买的人没有这个动作，提醒他等于说一句他做不到的话——
  // 所以只对能购买的人显示（余额本身在上面照样看得到）。
  const lowNote =
    low?.isLow && subject.canPurchase
      ? `额度偏低：当前 ${count(Number(low.availableCredits))} 积分，低于提醒线 ${count(Number(low.threshold))}，建议尽快充值。`
      : "";
  // 购买入口只给能管理本组织的人（档位与后端判权同一份）。其他人看不到按钮，改由**卡片下面
  // 一句话**说明出路——放进单张卡片里会让另一张空着（两张卡不对称，看着像坏了）。
  //
  // 文案与页面其余提示同一个语气：陈述事实 + 给出下一步，不揣测用户做了什么、不用"你可能…"。
  const purchaseActions = subject.canPurchase
    ? `<button type="button" class="primary-btn" data-billing-buy="member"><span>${memberActive ? "续费会员" : "开通会员"}</span></button>`
    : "";
  const topupActions = subject.canPurchase
    ? `<button type="button" class="ghost-btn" data-billing-buy="topup"><span>购买加量包</span></button>`
    : "";
  const noPurchaseNote = subject.canPurchase
    ? ""
    : purchaseElsewhere
      ? `<p class="billing-note billing-note-warn">当前组织「${escapeHtml(subject.label)}」不可购买：你不是该组织的管理员。切换到你有购买权的组织后即可购买。</p>`
      : `<p class="billing-note billing-note-warn">当前组织「${escapeHtml(subject.label)}」不可购买：你不是该组织的管理员。请联系组织管理员开通。</p>`;
  const creditsLabel = subject.label ? `${escapeHtml(subject.label)} · 积分` : "当前积分";
  const membershipLabel = subject.label ? `${escapeHtml(subject.label)} · 会员` : "会员状态";
  return `
    ${subjectLine}
    <div class="billing-overview">
      <div class="billing-stat">
        <span class="billing-label">${creditsLabel}</span>
        <strong class="billing-balance-value">${creditsText}</strong>
      </div>
      <div class="billing-stat">
        <span class="billing-label">${membershipLabel}</span>
        <strong class="billing-membership-value ${memberActive ? "is-active" : "is-inactive"}">${escapeHtml(billingMembershipLabel({ membership }))}</strong>
      </div>
    </div>
    <div class="billing-products">
      <article class="billing-product">
        <div class="billing-product-head">
          <span class="billing-product-name">会员</span>
          <span class="billing-product-price">${money(memberPrice)}</span>
        </div>
        <p class="billing-product-detail">${memberDetail}</p>
        ${purchaseActions}
      </article>
      <article class="billing-product">
        <div class="billing-product-head">
          <span class="billing-product-name">加量包</span>
          <span class="billing-product-price">${money(topupPrice)}</span>
        </div>
        <p class="billing-product-detail">获得 ${count(topupCredits)} 积分，适合已开通会员补充额度。</p>
        ${topupActions}
      </article>
    </div>
    ${noPurchaseNote}
    ${poolNote}
    ${billingState.meteringEnabled === false ? '<p class="billing-note">当前环境未启用用量计量，余额与会员状态暂不可用，以下为默认定价。</p>' : ""}
    ${lowNote ? `<p class="billing-note billing-note-warn">${escapeHtml(lowNote)}</p>` : ""}
    ${trialNote ? `<p class="billing-note">${escapeHtml(trialNote)}</p>` : ""}
    ${billingRatesMarkup()}
    ${billingLedgerMarkup()}
    <p class="billing-hint">支付由虎皮椒提供，微信扫码后积分自动到账。</p>
  `;
}

// 明细只展示最近这么多条：账单页是"对得上账"，不是审计台（审计在管理端）。
const BILLING_LEDGER_LIMIT = 20;

function billingMoneyText(value) {
  const text = String(value ?? "").trim();
  return text ? `${text} 元` : "-- 元";
}

// 计费口径来自后端 rates（单价已含加价倍数），前端不自己算价。
function billingRatesMarkup() {
  const rates = billingState.rates;
  if (!rates) return "";
  return `
    <div class="billing-rates">
      <p class="billing-section-title">计费口径</p>
      <ul class="billing-rate-list">
        <li><span>1 元</span><strong>${escapeHtml(String(rates.creditsPerYuan || "--"))} 积分</strong></li>
        <li><span>出文 · 输入</span><strong>${billingMoneyText(rates.textInputPerMillionRmb)} / 百万 token</strong></li>
        <li><span>出文 · 输出</span><strong>${billingMoneyText(rates.textOutputPerMillionRmb)} / 百万 token</strong></li>
        <li><span>生图 · 1024</span><strong>${billingMoneyText(rates.image1024Rmb)} / 张</strong></li>
        <li><span>生图 · 1536</span><strong>${billingMoneyText(rates.image1536Rmb)} / 张</strong></li>
      </ul>
      <p class="billing-note">出文、模板拆解、知识库问答都属于文本类调用，按实际输入/输出 token 计价；采集与浏览不扣积分。</p>
      <p class="billing-note">生成失败、主动取消或超时未完成都不扣积分：已经冻结的积分会自动放开，账单里对应记一条「未扣费」。任务在冻结之前就取消的不会有这条记录（本来也没扣）。</p>
    </div>
  `;
}

function billingLedgerTime(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// 「谁花的」只出现在消耗类的行上：发放/调整/过期本来就不是"某个人花掉的"，
// 给它们挂一个主体只会让人以为那也是消费。
// 姓名只有**组织管理员**拿得到（后端 `_billing_actor_labels` 只给管理员补 `actorName`），
// 其他人看到的是中性的「本组织成员」——有意不扩隐私面（docs/billing.md §4.4）。
function billingLedgerActorText(row) {
  const kind = String(row?.kind || "");
  if (kind !== "charge" && kind !== "release") return "";
  if (!row?.actorUserId) return "";
  if (row.isSelf) return "你";
  return String(row.actorName || "").trim() || "本组织成员";
}

// 每行说明"这是什么动作、按什么扣的、谁花的、扣完还剩多少"，release 行（未扣费）如实显示 0。
function billingLedgerMarkup() {
  const rows = billingState.transactions;
  if (!Array.isArray(rows)) return "";
  const title = `<p class="billing-section-title">扣费记录</p>`;
  if (!rows.length) {
    return `${title}<p class="billing-note">还没有积分变动记录。</p>`;
  }
  const items = rows
    .slice(0, BILLING_LEDGER_LIMIT)
    .map((row) => {
      const credits = Number(row?.credits) || 0;
      const sign = credits > 0 ? "+" : credits < 0 ? "−" : "";
      const tone = credits > 0 ? "is-plus" : credits < 0 ? "is-minus" : "is-zero";
      // 共用池的焦虑是"这钱是不是我花的"，所以"谁"放在动作名下面那行、与"按什么扣的"并列。
      const detailLine = [billingLedgerActorText(row), String(row?.detail || "")].filter(Boolean).join(" · ");
      const balance = Number(row?.balanceAfter);
      const balanceText = Number.isFinite(balance) ? `余额 ${balance.toLocaleString("zh-CN")}` : "";
      const timeText = billingLedgerTime(row?.createdAt) || "—";
      return `<li class="billing-ledger-row">
        <div class="billing-ledger-main"><strong>${escapeHtml(row?.label || row?.kind || "记录")}</strong>${
          detailLine ? `<small>${escapeHtml(detailLine)}</small>` : ""
        }</div>
        <div class="billing-ledger-side">
          <span class="billing-ledger-credits ${tone}">${sign}${Math.abs(credits).toLocaleString("zh-CN")}</span>
          <small>${escapeHtml([balanceText, timeText].filter(Boolean).join(" · "))}</small>
        </div>
      </li>`;
    })
    .join("");
  return `${title}<ul class="billing-ledger">${items}</ul>`;
}

async function loadBillingTransactions() {
  billingState.transactions = [];
  renderBillingPanel();
  try {
    const data = await getBackend(`/api/billing/transactions?limit=${BILLING_LEDGER_LIMIT}`);
    billingState.transactions = Array.isArray(data.transactions) ? data.transactions : [];
  } catch (_error) {
    /* 明细读不到就不显示这一块，不影响余额与充值入口。 */
    billingState.transactions = null;
  }
  renderBillingPanel();
}

function renderBillingPanel() {
  const content = $("#billingModalContent");
  if (content) content.innerHTML = billingPanelMarkup();
}

function openBillingPanel() {
  const modal = $("#billingModal");
  if (!modal) return;
  renderBillingPanel();
  showModal(modal);
  void loadBillingTransactions();
}

function closeBillingPanel() {
  stopBillingCheckoutPolling();
  billingCheckoutSucceeded = false;
  hideModal($("#billingModal"));
}

// ---- 虎皮椒扫码收银台 ----
let billingCheckoutTimer = null;
let billingCheckoutSucceeded = false;
// 最近一张"还没付"的订单，连同它**下单时所在的组织**。未支付单是绑组织的，切组织时不会跟过来，
// 而用户很容易以为"切了还能继续付"——所以切组织时要靠它提醒（见 pendingBillingOrderSwitchNotice）。
let billingPendingOrder = null;

// 支付有效期的分钟数来自订单报文（后端关单配置 → expiresAt），前端不再写死；
// 拿不到 expiresAt 时只说"扫码支付"，不编造一个时长。
function billingCheckoutHint(order) {
  const expiresAt = Number(order?.expiresAt);
  if (!Number.isFinite(expiresAt)) return "请使用微信扫码支付";
  const minutes = Math.max(1, Math.round((expiresAt - Date.now() / 1000) / 60));
  return `请使用微信扫码支付，请在 ${minutes} 分钟内完成支付`;
}

// 上游的 payUrl 既可能是二维码图片，也可能是"支付页链接"。收银台只按地址形态分派：
// 判不准的一律先当图片渲染，真的渲染不出来再由 onerror 兜底，保证用户总有路可走。
const BILLING_QR_IMAGE_PATH = /\.(png|jpe?g|gif|webp|bmp|svg|avif|ico)$|qrcode|qr_code|qr-code/i;
const BILLING_PAY_PAGE_PATH = /(?:^|\/)(?:pay|paypage|payments?|checkout|cashier)(?:\/)?$/i;

function billingPayUrlKind(payUrl) {
  const url = String(payUrl || "").trim();
  if (!url) return "none";
  if (/^data:image\//i.test(url)) return "image";
  let path = url;
  try {
    path = new URL(url, "http://localhost").pathname;
  } catch (_error) {
    path = url.split("?")[0].split("#")[0];
  }
  if (BILLING_QR_IMAGE_PATH.test(path)) return "image";
  if (BILLING_PAY_PAGE_PATH.test(path)) return "link";
  return "image";
}

// 二维码不可用时的降级面板：说清发生了什么，并给出两条出路（打开支付页 / 重新生成二维码）。
function billingQrFallbackMarkup(payUrl, reason, product) {
  const link = String(payUrl || "").trim();
  const title = reason === "link" ? "请在浏览器中打开支付页完成支付" : "二维码加载失败";
  const hint =
    reason === "link"
      ? "本次订单没有可直接扫描的二维码，请点击下方链接在浏览器中打开支付页。"
      : "二维码没有显示出来，可以重新生成，或点击下方链接在浏览器中打开支付页。";
  return `
    <div class="billing-qr-fallback" data-billing-qr-fallback>
      <p class="billing-qr-fallback-title">${title}</p>
      <p class="billing-qr-fallback-hint">${hint}</p>
      ${link ? `<a class="billing-qr-link" href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer">打开支付页</a>` : ""}
      <button type="button" class="ghost-btn billing-qr-refresh" data-billing-qr-refresh data-billing-product="${escapeHtml(product || "")}"><span>重新生成二维码</span></button>
    </div>
  `;
}

function billingCheckoutMarkup(order, status) {
  // 已到账的订单没有收银台可看：0 元档（免费活动/赠送额度）服务端下单即发放、回调先到时
  // 也一样。这时要直接给成功态——它不含二维码区，也就不会留下一块「正在生成支付二维码…」。
  if (status === "paid") return billingSuccessMarkup();
  const productName = order?.product === "topup" ? "加量包" : "会员";
  const amount = Number(order?.amountRmb);
  const money = Number.isFinite(amount) ? `${amount} 元` : "-- 元";
  const statusText =
    status === "failed" ? "❌ 支付失败" : "⏳ 等待支付中…";
  const qrKind = billingPayUrlKind(order?.payUrl);
  const qrMarkup =
    qrKind === "none"
      ? `<p class="billing-qr-placeholder">正在生成支付二维码…</p>`
      : qrKind === "link"
        ? billingQrFallbackMarkup(order.payUrl, "link", order?.product)
        : `<img class="billing-qr" src="${escapeHtml(order.payUrl)}" alt="支付二维码" data-billing-qr />`;
  const qrUnavailable = qrKind === "link";
  return `
    <div class="billing-checkout">
      <p class="billing-checkout-eyebrow">${qrUnavailable ? "微信支付" : "微信扫码支付"}</p>
      <h3 class="billing-checkout-title">${productName}充值</h3>
      <div class="billing-checkout-amount-row">
        <span>支付金额</span>
        <strong class="billing-checkout-amount">${money}</strong>
      </div>
      <div class="billing-qr-wrap${qrUnavailable ? " is-unavailable" : ""}" data-billing-qr-wrap>
        ${qrMarkup}
      </div>
      ${qrUnavailable ? "" : `<p class="billing-checkout-hint">${billingCheckoutHint(order)}</p>`}
      <p class="billing-checkout-status" data-billing-checkout-status>${qrUnavailable ? "⚠️ 二维码不可用，请按上方提示完成支付" : statusText}</p>
      <div class="billing-checkout-actions">
        <button type="button" class="ghost-btn" data-billing-checkout-cancel><span>取消</span></button>
      </div>
    </div>
  `;
}

// 图片渲染失败时把二维码区换成降级面板。只处理第一张图的第一次失败（图片只判一次），
// 不自动重试、不刷屏；要不要重来由用户点"重新生成二维码"决定。
function bindBillingQrFallback(order) {
  const img = $("#billingModalContent [data-billing-qr]");
  if (!img) return;
  let handled = false;
  const fail = () => {
    if (handled) return;
    handled = true;
    showBillingQrFallback(order, "error");
  };
  img.addEventListener("error", fail);
  if (img.complete && img.naturalWidth === 0) fail();
}

function showBillingQrFallback(order, reason) {
  const wrap = $("#billingModalContent [data-billing-qr-wrap]");
  if (!wrap) return;
  wrap.classList.add("is-unavailable");
  wrap.innerHTML = billingQrFallbackMarkup(order?.payUrl, reason, order?.product);
  $("#billingModalContent .billing-checkout-hint")?.remove();
  const eyebrow = $("#billingModalContent .billing-checkout-eyebrow");
  if (eyebrow) eyebrow.textContent = "微信支付";
  const statusNode = $("[data-billing-checkout-status]");
  if (statusNode) statusNode.textContent = "⚠️ 二维码不可用，请按上方提示完成支付";
}

async function regenerateBillingQr(button) {
  const product = button?.dataset.billingProduct || "member";
  const done = setButtonLoading(button, "正在重新生成…");
  try {
    await createBillingOrder(product, { forceNew: true });
  } catch (error) {
    toast(`创建支付订单失败：${error.message}`, { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

// 支付成功后常驻展示，只能由用户点"完成"关闭（遮罩/X 按钮在成功态都不响应）。
function billingSuccessMarkup() {
  const balance = Number(billingState.balance?.availableCredits);
  const memberActive = String(billingState.membership?.status || "").toLowerCase() === "active";
  const memberText = billingMembershipLabel({ membership: billingState.membership });
  // 回执要写清这笔进的是哪个组织：会员与积分归组织，只报"积分已到账"等于没回答"到哪了"。
  const subject = billingSubject();
  return `
    <div class="billing-checkout billing-success">
      <div class="billing-success-icon" aria-hidden="true">
        <svg viewBox="0 0 52 52" width="56" height="56"><circle cx="26" cy="26" r="25" fill="none" stroke="#16a34a" stroke-width="2"/><path fill="none" stroke="#16a34a" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" d="M14 27l8 8 16-17"/></svg>
      </div>
      <h3 class="billing-success-title">充值成功，积分已到账</h3>
      ${subject.label ? `<p class="billing-success-subject">已为 <strong>${escapeHtml(subject.label)}</strong> 入账</p>` : ""}
      <div class="billing-success-balance">
        <span class="billing-label">${subject.label ? `${escapeHtml(subject.label)} · 积分` : "当前积分"}</span>
        <strong class="billing-balance-value">${Number.isFinite(balance) ? balance.toLocaleString("zh-CN") : "--"}</strong>
      </div>
      ${memberActive ? `<p class="billing-success-member">${escapeHtml(memberText)}</p>` : ""}
      <button type="button" class="primary-btn billing-success-done" data-billing-checkout-done><span>完成</span></button>
    </div>
  `;
}

function stopBillingCheckoutPolling() {
  if (billingCheckoutTimer) {
    clearInterval(billingCheckoutTimer);
    billingCheckoutTimer = null;
  }
}

// 平台治理控制台：6 个视图都由原生表格渲染，编辑走 /api/admin/pricing-configs，
// 不再内嵌后台 iframe。
const adminConsoleState = {
  view: "pricing",
  page: 1,
  pageSize: 20,
  loading: false,
  error: "",
  scope: "",
  storeConfigured: true,
  /* 角色与能力由后端返回（来源 server/admin_roles.py）：前端只渲染，不自己判权。 */
  role: "",
  roleLabel: "",
  /* 能力位有第三态：null＝**尚未到达**。空数组说明不了"确实没有这个能力"还是"还没问到"，
     而两者的处置完全相反（见 adminConsoleHas）——首开那次渲染正卡在这个窗口里（P58）。 */
  capabilities: null,
  permissionTable: [],
  consoleRoles: [],
  canConfigureAi: false,
  /* 手工调额的护栏（单次上限 / 额度有效期）由后端下发，前端不另写一份常数。 */
  creditAdjustLimits: {},
  total: 0,
  items: [],
  /* 账目语境：当前筛选下的笔数与金额合计（后端投影声明了 summary 才有；见 console_reads）。
     订单列表里"一共收了多少钱"是运营第一眼要的，光有总条数答不了。 */
  summary: null,
  loaded: {},
  search: "",
  filterValues: {},
  roleCatalog: [],
};

// 账本 kind → 中文与后端 `billing_service.LEDGER_KIND_LABELS`（用户账单页与导出 CSV 用的同一份）
// **逐字一致**：管理台看到的是同一个租户的同一批流水，措辞不同会让运营与财务对不上账。
// `release` 是「未扣费」不是「释放」——它的 delta 是 0（冻结解除，钱没动），写成「释放」会被
// 读成"钱动过"。tests/test_ledger_kind_labels.py 会把这张表与后端对齐。
const ADMIN_CONSOLE_TRANSACTION_KINDS = {
  grant: "发放",
  charge: "扣费",
  release: "未扣费",
  expire: "过期",
  reversal: "退款冲正",
  /* 平台管理员的手工调额（可正可负）。与「发放」分开：它不是钱到账。 */
  adjust: "额度调整",
};

const ADMIN_CONSOLE_GRANT_SOURCES = {
  membership: "会员",
  topup: "充值",
  manual: "手工发放",
  trial: "试用额度",
};

// 平台级角色的中文名（后端只允许授予这三档，能力见 server/admin_roles.py）。
const ADMIN_CONSOLE_ROLE_LABELS = {
  platform_admin: "平台管理员",
  platform_operator: "运营（只读）",
  platform_finance: "财务",
};

const ADMIN_CONSOLE_RESERVATION_STATUSES = {
  held: "占用中",
  consumed: "已扣费",
  released: "已释放",
  expired: "已过期",
};

const ADMIN_CONSOLE_ORDER_STATUSES = {
  created: "待支付",
  paid: "已支付",
  /* 退款进行中、渠道结果未知（server/billing_service.py 的 refund_payment_order）：钱可能已经
     从渠道退出去，本地却没有回执（渠道超时/传输失败，或账本幂等信号说此前已冲正）。这一格是
     "去渠道后台核对"的待办标记，不能写成「已退款」——那会和 refunded（有渠道受理回执）混成
     同一种结清态。导出 CSV 的同一格走 server/reconciliation.py 的 ORDER_STATUS_LABELS，
     两处文案必须一致（财务会把页面和导出贴在同一张表里对）。 */
  refunding: "退款中（待核对）",
  failed: "支付失败",
  refunded: "已退款",
  cancelled: "已取消",
};

const ADMIN_CONSOLE_MEMBERSHIP_STATUSES = {
  active: "生效中",
  inactive: "未生效",
  expired: "已过期",
};

// 筛选用的状态下拉从上面两张表**派生**（一处定义）：以后加/改状态时下拉自动跟上，
// 不会出现"库里有了新状态，筛选栏里选不到"。
const ADMIN_CONSOLE_ORDER_STATUS_OPTIONS = Object.entries(ADMIN_CONSOLE_ORDER_STATUSES).map(
  ([value, label]) => ({ value, label })
);
const ADMIN_CONSOLE_MEMBERSHIP_STATUS_OPTIONS = Object.entries(ADMIN_CONSOLE_MEMBERSHIP_STATUSES).map(
  ([value, label]) => ({ value, label })
);

/* 商品档位的名字必须和导出文件同源：后端的 PRODUCT_KIND_LABELS（server/reconciliation.py）
   已把 member/topup 译成中文，页面却一直在直接渲染库里的英文枚举——同一张单在页面上是
   member、导出的对账表里是「会员」，运营两边一对就以为导错了（P135）。 */
const ADMIN_CONSOLE_PRODUCT_KINDS = {
  member: "会员",
  topup: "充值",
};

function adminConsoleStatusLabel(map, value) {
  const key = String(value || "").toLowerCase();
  return map[key] || value || "—";
}

// P102：本地冲正与"渠道真的把钱退回去"必须在列表上能区分。修复前所有「已退款」
// 其实都只做了积分冲正，渠道侧一分钱没退，运营却以为订单已经结清。
function adminConsoleOrderStatusLabel(row) {
  const label = adminConsoleStatusLabel(ADMIN_CONSOLE_ORDER_STATUSES, row?.status);
  if (String(row?.status || "").toLowerCase() !== "refunded") return label;
  return String(row?.refundChannel || "") === "succeeded" ? label : `${label}（渠道未执行）`;
}

/* 列表里的状态格：给上面那句文案套一层视觉标记。
   措辞只有一处（adminConsoleOrderStatusLabel），这里只负责"两种退款长得不一样"——
   「渠道未执行」那一格必须比「已退款」抢眼得多，否则财务扫一眼列表就会以为钱已经回去了。
   悬停再补一句它到底差在哪（P102 的同一条口径：本地冲正 ≠ 钱退回去）。 */
function adminConsoleOrderStatusCell(row) {
  const label = adminConsoleOrderStatusLabel(row);
  if (String(row?.status || "").toLowerCase() !== "refunded") return escapeHtml(label);
  if (String(row?.refundChannel || "") === "succeeded") {
    return `<span class="admin-console-order-status admin-console-order-status--refunded" title="渠道已受理退款，货款按原路退回">${escapeHtml(
      label
    )}</span>`;
  }
  return `<span class="admin-console-order-status admin-console-order-status--unsettled" title="只做了本地积分冲正，渠道侧没有退款：这不是结清态，要按渠道后台核对或线下退款"><i data-lucide="alert-triangle"></i>${escapeHtml(
    label
  )}</span>`;
}

// 定价的可编辑字段；服务端用 BillingPricing.from_mapping 逐字段校验。
// 元组第三项是表单控件类型：number = 数字输入，boolean = 复选框。
// isActive 是布尔——之前漏在表单里，结果表格列能看出「启用/停用」状态，但编辑弹层没
// 任何开关可以改它（Bug：点击编辑，字段有状态，但没法编辑启用或者停用状态）。
//
// 单位写进标签：这些字段混着三种量纲（元 / 积分 / 天），只写「文本输入单价」时
// 没人知道填的是"每次调用"还是"每一百万 token"——填错一位就是 100 万倍的价差。
// 单位与 server/billing.py 的计量口径一一对应：
//   * 文本单价 = 元 / 百万 token（DEFAULT_TEXT_INPUT_PER_MILLION）
//   * 图片单价 = 元 / 张（DEFAULT_IMAGE_COST_SMALL/LARGE）
//   * 倍率 = 倍（服务端 _multiplier_decimal）
const ADMIN_CONSOLE_PRICING_FIELDS = [
  ["membershipPriceRmb", "会员价（元）", "number"],
  ["membershipCredits", "会员赠送积分（积分）", "number"],
  ["membershipDays", "会员有效期（天）", "number"],
  ["topupPriceRmb", "充值价（元）", "number"],
  ["topupCredits", "充值赠送积分（积分）", "number"],
  ["textInputPriceRmb", "文本输入单价（元 / 百万 tokens）", "number"],
  ["textOutputPriceRmb", "文本输出单价（元 / 百万 tokens）", "number"],
  ["textMarkup", "文本倍率（倍）", "number"],
  ["image1024BaseRmb", "图片 1024 单价（元 / 张）", "number"],
  ["image1536BaseRmb", "图片 1536 单价（元 / 张）", "number"],
  ["imageMarkup", "图片倍率（倍）", "number"],
  ["isActive", "启用该档定价", "boolean"],
];

function formatAdminConsoleMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  return Number.isFinite(amount) ? `${amount.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")} 元` : "—";
}

function formatAdminConsoleNumber(value) {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toLocaleString("zh-CN") : "—";
}

function formatAdminConsoleTime(value) {
  if (!value) return "—";
  /* 秒与毫秒两种来源都会走到这里：控制台投影统一给 epoch 秒（server/canonical_repositories._timestamp），
     Logto 侧给的是毫秒。先归一成毫秒——按量级判秒/毫秒（阈值与 server/logto_accounts._timestamp
     同一条：1e11 秒＝公元 5138 年，1e11 毫秒＝1973 年），文本按 Date 解析。旧实现"number 一律 ×1000、
     其余直接丢给 Date"会把毫秒显示成 58676 年，也会把数字串（如 "1789108698"）解析成 Invalid Date
     ——整列变「—」。 */
  const text = typeof value === "number" ? "" : String(value).trim();
  const numeric = typeof value === "number" ? value : Number(text);
  /* 0 与后端同义＝没有这个时间（server/logto_accounts._timestamp 对 <=0 直接返回 None）。
     数字串要先在这里拦下：把它落到 Date 解析上会被当成 2000 年（new Date("0")）。 */
  if (Number.isFinite(numeric) && numeric === 0) return "—";
  const milliseconds = Number.isFinite(numeric)
    ? (Math.abs(numeric) > 10 ** 11 ? numeric : numeric * 1000)
    : NaN;
  const date = Number.isFinite(milliseconds)
    ? new Date(milliseconds)
    : new Date(typeof value === "number" ? value : text);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("zh-CN", { dateStyle: "short", timeStyle: "short" });
}

function adminConsoleShortTenantId(id) {
  const text = String(id || "").trim();
  return text.length > 12 ? `${text.slice(0, 6)}…${text.slice(-4)}` : text;
}

// 租户在**筛选栏里**的可读名：优先用本页行里已补好的 tenantName（后端只给当前页补名），
// 没有就退成缩短 id。筛选栏自己不去打 Logto——它是每次渲染都要跑的路径。
function adminConsoleTenantFilterLabel(tenantId) {
  const id = String(tenantId || "").trim();
  if (!id) return "";
  const row = (adminConsoleState.items || []).find((item) => String(item?.tenantId || "") === id);
  const name = String(row?.tenantName || "").trim();
  return name ? `${name}（${adminConsoleShortTenantId(id)}）` : adminConsoleShortTenantId(id);
}

// 用户账号列里"ID"格子的掩码：和租户列同款首 6 + … + 尾 4（< 12 直接全显）。
// 用户 ID 是 Logto 发的不透明串，prefix 决定存储桶，suffix 常是创建序号；
// 排查时这两头比中段更有用——所以中段打码而不是首尾。
function adminConsoleMaskAccountId(id) {
  const text = String(id || "").trim();
  return text.length > 12 ? `${text.slice(0, 6)}…${text.slice(-4)}` : text;
}

// 租户列：名称或缩略 ID，均提供完整 ID 的悬停提示和复制按钮。
// 名字由后端在积分三视图里尽力补上（tenantName），Logto 读不到时留空——前端不自己去猜。
function adminConsoleTenantCell(row) {
  const id = String(row?.tenantId || "").trim();
  const name = String(row?.tenantName || "").trim();
  if (!id) return name ? escapeHtml(name) : "—";
  return `<span class="admin-console-tenant-id"><span class="${name ? "admin-console-tenant-name" : "admin-console-tenant-id-short"}" title="${escapeHtml(id)}">${escapeHtml(name || adminConsoleShortTenantId(id))}</span><button type="button" class="admin-console-copy-btn" data-admin-console-copy="${escapeHtml(id)}" aria-label="复制租户 ID" title="复制租户 ID"><i data-lucide="copy"></i></button></span>`;
}

async function adminConsoleCopyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand("copy")) throw new Error("copy failed");
  } finally {
    textarea.remove();
  }
}

const ADMIN_CONSOLE_VIEWS = [
  {
    key: "pricing",
    label: "计费定价配置",
    path: "/api/admin/pricing-configs",
    editable: true,
    empty: "还没有定价配置",
    emptyHint: "全局默认与租户覆盖会显示在这里。",
    /* 租户管理员只看得到本租户的覆盖行，说"全局默认会显示在这里"就是错的。 */
    tenantEmptyHint: "本租户还没有定价覆盖；未覆盖时按全局默认计价。",
    columns: [
      /* 定价一行有 15 个字段，但横向对比时真正要并排看的只有"哪一份、卖多少钱、开没开"。
         其余（积分/天数/各类单价与倍率/最后修改人/更新时间）收进「详情」（条 5）。 */
      ["作用域", (row) => (row.scope === "global" ? "全局默认" : "租户覆盖")],
      ["租户", (row) => adminConsoleTenantCell(row)],
      ["类型", (row) => row.kind || "—"],
      ["会员价", (row) => formatAdminConsoleMoney(row.membershipPriceRmb)],
      ["状态", (row) => (row.isActive === false ? "停用" : "启用")],
    ],
    detailColumns: [
      ["会员赠送积分（积分）", (row) => formatAdminConsoleNumber(row.membershipCredits)],
      ["会员有效期（天）", (row) => formatAdminConsoleNumber(row.membershipDays)],
      ["充值价（元）", (row) => formatAdminConsoleMoney(row.topupPriceRmb)],
      ["充值赠送积分（积分）", (row) => formatAdminConsoleNumber(row.topupCredits)],
      ["文本输入（元 / 百万 tokens）", (row) => formatAdminConsoleMoney(row.textInputPriceRmb)],
      ["文本输出（元 / 百万 tokens）", (row) => formatAdminConsoleMoney(row.textOutputPriceRmb)],
      ["文本倍率（倍）", (row) => formatAdminConsoleNumber(row.textMarkup)],
      ["图片 1024（元 / 张）", (row) => formatAdminConsoleMoney(row.image1024BaseRmb)],
      ["图片 1536（元 / 张）", (row) => formatAdminConsoleMoney(row.image1536BaseRmb)],
      ["图片倍率（倍）", (row) => formatAdminConsoleNumber(row.imageMarkup)],
      ["最后修改人", (row) => row.updatedBy || "—"],
      ["更新时间", (row) => formatAdminConsoleTime(row.updatedAt)],
    ],
  },
  {
    key: "transactions",
    label: "积分流水",
    path: "/api/admin/credit-transactions",
    // 从行带入：每一行都能「调额」，把该租户直接带进调额弹层。
    rowAction: "credit-adjust",
    // 积分流水/发放/预占三块视图共用同一份租户筛选器（测试用例 33.4）：
    // 客服拿租户号定位是排障最常见路径，没有筛选框时只能翻整本流水找一行，
    // 在多租户场景里几乎不可用。tenantId 是精确匹配（与审计日志的 requestId
    // LIKE 不同），与后端 console_reads 的 ConsoleProjection.filters 对齐。
    filters: [
      { type: "tenantSearch", param: "tenantId", label: "租户" },
    ],
    empty: "还没有积分流水",
    emptyHint: "扣费、发放、释放与过期都会记在这里。",
    columns: [
      ["时间", (row) => formatAdminConsoleTime(row.createdAt)],
      ["租户", (row) => adminConsoleTenantCell(row)],
      ["类型", (row) => ADMIN_CONSOLE_TRANSACTION_KINDS[row.kind] || row.kind || "—"],
      [
        "变动",
        (row) =>
          `<span class="${Number(row.creditsDelta) < 0 ? "admin-console-negative" : "admin-console-positive"}">${
            Number(row.creditsDelta) > 0 ? "+" : ""
          }${formatAdminConsoleNumber(row.creditsDelta)}</span>`,
      ],
      ["余额", (row) => formatAdminConsoleNumber(row.balanceAfter)],
      ["计费", (row) => formatAdminConsoleMoney(row.chargeRmb)],
      ["请求号", (row) => governanceIdentity(row.requestId, { label: "请求号" })],
    ],
  },
  {
    key: "grants",
    label: "积分发放",
    path: "/api/admin/credit-grants",
    // 从行带入：每一行都能「调额」，把该租户直接带进调额弹层。
    rowAction: "credit-adjust",
    /* 这个视图是手工调额的落点：工具栏那只按钮只在它上面出现。 */
    viewAction: "credit-adjust",
    filters: [
      { type: "tenantSearch", param: "tenantId", label: "租户" },
    ],
    empty: "还没有积分发放记录",
    emptyHint:
      "试用、会员与充值的批次由系统写入；平台管理员可用「手工发放 / 扣减」直接给某个租户调额，调完的批次也会显示在这里。",
    columns: [
      ["租户", (row) => adminConsoleTenantCell(row)],
      ["积分", (row) => formatAdminConsoleNumber(row.credits)],
      ["剩余", (row) => formatAdminConsoleNumber(row.remaining)],
      ["来源", (row) => adminConsoleStatusLabel(ADMIN_CONSOLE_GRANT_SOURCES, row.sourceKind)],
      ["来源单号", (row) => row.sourceId || "—"],
      ["到期时间", (row) => formatAdminConsoleTime(row.expiresAt)],
      ["创建时间", (row) => formatAdminConsoleTime(row.createdAt)],
    ],
  },
  {
    key: "reservations",
    label: "积分预占",
    path: "/api/admin/credit-reservations",
    // 从行带入：每一行都能「调额」，把该租户直接带进调额弹层。
    rowAction: "credit-adjust",
    filters: [
      { type: "tenantSearch", param: "tenantId", label: "租户" },
    ],
    empty: "还没有积分预占",
    emptyHint: "AI 任务的预扣与释放会显示在这里。",
    columns: [
      ["租户", (row) => adminConsoleTenantCell(row)],
      ["积分", (row) => formatAdminConsoleNumber(row.credits)],
      ["类型", (row) => row.kind || "—"],
      ["状态", (row) => adminConsoleStatusLabel(ADMIN_CONSOLE_RESERVATION_STATUSES, row.status)],
      ["请求号", (row) => governanceIdentity(row.requestId, { label: "请求号" })],
      ["任务", (row) => row.jobId || row.resourceId || "—"],
      ["创建时间", (row) => formatAdminConsoleTime(row.createdAt)],
      ["释放时间", (row) => formatAdminConsoleTime(row.releasedAt)],
    ],
  },
  {
    key: "orders",
    label: "支付订单",
    path: "/api/admin/payment-orders",
    // 平台管理员可对"已支付"的订单发起退款（后端会再判一次资格）。
    rowAction: "refund",
    // 订单是**账目**：时间区间 / 组织 / 状态三件都要能筛，否则"这段时间收了多少、
    // 哪些单还挂着"只能翻页找。租户走"按名字找"的选择器，不手抄 12 位 ID。
    filters: [
      { type: "tenantSearch", param: "tenantId", label: "租户" },
      { type: "select", param: "status", label: "状态", options: ADMIN_CONSOLE_ORDER_STATUS_OPTIONS },
      { type: "dateRange", fromParam: "createdFrom", toParam: "createdTo", fromLabel: "下单起始", toLabel: "下单截止" },
    ],
    empty: "还没有支付订单",
    emptyHint: "会员与充值的下单、回调结果会显示在这里。",
    columns: [
      /* 主列只留"这一单是谁的、卖的是什么、多少钱、什么状态、什么时候下的"。
         退款三列与渠道/外部单号一起收进「详情」：13 列并排时表格必然横向滚动，
         而横向滚动会把「状态」挤出视口——那恰好是这张表最该被看到的一列（条 5）。 */
      ["创建时间", (row) => formatAdminConsoleTime(row.createdAt)],
      ["租户", (row) => adminConsoleTenantCell(row)],
      ["商品", (row) => adminConsoleStatusLabel(ADMIN_CONSOLE_PRODUCT_KINDS, row.productKind)],
      ["金额", (row) => formatAdminConsoleMoney(row.amountRmb)],
      ["状态", (row) => adminConsoleOrderStatusCell(row)],
    ],
    // 退款原因/操作者/时间由后端订单投影从订单的 callback_json 里读回（P91）。
    // 没有退款记录的订单一律显示「—」，不留空单元格。
    detailColumns: [
      ["积分", (row) => formatAdminConsoleNumber(row.credits)],
      ["天数", (row) => formatAdminConsoleNumber(row.membershipDays)],
      ["渠道", (row) => row.provider || "—"],
      ["外部单号", (row) => row.providerTradeId || "—"],
      ["支付时间", (row) => formatAdminConsoleTime(row.paidAt)],
      ["退款原因", (row) => row.refundReason || "—"],
      ["退款操作人", (row) => row.refundActor || "—"],
      ["退款时间", (row) => formatAdminConsoleTime(row.refundedAt)],
    ],
  },
  {
    key: "accounts",
    label: "用户账号",
    path: "/api/admin/users",
    // 平台级账号处置：角色、挂起、强制改密、踢会话。要 console.accounts
    // （只给平台管理员），运营/财务看得见别的页签但看不到这一行。
    capability: "console.accounts",
    rowAction: "account",
    empty: "还没有账号",
    emptyHint: "平台上的用户账号会显示在这里。",
    columns: [
      ["用户名", (row) => row.username || "—"],
      ["昵称", (row) => row.name || "—"],
      ["邮箱", (row) => row.primaryEmail || "未绑定"],
      // 区分两种 "空"：roles=[] 是真的没分配，rolesUnknown=true 是后端读 Logto 角色列表时
      // 抛了异常（旧实现两条都画成 "—"，把日志都没记的报错伪装成正常空行）。
      [
        "平台角色",
        (row) =>
          row.rolesUnknown
            ? '<span class="admin-console-status--warning" title="角色查询失败，请检查日志或稍后重试">—（查询失败）</span>'
            : (row.roles || []).map((role) => ADMIN_CONSOLE_ROLE_LABELS[role] || role).join("、") || "—",
      ],
      ["状态", (row) => adminConsoleAccountStatusLabel(row)],
      ["密码", (row) => (row.hasPassword ? "已设置" : "—")],
      ["最近登录", (row) => formatAdminConsoleTime(row.lastSignInAt)],
      ["注册时间", (row) => formatAdminConsoleTime(row.createdAt)],
      /* 用户 ID 默认打码，避免满屏裸奔；点短串切全显，旁边放复制按钮。
         短串格式：长 ID 取首 6 + … + 尾 4（避免遮住 prefix/suffix 这种排查时常用的两头）；< 12 直接全显。 */
      [
        "ID",
        (row) => {
          const id = row.id;
          if (!id) return "—";
          const masked = adminConsoleMaskAccountId(id);
          const safeId = escapeHtml(id);
          const safeMasked = escapeHtml(masked);
          return `<span class="admin-console-account-id">
            <button type="button" class="admin-console-account-id-short" data-admin-console-account-id-short="${safeId}" data-revealed="false" title="点击切换显隐">${safeMasked}</button>
            <button type="button" class="admin-console-copy-btn" data-admin-console-account-id-copy="${safeId}" aria-label="复制用户 ID" title="复制 ID"><i data-lucide="copy"></i></button>
          </span>`;
        },
      ],
    ],
  },
  {
    key: "memberships",
    label: "租户会员",
    path: "/api/admin/tenant-memberships",
    // 会员处置（发放/调整到期日）要 console.memberships.write；没有这一档就只是只读列表。
    rowAction: "membership",
    filters: [
      { type: "tenantSearch", param: "tenantId", label: "租户" },
      { type: "select", param: "status", label: "状态", options: ADMIN_CONSOLE_MEMBERSHIP_STATUS_OPTIONS },
      { type: "dateRange", fromParam: "createdFrom", toParam: "createdTo", fromLabel: "开通起始", toLabel: "开通截止" },
    ],
    empty: "还没有租户会员",
    emptyHint: "各租户的会员状态与累计积分会显示在这里。",
    columns: [
      /* 租户列走统一的渲染器：没有可读名时缩短成"首6…尾4"并给复制按钮，
         省得运营对着 12 位 ID 一个字符一个字符抄（条 3 的同一条诉求）。 */
      ["租户", (row) => adminConsoleTenantCell(row)],
      ["状态", (row) => adminConsoleStatusLabel(ADMIN_CONSOLE_MEMBERSHIP_STATUSES, row.status)],
      ["到期时间", (row) => formatAdminConsoleTime(row.expiresAt)],
      ["累计积分", (row) => formatAdminConsoleNumber(row.accumulatedCredits)],
      ["来源订单", (row) => row.sourceOrderId || "—"],
      ["创建时间", (row) => formatAdminConsoleTime(row.createdAt)],
      ["更新时间", (row) => formatAdminConsoleTime(row.updatedAt)],
    ],
  },
  {
    key: "audit",
    label: "审计日志",
    path: "/api/admin/audit-events",
    // 客服排障：只给请求号不够——按"用户 + 时间"缩小范围才是常用路径。
    // 时间窗做成一个日期范围选择器，而不是两个并列的日期输入：
    // 两个独立控件会被误读成"按起始日筛"和"按截止日筛"两条独立过滤，结果查不到东西
    // 还以为是后端没生效（之前的工单复盘）。日期范围用一个组件、中间一个"至"字，
    // 两个端点会互相约束（min/max 联动），任何一头改成空也都保留。
    // 服务端只认投影声明过的字段（console_reads 的 filters），未声明的参数会被忽略。
    filters: [
      { param: "requestId", label: "请求号", placeholder: "请求号" },
      { param: "actorUserId", label: "操作者用户 ID", placeholder: "先到「用户账号」复制用户 ID" },
      {
        type: "dateRange",
        fromParam: "createdFrom",
        toParam: "createdTo",
        fromLabel: "起始日期",
        toLabel: "截止日期",
        separator: "至",
      },
    ],
    empty: "还没有审计事件",
    emptyHint: "接口调用、权限变更与计费结算都会记在这里。",
    columns: [
      ["时间（北京时间）", (row) => governanceAuditTime(row.createdAt)],
      ["租户", (row) => governanceAuditTenant(row)],
      ["操作者", (row) => governanceAuditActor(row)],
      ["操作者 ID", (row) => governanceIdentity(row.actorUserId, { label: "操作者 ID" })],
      ["操作内容", (row) => governanceAuditOperation(row)],
      ["请求号", (row) => governanceIdentity(row.requestId || row.correlationId, { label: "请求号" })],
      ["结果", (row) => governanceAuditResult(row)],
    ],
    detailColumns: [["详细记录", (row) => governanceAuditDetails(row)]],
  },
];

function adminConsoleView(key = adminConsoleState.view) {
  return ADMIN_CONSOLE_VIEWS.find((view) => view.key === key) || ADMIN_CONSOLE_VIEWS[0];
}

async function ensureAdminSession() {
  // The admin mount authenticates from its own cookie, so this response's
  // Set-Cookie has to be stored: without credentials a session would be signed
  // but never honoured.
  await postBackend("/api/admin/session", {}, { credentials: "same-origin" });
}

function adminConsoleScopeText(scope) {
  if (scope === "all") return "数据范围：全部租户";
  if (scope === "tenant") return "数据范围：本租户";
  return "";
}

// 能力位的判据。三态：已知有 / 已知没有 / **尚未到达**（capabilities=null）。
// 前两态照能力集答；"尚未到达"必须按可见处理，不能当 false——按空能力集去藏，正好会把用户
// 本该看到的页签与入口一起藏掉（P58：首开没有【用户账号】，点过别的页签才出现）。它只决定
// "露不露"，真正的判权在服务端：每个动作后端都会再判一次，误露不会换来越权。
function adminConsoleHas(capability) {
  if (!Array.isArray(adminConsoleState.capabilities)) return true;
  return adminConsoleState.capabilities.includes(capability);
}

// 能否配置本组织的 AI 连接（目前是云端知识库）：写权限和能管成员的一致（后端 canConfigureAi）。
// 单机/兼容模式没有鉴权，本地配置就是真相，仍然可以打开。上游接口与生图水印不在这里——
// 那是平台统一配置，只有平台管理员能动（见 isPlatformAdmin）。
function canConfigureOrgAi() {
  if (!globalThis.xhsSpaAuth?.configured) return true;
  return adminConsoleState.canConfigureAi === true;
}

// 能否配置平台侧 AI（上游文本/生图接口与生图水印）。入口只在「治理 → AI 上游与后处理」，
// 那里是平台管理员专属；生成流程撞上"没有上游"时用它决定是打开面板还是提示该找谁——给普通
// 成员打开那个面板等于让他填一个保存必被后端回滚的表单。单机/兼容模式没有鉴权，仍是本机操作员。
function canConfigurePlatformAi() {
  if (!globalThis.xhsSpaAuth?.configured) return true;
  return isPlatformAdmin();
}

// 平台管理员（后端 platform_admin 角色）。用来收敛「平台侧统一配置」的东西：上游文本/生图
// 接口与生图水印后处理。租户自带一套上游就等于绕开积分体系，水印后处理是合规口径，两者都
// 只能平台改（后端 /api/config 再挡一次）。
function isPlatformAdmin() {
  return adminConsoleState.role === "platform_admin";
}

// 能力位落库的**唯一入口**。以前只在管理台那次读里赋值，于是"这个会话进过管理台没有"决定
// 了入口能不能出现（P58/C36：「知识库连接」对从没进过治理台的人永不显示）。现在能力位有两
// 个来源——登录后的 /api/config 与管理台读接口——都从这里进来，进来就重画一遍依赖它的入口。
function applyConsoleCapabilities(data) {
  if (!data || typeof data !== "object") return;
  /* "有才覆盖"：下发能力位的接口都会带齐，缺字段只可能是异常响应；此时清空能力集会直接
     让页签与入口一起消失（P58），保留上一轮的值更接近真相。 */
  if (typeof data.canConfigureAi === "boolean") adminConsoleState.canConfigureAi = data.canConfigureAi;
  if (typeof data.role === "string") adminConsoleState.role = data.role;
  if (typeof data.roleLabel === "string") adminConsoleState.roleLabel = data.roleLabel;
  /* 能力集到场即从"尚未到达"（null）变成"已知"：此后空数组就该真藏了（无权限账号）。 */
  if (Array.isArray(data.capabilities)) adminConsoleState.capabilities = data.capabilities;
  if (Array.isArray(data.permissionTable)) adminConsoleState.permissionTable = data.permissionTable;
  if (Array.isArray(data.consoleRoles)) adminConsoleState.consoleRoles = data.consoleRoles;
  /* 带能力块的响应有两种形状：管理台通用读接口给 canConfigureAi + 能力集，账号列表（/api/admin/users）
     只给能力集。只看 canConfigureAi 会让第二种"能力集落库了却不重画"，页签停在按空能力集画出来的
     那一版（P58：初始打开没有【用户账号】，点过【支付订单】才出现、点【租户会员】又没了）。 */
  if (typeof data.canConfigureAi !== "boolean" && !Array.isArray(data.capabilities)) return;
  return syncCapabilityDependents();
}

// 能力位变化后重画所有依赖它的入口——**唯一出口**。原来"知识库连接"由 init 与管理台渲染
// 各自补一次调用，而能力位在那些时刻还没有值（或还没有新值），谁都没有职责在能力位真的到位
// 之后重画。返回值表示管理台当前视图被换掉了，调用方要重新加载表体。
function syncCapabilityDependents() {
  /* 页签先重画：它按能力过滤，可能把当前视图换掉。 */
  const viewChanged = syncAdminConsoleTabs();
  renderAdminConsoleScope();
  /* 「团队治理 → 知识库连接」不在控制台里，但判据同源（canConfigureAi）。 */
  if (typeof syncOrgKnowledgeEntry === "function") syncOrgKnowledgeEntry();
  return viewChanged;
}

// 能力位是"这次会话是谁"的派生状态：会话结束就清掉，否则同一个标签页里换账号登录会先继承
// 上一个人的入口（无权限成员看到管理员才有的按钮）。下一个账号登录后由 /api/config 重填。
function clearConsoleCapabilities() {
  adminConsoleState.canConfigureAi = false;
  adminConsoleState.role = "";
  adminConsoleState.roleLabel = "";
  /* 会话结束＝能力位退回"尚未到达"：换账号登录后不能拿上一个人的能力集当"已知"。 */
  adminConsoleState.capabilities = null;
  adminConsoleState.permissionTable = [];
  adminConsoleState.consoleRoles = [];
  // 探测缓存跟着能力位一起清：换账号后必须重新判断能不能进治理台。
  adminConsoleProbeOutcome = "";
  syncCapabilityDependents();
}

function renderAdminConsoleScope() {
  const node = $("#adminConsoleScope");
  const view = adminConsoleView();
  // AI 配置（上游接口与生图水印）是**平台统一配置**：租户自带一套上游就绕开了积分体系，
  // 水印后处理是合规口径，所以只给平台管理员入口；租户管理员要配的是组织自己的知识库
  // （团队治理 → 知识库连接）。
  const aiBtn = $("#openAiConfigBtn");
  if (aiBtn) aiBtn.classList.toggle("hidden", !isPlatformAdmin());
  // 「知识库连接」不在控制台里，由 syncCapabilityDependents 统一重画（能力位只有一个出口）。
  // 运营配置（首页公告位）归平台管理员与运营。
  const opsBtn = $("#openOpsConfigBtn");
  if (opsBtn) opsBtn.classList.toggle("hidden", !adminConsoleHas("console.ops.config"));
  const createBtn = $("#adminConsoleCreateBtn");
  if (createBtn) {
    const canCreate = adminConsoleHas("console.pricing.write") && view.editable;
    createBtn.classList.toggle("hidden", !canCreate);
    if (canCreate) {
      // 全局默认行的排序在最前，所以第一页就能判断它是否已存在。
      const label = createBtn.querySelector("span");
      if (label) {
        label.textContent = adminConsoleNewScope() === "global" ? "新建全局默认" : "新建租户覆盖";
      }
    }
  }
  // 对账导出给平台管理员/运营/财务（后端按 console.export 再校验一次）。
  const exportGroup = $("#adminConsoleExportGroup");
  if (exportGroup) exportGroup.classList.toggle("hidden", !adminConsoleHas("console.export"));
  // 手工调额：只给平台管理员，且只在「积分发放」视图里出现——它改的是发放记录，
  // 摆在别的页签上会让人以为在改那个页签的数据。空表时也要在（造余额正是这个场景）。
  const adjustBtn = $("#adminConsoleCreditAdjustBtn");
  if (adjustBtn) {
    const canAdjust =
      adminConsoleHas("console.credits.adjust") && view.viewAction === "credit-adjust";
    adjustBtn.classList.toggle("hidden", !canAdjust);
  }
  // 支付渠道配置是平台级设置，不隶属某个数据视图，所以只看能力位。
  const paymentBtn = $("#adminConsolePaymentChannelBtn");
  if (paymentBtn) {
    paymentBtn.classList.toggle("hidden", !adminConsoleHas("console.payment.config"));
  }
  /* 创建账号只在「用户账号」页签、且有能力位时出现（P81：管理员不输密码）。 */
  const createAccountBtn = $("#adminConsoleCreateAccountBtn");
  if (createAccountBtn) {
    createAccountBtn.classList.toggle(
      "hidden",
      !(view.key === "accounts" && adminConsoleHas("console.accounts.lifecycle"))
    );
  }
  /* 会员发放/调整到期日只在「租户会员」页签出现，按 console.memberships.write 门控。 */
  const canManageMembership = adminConsoleHas("console.memberships.write");
  const grantBtn = $("#adminConsoleGrantMembershipBtn");
  if (grantBtn) grantBtn.classList.toggle("hidden", !(view.key === "memberships" && canManageMembership));
  const expiryBtn = $("#adminConsoleSetExpiryBtn");
  if (expiryBtn) expiryBtn.classList.toggle("hidden", !(view.key === "memberships" && canManageMembership));
  if (!node) return;
  const scope = adminConsoleScopeText(adminConsoleState.scope);
  /* 单个视图失败不代表「数据范围」读不到：能读到就用已知范围，别把一个辅助标签变成第二个报错点。
     没有已知范围时要区分「还没读回来」和「读回来了但确实没有」——都写成"加载中…"就是永久卡住
     的观感（P58 的第二半症状）。 */
  node.textContent =
    [adminConsoleState.roleLabel, scope].filter(Boolean).join(" · ") ||
    (adminConsoleState.loading ? "数据范围：加载中…" : "数据范围：未知");
}

const ADMIN_CONSOLE_EXPORT_LABELS = {
  orders: "订单",
  paid: "到账",
  grants: "积分发放",
  consumption: "积分消耗",
};

// 服务端把中文名放在 RFC 5987 的 filename* 里，并另给一个 ASCII 兜底 filename。
// 优先取前者，否则浏览器保存下来的还是英文名（P46：导出的文件名是英文）。
function adminConsoleExportFilename(disposition, dataset) {
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch (_error) {
      /* 编码异常就落到 ASCII 兜底，别让改名失败连带整个导出失败 */
    }
  }
  const plain = /filename="([^"]+)"/.exec(disposition);
  return plain ? plain[1] : `reconciliation-${dataset}.csv`;
}

async function downloadAdminConsoleExport(dataset) {
  const label = ADMIN_CONSOLE_EXPORT_LABELS[dataset] || dataset;
  try {
    const response = await backendFetch(
      `/api/admin/reconciliation.csv?dataset=${encodeURIComponent(dataset)}`
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // 走错误边界：导出的失败话术与 401 的处置和其它请求同源（见 core/client.js）。
      throw backendError(data, response.status, response);
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const filename = adminConsoleExportFilename(disposition, dataset);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast(`已导出${label}对账表`);
  } catch (error) {
    toast(`导出${label}失败：${error.message}`, { kind: "error", duration: 6000 });
  }
}

function bindAdminConsoleExport() {
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-admin-console-export]");
    if (!button) return;
    downloadAdminConsoleExport(button.getAttribute("data-admin-console-export"));
  });
}

function adminConsoleNewScope() {
  return adminConsoleState.items.some((row) => row.scope === "global") ? "tenant" : "global";
}

function adminConsoleVisibleViews() {
  /* 视图声明自己需要的能力：后端会 403 的页签不露出来，省得点进去必然报错。判据走
     adminConsoleHas，所以能力位"尚未到达"时一律露出来——那正是首开缺【用户账号】的成因（P58）。 */
  return ADMIN_CONSOLE_VIEWS.filter((view) => !view.capability || adminConsoleHas(view.capability));
}

function renderAdminConsoleTabs() {
  const tabs = $("#adminConsoleTabs");
  if (!tabs) return;
  const previousTabList = tabs.querySelector?.(".admin-console-tabs-list");
  const previousScrollLeft = previousTabList?.scrollLeft || 0;
  const views = adminConsoleVisibleViews();
  if (!views.some((view) => view.key === adminConsoleState.view) && views.length) {
    /* 当前视图被隐藏（例如平台角色被回收）时退回第一个可见视图，别停在看不见的 tab 上。 */
    adminConsoleState.view = views[0].key;
    adminConsoleState.page = 1;
    adminConsoleState.error = "";
  }
  const tabMarkup = views.map((view) => {
    const active = view.key === adminConsoleState.view;
    return `<button type="button" class="admin-console-tab${active ? " active" : ""}" role="tab" data-admin-console-view="${escapeHtml(
      view.key
    )}" aria-selected="${active}" aria-label="${escapeHtml(view.label)}"><span class="admin-console-tab-label">${escapeHtml(view.label)}</span>${view.key === "pricing" ? '<span class="admin-console-tab-label-mobile" aria-hidden="true">计费定价</span>' : ""}</button>`;
  }).join("");
  tabs.innerHTML = `<button type="button" class="admin-console-tabs-arrow" data-admin-console-tab-scroll="-1" aria-label="向左查看平台治理页签" title="向左查看页签"><i data-lucide="chevron-left"></i></button><div class="admin-console-tabs-list">${tabMarkup}</div><button type="button" class="admin-console-tabs-arrow" data-admin-console-tab-scroll="1" aria-label="向右查看平台治理页签" title="向右查看页签"><i data-lucide="chevron-right"></i></button>`;
  if (typeof refreshIcons === "function") refreshIcons();
  const tabList = tabs.querySelector?.(".admin-console-tabs-list");
  if (tabList) tabList.scrollLeft = previousScrollLeft;
  const syncTabArrows = () => {
    if (!tabList) return;
    const canScrollLeft = tabList.scrollLeft > 1;
    const canScrollRight = tabList.scrollLeft + tabList.clientWidth < tabList.scrollWidth - 1;
    tabs.querySelector?.('[data-admin-console-tab-scroll="-1"]')?.toggleAttribute("disabled", !canScrollLeft);
    tabs.querySelector?.('[data-admin-console-tab-scroll="1"]')?.toggleAttribute("disabled", !canScrollRight);
  };
  tabList?.addEventListener("scroll", syncTabArrows, { passive: true });
  globalThis.requestAnimationFrame?.(() => {
    syncTabArrows();
  });
  tabs.onclick = (event) => {
    const scrollButton = event.target.closest?.("[data-admin-console-tab-scroll]");
    if (scrollButton) {
      if (!tabList) return;
      const direction = Number(scrollButton.getAttribute("data-admin-console-tab-scroll")) || 1;
      tabList.scrollLeft += direction * Math.max(1, tabList.clientWidth + 4);
      syncTabArrows();
      return;
    }
    const button = event.target.closest("[data-admin-console-view]");
    if (!button) return;
    const key = button.getAttribute("data-admin-console-view");
    if (key === adminConsoleState.view) return;
    adminConsoleState.view = key;
    adminConsoleState.page = 1;
    adminConsoleState.error = "";
    renderAdminConsole();
  };
}

function adminConsolePagerHtml(total, page, pageSize) {
  // 简单分页查询面板：共 X 条 · 第 N/M 页 + 跳页 + 上一页/下一页 + 每页条数。
  // 之前只显示「共 X 条 · 第 N/M 页」+ 翻页按钮，但表大时根本不知道能切页大小、
  // 想跳到第 8 页也要一路点下一页——审计日志一天能上万条，靠两个按钮翻不到底。
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const sizeOptions = [20, 50, 100, 200];
  const sizeOptionsHtml = sizeOptions
    .map((size) => `<option value="${size}"${size === pageSize ? " selected" : ""}>${size} 条/页</option>`)
    .join("");
  return `<div class="admin-console-pager">
    <span class="admin-console-pager-info">共 ${formatAdminConsoleNumber(total)} 条 · 第 ${page} / ${pageCount} 页</span>
    <span class="admin-console-pager-actions">
      <label class="admin-console-pager-size">每页
        <select data-admin-console-page-size aria-label="每页条数">${sizeOptionsHtml}</select>
      </label>
      <button type="button" data-admin-console-page="prev"${page <= 1 ? " disabled" : ""}>上一页</button>
      <label class="admin-console-pager-jump">跳到第 <input type="number" min="1" max="${pageCount}" value="${page}" data-admin-console-page-jump aria-label="跳到第几页" /> 页</label>
      <button type="button" data-admin-console-page="next"${page >= pageCount ? " disabled" : ""}>下一页</button>
    </span>
  </div>`;
}

function adminConsoleFilterFields(view) {
  if (Array.isArray(view.filters) && view.filters.length) return view.filters;
  if (view.searchParam) {
    return [{ param: view.searchParam, label: view.searchPlaceholder || "筛选", placeholder: view.searchPlaceholder }];
  }
  return [];
}

function adminConsoleHasActiveFilter() {
  const values = adminConsoleState.filterValues || {};
  if (Object.values(values).some((value) => String(value ?? "").trim())) return true;
  return Boolean(String(adminConsoleState.search || "").trim());
}

// 筛选空态：与"本来就没数据"区分开，说清匹配方式与下一步。
// 租户筛选是精确匹配（不是模糊搜索），查不到多半是 ID 不完整或打错。
function adminConsoleFilteredEmpty(view) {
  const fields = adminConsoleFilterFields(view);
  const tenantField = fields.find((field) => field.param === "tenantId");
  const tenantValue = tenantField ? String(adminConsoleState.filterValues.tenantId || "").trim() : "";
  if (tenantField && tenantValue) {
    return {
      icon: "search",
      title: "没有匹配的租户记录",
      hint: "「租户」筛选按租户 ID 精确匹配，不做模糊搜索；查不到通常是 ID 不完整或打错。请核对/复制完整租户 ID 再查，或点「重置」清空筛选查看全部。",
    };
  }
  return {
    icon: "search",
    title: "没有符合筛选条件的记录",
    hint: "换个筛选条件再查，或点「重置」清空筛选查看全部。",
  };
}



function adminConsoleSearchHtml(view) {
  const fields = adminConsoleFilterFields(view);
  if (!fields.length) return "";
  const isAudit = view.key === "audit";
  const inputs = fields
    .map((field) => {
      if (field.type === "dateRange") {
        const fromValue = adminConsoleState.filterValues[field.fromParam] || "";
        const toValue = adminConsoleState.filterValues[field.toParam] || "";
        const fromId = `adminConsoleFilter_${field.fromParam}`;
        const toId = `adminConsoleFilter_${field.toParam}`;
        const separator = escapeHtml(field.separator || "至");
        // 一个可视组件：起始日期 [至] 截止日期；两端通过 min/max 联动，避免选了反向区间还误以为生效。
        const range = `<div class="admin-console-search-range" role="group" aria-label="${escapeHtml(field.fromLabel || "起始日期")} ${separator} ${escapeHtml(field.toLabel || "截止日期")}">
          <input type="date" id="${fromId}" data-console-filter="${escapeHtml(field.fromParam)}" data-range-pair="${escapeHtml(field.toParam)}" data-range-edge="from" value="${escapeHtml(fromValue)}" placeholder="${escapeHtml(field.fromLabel || "起始日期")}" aria-label="${escapeHtml(field.fromLabel || "起始日期")}" />
          <span class="admin-console-search-range-sep" aria-hidden="true">${separator}</span>
          <input type="date" id="${toId}" data-console-filter="${escapeHtml(field.toParam)}" data-range-pair="${escapeHtml(field.fromParam)}" data-range-edge="to" value="${escapeHtml(toValue)}" placeholder="${escapeHtml(field.toLabel || "截止日期")}" aria-label="${escapeHtml(field.toLabel || "截止日期")}" />
        </div>`;
        return isAudit ? `<div class="governance-filter governance-filter--date"><div class="governance-filter-heading"><span>日期范围</span><small>北京时间，包含起止当天</small></div>${range}</div>` : range;
      }
      // 状态这类**有限取值**的筛选给下拉：让运营手打 "paid"/"已支付" 只会打错，
      // 而后端只认库里的英文枚举，打错了就是"没有匹配记录"，还以为是没数据。
      if (field.type === "select") {
        const current = String(adminConsoleState.filterValues[field.param] || "");
        const options = (field.options || [])
          .map(
            (option) =>
              `<option value="${escapeHtml(option.value)}"${option.value === current ? " selected" : ""}>${escapeHtml(option.label)}</option>`
          )
          .join("");
        return `<select id="adminConsoleFilter_${field.param}" data-console-filter="${field.param}" aria-label="${escapeHtml(field.label || "筛选")}">
          <option value="">${escapeHtml(field.allLabel || "全部")}</option>${options}
        </select>`;
      }
      // 「按名字找租户」：租户 ID 是 12 位随机串，抄错一位就查成空（33.4 的那条诉求）。
      // 选中后写进隐藏的 data-console-filter 输入框——收集逻辑与其它字段完全一致。
      if (field.type === "tenantSearch") {
        const current = String(adminConsoleState.filterValues[field.param] || "");
        const currentLabel = current ? adminConsoleTenantFilterLabel(current) : "";
        return `<div class="admin-console-tenant-filter" data-tenant-search="${escapeHtml(field.param)}">
          <input type="search" id="adminConsoleFilter_${field.param}_search" value="${escapeHtml(currentLabel)}"
            placeholder="${escapeHtml(field.placeholder || "按组织名或租户 ID 找")}"
            aria-label="${escapeHtml(field.label || "租户")}" autocomplete="off" />
          <input type="hidden" id="adminConsoleFilter_${field.param}" data-console-filter="${escapeHtml(field.param)}" value="${escapeHtml(current)}" />
          <div class="admin-console-tenant-results hidden" id="adminConsoleFilter_${field.param}_results"></div>
        </div>`;
      }
      const value = adminConsoleState.filterValues[field.param] || (fields.length === 1 ? adminConsoleState.search : "");
      const type = field.type === "date" ? "date" : "search";
      const label = field.placeholder || field.label || "筛选";
      const input = `<input type="${type}" id="adminConsoleFilter_${field.param}" data-console-filter="${field.param}" value="${escapeHtml(
        value
      )}" placeholder="${escapeHtml(label)}" aria-label="${escapeHtml(field.label || label)}" />`;
      return isAudit ? `<label class="governance-filter"><span class="governance-filter-heading">${escapeHtml(field.label)}</span>${input}</label>` : input;
    })
    .join("");
  // 重置按钮把 filterValues / search / 输入框都清空，再触发一次加载。
  // 不放在「查询」按钮的位置——它俩职责完全不同（提交 vs 清空），合在一起反而容易误点。
  const isCommerceFilter = view.key === "orders" || view.key === "memberships";
  return `<div class="admin-console-search${isAudit ? " governance-audit-search" : ""}${isCommerceFilter ? " admin-console-search--commerce" : ""}">
    ${inputs}
    <button type="button" class="ghost-btn" id="adminConsoleSearchBtn"><i data-lucide="search"></i><span>查询</span></button>
    <button type="button" class="ghost-btn" id="adminConsoleResetBtn" title="清空所有筛选条件"><i data-lucide="rotate-ccw"></i><span>重置</span></button>
  </div>`;
}

// 筛选栏里的租户搜索：输入组织名/ID → 选一条 → 把 id 写进隐藏输入框。
// 复用调额弹层那套取数（`adminConsoleTenantSearchItems`），不另写一份租户搜索。
function bindAdminConsoleTenantFilter(field) {
  const search = $(`#adminConsoleFilter_${field.param}_search`);
  const hidden = $(`#adminConsoleFilter_${field.param}`);
  const results = $(`#adminConsoleFilter_${field.param}_results`);
  if (!search || !hidden || !results) return;
  const close = () => {
    results.classList.add("hidden");
    results.innerHTML = "";
  };
  search.oninput = async () => {
    const query = String(search.value || "").trim();
    // 手改了名字等于不再指向原来那个租户：先清掉旧的精确值，免得"看着是张三、查的是李四"。
    if (hidden.value && query !== adminConsoleTenantFilterLabel(hidden.value)) hidden.value = "";
    if (!query) {
      close();
      return;
    }
    results.classList.remove("hidden");
    results.innerHTML =
      '<div class="admin-console-tenant-result admin-console-tenant-result--status">搜索中…</div>';
    try {
      results.innerHTML = adminConsoleTenantResultsHtml(
        await adminConsoleTenantSearchItems(query),
        "没有匹配的租户，可直接按租户 ID 精确筛选",
        "filter"
      );
    } catch (error) {
      results.innerHTML =
        '<div class="admin-console-tenant-result admin-console-tenant-result--status">搜索失败：' +
        escapeHtml(error?.message || "请稍后重试") +
        "</div>";
    }
  };
  results.onclick = (event) => {
    const item = event.target.closest("[data-admin-console-tenant]");
    if (!item) return;
    const id = String(item.dataset.adminConsoleTenant || "").trim();
    hidden.value = id;
    search.value = adminConsoleTenantFilterLabel(id) || id;
    close();
  };
  search.onkeydown = (event) => {
    if (event.key === "Escape") close();
  };
  document.addEventListener("click", (event) => {
    if (!results.classList.contains("hidden") && !results.contains(event.target) && event.target !== search) {
      close();
    }
  });
}

function bindAdminConsoleSearch(view) {
  const fields = adminConsoleFilterFields(view);
  if (!fields.length) return;
  const button = $("#adminConsoleSearchBtn");
  const inputs = fields
    .flatMap((field) => {
      if (field.type === "dateRange") {
        const fromInput = $(`#adminConsoleFilter_${field.fromParam}`);
        const toInput = $(`#adminConsoleFilter_${field.toParam}`);
        // 起止两端互相约束 min/max：选了起始日后再去点截止日，浏览器自带的选择器不会弹出
        // 早于起始日的日期；反之亦然。空值代表「不限」，别互相把对方锁死。
        if (fromInput && toInput) {
          toInput.min = fromInput.value || "";
          fromInput.max = toInput.value || "";
          fromInput.addEventListener("change", () => {
            toInput.min = fromInput.value || "";
          });
          toInput.addEventListener("change", () => {
            fromInput.max = toInput.value || "";
          });
        }
        return [fromInput, toInput].filter(Boolean);
      }
      const input = $(`#adminConsoleFilter_${field.param}`);
      return input ? [input] : [];
    });
  if (!button || !inputs.length) return;
  // 「按名字找租户」：选中即把租户 id 写进隐藏输入框（它就是上面 inputs 里的那一个），
  // 所以查询/重置两条路都与其它字段共用同一套收集逻辑，不另开分支。
  fields
    .filter((field) => field.type === "tenantSearch")
    .forEach((field) => bindAdminConsoleTenantFilter(field));
  const collect = () => {
    // 从**当前值**出发再覆盖可见输入框：具名筛选（如 `refundUnsettled`，由「去处理」
    // 按钮写入）没有对应的输入框，否则一点「查询」就被清掉了——那正是"点了去处理、
    // 再点查询就变回全部"的由来。
    const next = { ...adminConsoleState.filterValues };
    inputs.forEach((input) => {
      next[input.dataset.consoleFilter] = String(input.value || "").trim();
    });
    return next;
  };
  const submit = () => {
    const next = collect();
    if (next.createdFrom && next.createdTo && next.createdFrom > next.createdTo) {
      toast("起始日期不能晚于截止日期，请调整日期范围。", { kind: "error" });
      return;
    }
    if (JSON.stringify(next) === JSON.stringify(adminConsoleState.filterValues)) return;
    adminConsoleState.filterValues = next;
    adminConsoleState.search = next[fields[0].param] || "";
    adminConsoleState.page = 1;
    loadAdminConsole();
  };
  button.onclick = submit;
  inputs.forEach((input) => {
    input.onkeydown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        submit();
      }
    };
  });
  const resetButton = $("#adminConsoleResetBtn");
  if (resetButton) {
    resetButton.onclick = () => {
      // 先把 DOM 输入框清空（避免 submit 拿到的还是旧值），再统一回填状态并重新加载。
      inputs.forEach((input) => {
        input.value = "";
        input.removeAttribute("max");
        input.removeAttribute("min");
      });
      adminConsoleState.filterValues = {};
      adminConsoleState.search = "";
      adminConsoleState.page = 1;
      loadAdminConsole();
    };
  }
}

function renderAdminConsoleNative(view) {
  const state = adminConsoleState;
  if (state.loading && !state.loaded[view.key]) {
    return `<div class="auth-management-loading"><span class="lucide" data-lucide="loader-circle"></span><p>正在加载${escapeHtml(
      view.label
    )}…</p></div>`;
  }
  if (state.error) {
    return `<div class="auth-empty"><strong>加载失败</strong><span>${escapeHtml(state.error)}</span></div>`;
  }
  const notice = state.storeConfigured
    ? ""
    : `<div class="admin-console-notice"><span class="lucide" data-lucide="info"></span><span>当前环境未接入计费存储，读数与流水暂为空。</span></div>`;
  const search = adminConsoleSearchHtml(view);
  const summaryBar = adminConsoleSummaryHtml(view);
  if (!state.items.length) {
    const filtered = adminConsoleHasActiveFilter() ? adminConsoleFilteredEmpty(view) : null;
    const emptyIcon = filtered ? filtered.icon : "inbox";
    const emptyTitle = filtered ? filtered.title : view.empty;
    const emptyHint = filtered
      ? filtered.hint
      : state.scope === "tenant" && view.tenantEmptyHint
        ? view.tenantEmptyHint
        : view.emptyHint;
    return `${notice}${search}<div class="auth-empty"><span class="lucide" data-lucide="${emptyIcon}"></span><strong>${escapeHtml(
      emptyTitle
    )}</strong><span>${escapeHtml(emptyHint)}</span></div>`;
  }
  const columns =
    view.editable && adminConsoleHas("console.pricing.write")
      ? [
          ...view.columns,
          [
            "操作",
            (row) =>
              `<button type="button" class="admin-console-row-action" data-admin-console-edit="${escapeHtml(
                row.id
              )}">编辑</button>`,
          ],
        ]
      : view.rowAction === "refund" && adminConsoleHas("console.refund")
        ? [
            ...view.columns,
            [
              "操作",
              (row) =>
                String(row.status) === "paid"
                  ? `<button type="button" class="admin-console-row-action" data-admin-console-refund="${escapeHtml(
                      row.id
                    )}">退款</button>`
                  : "—",
            ],
          ]
        : view.rowAction === "account" && adminConsoleHas("console.accounts")
          ? [
              ...view.columns,
              [
                "操作",
                (row) => {
                  const id = escapeHtml(row.id);
                  const restriction = row.restriction || {};
                  if (restriction.deleted) return `<span class="admin-console-row-actions">已删除</span>`;
                  /* 封禁按钮是"开/关"两态：已经在手的那一档给"解除"，还没给的那一档给"封禁"。
                     生命周期三件套（封禁/账号封禁/软删）只有平台管理员有能力位。 */
                  const lifecycle = adminConsoleHas("console.accounts.lifecycle");
                  const restrictedBanned = restriction.banKind === "restricted";
                  const loginBanned = restriction.banKind === "login";
                  /* 三个生命周期动作（封禁/账号封禁/软删）+ 它们的解除项一律用红色：
                     - 权限封禁即使"解除"也是带审计后果的恢复动作，不能伪装成中性按钮
                     - 用户裁决（2026-09-17）：整个生命周期族都属于"碰就留痕"的范围
                     强制改密 / 踢出会话 维持中性——它们不写生命周期审计，是会话类动作。 */
                  const primary = `<button type="button" class="admin-console-row-action admin-console-row-action--accent" data-admin-console-roles="${id}">修改角色</button>`;
                  const menuItems = [];
                  if (lifecycle) {
                    menuItems.push(
                      { danger: true, label: restrictedBanned ? "解除权限封禁" : "权限封禁", action: restrictedBanned ? "unban" : "ban" },
                      { danger: true, label: loginBanned ? "解除账号封禁" : "账号封禁", action: loginBanned ? "account-unban" : "account-ban" },
                      { danger: true, label: "软删", action: "soft-delete" }
                    );
                  }
                  menuItems.push(
                    { danger: true, label: "强制改密", action: "force-password-change" },
                    { danger: true, label: "踢出会话", action: "revoke-sessions" }
                  );
                  const menuId = `admin-console-account-menu-${id}`;
                  const menuButtons = menuItems
                    .map((item) => `<button type="button" class="admin-console-actions-item${item.danger ? " admin-console-actions-item--danger" : ""}" data-admin-console-account="${id}" data-account-action="${escapeHtml(item.action)}">${escapeHtml(item.label)}</button>`)
                    .join("");
                  const more = `<span class="admin-console-actions-menu">
                    <button type="button" class="admin-console-row-action admin-console-actions-trigger" aria-haspopup="menu" aria-expanded="false" data-admin-console-account-menu="${id}">更多<i data-lucide="chevron-down"></i></button>
                    <span class="admin-console-actions-menu-pop" role="menu" id="${menuId}" hidden>${menuButtons}</span>
                  </span>`;
                  return `<span class="admin-console-row-actions">${primary}${more}</span>`;
                },
              ],
            ]
          : view.rowAction === "membership" && adminConsoleHas("console.memberships.write")
            ? [
                ...view.columns,
                [
                  "操作",
                  (row) => {
                    const tenantId = escapeHtml(row.tenantId);
                    return `<span class="admin-console-row-actions">
                    <button type="button" class="admin-console-row-action" data-admin-console-membership="${tenantId}" data-membership-action="grant-days">发放</button>
                    <button type="button" class="admin-console-row-action" data-admin-console-membership="${tenantId}" data-membership-action="set-expiry">到期日</button>
                  </span>`;
                  },
                ],
              ]
          : view.rowAction === "credit-adjust" && adminConsoleHas("console.credits.adjust")
            ? [
                ...view.columns,
                [
                  "操作",
                  (row) =>
                    `<button type="button" class="admin-console-row-action" data-admin-console-adjust="${escapeHtml(
                      row.tenantId
                    )}">调额</button>`,
                ],
              ]
            : view.columns;
  /* 次要列收进「详情」：视图声明 detailColumns，行尾多一个折叠按钮，展开后是同级的
     一整行明细（<tr> 里塞 <dl>）。不这么做的话，13~15 列的表格必然横向滚动，
     运营要左右拉才能把一行看完（条 5）。折叠行与主行都按当前页的行序号配对，
     序号只在本页内唯一，够用——翻页会整表重画。 */
  const detailColumns = Array.isArray(view.detailColumns) ? view.detailColumns : [];
  const tableColumns = detailColumns.length
    ? [
        ...columns,
        [
          "详情",
          (row, index) =>
            `<button type="button" class="admin-console-row-action admin-console-detail-toggle" data-admin-console-detail-toggle="${index}" aria-expanded="false">详情</button>`,
        ],
      ]
    : columns;
  const head = tableColumns.map(([label]) => `<th>${escapeHtml(label)}</th>`).join("");
  const rows = state.items
.map((row, index) => {
      const cells = tableColumns.map(([label, render]) => `<td data-label="${escapeHtml(label)}">${render(row, index) ?? "—"}</td>`).join("");
      if (!detailColumns.length) return `<tr>${cells}</tr>`;
      const detail = detailColumns
        .map(
          ([label, render]) =>
            `<div class="admin-console-detail-item"><dt>${escapeHtml(label)}</dt><dd>${
              render(row, index) ?? "—"
            }</dd></div>`
        )
        .join("");
      return `<tr>${cells}</tr><tr class="admin-console-detail-row" data-admin-console-detail-row="${index}" hidden><td colspan="${tableColumns.length}"><dl class="admin-console-detail-list">${detail}</dl></td></tr>`;
    })
    .join("");
  return `${notice}${search}${summaryBar}<div class="admin-console-panel"><div class="admin-console-table-wrap"><table class="auth-table"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>${adminConsolePagerHtml(
    state.total,
    state.page,
    state.pageSize
  )}</div>`;
}

function renderAdminConsolePermissions() {
  /* 权限表由后端下发（server/admin_roles.py），这里只画表：前端不复制一份规则。 */
  const node = $("#adminConsolePermissionTable");
  if (!node) return;
  const roles = adminConsoleState.consoleRoles;
  const rows = adminConsoleState.permissionTable;
  if (!roles.length || !rows.length) {
    node.innerHTML = "";
    return;
  }
  const head = ["能力", ...roles.map((role) => role.label)]
    .map((label) => `<th>${escapeHtml(label)}</th>`)
    .join("");
  const body = rows
    .map(
      (row) =>
        `<tr><td data-label="能力">${escapeHtml(row.label)}</td>${roles
          .map(
            (role) =>
              `<td data-label="${escapeHtml(role.label)}" class="admin-console-permission-cell">${
                row.granted?.[role.key]
                  ? `<i data-lucide="check"></i><span class="sr-only">有</span>`
                  : `<span class="admin-console-permission--none">—</span>`
              }</td>`
          )
          .join("")}</tr>`
    )
    .join("");
  const mine = adminConsoleState.roleLabel
    ? `<p class="admin-console-permission-note">你当前的角色：${escapeHtml(adminConsoleState.roleLabel)}</p>`
    : "";
  node.innerHTML = `${mine}<div class="admin-console-table-wrap"><table class="auth-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
}

// 账目语境：**当前筛选下**的笔数与金额合计（含按状态分组），外加"还有多少笔要处理"。
// 列表本来只有行，运营问"这段时间一共收了多少"就得自己加；合计是账目页最常被要的数字。
function adminConsoleSummaryHtml(view) {
  const summary = adminConsoleState.summary;
  if (!summary) return "";
  const statusLabels = view.key === "orders" ? ADMIN_CONSOLE_ORDER_STATUSES : null;
  const chips = (Array.isArray(summary.groups) ? summary.groups : [])
    .map((group) => {
      const label = statusLabels ? adminConsoleStatusLabel(statusLabels, group.key) : String(group.key || "—");
      return `<span class="admin-console-summary-chip">${escapeHtml(label)} <strong>${formatAdminConsoleMoney(
        group.amountRmb
      )}</strong> · ${formatAdminConsoleNumber(group.count)} 笔</span>`;
    })
    .join("");
  // 「去处理」只出现在订单页，且**计数与筛选同一条判据**（后端 UNSETTLED_REFUND_CLAUSE）：
  // 点进去看到的行数就是这个数，不会出现"说 3 笔、点进去 0 条"。
  const unsettled = Number(summary.flags?.refundUnsettled || 0);
  const action = unsettled
    ? `<button type="button" class="admin-console-summary-action" data-admin-console-refund-unsettled="1">${
        formatAdminConsoleNumber(unsettled)
      } 笔退款未落到渠道 · 去处理</button>`
    : "";
  return `<div class="admin-console-summary" role="status">
    <span class="admin-console-summary-total">合计 <strong>${formatAdminConsoleMoney(
      summary.amountRmb
    )}</strong> · ${formatAdminConsoleNumber(summary.count)} 笔</span>
    ${chips}
    ${action}
  </div>`;
}

function renderAdminConsoleBody() {
  const content = $("#adminConsoleContent");
  if (!content) return;
  const view = adminConsoleView();
  content.innerHTML = renderAdminConsoleNative(view);
  renderAdminConsoleScope();
  renderAdminConsolePermissions();
  /* 权限表也在这一轮里生成，图标要在它之后统一替换成 SVG。 */
  refreshIcons();
  bindAdminConsoleSearch(view);
  governanceBindCopyControls(content);
}

async function loadAdminConsole() {
  const view = adminConsoleView();
  const sequence = adminConsoleState.loadSequence = (adminConsoleState.loadSequence || 0) + 1;
  const isCurrent = () => sequence === adminConsoleState.loadSequence && view.key === adminConsoleState.view;
  adminConsoleState.loading = true;
  adminConsoleState.error = "";
  renderAdminConsoleBody();
  /* 能力位到手后页签重画可能把当前视图换掉；换了就得重新加载，表体不能留在旧视图的行上。 */
  let viewChanged = false;
  try {
    const fields = adminConsoleFilterFields(view);
    if (!Object.keys(adminConsoleState.filterValues).length && adminConsoleState.search && fields.length) {
      adminConsoleState.filterValues = { [fields[0].param]: adminConsoleState.search };
    }
    const filter = governanceFilterQuery(view, adminConsoleState.filterValues);
    const data = await getBackend(
      `${view.path}?page=${adminConsoleState.page}&pageSize=${adminConsoleState.pageSize}${filter}`
    );
    if (!isCurrent()) return;
    adminConsoleState.items = Array.isArray(data.items) ? data.items : [];
    adminConsoleState.total = Number(data.total) || 0;
    adminConsoleState.summary = data.summary && typeof data.summary === "object" ? data.summary : null;
    /* 分页以服务端返回的 page/pageSize 为准：服务端会夹紧入参，前端按自己发出去的值
       算页数就会和实际条数对不上（P84）。响应缺这两个字段时才退回本地值。 */
    adminConsoleState.page = Number(data.page) || adminConsoleState.page;
    adminConsoleState.pageSize = Number(data.pageSize) || adminConsoleState.pageSize;
    adminConsoleState.storeConfigured = data.storeConfigured !== false;
    /* 数据范围是这一次查询的范围，不是能力位，单独收。 */
    if (typeof data.scope === "string") adminConsoleState.scope = data.scope;
    /* 能力位到手就重画依赖它的入口（含页签）。页签按能力过滤，重画可能把当前视图换成别的
       ——换了的话这一轮拿到的就是旧视图的行，收尾要重新加载（P58：页签概率性消失）。 */
    viewChanged = applyConsoleCapabilities(data) === true;
    adminConsoleState.creditAdjustLimits =
      data.creditAdjustLimits && typeof data.creditAdjustLimits === "object" ? data.creditAdjustLimits : {};
    adminConsoleState.roleCatalog = Array.isArray(data.roleCatalog) ? data.roleCatalog : [];
    adminConsoleState.loaded[view.key] = true;
  } catch (error) {
    if (!isCurrent()) return;
    adminConsoleState.items = [];
    adminConsoleState.total = 0;
    adminConsoleState.summary = null;
    const base = error?.message || "加载失败";
    /* 页签已经按能力过滤过，走到这里多半是角色刚被回收：如实说需要什么权限。 */
    adminConsoleState.error = view.capability ? `${view.label}需要更高权限（${base}）` : base;
  } finally {
    if (sequence !== adminConsoleState.loadSequence) return;
    adminConsoleState.loading = false;
    if (viewChanged) {
      loadAdminConsole();
      return;
    }
    renderAdminConsoleBody();
  }
}

/* 重画页签，并回答"当前视图是不是被换掉了"。 */
function syncAdminConsoleTabs() {
  const before = adminConsoleState.view;
  renderAdminConsoleTabs();
  return adminConsoleState.view !== before;
}

function renderAdminConsole() {
  renderAdminConsoleTabs();
  loadAdminConsole();
}

function refreshAdminConsole() {
  adminConsoleState.loaded = {};
  loadAdminConsole();
}

function bindAdminConsolePager() {
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-admin-console-page]");
    if (!button) return;
    const direction = button.getAttribute("data-admin-console-page");
    const next = direction === "next" ? adminConsoleState.page + 1 : adminConsoleState.page - 1;
    if (next < 1) return;
    if (direction === "next" && next > Math.ceil(adminConsoleState.total / adminConsoleState.pageSize)) return;
    adminConsoleState.page = next;
    loadAdminConsole();
  });
  document.addEventListener("change", (event) => {
    const sizeSelect = event.target.closest("[data-admin-console-page-size]");
    if (sizeSelect) {
      const next = Math.max(1, Number(sizeSelect.value) || adminConsoleState.pageSize);
      // 切每页大小会改变总页数；落到第 1 页再加载，避免停在已经不存在的页号。
      if (next !== adminConsoleState.pageSize) {
        adminConsoleState.pageSize = next;
        adminConsoleState.page = 1;
        loadAdminConsole();
      }
      return;
    }
    const jumpInput = event.target.closest("[data-admin-console-page-jump]");
    if (jumpInput) {
      const max = Math.max(1, Math.ceil(adminConsoleState.total / adminConsoleState.pageSize));
      const target = Math.min(max, Math.max(1, Number(jumpInput.value) || adminConsoleState.page));
      jumpInput.value = target;
      if (target !== adminConsoleState.page) {
        adminConsoleState.page = target;
        loadAdminConsole();
      }
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const jumpInput = event.target.closest("[data-admin-console-page-jump]");
    if (!jumpInput) return;
    // 输入完页码按回车跳转：避免用户要跳转还得去点翻页按钮。
    event.preventDefault();
    const max = Math.max(1, Math.ceil(adminConsoleState.total / adminConsoleState.pageSize));
    const target = Math.min(max, Math.max(1, Number(jumpInput.value) || adminConsoleState.page));
    jumpInput.value = target;
    if (target !== adminConsoleState.page) {
      adminConsoleState.page = target;
      loadAdminConsole();
    }
  });
}

// 定价编辑：只有平台管理员（服务端 canEdit）能打开，写接口也会再校验一次角色。
function openAdminConsolePricingForm(row, newScope) {
  const modal = $("#adminConsolePricingModal");
  const fields = $("#adminConsolePricingFields");
  if (!modal || !fields) return;
  const scope = row ? row.scope : newScope || "tenant";
  const scopeNode = $("#adminConsolePricingScope");
  modal.dataset.scope = scope;
  modal.dataset.kind = row?.kind || "default";
  const tenantField =
    scope === "tenant"
      ? `<label>租户 ID<input name="tenantId" value="${escapeHtml(row?.tenantId || "")}"${
          row ? " readonly" : ""
        } placeholder="例如：va262ox1owgf" autocomplete="off" /></label>`
      : "";
  fields.innerHTML =
    tenantField +
    ADMIN_CONSOLE_PRICING_FIELDS.map(([key, label, type]) => {
      const value = row?.[key];
      if (type === "boolean") {
        // 布尔开关：未勾 = 停用这一档定价。默认从 row 拿（新建时默认启用）。
        const checked = row ? value !== false : true;
        return `<label class="admin-console-form-toggle"><input type="checkbox" name="${escapeHtml(
          key
        )}"${checked ? " checked" : ""} /><span>${escapeHtml(label)}</span></label>`;
      }
      return `<label>${escapeHtml(label)}<input name="${escapeHtml(
        key
      )}" value="${
        value === null || value === undefined ? "" : escapeHtml(String(value))
      }" inputmode="decimal" autocomplete="off" /></label>`;
    }).join("");
  if (scopeNode) {
    scopeNode.textContent = row
      ? scope === "tenant"
        ? "租户覆盖：只对这个租户生效"
        : "全局默认：没有单独配置的租户都用这一份"
      : scope === "global"
        ? "新建全局默认：没有单独配置的租户都用这一份"
        : "新建租户覆盖：保存后该租户按这份定价计费";
  }
  // 删除覆盖按钮：只在编辑「已存在的租户覆盖」时显示，新建/全局都不显示。
  const deleteBtn = $("#deleteAdminConsolePricingBtn");
  if (deleteBtn) {
    const canDelete = adminConsoleHas("console.pricing.write") && Boolean(row) && scope === "tenant";
    deleteBtn.hidden = !canDelete;
    if (canDelete) deleteBtn.dataset.tenantId = row.tenantId || "";
    else deleteBtn.removeAttribute("data-tenant-id");
  }
  showModal(modal);
  refreshIcons();
}

// 标记「必填」与「必须为正」的定价项：空值和 0 是两件事（0 = 免费，空 = 没填）。
// 曾经空输入被静默丢弃、后端再用默认价补齐，于是「清空保存」看起来像把价格改回 99（台账 P87）。
// 必须为正的项：倍率、会员天数、两个积分数。0 倍率会把每一次计费都算成免费；0 天会员不是
// 一个可售的商品；0 积分则什么都送不出去——把会员价配成 0 做活动时，积分仍要按配置照常
// 发放，所以"不送积分"不是一种档位形态（台账 P126）。这三类服务端的值域都是 > 0
// （server/billing.py；天数还有库里的 CHECK persistence.py:409）。表单过去只拦倍率，
// 于是照着自己那句"不收费请填 0"填的人一定被 400 拒（台账 P114）。其余单价项 0 合法，就是"不收费"。
const ADMIN_CONSOLE_PRICING_POSITIVE_FIELDS = new Set([
  "textMarkup",
  "imageMarkup",
  "membershipDays",
  "membershipCredits",
  "topupCredits",
]);

/* 上面这几格填 0 被拒时，要说清"为什么不行、该怎么办"。
   表单对单价项写着「不收费请填 0」，照着这句做的人把 0 填进这几格就必然被拒——
   只回一句「必须大于 0」，他还是不知道错在哪、该改成什么（台账 P114/P126）。
   提示与 server/billing.py 的 _positive_int / _multiplier_decimal 值域一一对应。 */
const ADMIN_CONSOLE_PRICING_ZERO_HINTS = {
  textMarkup: "0 倍率会把每一次扣费都算成免费，不收费请把对应的单价填 0",
  imageMarkup: "0 倍率会把每一次扣费都算成免费，不收费请把对应的单价填 0",
  membershipDays: "0 天的会员不是一个可售档位；要做免费活动请把「会员价（元）」填 0",
  membershipCredits: "0 积分什么都送不出去；会员价填 0 时积分仍要照常发放",
  topupCredits: "0 积分什么都送不出去；不收费请把「充值价（元）」填 0",
};

function adminConsolePricingErrors(form) {
  const errors = [];
  for (const [key, label, type] of ADMIN_CONSOLE_PRICING_FIELDS) {
    if (type !== "number") continue;
    const raw = String(form.elements[key]?.value ?? "").trim();
    const positiveOnly = ADMIN_CONSOLE_PRICING_POSITIVE_FIELDS.has(key);
    if (raw === "") {
      errors.push(positiveOnly ? `${label}不能为空` : `${label}不能为空（不收费请填 0）`);
      continue;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      errors.push(`${label}要填数字（只填数值，不要带单位或文字）`);
      continue;
    }
    if (value < 0) {
      errors.push(`${label}不能是负数`);
      continue;
    }
    if (positiveOnly && value <= 0) {
      const hint = ADMIN_CONSOLE_PRICING_ZERO_HINTS[key];
      errors.push(`${label}必须大于 0${hint ? `：${hint}` : ""}`);
    }
  }
  return errors;
}

/* 保存前的确认摘要（P0 条 6：高风险操作先给一句人话）。
   要回答三个问题：改的是哪一份（作用域 + 租户）、改成什么（金额与积分）、
   以及万一改错了能不能救回来（影响面）。最后那句不是安慰话——它对应后端真实行为：
   订单创建时就把价与积分存成快照，结算读的是单上快照，所以改价只影响之后的新单。 */
function adminConsolePricingSummary(form, scope) {
  const text = (key) => String(form.elements[key]?.value ?? "").trim();
  const yuan = (key) => {
    const raw = text(key);
    return raw === "" || !Number.isFinite(Number(raw)) ? "（未填）" : `${raw} 元`;
  };
  const count = (key) => text(key) || "（未填）";
  const who =
    scope === "tenant" ? `租户覆盖 · 租户 ${text("tenantId") || "（未填）"}` : "全局默认（未单独配置的租户都用这一份）";
  const state = form.elements.isActive?.checked !== false ? "启用" : "停用";
  return [
    "确认保存这份定价？",
    `作用域：${who}（保存后${state}）`,
    `会员：${yuan("membershipPriceRmb")} / ${count("membershipCredits")} 积分 / ${count("membershipDays")} 天`,
    `充值：${yuan("topupPriceRmb")} / ${count("topupCredits")} 积分`,
    `文本输入 ${yuan("textInputPriceRmb")} 元/百万 tokens · 文本输出 ${yuan(
      "textOutputPriceRmb"
    )} 元/百万 tokens · 文本倍率 ${count("textMarkup")}`,
    `图片 1024 ${yuan("image1024BaseRmb")} / 张 · 图片 1536 ${yuan("image1536BaseRmb")} / 张 · 图片倍率 ${count(
      "imageMarkup"
    )}`,
    "影响：只对之后的新订单生效；已下单的按单上快照结算。改错可改回原值，已成交部分不追溯。",
  ].join("\n");
}

async function submitAdminConsolePricing(event) {
  event.preventDefault();
  const modal = $("#adminConsolePricingModal");
  const form = $("#adminConsolePricingForm");
  const button = $("#saveAdminConsolePricingBtn");
  if (!modal || !form || !button) return;
  const scope = modal.dataset.scope || "tenant";
  const payload = { scope, kind: modal.dataset.kind || "default" };
  // 空输入要原样报给后端（让它也拒绝一次），而不是悄悄丢掉——丢掉就等于用默认价覆盖用户设置。
  for (const [key, , type] of ADMIN_CONSOLE_PRICING_FIELDS) {
    if (type === "boolean") {
      payload[key] = form.elements[key]?.checked !== false;
    } else {
      payload[key] = String(form.elements[key]?.value ?? "").trim();
    }
  }
  if (scope === "tenant") {
    payload.tenantId = String(form.elements.tenantId?.value ?? "").trim();
    if (!payload.tenantId) {
      toast("请填写租户 ID", { kind: "error" });
      return;
    }
  }
  const errors = adminConsolePricingErrors(form);
  if (errors.length) {
    toast(errors[0], { kind: "error", duration: 5000 });
    return;
  }
  /* 改价是不可逆的收入侧动作，保存前用一句摘要把"改哪一份、改成什么、影响了谁"说清。
     摘要里的"能回退到哪"是照 server/billing_service.py 的下单快照写的：订单在创建时
     就把 amount_rmb / credits / membership_days 存了下来（create_billing_order 592-594），
     结算读的是单上快照（679-680），所以改价只对之后的新单生效。
     这里不用 adminConsoleConfirm：它在 confirm 不可用时返回 false，会让保存变成
     "点了没反应"；保存是显式点按钮触发的，少一次确认比静默不动更可接受。 */
  if (typeof window.confirm === "function" && !window.confirm(adminConsolePricingSummary(form, scope))) return;
  button.disabled = true;
  try {
    await postBackend("/api/admin/pricing-configs", payload);
    hideModal(modal);
    toast("定价已保存");
    refreshAdminConsole();
  } catch (error) {
    toast(`保存失败：${error?.message || "请检查填写内容"}`, { kind: "error", duration: 5000 });
  } finally {
    button.disabled = false;
  }
}

// 删除租户定价覆盖：让该租户回退用全局默认价。
// 全局默认不可删；后端会再校验一次 scope + 权限。
async function deleteAdminConsolePricing() {
  const modal = $("#adminConsolePricingModal");
  const button = $("#deleteAdminConsolePricingBtn");
  if (!modal || !button) return;
  const tenantId = String(button.dataset.tenantId || "").trim();
  if (!tenantId) {
    toast("缺少租户 ID", { kind: "error" });
    return;
  }
  if (!window.confirm("确认删除该租户的定价覆盖？删除后该租户将回退使用全局默认价。")) return;
  const done = setButtonLoading(button, "删除中...");
  try {
    await postBackend("/api/admin/pricing-configs", {
      action: "delete",
      scope: "tenant",
      tenantId,
      kind: modal.dataset.kind || "default",
    });
    hideModal(modal);
    toast("已删除租户覆盖，该租户回退用全局默认价");
    refreshAdminConsole();
  } catch (error) {
    toast(`删除失败：${error?.message || "请稍后重试"}`, { kind: "error", duration: 5000 });
  } finally {
    done();
  }
}

// 手工调额：平台管理员对指定租户发放或扣减额度。
// 幂等键在打开弹层时生成，表单内容一动就作废重生成——「没改内容重试」沿用同一把键
// （重复提交只生效一次），「改了内容再提交」则是一次新请求（这是两件不同的事）。
function adminConsoleNewAdjustKey() {
  return `credit-adjust:${Date.now()}:${Math.random().toString(16).slice(2, 10)}`;
}

const ADMIN_CONSOLE_TENANTS_PATH = "/api/admin/tenants";
let adminConsoleCreditAdjustSearchTimer = 0;
let adminConsoleMembershipSearchTimer = 0;

/* 租户搜索的取数与渲染，手工调额与会员处置两个入口共用（都是"按名字找租户，别手抄 ID"）。
   租户 ID 是 12 位不透明串，抄错一位就会把额度发到、或把会员开到别的租户头上；
   两个入口过去只有手填一条路，这里把"搜名字 → 点选回填"抽成同一份。 */
async function adminConsoleTenantSearchItems(query) {
  const data = await getBackend(
    `${ADMIN_CONSOLE_TENANTS_PATH}?q=${encodeURIComponent(query)}`
  );
  return (data?.items || []).filter((item) => item && item.id);
}

// `target` 决定点选后回填哪一个弹层：结果列表是共享的，得让点击知道自己属于谁。
function adminConsoleTenantResultsHtml(items, emptyText, target = "") {
  if (!items.length) {
    return `<div class="admin-console-tenant-result admin-console-tenant-result--status">${escapeHtml(
      emptyText
    )}</div>`;
  }
  return items
    .map(
      (item) =>
        `<button type="button" class="admin-console-tenant-result" role="option" data-admin-console-tenant="${escapeHtml(
          item.id
        )}" data-admin-console-tenant-name="${escapeHtml(item.name || "")}"${
          target ? ` data-admin-console-tenant-target="${escapeHtml(target)}"` : ""
        }><strong>${escapeHtml(item.name || "未命名组织")}</strong><span>${escapeHtml(
          item.id
        )}</span></button>`
    )
    .join("");
}

function adminConsoleCreditAdjustSetTenant(tenantId, tenantName = "") {
  const id = String(tenantId || "").trim();
  const name = String(tenantName || "").trim();
  const input = $("#adminConsoleCreditAdjustTenantId");
  if (input) input.value = id;
  /* 换租户就是换提交内容，作废上一次的幂等键；名字记在弹层上供提交摘要用。 */
  const modal = $("#adminConsoleCreditAdjustModal");
  if (modal) {
    modal.dataset.creditAdjustTenantName = name;
    modal.dataset.idempotencyKey = "";
  }
  const selected = $("#adminConsoleCreditAdjustTenantSelected");
  if (selected) {
    selected.textContent = "";
    if (!id) {
      selected.classList.add("hidden");
    } else {
      const label = document.createElement("strong");
      label.textContent = name || "已选租户";
      const idNode = document.createElement("span");
      idNode.textContent = id;
      idNode.className = "admin-console-tenant-selected-id";
      selected.append(label, idNode);
      selected.classList.remove("hidden");
    }
  }
  updateAdminConsoleCreditAdjustSummary();
}

// 提交前摘要：一行写清「将向/从某某发放/扣减 N 积分」，防调反、防点错。
function updateAdminConsoleCreditAdjustSummary() {
  const node = $("#adminConsoleCreditAdjustSummary");
  const form = $("#adminConsoleCreditAdjustForm");
  const modal = $("#adminConsoleCreditAdjustModal");
  if (!node || !form || !modal) return;
  const tenantId = String(form.elements.tenantId?.value || "").trim();
  const direction = String(form.elements.direction?.value || "").trim();
  const amountText = String(form.elements.amount?.value || "").trim();
  const amount = /^\d+$/.test(amountText) ? Number(amountText) : 0;
  node.textContent = "";
  node.className = "admin-console-adjust-summary";
  if (!tenantId || !amount) return;
  const name = String(modal.dataset.creditAdjustTenantName || "").trim();
  const label = name || tenantId;
  const isDeduct = direction === "deduct";
  node.textContent = `将${isDeduct ? "从" : "向"}「${label}」${isDeduct ? "扣减" : "发放"} ${formatAdminConsoleNumber(amount)} 积分`;
  node.className = `admin-console-adjust-summary ${isDeduct ? "admin-console-adjust-summary--deduct" : "admin-console-adjust-summary--grant"}`;
}

function hideAdminConsoleCreditAdjustResults() {
  const results = $("#adminConsoleCreditAdjustTenantResults");
  if (!results) return;
  results.classList.add("hidden");
  results.innerHTML = "";
}

async function adminConsoleCreditAdjustSearch(q) {
  const results = $("#adminConsoleCreditAdjustTenantResults");
  if (!results) return;
  const query = String(q || "").trim();
  if (!query) {
    hideAdminConsoleCreditAdjustResults();
    return;
  }
  results.classList.remove("hidden");
  results.innerHTML =
    '<div class="admin-console-tenant-result admin-console-tenant-result--status">搜索中…</div>';
  try {
    results.innerHTML = adminConsoleTenantResultsHtml(
      await adminConsoleTenantSearchItems(query),
      "没有匹配的租户，可在「高级」里手填租户 ID"
    );
  } catch (error) {
    results.innerHTML =
      '<div class="admin-console-tenant-result admin-console-tenant-result--status">搜索失败：' +
      escapeHtml(error?.message || "请稍后重试") +
      "</div>";
  }
}

function openAdminConsoleCreditAdjustModal(tenantId = "", tenantName = "") {
  const modal = $("#adminConsoleCreditAdjustModal");
  const form = $("#adminConsoleCreditAdjustForm");
  if (!modal || !form) return;
  const limits = adminConsoleState.creditAdjustLimits || {};
  const scope = $("#adminConsoleCreditAdjustScope");
  if (scope) {
    scope.textContent = `单次上限 ${formatAdminConsoleNumber(
      limits.maxPerAdjustment || 100000
    )} 积分 · 发放的额度 ${formatAdminConsoleNumber(limits.expiresInDays || 365)} 天后到期`;
  }
  const hint = $("#adminConsoleCreditAdjustHint");
  if (hint) {
    hint.textContent =
      "只调额度、不记成充值：账本类型是「手工调额」，不计入收入；扣减不会把余额调成负数。原因会写进账本与审计，请写清用途。";
  }
  form.reset();
  adminConsoleCreditAdjustSetTenant(tenantId, tenantName);
  const search = $("#adminConsoleCreditAdjustTenantSearch");
  if (search) search.value = "";
  hideAdminConsoleCreditAdjustResults();
  modal.dataset.idempotencyKey = adminConsoleNewAdjustKey();
  showModal(modal);
  refreshIcons();
}

async function submitAdminConsoleCreditAdjust(event) {
  event.preventDefault();
  const modal = $("#adminConsoleCreditAdjustModal");
  const form = $("#adminConsoleCreditAdjustForm");
  if (!modal || !form) return;
  const tenantId = String(form.elements.tenantId?.value || "").trim();
  const direction = String(form.elements.direction?.value || "").trim();
  const amountText = String(form.elements.amount?.value || "").trim();
  const reason = String(form.elements.reason?.value || "").trim();
  if (!tenantId) {
    toast("请先搜索选择租户，或展开「高级」手填租户 ID", { kind: "error" });
    return;
  }
  if (direction !== "grant" && direction !== "deduct") {
    toast("请选择发放或扣减", { kind: "error" });
    return;
  }
  /* 前端只做"填得对不对"的初筛，上限与余额仍由后端判（前端常数会被改掉）。
     数量是正整数，正负号由方向决定：发放为正、扣减为负。 */
  if (!/^\d+$/.test(amountText) || Number(amountText) <= 0) {
    toast("数量必须是正整数（发放或扣减由方向决定）", { kind: "error" });
    return;
  }
  if (!reason) {
    toast("请填写调额原因", { kind: "error" });
    return;
  }
  const signedAmount = direction === "grant" ? Number(amountText) : -Number(amountText);
  const idempotencyKey = modal.dataset.idempotencyKey || adminConsoleNewAdjustKey();
  modal.dataset.idempotencyKey = idempotencyKey;
  const submitButton = $("#saveAdminConsoleCreditAdjustBtn");
  const done = setButtonLoading(submitButton, "提交中…");
  try {
    const result = await postBackend("/api/admin/credits/adjust", {
      tenantId,
      amount: signedAmount,
      reason,
      idempotencyKey,
    });
    hideModal(modal);
    const amount = Number(result?.amount) || 0;
    const balance = formatAdminConsoleNumber(result?.balanceAfter);
    toast(
      result?.alreadyApplied
        ? `这次提交此前已经生效，未重复入账（余额 ${balance}）`
        : `已${amount < 0 ? "扣减" : "发放"} ${formatAdminConsoleNumber(
            Math.abs(amount)
          )} 积分（余额 ${balance}）`
    );
    refreshAdminConsole();
  } catch (error) {
    toast(`调额失败：${error?.message || "请检查填写内容"}`, { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

// 支付渠道配置（P90）：虎皮椒凭据原先只能靠环境变量，改一次要写 0600 env 再重启后端，
// 配错网关也只能等下单失败才发现。这里给平台管理员一个可写、可自查、可回滚的入口。
//
// 两条规矩：
//   1. 密钥只写不读——表单永远不回填它，留空表示"保持已保存的那一份"，
//      所以只改网关不会顺手把密钥清掉；
//   2. 保存后无需重启：下单与回调都按请求现读这份配置（见 server/xunhu_pay.py）。
const ADMIN_PAYMENT_CHANNEL_PATH = "/api/admin/payment-channel";
const ADMIN_PAYMENT_CHANNEL_PROBE_PATH = "/api/admin/payment-channel/probe";
const ADMIN_PAYMENT_CHANNEL_SOURCE_LABELS = {
  console: "平台配置",
  environment: "环境变量",
  unconfigured: "未配置",
};

// 回调地址留空不影响下单（照样出二维码），但回调永远不来：订单只能等维护循环的补单扫描
// 结算，用户付款后要过一阵才到账。所以这一栏必须说清"生效的是哪一份"，不能让空值
// 看起来像"配好了"（P8/C）。
function adminPaymentChannelNotifyLabel(channel) {
  const source = channel?.notifyUrlSource;
  if (source === "console") return "回调已配置";
  if (source === "environment") return "回调继承环境变量";
  return "回调地址缺失（订单要等补单扫描才到账）";
}

function renderAdminPaymentChannelStatus(channel) {
  const status = $("#adminConsolePaymentChannelStatus");
  if (status) {
    status.classList.toggle("is-loading", !channel);
    const source = channel
      ? ADMIN_PAYMENT_CHANNEL_SOURCE_LABELS[channel.activeSource] || ADMIN_PAYMENT_CHANNEL_SOURCE_LABELS.unconfigured
      : "读取中…";
    const secret = !channel ? "读取中…" : channel.secretConfigured ? `已配置 ${channel.maskedSecret}` : "未配置";
    const notify = channel ? adminPaymentChannelNotifyLabel(channel) : "读取中…";
    const who = !channel
      ? "读取中…"
      : channel.updatedBy
        ? channel.updatedByLabel || `${channel.updatedBy}（内部 ID）`
        : "暂无记录";
    $("#adminConsolePaymentSourceStatus").textContent = source;
    $("#adminConsolePaymentSecretStatus").textContent = secret;
    $("#adminConsolePaymentNotifyStatus").textContent = notify;
    $("#adminConsolePaymentUpdatedByStatus").textContent = who;
  }
  const hint = $("#adminConsolePaymentChannelHint");
  if (hint) {
    hint.textContent =
      "保存后即时生效，不需要重启后端。密钥只写不读，页面不会再显示明文；关闭「启用」则回到环境变量配置，配置内容保留。";
  }
}

function renderAdminPaymentChannelEnabledState(enabled) {
  const label = $("#adminConsolePaymentEnabledLabel");
  if (label) label.textContent = enabled ? "已开启" : "已关闭";
}

function validateAdminPaymentChannelUrl(input, errorId) {
  if (!input) return true;
  const value = String(input.value || "").trim();
  const valid = !value || /^https?:\/\/[^\s]+$/i.test(value);
  const message = valid ? "" : "请输入以 http:// 或 https:// 开头的完整地址。";
  input.setCustomValidity(message);
  input.setAttribute("aria-invalid", valid ? "false" : "true");
  const error = $(`#${errorId}`);
  if (error) error.textContent = message;
  return valid;
}

function validateAdminPaymentChannelUrls(form) {
  const apiBaseValid = validateAdminPaymentChannelUrl(form?.elements?.apiBase, "adminConsolePaymentApiBaseError");
  const notifyUrlValid = validateAdminPaymentChannelUrl(form?.elements?.notifyUrl, "adminConsolePaymentNotifyUrlError");
  return apiBaseValid && notifyUrlValid;
}

async function loadAdminPaymentChannel() {
  const form = $("#adminConsolePaymentChannelForm");
  if (!form) return;
  try {
    const data = await getBackend(ADMIN_PAYMENT_CHANNEL_PATH);
    const channel = data?.channel || {};
    form.elements.appid.value = channel.appid || "";
    // 只写不读：永远不回填密钥。
    form.elements.secret.value = "";
    form.elements.secret.placeholder = channel.secretConfigured
      ? `已保存（${channel.maskedSecret}），留空表示不修改`
      : "还没有保存过密钥";
    form.elements.apiBase.value = channel.apiBase || channel.defaultApiBase || "";
    form.elements.notifyUrl.value = channel.notifyUrl || "";
    // 回调地址留空 = 继承环境变量（或没有回调）。把这份"留空意味着什么"写在 placeholder 上，
    // 与密钥那栏"留空表示不修改"同一写法：清空输入框时立刻能看到它会用哪一份。
    const environmentNotifyUrl = channel.environment?.notifyUrl || "";
    form.elements.notifyUrl.placeholder = environmentNotifyUrl
      ? `留空则继承环境变量：${environmentNotifyUrl}`
      : "留空表示不接收回调，订单要等补单扫描才到账";
    form.elements.enabled.checked = Boolean(channel.enabled);
    renderAdminPaymentChannelEnabledState(form.elements.enabled.checked);
    validateAdminPaymentChannelUrls(form);
    renderAdminPaymentChannelStatus(channel);
    const restoreButton = $("#restoreAdminConsolePaymentChannelBtn");
    if (restoreButton) restoreButton.disabled = !channel.previous;
  } catch (error) {
    toast(`支付渠道读取失败：${error?.message || "请稍后重试"}`, { kind: "error", duration: 6000 });
  }
}

async function openAdminPaymentChannelModal() {
  const modal = $("#adminConsolePaymentChannelModal");
  const form = $("#adminConsolePaymentChannelForm");
  if (!modal || !form) return;
  form.reset();
  renderAdminPaymentChannelStatus(null);
  renderAdminPaymentChannelEnabledState(false);
  validateAdminPaymentChannelUrls(form);
  showModal(modal);
  refreshIcons();
  await loadAdminPaymentChannel();
}

function adminPaymentChannelPayload(form) {
  return {
    appid: String(form.elements.appid.value || "").trim(),
    // 空串=不修改（后端按"密钥只写不读"处理），不是"清空密钥"。
    secret: String(form.elements.secret.value || "").trim(),
    apiBase: String(form.elements.apiBase.value || "").trim(),
    notifyUrl: String(form.elements.notifyUrl.value || "").trim(),
    enabled: Boolean(form.elements.enabled.checked),
  };
}

async function submitAdminPaymentChannel(event) {
  event.preventDefault();
  const form = $("#adminConsolePaymentChannelForm");
  const modal = $("#adminConsolePaymentChannelModal");
  if (!form || !modal) return;
  if (!validateAdminPaymentChannelUrls(form)) {
    form.reportValidity();
    return;
  }
  const button = $("#saveAdminConsolePaymentChannelBtn");
  const done = setButtonLoading(button, "保存中…");
  try {
    const result = await postBackend(ADMIN_PAYMENT_CHANNEL_PATH, adminPaymentChannelPayload(form));
    const channel = result?.channel || {};
    renderAdminPaymentChannelStatus(channel);
    form.elements.secret.value = "";
    toast(channel.active ? "支付渠道已保存并生效" : "支付渠道已保存（未启用，仍在用环境变量）", {
      duration: 5200,
    });
  } catch (error) {
    toast(`保存失败：${error?.message || "请检查填写内容"}`, { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function restoreAdminPaymentChannel() {
  if (
    typeof window.confirm === "function" &&
    !window.confirm("确认回滚到上一版支付渠道配置？回滚后将立即使用上一版的网关、回调与启用状态，当前未保存内容会丢失。")
  ) return;
  const button = $("#restoreAdminConsolePaymentChannelBtn");
  const done = setButtonLoading(button, "回滚中…");
  try {
    const result = await postBackend(ADMIN_PAYMENT_CHANNEL_PATH, { restore: true });
    renderAdminPaymentChannelStatus(result?.channel || {});
    toast("已回滚到上一版支付渠道配置", { duration: 5200 });
    await loadAdminPaymentChannel();
  } catch (error) {
    toast(`回滚失败：${error?.message || "没有可回滚的上一版"}`, { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function probeAdminPaymentChannel() {
  const button = $("#probeAdminConsolePaymentChannelBtn");
  const done = setButtonLoading(button, "探测中…");
  try {
    const result = await postBackend(ADMIN_PAYMENT_CHANNEL_PROBE_PATH, {});
    const detail = [result?.providerCode ? `errcode ${result.providerCode}` : "", result?.providerMessage || ""]
      .filter(Boolean)
      .join(" ");
    toast(`网关可达（${result?.apiBase || "已配置网关"}）${detail ? ` · ${detail}` : ""}`, { duration: 8000 });
  } catch (error) {
    toast(`探测失败：${error?.message || "网关不可达，请检查网关地址与密钥"}`, { kind: "error", duration: 8000 });
  } finally {
    done();
  }
}

function bindAdminConsoleEditor() {
  document.addEventListener("click", (event) => {
    /* 「详情」折叠：主行与明细行按行序号配对（同一次渲染里生成，序号必然对得上）。
       展开的是同一张表里的一条同级 <tr>，不是新弹层——弹层会遮住整张表，
       而这里要的正是"对着这一行就地看明细"。 */
    const detailToggle = event.target.closest("[data-admin-console-detail-toggle]");
    if (detailToggle) {
      const index = detailToggle.getAttribute("data-admin-console-detail-toggle");
      const detailRow = document.querySelector(`[data-admin-console-detail-row="${index}"]`);
      if (detailRow) {
        const opening = detailRow.hidden;
        detailRow.hidden = !opening;
        detailToggle.setAttribute("aria-expanded", opening ? "true" : "false");
        detailToggle.textContent = opening ? "收起" : "详情";
      }
      event.preventDefault();
      return;
    }
    const editButton = event.target.closest("[data-admin-console-edit]");
    if (!editButton) return;
    const id = editButton.getAttribute("data-admin-console-edit");
    openAdminConsolePricingForm(adminConsoleState.items.find((item) => String(item.id) === id));
  });
  document.addEventListener("click", (event) => {
    const refundButton = event.target.closest("[data-admin-console-refund]");
    if (!refundButton) return;
    openAdminConsoleRefundModal(refundButton.getAttribute("data-admin-console-refund"));
  });
  // 「N 笔退款未落到渠道 · 去处理」：点一下就是把那条具名筛选打开（不是另开一页），
  // 于是列表、合计、计数三者自动一致。
  document.addEventListener("click", (event) => {
    const unsettled = event.target.closest("[data-admin-console-refund-unsettled]");
    if (!unsettled) return;
    adminConsoleState.filterValues = { ...adminConsoleState.filterValues, refundUnsettled: "1" };
    adminConsoleState.page = 1;
    loadAdminConsole();
  });
  document.addEventListener("click", (event) => {
    const rolesButton = event.target.closest("[data-admin-console-roles]");
    if (rolesButton) {
      openAdminConsoleRolesModal(rolesButton.getAttribute("data-admin-console-roles"));
      return;
    }
    const accountButton = event.target.closest("[data-admin-console-account]");
    if (accountButton) {
      const userId = accountButton.getAttribute("data-admin-console-account");
      const action = accountButton.getAttribute("data-account-action");
      /* 要填原因（+ 到期时间）的动作走弹层；其余确认后直接执行。 */
      if (ADMIN_CONSOLE_LIFECYCLE_ACTIONS[action]) {
        openAdminConsoleLifecycleModal(userId, action);
        return;
      }
      void runAdminConsoleAccountAction(userId, action);
    }
  });
  /* 用户账号行的「ID 短串 / 复制 ID / 更多」三件套——单独委托一次，避免污染上面那条
     行为已经稳定的 listener。点击短串切显隐、点复制走 navigator.clipboard、点「更多」
     弹下拉、点下拉外任一处收起。 */
  document.addEventListener("click", (event) => {
    const idShort = event.target.closest("[data-admin-console-account-id-short]");
    if (idShort) {
      const full = idShort.getAttribute("data-admin-console-account-id-short") || "";
      const revealed = idShort.dataset.revealed === "true";
      if (revealed) {
        const masked = adminConsoleMaskAccountId(full);
        idShort.textContent = masked;
        idShort.dataset.revealed = "false";
        idShort.setAttribute("title", "点击切换显隐");
      } else {
        idShort.textContent = full;
        idShort.dataset.revealed = "true";
        idShort.setAttribute("title", "点击恢复打码");
      }
      event.preventDefault();
      return;
    }
    const idCopy = event.target.closest("[data-admin-console-account-id-copy]");
    if (idCopy) {
      const full = idCopy.getAttribute("data-admin-console-account-id-copy") || "";
      void governanceCopyValue(idCopy, full, "用户 ID");
      event.preventDefault();
      return;
    }
    /* 「更多」触发器：先收起所有其他行再开当前行，保证一次只有一个菜单展开。 */
    const trigger = event.target.closest("[data-admin-console-account-menu]");
    if (trigger) {
      const currentWrap = trigger.parentElement;
      const currentPop = currentWrap?.querySelector(".admin-console-actions-menu-pop");
      const opening = !trigger.getAttribute("aria-expanded") || trigger.getAttribute("aria-expanded") === "false";
      document.querySelectorAll(".admin-console-actions-menu-pop:not([hidden])").forEach((pop) => {
        if (pop !== currentPop) {
          pop.hidden = true;
          const t = pop.parentElement?.querySelector(".admin-console-actions-trigger");
          if (t) t.setAttribute("aria-expanded", "false");
        }
      });
      if (currentPop) {
        currentPop.hidden = !opening;
        trigger.setAttribute("aria-expanded", opening ? "true" : "false");
      }
      event.preventDefault();
      return;
    }
    /* 点击菜单外任一处 → 收起所有。menu-pop 自身和 trigger 已经走过上面两个分支。 */
    document.querySelectorAll(".admin-console-actions-menu-pop:not([hidden])").forEach((pop) => {
      pop.hidden = true;
      const t = pop.parentElement?.querySelector(".admin-console-actions-trigger");
      if (t) t.setAttribute("aria-expanded", "false");
    });
  });
  document.addEventListener("click", (event) => {
    const membershipButton = event.target.closest("[data-admin-console-membership]");
    if (!membershipButton) return;
    /* 行内按钮把租户带进弹层：运营不必手抄租户 ID。 */
    openAdminConsoleMembershipModal(
      membershipButton.getAttribute("data-membership-action"),
      membershipButton.getAttribute("data-admin-console-membership"),
    );
  });
  $("#adminConsoleCreateBtn")?.addEventListener("click", () =>
    openAdminConsolePricingForm(null, adminConsoleNewScope())
  );
  $("#closeAdminConsolePricingBtn")?.addEventListener("click", () => hideModal($("#adminConsolePricingModal")));
  $("#cancelAdminConsolePricingBtn")?.addEventListener("click", () => hideModal($("#adminConsolePricingModal")));
  $("#deleteAdminConsolePricingBtn")?.addEventListener("click", deleteAdminConsolePricing);
  $("#adminConsolePricingForm")?.addEventListener("submit", submitAdminConsolePricing);
  $("#closeAdminConsoleRefundBtn")?.addEventListener("click", () => hideModal($("#adminConsoleRefundModal")));
  $("#cancelAdminConsoleRefundBtn")?.addEventListener("click", () => hideModal($("#adminConsoleRefundModal")));
  $("#adminConsoleRefundForm")?.addEventListener("submit", submitAdminConsoleRefund);
  $("#closeAdminConsoleRolesBtn")?.addEventListener("click", () => hideModal($("#adminConsoleRolesModal")));
  $("#cancelAdminConsoleRolesBtn")?.addEventListener("click", () => hideModal($("#adminConsoleRolesModal")));
  $("#adminConsoleRolesForm")?.addEventListener("submit", submitAdminConsoleRoles);
  $("#adminConsoleCreditAdjustBtn")?.addEventListener("click", () => openAdminConsoleCreditAdjustModal());
  $("#closeAdminConsoleCreditAdjustBtn")?.addEventListener("click", () =>
    hideModal($("#adminConsoleCreditAdjustModal"))
  );
  $("#cancelAdminConsoleCreditAdjustBtn")?.addEventListener("click", () =>
    hideModal($("#adminConsoleCreditAdjustModal"))
  );
  const adjustForm = $("#adminConsoleCreditAdjustForm");
  adjustForm?.addEventListener("submit", submitAdminConsoleCreditAdjust);
  // 内容一改，上一次的幂等键就不再代表"这次要提交的东西"，作废它；提交摘要跟着刷新。
  adjustForm?.addEventListener("input", () => {
    const modal = $("#adminConsoleCreditAdjustModal");
    if (modal) modal.dataset.idempotencyKey = "";
    updateAdminConsoleCreditAdjustSummary();
  });
  adjustForm?.addEventListener("change", () => updateAdminConsoleCreditAdjustSummary());
  // 租户搜索：按名称定位租户，点选后回填 tenantId（手填降级到「高级」折叠块里）。
  const tenantSearch = $("#adminConsoleCreditAdjustTenantSearch");
  tenantSearch?.addEventListener("input", () => {
    clearTimeout(adminConsoleCreditAdjustSearchTimer);
    adminConsoleCreditAdjustSearchTimer = setTimeout(
      () => adminConsoleCreditAdjustSearch(tenantSearch.value),
      250
    );
  });
  tenantSearch?.addEventListener("keydown", (event) => {
    if (event.key === "Escape") hideAdminConsoleCreditAdjustResults();
  });
  /* 会员弹层的租户搜索：节点是运行时注入的，所以在 document 上委托一次，
     不依赖"绑定时它已经存在"。 */
  document.addEventListener("input", (event) => {
    if (event.target?.id !== "adminConsoleMembershipTenantSearch") return;
    const value = event.target.value;
    clearTimeout(adminConsoleMembershipSearchTimer);
    adminConsoleMembershipSearchTimer = setTimeout(() => adminConsoleMembershipSearch(value), 250);
  });
  document.addEventListener("keydown", (event) => {
    if (event.target?.id === "adminConsoleMembershipTenantSearch" && event.key === "Escape") {
      hideAdminConsoleMembershipResults();
    }
  });
  document.addEventListener("click", (event) => {
    const search = $("#adminConsoleMembershipTenantSearch");
    const results = $("#adminConsoleMembershipTenantResults");
    if (!search || !results) return;
    if (search.contains(event.target) || results.contains(event.target)) return;
    hideAdminConsoleMembershipResults();
  });
  document.addEventListener("click", (event) => {
    const search = $("#adminConsoleCreditAdjustTenantSearch");
    const results = $("#adminConsoleCreditAdjustTenantResults");
    if (!search || !results) return;
    if (search.contains(event.target) || results.contains(event.target)) return;
    hideAdminConsoleCreditAdjustResults();
  });
  document.addEventListener("click", (event) => {
    const option = event.target.closest("[data-admin-console-tenant]");
    if (!option) return;
    const id = option.getAttribute("data-admin-console-tenant");
    const name = option.getAttribute("data-admin-console-tenant-name") || "";
    /* 结果列表是共享的，由按钮自己声明点选后回填哪一个弹层。 */
    if (option.getAttribute("data-admin-console-tenant-target") === "membership") {
      adminConsoleMembershipSetTenant(id, name);
      hideAdminConsoleMembershipResults();
      return;
    }
    adminConsoleCreditAdjustSetTenant(id, name);
    hideAdminConsoleCreditAdjustResults();
  });
  // 从行带入：积分流水/发放/预占每一行的「调额」把该租户直接带进弹层。
  document.addEventListener("click", (event) => {
    const adjustButton = event.target.closest("[data-admin-console-adjust]");
    if (!adjustButton) return;
    openAdminConsoleCreditAdjustModal(adjustButton.getAttribute("data-admin-console-adjust"));
  });
  // 租户名称与缩略 ID 都复制同一份完整租户 ID。
  document.addEventListener("click", (event) => {
    const copyButton = event.target.closest("[data-admin-console-copy]");
    if (!copyButton) return;
    const text = copyButton.getAttribute("data-admin-console-copy") || "";
    void governanceCopyValue(copyButton, text, "租户 ID");
  });
  $("#adminConsolePaymentChannelBtn")?.addEventListener("click", () => void openAdminPaymentChannelModal());
  $("#closeAdminConsolePaymentChannelBtn")?.addEventListener("click", () =>
    hideModal($("#adminConsolePaymentChannelModal"))
  );
  $("#cancelAdminConsolePaymentChannelBtn")?.addEventListener("click", () =>
    hideModal($("#adminConsolePaymentChannelModal"))
  );
  $("#probeAdminConsolePaymentChannelBtn")?.addEventListener("click", () => void probeAdminPaymentChannel());
  $("#restoreAdminConsolePaymentChannelBtn")?.addEventListener("click", () => void restoreAdminPaymentChannel());
  const paymentChannelForm = $("#adminConsolePaymentChannelForm");
  paymentChannelForm?.elements?.enabled?.addEventListener("change", (event) =>
    renderAdminPaymentChannelEnabledState(Boolean(event.currentTarget.checked))
  );
  paymentChannelForm?.elements?.apiBase?.addEventListener("input", (event) =>
    validateAdminPaymentChannelUrl(event.currentTarget, "adminConsolePaymentApiBaseError")
  );
  paymentChannelForm?.elements?.notifyUrl?.addEventListener("input", (event) =>
    validateAdminPaymentChannelUrl(event.currentTarget, "adminConsolePaymentNotifyUrlError")
  );
  $("#adminConsolePaymentChannelForm")?.addEventListener("submit", submitAdminPaymentChannel);
  $("#closeAdminConsoleLifecycleBtn")?.addEventListener("click", () => hideModal($("#adminConsoleLifecycleModal")));
  $("#cancelAdminConsoleLifecycleBtn")?.addEventListener("click", () => hideModal($("#adminConsoleLifecycleModal")));
  $("#adminConsoleLifecycleForm")?.addEventListener("submit", submitAdminConsoleLifecycle);
  $("#adminConsoleCreateAccountBtn")?.addEventListener("click", () => {
    const modal = $("#adminConsoleCreateAccountModal");
    if (!modal) return;
    /* 三个输入框每次打开都清空：上一次的邮箱/用户名留着会让人以为是在改那个账号。 */
    for (const id of [
      "adminConsoleCreateAccountUsername",
      "adminConsoleCreateAccountEmail",
      "adminConsoleCreateAccountName",
      "adminConsoleCreateAccountReason",
    ]) {
      const node = document.getElementById(id);
      if (node) node.value = "";
    }
    if (typeof showModal === "function") showModal(modal);
  });
  $("#closeAdminConsoleCreateAccountBtn")?.addEventListener("click", () => hideModal($("#adminConsoleCreateAccountModal")));
  $("#cancelAdminConsoleCreateAccountBtn")?.addEventListener("click", () => hideModal($("#adminConsoleCreateAccountModal")));
  $("#adminConsoleCreateAccountForm")?.addEventListener("submit", submitAdminConsoleCreateAccount);
  /* 会员两个动作共用一个弹层：靠 dataset.membershipAction 区分，避免两套几乎一样的表单。 */
  $("#adminConsoleGrantMembershipBtn")?.addEventListener("click", () => openAdminConsoleMembershipModal("grant-days"));
  $("#adminConsoleSetExpiryBtn")?.addEventListener("click", () => openAdminConsoleMembershipModal("set-expiry"));
  $("#closeAdminConsoleMembershipBtn")?.addEventListener("click", () => hideModal($("#adminConsoleMembershipModal")));
  $("#cancelAdminConsoleMembershipBtn")?.addEventListener("click", () => hideModal($("#adminConsoleMembershipModal")));
  $("#adminConsoleMembershipForm")?.addEventListener("submit", submitAdminConsoleMembership);
}

// 被封账号的界面提示：登录后拉一次自己的受限状态，顶部横幅说明"封禁到什么时候"。
// 读不到就当作没受限（横幅是提示，不是权限判定 —— 真正的拦截在后端统一入口）。
// 已经不处理"被删"：软删账号登不进来（登录环节就吊销了凭据），而且接口也刻意不回答
// "这个账号还在不在"（见 server/http_routes/account.py），所以这里没有可显示、也不该
// 显示的东西 —— 曾经那句「该账号已被删除…」等于把账号存在性告诉访客。
function accountRestrictionText(restriction) {
  const source = restriction || {};
  if (!source.banned) return "";
  const label = String(source.banLabel || "").trim();
  const suffix = label ? `（${label}）` : "";
  const until = formatAdminConsoleTime(source.bannedUntil);
  return until
    ? `账号已被封禁至 ${until}，期间无法使用任何功能${suffix}`
    : `账号已被封禁（未设到期时间），期间无法使用任何功能${suffix}`;
}

function renderAccountRestrictionBanner(restriction) {
  const node = document.getElementById("accountRestrictionBanner");
  if (!node) return;
  const text = accountRestrictionText(restriction);
  if (!text) {
    node.className = "ops-banner hidden";
    node.innerHTML = "";
    return;
  }
  node.className = "ops-banner ops-banner--warn account-restriction-banner";
  node.innerHTML = `<i data-lucide="ban"></i><span>${escapeHtml(text)}</span>`;
  if (typeof refreshIcons === "function") refreshIcons();
}

async function loadAccountRestriction() {
  try {
    const data = await getBackend("/api/account/restriction");
    const restriction = data?.restriction || null;
    renderAccountRestrictionBanner(restriction);
    return restriction;
  } catch (_error) {
    renderAccountRestrictionBanner(null);
    return null;
  }
}

function adminConsoleConfirm(message) {
  return typeof window.confirm === "function" ? window.confirm(message) : false;
}

// 平台级账号处置。密码只能"强制改密"（下次登录由用户自己设），操作者永远拿不到别人的密码。
// 这里放"确认后直接执行"的动作；需要填原因（+ 到期时间）的动作见下面的
// ADMIN_CONSOLE_LIFECYCLE_ACTIONS（走弹层，后端强制 reason + idempotencyKey）。
const ADMIN_CONSOLE_ACCOUNT_ACTIONS = {
  "force-password-change": {
    label: "强制该用户在下次登录时修改密码？（不会替他设置新密码）",
    done: "已标记下次登录需改密",
  },
  "revoke-sessions": { label: "踢出该账号的所有登录会话？", done: "已踢出全部会话" },
};

// 账号生命周期（P76/P77/P89 + 解除方向 P122）：软删、封禁/解除封禁。
// 全部要填原因（后端强制），封禁方向可选填到期时间（留空＝永久）。
// 「解除」必须在同一族里：后端对每个方向都要求 reason，前端少带一处就是一个必然 400 的死按钮。
const ADMIN_CONSOLE_LIFECYCLE_ACTIONS = {
  "soft-delete": {
    title: "软删账号",
    /* 口径（用户裁决 2026-09-15 晚）：软删后登录必须与「账号或密码错误」完全一样，
       不能让人看出「这个账号存在、只是被删了」——所以这里不再写「登录时提示账号不存在」。 */
    label: "该账号将不可访问、无法登录（登录表现与「账号或密码错误」一致，不透露账号状态）；数据保留，原邮箱与用户名会被释放以便重新注册。",
    done: "已软删该账号",
    expiry: false,
    reasonPlaceholder: "例如：用户申请注销 / 违规处置",
    danger: true,
  },
  ban: {
    title: "权限封禁",
    label: "该账号仍能登录，但所有功能受限，使用时提示「账号已被封禁至 xx」。留空到期时间＝永久封禁。",
    done: "已封禁该账号的功能权限",
    expiry: true,
    reasonPlaceholder: "例如：试用滥用 / 待核实的违规",
    danger: true,
  },
  "account-ban": {
    title: "账号封禁",
    label: "该账号将被阻止登录（登录页直接拦下）。留空到期时间＝永久封禁。",
    done: "已封禁该账号的登录",
    expiry: true,
    reasonPlaceholder: "例如：确认刷量 / 风控命中",
    danger: true,
  },
  unban: {
    title: "解除权限封禁",
    label: "解除该账号的权限封禁？解除后全部功能恢复可用。",
    done: "已解除权限封禁",
    expiry: false,
    reasonPlaceholder: "例如：封禁申诉通过 / 误封已核实",
  },
  "account-unban": {
    title: "解除账号封禁",
    label: "解除该账号的账号封禁？解除后该账号可以重新登录。",
    done: "已解除账号封禁",
    expiry: false,
    reasonPlaceholder: "例如：申诉通过 / 风控误判已核实",
  },
};

// 账号行「状态」列：生命周期状态优先于 Logto 的 isSuspended。
// 封禁到期后不能再退化显示 Logto 那个陈旧布尔值（P130）：账号封禁到期时应用层立刻不再
// 拦人（服务端 ban_is_active 是读取时动态算的），但 Logto 的 isSuspended 要等一轮解挂
// 清扫才会翻回去，中间会显示成「已挂起」——与「已到期」自相矛盾。
function adminConsoleAccountStatusLabel(row) {
  const restriction = row?.restriction || {};
  if (restriction.deleted) return "已删除";
  if (restriction.banned) {
    const label = restriction.banLabel || "封禁";
    const until = formatAdminConsoleTime(restriction.bannedUntil);
    return until ? `${label}至 ${until}` : `${label}（永久）`;
  }
  if (restriction.banExpired) {
    const until = formatAdminConsoleTime(restriction.banExpiredAt);
    return until ? `正常（封禁已于 ${until} 到期）` : "正常（封禁已到期）";
  }
  return row?.isSuspended ? "已挂起" : "正常";
}

function openAdminConsoleRolesModal(userId) {
  const modal = $("#adminConsoleRolesModal");
  const body = $("#adminConsoleRolesBody");
  const account = adminConsoleState.items.find((item) => String(item.id) === String(userId));
  if (!modal || !body || !account) return;
  modal.dataset.userId = account.id;
  const current = new Set(account.roles || []);
  /* 角色清单只认后端下发的：它和账号行来自同一次读，且带「这一档能干什么」的说明。 */
  const catalog = adminConsoleState.roleCatalog;
  const scope = $("#adminConsoleRolesScope");
  if (scope) scope.textContent = `账号：${account.username || account.id}`;
  if (!catalog.length) {
    body.innerHTML = `<p class="admin-console-permission-note">暂时读不到可授予的角色，请稍后重试。</p>`;
    showModal(modal);
    return;
  }
  body.innerHTML = catalog
    .map(
      (role) => `<label class="admin-console-role-option">
        <input type="radio" name="adminConsolePlatformRole" value="${escapeHtml(role.name)}" ${current.has(role.name) ? "checked" : ""} />
        <span class="admin-console-role-copy"><span>${escapeHtml(role.label || role.name)}</span>${
          role.hint ? `<small>${escapeHtml(role.hint)}</small>` : ""
        }</span>
      </label>`
    )
    .join("");
  showModal(modal);
}

async function submitAdminConsoleRoles(event) {
  event.preventDefault();
  const modal = $("#adminConsoleRolesModal");
  const userId = String(modal?.dataset.userId || "");
  if (!userId) return;
  /* 平台角色是能力下发入口，错一次就改不回来了，二次确认防手误。 */
  /* catalog 取 adminConsoleState 而不是 open 时的闭包：close→reopen 后状态可能已变。 */
  const catalog = adminConsoleState.roleCatalog || [];
  const roles = Array.from(modal.querySelectorAll("input[type=radio]:checked")).map((input) => input.value);
  const account = adminConsoleState.items.find((item) => String(item.id) === String(userId));
  const labels = roles
    .map((name) => {
      const entry = catalog.find((role) => role.name === name);
      return entry ? entry.label || entry.name : name;
    })
    .filter(Boolean);
  const summary = labels.length ? labels.join("、") : "（无任何角色）";
  const target = account.username || account.id;
  const confirmed = typeof window.confirm === "function"
    ? window.confirm(`确认把账号 ${target} 的平台角色改为「${summary}」吗？\n（角色变更会立即生效并写入审计）`)
    : true;
  if (!confirmed) return;
  try {
    await postBackend(`/api/admin/users/${encodeURIComponent(userId)}/roles`, { roles });
    hideModal(modal);
    toast("平台角色已更新");
    refreshAdminConsole();
  } catch (error) {
    toast(error?.message || "角色更新失败", { kind: "error", duration: 6000 });
  }
}

// 每个动作的路径都写字面量：前端契约测试会从源码反推调用面，用 ${action} 拼出来的
// 通用模板反推不出可校验的路由（会被当成 /api/admin/users/{id}/{id}）。
const ADMIN_CONSOLE_ACCOUNT_ENDPOINTS = {
  "account-ban": (userId) => `/api/admin/users/${encodeURIComponent(userId)}/account-ban`,
  "account-unban": (userId) => `/api/admin/users/${encodeURIComponent(userId)}/account-unban`,
  ban: (userId) => `/api/admin/users/${encodeURIComponent(userId)}/ban`,
  unban: (userId) => `/api/admin/users/${encodeURIComponent(userId)}/unban`,
  "soft-delete": (userId) => `/api/admin/users/${encodeURIComponent(userId)}/soft-delete`,
  "force-password-change": (userId) => `/api/admin/users/${encodeURIComponent(userId)}/force-password-change`,
  "revoke-sessions": (userId) => `/api/admin/users/${encodeURIComponent(userId)}/revoke-sessions`,
};

// 幂等键由浏览器生成（后端也认 Idempotency-Key 头，这里统一走 body 的 idempotencyKey）。
function adminConsoleIdempotencyKey() {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ? `acct-${uuid}` : `acct-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// 「到期时间」输入是 datetime-local（本地时区），后端收的是带时区的 ISO 字符串。
function adminConsoleExpiryIso(rawValue) {
  const text = String(rawValue || "").trim();
  if (!text) return null;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

async function runAdminConsoleAccountAction(userId, action) {
  const spec = ADMIN_CONSOLE_ACCOUNT_ACTIONS[action];
  const endpoint = ADMIN_CONSOLE_ACCOUNT_ENDPOINTS[action];
  if (!spec || !endpoint) return;
  if (!adminConsoleConfirm(spec.label)) return;
  try {
    const result = await postBackend(endpoint(userId), {});
    if (action === "revoke-sessions") {
      toast(`${spec.done}（${formatAdminConsoleNumber(result.revoked)} 个会话）`);
    } else {
      toast(spec.done);
    }
    refreshAdminConsole();
  } catch (error) {
    toast(error?.message || "账号操作失败", { kind: "error", duration: 6000 });
  }
}

function openAdminConsoleLifecycleModal(userId, action) {
  const spec = ADMIN_CONSOLE_LIFECYCLE_ACTIONS[action];
  const modal = $("#adminConsoleLifecycleModal");
  if (!spec || !modal) return;
  const account = adminConsoleState.items.find((item) => String(item.id) === String(userId));
  modal.dataset.userId = String(userId);
  modal.dataset.accountAction = action;
  const scope = $("#adminConsoleLifecycleScope");
  if (scope) scope.textContent = `账号 ${account?.username || account?.primaryEmail || userId}`;
  const title = $("#adminConsoleLifecycleTitle");
  if (title) title.textContent = spec.title;
  const hint = $("#adminConsoleLifecycleHint");
  if (hint) {
    // 封禁/软删/账号封禁是"碰就恢复成本极高"的处置：后果描述用 is-danger 警示样式，
    // 运营不能把红底当装饰——它是后果提醒，不是按钮色。其它方向（解除封禁/解除账号封禁等）
    // 没有这个 `danger` 标记，回落到正常灰字。
    hint.textContent = spec.label;
    hint.classList.toggle("is-danger", spec.danger === true);
  }
  const reason = $("#adminConsoleLifecycleReason");
  if (reason) {
    reason.value = "";
    reason.placeholder = spec.reasonPlaceholder || "请填写原因（必填）";
  }
  // 软删不涉及到期时间：整行隐藏，避免用户以为能"定时删号"。
  const expiryField = $("#adminConsoleLifecycleExpiryField");
  if (expiryField) expiryField.hidden = !spec.expiry;
  const expiry = $("#adminConsoleLifecycleExpiry");
  if (expiry) expiry.value = "";
  if (typeof showModal === "function") showModal(modal);
}

async function submitAdminConsoleLifecycle(event) {
  event.preventDefault();
  const modal = $("#adminConsoleLifecycleModal");
  const userId = String(modal?.dataset.userId || "");
  const action = String(modal?.dataset.accountAction || "");
  const spec = ADMIN_CONSOLE_LIFECYCLE_ACTIONS[action];
  const endpoint = ADMIN_CONSOLE_ACCOUNT_ENDPOINTS[action];
  if (!userId || !spec || !endpoint) return;
  const reason = String($("#adminConsoleLifecycleReason")?.value || "").trim();
  if (!reason) {
    toast("请填写处置原因", { kind: "error", duration: 4000 });
    return;
  }
  const bannedUntil = spec.expiry
    ? adminConsoleExpiryIso($("#adminConsoleLifecycleExpiry")?.value)
    : null;
  const button = $("#saveAdminConsoleLifecycleBtn");
  if (button) button.disabled = true;
  try {
    const result = await postBackend(endpoint(userId), {
      reason,
      bannedUntil,
      idempotencyKey: adminConsoleIdempotencyKey(),
    });
    if (modal && typeof hideModal === "function") hideModal(modal);
    // 回放（同一个幂等键重复提交）要说清楚"没有重复生效"，否则操作者会以为点了两次。
    toast(result?.alreadyApplied ? `${spec.done}（此前已生效，未重复执行）` : spec.done);
    refreshAdminConsole();
  } catch (error) {
    toast(error?.message || "账号处置失败", { kind: "error", duration: 6000 });
  } finally {
    if (button) button.disabled = false;
  }
}

/* 会员弹层的租户选择器是**运行时注入**的：index.html 里这个弹层只有一格「租户 ID」，
   于是工具栏那条路径（列表里没有这一行、或压根不想在表里找）只能靠人肉抄 ID。
   注入而不是改 index.html：这一格是纯展示层的补丁，没必要动整档结构。
   行内路径本来就是预填 ID，这里补齐"换成按名字搜"的能力，两条路径行为一致。 */
function ensureAdminConsoleMembershipTenantPicker() {
  const form = $("#adminConsoleMembershipForm");
  const tenantInput = $("#adminConsoleMembershipTenant");
  if (!form || !tenantInput) return;
  if ($("#adminConsoleMembershipTenantSearch")) return;
  const anchor = tenantInput.closest("label");
  if (!anchor) return;
  const box = document.createElement("div");
  box.className = "admin-console-adjust-tenant";
  box.innerHTML = `<span class="admin-console-adjust-direction-label">按名字找</span>
    <div class="admin-console-tenant-search">
      <input id="adminConsoleMembershipTenantSearch" type="search" placeholder="搜索租户名称（例如：内容中台小组）" autocomplete="off" aria-controls="adminConsoleMembershipTenantResults" />
      <div class="admin-console-tenant-results hidden" id="adminConsoleMembershipTenantResults" role="listbox" aria-label="租户搜索结果"></div>
    </div>`;
  form.insertBefore(box, anchor);
}

function hideAdminConsoleMembershipResults() {
  const results = $("#adminConsoleMembershipTenantResults");
  if (!results) return;
  results.classList.add("hidden");
  results.innerHTML = "";
}

// 点选回填：写进那格租户 ID（提交读的就是它），同时把"当前到期"提示刷成本次选中的租户。
function adminConsoleMembershipSetTenant(tenantId, tenantName = "") {
  const id = String(tenantId || "").trim();
  const name = String(tenantName || "").trim();
  const input = $("#adminConsoleMembershipTenant");
  if (input) input.value = id;
  const modal = $("#adminConsoleMembershipModal");
  if (modal) modal.dataset.membershipTenantName = name;
  const scope = $("#adminConsoleMembershipScope");
  if (!scope) return;
  if (!id) {
    scope.textContent = "尚未选择租户";
    return;
  }
  const row = adminConsoleState.items.find((item) => String(item.tenantId) === id);
  const until = row?.expiresAt
    ? `当前到期：${formatAdminConsoleTime(row.expiresAt)}`
    : "该租户当前没有生效的会员到期日";
  scope.textContent = `${name ? `${name} · ` : ""}${until}`;
}

async function adminConsoleMembershipSearch(q) {
  const results = $("#adminConsoleMembershipTenantResults");
  if (!results) return;
  const query = String(q || "").trim();
  if (!query) {
    hideAdminConsoleMembershipResults();
    return;
  }
  results.classList.remove("hidden");
  results.innerHTML =
    '<div class="admin-console-tenant-result admin-console-tenant-result--status">搜索中…</div>';
  try {
    results.innerHTML = adminConsoleTenantResultsHtml(
      await adminConsoleTenantSearchItems(query),
      "没有匹配的租户，可直接在下面手填租户 ID",
      "membership"
    );
  } catch (error) {
    results.innerHTML =
      '<div class="admin-console-tenant-result admin-console-tenant-result--status">搜索失败：' +
      escapeHtml(error?.message || "请稍后重试") +
      "</div>";
  }
}

// 会员处置（P79 发放 / P80 到期日调整）：目标租户要么从列表里选，要么手填租户 ID
// （未开通计费的租户可能还没有会员行，列表里看不到）。
const ADMIN_CONSOLE_MEMBERSHIP_ACTIONS = {
  "grant-days": {
    title: "发放会员",
    label: "在当前到期日上加 N 天（已过期的从今天算起）。这是增量动作，不会覆盖已有到期日。",
    done: "已发放会员天数",
    endpoint: "/api/admin/memberships/grant",
  },
  "set-expiry": {
    title: "调整会员到期日",
    label: "把到期日改成指定的那一天（绝对值）。早于当前到期日就是缩短；不能设到已经过去的日期。",
    done: "已调整会员到期日",
    endpoint: "/api/admin/memberships/set-expiry",
  },
};

function openAdminConsoleMembershipModal(action, tenantId = "") {
  const spec = ADMIN_CONSOLE_MEMBERSHIP_ACTIONS[action];
  const modal = $("#adminConsoleMembershipModal");
  if (!spec || !modal) return;
  ensureAdminConsoleMembershipTenantPicker();
  modal.dataset.membershipAction = action;
  const title = $("#adminConsoleMembershipTitle");
  if (title) title.textContent = spec.title;
  const hint = $("#adminConsoleMembershipHint");
  if (hint) hint.textContent = spec.label;
  const tenant = $("#adminConsoleMembershipTenant");
  if (tenant) tenant.value = String(tenantId || "");
  /* 搜索框与结果每次打开都清空：上一轮的搜索结果留着，会让人以为新弹层已经搜过了。 */
  const picker = $("#adminConsoleMembershipTenantSearch");
  if (picker) picker.value = "";
  hideAdminConsoleMembershipResults();
  const scope = $("#adminConsoleMembershipScope");
  const row = adminConsoleState.items.find((item) => String(item.tenantId) === String(tenantId));
  if (scope) {
    scope.textContent = row?.expiresAt
      ? `当前到期：${formatAdminConsoleTime(row.expiresAt)}`
      : "该租户当前没有生效的会员到期日";
  }
  const isGrant = action === "grant-days";
  const daysField = $("#adminConsoleMembershipDaysField");
  if (daysField) daysField.hidden = !isGrant;
  const expiryField = $("#adminConsoleMembershipExpiryField");
  if (expiryField) expiryField.hidden = isGrant;
  if ($("#adminConsoleMembershipDays")) $("#adminConsoleMembershipDays").value = "30";
  if ($("#adminConsoleMembershipExpiry")) $("#adminConsoleMembershipExpiry").value = "";
  if ($("#adminConsoleMembershipReason")) $("#adminConsoleMembershipReason").value = "";
  if (typeof showModal === "function") showModal(modal);
}

async function submitAdminConsoleMembership(event) {
  event.preventDefault();
  const modal = $("#adminConsoleMembershipModal");
  const action = String(modal?.dataset.membershipAction || "");
  const spec = ADMIN_CONSOLE_MEMBERSHIP_ACTIONS[action];
  if (!spec) return;
  const tenantId = String($("#adminConsoleMembershipTenant")?.value || "").trim();
  if (!tenantId) {
    toast("请填写租户 ID", { kind: "error", duration: 4000 });
    return;
  }
  const reason = String($("#adminConsoleMembershipReason")?.value || "").trim();
  if (!reason) {
    toast("请填写处置原因", { kind: "error", duration: 4000 });
    return;
  }
  const payload = { tenantId, reason, idempotencyKey: adminConsoleIdempotencyKey() };
  if (action === "grant-days") {
    const days = Number($("#adminConsoleMembershipDays")?.value);
    if (!Number.isInteger(days) || days < 1) {
      toast("请填写为正整数的天数", { kind: "error", duration: 4000 });
      return;
    }
    payload.days = days;
  } else {
    const expiresAt = adminConsoleExpiryIso($("#adminConsoleMembershipExpiry")?.value);
    if (!expiresAt) {
      toast("请选择到期日", { kind: "error", duration: 4000 });
      return;
    }
    payload.expiresAt = expiresAt;
  }
  const button = $("#saveAdminConsoleMembershipBtn");
  if (button) button.disabled = true;
  try {
    const result = await postBackend(spec.endpoint, payload);
    if (modal && typeof hideModal === "function") hideModal(modal);
    const until = formatAdminConsoleTime(result?.membership?.expiresAt);
    const suffix = result?.alreadyApplied ? "（此前已生效，未重复执行）" : "";
    toast(`${spec.done}${until ? `，到期 ${until}` : ""}${suffix}`);
    refreshAdminConsole();
  } catch (error) {
    toast(error?.message || "会员处置失败", { kind: "error", duration: 6000 });
  } finally {
    if (button) button.disabled = false;
  }
}

// P81 建号：管理员只给用户名/邮箱/昵称与原因，没有密码字段。
async function submitAdminConsoleCreateAccount(event) {
  event.preventDefault();
  const username = String($("#adminConsoleCreateAccountUsername")?.value || "").trim();
  const email = String($("#adminConsoleCreateAccountEmail")?.value || "").trim();
  const name = String($("#adminConsoleCreateAccountName")?.value || "").trim();
  const reason = String($("#adminConsoleCreateAccountReason")?.value || "").trim();
  if (!username || !email) {
    toast("用户名与邮箱都是必填", { kind: "error", duration: 4000 });
    return;
  }
  if (!reason) {
    toast("请填写创建原因", { kind: "error", duration: 4000 });
    return;
  }
  const button = $("#saveAdminConsoleCreateAccountBtn");
  if (button) button.disabled = true;
  try {
    const result = await postBackend("/api/admin/users", {
      username,
      email,
      name,
      reason,
      idempotencyKey: adminConsoleIdempotencyKey(),
    });
    const modal = $("#adminConsoleCreateAccountModal");
    if (modal && typeof hideModal === "function") hideModal(modal);
    // 建出来的账号没有密码：把"下一步怎么让本人进来"如实说清楚，别让运营以为已经能登录了。
    toast(`账号 ${result?.created?.username || username} 已创建（未设密码）；${result?.signInHint || ""}`, {
      duration: 9000,
    });
    refreshAdminConsole();
  } catch (error) {
    toast(error?.message || "创建账号失败", { kind: "error", duration: 6000 });
  } finally {
    if (button) button.disabled = false;
  }
}

function openAdminConsoleRefundModal(orderId) {
  const modal = $("#adminConsoleRefundModal");
  const form = $("#adminConsoleRefundForm");
  const order = adminConsoleState.items.find((item) => String(item.id) === String(orderId));
  if (!modal || !form || !order) return;
  modal.dataset.orderId = order.id;
  const scope = $("#adminConsoleRefundScope");
  if (scope) {
    /* 高风险资金动作的确认摘要：多少钱、退给谁、能不能撤回，逐项写在按钮上方。
       第一行是"退哪一单"，第二行是"点了会发生什么"——最后半句照 server/billing_service.py
       的真实语义写：渠道一旦受理就没有应用侧的撤销路径（只能线下协商），渠道拒绝时本单
       完全不动（口径与 422 refund_rejected 的话术一致）。
       `?? "—"` 不是装饰：订单投影缺字段时曾经直接把 undefined 印在摘要里。 */
    const product = adminConsoleStatusLabel(ADMIN_CONSOLE_PRODUCT_KINDS, order.productKind);
    const amount = formatAdminConsoleMoney(order.amountRmb);
    const credits = formatAdminConsoleNumber(order.credits);
    const days = Number(order.membershipDays) > 0 ? ` · ${formatAdminConsoleNumber(order.membershipDays)} 天` : "";
    const identity = `订单 ${String(order.id ?? "—")} · ${product} · ${amount} · ${credits} 积分${days} · 租户 ${String(
      order.tenantId || "—"
    )}`;
    const risk = `确认后先请求支付渠道原路退回 ${amount}；渠道受理才冲正 ${credits} 积分。渠道受理后无法在本页撤回（只能线下协商），渠道拒绝则本单不做任何变更。`;
    scope.innerHTML = `<span class="admin-console-refund-identity">${escapeHtml(
      identity
    )}</span><span class="admin-console-refund-risk">${escapeHtml(risk)}</span>`;
  }
  form.reset();
  showModal(modal);
}

async function submitAdminConsoleRefund(event) {
  event.preventDefault();
  const modal = $("#adminConsoleRefundModal");
  const form = $("#adminConsoleRefundForm");
  const orderId = modal?.dataset.orderId || "";
  const reason = String(form?.elements?.reason?.value || "").trim();
  // 退款是资金动作，原因是对账与客诉复盘唯一的线索：空原因不发请求，与同页「账号处置」
  // 那几处 if(!reason) 拦截保持同一套做法（P105）。后端也会再拦一次。
  if (!reason) {
    toast("请填写退款原因", { kind: "error", duration: 4000 });
    return;
  }
  const submitButton = $("#saveAdminConsoleRefundBtn");
  const done = setButtonLoading(submitButton, "退款中…");
  try {
    const result = await postBackend(
      `/api/admin/payment-orders/${encodeURIComponent(orderId)}/refund`,
      { reason }
    );
    hideModal(modal);
    const channel = result?.channelRefund || {};
    if (result?.alreadyRefunded) {
      toast("该订单已退款，无需重复操作");
    } else if (channel.status === "succeeded") {
      // 只有渠道真的受理了才说"退款成功"；否则一律照实说钱还在渠道那边（P102）。
      toast("退款成功：渠道已受理，积分已冲正", { duration: 9000 });
    } else {
      toast(channel.message || "积分已冲正；渠道退款未执行，请线下处理退款", { kind: "error", duration: 12000 });
    }
    refreshAdminConsole();
  } catch (error) {
    /* 「退款失败」这四个字只能给"这笔钱没动过"的结果。
       502 refund_outcome_unknown 与 409 refund_in_progress 都不是失败：前者订单已转
       「退款中」、积分已冲正，只是渠道那边的结果还不知道；后者是这笔单本来就在退。
       给它们套上「退款失败」就是把人推向"再点一次"，而那正是重复退款的入口
       （口径与 server/billing_service.py 的抢占式状态更新、以及 backend-errors.js 里
       这两条码的话术一致）。 */
    const code = String(error?.errorCode || "");
    if (code === "refund_outcome_unknown" || code === "refund_in_progress") {
      toast(error.message, { kind: "error", duration: 15000 });
    } else {
      toast(`退款失败：${error.message}`, { kind: "error", duration: 6000 });
    }
  } finally {
    done();
  }
}

// 订单已到账（回调轮询到 paid，或 0 元档下单即 paid）：换发令牌 → 刷新余额 → 成功态。
// 成功态只能由用户点「完成」关闭（遮罩与 X 按钮在成功态都不响应），所以这里必须先置位。
async function finishBillingCheckout() {
  billingCheckoutSucceeded = true;
  // 已到账：那张"待支付"记录到此为止，别在切组织时再提醒它。
  billingPendingOrder = null;
  stopBillingCheckoutPolling();
  // 支付回调可能跨域跳转过讯虎收银台，回到 SPA 后 Logto SDK 缓存里的 access token
  // 可能已是充值前签发的旧值或被刷新失败；若不强制换发，后续 AI 调用会走 anonymous
  // 兼容路径调通但 AuthContext 退化为 anonymous，扣费链路被静默跳过（AI 生图/改写
  // 成功但不扣积分、管理端流水也不写）。forceRefresh 让 SDK 直接换发新 token。
  try {
    await globalThis.xhsSpaAuth?.refreshAccessToken?.();
  } catch (_error) {
    // 刷新失败不阻塞支付成功提示：loadBillingStatus 仍能以现有 token 拉余额。
  }
  try {
    await loadBillingStatus();
  } catch (_error) {
    // 余额读不到也不把人卡在收银台（原实现会静默停在这里，轮询已停、成功态永不出现）。
  }
  renderBillingStatus();
  const target = $("#billingModalContent");
  if (target) target.innerHTML = billingSuccessMarkup();
}

// 记下"这张单还没付、它是哪个组织的"。只在这次会话里有意义（刷新后只有服务端知道），所以放内存。
function rememberPendingBillingOrder(order) {
  if (!order?.id) return;
  const active = activeOrganizationChoice();
  billingPendingOrder = {
    id: String(order.id),
    tenantId: String(active?.id || ""),
    tenantLabel: active ? organizationLabelFor(active) : "",
    amountText: billingMoneyText(order.amountRmb),
    expiresAt: Number(order.expiresAt) || 0,
  };
}

// 切换组织时那句提醒。只在"上一个组织确有未支付单、且还没关单"时给一句——未支付单绑组织，
// 切过去它不会跟过来，而用户很容易以为"切了还能继续付"，回头发现订单不在新组织里。
// 拿不到 expiresAt 时不编时长，只说"切回去"（同 billingCheckoutHint 的口径）。
function pendingBillingOrderSwitchNotice(previousTenantId) {
  const pending = billingPendingOrder;
  const previous = String(previousTenantId || "");
  if (!pending || !previous || pending.tenantId !== previous) return "";
  if (pending.expiresAt && pending.expiresAt * 1000 <= Date.now()) return "";
  const owner = pending.tenantLabel ? `「${pending.tenantLabel}」` : "上一个组织";
  return `${owner}还有一张未支付的订单（${pending.amountText}）。它属于${owner}，不会跟着切换过来；要完成这笔支付，请切回${owner}。`;
}

async function createBillingOrder(product, options = {}) {
  // 默认不带幂等键：前端每次点击生成一把新键（曾经是 billing:<商品>:<时间戳>），服务端见键即采用、
  // 跳过「复用 60 秒内最近未支付单」的查询，于是「取消后再点」必定多出一张单。默认把键交给后端
  // 按商品+窗口自己决定，防重复这一层就只有一个事实源了。
  // 「重新生成二维码」是明确想要一张新单（旧二维码已失效），用 forceNew 说出这个意图。
  const payload = options.forceNew
    ? { product, idempotencyKey: `billing:${product}:${Date.now()}` }
    : { product };
  const data = await postBackend("/api/billing/orders", payload);
  const order = data?.order;
  if (!order) throw new Error("订单创建失败");

  stopBillingCheckoutPolling();
  billingCheckoutSucceeded = false;
  if (order.status === "paid") {
    // 0 元档：服务端没有调用支付网关，回来就是已到账。没有二维码可扫，也没有等待
    // 过程可轮询，直接进成功态（余额由 finishBillingCheckout 重新拉一次）。
    await finishBillingCheckout();
    return;
  }
  const content = $("#billingModalContent");
  if (content) content.innerHTML = billingCheckoutMarkup(order, order.status);
  bindBillingQrFallback(order);
  // 到这一步就是"确实有待支付的单"（0 元档上面已经直接进成功态）。
  rememberPendingBillingOrder(order);

  billingCheckoutTimer = setInterval(async () => {
    try {
      const detail = await getBackend(`/api/billing/orders/${encodeURIComponent(order.id)}`);
      const current = detail?.order?.status;
      if (current === "paid") {
        await finishBillingCheckout();
      } else if (current === "failed") {
        stopBillingCheckoutPolling();
        const statusNode = $("[data-billing-checkout-status]");
        if (statusNode) statusNode.textContent = "❌ 支付失败，请重试";
      }
    } catch (_error) {
      // keep polling on transient errors
    }
  }, 3000);
}

// 个人组织（后端按用户哈希命名，形如 "薯作台个人组织-<16位哈希>"）在界面上只叫
// "个人组织"；多人组织直接用全名。判断同时看名称前缀和已登记的个人组织 id，
// 后者在名称被改过时仍然准确。
const PERSONAL_ORGANIZATION_PREFIX = "薯作台个人组织";

// 组织名长度上限，与后端 server/identity_projection.py 的 ORGANIZATION_NAME_MAX_CHARS
// 一致。后端超长会 400 而不是截断，这里（连同输入框的 maxlength）只是提前拦住，
// 让用户当场看到中文提示而不是提交后才发现。
const ORGANIZATION_NAME_MAX_CHARS = 80;

function isPersonalOrganization(name, id) {
  const normalizedId = String(id || "").trim();
  if (normalizedId && normalizedId === globalThis.xhsSpaAuth?.getProvisionedOrganizationId?.()) {
    return true;
  }
  return String(name || "").trim().startsWith(PERSONAL_ORGANIZATION_PREFIX);
}

function organizationDisplayLabel(name, id) {
  if (isPersonalOrganization(name, id)) return "个人组织";
  return String(name || id || "").trim();
}

// ID token 里的组织名要等下一次换发才更新，重命名后先用本地覆盖值显示。
const organizationNameOverrides = new Map();

function activeOrganizationChoice() {
  const auth = globalThis.xhsSpaAuth;
  const activeId = String(auth?.getActiveOrganizationId?.() || "").trim();
  return (auth?.organizations?.() || []).find((item) => item.id === activeId) || null;
}

function organizationLabelFor(item) {
  const overridden = organizationNameOverrides.get(item?.id) || item?.name;
  return organizationDisplayLabel(overridden, item?.id);
}

// 「谁能管理这个组织」的角色档：与后端 `resource_authorization.TENANT_ADMIN_ROLES`
// （owner / admin / 历史别名 tenant_admin）**同一档**——Owner 与 Admin 判权相同。
// 归一化口径也照后端（casefold + '-'→'_'）。**别再各写一遍**：平台侧一改判权，这里是连带项，
// `tests/test_role_sets_single_source.py` 会把前端这一份与后端断言相等。
const ORGANIZATION_ADMIN_ROLES = ["owner", "admin", "tenant_admin"];

function rolesCanManageOrganization(roles) {
  const list = Array.isArray(roles) ? roles : String(roles || "").split(/[,\s]+/);
  return list
    .map((role) => String(role).trim().toLowerCase().replace(/-/g, "_"))
    .some((role) => ORGANIZATION_ADMIN_ROLES.includes(role));
}

// 计费页的主语：当前组织（名字 + 我在里面的角色）。会员与积分都归**组织**，所以买之前必须
// 能看清"这笔买给谁"；切组织时这些值会变，因此每次渲染现算，不缓存。
//
// `purchasableElsewhere` 是"我在别的组织里有购买权"。有它才能把"不能买"说对：一个人同时管着
// 几个组织时，看到"你不是本组织的管理员"最可能的原因是**选错了组织**，而不是没权限——只报
// 权限会把人引向客服，其实顶栏切一下就能买。
function billingSubject() {
  const organizations = globalThis.xhsSpaAuth?.organizations?.() || [];
  const active = activeOrganizationChoice();
  const activeId = String(active?.id || "");
  return {
    organizationCount: organizations.length,
    label: active ? organizationLabelFor(active) : "",
    canPurchase: Boolean(active) && rolesCanManageOrganization(active?.roles),
    purchasableElsewhere: organizations
      .filter((item) => String(item?.id || "") !== activeId && rolesCanManageOrganization(item?.roles))
      .map((item) => organizationLabelFor(item)),
  };
}

// 组织选择器按当前标签给确定宽度。<select> 的固有宽度取"最宽的选项"，短标签（如
// "个人组织"）会白占一大片，长标签又会把顶栏动作区顶出屏幕——确定宽度让参与父级
// 宽度计算的宽度和渲染宽度一致。中文字按全角估宽，超长时交给 CSS 省略号。
function fitOrganizationSelectWidth(select, label) {
  if (!select) return;
  const text = String(label || "切换组织");
  const weighted = [...text].reduce(
    (sum, ch) => sum + (/[\u2e80-\u9fff\uff01-\uff60]/.test(ch) ? 12.5 : 7),
    0
  );
  const width = Math.round(Math.min(220, Math.max(116, weighted + 46)));
  select.style.setProperty("--org-select-width", `${width}px`);
}

// 顶栏组织下拉的渲染。占位项不再写死在 HTML 里：只有"当前组织还没定位到"时才补一个
// 不可选的提示项，正常状态下下拉里只有真实组织。否则列表里常驻一项什么都不做的
// 「切换组织」，用户选中它既不生效也不回滚（P59）。
// 放在模块作用域是为了让更名成功后（submitRenameOrganization）能立刻重画一次顶栏，
// 而不必等下一次 token 换发才看到新名字（P51）。
function renderOrganizationSelect(auth) {
  const organizations = [
    document.querySelector("#authOrganizationSelect"),
    document.querySelector("#mobileOrganizationSelect"),
  ].filter(Boolean);
  if (!organizations.length) return [];
  const selected = auth?.getActiveOrganizationId?.() || "";
  const choices = auth?.organizations?.() || [];
  const hasSelected = choices.some((item) => item.id === selected);
  organizations.forEach((organization) => {
    organization.innerHTML = "";
    for (const item of choices) {
      const option = document.createElement("option");
      option.value = item.id;
      option.textContent = organizationLabelFor(item);
      option.title = isPersonalOrganization(item.name, item.id)
        ? "个人组织"
        : String(organizationNameOverrides.get(item.id) || item.name || item.id || "");
      organization.append(option);
    }
    if (!hasSelected) {
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = choices.length ? "选择组织" : "切换组织";
      organization.prepend(placeholder);
    }
    organization.value = hasSelected ? selected : "";
    organization.classList.toggle("hidden", choices.length === 0);
    const activeChoice = choices.find((item) => item.id === organization.value);
    const label = activeChoice ? organizationLabelFor(activeChoice) : "";
    organization.title = label || "切换组织";
    fitOrganizationSelectWidth(organization, organization.title);
  });
  return choices;
}

// 组织名有 80 字上限，而后端是"拒"不是"截"。输入框曾经靠 maxlength 砍掉粘贴进来的
// 超长内容——用户看不出自己少了一段，界面还弹「已更新」，服务端那条 400 永远触发不到
// （P51）。现在不截断，改为把当前字数与上限写在提示行里，提交时再按同一条判据报错。
function renderRenameOrganizationHint() {
  const hint = $("#renameOrganizationHint");
  const input = $("#renameOrganizationForm")?.elements?.name;
  if (!hint || !input) return;
  const length = String(input.value || "").length;
  const overLimit = length > ORGANIZATION_NAME_MAX_CHARS;
  hint.textContent = overLimit
    ? `组织名称最多 ${ORGANIZATION_NAME_MAX_CHARS} 个字符，当前 ${length} 个：请精简后再保存。`
    : `组织内的成员都会看到这个名字，个人组织不参与更名。最多 ${ORGANIZATION_NAME_MAX_CHARS} 个字符（当前 ${length}）。`;
  hint.classList.toggle("is-over-limit", overLimit);
}

function openRenameOrganizationModal() {
  const modal = $("#renameOrganizationModal");
  const form = $("#renameOrganizationForm");
  const org = activeOrganizationChoice();
  if (!modal || !form || !org) return;
  form.elements.name.value = String(organizationNameOverrides.get(org.id) || org.name || "").trim();
  renderRenameOrganizationHint();
  showModal(modal);
  refreshIcons();
}

async function submitRenameOrganization(event) {
  event.preventDefault();
  const modal = $("#renameOrganizationModal");
  const form = $("#renameOrganizationForm");
  const button = $("#saveRenameOrganizationBtn");
  const org = activeOrganizationChoice();
  if (!modal || !form || !button || !org) return;
  const name = String(form.elements.name.value || "").trim();
  if (!name) {
    toast("请填写组织名称", { kind: "error" });
    return;
  }
  if (name.length > ORGANIZATION_NAME_MAX_CHARS) {
    toast(`组织名称最多 ${ORGANIZATION_NAME_MAX_CHARS} 个字符`, { kind: "error", duration: 5000 });
    return;
  }
  button.disabled = true;
  try {
    const result = await authManagementRequest("/api/auth/organization", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    // 顶栏标签读的是 ID token 里的组织名（组织 id 来自提供方、claims 里没有名字，所以
    // 名字是后端从提供方读回来的），它要等下一次换发才带上新名字。覆盖值就是为这一刻
    // 准备的，但还得主动重画一次下拉才会生效——否则界面停在旧名，用户以为没改成（P51）。
    // 以回包里的名字为准：那是提供方存下来的值，不是我们请求里的字符串。
    organizationNameOverrides.set(org.id, String(result?.name || "").trim() || name);
    renderOrganizationSelect(globalThis.xhsSpaAuth);
    hideModal(modal);
    toast("组织名称已更新");
    renderAuthManagement();
  } catch (error) {
    toast(`重命名失败：${error?.message || "请稍后重试"}`, { kind: "error", duration: 5000 });
  } finally {
    button.disabled = false;
  }
}

function bindRenameOrganizationModal() {
  $("#closeRenameOrganizationBtn")?.addEventListener("click", () => hideModal($("#renameOrganizationModal")));
  $("#cancelRenameOrganizationBtn")?.addEventListener("click", () => hideModal($("#renameOrganizationModal")));
  const form = $("#renameOrganizationForm");
  form?.addEventListener("submit", submitRenameOrganization);
  form?.elements?.name?.addEventListener("input", renderRenameOrganizationHint);
}

function syncMobileGovernanceVisibility() {
  const group = document.querySelector("#mobileDrawerGovernance");
  if (!group) return;
  const hasVisibleItem = Array.from(group.querySelectorAll(".mobile-drawer-item"))
    .some((node) => !node.classList.contains("hidden"));
  group.classList.toggle("hidden", !hasVisibleItem);
}

// 治理入口是一个下拉：团队治理（成员/邀请/令牌）与平台治理（计费/会员/定价）合并到
// 一个导航项里，靠这个容器在两者都不可见时整体隐藏。
function syncGovernanceNavVisibility() {
  const container = document.querySelector("#governanceNav");
  if (container) {
    const anyVisible = ["#authManagementNav", "#adminConsoleNav"].some((selector) => {
      const node = document.querySelector(selector);
      return Boolean(node) && !node.classList.contains("hidden");
    });
    container.classList.toggle("hidden", !anyVisible);
    if (!anyVisible) {
      container.querySelector("#governanceNavMenu")?.classList.add("hidden");
      container.querySelector("#governanceNavBtn")?.setAttribute("aria-expanded", "false");
    }
  }
  syncMobileGovernanceVisibility();
}

function authManagementSetNavVisible(visible) {
  ["#authManagementNav", "#authManagementSidebarNav", "#mobileDrawerAuthMgmt"].forEach((selector) => {
    document.querySelector(selector)?.classList.toggle("hidden", !visible);
  });
  syncGovernanceNavVisibility();
}

// The management console tab is only meaningful for platform admins and tenant
// owners. Visibility mirrors the very probe the admin mount uses, so the entry
// never appears for accounts the backend would reject with 403.
function adminConsoleSetNavVisible(visible) {
  ["#adminConsoleNav", "#adminConsoleSidebarNav", "#mobileDrawerAdminConsole"].forEach((selector) => {
    document.querySelector(selector)?.classList.toggle("hidden", !visible);
  });
  syncGovernanceNavVisibility();
}

async function refreshAdminConsoleAccess() {
  const auth = globalThis.xhsSpaAuth;
  const authenticated = auth?.configured
    ? await auth.isAuthenticated?.().catch(() => false)
    : false;
  if (authenticated) {
    const outcome = await probeAdminConsoleAccess();
    if (outcome !== "denied") {
      adminConsoleSetNavVisible(true);
      return outcome === "granted";
    }
  }
  adminConsoleSetNavVisible(false);
  if (state.currentSection === "admin-console") setSection("home");
  return false;
}

// 会话内缓存：普通成员登录后第一次 403 就定论，后面 auth-changed 再触发也不打
// /api/admin/session。能力位已从 /api/config 到位且没有 console.read 时，连第一次
// 探测都省掉（租户 Owner/Admin 不进平台治理台）。
let adminConsoleProbeOutcome = "";
let adminConsoleProbeInFlight = null;
// 登录后附加读（公告/封禁/新设备）：同一次会话只发一轮。
let accountSessionExtrasLoaded = false;

// Resolves "granted" (the backend minted an admin session), "denied" (the
// backend answered 403) or "unavailable" (network/5xx, e.g. the backend is
// restarting). Only a definite denial may hide the entry: hiding it on an
// unavailable backend costs the account its management console entry for the
// rest of the session, because this probe only runs when auth state settles.
async function probeAdminConsoleAccess() {
  if (adminConsoleProbeOutcome === "granted" || adminConsoleProbeOutcome === "denied") {
    return adminConsoleProbeOutcome;
  }
  // capabilities 已是数组且没有 console.read → 肯定进不了治理台，别去换 cookie。
  if (Array.isArray(adminConsoleState.capabilities)
    && !adminConsoleState.capabilities.includes("console.read")) {
    adminConsoleProbeOutcome = "denied";
    return "denied";
  }
  if (adminConsoleProbeInFlight) return adminConsoleProbeInFlight;
  adminConsoleProbeInFlight = (async () => {
    const attempts = 2;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        await ensureAdminSession();
        adminConsoleProbeOutcome = "granted";
        return "granted";
      } catch (error) {
        if (error?.status === 403) {
          adminConsoleProbeOutcome = "denied";
          return "denied";
        }
        if (attempt + 1 < attempts) await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    }
    return "unavailable";
  })();
  try {
    return await adminConsoleProbeInFlight;
  } finally {
    adminConsoleProbeInFlight = null;
  }
}

function authManagementDate(value) {
  if (!value) return "未设置";
  const date = new Date(Number(value) > 10 ** 11 ? Number(value) : String(value));
  return Number.isNaN(date.getTime()) ? escapeHtml(value) : date.toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" });
}

function authManagementMemberId(member) {
  return String(member?.user_id || member?.userId || member?.id || "").trim();
}

function authManagementRoles(member) {
  const roles = member?.organization_roles || member?.organizationRoles || member?.roles || [];
  return (Array.isArray(roles) ? roles : String(roles || "").split(/[,\s]+/)).map((role) => String(role).trim()).filter(Boolean);
}

function authManagementStatus(member) {
  return String(member?.membership_status || member?.membershipStatus || member?.status || "active").trim() || "active";
}

async function authManagementRequest(path, options = {}) {
  const response = await backendFetch(path, options);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    // 团队治理是独立于 client.js 的请求通道，后端在这条通道上回的是上游英文常量
    // （"Logto membership reconciliation required"），必须按 errorCode 换成中文再抛；
    // 没有登记码时退回 client.js 那套（Forbidden/Unauthorized 占位英文、状态码兜底）。
    const errorCode = String(payload.errorCode || "").trim();
    const error = new Error(
      // 401 的话术要区分"没登录"与"登录失效"，所以把这次请求的两项事实一并交给它
      // （与 core/client.js 的 backendError 同一口径）。
      response.status === 403
        ? "当前账号无法管理成员与邀请，请联系当前组织的管理员。"
        : backendErrorCodeMessage(errorCode, payload)
          || backendErrorMessage(payload, response.status, 0, {
            authAttached: response?.xhsAuthAttached,
            method: response?.xhsRequestMethod,
          })
    );
    error.status = response.status;
    error.errorCode = errorCode;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function confirmAuthMutation(message) {
  const params = new URLSearchParams(window.location.search);
  if (params.get("ui-audit") === "1" && params.has("ui-audit-auth-cancel")) {
    authManagementState.notice = "已取消权限操作";
    authManagementState.error = "";
    renderAuthManagement();
    return false;
  }
  return typeof window.confirm === "function" ? window.confirm(message) : false;
}

function renderAuthManagement() {
  const content = $("#authManagementContent");
  const notice = $("#authManagementNotice");
  if (!content) return;
  if (notice) {
    notice.textContent = authManagementState.error || authManagementState.notice || "";
    notice.classList.toggle("is-error", Boolean(authManagementState.error));
    notice.classList.toggle("is-success", Boolean(authManagementState.notice && !authManagementState.error));
  }
  if (authManagementState.loading) {
    content.innerHTML = `<div class="auth-management-loading"><i data-lucide="loader-circle"></i><span>正在读取权限数据…</span></div>`;
    window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
    return;
  }
  const tab = authManagementState.tab;
  const pendingInvitationCount = authManagementState.invitations.filter(
    (item) => authInvitationStatusKey(item.status) === "pending"
  ).length;
  const tabs = [
    ["members", "users", "成员"],
    ["invitations", "mail-plus", "发出的邀请", authManagementState.loaded.invitations ? pendingInvitationCount : 0],
    // 收件箱说的是"我被邀请加入别人的组织"，与上面两栏（管理当前组织）不是同一层级，
    // 单列一栏，不混进邀请记录里。
    ["inbox", "inbox", "收到的邀请"],
  ];
  const tabMarkup = tabs.map(([key, icon, label, count]) => `<button type="button" class="${tab === key ? "active" : ""}" role="tab" aria-selected="${tab === key}" data-auth-management-tab="${key}"><i data-lucide="${icon}"></i><span>${label}</span>${key === "invitations" && count ? `<small class="auth-tab-count" aria-label="${count} 条待接受">待接受 ${count}</small>` : ""}</button>`).join("");
  const body =
    tab === "inbox"
      ? (globalThis.xhsInvitationInbox?.renderPanel?.() || "")
      : governanceTeamUnavailable() || (tab === "members" ? renderAuthMembers() : renderAuthInvitations());
  content.innerHTML = `<div class="auth-management-tabs" role="tablist" aria-label="权限管理视图">${tabMarkup}</div><div class="auth-management-tab-panel">${body}</div>`;
  governanceBindCopyControls(content);
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

// 角色中文名 + 能干什么。四个英文词不解释的话，最容易给错权限。
// Owner 的说明**不能再写"包括转让组织"**：全站没有转让入口（docs/billing.md §二的实测表），
// 那是一句做不到的承诺。今天的判权现实是 Owner 与 Admin 同档（后端同属 TENANT_ADMIN_ROLES），
// 所以两者的话术也如实同义——差别只在"谁是所有者"这个名义上。
const AUTH_MEMBER_ROLES = {
  Viewer: { label: "只读", hint: "只能查看内容" },
  Editor: { label: "编辑", hint: "可创建与修改内容，不能管理成员" },
  Admin: { label: "管理员", hint: "可管理成员与内容，可为本组织开通会员与充值" },
  Owner: { label: "所有者", hint: "完整权限，可管理成员与内容，可为本组织开通会员与充值" },
};
const AUTH_MEMBER_PAGE_SIZE = 20;

function authMemberRoleLabel(role) {
  return AUTH_MEMBER_ROLES[role]?.label || role;
}

function authMemberRoleOptions(selected, { includeOwner = true } = {}) {
  return Object.entries(AUTH_MEMBER_ROLES)
    .filter(([value]) => includeOwner || value !== "Owner")
    .map(
      ([value, meta]) =>
        `<option value="${value}" ${selected === value ? "selected" : ""}>${meta.label}</option>`
    )
    .join("");
}

function authMemberRoleHint(role) {
  return AUTH_MEMBER_ROLES[role]?.hint || "请确认这个角色需要的权限范围";
}

function authMemberIdentity(member) {
  const id = authManagementMemberId(member);
  // 邮箱优先：绑定邮箱后成员列表一眼能认出是谁；都没有才退化到用户 ID。
  return {
    name: String(member?.email || member?.username || member?.name || id || "未命名成员").trim(),
    detail: [String(member?.name || "").trim(), id].filter(Boolean).join(" · "),
  };
}

function authMemberFiltered() {
  const query = String(authManagementState.memberQuery || "").trim().toLowerCase();
  if (!query) return authManagementState.members;
  return authManagementState.members.filter((member) => {
    const identity = authMemberIdentity(member);
    return `${identity.name} ${identity.detail}`.toLowerCase().includes(query);
  });
}

function renderAuthMembers() {
  const selfId = String(authManagementState.currentUserId || "").trim();
  // 重命名是 Owner/Admin 的治理动作。判权档只有一处定义（`ORGANIZATION_ADMIN_ROLES`，与后端
  // `_auth_can_manage_members` 同一份）；个人组织不参与更名；普通成员（Viewer/Editor）读不到
  // 成员列表（后端 403），按本人角色判，判不到就默认隐藏——别给一个后端必然拒绝的按钮
  // （受邀加入时默认角色是 Viewer，正是「给加入的组织更名保存失效」的现场）。
  const selfMember = authManagementState.members.find((member) => authManagementMemberId(member) === selfId);
  const selfRoles = selfMember ? authManagementRoles(selfMember) : [];
  const canRenameOrganization =
    isPlatformAdmin() || rolesCanManageOrganization(selfRoles);
  const activeOrg = activeOrganizationChoice();
  const renameEntry = activeOrg && !isPersonalOrganization(activeOrg.name, activeOrg.id) && canRenameOrganization
    ? `<button type="button" class="ghost-btn" id="renameOrganizationBtn"><i data-lucide="pencil"></i><span>重命名</span></button>`
    : "";
  // One Owner is the minimum an organization can stay manageable with, so the
  // sole owner's row must not offer removal either (the server refuses it too).
  const ownerIds = authManagementState.members
    .filter((member) => authManagementRoles(member).some((role) => String(role).toLowerCase() === "owner"))
    .map((member) => authManagementMemberId(member));
  const filtered = authMemberFiltered();
  const pageCount = Math.max(1, Math.ceil(filtered.length / AUTH_MEMBER_PAGE_SIZE));
  const page = Math.min(Math.max(1, Number(authManagementState.memberPage) || 1), pageCount);
  const visible = filtered.slice((page - 1) * AUTH_MEMBER_PAGE_SIZE, page * AUTH_MEMBER_PAGE_SIZE);
  const selected = authManagementState.selected || new Set();
  const hiddenSelectedCount = [...selected].filter((id) => !visible.some((member) => authManagementMemberId(member) === id)).length;
  const rows = visible.map((member) => {
    const id = authManagementMemberId(member);
    const roles = authManagementRoles(member);
    const status = authManagementStatus(member);
    const current = roles[0] || "Viewer";
    const isSelf = Boolean(id) && id === selfId;
    const isSoleOwner = !isSelf && ownerIds.length <= 1 && ownerIds.includes(id);
    const identity = authMemberIdentity(member);
    const action = isSelf
      ? `<span class="auth-self-marker">你自己</span>`
      : isSoleOwner
        ? `<button type="button" class="icon-btn danger-btn" disabled title="组织至少需要保留一名所有者" aria-label="组织至少需要保留一名所有者"><i data-lucide="user-minus"></i></button>`
        : `<button type="button" class="icon-btn danger-btn" data-auth-remove-member="${escapeHtml(id)}" title="移除成员" aria-label="移除成员"><i data-lucide="user-minus"></i></button>`;
    // 自己及唯一所有者不能在此降权，避免团队失去管理入口或所有者。
    const roleCell = isSelf || isSoleOwner
      ? `<div><span class="auth-role-readonly">${escapeHtml(authMemberRoleLabel(current))}</span>${isSoleOwner ? "<small>需保留至少一名所有者，请先指定另一名所有者。</small>" : ""}</div>`
      : `<select data-auth-member-role="${escapeHtml(id)}" data-current-role="${escapeHtml(current)}" title="修改角色" aria-label="修改 ${escapeHtml(identity.name)} 的角色">${authMemberRoleOptions(current)}</select>`;
    const protectedReason = isSelf ? "不能批量操作自己" : isSoleOwner ? "需保留至少一名所有者" : "";
    const selection = authManagementState.bulkMode ? `<td data-label="选择" class="auth-member-selection"><input type="checkbox" data-auth-member-select="${escapeHtml(id)}" ${selected.has(id) ? "checked" : ""} ${protectedReason ? "disabled" : ""} aria-label="选择 ${escapeHtml(identity.name)}${protectedReason ? `：${protectedReason}` : ""}" /></td>` : "";
    return `<tr>${selection}<td data-label="成员" class="auth-member-identity"><strong>${escapeHtml(identity.name === id ? "未命名成员" : identity.name)}</strong><small>${member.name ? `${escapeHtml(member.name)} · ` : ""}${governanceIdentity(id)}</small>${authManagementState.bulkMode && protectedReason ? `<small class="auth-member-protection">${protectedReason}</small>` : ""}</td><td data-label="角色" class="auth-member-role">${roleCell}</td><td data-label="状态" class="auth-member-status"><span class="auth-status ${status.toLowerCase() === "active" ? "is-active" : ""}">${escapeHtml(status === "active" ? "在组" : status)}</span></td><td data-label="操作" class="auth-member-actions">${action}</td></tr>`;
  }).join("");
  const legend = Object.entries(AUTH_MEMBER_ROLES)
    .map(([, meta]) => `<li><strong>${escapeHtml(meta.label)}</strong><span>${escapeHtml(meta.hint)}</span></li>`)
    .join("");
  const bulk = authManagementState.bulkMode
    ? `<div class="auth-bulk-bar"><span role="status">${selected.size ? `已选 ${selected.size} 人${hiddenSelectedCount ? `（含当前页未显示的 ${hiddenSelectedCount} 人）` : ""}` : "请选择需要操作的成员"}</span><button type="button" class="ghost-btn" data-auth-bulk-apply ${selected.size ? "" : "disabled"}><span>修改角色</span></button><button type="button" class="ghost-btn danger-btn" data-auth-bulk-remove ${selected.size ? "" : "disabled"}><span>移出团队</span></button><button type="button" class="ghost-btn" data-auth-bulk-clear><span>取消</span></button></div>`
    : "";
  const pager = pageCount > 1
    ? `<div class="auth-pager"><button type="button" class="ghost-btn" data-auth-member-page="prev" ${page <= 1 ? "disabled" : ""}><span>上一页</span></button><span>第 ${page} / ${pageCount} 页</span><button type="button" class="ghost-btn" data-auth-member-page="next" ${page >= pageCount ? "disabled" : ""}><span>下一页</span></button></div>`
    : "";
  const search = authManagementState.members.length > AUTH_MEMBER_PAGE_SIZE || authManagementState.memberQuery
    ? `<label class="auth-search"><i data-lucide="search"></i><input type="search" data-auth-member-search value="${escapeHtml(authManagementState.memberQuery || "")}" placeholder="按邮箱 / 用户名 / ID 搜索成员" /></label>`
    : "";
  const countLabel = authManagementState.memberQuery
    ? `匹配 ${filtered.length} / 共 ${authManagementState.members.length} 人`
    : `共 ${authManagementState.members.length} 人`;
  return `<section class="auth-management-panel auth-management-list auth-members-panel"><div class="auth-panel-heading"><div><span>当前组织</span><h3>成员列表</h3></div><div class="auth-heading-side"><button type="button" class="ghost-btn" data-auth-management-go-invite><i data-lucide="user-plus"></i><span>邀请成员</span></button>${renameEntry}${authManagementState.bulkMode ? "" : `<button type="button" class="ghost-btn" data-auth-bulk-start ${authManagementState.members.length ? "" : "disabled"}><span>批量管理</span></button>`}<span class="auth-member-count">${countLabel}</span></div></div>${search}${bulk}${rows ? `<div class="auth-table-wrap"><table class="auth-table auth-members-table ${authManagementState.bulkMode ? "is-selecting" : ""}"><thead><tr>${authManagementState.bulkMode ? "<th>选择</th>" : ""}<th>成员</th><th>角色</th><th>状态</th><th>操作</th></tr></thead><tbody>${rows}</tbody></table></div>` : `<div class="auth-empty"><i data-lucide="users-round"></i><strong>${authManagementState.memberQuery ? "没有匹配的成员" : "暂无成员"}</strong><span>${authManagementState.memberQuery ? "换个关键词试试。" : "当前组织还没有可管理的成员记录。"}</span></div>`}${pager}<div class="auth-role-legend-block"><p class="auth-role-legend-title">角色权限说明</p><ul class="auth-role-legend">${legend}</ul></div></section>`;
}

const AUTH_INVITATION_STATUSES = { pending: "待接受", accepted: "已接受", expired: "已过期", revoked: "已撤销" };

// 缺失或认不出的状态一律按"待接受"处理：与后端同一口径（查不到不等于已经结束），
// 也避免 Logto 将来多一个状态就把整行的按钮全判没了。
function authInvitationStatusKey(value) {
  const key = String(value || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(AUTH_INVITATION_STATUSES, key) ? key : "pending";
}

function authInvitationStatus(value) {
  return AUTH_INVITATION_STATUSES[authInvitationStatusKey(value)];
}

// 同一封邀请在不同状态下能干的事不同：待接受可以重发/撤销；已撤销、已过期只能
// "重新邀请"（重发即再发一封，不需要先撤销）；已接受什么都不给——人已经进组织了，
// 要请他出去得去成员列表，不是在这里点。按钮的数据属性保持一致，
// 事件绑定与既有用例都认这一套。
function authInvitationActionButton(kind, id) {
  const isRevoke = kind === "revoke";
  const title = isRevoke ? "撤销邀请" : kind === "reinvite" ? "重新邀请" : "重新发送";
  const icon = isRevoke ? "mail-x" : "send";
  const attribute = isRevoke ? "data-auth-revoke-invitation" : "data-auth-resend-invitation";
  return `<button type="button" class="ghost-btn auth-invitation-action${isRevoke ? " danger-btn" : ""}" ${attribute}="${escapeHtml(id)}" title="${title}" aria-label="${title}"><i data-lucide="${icon}"></i><span>${title}</span></button>`;
}

function authInvitationActions(statusKey, id) {
  if (!id) return "";
  if (statusKey === "pending") return authInvitationActionButton("resend", id) + authInvitationActionButton("revoke", id);
  if (statusKey === "revoked" || statusKey === "expired") return authInvitationActionButton("reinvite", id);
  return "";
}

function renderAuthInvitations() {
  const draft = authManagementState.invitationDraft;
  const filter = authManagementState.invitationFilter || "pending";
  const filteredInvitations = filter === "all"
    ? authManagementState.invitations
    : authManagementState.invitations.filter((invitation) => authInvitationStatusKey(invitation.status) === filter);
  const rows = filteredInvitations.map((invitation) => {
    const id = String(invitation.id || invitation.invitationId || "").trim();
    const statusKey = authInvitationStatusKey(invitation.status);
    const roleSource = invitation.organizationRoles || invitation.roles || [];
    const roles = (Array.isArray(roleSource) ? roleSource : String(roleSource).split(/[,\s]+/))
      .map((role) => (typeof role === "string" ? role : String(role?.name || "")))
      .filter(Boolean);
    const labels = roles.map((role) => authMemberRoleLabel(String(role).trim())).join("、") || "（未指定角色）";
    // 副行只放能跟邮件对上的东西：邮件里 {{code}} 就是邀请 id（前 8 位足够核对，
    // 全文挂在 title 上）；已接受的行再加上接受时间——"谁在什么时候接受的"
    // 以前在界面上完全看不到。不再直接摊一个裸 id 出来。
    const acceptedAt = statusKey === "accepted"
      ? authManagementDate(invitation.updatedAt || invitation.updated_at || invitation.acceptedAt)
      : "";
    const subline = [id ? `邀请码 ${escapeHtml(id.slice(0, 8))}` : "", acceptedAt ? `接受于 ${acceptedAt}` : ""]
      .filter(Boolean)
      .join(" · ");
    // 过期时间对已经结束的邀请没有意义，别摆一个日期让人误以为它还会变。
    const expiry = statusKey === "pending" ? authManagementDate(invitation.expiresAt || invitation.expires_at) : "—";
    return `<tr><td data-label="收件人"><strong>${escapeHtml(invitation.invitee || invitation.email || invitation.receiver || "未提供邮箱")}</strong><small title="${escapeHtml(id)}">${subline}</small></td><td data-label="角色">${escapeHtml(labels)}</td><td data-label="状态"><span class="auth-status is-${statusKey}">${escapeHtml(authInvitationStatus(statusKey))}</span></td><td data-label="过期时间"><small>${expiry}</small></td><td data-label="操作"><div class="auth-row-actions">${authInvitationActions(statusKey, id)}</div></td></tr>`;
  }).join("");
  const shareLink = authManagementState.invitationLink
    ? `<div class="auth-token-reveal" role="status"><strong>邀请链接</strong><code>${escapeHtml(authManagementState.invitationLink)}</code><button type="button" class="icon-btn" data-copy-invitation-link title="复制邀请链接" aria-label="复制邀请链接"><i data-lucide="copy"></i></button></div>`
    : "";
  // 列表返回的是全部邀请（含已结束的），标题就不能再叫"待处理"——那是把历史记录
  // 当成待办在显示。计数按待接受单独标出来，一眼能看出还剩几封没被接受。
  const total = authManagementState.invitations.length;
  const pendingCount = authManagementState.invitations.filter((item) => authInvitationStatusKey(item.status) === "pending").length;
  const countLabel = pendingCount === total ? `共 ${total} 条` : `共 ${total} 条 · 待接受 ${pendingCount}`;
  const filterLabels = { all: "全部", pending: "待接受", accepted: "已接受", expired: "已过期", revoked: "已撤销" };
  const emptyTitle = !total ? "暂无邀请记录" : filter === "all" ? "暂无邀请记录" : `暂无${filterLabels[filter]}的邀请`;
  const emptyHint = total ? "当前状态下没有邀请，可切换“全部”查看历史记录。" : "发送邀请后，状态会显示在这里。";
  return `${shareLink}<div class="auth-management-grid"><form class="auth-management-panel auth-management-form" data-auth-form="invite"><div class="auth-panel-heading"><div><span>邀请成员</span><h3>发送新邀请</h3></div><i data-lucide="mail-plus"></i></div><label>邮箱<input required type="email" name="email" value="${escapeHtml(draft.email)}" placeholder="name@example.com" autocomplete="off" /></label><label>角色<select name="role" data-auth-invite-role>${authMemberRoleOptions(draft.role, { includeOwner: false })}</select></label><p class="auth-role-hint" data-auth-role-hint>${escapeHtml(authMemberRoleHint(draft.role))}</p><p class="auth-role-hint">邀请邮件 7 天内有效；对方没收到可以直接「重新发送」。</p><button class="primary-btn" type="submit"><i data-lucide="send"></i><span>发送邀请</span></button></form><section class="auth-management-panel auth-management-list auth-invitations-panel"><div class="auth-panel-heading"><div><span>当前组织</span><h3>发出的邀请</h3></div><span class="auth-invitation-count">${countLabel}</span></div><div class="auth-invitation-toolbar"><label for="authInvitationFilter">查看</label><select id="authInvitationFilter" data-auth-invitation-filter>${Object.entries(filterLabels).map(([value, label]) => `<option value="${value}" ${filter === value ? "selected" : ""}>${label}</option>`).join("")}</select>${filter === "pending" && pendingCount ? `<span>还有 ${pendingCount} 人待接受</span>` : ""}</div>${rows ? `<div class="auth-table-wrap"><table class="auth-table auth-invitation-table"><thead><tr><th>收件人</th><th>角色</th><th>状态</th><th>过期时间</th><th>操作</th></tr></thead><tbody>${rows}</tbody></table></div>` : `<div class="auth-empty"><i data-lucide="mail-open"></i><strong>${emptyTitle}</strong><span>${emptyHint}</span></div>`}</section></div>`;
}

function buildAuthInvitationLink(invitation) {
  const id = String(invitation?.id || invitation?.invitationId || "").trim();
  const token = String(invitation?.token || invitation?.invitationToken || "").trim();
  if (!id && !token) return "";
  const url = new URL(window.location.href);
  ["section", "invitationId", "invitation_id", "invite", "invitationToken", "invitation_token"].forEach((key) => url.searchParams.delete(key));
  url.searchParams.set(id ? "invitationId" : "invitationToken", id || token);
  return url.toString();
}

async function loadAuthManagement(force = false) {
  if (authManagementState.loading) return;
  const organizationId = globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "";
  if (authManagementState.invitationDraftOrganizationId !== organizationId) {
    authManagementState.invitationDraftOrganizationId = organizationId;
    authManagementState.invitationDraft = { email: "", role: "Viewer" };
    authManagementState.invitationFilter = "pending";
    authManagementState.selected = new Set();
    authManagementState.bulkMode = false;
  }
  const tab = authManagementState.tab;
  if (authManagementState.loaded[tab] && !force) {
    renderAuthManagement();
    return;
  }
  authManagementState.loading = true;
  authManagementState.error = "";
  authManagementState.loadError = "";
  authManagementState.loadErrorStatus = 0;
  renderAuthManagement();
  try {
    if (tab === "inbox") {
      // 收件箱不属于当前组织（邀请可能就是来自别的组织），所以不走上面那条
      // "请先在顶部选择组织"的前置；数据由模块自己管，这里等它读完再渲染。
      await globalThis.xhsInvitationInbox?.load?.({ force });
      authManagementState.loaded[tab] = true;
      return;
    }
    const auth = globalThis.xhsSpaAuth;
    if ((auth?.organizations?.() || []).length && !auth.getActiveOrganizationId?.()) {
      authManagementState.loadError = "请先在顶部选择组织";
      return;
    }
    if (tab === "members") {
      const data = await authManagementRequest("/api/auth/members");
      authManagementState.members = Array.isArray(data.members) ? data.members : [];
      authManagementState.currentUserId = String(data.currentUserId || "").trim();
    } else if (tab === "invitations") {
      const data = await authManagementRequest("/api/auth/invitations");
      authManagementState.invitations = Array.isArray(data.invitations) ? data.invitations : [];
    }
    authManagementState.loaded[tab] = true;
  } catch (error) {
    authManagementState.loadError = error.message || "权限数据读取失败";
    authManagementState.loadErrorStatus = Number(error.status) || 0;
  } finally {
    authManagementState.loading = false;
    renderAuthManagement();
  }
}

async function submitAuthManagementForm(form, submitter) {
  const formData = new FormData(form);
  const action = form.dataset.authForm;
  if (action === "invite") {
    if (!confirmAuthMutation("确定要发送组织邀请吗？")) return;
    const response = await authManagementRequest("/api/auth/invitations", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: formData.get("email"), roles: [formData.get("role") || "Viewer"] }) });
    // "邀请建好了"与"信发出去了"是两件事：发信失败时邀请其实已经落库，
    // 报一句笼统的失败会让人以为没成功，重试又撞上重复邀请。说清还剩哪条路可走。
    const invitation = response.invitation;
    const mailSent = invitation?.mailSent;
    authManagementState.notice = !invitation
      ? "邀请请求已提交"
      : mailSent === false
        ? "邀请已创建，但邀请邮件没有发出去：请用「重新发送」重试，或复制下面的链接手动转发给同事。"
        : mailSent === true
          ? "邀请已发送，对方会收到邀请邮件。"
          : "邀请已提交";
    authManagementState.invitationLink = buildAuthInvitationLink(invitation);
    authManagementState.invitationFilter = "pending";
    authManagementState.invitationDraft = { email: "", role: "Viewer" };
    authManagementState.loaded.invitations = false;
    authManagementState.tab = "invitations";
    return true;
  }
  return false;
}

async function handleAuthManagementAction(action, target) {
  authManagementState.error = "";
  authManagementState.notice = "";
  if (action === "member-role") {
    const memberId = target.dataset.authMemberRole;
    const role = target.value;
    const previous = target.dataset.currentRole || "";
    const owners = authManagementState.members.filter((member) => authManagementRoles(member).some((item) => item.toLowerCase() === "owner"));
    if (memberId === authManagementState.currentUserId || (role !== "Owner" && owners.length === 1 && authManagementMemberId(owners[0]) === memberId)) {
      target.value = previous;
      authManagementState.error = memberId === authManagementState.currentUserId
        ? "不能在此修改自己的角色，请联系其他管理员。"
        : "团队需保留至少一名所有者，请先指定另一名所有者，再修改此成员的角色。";
      renderAuthManagement();
      return;
    }
    const confirmation = `确定把该成员的角色从「${authMemberRoleLabel(previous)}」改为「${authMemberRoleLabel(role)}」吗？`;
    if (!confirmAuthMutation(confirmation)) {
      target.value = previous;
      renderAuthManagement();
      return;
    }
    const organizationId = globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "";
    await authManagementRequest(`/api/auth/members/${encodeURIComponent(memberId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roles: [role] }),
    });
    authManagementState.loaded.members = false;
    await loadAuthManagement(true);
    const updated = authManagementState.members.find((member) => authManagementMemberId(member) === memberId);
    const actualRoles = updated ? authManagementRoles(updated) : [];
    if (authManagementState.loadError || authManagementState.loading || authManagementState.tab !== "members" || organizationId !== (globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "")) {
      authManagementState.error = "修改请求已提交，但暂时无法确认最新角色，请返回原团队刷新成员列表核对。";
    } else if (actualRoles.length === 1 && actualRoles[0].toLowerCase() === role.toLowerCase()) {
      authManagementState.notice = `成员角色已改为「${authMemberRoleLabel(role)}」`;
    } else {
      authManagementState.error = `未确认角色已改为「${authMemberRoleLabel(role)}」，当前角色为「${actualRoles.map(authMemberRoleLabel).join("、") || "未读取到"}」。请刷新核对后再操作。`;
    }
    renderAuthManagement();
    return;
  } else if (action === "remove-member") {
    const memberId = target.dataset.authRemoveMember;
    if (!memberId || !window.confirm("确定要移除这名成员吗？移除后对方将无法访问团队内容，恢复访问需要重新邀请。")) return;
    await authManagementRequest(`/api/auth/members/${encodeURIComponent(memberId)}`, { method: "DELETE" });
    authManagementState.notice = "成员已移除";
    authManagementState.loaded.members = false;
  } else if (action === "resend-invitation") {
    const invitationId = target.dataset.authResendInvitation;
    if (!invitationId || !window.confirm("重新发送会作废原邀请并生成新的邀请码，确定继续吗？")) return;
    await authManagementRequest(`/api/auth/invitations/${encodeURIComponent(invitationId)}/resend`, {
      method: "POST",
    });
    authManagementState.notice = "邀请已重新发送";
    authManagementState.invitationLink = "";
    authManagementState.loaded.invitations = false;
  } else if (action === "revoke-invitation") {
    const invitationId = target.dataset.authRevokeInvitation;
    if (!invitationId || !window.confirm("确定要撤销这封邀请吗？撤销后，原邀请链接将不能再使用。")) return;
    await authManagementRequest(`/api/auth/invitations/${encodeURIComponent(invitationId)}`, { method: "DELETE" });
    authManagementState.notice = "邀请已撤销";
    authManagementState.loaded.invitations = false;
  }
  renderAuthManagement();
  await loadAuthManagement(true);
}

// ----- js/auth/my-organizations.js -----
// 「我的组织」：一个账号在多个组织下，各自的会员与余额。
//
// 会员与积分都是**按组织**记的，"我的余额/我的会员"在多组织下没有正确语义——一个账号管着三个
// 组织就是三个数，而计费页只能显示"当前组织"那一个。谁要问"我在哪几个组织里、各自什么情况"，
// 之前唯一的办法是逐个切过去看。这张页给出一次问答。
//
// 数据由 `GET /api/billing/organizations` 出：**组织清单是服务端按 user_id 自己查的**
// （客户端不传任何组织 id，也就无从读别人的组织）。组织名不用服务端给——`organizationLabelFor`
// 已经是全站唯一的标签口径（个人组织的改名、名称覆盖都在那儿），这里复用它，别自己拼。

const myOrganizationsState = {
  open: false,
  loading: false,
  error: "",
  organizations: null,
  // 我付过的单（付款人视角的收据）：与组织列表一起加载，跨组织。
  orders: null,
  // 点了"切到这个组织"之后在等的那个 tenantId；切换成功的事件回来才重读。
  switchTarget: "",
};

function myOrganizationsModal() {
  return document.getElementById("myOrganizationsModal");
}

function myOrganizationsLabel(tenantId) {
  const match = (globalThis.xhsSpaAuth?.organizations?.() || []).find(
    (item) => String(item?.id || "") === String(tenantId || ""),
  );
  return organizationLabelFor(match || { id: tenantId });
}

// 读不到组织信息时**不能说"未开通"**（那是断言的语义，而我们并不知道），要说"读不到"。
// §4.1 的同一条规矩：0 与"未开通"都是合法取值，不能拿来表达"这次没读到"。
function myOrganizationsMembershipText(entry) {
  if (entry?.unavailable || !entry?.membership) return "读不到";
  const membership = entry.membership;
  if (String(membership.status || "").toLowerCase() !== "active") return "未开通";
  const expiry = billingExpiryDateText(membership.expiresAt);
  return expiry ? `有效 · ${expiry} 到期` : "有效";
}

function myOrganizationsCreditsText(entry) {
  if (entry?.unavailable || !entry?.balance) return "读不到";
  const credits = Number(entry.balance.availableCredits);
  return Number.isFinite(credits) ? credits.toLocaleString("zh-CN") : "读不到";
}

function myOrganizationsRoleText(entry) {
  const roles = Array.isArray(entry?.roles) ? entry.roles : [];
  if (!roles.length) return "—";
  return roles.map((role) => authMemberRoleLabel(role)).join(" / ");
}

function myOrganizationsRowMarkup(entry, activeTenantId) {
  const tenantId = String(entry?.tenantId || "");
  const isActive = tenantId && tenantId === String(activeTenantId || "");
  const canManage = rolesCanManageOrganization(entry?.roles);
  const unavailableNote = entry?.unavailable
    ? `<p class="my-organizations-note is-warning">这个组织的会员与积分暂时读不到，等一下再试。</p>`
    : "";
  return `
    <li class="my-organizations-row${isActive ? " is-active" : ""}">
      <div class="my-organizations-row-head">
        <strong>${escapeHtml(myOrganizationsLabel(tenantId))}</strong>
        ${isActive ? `<span class="my-organizations-badge">当前组织</span>` : ""}
      </div>
      <dl class="my-organizations-facts">
        <div><dt>我的角色</dt><dd>${escapeHtml(myOrganizationsRoleText(entry))}${canManage ? "（可管理）" : ""}</dd></div>
        <div><dt>会员</dt><dd>${escapeHtml(myOrganizationsMembershipText(entry))}</dd></div>
        <div><dt>积分</dt><dd>${escapeHtml(myOrganizationsCreditsText(entry))}</dd></div>
      </dl>
      ${unavailableNote}
      ${isActive ? "" : `<button type="button" class="ghost-btn" data-my-organizations-switch="${escapeHtml(tenantId)}"><span>切到这个组织</span></button>`}
    </li>
  `;
}

// 我付过的单：付款人视角的收据。订单本身挂在组织上（会员与积分都记在组织），但"我掏过
// 哪些钱"是人维度的——这条按付款人跨组织查，每单都标出它属于哪个组织。
function myOrganizationsOrderStatusText(status) {
  const key = String(status || "").toLowerCase();
  if (key === "paid") return "已支付";
  if (key === "created") return "待支付";
  if (key === "refunded") return "已退款";
  if (key === "refunding") return "退款中";
  if (key === "failed") return "支付失败";
  if (key === "cancelled") return "已取消";
  return key || "—";
}

function myOrganizationsOrdersMarkup() {
  const orders = Array.isArray(myOrganizationsState.orders) ? myOrganizationsState.orders : null;
  if (!orders) return "";
  if (!orders.length) {
    return `<p class="my-organizations-orders-empty">还没有付过的单。</p>`;
  }
  const rows = orders
    .map(
      (order) => `<li class="my-organizations-order">
        <div class="my-organizations-order-main">
          <strong>${escapeHtml(String(order?.tenantId ? myOrganizationsLabel(order.tenantId) : ""))}</strong>
          <small>${escapeHtml(
            [
              order?.product === "topup" ? "加量包" : "会员",
              billingMoneyText(order?.amountRmb),
              myOrganizationsOrderStatusText(order?.status),
            ]
              .filter(Boolean)
              .join(" · ")
          )}</small>
        </div>
        <small class="my-organizations-order-time">${escapeHtml(billingLedgerTime(order?.createdAt) || "—")}</small>
      </li>`
    )
    .join("");
  return `<p class="my-organizations-lede">我付过的单（收据视角，跨组织）：</p>
    <ul class="my-organizations-orders">${rows}</ul>`;
}

function myOrganizationsMarkup() {
  const organizations = Array.isArray(myOrganizationsState.organizations)
    ? myOrganizationsState.organizations
    : [];
  const activeTenantId = globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "";
  const body = organizations.length
    ? `<ul class="my-organizations-list">${organizations
        .map((entry) => myOrganizationsRowMarkup(entry, activeTenantId))
        .join("")}</ul>`
    : `<div class="empty-state"><strong>读不到组织信息</strong><span>稍后重试，或刷新页面。</span></div>`;
  return `
    <div class="my-organizations-panel">
      <p class="my-organizations-lede">会员与积分按组织记：下面每一行只说明<strong>你在那个组织里</strong>的额度与会员，组织之间不通用。</p>
      ${myOrganizationsState.error ? `<p class="my-organizations-note is-error">${escapeHtml(myOrganizationsState.error)}</p>` : ""}
      ${myOrganizationsState.loading ? `<p class="my-organizations-note">正在读取…</p>` : body}
      ${myOrganizationsState.loading ? "" : myOrganizationsOrdersMarkup()}
    </div>
  `;
}

function renderMyOrganizations() {
  const target = document.getElementById("myOrganizationsBody");
  if (target) target.innerHTML = myOrganizationsMarkup();
}

async function loadMyOrganizations() {
  myOrganizationsState.loading = true;
  myOrganizationsState.error = "";
  renderMyOrganizations();
  try {
    // `getBackend` 已经把 `ok:false` 与非 2xx 都抛成带中文 message 的 Error，这里不再自查一遍。
    const data = await getBackend("/api/billing/organizations");
    myOrganizationsState.organizations = Array.isArray(data?.organizations) ? data.organizations : [];
    // 收据是第二条读：它挂了不该让"我在哪些组织"也看不见，所以单独兜住。
    try {
      const receipts = await getBackend("/api/billing/orders");
      myOrganizationsState.orders = Array.isArray(receipts?.orders) ? receipts.orders : [];
    } catch (_error) {
      myOrganizationsState.orders = null;
    }
  } catch (error) {
    myOrganizationsState.organizations = null;
    // 抛出的 message 已经过 `backendErrorMessage`（错误码 → 中文的唯一收口点），别再自己拼话术。
    myOrganizationsState.error = String(error?.message || "").trim() || "读取组织信息失败";
  } finally {
    myOrganizationsState.loading = false;
    renderMyOrganizations();
  }
}

function openMyOrganizations() {
  const modal = myOrganizationsModal();
  if (!modal) return;
  bindMyOrganizationsChrome();
  myOrganizationsState.open = true;
  myOrganizationsState.organizations = null;
  myOrganizationsState.orders = null;
  myOrganizationsState.error = "";
  showModal(modal);
  void loadMyOrganizations();
}

function closeMyOrganizations() {
  myOrganizationsState.open = false;
  myOrganizationsState.organizations = null;
  myOrganizationsState.orders = null;
  myOrganizationsState.error = "";
  myOrganizationsState.switchTarget = "";
  hideModal(myOrganizationsModal());
}

// 从这一页直接切组织：切换本身仍走顶栏那套唯一实现（`#authOrganizationSelect` 的 change 事件）
// ——未支付单提醒、工作区落库、账单刷新都在那一处，这里只负责把选择器设好再触发。
// 切完等 `xhs-organization-switched`（session.js 在切换落定后派发）再重读，让"当前组织"
// 那枚标记落到新行上；用定时器猜时长在慢网络上必然猜错。
function switchOrganizationFromMyOrganizations(tenantId) {
  const select = document.getElementById("authOrganizationSelect");
  if (!select) return;
  if (![...select.options].some((option) => option.value === tenantId)) {
    toast("这个组织当前不可切换，请刷新页面后重试", { kind: "error", duration: 6000 });
    return;
  }
  if (select.value === tenantId) return;
  myOrganizationsState.switchTarget = tenantId;
  select.value = tenantId;
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

function bindMyOrganizationsChrome() {
  const modal = myOrganizationsModal();
  if (!modal || modal.dataset.myOrganizationsBound === "1") return;
  document.getElementById("closeMyOrganizationsBtn")?.addEventListener("click", closeMyOrganizations);
  document.getElementById("myOrganizationsRetryBtn")?.addEventListener("click", () => void loadMyOrganizations());
  // 切换成功后才重读；切失败（选择器被退回、弹出失败提示）时把待办清掉，不白刷一次。
  document.addEventListener("xhs-organization-switched", (event) => {
    const switched = String(event?.detail?.tenantId || "");
    if (!myOrganizationsState.switchTarget || switched !== myOrganizationsState.switchTarget) return;
    myOrganizationsState.switchTarget = "";
    if (myOrganizationsState.open) void loadMyOrganizations();
  });
  modal.addEventListener("click", (event) => {
    if (event.target === modal) closeMyOrganizations();
    const switcher = event.target.closest("[data-my-organizations-switch]");
    if (switcher) switchOrganizationFromMyOrganizations(switcher.dataset.myOrganizationsSwitch);
  });
  modal.dataset.myOrganizationsBound = "1";
}

function initMyOrganizations() {
  document.getElementById("myOrganizationsEntry")?.addEventListener("click", () => openMyOrganizations());
}

// ----- js/auth/team-ux.js -----
// Team member identity, copy controls and loading states.
function governanceIdentity(id, { label = "用户 ID", full = false } = {}) {
  const value = String(id || "");
  if (!value) return "—";
  const short = !full && value.length > 10 ? `${value.slice(0, 4)}…${value.slice(-4)}` : value;
  return `<span class="governance-identity${full ? " is-full" : ""}"><span title="${escapeHtml(value)}">${escapeHtml(short)}</span><button type="button" class="governance-copy" data-governance-copy="${escapeHtml(value)}" data-copy-label="${escapeHtml(label)}" title="复制${escapeHtml(label)}" aria-label="复制${escapeHtml(label)}"><i data-lucide="copy"></i></button></span>`;
}

async function governanceCopyValue(button, value, label) {
  try {
    await adminConsoleCopyText(value);
    toast(`${label}已复制`);
  } catch (_error) {
    toast(`复制失败，已展开完整${label}，请手动复制`, { kind: "error" });
    let fallback = button.parentElement.querySelector(".governance-copy-fallback");
    if (!fallback) {
      fallback = document.createElement("span");
      fallback.className = "governance-copy-fallback";
      button.parentElement.appendChild(fallback);
    }
    fallback.textContent = value;
    const range = document.createRange();
    range.selectNodeContents(fallback);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
  }
}

function governanceBindCopyControls(content) {
  content.querySelectorAll("[data-governance-copy]").forEach((button) => {
    button.onclick = () => governanceCopyValue(button, button.dataset.governanceCopy, button.dataset.copyLabel || "用户 ID");
  });
}

function governanceTeamUnavailable() {
  const state = authManagementState;
  if (!state.loadError) return "";
  const denied = state.loadErrorStatus === 403;
  return `<div class="auth-empty" role="status"><i data-lucide="${denied ? "lock-keyhole" : "cloud-off"}"></i><strong>${denied ? "当前账号无法管理成员与邀请" : "成员与邀请加载失败"}</strong><span>${denied ? "请联系当前组织的管理员，或切换到有管理权限的组织。" : escapeHtml(state.loadError)}</span>${denied ? "" : '<button type="button" class="ghost-btn" data-governance-team-retry>重试</button>'}</div>`;
}

// ----- js/auth/audit-ux.js -----
// Readable audit events and Beijing date boundaries.
function governanceAuditTime(value) {
  if (!value) return "—";
  const date = new Date(typeof value === "number" ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("zh-CN", {
    timeZone: "Asia/Shanghai", dateStyle: "short", timeStyle: "medium",
  });
}

function governanceAuditResult(row) {
  if (typeof row !== "object") row = { result: row };
  const status = Number(row.status || row.result);
  const jobLabels = { queued: "排队中", running: "处理中", cancelling: "正在取消", paused: "已暂停", needs_login: "需要登录", needs_verification: "需要验证", completed: "已完成", cancelled: "已取消", failed: "失败" };
  let label = jobLabels[row.jobStatus];
  if (!label) {
    if (status === 403 || row.result === "denied") label = "无权限";
    else if (status === 401) label = "未登录或登录过期";
    else if (status >= 400 || ["failure", "failed"].includes(row.result)) label = "失败";
    else if (status === 202) label = "已受理";
    else if (row.result === "pending" || status === 102) label = "处理中";
    else if (row.result === "success" || (status >= 200 && status < 300)) label = "成功";
    else label = "结果未记录";
  }
  const kind = ["成功", "已完成"].includes(label) ? "positive" : ["失败", "无权限", "未登录或登录过期"].includes(label) ? "negative" : "";
  return `<span class="governance-audit-result ${kind ? `admin-console-${kind}` : ""}">${escapeHtml(label)}</span>`;
}

function governanceAuditActor(row) {
  return `<span class="governance-actor">${escapeHtml(row.actorName || (row.actorUserId ? "用户名暂不可用" : "未记录操作者"))}</span>`;
}

function governanceAuditTenant(row) {
  return row.tenantId || row.tenantName ? adminConsoleTenantCell(row) : "未记录租户";
}

function governanceAuditOperation(row) {
  const action = String(row.action || row.eventType || "");
  const known = { "refund.create": "申请订单退款", "credit.adjust": "手工调整积分", "pricing.update": "修改计费定价", "account.ban": "封禁账号权限", "account.soft-delete": "删除账号", "session.revoke": "注销账号会话" };
  if (known[action]) return known[action];
  const match = action.match(/^(GET|POST|PUT|PATCH|DELETE) (\/\S+)/);
  const method = String(row.method || match?.[1] || "").toUpperCase();
  const path = String(row.path || match?.[2] || "").split("?")[0];
  const detail = row.detail || {};
  const jobs = { "viral-analysis": "爆款笔记分析任务", "low-fan-collection": "低粉爆款采集任务", ai: "AI 生成任务" };
  if (action.startsWith("JOB ")) return jobs[action.slice(4).split("/")[0]] || "后台任务执行";
  const lifecycle = { "soft-delete": "删除账号", ban: "封禁账号权限", unban: "解除账号权限封禁", "account-ban": "禁止账号登录", "account-unban": "恢复账号登录", "create-account": "创建账号", "grant-days": "发放会员天数", "set-expiry": "调整会员到期时间" };
  if (/^(identity\.account\.lifecycle|billing\.membership\.manage):/.test(action)) return lifecycle[action.split(":").pop()] || "账号或会员变更";
  const accountAction = path.match(/^\/api\/admin\/users\/[^/]+\/([^/]+)$/)?.[1];
  if (method === "POST" && accountAction) return ({ ...lifecycle, roles: "修改账号角色", "force-password-change": "要求账号重设密码", "revoke-sessions": "注销账号会话", suspend: "禁止账号登录", resume: "恢复账号登录" })[accountAction] || "其他操作";
  const routes = [
    ["GET", /^\/api\/auth\/members$/, "查看成员列表"],
    ["PUT", /^\/api\/auth\/members\/[^/]+$/, "修改成员角色"],
    ["PUT", /^\/api\/auth\/organization$/, "修改组织名称"],
    ["DELETE", /^\/api\/auth\/members\/[^/]+$/, "移除团队成员"],
    ["GET", /^\/api\/auth\/invitations$/, "查看邀请列表"],
    ["POST", /^\/api\/auth\/invitations$/, "发送成员邀请"],
    ["POST", /^\/api\/auth\/invitations\/[^/]+\/resend$/, "重新发送成员邀请"],
    ["DELETE", /^\/api\/auth\/invitations\/[^/]+$/, "撤销成员邀请"],
    ["GET", /^\/api\/admin\/audit-events$/, "查看审计日志"],
    ["GET", /^\/api\/admin\/users$/, "查找用户账号"],
    ["POST", /^\/api\/admin\/users$/, "创建账号"],
    ["GET", /^\/api\/(admin\/)?ops-config$/, "读取运营配置"],
    ["POST", /^\/api\/admin\/ops-config$/, "修改运营配置"],
    ["GET", /^\/api\/admin\/reconciliation\.csv$/, "导出对账数据"],
    ["GET", /^\/api\/admin\/payment-channel$/, "查看支付渠道配置"],
    ["POST", /^\/api\/admin\/payment-channel$/, "修改支付渠道配置"],
    ["POST", /^\/api\/admin\/payment-channel\/probe$/, "检测支付渠道连接"],
    ["POST", /^\/api\/admin\/credits\/adjust$/, "手工调整积分"],
    ["POST", /^\/api\/admin\/memberships\/grant$/, "发放会员天数"],
    ["POST", /^\/api\/admin\/memberships\/set-expiry$/, "调整会员到期时间"],
    ["POST", /^\/api\/admin\/payment-orders\/[^/]+\/refund$/, "申请订单退款"],
    ["POST", /^\/api\/ai\/text$/, "请求生成 AI 文案"],
    ["POST", /^\/api\/ai\/image$/, "请求生成 AI 图片"],
    ["POST", /^\/api\/viral\/analyze\/jobs$/, "提交爆款笔记分析任务"],
    ["POST", /^\/api\/viral\/low-fan-collection\/jobs$/, "提交低粉爆款采集任务"],
    ["GET", /^\/api\/(jobs|viral\/analyze\/jobs|viral\/low-fan-collection\/jobs)(\/[^/]+)?$/, "查看任务进度"],
    ["GET", /^\/api\/state$/, "读取工作台数据"],
    ["POST", /^\/api\/state$/, "保存工作台数据"],
    ["GET", /^\/api\/config$/, "读取工作台配置"],
    ["POST", /^\/api\/config$/, "修改工作台配置"],
    ["GET", /^\/api\/admin\/pricing-configs$/, "查看计费定价"],
    ["POST", /^\/api\/admin\/pricing-configs$/, "修改计费定价"],
    ["GET", /^\/api\/admin\/payment-orders$/, "查看支付订单"],
    ["GET", /^\/api\/admin\/credit-transactions$/, "查看积分流水"],
    ["GET", /^\/api\/admin\/credit-grants$/, "查看积分发放"],
    ["GET", /^\/api\/admin\/credit-reservations$/, "查看积分预占"],
    ["GET", /^\/api\/admin\/tenant-memberships$/, "查看租户会员"],
  ];
  let label = routes.find(([verb, pattern]) => method === verb && pattern.test(path))?.[2];
  if (!label) return "其他操作";
  if (label === "修改成员角色" && Array.isArray(detail.rolesBefore) && Array.isArray(detail.rolesAfter)) {
    const roles = { Owner: "所有者", Admin: "管理员", Editor: "编辑", Viewer: "只读" };
    const names = (values) => values.map((role) => roles[role] || "其他角色").join("、") || "无角色";
    label += `（${names(detail.rolesBefore)} → ${names(detail.rolesAfter)}）`;
  }
  return label;
}

function governanceAuditDetails(row) {
  const fields = [["事件类型", row.eventType], ["原始动作", row.action], ["请求方法", row.method], ["接口路径", row.path], ["状态码", row.status], ["任务状态", row.jobStatus], ["原始结果", row.result], ["租户 ID", row.tenantId], ["操作者 ID", row.actorUserId], ["请求号", row.requestId || row.correlationId], ["资源 ID", row.resourceId], ["错误代码", row.errorCode], ["原因", row.reason]];
  const copyLabels = ["租户 ID", "操作者 ID", "请求号"];
  const entries = fields.filter(([, value]) => value !== null && value !== undefined && value !== "").map(([label, value]) => `<dt>${label}</dt><dd>${copyLabels.includes(label) ? governanceIdentity(value, { label, full: true }) : escapeHtml(String(value))}</dd>`).join("");
  const change = row.detail && Object.keys(row.detail).length ? `<dt>变更详情</dt><dd><pre>${escapeHtml(JSON.stringify(row.detail, null, 2))}</pre></dd>` : "";
  return `<dl class="governance-audit-details">${entries}${change}</dl>`;
}

function governanceFilterQuery(view, values) {
  const query = new URLSearchParams();
  for (const field of adminConsoleFilterFields(view)) {
    if (field.type === "dateRange") {
      for (const [param, time] of [[field.fromParam, "00:00:00.000"], [field.toParam, "23:59:59.999"]]) {
        if (values[param]) query.set(param, `${values[param]}T${time}+08:00`);
      }
    } else if (String(values[field.param] || "").trim()) {
      query.set(field.param, String(values[field.param]).trim());
    }
  }
  return query.size ? `&${query.toString()}` : "";
}

// ----- js/auth/team-bulk.js -----
// 批量操作先选成员，再在同级弹窗内确认角色或移出后果。
function authBulkSelectionError(ids) {
  const members = authManagementState.members;
  const owners = members.filter((member) => authManagementRoles(member).some((role) => role.toLowerCase() === "owner"));
  if (!ids.length) return "请先选择需要操作的成员。";
  if (ids.includes(authManagementState.currentUserId)) return "不能批量操作自己，请取消选择自己的账号。";
  if (owners.length <= 1 && owners.some((member) => ids.includes(authManagementMemberId(member)))) {
    return "团队需保留至少一名所有者，请取消选择该成员。";
  }
  if (owners.length && owners.every((member) => ids.includes(authManagementMemberId(member)))) {
    return "不能同时操作全部所有者，请保留至少一名所有者。";
  }
  if (ids.some((id) => !members.some((member) => authManagementMemberId(member) === id))) {
    return "部分成员已不在当前列表，请取消选择后重新勾选。";
  }
  return "";
}

function openAuthBulkDialog(action) {
  const ids = Array.from(authManagementState.selected);
  const selectionError = authBulkSelectionError(ids);
  if (selectionError) {
    authManagementState.error = selectionError;
    renderAuthManagement();
    return;
  }
  const organizationId = globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "";
  const dialog = document.getElementById("authBulkDialog");
  const form = document.getElementById("authBulkForm");
  const select = document.getElementById("authBulkRole");
  const hint = document.getElementById("authBulkRoleHint");
  const error = document.getElementById("authBulkError");
  const cancel = document.getElementById("authBulkCancel");
  const confirm = document.getElementById("authBulkConfirm");
  const removing = action === "remove";
  document.getElementById("authBulkTitle").textContent = removing ? `移出 ${ids.length} 名成员` : `修改 ${ids.length} 名成员的角色`;
  document.getElementById("authBulkConsequence").textContent = removing
    ? "移出后，以下成员将无法访问该团队内容。如需恢复访问，需重新邀请。"
    : "确认后，以下成员的原有角色将统一替换为新角色，对应权限随之改变。";
  document.getElementById("authBulkMembers").innerHTML = ids.map((id) => {
    const member = authManagementState.members.find((item) => authManagementMemberId(item) === id);
    return `<li>${escapeHtml(authMemberIdentity(member).name)}</li>`;
  }).join("");
  document.getElementById("authBulkRoleField").hidden = removing;
  select.disabled = removing;
  select.innerHTML = '<option value="" disabled selected>请选择角色</option>' + authMemberRoleOptions("", { includeOwner: false });
  hint.textContent = "请选择要授予的角色。";
  error.textContent = "";
  confirm.textContent = removing ? "确认移出团队" : "确认修改";
  confirm.disabled = !removing;
  cancel.disabled = false;
  let busy = false;
  select.onchange = () => {
    hint.textContent = authMemberRoleHint(select.value);
    confirm.disabled = !select.value;
  };
  cancel.onclick = () => dialog.close();
  dialog.oncancel = (event) => { if (busy) event.preventDefault(); };
  dialog.onclose = () => {
    document.querySelector(removing ? "[data-auth-bulk-remove]" : "[data-auth-bulk-apply]")?.focus();
  };
  form.onsubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const role = select.value;
    if (!removing && !["Viewer", "Editor", "Admin"].includes(role)) {
      error.textContent = "请选择要授予的角色。";
      return;
    }
    error.textContent = authBulkSelectionError(ids);
    if (organizationId !== (globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "")) {
      error.textContent = "当前组织已切换，请关闭弹窗后重新选择成员。";
    }
    if (error.textContent) return;
    busy = true;
    select.disabled = cancel.disabled = confirm.disabled = true;
    confirm.textContent = "处理中…";
    const failures = [];
    for (const id of ids) {
      try {
        if (organizationId !== (globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "")) throw new Error("组织已切换，操作已停止");
        await authManagementRequest(`/api/auth/members/${encodeURIComponent(id)}`, removing
          ? { method: "DELETE" }
          : { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ roles: [role] }) });
      } catch (cause) {
        failures.push({ id, message: cause.message || "操作失败，请重试" });
      }
    }
    busy = false;
    dialog.close();
    if (organizationId !== (globalThis.xhsSpaAuth?.getActiveOrganizationId?.() || "")) {
      authManagementState.selected = new Set();
      authManagementState.bulkMode = false;
      authManagementState.notice = "组织已切换，未继续处理剩余成员。请返回原组织核对结果。";
    } else {
      authManagementState.selected = new Set(failures.map((item) => item.id));
      authManagementState.bulkMode = Boolean(failures.length);
      const result = removing ? "移出团队" : `修改为「${authMemberRoleLabel(role)}」`;
      authManagementState.notice = failures.length
        ? `已${result} ${ids.length - failures.length} 人，${failures.length} 人失败，失败项仍保持勾选。原因：${[...new Set(failures.map((item) => item.message))].join("；")}`
        : `已将 ${ids.length} 名成员${result}。`;
    }
    authManagementState.loaded.members = false;
    await loadAuthManagement(true);
    if (failures.length) {
      authManagementState.error = authManagementState.notice;
      authManagementState.notice = "";
      renderAuthManagement();
    }
    document.querySelector(failures.length ? "[data-auth-bulk-apply]" : "[data-auth-bulk-start]")?.focus();
  };
  dialog.showModal();
}

// ----- js/auth/login-gate.js -----
// 登录闸门：租户模式下「未登录」本身就是一个完整、明确的状态，不是故障。
//
// 它过去被翻译成别的故障——「上游文本 AI 尚未配置，请联系平台管理员」「云端知识库由组织
// 管理员配置」「工作台数据还没同步成功，请刷新页面重试」「登录状态已失效，请重新登录」——
// 每一句都把用户指到一个他改不动的地方：他不是配置错了，也不是权限不够，就是没有登录。
// 这里收口一件事：未登录 → 一句准确的话 + 把登录入口直接递到手上，而不是只丢一句错误。
//
// 单机/兼容模式（xhsSpaAuth 未配置）没有登录这回事，闸门整体不生效——本机操作员照旧用。
//
// 两个入口各管一段：
//   requireSignedInForAction      —— 用户主动触发的动作（异步，问一次 SDK，最权威）；
//   requireSignedInForActionSync  —— 同步入口（生成前的 AI 配置检查、渲染期的入口判定），
//                                    读启动期确认过的缓存；状态未知一律放行。

// 同一个动作经常连环失败（一次点击引发多个请求），提示只留最近这一句，弹窗只开一次：
// 把用户刚关掉的弹窗又顶开会比不提示更烦人。
const LOGIN_GATE_PROMPT_INTERVAL_MS = 3000;
// 认证就绪的等待上限。与 core/api.js 的 AUTH_READY_WAIT_MS 同一个考虑：启动期那次令牌
// 交换正好挂住时，不能把"点一下"变成"点了没反应"。
const LOGIN_GATE_READY_WAIT_MS = 2000;

// null = 还没确认。未知必须和"确认没有"分开：未知时放行，让服务端回它真实的 401，
// 也好过对着一个可能已经登录的人说"请先登录"。
let loginGateSignedIn = null;
let loginGateReadySettled = !globalThis.xhsSpaAuthReady;
let loginGatePromptedAt = 0;

if (globalThis.xhsSpaAuthReady) {
  Promise.resolve(globalThis.xhsSpaAuthReady)
    .catch(() => {})
    .then(() => { loginGateReadySettled = true; });
}

function loginGateActive() {
  return Boolean(globalThis.xhsSpaAuth?.configured);
}

// 登录态的唯一落库口：auth/session.js 的 syncAuthUi 每次拿到权威结果都写这里（启动、
// 登录落地、登出、切组织、跨标签登出都经过它），闸门只读这个值。
function noteLoginGateSignedIn(authenticated) {
  loginGateSignedIn = Boolean(authenticated);
}

// D10（前端同一状态多副本）方案 A 的第一步：为「当前是否已登录」立**唯一读口**。
// 所有判断都从这里出——本文件的 loginGateSignedOut / loginGateSignedInKnown /
// handleUnauthenticatedBackendAction，以及下面这些走 loginGateSignedOut 的调用方：
//   core/state.js:1703、knowledge/local-files.js:22、tools/low-fan.js:759（水合/入口判定）
//   core/client.js:289 → requireSignedInForActionSync（请求前的同步闸门）
// 别再各自读 DOM、另立变量或直接读 loginGateSignedIn：副本没跟上的那一个就是
// 「界面已登录、接口已失效」的分裂点。
// 三态：true = 已确认登录，false = 已确认没登录，null = 还没确认（未知一律放行，
// 宁可让后端回它真实的 401，也不要对着可能已登录的人说"请先登录"）。
// 这一轮只统一读口：loginGateSignedIn 本身、session.js 的 lastObservedAuthenticated、
// 以及 DOM 文本 #accountName 三个副本都保留，删副本等读口稳定后另开一轮（写口收敛）。
function signedInState() {
  return loginGateSignedIn;
}

// 同一个读口的"权威态"：向 SDK 问一次"当前到底登没登"。
// 本文件里所有向 SDK 提问的地方都走它，别再各写一份 .catch(() => false)——两处各写一遍
// 迟早一处改一处忘（未配置/抛异常一律按"没登录"回，与既有各处口径一致）。
// 它只读不写：写口仍然只有 noteLoginGateSignedIn（写口收敛是下一轮的事）。
async function resolveSignedInState() {
  try {
    return Boolean(await globalThis.xhsSpaAuth?.isAuthenticated?.());
  } catch (_error) {
    return false;
  }
}

function loginGateSignedInKnown() {
  return signedInState();
}

// "确认没登录"才拦。未知（启动早期还没确认）时返回 false，闸门放行。
function loginGateSignedOut() {
  return loginGateActive() && signedInState() === false;
}

// 开机被动读（/api/config、/api/state…）要等闸门有答案：游客确认未登录就不发，
// 避免 Network 里一串必然 401；已登录或单机兼容模式照旧放行。
async function whenLoginGateReady(timeoutMs = LOGIN_GATE_READY_WAIT_MS) {
  if (!loginGateActive()) return { active: false, signedIn: true };
  if (!loginGateReadySettled && globalThis.xhsSpaAuthReady) {
    await Promise.race([
      Promise.resolve(globalThis.xhsSpaAuthReady).catch(() => {}),
      new Promise((resolve) => { globalThis.setTimeout(resolve, timeoutMs); }),
    ]);
  }
  if (loginGateSignedIn === null) {
    try {
      const authenticated = Boolean(await globalThis.xhsSpaAuth?.isAuthenticated?.());
      noteLoginGateSignedIn(authenticated);
    } catch (_error) {
      // 保持未知：未知时仍允许发起请求，由服务端回真实 401/403。
    }
  }
  return {
    active: true,
    signedIn: loginGateSignedIn === true,
    known: loginGateSignedIn !== null,
  };
}

function loginGateActionMessage(actionLabel = "") {
  const label = String(actionLabel || "").trim();
  return label ? `请先登录后再使用「${label}」` : UNAUTHENTICATED_ACTION_MESSAGE;
}

// 顶栏那个「登录」按钮是**唯一**绑定了收尾同步（登录成功后 syncAuthUi）的入口。自己另起
// 一份弹窗初始化就会漏掉那一步，用户登录完界面还停在未登录的样子。所以这里复用它。
function openLoginGateModal(attempt = 0) {
  const button = document.querySelector("#authSignInBtn");
  if (button && button.dataset.bound === "1") {
    button.click();
    return true;
  }
  // 绑定是异步的（要等 xhsSpaAuth 就绪，最长几十毫秒）。给它一点时间，别在这个窗口里
  // 判"没有入口"。
  if (attempt < 10) {
    window.setTimeout(() => openLoginGateModal(attempt + 1), 100);
    return true;
  }
  // 兜底：入口还没绑定时直接开弹窗，保证"请先登录"永远是一个能立刻完成的动作。
  const auth = globalThis.xhsSpaAuth;
  if (typeof openAuthLoginModal === "function" && auth) {
    void Promise.resolve(openAuthLoginModal(auth, () => {})).catch(() => {});
    return true;
  }
  return false;
}

function promptSignInForAction(actionLabel = "") {
  // 登录弹窗已经开着：别再点一次——那会把用户填了一半的表单重置掉。也别再刷屏提示。
  const modal = document.querySelector("#authLoginModal");
  if (modal && !modal.classList.contains("hidden")) return false;
  const now = Date.now();
  // 去抖只压提示，不压入口：连环失败不该刷屏，但用户刚把上一次的弹窗关掉再点别的功能时，
  // 登录入口必须照旧递到手上——"点了没反应"比重复一句话更糟。
  if (now - loginGatePromptedAt >= LOGIN_GATE_PROMPT_INTERVAL_MS) {
    loginGatePromptedAt = now;
    toast(loginGateActionMessage(actionLabel), { kind: "error", duration: 5200 });
  }
  return openLoginGateModal();
}

// 用户主动触发的动作入口。等认证就绪（有上限）后问一次 SDK，拿到答案再决定拦不拦。
async function requireSignedInForAction(actionLabel = "") {
  if (!loginGateActive()) return true;
  if (!loginGateReadySettled && globalThis.xhsSpaAuthReady) {
    await Promise.race([
      Promise.resolve(globalThis.xhsSpaAuthReady).catch(() => {}),
      new Promise((resolve) => { globalThis.setTimeout(resolve, LOGIN_GATE_READY_WAIT_MS); }),
    ]);
  }
  const authenticated = await resolveSignedInState();
  noteLoginGateSignedIn(authenticated);
  if (authenticated) return true;
  promptSignInForAction(actionLabel);
  return false;
}

// 同步入口用。只有"已经确认没登录"才拦，未知放行。
function requireSignedInForActionSync(actionLabel = "") {
  if (!loginGateSignedOut()) return true;
  promptSignInForAction(actionLabel);
  return false;
}

// HTTP 边界递过来的事实：401 + 这次请求没带凭据 = 没登录。方法不是 GET/HEAD/OPTIONS 就
// 说明这是用户（或界面代用户）发起的一次写入动作，值得把登录入口递到手上；页面自己那些
// 被动读（开机时的 /api/config、/api/state…）由 whenLoginGateReady 把关：确认游客就不发。
// 这里只管「用户主动点了却没登录」——那些请求失败本就该安静。
function handleUnauthenticatedBackendAction(status, meta = {}) {
  if (Number(status) !== 401) return false;
  if (meta.authAttached !== false) return false;
  const method = String(meta.method || "GET").toUpperCase();
  if (["GET", "HEAD", "OPTIONS"].includes(method)) return false;
  // 已经确认登录了还拿到"没带凭据"：那是取令牌那一步出了问题，不是没登录，别误报。
  if (signedInState() === true) return false;
  noteLoginGateSignedIn(false);
  promptSignInForAction(meta.actionLabel || "");
  return true;
}

// 启动期先自己确认一次：闸门要在**点击之前**就有答案，不能等第一次点击才发现状态未知。
void (async () => {
  if (!loginGateActive()) return;
  try {
    if (!loginGateReadySettled && globalThis.xhsSpaAuthReady) {
      await Promise.race([
        Promise.resolve(globalThis.xhsSpaAuthReady).catch(() => {}),
        new Promise((resolve) => { globalThis.setTimeout(resolve, LOGIN_GATE_READY_WAIT_MS); }),
      ]);
    }
    const auth = globalThis.xhsSpaAuth;
    if (typeof auth?.isAuthenticated !== "function") return;
    noteLoginGateSignedIn(await resolveSignedInState());
  } catch (_error) {
    // 保持"未知"：未知一律放行，不拿一次探测失败去误伤已经登录的人。
  }
})();

// ----- js/auth/invitation-inbox.js -----
// 「我收到的邀请」：登录后按账号身份确认，不必依赖邮件里的链接。
//
// 邮件只负责通知（链接直达平台，见 session.js 的 capturePendingAuthInvitation）；收件人
// 如果先登录了平台，也应该在这里看到这条邀请。过去只有邮件一条路，而且信里给的是一串
// 邀请 id，平台内没有能填它的地方（台账 P26）。
//
// 服务端按当前账号在 Logto 里的邮箱过滤（/api/auth/invitations/inbox），所以这里不需要
// 组织上下文，也不该借用权限管理页那份"当前组织"的状态。

const INVITATION_INBOX_IGNORED_KEY = "xhs-ignored-organization-invitations-v1";

const invitationInboxState = {
  loading: false,
  loaded: false,
  error: "",
  invitations: [],
};

// 忽略只影响横幅：面板里仍然看得到，否则"想稍后处理"就变成了"再也找不到"。
// 存在 sessionStorage 里，同一次登录会话内不再打扰，刷新页面也还记得。
function invitationInboxIgnoredIds() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(INVITATION_INBOX_IGNORED_KEY) || "[]");
    return new Set(Array.isArray(raw) ? raw.map((value) => String(value || "").trim()).filter(Boolean) : []);
  } catch (_error) {
    return new Set();
  }
}

function invitationInboxIgnore(invitationId) {
  const id = String(invitationId || "").trim();
  if (!id) return;
  const ignored = invitationInboxIgnoredIds();
  ignored.add(id);
  try {
    sessionStorage.setItem(INVITATION_INBOX_IGNORED_KEY, JSON.stringify([...ignored]));
  } catch (_error) {
    // 存不下只是横幅这次会话里还会再出现一次，不影响接受。
  }
  invitationInboxRenderPrompt();
}

function invitationInboxName(invitation) {
  return String(invitation?.organizationName || "").trim() || "一个组织";
}

function invitationInboxMeta(invitation) {
  const roles = Array.isArray(invitation?.roles) ? invitation.roles.filter(Boolean) : [];
  const labels = roles.map((role) => authMemberRoleLabel(String(role).trim())).join("、");
  const expiresAt = Number(invitation?.expiresAt);
  const expiry = Number.isFinite(expiresAt) && expiresAt > 0
    ? `${new Date(expiresAt).toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" })} 前有效`
    : "";
  return [labels ? `角色：${labels}` : "", expiry].filter(Boolean).join(" · ");
}

async function invitationInboxLoad({ force = false } = {}) {
  if (invitationInboxState.loading) return invitationInboxState.invitations;
  if (invitationInboxState.loaded && !force) return invitationInboxState.invitations;
  invitationInboxState.loading = true;
  invitationInboxState.error = "";
  try {
    const payload = await getBackend("/api/auth/invitations/inbox");
    invitationInboxState.invitations = Array.isArray(payload?.invitations) ? payload.invitations : [];
    invitationInboxState.loaded = true;
  } catch (error) {
    // 读不到邀请不该影响登录，也不该把横幅做成一个报错：面板里如实说明，横幅不显示。
    invitationInboxState.invitations = [];
    invitationInboxState.error = error?.message || "邀请读取失败";
  } finally {
    invitationInboxState.loading = false;
  }
  return invitationInboxState.invitations;
}

function invitationInboxPromptHost() {
  let host = document.getElementById("invitationInboxPrompt");
  if (!host) {
    host = document.createElement("div");
    host.id = "invitationInboxPrompt";
    host.className = "invitation-inbox-prompt";
    host.hidden = true;
    (document.body || document.documentElement).appendChild(host);
  }
  return host;
}

function invitationInboxAcceptButton(invitationId, className) {
  return `<button type="button" class="${className}" data-invitation-accept="${escapeHtml(invitationId)}">接受</button>`;
}

async function invitationInboxAccept(invitationId, button) {
  const id = String(invitationId || "").trim();
  const invitation = invitationInboxState.invitations.find(
    (item) => String(item?.invitationId || "").trim() === id
  );
  if (button) button.disabled = true;
  try {
    await globalThis.acceptOrganizationInvitation?.(id, invitationInboxName(invitation));
  } catch (error) {
    toast(`接受邀请失败：${error?.message || "请稍后重试"}`, { kind: "error", duration: 8000 });
    if (button) button.disabled = false;
    return;
  }
  invitationInboxState.loaded = false;
  await invitationInboxLoad({ force: true });
  invitationInboxRenderPrompt();
  if (typeof authManagementState !== "undefined" && authManagementState.tab === "inbox") {
    renderAuthManagement();
  }
}

async function invitationInboxHandleClick(event) {
  const acceptButton = event.target.closest("[data-invitation-accept]");
  if (acceptButton) {
    await invitationInboxAccept(acceptButton.dataset.invitationAccept, acceptButton);
    return;
  }
  const ignoreButton = event.target.closest("[data-invitation-ignore]");
  if (ignoreButton) {
    invitationInboxIgnore(ignoreButton.dataset.invitationIgnore);
  }
}

function invitationInboxBindDelegates() {
  const root = document.documentElement;
  if (!root || root.dataset.invitationInboxBound === "1") return;
  root.dataset.invitationInboxBound = "1";
  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-invitation-accept], [data-invitation-ignore]")) return;
    void invitationInboxHandleClick(event);
  });
}

function invitationInboxRenderPrompt() {
  const host = invitationInboxPromptHost();
  const ignored = invitationInboxIgnoredIds();
  const visible = invitationInboxState.invitations.filter(
    (item) => !ignored.has(String(item?.invitationId || "").trim())
  );
  if (!visible.length) {
    host.hidden = true;
    host.innerHTML = "";
    return;
  }
  host.hidden = false;
  host.innerHTML = `
    <div class="invitation-inbox-prompt-head"><i data-lucide="mail-plus"></i><strong>有人邀请你加入组织</strong></div>
    <ul>${visible
      .map((item) => {
        const id = String(item?.invitationId || "").trim();
        const meta = invitationInboxMeta(item);
        return `<li><span class="invitation-inbox-prompt-item"><strong>${escapeHtml(invitationInboxName(item))}</strong>${meta ? `<small>${escapeHtml(meta)}</small>` : ""}</span><span class="invitation-inbox-prompt-actions">${invitationInboxAcceptButton(id, "primary-btn")}<button type="button" class="ghost-btn" data-invitation-ignore="${escapeHtml(id)}">忽略</button></span></li>`;
      })
      .join("")}</ul>`;
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function invitationInboxRenderPanel() {
  if (invitationInboxState.loading) {
    return `<div class="auth-management-loading"><i data-lucide="loader-circle"></i><span>正在读取邀请…</span></div>`;
  }
  if (invitationInboxState.error) {
    return `<div class="auth-empty"><i data-lucide="triangle-alert"></i><strong>邀请读取失败</strong><span>${escapeHtml(invitationInboxState.error)}</span></div>`;
  }
  const invitations = invitationInboxState.invitations;
  if (!invitations.length) {
    return `<div class="auth-empty"><i data-lucide="inbox"></i><strong>没有待接受的邀请</strong><span>发给当前登录账号邮箱的邀请会显示在这里，与当前选择的组织无关。</span></div>`;
  }
  const rows = invitations
    .map((item) => {
      const id = String(item?.invitationId || "").trim();
      const meta = invitationInboxMeta(item);
      return `<tr><td data-label="组织"><strong>${escapeHtml(invitationInboxName(item))}</strong>${meta ? `<small>${escapeHtml(meta)}</small>` : ""}</td><td data-label="操作"><div class="auth-row-actions">${invitationInboxAcceptButton(id, "ghost-btn")}</div></td></tr>`;
    })
    .join("");
  return `<div class="auth-management-grid auth-inbox-grid"><section class="auth-management-panel auth-management-list"><div class="auth-panel-heading"><div><span>邀请我加入</span><h3>待接受的邀请</h3></div><span class="auth-invitation-count">共 ${invitations.length} 条</span></div><div class="auth-table-wrap"><table class="auth-table auth-inbox-table"><thead><tr><th>组织</th><th>操作</th></tr></thead><tbody>${rows}</tbody></table></div></section></div>`;
}

async function invitationInboxPrompt() {
  invitationInboxBindDelegates();
  await invitationInboxLoad();
  invitationInboxRenderPrompt();
}

globalThis.xhsInvitationInbox = {
  load: invitationInboxLoad,
  prompt: invitationInboxPrompt,
  renderPanel: invitationInboxRenderPanel,
  state: invitationInboxState,
};

// ----- js/auth/account-settings.js -----
// 账号设置：自助改昵称、改密码、绑定/更换邮箱、管理登录设备。
//
// 全部直连 Logto Account API（`/api/my-account`、`/api/verifications/*`），
// 密码与验证码不经过我们的后端。Logto 的实测约定（2026-09-12 在本机 Logto 上逐条验过）：
//   - 令牌必须是「不指定 resource」的不透明用户令牌，指定 resource 会得到 scope 为空的 JWT，
//     Account API 直接 401（见 `getAccountToken`）。
//   - 敏感操作（改密码、查看/踢出会话、改邮箱）需要先用当前密码换一个 `logto-verification-id`，
//     有效期 10 分钟，且**每次调用都要新换一个**。
//   - 会话标识是 `payload.uid`（`sid`/`jti` 会被判成 session_not_found）。
//   - 绑定邮箱是两步：给新地址发码（模板 `BindNewIdentifier`）→ 用码换
//     `newIdentifierVerificationRecordId`，连同身份验证记录一起提交。
//   - `customData` 在 Logto 侧设为只读，前端也不发送：个人组织标记存在里面。

const accountSettingsState = {
  open: false,
  profile: null,
  sessions: null,
  error: "",
  busy: false,
};

// 当前密码只在弹层打开期间保存在内存里，用于换验证记录；关闭即清空。
let accountPasswordUnlock = "";
// 绑定邮箱的两步流程：先给新邮箱发码，再拿码换「新标识验证记录」。
let accountEmailFlow = { email: "", verificationId: "" };

function accountAuthApi() {
  const auth = globalThis.xhsSpaAuth;
  if (!auth?.getAccountToken || !auth?.accountApiUrl) return null;
  return auth;
}

function accountSettingsErrorMessage(payload, status, retryAfterSeconds = 0) {
  // 共用 logto-errors.js 的映射。只有这一条在这里要改口：账号设置里的
  // session.invalid_credentials 指"刚才让你输入的当前密码不对"，不是登录弹层那句
  // "账号或密码不正确"。
  if (String(payload?.code || "") === "session.invalid_credentials") return "当前密码不正确。";
  return logtoErrorMessage(payload, status, { retryAfterSeconds });
}

// Account API 的写接口两种形态都有：改昵称回整个 profile，改/解绑邮箱回 204 空体。
// 空体不能直接当 profile 用（会把面板清成"未绑定邮箱"，用户以为改绑失败），所以三条写
// 路径共用这一个入口：有返回体就用它，没有就用本地已经知道的值补上。
function accountSettingsProfileAfterWrite(updated, patch) {
  return updated && Object.keys(updated).length
    ? updated
    : { ...(accountSettingsState.profile || {}), ...patch };
}

async function accountApiRequest(path, options = {}) {
  const auth = accountAuthApi();
  if (!auth) throw new Error("当前登录方式不支持自助账号设置（如需使用请退出后重新登录）。");
  const token = await auth.getAccountToken();
  if (!token) throw new Error("登录状态缺少账号设置权限：请退出后重新登录一次再试。");
  const headers = { Authorization: `Bearer ${token}` };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.verificationId) headers["logto-verification-id"] = options.verificationId;
  const response = await fetch(auth.accountApiUrl(path), {
    method: options.method || "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const text = await response.text();
  let payload = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = {};
  }
  if (!response.ok) {
    throw new Error(accountSettingsErrorMessage(payload, response.status, logtoRetryAfterSeconds(response)));
  }
  return payload;
}

async function fetchAccountVerification(password) {
  const result = await accountApiRequest("/api/verifications/password", {
    method: "POST",
    body: { password },
  });
  const verificationId = String(result?.verificationRecordId || "").trim();
  if (!verificationId) throw new Error("账号服务未返回验证记录，请稍后重试。");
  return verificationId;
}

function accountSettingsSessionLabel(session) {
  const context = session?.lastSubmission?.signInContext || {};
  const agent = String(context.userAgent || "").trim();
  const platform = /Windows/i.test(agent) ? "Windows"
    : /Macintosh|Mac OS/i.test(agent) ? "macOS"
      : /Android/i.test(agent) ? "Android"
        : /iPhone|iPad|iOS/i.test(agent) ? "iOS"
          : /Linux/i.test(agent) ? "Linux"
            : "";
  const browser = /Edg\//i.test(agent) ? "Edge"
    : /Chrome\//i.test(agent) ? "Chrome"
      : /Firefox\//i.test(agent) ? "Firefox"
        : /Safari\//i.test(agent) ? "Safari"
          : "";
  const name = [platform, browser].filter(Boolean).join(" · ") || "未知设备";
  const ip = String(context.ip || "").replace(/^::ffff:/, "");
  return { name, ip };
}

function accountSettingsTimeText(value) {
  const stamp = Number(value);
  if (!Number.isFinite(stamp) || stamp <= 0) return "—";
  return new Date(stamp).toLocaleString("zh-CN", { hour12: false });
}

function accountSettingsProfileMarkup() {
  const profile = accountSettingsState.profile || {};
  const name = String(profile.name || "").trim();
  const username = String(profile.username || "").trim();
  return `
    <section class="account-settings-block">
      <h3>昵称</h3>
      <form id="accountProfileForm" class="account-settings-form">
        <label>登录用户名<input type="text" value="${escapeHtml(username)}" readonly /></label>
        <label>昵称<input type="text" id="accountProfileName" maxlength="80" placeholder="给自己起个名字" value="${escapeHtml(name)}" /></label>
        <div class="account-settings-actions">
          <button type="submit" class="primary-btn" id="accountProfileSubmit"><span>保存昵称</span></button>
        </div>
      </form>
    </section>
  `;
}

function accountSettingsEmailMarkup() {
  const primaryEmail = String(accountSettingsState.profile?.primaryEmail || "").trim();
  const pendingEmail = accountEmailFlow.email;
  const codeSent = Boolean(accountEmailFlow.verificationId && pendingEmail);
  return `
    <section class="account-settings-block">
      <h3>登录邮箱</h3>
      <p class="account-settings-hint">当前绑定：<strong>${escapeHtml(primaryEmail || "未绑定邮箱")}</strong></p>
      <form id="accountEmailSendForm" class="account-settings-form">
        <div class="account-settings-inline">
          <label>新邮箱<input type="email" id="accountNewEmail" placeholder="name@example.com" autocomplete="email" required /></label>
          <button type="submit" class="ghost-btn" id="accountSendCodeBtn"><span>${codeSent ? "重新发送" : "发送验证码"}</span></button>
        </div>
      </form>
      ${codeSent ? `
      <form id="accountEmailConfirmForm" class="account-settings-form">
        <p class="account-settings-hint">验证码已发送到 ${escapeHtml(pendingEmail)}，10 分钟内有效。</p>
        <label>验证码<input type="text" id="accountEmailCode" inputmode="numeric" autocomplete="one-time-code" required /></label>
        <label>当前密码<input type="password" id="accountEmailPassword" autocomplete="current-password" required /></label>
        <div class="account-settings-actions">
          <button type="button" class="ghost-btn" id="accountEmailCancelBtn"><span>取消</span></button>
          <button type="submit" class="primary-btn" id="accountEmailSaveBtn"><span>保存邮箱</span></button>
        </div>
      </form>` : ""}
      ${primaryEmail ? `
      <div class="account-settings-actions">
        <button type="button" class="ghost-btn danger-btn" id="accountEmailUnbindBtn"><span>解绑邮箱</span></button>
      </div>` : ""}
    </section>
  `;
}

function accountSettingsPasswordMarkup() {
  return `
    <section class="account-settings-block">
      <h3>登录密码</h3>
      <form id="accountPasswordForm" class="account-settings-form">
        <label>当前密码<input type="password" id="accountCurrentPassword" autocomplete="current-password" required /></label>
        <label>新密码<input type="password" id="accountNewPassword" autocomplete="new-password" required /></label>
        <label>确认新密码<input type="password" id="accountConfirmPassword" autocomplete="new-password" required /></label>
        <div class="account-settings-actions">
          <button type="submit" class="primary-btn" id="accountPasswordSubmit"><span>修改密码</span></button>
        </div>
      </form>
      <p class="account-settings-hint">改完密码后，其他设备上的登录会陆续失效，建议同时查看下方登录设备。</p>
    </section>
  `;
}

function accountSettingsSessionsMarkup() {
  const sessions = accountSettingsState.sessions;
  if (sessions === null) {
    return `
      <section class="account-settings-block">
        <h3>登录设备</h3>
        <form id="accountSessionsUnlockForm" class="account-settings-form">
          <p class="account-settings-hint">查看和移除登录设备需要先验证当前密码（凭证只用于本次验证，不落盘）。</p>
          <div class="account-settings-inline">
            <label>当前密码<input type="password" id="accountSessionsPassword" autocomplete="current-password" required /></label>
            <button type="submit" class="ghost-btn" id="accountSessionsUnlock"><span>查看登录设备</span></button>
          </div>
        </form>
      </section>
    `;
  }
  const rows = sessions.map((session) => {
    const label = accountSettingsSessionLabel(session);
    const current = session?.isCurrent ? '<span class="account-session-current">本机</span>' : "";
    const action = session?.isCurrent
      ? ""
      : `<button type="button" class="mini-btn retry-btn" data-account-session-revoke="${escapeHtml(String(session?.payload?.uid || ""))}">移除此设备</button>`;
    return `
      <tr>
        <td><strong>${escapeHtml(label.name)}</strong>${current}<small>${escapeHtml(label.ip || "—")}</small></td>
        <td>${escapeHtml(accountSettingsTimeText(session?.expiresAt))}</td>
        <td>${action}</td>
      </tr>
    `;
  }).join("");
  const others = sessions.filter((session) => !session?.isCurrent);
  const revokeAll = others.length
    ? `<button type="button" class="ghost-btn danger-btn" id="accountRevokeOthersBtn"><span>移除其他设备</span></button>`
    : "";
  return `
    <section class="account-settings-block">
      <h3>登录设备（${sessions.length}）</h3>
      <table class="account-settings-sessions">
        <thead><tr><th>设备</th><th>有效期至</th><th>操作</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="3">没有可显示的登录设备</td></tr>'}</tbody>
      </table>
      <div class="account-settings-actions">
        ${revokeAll}
        <button type="button" class="ghost-btn" id="accountReloadSessionsBtn"><span>刷新</span></button>
      </div>
      <p class="account-settings-hint">移除只会清掉这条登录记录，不会让对方设备立刻退出登录（它手里的登录凭据到期前仍可用）。如果怀疑账号被别人使用，请同时修改密码。</p>
    </section>
  `;
}

// 每次渲染后按 id 直接绑定：弹层内容是整块重建的，用事件委托会在
// 重建/首次渲染的时序上踩空（实测提交不触发）。
function bindAccountSettingsForm(form, handler) {
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    handler();
  });
}

function bindAccountSettingsBody() {
  const body = document.getElementById("accountSettingsBody");
  if (!body) return;
  bindAccountSettingsForm(document.getElementById("accountProfileForm"), () => void submitAccountProfile());
  bindAccountSettingsForm(document.getElementById("accountEmailSendForm"), () => void sendAccountEmailCode());
  bindAccountSettingsForm(document.getElementById("accountEmailConfirmForm"), () => void saveAccountEmail());
  bindAccountSettingsForm(document.getElementById("accountPasswordForm"), () => void submitAccountPassword());
  bindAccountSettingsForm(document.getElementById("accountSessionsUnlockForm"), () => void unlockAccountSessions());
  document.getElementById("accountEmailCancelBtn")?.addEventListener("click", () => {
    accountEmailFlow = { email: "", verificationId: "" };
    renderAccountSettings();
  });
  document.getElementById("accountEmailUnbindBtn")?.addEventListener("click", () => void unbindAccountEmail());
  body.querySelector("[data-account-settings-retry]")?.addEventListener("click", () => void loadAccountSettings());
  document.getElementById("accountReloadSessionsBtn")?.addEventListener("click", () => {
    void reloadAccountSessions().catch((error) => toast(error?.message || "登录设备读取失败", { kind: "error" }));
  });
  document.getElementById("accountRevokeOthersBtn")?.addEventListener("click", () => void revokeOtherAccountSessions());
  body.querySelectorAll("[data-account-session-revoke]").forEach((button) => {
    button.addEventListener("click", () => void revokeAccountSession(button.dataset.accountSessionRevoke));
  });
}

function renderAccountSettings() {
  const body = document.getElementById("accountSettingsBody");
  if (!body) return;
  if (accountSettingsState.error) {
    body.innerHTML = `
      <div class="billing-state billing-state-error" role="alert">
        <p>${escapeHtml(accountSettingsState.error)}</p>
        <button type="button" class="ghost-btn" data-account-settings-retry><span>重试</span></button>
      </div>
    `;
    bindAccountSettingsBody();
    return;
  }
  if (!accountSettingsState.profile) {
    body.innerHTML = '<p class="account-settings-hint">正在读取账号信息…</p>';
    return;
  }
  body.innerHTML = [
    accountSettingsProfileMarkup(),
    accountSettingsEmailMarkup(),
    accountSettingsPasswordMarkup(),
    accountSettingsSessionsMarkup(),
  ].join("");
  bindAccountSettingsBody();
  if (typeof refreshIcons === "function") refreshIcons();
}

async function loadAccountSettings() {
  accountSettingsState.error = "";
  accountSettingsState.profile = null;
  renderAccountSettings();
  try {
    const profile = await accountApiRequest("/api/my-account");
    accountSettingsState.profile = profile;
  } catch (error) {
    accountSettingsState.error = error?.message || "账号信息读取失败";
  }
  renderAccountSettings();
}

// 关闭按钮挂在弹层上，弹层在 DOM 末尾；按需绑定避免依赖 boot 时的 DOM 快照。
function bindAccountSettingsChrome() {
  const modal = document.getElementById("accountSettingsModal");
  if (!modal || modal.dataset.accountSettingsBound === "1") return;
  document.getElementById("closeAccountSettingsBtn")?.addEventListener("click", closeAccountSettings);
  modal.dataset.accountSettingsBound = "1";
}

function openAccountSettings() {
  const modal = document.getElementById("accountSettingsModal");
  if (!modal) return;
  bindAccountSettingsChrome();
  accountSettingsState.open = true;
  accountSettingsState.sessions = null;
  accountSettingsState.error = "";
  showModal(modal);
  void loadAccountSettings();
}

function closeAccountSettings() {
  accountSettingsState.open = false;
  accountSettingsState.profile = null;
  accountSettingsState.sessions = null;
  accountPasswordUnlock = "";
  hideModal(document.getElementById("accountSettingsModal"));
}

async function submitAccountProfile() {
  const nameInput = document.getElementById("accountProfileName");
  const button = document.getElementById("accountProfileSubmit");
  const done = button ? setButtonLoading(button, "保存中…") : () => {};
  try {
    // 只发送 name：customData 保持不动（个人组织标记在里面），没有展示位的字段一律不提交。
    // 清空昵称发 null，不要发空字符串。
    const name = String(nameInput?.value || "").trim();
    const updated = await accountApiRequest("/api/my-account", {
      method: "PATCH",
      body: { name: name || null },
    });
    accountSettingsState.profile = accountSettingsProfileAfterWrite(updated, { name: name || null });
    toast("账号信息已保存");
  } catch (error) {
    toast(error?.message || "保存失败", { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function submitAccountPassword() {
  const currentInput = document.getElementById("accountCurrentPassword");
  const newInput = document.getElementById("accountNewPassword");
  const confirmInput = document.getElementById("accountConfirmPassword");
  const button = document.getElementById("accountPasswordSubmit");
  const current = String(currentInput?.value || "");
  const next = String(newInput?.value || "");
  if (!current || !next) {
    toast("请填写当前密码与新密码", { kind: "error" });
    return;
  }
  if (next !== String(confirmInput?.value || "")) {
    toast("两次输入的新密码不一致", { kind: "error" });
    return;
  }
  const done = button ? setButtonLoading(button, "提交中…") : () => {};
  try {
    const verificationId = await fetchAccountVerification(current);
    await accountApiRequest("/api/my-account/password", {
      method: "POST",
      verificationId,
      body: { password: next },
    });
    accountPasswordUnlock = next;
    document.getElementById("accountPasswordForm")?.reset();
    toast("密码已修改，其他设备上的登录将陆续失效");
  } catch (error) {
    toast(error?.message || "修改密码失败", { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function sendAccountEmailCode() {
  const input = document.getElementById("accountNewEmail");
  const button = document.getElementById("accountSendCodeBtn");
  const email = String(input?.value || "").trim();
  if (!email) {
    toast("请先填写新邮箱", { kind: "error" });
    return;
  }
  const done = button ? setButtonLoading(button, "发送中…") : () => {};
  try {
    // 不传 templateType：Logto 只接受 BindMfa/UserPermissionValidation 作为覆盖值，
    // 而「新标识」会自动用 BindNewIdentifier 模板发码。
    const result = await accountApiRequest("/api/verifications/verification-code", {
      method: "POST",
      body: { identifier: { type: "email", value: email } },
    });
    // 发码返回的是 verificationRecordId；verify 时把它当 verificationId 传回去。
    const verificationId = String(result?.verificationRecordId || "").trim();
    if (!verificationId) throw new Error("账号服务未返回验证编号，请稍后重试。");
    accountEmailFlow = { email, verificationId };
    renderAccountSettings();
    toast(`验证码已发送到 ${email}`);
  } catch (error) {
    toast(error?.message || "验证码发送失败", { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function saveAccountEmail() {
  const codeInput = document.getElementById("accountEmailCode");
  const passwordInput = document.getElementById("accountEmailPassword");
  const button = document.getElementById("accountEmailSaveBtn");
  const code = String(codeInput?.value || "").trim();
  const password = String(passwordInput?.value || "");
  const email = accountEmailFlow.email;
  if (!accountEmailFlow.verificationId || !email) {
    toast("请先发送验证码", { kind: "error" });
    return;
  }
  if (!code || !password) {
    toast("请填写验证码与当前密码", { kind: "error" });
    return;
  }
  const done = button ? setButtonLoading(button, "保存中…") : () => {};
  try {
    // 1) 用收到的码换「新邮箱归属」验证记录；2) 用当前密码换身份验证记录；
    // 3) 两个记录一起提交，Logto 才会把新邮箱写成 primaryEmail。
    const verified = await accountApiRequest("/api/verifications/verification-code/verify", {
      method: "POST",
      body: { identifier: { type: "email", value: email }, verificationId: accountEmailFlow.verificationId, code },
    });
    const newIdentifierVerificationRecordId = String(verified?.verificationRecordId || "").trim();
    if (!newIdentifierVerificationRecordId) throw new Error("验证码校验未通过，请重新获取。");
    const identityRecord = await fetchAccountVerification(password);
    const updated = await accountApiRequest("/api/my-account/primary-email", {
      method: "POST",
      verificationId: identityRecord,
      body: { email, newIdentifierVerificationRecordId },
    });
    accountSettingsState.profile = accountSettingsProfileAfterWrite(updated, { primaryEmail: email });
    accountPasswordUnlock = password;
    accountEmailFlow = { email: "", verificationId: "" };
    renderAccountSettings();
    toast(`登录邮箱已更新为 ${email}`);
  } catch (error) {
    toast(error?.message || "邮箱保存失败", { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function unbindAccountEmail() {
  const password = accountPasswordUnlock;
  if (!password) {
    toast("请先在下方「登录设备」验证当前密码，再解绑邮箱", { kind: "error", duration: 6000 });
    return;
  }
  try {
    const verificationId = await fetchAccountVerification(password);
    const updated = await accountApiRequest("/api/my-account/primary-email", {
      method: "DELETE",
      verificationId,
    });
    accountSettingsState.profile = accountSettingsProfileAfterWrite(updated, { primaryEmail: null });
    renderAccountSettings();
    toast("已解绑邮箱");
  } catch (error) {
    toast(error?.message || "解绑失败", { kind: "error", duration: 6000 });
  }
}

async function unlockAccountSessions() {
  const input = document.getElementById("accountSessionsPassword");
  const button = document.getElementById("accountSessionsUnlock");
  const password = String(input?.value || "");
  if (!password) {
    toast("请输入当前密码", { kind: "error" });
    return;
  }
  const done = button ? setButtonLoading(button, "验证中…") : () => {};
  try {
    accountPasswordUnlock = password;
    await reloadAccountSessions();
  } catch (error) {
    accountPasswordUnlock = "";
    toast(error?.message || "登录设备读取失败", { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function reloadAccountSessions() {
  if (!accountPasswordUnlock) {
    accountSettingsState.sessions = null;
    renderAccountSettings();
    return;
  }
  // 每次敏感调用都要新换验证记录（实测旧记录会被拒）。
  const verificationId = await fetchAccountVerification(accountPasswordUnlock);
  const result = await accountApiRequest("/api/my-account/sessions", { verificationId });
  accountSettingsState.sessions = Array.isArray(result?.sessions) ? result.sessions : [];
  renderAccountSettings();
}

async function revokeAccountSession(sessionId) {
  const id = String(sessionId || "").trim();
  if (!id) return;
  try {
    const verificationId = await fetchAccountVerification(accountPasswordUnlock);
    await accountApiRequest(`/api/my-account/sessions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      verificationId,
    });
    toast("已从登录设备列表移除该设备");
    await reloadAccountSessions();
  } catch (error) {
    toast(error?.message || "移除此设备失败", { kind: "error", duration: 6000 });
  }
}

async function revokeOtherAccountSessions() {
  const sessions = Array.isArray(accountSettingsState.sessions) ? accountSettingsState.sessions : [];
  const others = sessions.filter((session) => !session?.isCurrent && session?.payload?.uid);
  if (!others.length) return;
  try {
    for (const session of others) {
      const verificationId = await fetchAccountVerification(accountPasswordUnlock);
      await accountApiRequest(`/api/my-account/sessions/${encodeURIComponent(String(session.payload.uid))}`, {
        method: "DELETE",
        verificationId,
      });
    }
    toast(`已从登录设备列表移除 ${others.length} 台设备`);
    await reloadAccountSessions();
  } catch (error) {
    toast(error?.message || "移除其他设备失败", { kind: "error", duration: 6000 });
    await reloadAccountSessions().catch(() => {});
  }
}

function initAccountSettings() {
  document.getElementById("accountSettingsEntry")?.addEventListener("click", () => openAccountSettings());
}

// ----- js/auth/prompt-settings.js -----
// 我的提示词：当前用户自己的一套 AI 提示词模板。
//
// 只有一个层级——这份模板只属于这个浏览器里的这个人：改完只影响自己，不会动到别人，也不会
// 写任何组织级配置。「恢复默认」= 回到产品内置的那两套模板。
//
// 与管理端「AI 配置」的分工：那边管组织级的东西（上游 API/模型/知识库/生图水印），提示词
// 不掺和进去，所以这里是唯一入口，用户不必先进平台治理。
//
// 独立成一个弹层而不是塞进「账号设置」：账号设置走 Logto Account API（改的是身份），
// 提示词走本地配置（改的是生成参数），两件事的失败方式、权限、数据落点都不一样。

const promptSettingsState = { open: false };
// 编辑草稿：整块重渲染会丢输入，所以换模板/换模式/改名之前先收进这里。
let promptSettingsDraft = null;
// 「系统附加指令」：生成时除了用户自己的提示词之外、后端固定追加的指令。原件在服务端
// （与生成链路同一份常量，见 /api/ai/system-instructions），这里只读展示，不可编辑。
let promptSettingsSystemInstructions = null;
let promptSettingsSystemInstructionsError = "";

const PROMPT_SETTINGS_MODES = [
  ["rewrite", "链接改写"],
  ["image", "看图配文"],
];
const PROMPT_SETTINGS_FIELDS = [
  ["titlePrompt", "标题提示词"],
  ["bodyPrompt", "正文提示词"],
  ["tagsPrompt", "标签提示词"],
  ["imagePrompt", "图片提示词"],
];
const PROMPT_SETTINGS_MAX_NAME = 40;
// 产品口径（scripts/export_main_features.py）写的是"最多 5 套"，但代码里一直没有拦：
// 新建按钮能一路点下去，下拉里堆出十几条同名「新模板」。额度只算用户自己建的
// （id 前缀 my-），系统默认那两套是产品内置的，不该占用户的额度。
const PROMPT_SETTINGS_MAX_TEMPLATES = 5;
const PROMPT_SETTINGS_USER_PREFIX = "my-";

function promptSettingsTemplateQuota(draft = promptSettingsDraftState()) {
  const used = (draft?.templates || []).filter(
    (item) => item && String(item.id).startsWith(PROMPT_SETTINGS_USER_PREFIX),
  ).length;
  return { used, max: PROMPT_SETTINGS_MAX_TEMPLATES, full: used >= PROMPT_SETTINGS_MAX_TEMPLATES };
}

// 提示词只落在浏览器 localStorage（不进后端、不进 state 同步），所以"写不进去"是
// 真实可能的失败：配额满、隐私模式下禁用存储。快照/回滚保证内存里的选择与真正落盘
// 的那一份一致——否则界面看着改成功了，刷新后又变回去。
function snapshotPromptSettingsState() {
  return {
    templates: state.aiConfig.templates,
    activeTemplateId: state.aiConfig.activeTemplateId,
    promptMode: state.aiConfig.promptMode,
  };
}

function restorePromptSettingsState(snapshot) {
  state.aiConfig.templates = snapshot.templates;
  state.aiConfig.activeTemplateId = snapshot.activeTemplateId;
  state.aiConfig.promptMode = snapshot.promptMode;
}

// 失败原因要能区分：配额满要用户去清理，存储被禁用要用户换浏览器/退出隐私模式。
function promptSettingsPersistFailureDetail(error) {
  const message = String(error?.message || "");
  const quotaFull =
    error?.name === "QuotaExceededError" ||
    error?.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    error?.code === 22 ||
    error?.code === 1014 ||
    /quota|exceeded/i.test(message);
  if (quotaFull) return "浏览器本地存储已满";
  return message || "浏览器本地存储不可用";
}

function reportPromptSettingsPersistFailure(action, error) {
  console.error(`[prompt-settings] ${action}失败`, error);
  toast(`${action}失败：${promptSettingsPersistFailureDetail(error)}。修改还在弹层里，清理后可重试。`, {
    kind: "error",
    duration: 4200,
  });
}

function promptSettingsDraftState() {
  if (promptSettingsDraft) return promptSettingsDraft;
  const templates = JSON.parse(JSON.stringify(state.aiConfig.templates || []));
  const wanted = String(state.aiConfig.activeTemplateId || "");
  const selected = templates.find((item) => item.id === wanted) || templates[0];
  promptSettingsDraft = {
    templates,
    activeTemplateId: selected?.id || "",
    mode: selected?.mode === "image" ? "image" : "rewrite",
    renaming: false,
  };
  return promptSettingsDraft;
}

function promptSettingsCurrentTemplate() {
  const draft = promptSettingsDraftState();
  return draft.templates.find((item) => item.id === draft.activeTemplateId) || draft.templates[0] || null;
}

function promptSettingsPromptSet(template, mode) {
  const normalized = mode === "image" ? "image" : "rewrite";
  const configured = template?.promptSets?.[normalized];
  if (configured && typeof configured === "object") return configured;
  return template?.mode === normalized ? template : {};
}

function promptSettingsTemplateOptions(draft) {
  return draft.templates
    .map(
      (item) =>
        `<option value="${escapeHtml(item.id)}" ${item.id === draft.activeTemplateId ? "selected" : ""}>${escapeHtml(item.name || item.id)}（${item.mode === "image" ? "看图配文" : "链接改写"}）</option>`,
    )
    .join("");
}

function promptSettingsMarkup() {
  const draft = promptSettingsDraftState();
  if (!draft.templates.length) {
    return `
      <section class="account-settings-block">
        <h3>我的提示词</h3>
        <p class="account-settings-hint">还没有提示词模板。点下面的「新建模板」从内置默认里复制一套开始改。</p>
        <div class="account-settings-actions">
          <button type="button" class="primary-btn" id="promptSettingsAddBtn"><span>新建模板</span></button>
        </div>
      </section>
    `;
  }
  const template = promptSettingsCurrentTemplate();
  const promptSet = promptSettingsPromptSet(template, draft.mode);
  const quota = promptSettingsTemplateQuota(draft);
  const nameRow = draft.renaming
    ? `<div class="account-settings-inline">
        <label>模板名称<input type="text" id="promptSettingsNameInput" maxlength="${PROMPT_SETTINGS_MAX_NAME}" value="${escapeHtml(template?.name || "")}" /></label>
        <button type="button" class="ghost-btn" id="promptSettingsRenameConfirm"><span>确认</span></button>
        <button type="button" class="ghost-btn" id="promptSettingsRenameCancel"><span>取消</span></button>
      </div>`
    : `<div class="account-settings-inline">
        <label>模板<select id="promptSettingsTemplate">${promptSettingsTemplateOptions(draft)}</select></label>
        <button type="button" class="ghost-btn" id="promptSettingsRenameBtn"><span>重命名</span></button>
      </div>`;
  const modes = PROMPT_SETTINGS_MODES.map(
    ([mode, label]) =>
      `<button type="button" class="${draft.mode === mode ? "active" : ""}" data-prompt-settings-mode="${mode}">${label}</button>`,
  ).join("");
  const fields = PROMPT_SETTINGS_FIELDS.map(
    ([key, label]) =>
      `<label>${label}<textarea rows="${key === "bodyPrompt" ? 6 : 4}" data-prompt-settings-field="${key}">${escapeHtml(String(promptSet?.[key] || ""))}</textarea></label>`,
  ).join("");
  return `
    <section class="account-settings-block" id="promptSettingsBlock">
      <h3>我的提示词</h3>
      <p class="account-settings-hint">只对你自己的生成生效，不会影响同一个组织里的其他人。已保存 ${quota.used}/${PROMPT_SETTINGS_MAX_TEMPLATES} 套自建模板${quota.full ? "（已达上限，新建前请先删一套）" : ""}。</p>
      ${nameRow}
      <div class="prompt-mode-tabs" role="group" aria-label="提示词模式">${modes}</div>
      <form class="account-settings-form" id="promptSettingsForm">
        ${fields}
      </form>
      <div class="account-settings-actions">
        <button type="button" class="ghost-btn danger-btn" id="promptSettingsDeleteBtn"><span>删除当前模板</span></button>
        <button type="button" class="ghost-btn" id="promptSettingsAddBtn"><span>新建模板</span></button>
        <button type="button" class="ghost-btn" id="promptSettingsResetBtn"><span>恢复默认提示词</span></button>
        <button type="button" class="primary-btn" id="promptSettingsSaveBtn"><span>保存</span></button>
      </div>
    </section>
  `;
}

// 只读披露：用户能看见「自己的提示词之外还被追加了什么」，但不给编辑入口——
// 这几段是服务端固定指令，改了也不会生效，做成可编辑只会骗人。
function promptSettingsSystemInstructionsMarkup() {
  const data = promptSettingsSystemInstructions;
  if (!data) {
    return `
      <section class="account-settings-block" id="promptSettingsSystemBlock">
        <h3>系统附加指令</h3>
        <p class="account-settings-hint">${escapeHtml(promptSettingsSystemInstructionsError || "正在读取系统附加指令…")}</p>
        ${promptSettingsSystemInstructionsError ? `
        <div class="account-settings-actions">
          <button type="button" class="ghost-btn" id="promptSettingsSystemRetryBtn"><span>重试</span></button>
        </div>` : ""}
      </section>
    `;
  }
  const blocks = [
    ["系统角色（每次生成都会随请求发送）", data.system],
    ["输出要求（每次生成都会随请求发送）", data.outputDirective],
    ["命中知识证据时追加", data.withKnowledgeEvidence],
    ["没有知识证据时追加", data.withoutKnowledgeEvidence],
  ]
    .filter(([, text]) => String(text || "").trim())
    .map(
      ([title, text]) =>
        `<div class="system-instruction-block"><strong>${escapeHtml(title)}</strong><p>${escapeHtml(String(text))}</p></div>`,
    )
    .join("");
  return `
    <section class="account-settings-block" id="promptSettingsSystemBlock">
      <h3>系统附加指令</h3>
      <p class="account-settings-hint">下面是生成时在你自己写的提示词之外、由系统固定追加的指令（只读）。上面模板里改的内容不会覆盖它们。</p>
      ${blocks}
      <p class="account-settings-hint">${escapeHtml(String(data.payloadNote || ""))}</p>
    </section>
  `;
}

async function loadPromptSettingsSystemInstructions() {
  promptSettingsSystemInstructionsError = "";
  try {
    const data = await getBackend("/api/ai/system-instructions");
    promptSettingsSystemInstructions = data?.instructions || null;
    if (!promptSettingsSystemInstructions) throw new Error("系统附加指令读取失败");
  } catch (error) {
    // 读不到就说读不到：这一段的重点是"不藏着"，加载失败也不该把整块悄悄去掉。
    // 不把 error.message 直接写进界面：后端未同步部署时这里回的是英文 "Not found"。
    console.error("[prompt-settings] 系统附加指令读取失败", error);
    promptSettingsSystemInstructions = null;
    promptSettingsSystemInstructionsError = "系统附加指令读取失败，请稍后重试。";
  }
  if (!promptSettingsState.open) return;
  // 重渲染会重建 textarea：先把当前输入收回草稿，否则异步回来会吞掉用户正在改的内容。
  collectPromptSettingsForm();
  renderPromptSettings();
}

function renderPromptSettings() {
  const body = document.getElementById("aiPromptsBody");
  if (!body) return;
  body.innerHTML = promptSettingsMarkup() + promptSettingsSystemInstructionsMarkup();
  bindPromptSettingsBody();
  if (typeof refreshIcons === "function") refreshIcons();
}

function collectPromptSettingsForm() {
  const draft = promptSettingsDraftState();
  const template = promptSettingsCurrentTemplate();
  if (!template) return draft;
  const values = {};
  for (const [key] of PROMPT_SETTINGS_FIELDS) {
    const node = document.querySelector(`[data-prompt-settings-field="${key}"]`);
    if (node) values[key] = String(node.value || "");
  }
  template.promptSets = { ...(template.promptSets || {}) };
  template.promptSets[draft.mode] = values;
  // 模板自己那套（扁平字段）跟着同步：生成流程按扁平字段取当前模式的值。
  const ownMode = template.mode === "image" ? "image" : "rewrite";
  if (draft.mode === ownMode) Object.assign(template, values);
  return draft;
}

function commitPromptSettings() {
  const draft = promptSettingsDraftState();
  const templates = draft.templates.filter((item) => item && item.id);
  const persisted = snapshotPromptSettingsState();
  if (!templates.length) {
    // 删到空等于恢复默认，不要留一个"没有模板"的空状态。
    state.aiConfig.templates = cloneDefaultAiConfig().templates;
    state.aiConfig.activeTemplateId = state.aiConfig.templates[0]?.id || "system-link";
  } else {
    state.aiConfig.templates = JSON.parse(JSON.stringify(templates));
    const activeId = templates.some((item) => item.id === draft.activeTemplateId)
      ? draft.activeTemplateId
      : templates[0].id;
    state.aiConfig.activeTemplateId = activeId;
    state.aiConfig.promptMode = templates.find((item) => item.id === activeId)?.mode || "rewrite";
  }
  try {
    saveAiConfig();
  } catch (error) {
    restorePromptSettingsState(persisted);
    // 草稿留在原处（不置 null），调用方据此告诉用户"改动还在、可重试"。
    throw error;
  }
  promptSettingsDraft = null;
}

// 保存这条链路的唯一入口（提交表单与点「保存」都走这里）：写盘失败必须变成用户看得见的
// 提示，且失败时不重渲染，用户填的内容与当前选择原样留在弹层里。
function savePromptSettingsFromForm() {
  try {
    collectPromptSettingsForm();
    commitPromptSettings();
  } catch (error) {
    reportPromptSettingsPersistFailure("保存", error);
    return false;
  }
  renderPromptSettings();
  if (typeof renderEditor === "function") renderEditor();
  toast("我的提示词已保存");
  return true;
}

function resetPromptSettingsToDefaults() {
  const defaults = cloneDefaultAiConfig();
  const persisted = snapshotPromptSettingsState();
  state.aiConfig.templates = defaults.templates;
  state.aiConfig.activeTemplateId = defaults.templates[0]?.id || "system-link";
  state.aiConfig.promptMode = "rewrite";
  try {
    saveAiConfig();
  } catch (error) {
    restorePromptSettingsState(persisted);
    reportPromptSettingsPersistFailure("恢复默认", error);
    return false;
  }
  promptSettingsDraft = null;
  renderPromptSettings();
  if (typeof renderEditor === "function") renderEditor();
  toast("已恢复默认提示词");
  return true;
}

function bindPromptSettingsBody() {
  const draft = promptSettingsDraftState();
  document.getElementById("promptSettingsForm")?.addEventListener("submit", (event) => {
    event.preventDefault();
    savePromptSettingsFromForm();
  });
  document.getElementById("promptSettingsSaveBtn")?.addEventListener("click", () => savePromptSettingsFromForm());
  document.getElementById("promptSettingsResetBtn")?.addEventListener("click", () => resetPromptSettingsToDefaults());
  document.getElementById("promptSettingsSystemRetryBtn")?.addEventListener("click", () => void loadPromptSettingsSystemInstructions());
  document.getElementById("promptSettingsAddBtn")?.addEventListener("click", () => {
    collectPromptSettingsForm();
    const template = promptSettingsAddTemplate(draft);
    if (!template) {
      toast(`最多只能保存 ${PROMPT_SETTINGS_MAX_TEMPLATES} 套自己的提示词模板，请先删除一套再新建`, {
        kind: "error",
        duration: 4200,
      });
      return;
    }
    draft.activeTemplateId = template.id;
    draft.mode = template.mode;
    draft.renaming = true;
    renderPromptSettings();
  });
  document.getElementById("promptSettingsDeleteBtn")?.addEventListener("click", () => {
    const template = promptSettingsCurrentTemplate();
    if (!template) return;
    if (!window.confirm(`删除模板「${template.name || template.id}」？`)) return;
    collectPromptSettingsForm();
    draft.templates = draft.templates.filter((item) => item.id !== template.id);
    draft.activeTemplateId = draft.templates[0]?.id || "";
    draft.mode = promptSettingsCurrentTemplate()?.mode === "image" ? "image" : "rewrite";
    renderPromptSettings();
  });
  document.getElementById("promptSettingsTemplate")?.addEventListener("change", (event) => {
    collectPromptSettingsForm();
    draft.activeTemplateId = String(event.target.value || "");
    draft.mode = promptSettingsCurrentTemplate()?.mode === "image" ? "image" : "rewrite";
    renderPromptSettings();
  });
  document.getElementById("promptSettingsRenameBtn")?.addEventListener("click", () => {
    collectPromptSettingsForm();
    draft.renaming = true;
    renderPromptSettings();
    document.getElementById("promptSettingsNameInput")?.focus();
  });
  document.getElementById("promptSettingsRenameCancel")?.addEventListener("click", () => {
    draft.renaming = false;
    renderPromptSettings();
  });
  const confirmRename = () => {
    const template = promptSettingsCurrentTemplate();
    if (!template) return;
    const value = String(document.getElementById("promptSettingsNameInput")?.value || "").trim();
    if (!value) {
      toast("模板名称不能为空", { kind: "error" });
      return;
    }
    collectPromptSettingsForm();
    template.name = value.slice(0, PROMPT_SETTINGS_MAX_NAME);
    draft.renaming = false;
    renderPromptSettings();
  };
  document.getElementById("promptSettingsRenameConfirm")?.addEventListener("click", confirmRename);
  document.getElementById("promptSettingsNameInput")?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      confirmRename();
    }
  });
  document.querySelectorAll("[data-prompt-settings-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      collectPromptSettingsForm();
      draft.mode = button.dataset.promptSettingsMode === "image" ? "image" : "rewrite";
      renderPromptSettings();
    });
  });
}

// 新模板从当前这套复制一份：用户通常是在默认基础上改，而不是从空白开始。
// 已满则返回 null：调用方据此提示"先删一套"，不静默失败、也不偷偷顶掉别人的模板。
function promptSettingsAddTemplate(draft) {
  const quota = promptSettingsTemplateQuota(draft);
  if (quota.full) return null;
  const source = promptSettingsCurrentTemplate() || cloneDefaultAiConfig().templates[0];
  const id = `my-${Date.now().toString(36)}`;
  const template = JSON.parse(JSON.stringify(source || {}));
  template.id = id;
  // 默认名带序号：连着建几套又不改名时，下拉里不会出现一串同名条目。
  const taken = new Set((draft.templates || []).map((item) => String(item.name || "")));
  let name = "新模板";
  for (let index = 2; taken.has(name); index += 1) name = `新模板 ${index}`;
  template.name = name;
  template.promptSets = template.promptSets || {};
  const mode = template.mode === "image" ? "image" : "rewrite";
  template.mode = mode;
  if (!template.promptSets[mode]) {
    template.promptSets[mode] = Object.fromEntries(PROMPT_SETTINGS_FIELDS.map(([key]) => [key, template[key] || ""]));
  }
  draft.templates.push(template);
  return template;
}

// 关闭按钮挂在弹层上；按需绑定，避免依赖 boot 时的 DOM 快照。
function bindPromptSettingsChrome() {
  const modal = document.getElementById("aiPromptsModal");
  if (!modal || modal.dataset.promptSettingsBound === "1") return;
  document.getElementById("closeAiPromptsBtn")?.addEventListener("click", closePromptSettings);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) closePromptSettings();
  });
  modal.dataset.promptSettingsBound = "1";
}

function openPromptSettings() {
  const modal = document.getElementById("aiPromptsModal");
  if (!modal) return;
  bindPromptSettingsChrome();
  promptSettingsState.open = true;
  promptSettingsDraft = null;
  // 每次打开都重取：这段文本跟着服务端走，缓存住旧版会变成第二次误导。
  promptSettingsSystemInstructions = null;
  void loadPromptSettingsSystemInstructions();
  showModal(modal);
  renderPromptSettings();
}

function closePromptSettings() {
  promptSettingsState.open = false;
  // 没保存的编辑不留在草稿里：下次打开拿到当前真实生效的那一份。
  promptSettingsDraft = null;
  hideModal(document.getElementById("aiPromptsModal"));
}

function initPromptSettings() {
  document.getElementById("accountPromptsEntry")?.addEventListener("click", () => openPromptSettings());
}

// ----- js/org/knowledge-settings.js -----
// 组织级 AI 连接：当前组织共用的云端知识库（Dify）。
//
// 这是**组织级**配置，所以入口在「团队治理」而不是平台治理后台：写入权限判定和成员管理同源
// （租户 Owner/Admin），成员写作时的「资料参照」取的就是这份。平台管理员本身没有额外权限。
//
// 原来的位置是「AI 配置中心 → 知识库连接」，和上游 API/水印挤在一个面板里；上游 API 与
// 生图水印是平台侧口径（见 server/backend.py 的 /api/config 写入判定），知识库是组织自己的
// 连接，两者不该共用一个面。

const orgKnowledgeState = { open: false };
// Dify 密钥只在用户真的输入过之后才提交；否则沿用后端已存的（避免每次保存把密钥写成空）。
let orgKnowledgeSecretDirty = false;

function orgKnowledgeRender() {
  const knowledge = state.aiConfig.knowledge || defaultAiConfig.knowledge;
  const dify = knowledge.dify || {};
  const enabled = document.getElementById("knowledgeEnabled");
  if (!enabled) return;
  enabled.checked = knowledge.enabled === true;
  document.getElementById("knowledgeMinScore").value = Number(knowledge.minScore ?? 0.55);
  document.getElementById("difyApiUrl").value = dify.apiUrl || "";
  document.getElementById("difyDatasetId").value = dify.datasetId || "";
  document.getElementById("difyApiKey").value = dify.maskedApiKey || "";
  document.getElementById("knowledgeDifyFields")?.classList.remove("hidden");
  document.getElementById("knowledgeConfigWarning")?.classList.toggle(
    "hidden",
    !(dify.apiUrl || "").startsWith("http://"),
  );
  orgKnowledgeSecretDirty = false;
}

function orgKnowledgePayload({ includeApiKeyChanges = false } = {}) {
  const knowledge = state.aiConfig.knowledge || defaultAiConfig.knowledge;
  const dify = {
    apiUrl: document.getElementById("difyApiUrl").value.trim(),
    datasetId: document.getElementById("difyDatasetId").value.trim(),
  };
  if (includeApiKeyChanges && orgKnowledgeSecretDirty) {
    const typed = String(document.getElementById("difyApiKey").value || "").trim();
    // 掩码被原样提交时不能当密钥写回去。
    if (typed && typed !== String(dify.maskedApiKey || "")) dify.apiKey = typed;
    else if (!typed && knowledge.dify?.hasApiKey) dify.clearApiKey = true;
  }
  return {
    knowledge: {
      enabled: document.getElementById("knowledgeEnabled").checked,
      provider: "dify",
      minScore: Number(document.getElementById("knowledgeMinScore").value || 0.55),
      dify,
    },
  };
}

function orgKnowledgeApplyServerConfig(config) {
  if (!config) return;
  if (typeof mergeKnowledgeConfig === "function") {
    state.aiConfig.knowledge = mergeKnowledgeConfig(state.aiConfig.knowledge, config);
  } else {
    state.aiConfig.knowledge = { ...(state.aiConfig.knowledge || {}), ...config };
  }
  saveAiConfig();
  orgKnowledgeRender();
}

async function saveOrgKnowledge() {
  const button = document.getElementById("saveOrgKnowledgeBtn");
  const done = button ? setButtonLoading(button, "保存中…") : () => {};
  try {
    const data = await postBackend("/api/config", {
      config: orgKnowledgePayload({ includeApiKeyChanges: true }),
    });
    orgKnowledgeApplyServerConfig(data.config?.knowledge);
    toast("知识库连接已保存");
  } catch (error) {
    toast(`知识库连接保存失败：${error.message}`, { kind: "error", duration: 6000 });
  } finally {
    done();
  }
}

async function testOrgKnowledgeConnection() {
  const status = document.getElementById("knowledgeConfigRuntimeStatus");
  if (status) status.textContent = "正在测试 Dify 知识库...";
  const done = setButtonLoading(document.getElementById("testKnowledgeBtn"), "测试中...");
  try {
    const data = await postBackend("/api/knowledge/test", {
      config: orgKnowledgePayload({ includeApiKeyChanges: true }),
    });
    if (status) status.textContent = `Dify 连接成功 · 返回 ${Number(data.records || 0)} 条测试结果`;
  } catch (error) {
    if (status) status.textContent = `连接失败：${error.message}`;
  } finally {
    done();
    if (status) status.className = "model-fetch-status";
  }
}

function bindOrgKnowledgeChrome() {
  const modal = document.getElementById("orgKnowledgeModal");
  if (!modal || modal.dataset.orgKnowledgeBound === "1") return;
  document.getElementById("closeOrgKnowledgeBtn")?.addEventListener("click", closeOrgKnowledge);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) closeOrgKnowledge();
  });
  document.getElementById("saveOrgKnowledgeBtn")?.addEventListener("click", () => void saveOrgKnowledge());
  document.getElementById("testKnowledgeBtn")?.addEventListener("click", () => void testOrgKnowledgeConnection());
  const keyInput = document.getElementById("difyApiKey");
  keyInput?.addEventListener("focus", () => keyInput.select());
  keyInput?.addEventListener("input", () => {
    orgKnowledgeSecretDirty = true;
  });
  ["copy", "cut", "contextmenu", "dragstart"].forEach((eventName) =>
    keyInput?.addEventListener(eventName, (event) => event.preventDefault()),
  );
  modal.dataset.orgKnowledgeBound = "1";
}

function openOrgKnowledge() {
  const modal = document.getElementById("orgKnowledgeModal");
  if (!modal) return;
  bindOrgKnowledgeChrome();
  orgKnowledgeState.open = true;
  showModal(modal);
  orgKnowledgeRender();
}

function closeOrgKnowledge() {
  orgKnowledgeState.open = false;
  hideModal(document.getElementById("orgKnowledgeModal"));
}

// 「组织有知识库连接」这件事只对有写权限的人露出入口：成员看不到这个按钮（他们只需要在写作时
// 用「资料参照」），未配置时工作台那边会给出提示。
function syncOrgKnowledgeEntry() {
  const button = document.getElementById("orgKnowledgeBtn");
  if (!button) return;
  // 云端知识库连接整块还没做完，入口先收起来（见 js/core/unfinished-surfaces.js）：
  // 恢复时删掉这个短路，下面的权限判定照旧。
  if (!CLOUD_KNOWLEDGE_ENABLED) {
    button.classList.add("hidden");
    return;
  }
  const allowed = typeof canConfigureOrgAi === "function" && canConfigureOrgAi();
  button.classList.toggle("hidden", !allowed);
}

function initOrgKnowledge() {
  syncOrgKnowledgeEntry();
  document.getElementById("orgKnowledgeBtn")?.addEventListener("click", () => openOrgKnowledge());
}

// ----- js/auth/ops-config.js -----
// 运营配置位：管理端改、前台读。两个位：首页公告条（8.12）+ 客服联系方式（14.5）。
//
// 分工写清楚：
//   - 读：任意已登录成员 GET /api/ops-config（读不到就不用它，绝不拖垮页面）
//   - 写：控制台「运营配置」弹层 POST /api/admin/ops-config（要 console.ops.config）
// 表单按后端下发的 schemas 生成（每个位一段），长度与枚举沿用后端配置。
// 前端提示必填和格式错误；兼容尚未下发格式标记的旧版 schema，后端仍负责最终校验。

const OPS_CONFIG_ADMIN_PATH = "/api/admin/ops-config";
const OPS_CONFIG_PUBLIC_PATH = "/api/ops-config";

const opsConfigState = {
  loading: false,
  loaded: false,
  saving: false,
  updatedBy: "",
  updatedByLabel: "",
  updatedAt: "",
  schemas: [],
  supportContact: null,
};

// 只有 https:// 与站内 / 路径能成为链接（后端也会拒，这里是纵深防御）。
function opsSafeLink(value) {
  const link = String(value || "").trim();
  if (!link) return "";
  if (!(link.startsWith("https://") || link.startsWith("/")) || link.startsWith("//") || /[\\\s]/.test(link)) return "";
  if (link.startsWith("https://") && !/^https:\/\/[^/]/.test(link)) return "";
  try {
    const parsed = new URL(link, "https://ops-config.invalid");
    return parsed.protocol === "https:" && parsed.hostname ? link : "";
  } catch (_error) {
    return "";
  }
}

function opsFormatUpdatedAt(value) {
  if (!value) return "";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" });
}

function renderOpsBanner(banner) {
  const node = document.getElementById("opsBanner");
  if (!node) return;
  const text = String(banner?.text || "").trim();
  if (!banner?.enabled || !text) {
    node.className = "ops-banner hidden";
    node.innerHTML = "";
    return;
  }
  const link = opsSafeLink(banner.link);
  const level = banner.level === "warn" ? "warn" : "info";
  const body = `<i data-lucide="${level === "warn" ? "alert-triangle" : "megaphone"}"></i><span>${escapeHtml(text)}</span>`;
  node.className = `ops-banner ops-banner--${level}`;
  node.innerHTML = link
    ? `<a class="ops-banner-link" href="${escapeHtml(link)}"${
        link.startsWith("https://") ? ' target="_blank" rel="noopener noreferrer"' : ""
      }>${body}<i data-lucide="arrow-right"></i></a>`
    : body;
  refreshIcons();
}

async function loadOpsConfig() {
  try {
    const data = await getBackend(OPS_CONFIG_PUBLIC_PATH);
    renderOpsBanner(data?.homeBanner);
    opsConfigState.supportContact = data?.supportContact || null;
    renderSupportContact(data?.supportContact);
  } catch (_error) {
    /* 这些都是锦上添花：读不到（未登录、后端重启）就当作没配置。 */
    renderOpsBanner(null);
    renderSupportContact(null);
  }
}

// 客服联系方式（14.5）：运营在控制台填，成员在「帮助与反馈」里看到。
// 填什么显示什么，没填的行不显示 —— 不编联系方式。
function renderSupportContact(contact) {
  const node = document.getElementById("supportContactBody");
  if (!node) return;
  const email = String(contact?.email || "").trim();
  const link = opsSafeLink(contact?.link);
  const note = String(contact?.note || "").trim();
  if (!email && !link && !note) {
    node.innerHTML = `
      <div class="support-empty" role="status">
        <i data-lucide="headphones"></i>
        <div class="support-empty-copy">
          <strong>暂未配置客服联系方式</strong>
          <p>管理员还没有填写客服联系方式，请先查看使用教程。</p>
        </div>
      </div>
    `;
    window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
    return;
  }
  const rows = [];
  if (email) {
    rows.push(
      `<div class="support-contact-row"><i data-lucide="mail"></i><div class="support-contact-copy"><span>客服邮箱</span><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></div></div>`
    );
  }
  if (link) {
    const external = link.startsWith("https://");
    rows.push(
      `<div class="support-contact-row"><i data-lucide="message-circle"></i><div class="support-contact-copy"><span>在线客服</span><a href="${escapeHtml(link)}"${
        external ? ' target="_blank" rel="noopener noreferrer"' : ""
      }>打开客服入口</a></div></div>`
    );
  }
  if (note) rows.push(`<div class="support-contact-row"><i data-lucide="info"></i><div class="support-contact-copy"><span>补充说明</span><strong>${escapeHtml(note)}</strong></div></div>`);
  node.innerHTML = rows.join("");
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function opsFieldMarkup(field, value) {
  // 展示文案在这里统一，长度、枚举和校验仍使用服务端 schema。
  const hints = {
    "homeBanner.enabled": "关闭后隐藏公告，保留已填内容；开启时必须填写公告文案。",
    "homeBanner.link": "选填，留空时仅展示文案。支持 https:// 完整地址或 /help 等站内路径，不支持 http://、// 或空格。",
    "supportContact.link": "选填，留空不展示在线客服入口。支持 https:// 完整地址或 /help 等站内路径，不支持 http://、// 或空格。",
  };
  field = { ...field, hint: hints[field.name] || field.hint };
  if (field.name === "homeBanner.level") field.label = "公告样式";
  const name = escapeHtml(field.name);
  const label = escapeHtml(field.label);
  if (field.type === "boolean") {
    return `<label class="ops-config-toggle"><span class="ops-config-toggle-row"><input type="checkbox" role="switch" data-ops-field="${name}" ${
      value ? "checked" : ""
    } /><span>${label}</span><strong data-ops-toggle-status="${name}">${value ? "已开启" : "已关闭"}</strong></span>${
      field.hint ? `<small>${escapeHtml(field.hint)}</small>` : ""
    }</label>`;
  }
  if (field.type === "select") {
    const options = (field.options || [])
      .map(
        (option) =>
          `<option value="${escapeHtml(option.value)}" ${String(value) === String(option.value) ? "selected" : ""}>${escapeHtml(
            option.label
          )}</option>`
      )
      .join("");
    return `<label class="admin-console-form-field"><span>${label}</span><select data-ops-field="${name}">${options}</select>${
      field.hint ? `<small>${escapeHtml(field.hint)}</small>` : ""
    }</label>`;
  }
  return `<label class="admin-console-form-field"><span>${label}</span><input type="text" data-ops-field="${name}" aria-describedby="ops-error-${name}" value="${escapeHtml(
    value == null ? "" : String(value)
  )}"${field.maxLength ? ` maxlength="${Number(field.maxLength)}"` : ""} />${
    field.hint ? `<small>${escapeHtml(field.hint)}</small>` : ""
  }${field.maxLength ? `<span class="ops-char-count" data-ops-count="${name}">${String(value || "").length} / ${Number(field.maxLength)}</span>` : ""}<span class="ops-field-error" id="ops-error-${name}" data-ops-error="${name}" aria-live="polite"></span></label>`;
}

function opsValidateFields({ focus = false } = {}) {
  const payload = opsReadForm();
  let firstInvalid = null;
  for (const schema of opsConfigState.schemas) {
    for (const field of schema.fields || []) {
      if (field.type !== "text") continue;
      const name = `${schema.key}.${field.name}`;
      const input = document.querySelector(`[data-ops-field="${name}"]`);
      if (!input) continue;
      const value = String(payload[schema.key]?.[field.name] || "").trim();
      // 当前服务端可能尚未提供 requiredWhen / format。
      const requiredWhen = field.requiredWhen || (name === "homeBanner.text" ? "enabled" : "");
      const format = field.format || (["homeBanner.link", "supportContact.link"].includes(name)
        ? "safeLink" : name === "supportContact.email" ? "email" : "");
      let error = "";
      if (requiredWhen && payload[schema.key]?.[requiredWhen] && !value) error = "开启公告时，请填写公告文案。";
      if (value && format === "safeLink" && !opsSafeLink(value)) error = "请输入完整 https:// 地址或站内 / 路径，不支持 http://、// 或空格。";
      if (value && format === "email" && !/^[^@\s]+@[^@\s]+$/.test(value)) error = "请输入有效的客服邮箱，例如 support@example.com。";
      if (field.maxLength && value.length > field.maxLength) error = `最多填写 ${field.maxLength} 字。`;
      input.setAttribute("aria-invalid", String(Boolean(error)));
      const note = document.querySelector(`[data-ops-error="${name}"]`);
      if (note) note.textContent = error;
      const count = document.querySelector(`[data-ops-count="${name}"]`);
      if (count) count.textContent = `${input.value.length} / ${field.maxLength}`;
      if (error && !firstInvalid) firstInvalid = input;
    }
  }
  if (focus) firstInvalid?.focus();
  return !firstInvalid;
}

// 一个配置位一段：标题 + 说明 + 字段。位是后端声明的，前端不写死有几个。
function renderOpsConfigForm(payload) {
  const fields = document.getElementById("opsConfigFields");
  const status = document.getElementById("opsConfigStatus");
  if (!fields) return;
  const schemas =
    Array.isArray(payload?.schemas) && payload.schemas.length ? payload.schemas : opsConfigState.schemas;
  opsConfigState.schemas = schemas || [];
  opsConfigState.updatedBy = payload?.updatedBy ?? opsConfigState.updatedBy;
  opsConfigState.updatedByLabel = payload?.updatedByLabel ?? opsConfigState.updatedByLabel;
  opsConfigState.updatedAt = payload?.updatedAt ?? opsConfigState.updatedAt;
  fields.innerHTML = (schemas || [])
    .map((schema) => {
      const value = payload?.[schema.key] || {};
      const body = (schema.fields || [])
        .map((field) => opsFieldMarkup({ ...field, name: `${schema.key}.${field.name}` }, value[field.name]))
        .join("");
      return `<section class="ops-config-slot">
        <h3 class="ops-config-slot-title">${escapeHtml(schema.label || schema.key)}</h3>
        ${
          schema.description || schema.key === "supportContact"
            ? `<p class="admin-console-permission-note">${escapeHtml(schema.key === "homeBanner" ? "显示在用户首页顶部。" : schema.key === "supportContact" ? "显示在用户端「帮助与反馈」中，未填写的项目不展示。" : schema.description)}</p>`
            : ""
        }
        <div class="admin-console-form-grid">${body}</div>
        ${schema.key === "homeBanner" ? '<div class="ops-config-preview"><div class="ops-config-preview-heading"><strong>公告预览</strong><span>编辑后需保存才生效</span></div><div id="opsBannerPreview" aria-live="polite"></div></div>' : ""}
      </section>`;
    })
    .join("");
  renderOpsConfigPreview();
  if (status) {
    // 后端把操作者解析成可读名字（updatedByLabel）；拿不到就如实标注这是内部 ID，
    // 不要把那串字符当成一个人名展示（P71）。
    const who = opsConfigState.updatedBy
      ? `最近修改：${opsConfigState.updatedByLabel || `${opsConfigState.updatedBy}（内部 ID）`}`
      : "还没有人改过";
    const when = opsFormatUpdatedAt(opsConfigState.updatedAt);
    status.textContent = when ? `${who} · ${when}` : who;
  }
}

function renderOpsConfigPreview() {
  const node = document.getElementById("opsBannerPreview");
  if (!node) return;
  const banner = opsReadForm().homeBanner || {};
  const status = document.querySelector('[data-ops-toggle-status="homeBanner.enabled"]');
  if (status) status.textContent = banner.enabled ? "已开启" : "已关闭";
  const text = String(banner.text || "").trim();
  if (!banner.enabled || !text) {
    node.innerHTML = `<p class="ops-config-preview-empty">${banner.enabled ? "填写公告文案后，这里会显示预览。" : "公告已关闭，首页不展示；已填内容会保留。"}</p>`;
    return;
  }
  const level = banner.level === "warn" ? "warn" : "info";
  const link = opsSafeLink(banner.link);
  // 预览不生成可点击链接，避免填写过程中误跳转丢失内容。
  node.innerHTML = `<div class="ops-banner ops-banner--${level}"><i data-lucide="${level === "warn" ? "alert-triangle" : "megaphone"}"></i><span>${escapeHtml(text)}</span>${link ? '<i data-lucide="arrow-right"></i>' : ""}</div><p class="ops-config-preview-note">${link ? `点击公告将前往：${escapeHtml(link)}` : banner.link ? "链接格式有误，请修改后保存。" : "仅展示文案，不跳转。"}</p>`;
  refreshIcons();
}

// 字段名是「槽位.字段」，读回来按槽位分桶 —— 后端按槽位取白名单。
function opsReadForm() {
  const fields = document.getElementById("opsConfigFields");
  const payload = {};
  fields?.querySelectorAll("[data-ops-field]").forEach((node) => {
    const [slot, name] = String(node.dataset.opsField || "").split(".");
    if (!slot || !name) return;
    payload[slot] = payload[slot] || {};
    payload[slot][name] = node.type === "checkbox" ? node.checked : node.value;
  });
  return payload;
}

async function openOpsBannerModal() {
  const modal = document.getElementById("opsConfigModal");
  if (!modal) return;
  if (opsConfigState.loading || opsConfigState.saving) return;
  opsConfigState.loading = true;
  opsConfigState.loaded = false;
  const fields = document.getElementById("opsConfigFields");
  const button = document.getElementById("saveOpsConfigBtn");
  const status = document.getElementById("opsConfigStatus");
  if (status) status.textContent = "";
  if (fields) fields.innerHTML = '<p role="status">正在读取运营配置…</p>';
  if (button) button.disabled = true;
  if (typeof showModal === "function") showModal(modal);
  try {
    const payload = await getBackend(OPS_CONFIG_ADMIN_PATH);
    if (!Array.isArray(payload?.schemas) || !payload.schemas.length ||
        payload.schemas.some((schema) => !schema?.key || !Array.isArray(schema.fields) || !schema.fields.length)) {
      throw new Error("未能读取运营配置表单，请重试；若仍为空，请联系管理员检查配置服务。");
    }
    renderOpsConfigForm(payload);
    opsConfigState.loaded = true;
  } catch (error) {
    const status = document.getElementById("opsConfigStatus");
    if (status) status.textContent = error?.message || "运营配置读取失败";
    if (fields) fields.innerHTML = '<p>配置读取失败，尚不能保存。</p><button type="button" class="ghost-btn" id="retryOpsConfigBtn">重试</button>';
    document.getElementById("retryOpsConfigBtn")?.addEventListener("click", () => openOpsBannerModal());
  } finally {
    opsConfigState.loading = false;
    if (button) button.disabled = !opsConfigState.loaded;
  }
}

async function saveOpsBanner(event) {
  event?.preventDefault?.();
  if (opsConfigState.saving || !opsConfigState.loaded || !opsValidateFields({ focus: true })) return;
  const button = document.getElementById("saveOpsConfigBtn");
  opsConfigState.saving = true;
  if (button) button.disabled = true;
  try {
    const payload = await postBackend(OPS_CONFIG_ADMIN_PATH, opsReadForm());
    renderOpsConfigForm({ ...payload, schemas: opsConfigState.schemas });
    /* 存完立刻让前台看到：不必等下次刷新。 */
    renderOpsBanner(payload?.homeBanner);
    if (payload?.supportContact) {
      opsConfigState.supportContact = payload.supportContact;
      renderSupportContact(payload.supportContact);
    }
    toast("运营配置已保存，成员重新打开或刷新页面后生效");
  } catch (error) {
    const status = document.getElementById("opsConfigStatus");
    if (status) status.textContent = error?.message || "保存失败，请重试；填写内容已保留。";
    toast(error?.message || "保存失败", { kind: "error", duration: 6000 });
  } finally {
    opsConfigState.saving = false;
    if (button) button.disabled = false;
  }
}

function initOpsConfig() {
  const update = () => {
    opsValidateFields();
    renderOpsConfigPreview();
  };
  document.getElementById("opsConfigFields")?.addEventListener("input", update);
  document.getElementById("opsConfigFields")?.addEventListener("change", update);
  document.getElementById("openOpsConfigBtn")?.addEventListener("click", () => {
    void openOpsBannerModal();
  });
  document.getElementById("closeOpsConfigBtn")?.addEventListener("click", () => {
    const modal = document.getElementById("opsConfigModal");
    if (modal && typeof hideModal === "function") hideModal(modal);
  });
  document.getElementById("cancelOpsConfigBtn")?.addEventListener("click", () => {
    const modal = document.getElementById("opsConfigModal");
    if (modal && typeof hideModal === "function") hideModal(modal);
  });
  document.getElementById("opsConfigForm")?.addEventListener("submit", (event) => {
    void saveOpsBanner(event);
  });
  document.getElementById("closeSupportBtn")?.addEventListener("click", () => {
    const modal = document.getElementById("supportModal");
    if (modal && typeof hideModal === "function") hideModal(modal);
  });
}

// 「帮助与反馈」弹层：只展示运营配置的联系方式。
function openSupportModal() {
  const modal = document.getElementById("supportModal");
  if (!modal) return;
  renderSupportContact(opsConfigState.supportContact);
  if (typeof showModal === "function") showModal(modal);
}

// ----- js/auth/login-device.js -----
// 新设备登录提醒（7.6）：登录态落定后告诉后端"这次是哪台设备"。
//
// 前端只负责上报，判定与发信都在服务端（`server/login_devices.py`）：设备身份以服务端
// 签发的持久 cookie 为主（请求头特征只兜底），用户邮箱从 Logto 取，前端拿不到也改不了收件人。
//
// 提示只在新设备时出现一次，避免每次登录都弹。**没发出邮件时不再笼统说
// "未配置邮件提醒"**：后端会给 `notifyReason`——"没绑邮箱"要引导用户去绑、
// "环境没配 SMTP"是运维的事、"开关关闭""发送失败"又各不相同。把它们混成
// 一句话，正是用户报"邮件收不到"却无从下手的原因。

let loginDeviceReported = false;

// 对应后端 `login_devices.NOTIFY_REASONS`（共 7 个取值）里的 5 个，是这些原因仅有的用户可见落点。
// 另两个（`device_known` / `not_recorded`）**刻意不映射**：它们只在"不是新设备"或"压根没记下来"
// 时出现，永远伴随 `isNew: false`，而下面 `loginDeviceNotice` 第一句就早退——用户不需要为
// "这次是老设备"或"这次没记下来"看到一句提示。新增后端取值必须同时补进这里，否则
// tests/test_notify_reasons_single_source.py 会红。
const LOGIN_DEVICE_NOTICES = {
  sent: "新设备登录已记录，我们把提醒发到了你的邮箱",
  alert_disabled: "新设备登录已记录（本站暂时关闭了邮件提醒，可在「账号设置 → 登录设备」查看）",
  no_recipient: "新设备登录已记录。你还没有绑定邮箱，绑定后才能收到登录提醒（可在「账号设置 → 登录设备」查看）",
  smtp_not_configured: "新设备登录已记录（当前环境未配置邮件提醒，可在「账号设置 → 登录设备」查看）",
  send_failed: "新设备登录已记录（提醒邮件发送失败，请在「账号设置 → 登录设备」核对这次登录）",
};

function loginDeviceNotice(data) {
  if (!data?.isNew) return "";
  const notice = LOGIN_DEVICE_NOTICES[String(data.notifyReason || "")];
  if (notice) return notice;
  // 没带原因（字段缺失/网关截断）时只说"没发出"，不替服务端猜是哪一种。
  return data.notified
    ? LOGIN_DEVICE_NOTICES.sent
    : "新设备登录已记录（这次没能发出邮件提醒，可在「账号设置 → 登录设备」查看）";
}

async function announceLoginDevice() {
  if (loginDeviceReported) return;
  loginDeviceReported = true;
  try {
    // 带 cookie：服务端据此下发/回传持久设备标识——它才是判定"这台设备"的主依据
    // （只靠请求头特征，同网段 + 同版本浏览器的两台机器会算成同一台）。
    const data = await postBackend("/api/auth/login-device", {}, { credentials: "same-origin" });
    const notice = loginDeviceNotice(data);
    if (notice) toast(notice, { duration: 6000 });
  } catch (_error) {
    /* 提醒是附加服务：上报失败不打扰用户，也不影响登录。 */
  }
}

function resetLoginDeviceReport() {
  // 退出登录后再登录，要重新上报一次（可能是另一台设备/另一个人）。
  loginDeviceReported = false;
}

// ----- js/core/state.js -----
const workspaceSections = {
  home: { eyebrow: "薯作台", title: "首页" },
  intake: { eyebrow: "快速笔记", title: "导入原笔记" },
  original: { eyebrow: "原创笔记", title: "导入参考笔记" },
  rewrite: { eyebrow: "创作工作台", title: "图文编辑" },
  viral: { eyebrow: "原创笔记", title: "内容模板库" },
  knowledge: { eyebrow: "资料与依据", title: "知识库" },
  image: { eyebrow: "视觉工作台", title: "AI 生图" },
  plugin: { eyebrow: "独立工具", title: "低粉爆款采集" },
  publish: { eyebrow: "发布中心", title: "待发布内容" },
  "auth-management": { eyebrow: "团队治理", title: "权限管理" },
  "admin-console": { eyebrow: "平台治理", title: "管理后台" },
};

const workflowSectionOptions = {
  replica: ["intake", "rewrite"],
  original: ["original", "rewrite", "viral"],
  tools: ["image", "plugin", "knowledge", "publish"],
};

const defaultLastWorkflowSections = {
  replica: "intake",
  original: "original",
  tools: "image",
};

const enteredWorkflowsThisSession = new Set();

const defaultAiConfig = {
  activeTemplateId: "system-link",
  promptMode: "rewrite",
  text: {
    apiUrl: "",
    apiKey: "",
    model: "gpt-4o-mini",
    models: [],
  },
  image: {
    apiUrl: "",
    apiKey: "",
    model: "gpt-image-1",
    models: [],
  },
  knowledge: {
    enabled: false,
    provider: "dify",
    minScore: 0.55,
    dify: { apiUrl: "", apiKey: "", hasApiKey: false, maskedApiKey: "", datasetId: "", insecure: false },
  },
  templateAnalysis: {
    concurrency: 1,
  },
  watermark: {
    enabled: true,
    visible: true,
    metadata: true,
    visibleBackend: "cv2",
    sensitivity: "auto",
    failOpen: true,
  },
  templates: [
    {
      id: "system-link",
      name: "系统默认 1",
      mode: "rewrite",
      titlePrompt:
        "在原始标题和正文基础上，改写 1 条小红书标题。保留原意和真实信息，不新增原文没有的数字、价格、地点、功效或体验。标题要自然、有吸引力，20 字以内，只输出标题。",
      bodyPrompt:
        "围绕主题写一篇可直接发布的小红书正文。开头用 1-2 句口语化表达快速点出读者痛点、场景或一个明确结论，让人愿意继续读；正文用 3-5 个短段落展开，每段只讲一个重点，优先写具体做法、对比、清单、避坑提醒或真实细节，信息要有收藏价值；结尾用一句自然的问题或选择题引导互动。整体像真实用户分享，句子短、节奏轻、有换行和留白，可少量使用 emoji 但不要堆砌。保留原文事实和边界，不新增原文没有的品牌、价格、功效、数据、身份或亲身体验；不要广告腔、新闻稿腔、空泛鸡汤、连续使用“首先/其次/综上”，不要解释写作过程。只输出正文。",
      tagsPrompt:
        "根据标题、正文和图片信息，生成 4-8 个小红书标签。标签要贴近内容，不堆砌无关热词。只输出标签，格式如：#标签1 #标签2。",
      imagePrompt:
        "基于输入图片进行轻度重绘，生成一张风格自然、适合小红书发布的新图。保持主体、产品、人物和核心构图基本不变，可调整背景细节、光影、色温、纹理、配色、装饰元素或文字样式，让画面更干净、更有发布感。",
      promptSets: {
        rewrite: {
          titlePrompt: "在原始标题和正文基础上，改写 1 条小红书标题。保留原意和真实信息，不新增原文没有的数字、价格、地点、功效或体验。标题要自然、有吸引力，20 字以内，只输出标题。",
          bodyPrompt: "围绕主题写一篇可直接发布的小红书正文。开头用 1-2 句口语化表达快速点出读者痛点、场景或一个明确结论，让人愿意继续读；正文用 3-5 个短段落展开，每段只讲一个重点，优先写具体做法、对比、清单、避坑提醒或真实细节，信息要有收藏价值；结尾用一句自然的问题或选择题引导互动。整体像真实用户分享，句子短、节奏轻、有换行和留白，可少量使用 emoji 但不要堆砌。保留原文事实和边界，不新增原文没有的品牌、价格、功效、数据、身份或亲身体验；不要广告腔、新闻稿腔、空泛鸡汤、连续使用“首先/其次/综上”，不要解释写作过程。只输出正文。",
          tagsPrompt: "根据标题、正文和图片信息，生成 4-8 个小红书标签。标签要贴近内容，不堆砌无关热词。只输出标签，格式如：#标签1 #标签2。",
          imagePrompt: "基于输入图片进行轻度重绘，生成一张风格自然、适合小红书发布的新图。保持主体、产品、人物和核心构图基本不变，可调整背景细节、光影、色温、纹理、配色、装饰元素或文字样式，让画面更干净、更有发布感。",
        },
        image: {
          titlePrompt: "请先仔细观察输入图片，再结合用户补充描述，生成 1 条适合小红书的标题。提炼图片中真实可见的主体、场景、动作、色彩或氛围，突出一个具体亮点，标题自然口语化、有画面感和点击欲，13-20 字，不超过 20 字。不得臆测图片看不到的品牌、价格、地点、人物身份、功效、数据或经历，不加引号、编号和解释，只输出标题。",
          bodyPrompt: "请根据输入图片和用户补充描述，写一篇符合小红书风格、可直接发布的图文正文。开头用 1-2 句自然描述图片主体和场景；中间围绕图片中明确可见的细节，整理 3-5 条有用、值得收藏或能引发共鸣的内容；结尾用轻松自然的问题引导互动。只写图片明确呈现或用户明确提供的信息，看不清、无法确认或图片没有体现的品牌、价格、地点、身份、功效、数据和经历一律不要补写。语气真实、有细节、有分段和留白，避免广告腔、流水账和空泛鸡汤；信息不足时宁可简洁，不要硬凑。只输出正文。",
          tagsPrompt: "根据输入图片中明确可见的主体、场景、风格、主题和用户补充描述，生成 4-8 个精准的小红书标签。标签要具体、自然、彼此不重复，覆盖内容主题、场景、对象或适合人群；不得使用图片无法确认的品牌、地点、功效、价格和无关热词。每个标签 2-12 个字，只输出空格分隔的标签，格式如：#标签1 #标签2。",
          imagePrompt: "",
        },
      },
    },
    {
      id: "system-image",
      name: "系统默认 2",
      mode: "image",
      titlePrompt:
        "请仔细观察输入图片，并结合用户补充描述，提炼画面中明确可见的信息，生成 1 条适合小红书的标题。先判断主题、场景和主体，再突出一个最有价值或最吸引人的点。不得臆测图片看不到的品牌、价格、地点、人物身份、功效、数据或经历；不把画面文字扩写成未经证实的事实。标题具体、口语自然、有点击欲，20 字以内，不加引号、编号或解释，只输出标题。",
      bodyPrompt:
        "请根据输入图片和用户补充描述，写一篇可直接发布的小红书图文正文。先用 1-2 句准确描述画面主体与场景，再提炼 3-5 条对读者有用的信息或观察，最后用自然的问题引导互动。只使用图片明确可见和用户明确提供的内容；看不清或无法确认的文字、品牌、价格、地点、时间、人物身份、功效和数据一律不补写。避免流水账、夸张广告和模板化开场；分段留白，语气像真实用户分享。信息不足时宁可简洁，也不要硬凑细节。只输出正文。",
      tagsPrompt:
        "根据图片中明确可见的主题、场景、对象和用户描述，生成 4-8 个精准的小红书标签。优先覆盖内容主题、场景、风格、对象和目标人群；不得使用图片无法确认的品牌、地点、功效、价格或无关热词。标签彼此不重复，每个 2-12 个字，只输出空格分隔的标签，格式如：#标签1 #标签2。",
      imagePrompt:
        "",
      promptSets: {
        rewrite: {
          titlePrompt: "在原始标题和正文基础上，改写 1 条小红书标题。保留原意和真实信息，不新增原文没有的数字、价格、地点、功效或体验。标题要自然、有吸引力，20 字以内，只输出标题。",
          bodyPrompt: "围绕主题写一篇可直接发布的小红书正文。开头用 1-2 句口语化表达快速点出读者痛点、场景或一个明确结论，让人愿意继续读；正文用 3-5 个短段落展开，每段只讲一个重点，优先写具体做法、对比、清单、避坑提醒或真实细节，信息要有收藏价值；结尾用一句自然的问题或选择题引导互动。整体像真实用户分享，句子短、节奏轻、有换行和留白，可少量使用 emoji 但不要堆砌。保留原文事实和边界，不新增原文没有的品牌、价格、功效、数据、身份或亲身体验；不要广告腔、新闻稿腔、空泛鸡汤、连续使用“首先/其次/综上”，不要解释写作过程。只输出正文。",
          tagsPrompt: "根据标题、正文和图片信息，生成 4-8 个小红书标签。标签要贴近内容，不堆砌无关热词。只输出标签，格式如：#标签1 #标签2。",
          imagePrompt: "基于输入图片进行轻度重绘，生成一张风格自然、适合小红书发布的新图。保持主体、产品、人物和核心构图基本不变，可调整背景细节、光影、色温、纹理、配色、装饰元素或文字样式，让画面更干净、更有发布感。",
        },
        image: {
          titlePrompt: "请先仔细观察输入图片，再结合用户补充描述，生成 1 条适合小红书的标题。提炼图片中真实可见的主体、场景、动作、色彩或氛围，突出一个具体亮点，标题自然口语化、有画面感和点击欲，13-20 字，不超过 20 字。不得臆测图片看不到的品牌、价格、地点、人物身份、功效、数据或经历，不加引号、编号和解释，只输出标题。",
          bodyPrompt: "请根据输入图片和用户补充描述，写一篇符合小红书风格、可直接发布的图文正文。开头用 1-2 句自然描述图片主体和场景；中间围绕图片中明确可见的细节，整理 3-5 条有用、值得收藏或能引发共鸣的内容；结尾用轻松自然的问题引导互动。只写图片明确呈现或用户明确提供的信息，看不清、无法确认或图片没有体现的品牌、价格、地点、身份、功效、数据和经历一律不要补写。语气真实、有细节、有分段和留白，避免广告腔、流水账和空泛鸡汤；信息不足时宁可简洁，不要硬凑。只输出正文。",
          tagsPrompt: "根据输入图片中明确可见的主体、场景、风格、主题和用户补充描述，生成 4-8 个精准的小红书标签。标签要具体、自然、彼此不重复，覆盖内容主题、场景、对象或适合人群；不得使用图片无法确认的品牌、地点、功效、价格和无关热词。每个标签 2-12 个字，只输出空格分隔的标签，格式如：#标签1 #标签2。",
          imagePrompt: "",
        },
      },
    },
  ],
};

const state = {
  currentSection: "home",
  currentSectionWorkflow: "replica",
  activeWorkflow: "replica",
  lastWorkflowSections: { ...defaultLastWorkflowSections },
  drafts: [],
  publish: [],
  // Hydrated from the backend; kept separate from publish-list items because
  // the extension uses this durable task record while the creator page is open.
  publishTasks: null,
  stateRevision: 0,
  stateWriteBlocked: false,
  activeDraftId: null,
  uploadFilesByWorkflow: { replica: [], original: [] },
  selectedCount: 1,
  selectedStyle: "study",
  imageStudioMode: "cover",
  selectedImageTemplateId: "",
  imageGenerationMode: "create",
  imageReferences: [],
  replacementReferences: [],
  regeneratingDraftImageKey: "",
  draggedDraftImageKey: "",
  draftImageRegenerationQueue: [],
  draftImageRegenerationActive: new Map(),
  draftImageRegenerationLimit: IMAGE_GENERATION_CONCURRENCY,
  draftImageRegenerationConfigPromise: null,
  singleImageGenerationKeys: new Set(),
  singleImageEditor: { draftId: "", imageIndex: -1, label: "", source: "", prompt: "", references: [], generating: false },
  latestGeneratedImages: [],
  generatedImages: [],
  imageTemplateOverrides: {},
  customSceneImageTemplates: [],
  deletedSceneImageTemplateIds: [],
  imageTemplateEditing: { cover: false, scene: false },
  imageTemplateDeleteSelections: { cover: [], scene: [] },
  aiConfig: null,
  aiConfigView: "api",
  knowledgeStatus: null,
  knowledgeDocuments: [],
  knowledgeCloudDocuments: [],
  knowledgeCloudLoading: false,
  knowledgeCloudLoadAttempted: false,
  knowledgeFolders: [],
  knowledgeCollapsedFolderIds: [],
  knowledgeUploadQueue: [],
  knowledgeUploadFailures: [],
  knowledgeUploadOptions: { targetFolderId: "", conflictPolicy: "version", metadata: {} },
  knowledgeUploadProgress: null,
  knowledgeReview: { query: "", documents: [], results: [], selected: {}, pendingSelections: [], preferredDocumentIds: [], continuation: null, allowFallback: true, loading: false },
  knowledgeSource: "local",
  knowledgeEvidenceCollapsed: true,
  // Temporary local reference files are scoped to the active original task.
  localUploadQueue: [],
  localUploadQueuesByDraft: new Map(),
  reverseTopicUploadQueue: [],
  reverseTopicCloudDocuments: [],
  reverseTopicCloudSelection: [],
  reverseTopicSource: "local",
  reverseTopicCloudLoading: false,
  reverseTopicGeneration: { active: false, stage: "", mode: "focused" },
  localReferenceSelection: [],
  localReferenceSelectionOwnerId: "",
  reverseTopicSelection: { local: [], cloud: [] },
  pendingLocalKnowledge: null,
  layoutTemplates: [],
  activeLayoutTemplateId: null,
  activeTemplateRegionId: null,
  previewImageIndex: 0,
  previewImageCount: 1,
  viralAnalysis: null,
  viralAtoms: [],
  viralAtomTypes: {
    topic_angle: "选题角度",
    audience_pain: "人群痛点",
    title_formula: "标题结构",
    opening_hook: "开头钩子",
    body_framework: "正文框架",
    content_component: "内容组件",
    interaction_ending: "互动结尾",
    visual_narrative: "图文叙事",
    visual_style: "视觉风格",
  },
  viralSelections: {},
  viralAdvancedOverrides: {},
  viralLibraries: { topic: [], title: [], cover: [], inner: [] },
  viralTopicPackages: [],
  viralTopicQuery: "",
  viralTopicTrack: "",
  viralTopicFormat: "",
  viralLibrarySelections: { topicId: "", titleId: "", coverId: "", innerId: "", templateSetId: "" },
  viralActiveLibraryKinds: { topic: "topic", title: "title", cover: "cover", inner: "inner" },
  viralActiveWorkbench: "topic",
  innerPagePicker: null,
  viralLastGeneration: null,
  viralAtomQuery: "",
  viralAtomTypeFilter: "",
  viralComposeSourceDraftId: null,
  rewriteGeneration: { active: false, ownerDraftId: "", stage: "text", completed: 0, total: 0, currentLabel: "" },
  performanceSnapshots: [],
  templateDeletion: null,
  visualLibraryEditing: { cover: false, inner: false },
  visualLibraryDeleteSelections: { cover: [], inner: [] },
  visualTemplateImport: null,
  textLibraryEditing: { topic: false, title: false },
  imageViewer: { images: [], index: 0, title: "图片预览", zoom: 1, fit: true, draftId: "", draftImageLabels: [] },
  imageAssignment: null,
  openDraftAiBlock: "",
  lowFanCollection: { job: null, results: [], counts: { total: 0, complete: 0, incomplete: 0 }, filter: "all", watcher: 0, suffixes: [] },
  lowFanVisualSearch: { src: "", fileName: "", analysis: null, busy: false },
};

// Prevent the initial empty UI state from overwriting durable backend data
// while asynchronous hydration is still in progress.
let backendStateHydrated = false;
let backendSaveDirty = false;
let backendSaveRunning = null;
let backendSaveFailureShown = false;
// 最后一次写盘失败的真实原因（HTTP 状态 + 服务端 errorCode）。发布前的落库闸门原本只回
// 一句"还没保存到服务器"，把 404 resource_not_found / 403 / 5xx 全糊成一团：用户照着这句
// 既不知道去哪修，排障也分不清是哪一类。这里留一份给调用方按码分档
// （见 low-fan.js 的 publishPersistenceFailureMessage）。
let backendStateWriteError = null;
let pendingBackendRecovery = null;
let closeGuardArmed = false;
// 本会话是否发生过用户编辑：水合/切租户这类程序化写入不算，见 saveState(options)。
let userEditedSinceHydration = false;
// 每次成功保存最多做一次静默重水合，防止版本冲突时反复拉取。
let stateConflictRecoveryAttempted = false;
// 本次"用本机版本重放"是否成功落库：成功后要明确告诉用户覆盖了另一个窗口的更新（P98）。
let stateConflictOverwrote = false;
// 切租户（含登录落定后第一次切桶）到服务端水合对齐 revision 之间，写盘必然带着
// 上一个/新租户的错配上下文出去。这段时间只写本地，落盘交给水合之后（P95）。
let tenantScopeSwitchPending = false;
// 403 不是版本冲突，本地内容本身没错，只是这一轮请求没被接受（身份/组织上下文未就绪）。
// 不能像 409 那样停写，但也不能无限重试——按固定档位退避几次，之后交给用户决定。
const STATE_WRITE_RETRY_DELAYS_MS = [2000, 6000, 15000];
let stateWriteRetryAttempt = 0;
let stateWriteRetryTimer = null;
// 启动期/登录落定那一次 `GET /api/state` 会撞上 401（令牌还没就绪）、403（组织上下文未就绪）、
// 429（首屏并发压到 nginx 的 limit_conn），或者拿到 200 + 空 body；这些都属于"这一次没读到"
// （P85）。按固定档位退避重试，仍读不到才进入降级态。
const HYDRATION_RETRY_DELAYS_MS = [1500, 4000, 9000];
let hydrationRunToken = 0;
let workspaceHydrationDegraded = false;

let templatePreviewTimer = null;
let templatePreviewRequestId = 0;
let templateCanvasDrag = null;
let previewDrag = null;
let aiConfigBeforeEdit = null;
const confirmedApiKeyTypes = new Set();
const apiKeyEditBackups = { text: null, image: null };
const knowledgeSecretDirty = { dify: false };
let visualTemplateImportController = null;
// 当前单图模板导入对应的服务端任务 id。停止上传时必须拿它去 /api/jobs/cancel，
// 只 abort 浏览器请求不会停掉服务端 worker（见 generate.js 的 cancelVisualTemplateImportJob）。
let visualTemplateImportJobId = "";
let viralPromptBuildController = null;
const pageExpansionCancelBackups = new Map();
let rewriteGenerationController = null;
let rewriteGenerationOwnerDraftId = "";
let imageGenerationController = null;
let knowledgeReindexController = null;
let imageTemplateEditorPages = [];
let imageTemplateEditorPageIndex = 0;
const imageGenerationControlStates = new Map();
const pendingTopicPackageArchives = new Set();
const archivedTopicPackageIds = new Set();
// 新建选题的占位卡：保存要跑字段补齐 + 取整库快照，可能几秒；先把卡片放上去，
// 用户才不会对着没变化的列表怀疑自己没点上。
const pendingTopicPackageSaves = new Set();
const pendingTitleTemplateArchives = new Set();
const archivedTitleAtomIds = new Set();
// 新建标题模板的占位卡：与选题库同一套做法，避免新卡片"突然冒出来"。
const pendingTitleTemplateSaves = new Set();
let sectionTransitionCleanup = null;
let imageReferenceMentionState = null;
const uiMotionTimers = new WeakMap();
const modalMotionTimers = new WeakMap();
const modalReturnFocus = new WeakMap();
const viralSnapshotLoads = new Set();
const draftImageDrag = {
  active: false,
  board: null,
  card: null,
  handle: null,
  preview: null,
  placeholder: null,
  pointerId: null,
  startX: 0,
  startY: 0,
  offsetX: 0,
  offsetY: 0,
  targetKey: "",
  placeAfter: false,
  pressActive: false,
  longPressTimer: null,
  longPressFired: false,
  suppressClick: false,
};

function defaultGenericTemplatePage(index = 0) {
  return {
    headline: index === 0 ? "可替换红色标题第一行" : `内页 ${index} 红色标题占位`,
    subtitle: index === 0 ? "可替换红色标题第二行" : "替换为本页核心观点",
    body1: "黑色粗体正文占位第一行",
    body2: "黑色粗体正文占位第二行",
    body3: "黑色粗体正文占位第三行",
    quote: index === 0 ? "--《书名/来源占位》" : "-- 章节/署名占位",
    tags: "#标签占位 #主题占位",
    imageNote: `替换为新素材图 ${index + 1}`,
    imageSrc: "",
  };
}

function defaultTocTemplateFields() {
  return {
    type: "toc-page",
    activePage: 0,
    headline: "揭开隐藏于社会暗处的惊世真相",
    subtitle: "随时可能消失，感兴趣的抓紧了",
    items: [
      { label: "维闺时代的女主角", page: "", highlight: true, underline: false, level: 2 },
      { label: "凡人赵忠祥", page: "", highlight: false, underline: false, level: 2 },
      { label: "债务人牛群", page: "", highlight: false, underline: true, level: 2 },
      { label: "徐明是谁", page: "28", highlight: false, underline: false, level: 2 },
      { label: "财富", page: "", highlight: false, underline: false, level: 1 },
      { label: "谁是有钱人", page: "307", highlight: false, underline: false, level: 1 },
      { label: "中国人的财富秘密", page: "322", highlight: false, underline: false, level: 1 },
      { label: "媒壳资本家的财富秘史", page: "335", highlight: true, underline: false, level: 1 },
      { label: "社会", page: "", highlight: false, underline: false, level: 0 },
      { label: "交通厅长的“事故多发期”", page: "367", highlight: true, underline: false, level: 0 },
      { label: "人命与仕途", page: "385", highlight: false, underline: false, level: 0 },
      { label: "青基会的赢与亏", page: "400", highlight: false, underline: true, level: 0 },
      { label: "新闻笔记", page: "", highlight: false, underline: false, level: -1 },
      { label: "调查李真", page: "421", highlight: false, underline: false, level: -1 },
      { label: "叙述马德", page: "450", highlight: false, underline: false, level: -1 },
    ],
  };
}

function defaultGenericTemplateFields() {
  return {
    type: "generic-card",
    activePage: 0,
    pages: Array.from({ length: 18 }, (_, index) => defaultGenericTemplatePage(index)),
  };
}

function defaultPosterTemplateFields(template) {
  const editablePages = templateEditablePages(template);
  return {
    type: "poster-auto",
    activePage: 0,
    themeId: "original",
    pages: editablePages.map((page, pageIndex) => ({
      sourceImage: page.sourceImage || "",
      regions: Object.fromEntries(
        (page.regions || []).map((region, regionIndex) => [
          region.id || `region_${regionIndex}`,
          {
            value: region.kind === "text" ? region.placeholder || (pageIndex === 0 ? "可替换标题" : `可替换文字 ${regionIndex + 1}`) : "",
            imageSrc: "",
          },
        ])
      ),
    })),
  };
}

function isLayeredTemplate(template) {
  return ["layered-v1", "layered"].includes((template?.params?.engine || template?.params?.layout?.cover || "").toLowerCase());
}

function templateLayerPages(template) {
  return Array.isArray(template?.params?.pages) ? template.params.pages : [];
}

function defaultLayeredPageFields(template) {
  const page = templateLayerPages(template)[0] || {};
  const layers = page.layers || [];
  const content = {};
  const layerState = {};
  layers.forEach((layer, index) => {
    const id = layer.id || `layer_${index}`;
    if (!layer) return;
    if (layer.contentKey) {
      content[layer.contentKey] = layer.defaultContent || content[layer.contentKey] || "";
    }
    layerState[id] = {
      hidden: Boolean(layer.hidden),
      locked: Boolean(layer.locked),
    };
  });
  return {
    type: "layered-v1",
    activePage: 0,
    themeId: "original",
    pages: [
      {
        sourceImage: page.sourceImage || "",
        content,
        layers: layerState,
      },
    ],
  };
}

const styles = [
  {
    id: "study",
    name: "手写笔记考点卡片",
    desc: "适合资料、考试、知识点整理类图文",
    art: "linear-gradient(135deg,#fff8de,#fefefe 48%,#ffe4e9)",
  },
  {
    id: "quote",
    name: "纯色大字情绪卡",
    desc: "适合观点、吐槽、口播金句封面",
    art: "linear-gradient(135deg,#cdefff,#fff 54%,#eef7ff)",
  },
  {
    id: "photo",
    name: "真实生活照片",
    desc: "适合美食、穿搭、探店、日常记录",
    art: "linear-gradient(145deg,#e9f6e5,#fff 42%,#ffe8ed)",
  },
  {
    id: "compare",
    name: "对比图模板",
    desc: "适合前后变化、成绩对照、清单总结",
    art: "linear-gradient(135deg,#f1e8ff,#fff 46%,#dff7ed)",
  },
];

function builtInScenePreview(label, background, foreground = "#243142") {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="900" height="1200" viewBox="0 0 900 1200"><rect width="900" height="1200" fill="${background}"/><rect x="90" y="150" width="720" height="720" rx="36" fill="#ffffff" fill-opacity=".72"/><circle cx="450" cy="430" r="150" fill="${foreground}" fill-opacity=".16"/><path d="M220 760c70-170 390-170 460 0" fill="none" stroke="${foreground}" stroke-width="28" stroke-linecap="round" opacity=".55"/><text x="450" y="1010" text-anchor="middle" fill="${foreground}" font-size="42" font-family="Arial, sans-serif">${label}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// The built-in “真实场景” presets were removed on request. The AI生图 scene tab now
// lists only scene templates created through 场景模板分析 (state.customSceneImageTemplates).
const sceneImageTemplates = [];

const sampleImages = [
  mockImage("参考笔记", "链接采集", "#fff2f4", "#ff2442"),
  mockImage("图片配文", "上传素材", "#eef7ff", "#2f80ed"),
  mockImage("AI 生图", "原创封面", "#f0f8ea", "#1fb66a"),
  mockImage("插件采集", "页面导入", "#fff7e8", "#cf7a1a"),
];

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => Array.from(document.querySelectorAll(selector));

function uid(prefix = "id") {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function mockImage(title, subtitle, bg, accent) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="900" height="1200" viewBox="0 0 900 1200">
      <rect width="900" height="1200" fill="${bg}"/>
      <rect x="72" y="86" width="756" height="1028" rx="46" fill="#fffdf9" stroke="#eadfd8" stroke-width="4"/>
      <circle cx="704" cy="226" r="86" fill="${accent}" opacity=".16"/>
      <rect x="122" y="178" width="228" height="28" rx="14" fill="${accent}" opacity=".88"/>
      <text x="122" y="440" fill="#22201f" font-family="Arial, sans-serif" font-size="84" font-weight="800">${title}</text>
      <text x="122" y="536" fill="#22201f" font-family="Arial, sans-serif" font-size="60" font-weight="700">${subtitle}</text>
      <rect x="122" y="650" width="560" height="24" rx="12" fill="#d9d2cd"/>
      <rect x="122" y="710" width="430" height="24" rx="12" fill="#d9d2cd"/>
      <rect x="122" y="770" width="610" height="24" rx="12" fill="#d9d2cd"/>
      <text x="122" y="1014" fill="${accent}" font-family="Arial, sans-serif" font-size="36" font-weight="700">AI Xiaohongshu Workflow</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function loadState() {
  try {
    const workspaceKey = tenantStorageKey(storageKey);
    const localSaved = JSON.parse(localStorage.getItem(workspaceKey) || "{}");
    const sessionSaved = location.search.includes("mock-oidc=1")
      ? JSON.parse(sessionStorage.getItem(`${workspaceKey}:session`) || "{}")
      : {};
    const saved = {
      ...sessionSaved,
      ...localSaved,
      drafts: localSaved.drafts?.length ? localSaved.drafts : sessionSaved.drafts,
      publish: localSaved.publish?.length ? localSaved.publish : sessionSaved.publish,
      generatedImages: localSaved.generatedImages?.length ? localSaved.generatedImages : sessionSaved.generatedImages,
    };
    pendingBackendRecovery = readBackendRecoverySnapshot();
    const recoveryState = pendingBackendRecovery?.payload && typeof pendingBackendRecovery.payload === "object"
      ? pendingBackendRecovery.payload
      : {};
    const restoredDrafts = assignDraftTaskNumbers((recoveryState.drafts || saved.drafts || []).map(normalizeSavedDraft));
    const activeDraftId = recoveryState.activeDraftId ?? saved.activeDraftId;
    const activeDraft = restoredDrafts.find((draft) => draft.id === activeDraftId) || restoredDrafts[0] || null;
    state.drafts = restoredDrafts;
    writePublishItems(
      (recoveryState.publish || saved.publish || [])
        .map(normalizeSavedItem)
        .map((item) => restoreReplacedPublishImages(item, restoredDrafts))
        .map((item) => {
          const cleanItem = { ...item };
          delete cleanItem.auditEnforcement;
          delete cleanItem.auditBypassed;
          return cleanItem;
        }),
    );
    state.stateRevision = Number.isInteger(recoveryState.stateRevision) && recoveryState.stateRevision >= 0
      ? recoveryState.stateRevision
      : (Number.isInteger(saved.stateRevision) && saved.stateRevision >= 0 ? saved.stateRevision : 0);
    state.activeDraftId = activeDraft?.id || null;
    state.activeWorkflow = ["replica", "original", "tools"].includes(recoveryState.activeWorkflow || saved.activeWorkflow)
      ? (recoveryState.activeWorkflow || saved.activeWorkflow)
      : "replica";
    state.lastWorkflowSections = normalizeLastWorkflowSections(recoveryState.lastWorkflowSections || saved.lastWorkflowSections);
    state.generatedImages = (Array.isArray(recoveryState.generatedImages)
      ? recoveryState.generatedImages
      : (Array.isArray(saved.generatedImages) ? saved.generatedImages : []))
      .filter((item) => item?.src)
      .map((item) => ({ ...item, src: normalizeImageSrc(item.src) }));
    const savedOverrides = recoveryState.imageTemplateOverrides ?? saved.imageTemplateOverrides;
    state.imageTemplateOverrides = savedOverrides && typeof savedOverrides === "object" ? savedOverrides : {};
    state.customSceneImageTemplates = Array.isArray(recoveryState.customSceneImageTemplates)
      ? recoveryState.customSceneImageTemplates
      : (Array.isArray(saved.customSceneImageTemplates) ? saved.customSceneImageTemplates : []);
    // Keep the legacy restore assignment explicit; a revision-matched recovery
    // snapshot may then replace it with the newer browser state below.
    state.deletedSceneImageTemplateIds = Array.isArray(saved.deletedSceneImageTemplateIds)
      ? saved.deletedSceneImageTemplateIds
      : [];
    if (Array.isArray(recoveryState.deletedSceneImageTemplateIds)) {
      state.deletedSceneImageTemplateIds = recoveryState.deletedSceneImageTemplateIds;
    }
    state.knowledgeCollapsedFolderIds = Array.isArray(recoveryState.knowledgeCollapsedFolderIds)
      ? recoveryState.knowledgeCollapsedFolderIds
      : (Array.isArray(saved.knowledgeCollapsedFolderIds) ? saved.knowledgeCollapsedFolderIds : []);
    // 两个副本一起改：快照里没有 publishTasks 时必须置回 null（尚未水合），
    // 不能把上一个组织留下的任务记录继续留在内存里——那正是「副本没跟上」的分裂。
    writePublishTasks(Array.isArray(recoveryState.publishTasks) ? recoveryState.publishTasks : null);
  } catch {
    state.drafts = [];
    writePublishItems([]);
    writePublishTasks(null);
  }
}

function cloneDefaultAiConfig() {
  return JSON.parse(JSON.stringify(defaultAiConfig));
}

function normalizeTemplateAnalysisConfig(value = {}) {
  const concurrency = Number(value?.concurrency);
  return {
    concurrency: Number.isInteger(concurrency) ? Math.max(1, Math.min(concurrency, 3)) : 1,
  };
}

function migrateLegacySystemImageTemplate(templates, defaults) {
  const defaultImage = defaults.templates.find((template) => template.id === "system-image");
  if (!defaultImage) return templates;
  const legacySets = [
    {
      titlePrompt: "根据图片内容和用户描述，生成 1 条小红书标题。标题要具体、口语化、有点击欲，40 字以内，只输出标题。",
      bodyPrompt: "根据图片内容和用户描述，写一篇小红书图文正文。先描述画面和场景，再提炼可收藏的重点，最后给出自然互动引导。不要编造图片里看不到的信息。只输出正文。",
      tagsPrompt: "根据图片内容生成 4-8 个小红书标签，覆盖场景、风格、主题和人群。只输出标签，格式如：#标签1 #标签2。",
      imagePrompt: "生成一张适合小红书图文发布的原创图片。画面主体清晰，构图干净，真实自然光，保留标题排版空间，整体质感精致但不过度商业化。",
    },
    {
      titlePrompt: "请仔细观察输入图片，并结合用户补充描述，提炼画面中明确可见的信息，生成 1 条适合小红书的标题。先判断主题、场景和主体，再突出一个最有价值或最吸引人的点。不得臆测图片看不到的品牌、价格、地点、人物身份、功效、数据或经历；不把画面文字扩写成未经证实的事实。标题具体、口语自然、有点击欲，40 字以内，不加引号、编号或解释，只输出标题。",
      bodyPrompt: "请根据输入图片和用户补充描述，写一篇可直接发布的小红书图文正文。先用 1-2 句准确描述画面主体与场景，再提炼 3-5 条对读者有用的信息或观察，最后用自然的问题引导互动。只使用图片明确可见和用户明确提供的内容；看不清或无法确认的文字、品牌、价格、地点、时间、人物身份、功效和数据一律不补写。避免流水账、夸张广告和模板化开场；分段留白，语气像真实用户分享。信息不足时宁可简洁，也不要硬凑细节。只输出正文。",
      tagsPrompt: "根据图片中明确可见的主题、场景、对象和用户描述，生成 4-8 个精准的小红书标签。优先覆盖内容主题、场景、风格、对象和目标人群；不得使用图片无法确认的品牌、地点、功效、价格或无关热词。标签彼此不重复，每个 2-12 个字，只输出空格分隔的标签，格式如：#标签1 #标签2。",
      imagePrompt: "以输入图片和已生成文案为内容参考，生成 1 张适合小红书发布的原创竖版配图。提炼原图主体、场景和氛围，但不要复制水印、平台界面、品牌标识或无关文字；主体清晰，构图简洁，使用自然光和真实材质，预留标题排版空间。除非文案明确要求，画面不要添加大段文字、数字、二维码或 logo，也不要虚构可识别的事实信息。",
    },
  ];
  return templates.map((template) => {
    if (template.id !== "system-image") return template;
    const isLegacy = legacySets.some((legacy) => (
      ["titlePrompt", "bodyPrompt", "tagsPrompt", "imagePrompt"]
        .every((key) => String(template[key] || "") === legacy[key])
    ));
    return isLegacy ? { ...template, ...defaultImage } : template;
  });
}

function migrateLegacySystemRewritePrompt(templates, defaults) {
  const fallback = defaults.templates.find((item) => item.id === "system-link");
  const nextBodyPrompt = String(fallback?.promptSets?.rewrite?.bodyPrompt || fallback?.bodyPrompt || "");
  if (!nextBodyPrompt) return templates;
  const legacyMarker = "在原始标题和正文基础上，改写一篇小红书笔记正文";
  return templates.map((template) => {
    if (template.id !== "system-link") return template;
    const rewrite = template.promptSets?.rewrite || {};
    const isLegacy = String(template.bodyPrompt || "").includes(legacyMarker)
      || String(rewrite.bodyPrompt || "").includes(legacyMarker);
    if (!isLegacy) return template;
    return {
      ...template,
      bodyPrompt: nextBodyPrompt,
      promptSets: { ...(template.promptSets || {}), rewrite: { ...rewrite, bodyPrompt: nextBodyPrompt } },
    };
  });
}

function loadAiConfig() {
  const defaults = cloneDefaultAiConfig();
  try {
    const saved = JSON.parse(localStorage.getItem(tenantStorageKey(aiConfigStorageKey)) || "{}");
    const sourceTemplates = saved.templates?.length ? saved.templates : defaults.templates;
    state.aiConfig = {
      ...defaults,
      ...saved,
      text: {
        ...defaults.text,
        model: saved.text?.model || defaults.text.model,
        models: Array.isArray(saved.text?.models) ? saved.text.models : defaults.text.models,
      },
      image: {
        ...defaults.image,
        model: saved.image?.model || defaults.image.model,
        models: Array.isArray(saved.image?.models) ? saved.image.models : defaults.image.models,
      },
      knowledge: {
        ...defaults.knowledge,
        ...(saved.knowledge || {}),
        dify: { ...defaults.knowledge.dify, ...(saved.knowledge?.dify || {}), apiKey: "" },
      },
      templateAnalysis: normalizeTemplateAnalysisConfig(saved.templateAnalysis),
      templates: sourceTemplates.map((template, index) => {
        const merged = {
          ...defaults.templates[Math.min(index, defaults.templates.length - 1)],
          ...template,
        };
        if (!template.promptSets) {
          const mode = template.mode === "image" ? "image" : "rewrite";
          merged.promptSets = {
            ...(merged.promptSets || {}),
            [mode]: {
              titlePrompt: template.titlePrompt ?? merged.promptSets?.[mode]?.titlePrompt ?? "",
              bodyPrompt: template.bodyPrompt ?? merged.promptSets?.[mode]?.bodyPrompt ?? "",
              tagsPrompt: template.tagsPrompt ?? merged.promptSets?.[mode]?.tagsPrompt ?? "",
              imagePrompt: template.imagePrompt ?? merged.promptSets?.[mode]?.imagePrompt ?? "",
            },
          };
        }
        return merged;
      }),
    };
    state.aiConfig.templates = migrateLegacySystemImageTemplate(state.aiConfig.templates, defaults);
    state.aiConfig.templates = migrateLegacySystemRewritePrompt(state.aiConfig.templates, defaults);
  } catch {
    state.aiConfig = defaults;
  }
  if (!currentAiTemplate()) {
    state.aiConfig.activeTemplateId = state.aiConfig.templates[0]?.id || "system-link";
  }
  if (!state.aiConfig.promptMode) {
    state.aiConfig.promptMode = currentAiTemplate()?.mode || "rewrite";
  }
  saveAiConfig();
}

function clientSafeAiConfig() {
  const providerConfig = (type) => {
    const current = state.aiConfig?.[type] || {};
    const defaults = defaultAiConfig[type] || {};
    return {
      apiUrl: current.apiUrl || defaults.apiUrl || "",
      model: current.model || defaults.model || "",
      models: Array.isArray(current.models) ? current.models : [],
      hasApiKey: Boolean(current.hasApiKey),
      maskedApiKey: current.maskedApiKey || "",
    };
  };
  return {
    activeTemplateId: state.aiConfig.activeTemplateId,
    promptMode: state.aiConfig.promptMode,
    text: providerConfig("text"),
    image: providerConfig("image"),
    knowledge: {
      enabled: state.aiConfig.knowledge?.enabled === true,
      provider: state.aiConfig.knowledge?.provider || "dify",
      minScore: Number(state.aiConfig.knowledge?.minScore ?? 0.55),
      dify: {
        apiUrl: state.aiConfig.knowledge?.dify?.apiUrl || "",
        datasetId: state.aiConfig.knowledge?.dify?.datasetId || "",
        hasApiKey: Boolean(state.aiConfig.knowledge?.dify?.hasApiKey),
        maskedApiKey: state.aiConfig.knowledge?.dify?.maskedApiKey || "",
        insecure: state.aiConfig.knowledge?.dify?.insecure === true,
      },
    },
    templateAnalysis: normalizeTemplateAnalysisConfig(state.aiConfig.templateAnalysis),
    watermark: {
      enabled: state.aiConfig.watermark?.enabled !== false,
      visible: state.aiConfig.watermark?.visible !== false,
      metadata: state.aiConfig.watermark?.metadata !== false,
      visibleBackend: state.aiConfig.watermark?.visibleBackend || "cv2",
      sensitivity: state.aiConfig.watermark?.sensitivity || "auto",
      failOpen: state.aiConfig.watermark?.failOpen !== false,
    },
    templates: state.aiConfig.templates,
  };
}

function mergeAiProviderConfig(current, incoming) {
  const next = { ...(current || {}) };
  const source = incoming || {};
  if (source.apiUrl?.trim()) next.apiUrl = source.apiUrl.trim();
  if (source.model?.trim()) next.model = source.model.trim();
  if (Array.isArray(source.models) && source.models.length) next.models = source.models;
  if (typeof source.hasApiKey === "boolean") {
    next.hasApiKey = source.hasApiKey;
    next.maskedApiKey = source.maskedApiKey || "";
    next.apiKey = "";
  }
  return next;
}

function debounce(callback, delay = 200) {
  let timer = 0;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => callback(...args), delay);
  };
}

function refreshIcons() {
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
  // 每次渲染后统一按能力清单收敛入口（只加 .hidden，不删 DOM）。
  if (typeof applyCapabilityVisibility === "function") applyCapabilityVisibility();
}

function mergeKnowledgeConfig(current, incoming) {
  const next = { ...(defaultAiConfig.knowledge || {}), ...(current || {}), ...(incoming || {}) };
  const sourceDify = incoming?.dify || {};
  next.dify = {
    ...(defaultAiConfig.knowledge?.dify || {}),
    ...(current?.dify || {}),
    ...sourceDify,
    apiKey: "",
  };
  return next;
}

function saveAiConfig() {
  localStorage.setItem(tenantStorageKey(aiConfigStorageKey), JSON.stringify(clientSafeAiConfig()));
}

async function loadAiConfigFromBackend() {
  try {
    const response = await backendFetch("/api/config");
    const data = await response.json();
    /* 能力位跟这次读一起回来——它是登录后第一个必读的接口，入口（团队治理的知识库连接、
       控制台入口）靠它拿到判据，不必等"这个会话打开过管理台"（P58/C36）。放在 config 判定
       之前：能力位读不读得到，跟配置能不能读是两件事。 */
    if (typeof applyConsoleCapabilities === "function") applyConsoleCapabilities(data);
    if (!response.ok || data.ok === false || !data.config) return;
    state.aiConfig.text = mergeAiProviderConfig(state.aiConfig.text, data.config.text);
    state.aiConfig.image = mergeAiProviderConfig(state.aiConfig.image, data.config.image);
    state.aiConfig.knowledge = mergeKnowledgeConfig(state.aiConfig.knowledge, data.config.knowledge);
    state.aiConfig.templateAnalysis = normalizeTemplateAnalysisConfig(data.config.templateAnalysis);
    state.aiConfig.watermark = { ...state.aiConfig.watermark, ...(data.config.watermark || {}) };
    saveAiConfig();
  } catch {
    // 后端未启动时不读取浏览器里遗留的 API Key。
  }
}

function currentAiTemplate() {
  return state.aiConfig?.templates.find((item) => item.id === state.aiConfig.activeTemplateId) || null;
}

function normalizeSavedItem(item) {
  return {
    ...item,
    images: (item.images || []).map(normalizeImageSrc).filter(Boolean),
  };
}

function restoreReplacedDraftImages(draft) {
  const images = (draft?.images || []).filter(Boolean);
  if (!images.length || !images.every((src) => sampleImages.includes(src))) return draft;
  const plannedImages = draftPlannedPageEntries(draft).map((entry) => entry.src).filter(Boolean);
  if (!plannedImages.length || plannedImages.every((src) => sampleImages.includes(src))) return draft;
  const restored = { ...draft, images: plannedImages };
  const renderedImages = (draft.templateRenderImages || []).filter(Boolean);
  if (renderedImages.length && renderedImages.every((src) => sampleImages.includes(src))) {
    restored.templateRenderImages = [...plannedImages];
  }
  return restored;
}

function restoreReplacedPublishImages(item, drafts) {
  const images = (item?.images || []).filter(Boolean);
  if (!images.length || !images.every((src) => sampleImages.includes(src))) return item;
  const draft = (drafts || []).find((candidate) => candidate.id === item.draftId);
  const restoredImages = (draft?.templateRenderImages?.length ? draft.templateRenderImages : draft?.images || [])
    .filter((src) => src && !sampleImages.includes(src));
  return restoredImages.length ? { ...item, images: restoredImages } : item;
}

const fabricatedImportedTags = new Set([
  "#xiaohongshu #ai #content",
  "#小红书采集 #ai改写 #内容工作流",
]);
const importedHashtagChainPattern = /(^|[\s，,。.!！?？;；、|])((?:#\s*[^#\s，,。.!！?？;；、|]+)+)/gm;
const importedHashtagTokenPattern = /#\s*([^#\s，,。.!！?？;；、|]+)/g;

function normalizeImportedNoteFields(body = "", tags = "") {
  const collectedTags = [];
  const appendTag = (value) => {
    const tag = String(value || "")
      .replace(/\s*\[话题\]\s*/gi, "")
      .trim()
      .replace(/^[#\s]+|[#\s，,。.!！?？;；、|]+$/g, "");
    if (tag && !collectedTags.includes(tag) && collectedTags.length < 30) collectedTags.push(tag);
  };
  const stripTopics = (value) => String(value || "")
    .replace(/#\s*([^#\n]+?)\s*\[话题\]\s*#/gi, (_match, tag) => {
      appendTag(tag);
      return " ";
    })
    .replace(importedHashtagChainPattern, (_match, prefix, chain) => {
      for (const match of chain.matchAll(importedHashtagTokenPattern)) appendTag(match[1]);
      return prefix;
    });

  const rawTags = String(tags || "").replace(/\s+/g, " ").trim();
  if (rawTags && !fabricatedImportedTags.has(rawTags.toLowerCase())) {
    const previousCount = collectedTags.length;
    stripTopics(rawTags);
    if (collectedTags.length === previousCount) appendTag(rawTags);
  }
  const cleanBody = stripTopics(body)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return {
    body: cleanBody,
    tags: collectedTags.map((tag) => `#${tag}`).join(" "),
  };
}

const importedPageShellTitles = new Set([
  "小红书",
  "小红书 - 你的生活指南",
  "小红书链接导入笔记",
  "想了解些什么？",
  "想了解些什么?",
  "做旅行攻略",
  "答疑解惑",
  "搜索小红书",
]);

function normalizeImportedNoteTitle(title = "", body = "") {
  const cleanTitle = String(title || "").trim();
  if (importedPageShellTitles.has(cleanTitle)) return "";
  const titleCopy = normalizeImportedNoteFields(cleanTitle, "").body.replace(/[\s，,。.!！?？;；、|]+/g, "").toLowerCase();
  const bodyCopy = normalizeImportedNoteFields(body, "").body.replace(/[\s，,。.!！?？;；、|]+/g, "").toLowerCase();
  const titleLooksLikeBody = cleanTitle.length > 60
    || cleanTitle.includes("\n")
    || /(^|[\s，,。.!！?？;；、|])(?:#\s*[^#\s，,。.!！?？;；、|]+)+/m.test(cleanTitle);
  return titleLooksLikeBody && titleCopy && titleCopy === bodyCopy ? "" : cleanTitle;
}

function reconcileSavedImportedNoteCopy(title = "", body = "") {
  const cleanTitle = String(title || "").trim();
  const cleanBody = String(body || "").trim();
  const shellTitle = importedPageShellTitles.has(cleanTitle);
  if (shellTitle && cleanBody && cleanBody.length <= 60 && !cleanBody.includes("\n")) {
    return { title: cleanBody, body: "" };
  }
  return { title: normalizeImportedNoteTitle(cleanTitle, cleanBody), body: cleanBody };
}

function normalizeSavedDraft(item) {
  const normalized = restoreReplacedDraftImages(normalizeSavedItem(item));
  normalized.imageGenerationFailedLabels = Array.isArray(normalized.imageGenerationFailedLabels)
    ? normalized.imageGenerationFailedLabels.filter((label) => typeof label === "string" && label.trim())
    : [];
  normalized.imageGenerationFailedJobKeys = Array.isArray(normalized.imageGenerationFailedJobKeys)
    ? normalized.imageGenerationFailedJobKeys.filter((key) => typeof key === "string" && key.trim())
    : [];
  if (!normalized.knowledgeChannel) {
    normalized.knowledgeChannel = normalized.knowledge?.decision === "grounded" ? "reference" : "pure-ai";
  }

  delete normalized.templateRenderQaReports;
  delete normalized.knowledgeSetQualityAudit;
  if (Array.isArray(normalized.templateRenderImageStates)) {
    normalized.templateRenderImageStates = normalized.templateRenderImageStates.map((state) => (
      state === "qa-failed" ? "generated" : state
    ));
  }
  const candidatePlan = normalized.executionPlan;
  // 契约三值只有 `js/core/api.js` 一份定义（服务端同源于 `server/pipeline_contracts.py`）：
  // 这里曾把 6 / "ExecutionBundleV6" / "knowledge-template-v2" 又写了一遍字面量——服务端升版
  // 而这里没跟，合法的执行计划会被判定"不是当前契约"而**静默重建**，表现是用户改过的内容被丢掉。
  const executionPlan = Number(candidatePlan?.version || 0) === EXECUTION_BUNDLE_VERSION
    && candidatePlan?.bundleType === EXECUTION_BUNDLE_TYPE
    && candidatePlan?.pipelineContract === PIPELINE_CONTRACT
    ? (() => {
        const cleanPlan = { ...candidatePlan };
        delete cleanPlan.knowledgeSetQualityAudit;
        delete cleanPlan.auditEnforcement;
        delete cleanPlan.auditBypassed;
        if (cleanPlan.lockedCopySet && typeof cleanPlan.lockedCopySet === "object") {
          cleanPlan.lockedCopySet = { ...cleanPlan.lockedCopySet };
          delete cleanPlan.lockedCopySet.auditStatus;
          delete cleanPlan.lockedCopySet.auditEnforcement;
          delete cleanPlan.lockedCopySet.auditBypassed;
          if (Array.isArray(cleanPlan.lockedCopySet.series)) {
            cleanPlan.lockedCopySet.series = cleanPlan.lockedCopySet.series.map((series) => {
              const cleanSeries = { ...series };
              delete cleanSeries.audit;
              delete cleanSeries.auditStatus;
              delete cleanSeries.auditEnforcement;
              delete cleanSeries.auditBypassed;
              return cleanSeries;
            });
          }
        }
        if (Array.isArray(cleanPlan.seriesExecutions)) {
          cleanPlan.seriesExecutions = cleanPlan.seriesExecutions.map((series) => {
            const cleanSeries = { ...series };
            delete cleanSeries.audit;
            delete cleanSeries.auditStatus;
            delete cleanSeries.auditEnforcement;
            delete cleanSeries.auditBypassed;
            return cleanSeries;
          });
        }
        return cleanPlan;
      })()
    : undefined;
  const titlePrefixes = {
    link: "这篇参考笔记可以这样改",
    upload: "图片已经够好看了，文案这样写",
    image: "AI 生成图也能做成爆款笔记",
    plugin: "从小红书采集来的灵感这样重写",
    blank: "新笔记草稿",
  };
  const titlePrefix = titlePrefixes[normalized.source] || "小红书笔记灵感";
  const title = String(normalized.title || "").trim();
  const importedFields = ["link", "plugin"].includes(normalized.source)
    ? normalizeImportedNoteFields(normalized.body, normalized.tags)
    : { body: normalized.body || "", tags: normalized.tags || "" };
  const body = String(importedFields.body || "").trim();
  const tags = String(importedFields.tags || "").trim();
  const restoredTitle = title.startsWith(`${titlePrefix}｜`) || title.startsWith(`${titlePrefix} |`) ? "" : normalized.title || "";
  const restoredCopy = ["link", "plugin"].includes(normalized.source)
    ? reconcileSavedImportedNoteCopy(restoredTitle, importedFields.body)
    : { title: restoredTitle, body: importedFields.body };
  return {
    ...normalized,
    executionPlan,
    title: restoredCopy.title,
    body: body.startsWith("我把素材重新整理成更适合小红书阅读的版本：") && body.includes("原始素材关键词：") ? "" : restoredCopy.body,
    tags: tags === "#小红书运营 #AI改写 #图文笔记 #内容工作流" ? "" : importedFields.tags,
  };
}

function normalizeImageSrc(src) {
  if (!src || typeof src !== "string") return src;
  if (
    src.startsWith("data:") ||
    src.startsWith("blob:") ||
    src.startsWith(`${backendBaseUrl}/api/proxy-image`) ||
    src.startsWith(`${backendBaseUrl}/api/cached-image`)
  ) {
    return src;
  }
  if (/^https?:\/\/.+xhscdn\.com/i.test(src) || /^https?:\/\/sns-/i.test(src)) {
    return `${backendBaseUrl}/api/proxy-image?url=${encodeURIComponent(src)}`;
  }
  return src;
}

function normalizeImages(images) {
  return (Array.isArray(images) ? images : []).map(normalizeImageSrc).filter(Boolean);
}

function imageUrlWithCacheVersion(src, version = Date.now()) {
  if (!src) return src;
  return `${src}${src.includes("?") ? "&" : "?"}v=${version}`;
}

function replaceMarkupPreservingImages(container, markup) {
  if (!container) return;
  const reusableImages = new Map();
  container.querySelectorAll("img[src]").forEach((image) => {
    const src = image.getAttribute("src") || "";
    if (!src) return;
    const matches = reusableImages.get(src) || [];
    matches.push(image);
    reusableImages.set(src, matches);
  });

  const template = document.createElement("template");
  template.innerHTML = markup;
  template.content.querySelectorAll("img[src]").forEach((nextImage) => {
    const src = nextImage.getAttribute("src") || "";
    const matches = reusableImages.get(src);
    const currentImage = matches?.shift();
    if (!currentImage) return;
    [...currentImage.attributes].forEach((attribute) => {
      if (!nextImage.hasAttribute(attribute.name)) currentImage.removeAttribute(attribute.name);
    });
    [...nextImage.attributes].forEach((attribute) => {
      if (currentImage.getAttribute(attribute.name) !== attribute.value) {
        currentImage.setAttribute(attribute.name, attribute.value);
      }
    });
    nextImage.replaceWith(currentImage);
  });
  container.replaceChildren(template.content);
}

// Protected images (cached-image / proxy-image / template assets) require tenant
// credentials, which an <img> element cannot send. Fetch them through
// backendFetch (which attaches the Authorization header), turn the bytes into
// an object URL, and swap it onto the <img>. A single MutationObserver drives
// this so every render path is covered without touching each template.
const PROTECTED_IMAGE_PATH_PREFIXES = [
  "/api/cached-image/",
  "/api/template-asset/",
  "/api/template-output/",
  "/api/proxy-image",
];

function protectedImageRequestPath(src) {
  if (!src || typeof src !== "string") return "";
  if (src.startsWith("data:") || src.startsWith("blob:")) return "";
  let url;
  try {
    url = new URL(src, backendBaseUrl);
  } catch (_error) {
    return "";
  }
  if (url.origin !== new URL(backendBaseUrl).origin) return "";
  const path = url.pathname || "";
  const isProtected = PROTECTED_IMAGE_PATH_PREFIXES.some(
    (prefix) => path === prefix.replace(/\/$/, "") || path.startsWith(prefix),
  );
  if (!isProtected) return "";
  return `${url.pathname}${url.search}`;
}

// 等待字节期间留在 src 上的占位图。受保护地址需要 Bearer 鉴权，而 <img> 发不出这个头：
// 把原地址留在 src 上，浏览器会先发一次必然 401 的请求、首屏先亮一块破图，直到水合的
// 字节到达才被换掉（P86 实测：首屏 img[data-protected-image] naturalWidth=0 + 真实 401）。
// 占位用内联透明 SVG —— 不发请求，也不会被渲染成"加载失败"图标。
const PROTECTED_IMAGE_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E";
// 水合失败后的自愈重试间隔（毫秒）。令牌尚未就绪、限流、瞬时 401/5xx 都会在几百毫秒后
// 恢复；此前只有"用户再交互一次触发重渲染"才会重试，于是破图会一直挂着（P86）。
const PROTECTED_IMAGE_RETRY_DELAYS = [400, 1200, 3600];

const protectedImageBlobCache = new Map();
// Resolved blobs, kept so a re-rendered <img> can be hydrated synchronously.
// Waiting for the promise (even a cache hit) paints an empty box first, which
// is what made the cover/inner library previews flicker on every re-render.
const protectedImageBlobReady = new Map();
// requestPath -> object URL, reused by every element that shows the asset.
const protectedImageSharedObjectUrls = new Map();
const protectedImageObjectUrls = new WeakMap();
let protectedImageObserver = null;

function loadProtectedImageBlob(requestPath) {
  const cached = protectedImageBlobCache.get(requestPath);
  if (cached) return cached;
  const promise = backendFetch(requestPath)
    .then((response) => {
      // 受保护图片的读取同样走错误边界：401（没登录/登录失效）与 403 的处置要和
      // 其它请求一致，别在这里另造一句 `image 401` 这种没人看得懂的话。
      if (!response.ok) throw backendError({}, response.status, response);
      return response.blob();
    })
    .catch((error) => {
      protectedImageBlobCache.delete(requestPath);
      throw error;
    });
  protectedImageBlobCache.set(requestPath, promise);
  return promise;
}

function releaseProtectedImage(image) {
  // The object URL is shared per asset, so removing one element must not revoke
  // it for the others (or for the next render of the same preview).
  protectedImageObjectUrls.delete(image);
}

function protectedImageObjectUrl(requestPath, blob) {
  // One object URL per asset: a re-rendered <img> then points at a URL the
  // browser has already decoded, so the preview paints immediately instead of
  // flashing through an empty frame on every library re-render.
  const existing = protectedImageSharedObjectUrls.get(requestPath);
  if (existing) return existing;
  const created = URL.createObjectURL(blob);
  protectedImageSharedObjectUrls.set(requestPath, created);
  return created;
}

function applyProtectedImageBlob(image, requestPath, blob) {
  if (!image.isConnected) return;
  if (image.getAttribute("data-protected-image") !== requestPath) return;
  const objectUrl = protectedImageObjectUrl(requestPath, blob);
  protectedImageObjectUrls.set(image, objectUrl);
  image.src = objectUrl;
}

function hydrateProtectedImage(image) {
  if (!(image instanceof HTMLImageElement)) return;
  const currentSrc = image.getAttribute("src") || "";
  const requestPath = protectedImageRequestPath(currentSrc);
  if (!requestPath) return;
  // 短路条件必须同时确认元素当前显示的就是该资源的水合结果：持久化的 img（例如图片
  // 查看器的 #imageViewerImg）在关闭/重新渲染时会被重新赋回受保护原路径，此时若只看
  // 属性就跳过水合，<img> 会直接去请求需要鉴权的地址并 401，表现为「点开破图、切走再
  // 切回才正常」。
  const hydratedUrl = protectedImageObjectUrls.get(image);
  if (
    image.getAttribute("data-protected-image") === requestPath
    && hydratedUrl
    && currentSrc === hydratedUrl
  ) return;
  image.setAttribute("data-protected-image", requestPath);
  image.removeAttribute("data-protected-image-error");
  const readyBlob = protectedImageBlobReady.get(requestPath);
  if (readyBlob) {
    // Already fetched in this session: swap it in synchronously, before the
    // browser paints, so a re-created element never flashes empty.
    applyProtectedImageBlob(image, requestPath, readyBlob);
    return;
  }
  // 字节还没到：必须先把需要鉴权的地址从 src 上摘掉，否则浏览器会拿它去请求并 401，
  // 用户看到的是破图而不是"加载中"。
  if (currentSrc !== PROTECTED_IMAGE_PLACEHOLDER) {
    image.setAttribute("src", PROTECTED_IMAGE_PLACEHOLDER);
  }
  fetchProtectedImageInto(image, requestPath, 0);
}

function fetchProtectedImageInto(image, requestPath, attempt) {
  loadProtectedImageBlob(requestPath)
    .then((blob) => {
      protectedImageBlobReady.set(requestPath, blob);
      applyProtectedImageBlob(image, requestPath, blob);
    })
    .catch(() => {
      if (!image.isConnected) return;
      if (image.getAttribute("data-protected-image") !== requestPath) return;
      const delay = PROTECTED_IMAGE_RETRY_DELAYS[attempt];
      if (delay === undefined) {
        image.setAttribute("data-protected-image-error", "1");
        return;
      }
      // 失败不许就地躺平：令牌刚建立、网关限流、上游抖动都会自愈，重试几次即可，
      // 不必等用户再点一下才恢复。
      setTimeout(() => {
        if (!image.isConnected) return;
        if (image.getAttribute("data-protected-image") !== requestPath) return;
        fetchProtectedImageInto(image, requestPath, attempt + 1);
      }, delay);
    });
}

// 元素当前真正代表的地址：水合完成后是 blob URL，尚未完成时是它代表的受保护原路径。
// 需要"把这张图交给别处显示"（打开大图查看器等）时要走这里，别直接读 src——等待期间
// src 上放的是占位图。
function protectedImageDisplaySource(image) {
  if (!(image instanceof HTMLImageElement)) return "";
  return (
    protectedImageObjectUrls.get(image)
    || image.getAttribute("data-protected-image")
    || image.getAttribute("src")
    || ""
  );
}

function hydrateProtectedImages(root) {
  if (!root) return;
  if (root instanceof HTMLImageElement) {
    hydrateProtectedImage(root);
    return;
  }
  root.querySelectorAll?.("img").forEach(hydrateProtectedImage);
}

function releaseProtectedImageTree(node) {
  if (node instanceof HTMLImageElement) {
    if (!node.isConnected) releaseProtectedImage(node);
    return;
  }
  if (!(node instanceof Element)) return;
  node.querySelectorAll("img").forEach((image) => {
    if (!image.isConnected) releaseProtectedImage(image);
  });
}

function startProtectedImageHydration() {
  hydrateProtectedImages(document);
  if (protectedImageObserver) return;
  protectedImageObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        if (mutation.target instanceof HTMLImageElement) hydrateProtectedImage(mutation.target);
        continue;
      }
      mutation.removedNodes.forEach(releaseProtectedImageTree);
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLImageElement) hydrateProtectedImage(node);
        else if (node instanceof Element) hydrateProtectedImages(node);
      });
    }
  });
  protectedImageObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["src"],
  });
}

function assignDraftTaskNumbers(drafts) {
  const used = new Set(
    drafts
      .map((draft) => Number(draft.taskNumber || 0))
      .filter((value) => Number.isInteger(value) && value > 0),
  );
  let next = 1;
  [...drafts].reverse().forEach((draft) => {
    if (Number.isInteger(Number(draft.taskNumber)) && Number(draft.taskNumber) > 0) return;
    while (used.has(next)) next += 1;
    draft.taskNumber = next;
    used.add(next);
  });
  return drafts;
}

function nextDraftTaskNumber() {
  return Math.max(...state.drafts.map((draft) => Number(draft.taskNumber || 0)), 0) + 1;
}

function draftForPersistence(draft) {
  const copy = normalizeSavedItem(JSON.parse(JSON.stringify(draft || {})));
  const localReferenceDraft = copy.knowledge?.mode === "local";
  if (localReferenceDraft) {
    // Local uploads belong to the current task session and cannot survive a backend restart.
    delete copy.knowledge;
    delete copy.sourceCorpusId;
    delete copy.knowledgeBasis;
    if (copy.executionPlan && typeof copy.executionPlan === "object") {
      delete copy.executionPlan.sourceCorpusId;
    }
    if (copy.viralBrief && typeof copy.viralBrief === "object") {
      delete copy.viralBrief.sourceCorpusId;
      delete copy.viralBrief.knowledgeBasis;
    }
  }
  // 派生数据一律不进 state：服务端会按 selection/corpusId 自己重建
  // （library_compose_context 从模板库取整份模板、knowledge_basis_from_usage 自己抽知识），
  // 模板快照也从 /api/viral/libraries 重新水合（hydrateViralDraftSnapshots，仅当
  // draft.librarySnapshots 缺失时触发）。这些字段是"把整份模板复制进每个内页条目、把知识
  // 依据与四份快照整包存进草稿"的元凶：一条 18 页 + 长资料的草稿能被撑到几 MB，超过
  // 单条上限 MAX_TENANT_STATE_OBJECT_BYTES(2MB) 后，整包 /api/state 被 413 拒掉，
  // 整个工作区的自动保存与发布全部瘫痪。
  delete copy.knowledgeBasis;
  if (copy.viralBrief && typeof copy.viralBrief === "object") {
    delete copy.viralBrief.knowledgeBasis;
    delete copy.viralBrief.innerPageEntries;
    // 整份模板对象（含 aiImage 174KB + templateContract 74KB）不进草稿：innerTemplateId/
    // coverTemplateId 已单独存，hydrateViralDraftSnapshots 会从 /api/viral/libraries 重新
    // 水合。一条 18 页草稿光内页模板就能占 250KB，是超 MAX_TENANT_STATE_OBJECT_BYTES
    // 的第二元凶。
    delete copy.viralBrief.innerTemplate;
    delete copy.viralBrief.coverTemplate;
    delete copy.viralBrief.sourceCorpus;
  }
  if (copy.executionPlan && typeof copy.executionPlan === "object") {
    // seriesExecutions[0] 里的 pageScript/pageTasks/lockedPageCopies/generatedContent
    // 与 executionPlan 顶层同名字段是同一份数据（executionPlanForSeries 同时写两处），
    // 生成完成后顶层字段已够用，seriesExecutions 只在生成期 linkedSeriesData 里用。
    // 18 页草稿里这条占 ~286KB，剥掉不影响刷新后编辑/重生成。
    delete copy.executionPlan.seriesExecutions;
  }
  // 注意：viralBrief.topicPackage 必须保留——knowledgeTopicPackageForDraft
  // （knowledge/local-files.js）读它的 sourceDocumentIds/sourceEvidence/keywords 等来拼
  // 知识检索词，这些不在 plain brief 标量里，剥掉会让未搭计划的云端草稿在刷新后检索退化。
  delete copy.librarySnapshots;
  return copy;
}

function backendStateForWrite() {
  const payload = {
    drafts: state.drafts.map(draftForPersistence),
    publish: state.publish,
    stateRevision: state.stateRevision,
    activeDraftId: state.activeDraftId,
    activeWorkflow: state.activeWorkflow,
    imageTemplateOverrides: state.imageTemplateOverrides,
    customSceneImageTemplates: state.customSceneImageTemplates,
    deletedSceneImageTemplateIds: state.deletedSceneImageTemplateIds,
    knowledgeCollapsedFolderIds: state.knowledgeCollapsedFolderIds,
  };
  // Do not send an empty value before the initial backend hydration finishes.
  // Older workstations interpret an explicit empty list as a deletion.
  if (Array.isArray(state.publishTasks)) payload.publishTasks = state.publishTasks;
  return payload;
}

function generatedImagesStateForWrite() {
  return { generatedImages: state.generatedImages };
}

function browserStateForWrite() {
  return {
    storageVersion: STATE_STORAGE_VERSION,
    activeDraftId: state.activeDraftId,
    activeWorkflow: state.activeWorkflow,
    lastWorkflowSections: state.lastWorkflowSections,
    knowledgeCollapsedFolderIds: state.knowledgeCollapsedFolderIds,
  };
}

function saveBrowserState() {
  const serialized = JSON.stringify(browserStateForWrite());
  const workspaceKey = tenantStorageKey(storageKey);
  try {
    localStorage.setItem(workspaceKey, serialized);
  } catch (error) {
    if (error?.name !== "QuotaExceededError" && error?.name !== "NS_ERROR_DOM_QUOTA_REACHED") throw error;
    localStorage.removeItem(workspaceKey);
    localStorage.setItem(workspaceKey, serialized);
  }
  if (location.search.includes("mock-oidc=1")) {
    try {
      sessionStorage.setItem(`${workspaceKey}:session`, JSON.stringify(sessionStateForWrite()));
    } catch {
      // Session quota failures should not block the canonical backend write.
    }
  }
}

let workspaceTenantScope = "";

async function switchWorkspaceTenantScope() {
  // Called once authentication settles and on every organization switch: the
  // workspace cache must follow the active tenant, not the browser.
  const scope = activeTenantStorageScope();
  if (scope === workspaceTenantScope) return false;
  // 换租户=换一整套数据：上一个组织留下的恢复快照不能跨过来（P97）。
  // 必须在改 workspaceTenantScope 之前清上一个桶——clearBackendRecoverySnapshot() 用的是
  // 当前 scope，等 scope 换完再清就是清新桶了。
  const outgoingScope = workspaceTenantScope;
  pendingBackendRecovery = null;
  if (outgoingScope) {
    try {
      sessionStorage.removeItem(`${stateRecoveryStorageKey}::${outgoingScope}`);
    } catch {
      // Storage can be unavailable in hardened browser modes.
    }
  }
  // 从这一刻到服务端水合对齐 revision 之前不许 POST /api/state：请求头里的组织令牌
  // 与 body 里的记录可能分属两个组织（P95 的间歇 403）。落盘交给水合之后。
  tenantScopeSwitchPending = true;
  if (scope) {
    claimTenantStorage(storageKey, scope);
    claimTenantStorage(aiConfigStorageKey, scope);
    claimTenantStorage(stateRecoveryStorageKey, scope);
    claimTenantStorage(lowFanCollectionJobStorageKey, scope);
    claimTenantStorage(lowFanSuffixesStorageKey, scope);
  }
  workspaceTenantScope = scope;
  loadState();
  loadAiConfig();
  // 换租户=换一整套数据：上一个组织留在编辑器 DOM 里的未提交输入不属于这里，
  // 必须在按新组织的 state 重渲染之前离开，否则它会被当成新组织的内容保存。
  if (typeof resetDraftEditorInputs === "function") resetDraftEditorInputs();
  if (typeof restoreLowFanCollection === "function") restoreLowFanCollection();
  if (typeof renderAll === "function") renderAll();
  await loadAiConfigFromBackend().catch(() => {});
  // 这里刻意不写盘：新租户的本地快照里没有服务端 revision（通常是 0），
  // 此刻写 /api/state 会拿过期 revision 去比对而必然 409。落盘交给调用方在
  // 服务端水合（mergeSavedImageLibraryFromBackend）对齐 revision 之后再做。
  return true;
}

// 切租户/登录落定后的收尾写入。必须在服务端水合之后调用：水合会把 stateRevision
// 校正成服务端当前值，先写就是用本地旧 revision 写盘，服务端判等失败直接回 409，
// 并把 stateWriteBlocked 打开让后续编辑静默停止落库。
function persistWorkspaceAfterHydration() {
  saveState();
}

// 切组织之前先把上一个组织的待写内容落库。saveState() 的 250ms 防抖可能在
// activeOrganizationId 已经换成新组织之后才发出请求，那时请求头里带的是新组织的
// 访问令牌，旧组织的草稿就会被写进新组织。切之前等这一次落库结束即可关掉窗口。
async function flushWorkspaceBeforeTenantSwitch() {
  return flushBackendStateSave({ singlePass: true });
}

// 发布任务在服务端是"逐条 can_write 之后才建得出来"的，而发布条目要等这次 /api/state
// 写入才注册成 canonical 资源（见 server/backend.py 的 _prepare_state_resource_collections）。
// 「加入发布列表」走的是 250ms 防抖保存，用户紧接着点「发布」就会让 /api/publish/prepare
// 抢在注册之前——后端只能回 404「Resource not found」，工作台随即把刚打开的小红书发布页
// 关掉（现场：连续 8 次点发布、8 次 500，资源表里那个条目从头到尾没有行）。
//
// 所以建发布任务之前必须先把待写内容落库，并且确认这一次写真的成功了。判据只能是
// "写完之后还没脏"：自动保存失败会把 backendSaveDirty 重新置真，暂停（stateWriteBlocked）
// 和没水合（backendStateHydrated=false）时 saveState 本来就只写本地，条目在服务端根本不存在。
function workspacePersistenceState() {
  return {
    hydrated: backendStateHydrated === true,
    blocked: state.stateWriteBlocked === true,
    pending: backendSaveDirty === true || Boolean(backendSaveRunning),
    // 降级态：水合重试已用尽，这次会话的工作台还没跟服务端对齐（P85）。hydrated 同样是 false，
    // 这里额外给调用方一个"试过了、失败了"的信号，便于把文案从"稍候"改成"请刷新"。
    degraded: workspaceHydrationDegraded === true,
    // 最近一次写盘失败的原因；没失败过就是 null（调用方据此区分"没写过"和"写失败了"）。
    lastError: backendStateWriteError,
  };
}

async function flushWorkspaceForPublish() {
  const started = workspacePersistenceState();
  if (!started.hydrated || started.blocked) return { ok: false, ...started };
  // 就算当前没有待写内容也要真写一次：条目可能在自动保存失败后只留在本机，
  // 只看 dirty 标志会把这种"从未落库"的状态误判成已落库。
  backendSaveDirty = true;
  await flushBackendStateSave();
  const settled = workspacePersistenceState();
  return { ok: settled.hydrated && !settled.blocked && !settled.pending, ...settled };
}

function sessionStateForWrite() {
  return {
    drafts: state.drafts,
    publish: state.publish,
    generatedImages: state.generatedImages,
    imageTemplateOverrides: state.imageTemplateOverrides,
    customSceneImageTemplates: state.customSceneImageTemplates,
    deletedSceneImageTemplateIds: state.deletedSceneImageTemplateIds,
  };
}

function readBackendRecoverySnapshot() {
  try {
    const snapshotKey = tenantStorageKey(stateRecoveryStorageKey);
    const snapshot = JSON.parse(sessionStorage.getItem(snapshotKey) || "null");
    if (!snapshot) return null;
    // 只有"可证明是垃圾"的才删：版本不对、形状不对。这类删掉是清垃圾。
    if (snapshot.storageVersion !== STATE_STORAGE_VERSION || typeof snapshot.payload !== "object") {
      sessionStorage.removeItem(snapshotKey);
      return null;
    }
    // 快照必须自带"它属于哪个租户"：F5 回落、切组织、登录落定前的 ::claimed 兜底都会让
    // 「当前租户是谁」在同一个 key 上漂移，只按当前租户反推就会把上一个组织的草稿
    // 混进这个组织的视图（P97）。归属由数据自身携带，当前租户只用来比对。
    // 比对不上时**只拒绝、不删**：读这一刻解析出来的租户可能只是"还没落定"（登录/重登
    // 的加载早期），删掉就等于把用户唯一的那份未保存编辑销毁掉。真正的清理在换租户那一步
    // （switchWorkspaceTenantScope 清上一个桶），那里删的一定是已经确定不要的那一份。
    if (!snapshot.tenantScope || snapshot.tenantScope !== resolvedTenantStorageScope(stateRecoveryStorageKey)) {
      return null;
    }
    return snapshot;
  } catch {
    try {
      sessionStorage.removeItem(tenantStorageKey(stateRecoveryStorageKey));
    } catch {
      // Storage can be unavailable in hardened browser modes.
    }
    return null;
  }
}

function saveBackendRecoverySnapshot(options = {}) {
  if (!backendStateHydrated) return false;
  try {
    pendingBackendRecovery = {
      storageVersion: STATE_STORAGE_VERSION,
      // 写入时把归属租户钉进快照本身：读取端只能拿它比对，不能用"当前是哪个租户"反推。
      tenantScope: resolvedTenantStorageScope(stateRecoveryStorageKey),
      // 这张快照是"真冲突后被停写"留下的本机版本：它的 revision 一定落后于服务端，
      // 不能再按 revision 相等来放行，否则用户刷新一次这批编辑就无声消失（P98）。
      conflict: options.conflict === true,
      baseRevision: state.stateRevision,
      savedAt: new Date().toISOString(),
      payload: {
        ...backendStateForWrite(),
        generatedImages: state.generatedImages,
      },
    };
    sessionStorage.setItem(tenantStorageKey(stateRecoveryStorageKey), JSON.stringify(pendingBackendRecovery));
    return true;
  } catch {
    return false;
  }
}

function clearBackendRecoverySnapshot() {
  pendingBackendRecovery = null;
  try {
    sessionStorage.removeItem(tenantStorageKey(stateRecoveryStorageKey));
  } catch {
    // Storage can be unavailable in hardened browser modes.
  }
}

function applyBackendStateRevision(value) {
  const revision = Number(value?.state?.stateRevision ?? value?.stateRevision ?? value?.revision);
  if (Number.isInteger(revision) && revision >= 0) state.stateRevision = Math.max(state.stateRevision, revision);
}

function normalizeLastWorkflowSections(value) {
  const saved = value && typeof value === "object" ? value : {};
  return Object.fromEntries(
    Object.entries(defaultLastWorkflowSections).map(([workflow, fallback]) => [
      workflow,
      workflowSectionOptions[workflow].includes(saved[workflow]) ? saved[workflow] : fallback,
    ]),
  );
}

// 403 的退避重试：本地内容没坏，只是这一轮请求没被接受（组织/身份上下文未就绪）。
// 有界重试，额度用尽就明确告知"已暂停"，不让用户以为还在保存（P95/P98 同族）。
function scheduleStateWriteRetry() {
  if (stateWriteRetryTimer) return true;
  if (stateWriteRetryAttempt >= STATE_WRITE_RETRY_DELAYS_MS.length) {
    backendSaveFailureShown = true;
    toast("自动保存仍未通过校验，已暂停；请刷新页面后继续编辑", { kind: "error", duration: 8000 });
    return false;
  }
  const delay = STATE_WRITE_RETRY_DELAYS_MS[stateWriteRetryAttempt];
  stateWriteRetryAttempt += 1;
  stateWriteRetryTimer = window.setTimeout(() => {
    stateWriteRetryTimer = null;
    if (state.stateWriteBlocked || !backendStateHydrated) return;
    backendSaveDirty = true;
    flushBackendStateSave();
  }, delay);
  return true;
}

async function flushBackendStateSave(options = {}) {
  window.clearTimeout(saveState.backendTimer);
  // 切租户窗口内一律不发请求（含 beforeunload 的 keepalive 收尾写盘）：此时请求头里的
  // 组织与 body 里的记录可能分属两个组织，服务端只能判 403。窗口由水合关闭，期间的内容
  // 由恢复快照兜着（saveState 已经写了本地与快照）。
  if (tenantScopeSwitchPending) return backendSaveRunning;
  if (!backendStateHydrated || state.stateWriteBlocked || !backendSaveDirty) return backendSaveRunning;
  if (backendSaveRunning) return backendSaveRunning;
  backendSaveRunning = (async () => {
    while (backendStateHydrated && !state.stateWriteBlocked && backendSaveDirty) {
      backendSaveDirty = false;
      try {
        saveBackendRecoverySnapshot();
        const result = options.keepalive || options.singlePass
          ? await postBackend("/api/state", backendStateForWrite(), {
            keepalive: options.keepalive === true,
            waitForJob: false,
          })
          : await postBackend("/api/state", backendStateForWrite());
        applyBackendStateRevision(result);
        backendSaveFailureShown = false;
        backendStateWriteError = null;
        stateConflictRecoveryAttempted = false;
        stateWriteRetryAttempt = 0;
        if (stateConflictOverwrote) {
          // 上一次 409 是"真冲突"，我们用刚拿到的服务端 revision 把本机这一版重放过一次——
          // 能走到这里说明落库成功。这次覆盖另一个窗口的更新必须是可见的，不能悄悄发生（P98）。
          stateConflictOverwrote = false;
          toast("这条冲突已按本机版本重新保存（另一个窗口刚做的更新被覆盖）", { kind: "error", duration: 7000 });
        }
        if (backendSaveDirty) saveBackendRecoverySnapshot();
        else clearBackendRecoverySnapshot();
      } catch (error) {
        backendSaveDirty = true;
        backendStateWriteError = {
          status: Number(error?.status || 0),
          errorCode: String(error?.payload?.errorCode || error?.errorCode || ""),
          message: String(error?.message || ""),
        };
        const baseRevision = Number(state.stateRevision);
        // 刚写完的资源（配方/模板）本地已落库，但 OpenFGA 授权元组由 outbox 异步 drain
        // （默认 5s）。这几秒内回存 state 会被判 resource_not_found——不是内容问题，短暂
        // 退避重试即可，不必用红字吓用户（后端已在内联物化时补发元组，这里再兜一层）。
        const resourceNotReadyYet = Number(error?.status) === 404
          && String(error?.payload?.errorCode || error?.errorCode || "") === "resource_not_found";
        if (error?.status === 409) {
          const serverRevision = Number(
            error?.payload?.state?.stateRevision ?? error?.payload?.stateRevision ?? error?.payload?.revision,
          );
          console.warn(`state 409: base=${baseRevision} server=${Number.isInteger(serverRevision) ? serverRevision : "unknown"}`);
          applyBackendStateRevision(error?.payload);
          const tombstoneConflict = error?.payload?.errorCode === "state_tombstone_conflict";
          if (!tombstoneConflict && !userEditedSinceHydration && !stateConflictRecoveryAttempted) {
            // 本会话还没有任何用户编辑，本地内容与上次水合的服务端快照等价，
            // 这次 409 是假冲突：静默按服务端 revision 重新水合，不提示、不阻断。
            stateConflictRecoveryAttempted = true;
            backendSaveFailureShown = false;
            await mergeSavedImageLibraryFromBackend();
            // 重水合已把 stateRevision 对齐到服务端，用新 revision 把这批内容落盘；
            // 页面卸载时的 keepalive 写入维持原来的"只写一次"。
            if (options.singlePass) break;
            continue;
          }
          if (!tombstoneConflict && !stateConflictRecoveryAttempted) {
            // 本会话确实编辑过（真并发冲突）：**不能停写**——停写的代价是用户产出的内容
            // 只留在本机、重新登录后整批消失（P85 实测的正是这条，真因在本条）。
            // 上面的 applyBackendStateRevision 已经拿到服务端刚回的 revision，所以这里
            // 用新 revision 把本机这一版**一次性重放**：成功即视为已保存并把"覆盖了另一个
            // 窗口的更新"说出来；再冲突才进入停写 + 冲突快照的兜底。
            stateConflictRecoveryAttempted = true;
            stateConflictOverwrote = true;
            backendSaveFailureShown = false;
            if (options.singlePass) break;
            continue;
          }
          state.stateWriteBlocked = true;
          stateConflictOverwrote = false;
          // 已经重放过一次仍然冲突（或墓碑）：到这里才停写，且必须留下凭证——
          // 否则用户刷新一次这批编辑就无声消失（P85/P98）。带 conflict 标记的快照在水合时
          // 不看 revision 相等，重新加载后本机编辑会回到界面并按服务端 revision 重新落库。
          saveBackendRecoverySnapshot({ conflict: true });
        }
        if (!backendSaveFailureShown && !resourceNotReadyYet) {
          backendSaveFailureShown = true;
          toast(
            error?.status === 409
              ? (error?.payload?.errorCode === "state_tombstone_conflict"
                ? "已删除的记录不能恢复，请刷新后继续"
                : "状态已被其他窗口更新，自动保存已暂停；本机修改已保留，刷新后可继续编辑并重新保存")
              : error?.status === 403
                // 403 与 409 的原因完全不同：内容没问题，是这一轮请求没被接受
                // （组织/身份上下文未就绪）。文案不能复用 409 的"其他窗口"，也别说成网络故障。
                ? "自动保存未通过校验（权限或组织上下文未就绪），内容已留在本地，正在自动重试"
                : `自动保存失败：${error.message || "无法连接后端"}`,
            { kind: "error", duration: 7000 },
          );
        }
        // 403（身份/组织上下文未就绪）与 404 resource_not_found（刚写完的资源授权还没同步）
        // 都是"这次没被接受"，短暂退避后重试；重试用尽才提示用户。
        if (error?.status === 403 || resourceNotReadyYet) scheduleStateWriteRetry();
        break;
      }
      if (options.singlePass) break;
    }
  })().finally(() => { backendSaveRunning = null; });
  return backendSaveRunning;
}

function saveState(options = {}) {
  // 程序化写入（登录/切租户时的水合与迁移）不代表用户编辑，不能让它把
  // 之后的版本冲突升级成"真冲突"提示。
  if (options.programmatic !== true) userEditedSinceHydration = true;
  window.clearTimeout(saveState.backendTimer);
  if (state.stateWriteBlocked) {
    // 停写（第二次 409 / 墓碑冲突）之后，用户的编辑既不上服务端、**也必须留在本机**——
    // 恢复快照是这批编辑唯一的去处。以前这里只调 saveBrowserState()，而它只存
    // activeDraftId/activeWorkflow 那几个字段、不含 drafts：于是停写之后的每一次编辑都
    // 既不落库也不进快照，刷新即丢，用户只会看到一条 toast 然后"一切正常"。
    // 快照照旧标 conflict，读取端据此不按 revision 相等放行（P98 的既有约定）。
    saveBrowserState();
    saveBackendRecoverySnapshot({ conflict: true });
    return;
  }
  backendSaveDirty = true;
  saveBrowserState();
  saveBackendRecoverySnapshot();
  // 切租户期间只攒脏、不发请求（P95）：此时请求头里的组织令牌与 body 里的记录可能分属
  // 两个组织，服务端只能判 403。水合把 revision 对齐之后由调用方统一落盘。
  if (tenantScopeSwitchPending) return;
  saveState.backendTimer = window.setTimeout(flushBackendStateSave, 250);
}

// 是否有流程正在往草稿里写内容（一键生成 / 单图生成 / 单视觉角色 / 模板入库 / 视觉模板上传）。
//
// 这些流程持有的是草稿**对象**的引用，不是每次按 id 查表。所以只要有"按 id 重建 state.drafts"
// 的水合插进来，它们手里那个对象就成了孤儿：之后写进去的标题、正文、标签、图片全都落在没人
// 保存、也没人渲染的实例上。实测现场就是这样——生成任务全部成功、平台照扣费、进度条正常走完，
// 界面与落库内容却一点没变（连续两轮都一样），因为唯一被保存的是服务端那份旧快照。
function draftWriteInFlight() {
  if (typeof rewriteGenerationController !== "undefined" && rewriteGenerationController) return true;
  if (typeof templateImportController !== "undefined" && templateImportController) return true;
  if (typeof visualTemplateImportController !== "undefined" && visualTemplateImportController) return true;
  if (state?.singleImageGenerationKeys instanceof Set && state.singleImageGenerationKeys.size > 0) return true;
  return false;
}

// 水合的重试与降级（P85）。过去这一次读失败只 console.error 一句就结束：用户看到的是一整套
// 空工作台，而且要等到下一次登录/切组织才会再读一次（RG4-R5 实测空窗 10–20s）。额度用尽后
// 进入降级态：hydrated 保持 false（发布等入口据此拦住"还没和服务器同步完成"的操作），
// 并明确告知用户，而不是让"没读到"冒充"这个租户是空的"。
// 只有"最新发起的那次水合"有资格继续重试并播报：登录落定与切组织都会再发起一次，
// 旧的那次必须安静退出，否则两次读会互相覆盖。
async function retryWorkspaceHydration(attemptHydration, delays = HYDRATION_RETRY_DELAYS_MS) {
  const runToken = (hydrationRunToken += 1);
  for (let attempt = 0; ; attempt += 1) {
    // 游客确认未登录时不要发 /api/state：连第一次都不必打。
    if (typeof loginGateSignedOut === "function" && loginGateSignedOut()) return false;
    try {
      await attemptHydration();
      workspaceHydrationDegraded = false;
      return true;
    } catch (error) {
      if (runToken !== hydrationRunToken) return false;
      // 游客未登录时 GET /api/state 必然 401：重试只会把同一条红请求刷到 Network 里。
      const signedOut = typeof loginGateSignedOut === "function" && loginGateSignedOut();
      if (signedOut || (Number(error?.status) === 401 && typeof loginGateSignedInKnown === "function" && loginGateSignedInKnown() === false)) {
        return false;
      }
      const delay = delays[attempt];
      if (delay === undefined) {
        workspaceHydrationDegraded = true;
        console.error("Workspace hydration failed", error);
        // 这句只对「确认已登录、却读不到 /api/state」有意义。游客、登录弹窗、鉴权还
        // 未知时水合失败是常态（401），刷新也解决不了；以前用「不是 signedOut 就 toast」
        // 会在启动竞态里误伤（闸门还是 null 时 signedOut 为 false）。见 auth/login-gate.js。
        // 单机/兼容模式没有登录闸门：同步失败仍要说清楚。
        const gateActive = typeof loginGateActive === "function" && loginGateActive();
        const confirmedSignedIn = typeof loginGateSignedInKnown === "function"
          && loginGateSignedInKnown() === true;
        if (!gateActive || confirmedSignedIn) {
          toast("工作台数据还没同步成功，界面可能不是最新的；请刷新页面重试", { kind: "error", duration: 12000 });
        }
        return false;
      }
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      if (runToken !== hydrationRunToken) return false;
      // 退避期间闸门可能已经确认是游客：下一轮不要再发。
      if (typeof loginGateSignedOut === "function" && loginGateSignedOut()) return false;
    }
  }
}

// `GET /api/state` 的契约是"一份完整状态文档"：服务端对缺失的租户状态用 DEFAULT_STATE 兜底，
// 集合字段一定在（见 server/backend.py 的 _load_unlocked / _normalize_state）。所以"解析成功
// 但连这些集合都没有"只能说明这次读到的不是状态文档，不能当成"这个租户是空的"——那条路会
// 推进 revision、清掉本机恢复快照，把界面与之后的落库一起变成空的（P85）。
function assertBackendStateDocument(payload) {
  const usable = Boolean(payload)
    && typeof payload === "object"
    && Array.isArray(payload.drafts)
    && Array.isArray(payload.publish);
  if (!usable) {
    const error = new Error("服务端返回的状态文档无法识别（缺少 drafts/publish 集合）");
    error.status = 200;
    error.payload = {};
    throw error;
  }
  return payload;
}

// ── 发布数据的单一读口 / 写口（方案 A 第一阶段） ──────────────────────────────
// 同一批发布数据在内存里有两个副本：
//   ① state.publish      —— 按条目的视图，发布列表直接渲染它；
//   ② state.publishTasks —— 按任务的持久记录（扩展与轮询只更新它），
//      它的 task.items[] 与 ① 的条目共用同一个 id。
// 只要有一个副本没跟上（切组织、多标签登出、某处只写了一个），就会复现
// 「界面已登录、接口已失效」或「条目只留本机」的分裂。
//
// 这一阶段只立唯一入口，一个副本都不删：没有单一读口之前，无法证明某个副本没人在用，
// 删错就是数据丢失。写口目前只覆盖 loadState 的读入与失败归零（本文件里能安全收口的两处）；
// 其余写点（rewrite/generate.js、tools/low-fan.js、publish/list.js）与水合段
// hydrateWorkspaceFromBackendOnce 里的赋值留给下一阶段——后者被 tests/test_state_recovery.js
// 整段 eval 进沙箱，迁写口要连测试一起考虑。
function readPublishItems() {
  return Array.isArray(state.publish) ? state.publish : [];
}

// 任务副本的单一读口。null 是「尚未水合」的哨兵，与空列表不是一回事
// （backendStateForWrite 靠它决定不把空列表写回服务端，见那里的注释）。
function readPublishTasks() {
  return Array.isArray(state.publishTasks) ? state.publishTasks : [];
}

// 同一条目在任务副本里的记录：两个副本各存一份 publishStatus / xhsPublishTaskId /
// publishError，需要交叉核对（或以后收敛时取值）只从这里取。
function readPublishTaskItem(itemId) {
  const id = String(itemId || "");
  if (!id) return null;
  for (const task of readPublishTasks()) {
    const items = Array.isArray(task?.items) ? task.items : [];
    const match = items.find((entry) => String(entry?.id || "") === id);
    if (match) return match;
  }
  return null;
}

// 条目副本的单一写口。
function writePublishItems(items) {
  state.publish = Array.isArray(items) ? items : [];
  return state.publish;
}

// 任务副本的单一写口；传 null 表示回到「尚未水合」，保留 backendStateForWrite 的语义。
function writePublishTasks(tasks) {
  state.publishTasks = Array.isArray(tasks) ? tasks : null;
  return state.publishTasks;
}

async function mergeSavedImageLibraryFromBackend() {
  return retryWorkspaceHydration(hydrateWorkspaceFromBackendOnce);
}

async function hydrateWorkspaceFromBackendOnce() {
  try {
    const backendState = assertBackendStateDocument(await getBackend("/api/state"));
    state.stateWriteBlocked = false;
    applyBackendStateRevision(backendState);
    const backendRevision = Number.isInteger(backendState.stateRevision) && backendState.stateRevision >= 0
      ? backendState.stateRevision
      : state.stateRevision;
    state.stateRevision = backendRevision;
    const recoveryBaseRevision = Number(pendingBackendRecovery?.baseRevision);
    // 冲突快照（真冲突后停写留下的本机版本）不看 revision 相等：它的 revision 必然落后，
    // 按相等判定就等于用户刷新一次丢一次编辑（P98）。放行它并不覆盖别人的内容——下面会
    // 把 stateRevision 对齐到服务端当前值，本机版本按新 revision 重新落库。
    const recoveryApplies = Boolean(
      pendingBackendRecovery
      && (
        pendingBackendRecovery.conflict === true
        || (Number.isInteger(recoveryBaseRevision) && recoveryBaseRevision === backendRevision)
      )
    );
    const discardLocalRecovery = Boolean(pendingBackendRecovery && !recoveryApplies);
    // 下面重写快照时会清掉 conflict 标记，提示文案要用的那一刻先记下来。
    const appliedConflictSnapshot = Boolean(recoveryApplies && pendingBackendRecovery?.conflict === true);
    if (discardLocalRecovery) clearBackendRecoverySnapshot();
    const durableState = recoveryApplies
      ? { ...backendState, ...pendingBackendRecovery.payload, stateRevision: backendRevision }
      : backendState;
    const localDrafts = discardLocalRecovery ? [] : state.drafts;
    const localPublish = discardLocalRecovery ? [] : state.publish;
    const localImages = discardLocalRecovery
      ? []
      : (Array.isArray(state.generatedImages) ? state.generatedImages : []);
    const localOverrides = discardLocalRecovery ? {} : (state.imageTemplateOverrides || {});
    const localSceneTemplates = discardLocalRecovery ? [] : state.customSceneImageTemplates;
    const localKnowledgeCollapsedFolderIds = discardLocalRecovery ? [] : state.knowledgeCollapsedFolderIds;
    const backendDraftIds = new Set((backendState.drafts || []).map((item) => item?.id).filter(Boolean));
    const backendPublishIds = new Set((backendState.publish || []).map((item) => item?.id).filter(Boolean));
    const localOverridesNeedWrite = Object.entries(localOverrides).some(([templateId, override]) => (
      JSON.stringify(backendState.imageTemplateOverrides?.[templateId]) !== JSON.stringify(override)
    ));
    const hydrationWriteNeeded = recoveryApplies
      || localDrafts.some((item) => item?.id && !backendDraftIds.has(item.id))
      || localPublish.some((item) => item?.id && !backendPublishIds.has(item.id))
      || localOverridesNeedWrite
      || (localKnowledgeCollapsedFolderIds.length > 0
        && JSON.stringify(localKnowledgeCollapsedFolderIds) !== JSON.stringify(backendState.knowledgeCollapsedFolderIds || []));
    const mergeById = (backendItems, localItems, normalize) => {
      const merged = new Map();
      [...(backendItems || []), ...(localItems || [])].forEach((item) => {
        const normalized = normalize(item);
        const key = normalized?.id;
        if (key && !merged.has(key)) merged.set(key, normalized);
      });
      return [...merged.values()];
    };
    const tombstoneKeys = new Set(
      (Array.isArray(backendState.recordTombstones) ? backendState.recordTombstones : [])
        .filter((item) => item && typeof item === "object")
        .map((item) => `${item.collection}:${item.id}`),
    );
    const withoutDeleted = (items, collection) => (items || []).filter((item) => (
      !item?.id || !tombstoneKeys.has(`${collection}:${item.id}`)
    ));
    // 本地还有没写完的编辑、或者有流程正在往草稿里写内容时，这一轮水合只对齐 revision，
    // **不重建 drafts**：本地这一份才是新的，服务端快照对它是旧的；重建会让正在生成的流程
    // 失去自己的草稿对象（见 draftWriteInFlight 的说明）。
    const deferDraftHydration = Boolean(
      backendSaveDirty || backendSaveRunning || state.stateWriteBlocked || draftWriteInFlight(),
    );
    if (deferDraftHydration) {
      // 一个字段都不动，也不要做 normalizeSavedDraft 那种"顺手复制一份"的规整：它返回的是新
      // 对象，等于又换了一次实例（引用照样断）。
      state.drafts = assignDraftTaskNumbers(state.drafts || []);
    } else if (!recoveryApplies && !discardLocalRecovery) {
      state.drafts = assignDraftTaskNumbers(mergeById(backendState.drafts, state.drafts, normalizeSavedDraft));
      state.drafts = assignDraftTaskNumbers(withoutDeleted(state.drafts, "drafts"));
    } else {
      const mergedDrafts = recoveryApplies
        ? mergeById(durableState.drafts, withoutDeleted(localDrafts, "drafts"), normalizeSavedDraft)
        : (backendState.drafts || []);
      state.drafts = assignDraftTaskNumbers(withoutDeleted(mergedDrafts, "drafts"));
    }
    state.publish = mergeById(
      withoutDeleted(durableState.publish, "publish"),
      withoutDeleted(localPublish, "publish"),
      (item) => restoreReplacedPublishImages(normalizeSavedItem(item), state.drafts),
    );
    if (recoveryApplies) {
      state.publishTasks = withoutDeleted(
        Array.isArray(durableState.publishTasks) ? durableState.publishTasks : [],
        "publishTasks",
      );
    } else {
      state.publishTasks = withoutDeleted(
        Array.isArray(backendState.publishTasks) ? backendState.publishTasks : [],
        "publishTasks",
      );
    }
    const restoredActiveId = state.drafts.some((draft) => draft.id === state.activeDraftId)
      ? state.activeDraftId
      : durableState.activeDraftId;
    const mergedActiveDraftId = state.drafts.some((draft) => draft.id === restoredActiveId)
      ? restoredActiveId
      : state.drafts[0]?.id || null;
    setActiveDraftId(mergedActiveDraftId);
    if (["original", "replica", "tools"].includes(durableState.activeWorkflow)) {
      state.activeWorkflow = durableState.activeWorkflow;
    }
    const backendImages = withoutDeleted(
      Array.isArray(durableState.generatedImages) ? durableState.generatedImages : [],
      "generatedImages",
    );
    const merged = new Map();
    [...backendImages, ...withoutDeleted(localImages, "generatedImages")].forEach((item) => {
      if (!item?.src) return;
      const src = normalizeImageSrc(item.src);
      if (!merged.has(src)) merged.set(src, { ...item, src });
    });
    state.generatedImages = [...merged.values()];
    state.imageTemplateOverrides = {
      ...(backendState.imageTemplateOverrides || {}),
      ...localOverrides,
    };
    state.customSceneImageTemplates = mergeById(
      durableState.customSceneImageTemplates,
      localSceneTemplates,
      (item) => item,
    );
    state.deletedSceneImageTemplateIds = [...new Set([
      ...(durableState.deletedSceneImageTemplateIds || []),
      ...(discardLocalRecovery ? [] : (state.deletedSceneImageTemplateIds || [])),
    ])];
    state.knowledgeCollapsedFolderIds = localKnowledgeCollapsedFolderIds.length
      ? localKnowledgeCollapsedFolderIds
      : (durableState.knowledgeCollapsedFolderIds || []);
    // 水合后本地状态与服务端对齐，本会话的编辑计数从这里重新开始。只能在"这一份真的
    // 用上了"之后复位：重试期间用户仍可能编辑，提前复位会把一次真冲突降级成"本会话没编辑过"
    // 而被静默按服务端重读（409 分支据此决定重放还是停写）。
    userEditedSinceHydration = false;
    backendStateHydrated = true;
    // 水合已把 revision 对齐到服务端：切租户期间"只攒脏、不写盘"的窗口到此结束（P95），
    // 403 的退避额度也按新的组织上下文重新计。
    tenantScopeSwitchPending = false;
    stateWriteRetryAttempt = 0;
    saveBrowserState();
    backendSaveDirty = hydrationWriteNeeded;
    if (hydrationWriteNeeded) {
      saveBackendRecoverySnapshot();
      flushBackendStateSave();
    } else {
      clearBackendRecoverySnapshot();
    }
    renderAll();
    if (recoveryApplies) {
      // 冲突快照不是"刷新前没保存完"，而是"上一次真冲突被停写"：本机这版比服务端新，
      // 现在按本机版本重新落库。必须说出来——它确实盖掉了另一个窗口的更新（P98）。
      toast(
        appliedConflictSnapshot
          ? "已用本机未保存的修改覆盖保存（上一次冲突时的编辑保留了下来）"
          : "已恢复刷新前尚未完成保存的编辑，正在重新保存",
        { duration: 5200 },
      );
    }
  } catch (error) {
    // The local library remains usable when the backend is unavailable.
    // 水合失败也必须放行写盘：否则切租户窗口会一直挂着，之后的编辑永远不落库（P95）。
    tenantScopeSwitchPending = false;
    // 这一次没读到状态：hydrated 保持 false、本机内容一个字节都不动、也不渲染空集合。
    // 重试还是降级由 retryWorkspaceHydration 决定（P85）。
    throw error;
  }
}


// ----- js/core/backend-errors.js -----
// 后端错误的机器可读码（errorCode）→ 用户话术。只在 HTTP 边界翻译这一次。
//
// 后端在异常分支回的是「error 给日志 + errorCode 给程序」：error 常常是上游英文常量
// （payment_unavailable 那句 "Xunhu credentials are not configured"、资源授权的
// "Resource not found"），过去前端只取 data.error 原文，等于把上游英文直接丢给用户。
// 这里按码给话术，和 web/js/auth/logto-errors.js 是同一套收口思路。
//
// 没登记的码一律回退原文，不做「统一兜底」：把未知错误吞成一句万金油既掩盖真实故障，
// 也让排障失去抓手（台账 P4 验收断言 3 要求未知码仍能回退）。新增码时补一行即可。
// 「这次请求没带凭据」= 没登录，和「服务端已经不认这份凭据」= 登录状态失效，是两件事。
// 对没登录的人说"登录状态已失效，请重新登录"，他会去找一个并不存在的旧登录态；对登录
// 真的失效的人说"请先登录"，他又以为要从头走一遍。这句话是前者唯一的口径：client.js 的
// 401 翻译与登录闸门 login-gate.js 的提示都读它。
const UNAUTHENTICATED_ACTION_MESSAGE = "请先登录后再使用该功能";

const BACKEND_ERROR_MESSAGES = {
  // 生成输入（server/services/knowledge.py）：空草稿不具备生成条件，后端在预扣费前拒绝。
  // 前端按钮门控用的是同一条判据（web/js/rewrite/drafts.js 的 draftHasUsableContent）。
  ai_draft_empty: "先填写标题、正文，或加入图片素材，再生成",
  // 计费 / 支付（server/billing_service.py、server/xunhu_pay.py）
  payment_unavailable: "当前环境未配置支付通道，请联系管理员开通后再试。",
  billing_unavailable: "支付服务暂时不可用，请稍后重试；若持续失败请联系管理员。",
  billing_spec_invalid: "暂不支持该购买项，请刷新页面后重新选择。",
  // 定价保存被拒（server/billing_service.py 的 write_pricing_config）。这句话曾是下单语境的
  // "核对后再下单"，运营在改价页看到它会以为自己在下单；现在按"保存改价"写，并优先用后端
  // 点名的字段（见下面的 backendPricingMessage，台账 P114）。
  billing_pricing_invalid: "定价保存失败：请检查每个定价项的值，修改后再保存。",
  // 清空定价输入框再保存曾经等于"恢复默认价"（空值被当成缺省值，台账 P87）；现在直接报错，
  // 所以这句话必须说清"空"与"0"的区别，且 0 只对单价合法（倍率、会员天数与两个积分必须大于 0）。
  billing_pricing_blank: "有定价项没填：请补全后再保存（不收费的单价请填 0，倍率、会员天数与积分必须大于 0）。",
  billing_reconcile_failed: "订单支付状态同步失败，请稍后重试或联系管理员。",
  refund_not_allowed: "当前订单状态不支持退款，请刷新订单状态后再试。",
  // 渠道退款被上游拒绝（P102）：本地一律不冲正、不改订单状态，所以话术必须把
  // "本单什么都没变"说出来。上游自己的错误原文走下面的 formatter 附在后面。
  // channel_refund_failed 是历史码（502），退款链路拆分后已不再新增，但保留登记
  // 以兼容存量响应与 test_refund_honesty.js 的断言。
  channel_refund_failed: "渠道退款未成功，本单未做任何变更（积分未冲正、订单仍为已支付）。",
  // 退款被渠道业务拒绝（422，server/billing_service.py 的 refund_payment_order）：
  // 渠道可达但拒绝退款（订单不可退、退款窗口关闭等）。与 billing_unavailable（503，
  // 渠道打不通）区分，让运营分清"渠道说不退"和"渠道打不通"。
  refund_rejected: "渠道拒绝退款，本单未做任何变更（积分未冲正、订单仍为已支付）。",
  // 退款必须写原因（server/billing_service.py 的 refund_payment_order）：它是这笔资金冲正
  // 唯一的对账与客诉线索，空原因在界面与接口两侧都拦（台账 P105）。
  refund_reason_required: "请填写退款原因：它是对账与客诉复盘的依据。",
  // 渠道退款结果未知（502，server/billing_service.py 的 refund_payment_order）：渠道超时/
  // 传输失败，请求可能已经受理——钱可能已经退出去，本地却没有回执。订单已转「退款中」、
  // 积分已冲正，所以这句绝不能写成"退款失败"（那会诱导运营再点一次，而重试就是第二次真实
  // 退款），必须说清"结果未知 + 去哪核对 + 勿重复"三件事。渠道自己的错误原文在 channelError
  // 里，走下面的 formatter 附在后面给财务核对用。
  refund_outcome_unknown: "渠道退款结果未知：本单已转为「退款中」，积分已冲正；请到渠道后台核对结果后再处理，勿重复退款。",
  // 该单已经在退（或已退完）时又提交了一次（409，billing_service 的 _refund_in_progress_payload）：
  // 并发里抢占失败的那一个、refunding 状态下的重试、超时冲正后的重复点击都走这里。动作已经
  // 发出去了，用户唯一能做的是刷新订单状态看结果，别让人以为还能再退一次。
  refund_in_progress: "该订单正在退款或已退款，请刷新订单状态后再试。",
  // not_found 现在只由计费订单抛出（billing_service.py），话术按订单写
  not_found: "订单不存在或已被删除，请刷新后重试。",
  // 「我的组织」（server/billing_service.py 的 user_organizations）：组织清单是按令牌里的
  // user_id 查的，查不到用户身份说明这次请求没带上有效的登录态——不是"这个账号没有组织"。
  user_identity_required: "读取组织信息需要有效的登录状态，请重新登录后再试。",
  billing_storage_unavailable: "计费数据暂时读不到，请稍后重试；若持续失败请联系管理员。",
  // 资源授权（server/resource_authorization.py）：后端有意不区分「尚未就绪」与「无权访问」，
  // 以免泄露资源是否存在；话术也保持同等模糊，别在这里替它下结论。
  resource_not_found: "该资源尚未就绪，或当前账号没有访问权限；请稍后重试或联系管理员。",
  resource_authorization_unavailable: "权限服务暂时不可用，请稍后重试。",
  // 身份/租户上下文被拒（server/resource_authorization.py 的 ResourceIdentityForbidden，
  // 经 backend.py 的 403 边界下发，台账 P95）。以前这种情况只回一句 "Forbidden"，
  // 前端分不出「内容有问题」还是「组织上下文正在切换」。话术与后端一样保持不具体——
  // 是哪条守卫拒绝的只进服务端安全日志，回给用户就等于一个租户边界探测器。
  resource_identity_forbidden: "当前组织下的身份或权限尚未就绪：请稍后重试；若反复出现请联系管理员。",
  // 体积超限（server/tenant_storage.py、server/backend.py 的请求体上限）：这一族以前没有
  // 码，前端只能把后端的 "payload too large" 原文或一句"连不上后端"丢给用户（台账 P72/P85）。
  // 话术要指向用户能做的动作——减少参考图/内嵌图片，而不是让他去查网络。
  payload_too_large: "本次提交的数据体积超过服务器上限，请减少参考图或内嵌图片数量后重试。",
  // 团队治理（server/backend.py `_logto_management_error_response`、
  // server/identity_projection.py `classify_logto_management_error`）：这一族码对应的
  // error 全是英文常量（"Logto Management API unavailable" /
  // "Logto membership reconciliation required"），所以整族都要登记，别漏一条——
  // 团队治理走的是独立于 client.js 的请求通道，兜底拿不到这里的话术就会原样漏英文（台账 P14）。
  // 区分两类语义：可重试（服务不可用/尚未配置/版本不匹配）与需人工核对（数据要重新同步）。
  logto_management_unavailable: "权限服务暂时不可用，请稍后重试。",
  logto_management_rejected: "该操作未被权限服务接受：请刷新列表核对成员与邀请状态，或联系管理员。",
  logto_management_conflict: "权限数据刚刚被其它操作更新，请刷新列表后重试。",
  logto_management_target_missing: "这条成员或邀请已经不存在了，请刷新列表后重试。",
  logto_management_unique_violation: "该邮箱已有一条未处理的邀请：请在列表里撤销它，或直接用「重新发送」。",
  logto_management_not_configured: "权限服务尚未配置，请联系管理员。",
  logto_management_contract_missing: "权限服务版本不匹配，请联系管理员。",
  logto_management_error: "权限操作失败，请稍后重试或联系管理员。",
  // 接受邀请前的邀请状态核对（server/identity_projection.py 的
  // accept_logto_organization_invitation）。和上面那族一样，这里的 error 全是英文常量
  // （server/backend.py 的 `_logto_management_error_response`：可重试的码回
  // "Logto Management API unavailable"，其余回 "Logto membership reconciliation required"），
  // 所以整族都要登记，别漏一条——台账 P123 现场就是漏了 logto_invitation_not_pending
  // （邀请过了有效期），用户看到的是那句英文原文。
  // 前四条是"再试也没用"：邀请没了/已结束/对不上，只能让邀请人重发；后两条是接受动作
  // 已经发出但结果核实不了，上游本身可重试，所以话术指向"稍后重试"。
  logto_invitation_not_pending: "这条邀请已失效或已过期，请让邀请人重新发起邀请。",
  logto_invitation_missing: "这条邀请已不存在（可能已被撤销），请让邀请人重新发起邀请。",
  logto_invitation_mismatch: "这条邀请与链接不匹配，请让邀请人重新发起邀请。",
  logto_invitation_organization_missing: "这条邀请没有指向任何组织，请联系管理员重新发起邀请。",
  logto_invitation_acceptance_missing: "邀请接受未确认成功：请稍后重试；若反复出现请联系管理员。",
  logto_invitation_acceptance_mismatch: "邀请接受结果与邀请记录不一致：请稍后重试；若反复出现请联系管理员核对。",
  // 这条邀请的收件邮箱不是当前账号。管理 API 的接受动作本身不看收件人，守卫在我们这边；
  // 话术要说清问题出在"这封邀请不是发给你的邮箱"，而不是让用户反复重试。
  logto_invitation_recipient_mismatch: "这条邀请不是发给当前账号的邮箱的：请让邀请人重新邀请你的邮箱。",
  // 异步任务终态（server/services/jobs.py）：任务失败只回这个码，阶段文案另外下发，
  // 所以这里给一句中性话术，不加"上游"之类的具体归因（台账 P66：用户侧曾看到英文原文）。
  execution_failed: "任务执行失败，请重试；若反复失败请联系管理员。",
  // 后端进程重启把上一个运行时仍在跑的任务判成失败（server/canonical_jobs.py 与
  // server/tenant_storage.py 的 mark_interrupted，errorCode 就是这个码）：任务不是"跑错了"，
  // 是被重启掐掉的，也不会自动续跑。现场（「搭建笔记内容」）用户在面板上看到的是英文码本身
  // （"笔记内容搭建失败：interrupted_by_restart"）——既像这次的内容有问题，又拿不到下一步动作。
  // 所以话术必须点明"服务端重启"这个与用户内容无关的原因，并只给一个能做的动作：重新发起。
  interrupted_by_restart: "服务端重启中断了本次任务，它不会自动继续，请重新发起。",
  // 上游有响应但给不出正文（server/services/outbound.py 的 AiReplyUnusableError，经
  // jobs.py 的白名单透出）。测试模型最常撞：推理模型把预算全花在 reasoning_content 上，
  // 上游回 200、正文却是空的。以前只有一句"任务执行失败"，真因透不出来（P29）。
  ai_reply_unusable: "模型有响应但没给出正文：请确认模型名与上游接口匹配，或换一个模型再测。",
  // 测试/探测时填的上游地址不合法（server/services/outbound.py 的 AiUpstreamUrlError）：
  // 临时地址不走保存路径的校验，过去填 http:// 会**真的按 http 出站**（P29）。判据与
  // 保存 AI 配置时那句（P20）同源，界面这一侧也保持同一句话。
  ai_upstream_url_invalid: "AI 接口地址不合法：必须是带主机名的 https:// 地址，且不能内嵌用户名或密码。",
  // 拆解中止/部分失败后的「保留已完成的页并入库」：这一次确实没有留下可入库的页
  // （逐页结果还没落库，或全部页都失败了）。话术必须指向用户能做的事——重试失败页或
  // 重新拆解——而不是又一句"任务执行失败"（现场报障就是卡在那句话上）。
  no_committable_pages: "这次拆解没有留下可入库的页面：请重试失败页，或重新拆解后再入库。",
  // 采集授权（server/services/note_consent.py 的 NoteConsentRequired）：正常路径走不到
  // 这句话——前端在采集入口就弹过授权说明并当场确认了。留着是因为服务端才是权威：
  // 直接调接口、或在另一处撤销后重试时，用户看到的应该是"去确认"，而不是一句
  // "采集任务创建失败"。
  note_capture_consent_required: "采集前需要先确认采集授权说明：请在采集入口重新确认后再试。",
  // 采集节奏（server/services/import_note.py 的 ImportNoteRateLimited）：在飞锁、条间间隔、
  // 每分钟/每天上限、图片冷却共用这个码。服务端那句 error 带着具体原因和剩余秒数，正常由
  // 下面的 formatter 原样透传；这条静态句只是拿不到中文时的退路。关键是它绝不能落回
  // 「这次采集没拿到内容 + 去小红书确认笔记可见」那套建议——内容根本没被抓过，是这次不该开跑。
  import_note_rate_limited: "采集现在不能开始（节奏限制）：请按提示稍后再试。",
  // 账号生命周期（server/account_lifecycle.py + server/http_routes/console.py）：
  // 封禁/软删的拦截与处置动作。account_banned 的文案要带上到期时间，走下面的
  // formatter（静态这句只是取不到 bannedUntil 时的退路）。
  account_banned: "账号已被封禁，期间无法使用任何功能。",
  account_deleted: "账号不存在或已被删除，请联系管理员。",
  account_lifecycle_forbidden: "账号生命周期处置需要平台管理员权限。",
  account_lifecycle_unavailable: "账号生命周期存储未接入，请联系管理员。",
  account_lifecycle_invalid: "账号处置请求不完整，请刷新后重试。",
  reason_required: "请填写处置原因。",
  idempotency_required: "请求缺少幂等键，请刷新页面后重试。",
  idempotency_conflict: "幂等键已用于另一个动作，请刷新页面后重试。",
  // 强制改密依赖账号服务的「密码有效期」开关（server/logto_accounts.py 的
  // force_password_change，P138）。没开时上游回一句英文 code
  // （sign_in_experiences.password_expiration[_not]_enabled），过去会一路冒到通用
  // 500 把英文原文丢给操作者。这里说清"是什么没开、去哪开"，别只说一句"失败"。
  password_expiration_unavailable:
    "账号服务没有开启「密码有效期」，暂时无法强制改密；请先在登录体验设置里开启 passwordExpiration。",
  // 组织自我降权（server/http_routes/auth.py 的 PUT /api/auth/members/{id}，P134）：
  // 拦住"最后一名管理员把自己改成只读"——降下去就再也拿不回管理入口了。
  last_admin_self_demotion_forbidden:
    "你是本组织当前唯一的管理员，不能把自己降为只读；请先指定另一位管理员，再改自己的角色。",
  // 建号（P81）：管理员不输密码，所以只可能是"缺用户名/缺邮箱"这类输入问题。
  username_required: "请填写用户名（上限 128 字）。",
  email_required: "请填写合法邮箱：建出来的账号没有密码，用户要靠邮箱自助设置密码。",
  // 会员处置（P79/P80，server/membership_admin.py）
  membership_forbidden: "会员处置需要平台管理员权限。",
  tenant_required: "请填写租户 ID。",
  days_required: "请填写 1 到 3650 之间的天数。",
  expiry_required: "请选择到期日（早于当前到期日＝缩短，但不能早于当前时间）。",
  // 过去日期不是"收回会员"的表达方式：那样只会记成一次成功的调整，权益却没了（P80）。
  expiry_in_the_past: "到期时间必须晚于当前时间：不能把到期日设到已过去的日期。",
  tenant_not_provisioned: "该租户尚未开通，无法发放会员。",
  tenant_inactive: "该租户当前不可用（已挂起或已删除），请先恢复租户。",
};

function backendAccountBannedMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const label = String(source.banLabel || "").trim();
  const suffix = label ? `（${label}）` : "";
  const until = Number(source.bannedUntil);
  if (!Number.isFinite(until) || until <= 0) {
    return `账号已被封禁（未设到期时间），期间无法使用任何功能${suffix}`;
  }
  const when = new Date(until * 1000).toLocaleString("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `账号已被封禁至 ${when}，期间无法使用任何功能${suffix}`;
}

// 预扣被拒（402 quota_exceeded，见 server/services/jobs.py 的 send_ai_job 与
// server/http_routes/viral.py 的 /api/viral/analyze）。服务端在 billing 里给了
// "这次要多少 / 现在还有多少 / 会员是什么状态"，提示就得把这三个说清楚：旧文案把
// "会员已到期"和"积分 余额不足"挤成一句，在从未开通过会员的账号上看到这句
// 只会更困惑（台账 C25）。余额够却仍被拒（并发预占）时不提这两个原因。
// 429 的用量上限也回 quota_exceeded，但不带 billing：这里返回空串让它回退原文。
function backendQuotaExceededMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const billing = source.billing && typeof source.billing === "object" ? source.billing : null;
  const required = Number(billing && billing.requiredCredits);
  if (!billing || !Number.isFinite(required) || required <= 0) return "";
  const rawAvailable = billing.availableCredits;
  const available = Number(rawAvailable);
  const hasAvailable = rawAvailable !== null && rawAvailable !== undefined && Number.isFinite(available);
  const status = String(billing.membershipStatus || "").trim().toLowerCase();
  const membership = status === "expired" ? "会员已到期" : (status === "inactive" ? "尚未开通会员" : "");
  const funding = membership ? "充值或开通会员" : "充值";
  if (!hasAvailable) {
    return `${membership ? `${membership}，且` : ""}本次操作预计需要 ${required} 积分，当前积分不足，请${funding}后再试。`;
  }
  if (available >= required) {
    return `本次操作预计需要 ${required} 积分，当前可用 ${available} 积分；预扣校验未通过，请刷新后重试或联系管理员。`;
  }
  return `${membership ? `${membership}，且 积分 余额不足。` : "积分 余额不足。"}本次操作预计需要 ${required} 积分，当前可用 ${available} 积分，请${funding}后再试。`;
}

// 渠道退款被拒（P102）：后端把上游（支付渠道）自己的错误原文放在 channelError 里。
// 只说"退款失败"会让人以为订单被动过，所以基句必须交代"本单未做任何变更"，
// 再把上游原文附上供财务核对渠道后台。
function backendChannelRefundFailedMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const detail = String(source.channelError || "").trim();
  return detail
    ? `渠道退款未成功，本单未做任何变更（积分未冲正、订单仍为已支付）：${detail}`
    : "渠道退款未成功，本单未做任何变更（积分未冲正、订单仍为已支付）；请核对渠道后台后重试。";
}

// 退款被渠道业务拒绝（422 refund_rejected）：渠道可达但拒绝退款（订单不可退、
// 退款窗口关闭、errcode!=0 或 refund_status=UD）。与 channel_refund_failed 的区别
// 是后者是历史码（502），现在拆成 refund_rejected（业务拒绝）和 billing_unavailable
// （网络失败）两条。channelError 里是渠道自己的中文原文，必须带给财务核对。
function backendRefundRejectedMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const detail = String(source.channelError || "").trim();
  return detail
    ? `渠道拒绝退款，本单未做任何变更（积分未冲正、订单仍为已支付）：${detail}`
    : "渠道拒绝退款，本单未做任何变更（积分未冲正、订单仍为已支付）；请核对渠道后台后重试。";
}

// 渠道退款结果未知（502 refund_outcome_unknown + channelError）：和 refund_rejected 一样，
// 后端把渠道自己的错误原文放在 channelError 里（连接/读取超时这类），财务要到渠道后台核对，
// 就必须看到它——所以基句后面附上原文；取不到原文时退回上面的静态话术。
// 基句与静态那条同源，别只在一边改：改一处两处都要动（这才是"话术收口"）。
function backendRefundOutcomeUnknownMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const detail = String(source.channelError || "").trim();
  const base = "渠道退款结果未知：本单已转为「退款中」，积分已冲正；请到渠道后台核对结果后再处理，勿重复退款";
  return detail ? `${base}：${detail}` : `${base}。`;
}

// 退款场景下的渠道网络/传输失败（503 billing_unavailable + channelError）：
// billing_unavailable 是通用码，但退款链路里后端会在 channelError 附上渠道错误原文。
// 有 channelError 时换成退款语境的话术（"渠道退款服务不可用，本单未做任何变更"），
// 没有 channelError 时回退空串，让 backendErrorCodeMessage 走静态那条通用话术。
function backendBillingUnavailableRefundMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const detail = String(source.channelError || "").trim();
  if (!detail) return "";
  return `渠道退款服务不可用，本单未做任何变更（积分未冲正、订单仍为已支付）：${detail}`;
}

// 改价被拒的话术要带上"哪个框不对"：后端把字段名放在 pricingField（server/billing_service.py
// 的 pricing_violation_result），没有它时用后端那句控制台语境的中文原文（它已点名字段），
// 两者都取不到才回退到上面的静态文案。改价与下单是两件事，话术不能互相借（台账 P114）。
function backendPricingMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const field = String(source.pricingField || "").trim();
  if (field) return `${field}的值不合法，请检查后再保存（改价不影响已支付订单）。`;
  const raw = String(source.error || "").trim();
  return /[\u4e00-\u9fa5]/.test(raw) ? raw : "";
}

// 退款被拒（台账 35.5）：后端在 CreditRefundNotAllowed 异常里已按 3 种场景给了
// 中文具体原因（server/credit_repositories.py）：
//   - 「找不到该订单对应的赠额，无法冲正」（grant 缺失）
//   - 「订单赠额与账本记录不一致，需人工核对」（额度对不上）
//   - 「该订单的积分已被部分使用，整单退款不适用」（剩余<总额，最常见）
// 这些原文会经 server/billing_service.py 的 refund_payment_order 落进响应 data.error，
// 过去前端只用静态文案「当前订单状态不支持退款」盖过去，结果用户在「积分已被部分
// 使用」这种典型场景下完全看不清真实原因，需要客服翻日志才能解释。这里把后端中文
// 原文透传，让用户直接看到「积分已被部分使用」等具体原因；后端取不到中文时退回
// 上面那条静态文案（保持原有兜底语义）。
function backendRefundNotAllowedMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const raw = String(source.error || "").trim();
  return /[\u4e00-\u9fa5]/.test(raw) ? raw : "";
}

// 采集节奏限制：服务端在 error 里写的是「上一次采集还没有结束，请等它完成后再试」这种
// 带原因和剩余秒数的中文，比任何静态话术都准，能取到就原样透传；取不到才编一句退路。
function backendImportNoteRateLimitedMessage(data) {
  const source = data && typeof data === "object" ? data : {};
  const raw = String(source.error || "").trim();
  if (/[\u4e00-\u9fa5]/.test(raw)) return raw;
  const seconds = Math.max(0, Math.round(Number(source.retryAfterSeconds || 0) || 0));
  return seconds > 0
    ? `采集现在不能开始：请等约 ${seconds} 秒后再试。`
    : "采集现在不能开始（节奏限制）：请稍后再试。";
}

// 少数码的话术要带上同一次响应里的动态字段（封禁到期时间），所以除静态话术外
// 还给一个格式化函数：取不到字段就退回上面那条静态文案。仍然只有这一处收口。
const BACKEND_ERROR_FORMATTERS = {
  credit_adjust_over_limit: (data) => String(data.error || "单次调额超过上限，请减少积分数量。").replace(/\bCredits\b/gi, "积分"),
  account_banned: backendAccountBannedMessage,
  quota_exceeded: backendQuotaExceededMessage,
  channel_refund_failed: backendChannelRefundFailedMessage,
  refund_rejected: backendRefundRejectedMessage,
  refund_outcome_unknown: backendRefundOutcomeUnknownMessage,
  billing_unavailable: backendBillingUnavailableRefundMessage,
  billing_pricing_invalid: backendPricingMessage,
  billing_pricing_blank: backendPricingMessage,
  refund_not_allowed: backendRefundNotAllowedMessage,
  import_note_rate_limited: backendImportNoteRateLimitedMessage,
};

function backendErrorCodeMessage(errorCode, data) {
  const code = String(errorCode || "").trim();
  if (!code) return "";
  const formatter = BACKEND_ERROR_FORMATTERS[code];
  if (typeof formatter === "function") {
    const message = String(formatter(data || {}) || "").trim();
    if (message) return message;
  }
  return String(BACKEND_ERROR_MESSAGES[code] || "");
}

// 上游/模型侧的原话大多是英文，措辞还随网关版本变（同一个现象就有 "Upstream response
// stream ended before completion" / "stream disconnected before completion" /
// "missing terminal event" / "incomplete stream" 几种说法）。把英文原话直接摆给用户，
// 等于让人猜"到底是谁的问题"；但也不能一律吞掉——未登记的原话照旧回退原文，保住排障
// 线索（与下面的 INTERNAL_ERROR_DETAIL_MESSAGES 同一条底线）。所以只认"片段"，不比整句。
const UPSTREAM_FAILURE_PLAIN_TEXT = [
  [/ended before completion|stream ended|stream disconnected|incomplete stream|missing terminal event|流式响应未结束/i, "AI 返回中途断了"],
  [/connection reset|连接被重置|连接中断/i, "和 AI 的连接中断了"],
  [/没有返回有效的流式内容|没有返回有效 ?json|返回了无效 ?json|无法解析的 SSE/i, "AI 没返回完整内容"],
  [/达到 ?token 上限|结果被截断/i, "AI 输出太长，被截断了"],
  [/但没有正文|没有可解析的正文|拒绝生成/i, "AI 没返回正文"],
  [/请求超时|timed out|proxy read timeout/i, "AI 响应超时"],
  [/上游无响应/, "AI 服务没有响应"],
  [/上游 ?ai ?服务暂时不可用|http error 50[234]/i, "AI 服务暂时不可用"],
];

// 返回人话；没登记过的原样返回，调用方继续展示原文（别把映射写成万金油）。
function humanizeFailureReason(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  for (const [pattern, plain] of UPSTREAM_FAILURE_PLAIN_TEXT) {
    if (pattern.test(raw)) return plain;
  }
  return raw;
}

// 有些失败原因不是「码」，而是服务端内部持久化层的英文常量：数据库写入失败时
// canonical_base.py / canonical_outbox.py / canonical_usage.py / credit_repositories.py
// 抛出的 "Canonical persistence operation failed" 一族，以及租户投影缺失时的
// "Canonical tenant projection is not provisioned" 一族。它们既没有用户能执行的动作，
// 也不该以英文出现在提示里（现场报障：模板入库失败只看到一句英文）；但又是排障要看的
// 真因，所以只在这里登记的常量才翻成中文，未登记的仍按老约定回退原文——不能被一句
// 万金油吞掉（台账 P4/P66 的同一底线）。
const INTERNAL_ERROR_DETAIL_MESSAGES = {
  "Canonical persistence operation failed": "服务器写入数据失败（存储暂时不可用）",
  "Canonical outbox persistence operation failed": "服务器写入数据失败（存储暂时不可用）",
  "Canonical usage persistence operation failed": "服务器记录用量数据失败（存储暂时不可用）",
  "Credit persistence operation failed": "服务器处理账户数据失败（存储暂时不可用）",
  "Canonical tenant projection is not provisioned": "当前组织的数据空间还没有初始化",
  "Canonical tenant is not active": "当前组织暂时不可用（已挂起或未激活）",
};

// 返回登记过的中文原因（短句，不带"请重试"这类动作，由调用方按场景补）；未登记返回空串，
// 调用方继续按老约定回退原文。
function backendInternalErrorDetail(raw) {
  const text = String(raw || "").trim();
  return text ? String(INTERNAL_ERROR_DETAIL_MESSAGES[text] || "") : "";
}

// ----- js/core/client.js -----
// 刻意未迁移的路由对租户会话返回 503「Tenant route not migrated」；这是迁移中的正常状态，
// 不是故障。统一换成用户看得懂的说明，避免用户在界面上看到内部术语。
const UNMIGRATED_ROUTE_ERROR = "Tenant route not migrated";

function backendErrorMessage(data, status, retryAfterSeconds = 0, meta = {}) {
  // 错误码是后端给的机器可读意图，比 error 里那句给日志看的英文更可靠：有码先按码翻译。
  // 报文一起传进去：个别码（账号封禁）的话术要带上响应里的到期时间。
  const coded = backendErrorCodeMessage(data && data.errorCode, data);
  if (coded) return coded;
  const raw = String((data && data.error) || "").trim();
  // 服务端持久化层那些内部英文常量（"Canonical persistence operation failed" 等）直接显示
  // 对用户没有意义；后端没给码时也必须在这里收口（见 backend-errors.js 的登记表）。
  const internalDetail = backendInternalErrorDetail(raw);
  if (internalDetail) return `${internalDetail}，请稍后重试；若反复失败请联系管理员。`;
  if (status === 503 && raw === UNMIGRATED_ROUTE_ERROR) {
    return "该功能尚未开放（内容中台迁移中），暂不影响其它功能。";
  }
  /* 后端用 Forbidden/Unauthorized 这类英文短语表示权限问题，直接显示对用户没有意义。 */
  if (status === 403) return raw && raw !== "Forbidden" ? raw : "没有权限执行该操作";
  // 401 有两种，话术必须分开：meta.authAttached === false 表示这次请求**没带凭据**
  // （用户根本没登录），那不是"登录状态已失效"——照后者说，用户会去找一个不存在的旧登录态。
  if (status === 401) {
    if (raw && raw !== "Unauthorized") return raw;
    return meta.authAttached === false ? UNAUTHENTICATED_ACTION_MESSAGE : "登录状态已失效，请重新登录";
  }
  // 429 不一定都是限流：应用侧也会用 429 表示用量/配额上限，那种响应带 errorCode 或
  // 可读的 error 原文，下面的原样透传分支会先接住它（别把「本月 AI 调用已达上限」说成
  // 「请求过快」）。只有报文里什么都读不出来时才按限流分档——那多半是 nginx 限流层的
  // 默认 HTML 页，落到 `接口返回 429` 就是把状态码丢给用户（P96）。
  // 与 auth 侧 logto-errors.js 的 429 同一句式：两条通道各自独立成文，改一处记得改另一处。
  return raw || (status === 429 ? rateLimitMessage(retryAfterSeconds, data) : `接口返回 ${status}`);
}

// 429 的等待时间两个来源：响应头 Retry-After，或报文里的 retryAfterSeconds（网关回 JSON 时）。
// 都没有就只说「稍等片刻」——不要在调用点编一个数字，那比不说更误导。
function rateLimitMessage(retryAfterSeconds, data) {
  const seconds = Math.round(Number(retryAfterSeconds || data?.retryAfterSeconds));
  if (!Number.isFinite(seconds) || seconds <= 0) return "请求过于频繁，请稍等片刻后再试。";
  const wait = seconds >= 60 ? `${Math.max(1, Math.round(seconds / 60))} 分钟` : `${seconds} 秒`;
  return `请求过于频繁，请等约 ${wait} 后再试。`;
}

// 429 的可等待时间只在响应头里。取不到就返回 0，话术退化成「稍等片刻」——
// 不要在调用点编一个数字，那比不说更误导。
function retryAfterSecondsFromResponse(response) {
  const raw = String(response?.headers?.get?.("Retry-After") || "").trim();
  if (!raw) return 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds > 0) return Math.round(seconds);
  const at = Date.parse(raw);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, Math.round((at - Date.now()) / 1000));
}

// 调用点偶尔要靠错误码区分「同码不同因」的场景（知识库把 resource_not_found 当"尚未就绪"
// 呈现空态），所以抛出的错误对象上要带码和原始报文，而不只是翻译好的那句话。
function backendError(data, status, response = null) {
  // P40（跨标签登录态）需要知道这次请求带没带凭据、属于哪个组织；401 的话术还要区分
  // "没登录"与"登录失效"（见 backendErrorMessage）；429 的重试等待时间来自响应头。
  // 这些事实都在同一个 response 上，所以第三个参数统一收 response，调用方不必各传一份。
  const meta = {
    authAttached: response?.xhsAuthAttached,
    authOrganization: response?.xhsAuthOrganization,
    method: response?.xhsRequestMethod,
  };
  const error = new Error(backendErrorMessage(data, status, retryAfterSecondsFromResponse(response), meta));
  error.status = status;
  error.payload = data;
  error.errorCode = String((data && data.errorCode) || "");
  invalidateSessionOnUnauthorized(status, data, meta);
  // 没登录的人点了一个会写数据的动作：上面那句错误已经说清了原因，这里再把登录入口递到
  // 手上（见 auth/login-gate.js）。被动读（开机时那几个 GET）不走这条。
  if (typeof handleUnauthenticatedBackendAction === "function") {
    handleUnauthenticatedBackendAction(status, meta);
  }
  return error;
}

// 401 = 服务端已经不认这个会话了：登出发生在别的标签、会话被撤销、令牌过期。界面必须
// 一起回退到未登录态，否则会停在"顶部还是登录态、接口全失效"的分裂态（P40）。跨标签
// 广播在存储不可用等场景下会丢，这里是兜底。
// 排除「接口要求重新验证」：那是账号侧的一次性校验要求，重新登录不是它的处置方式。
const SESSION_EXPIRED_EXCLUDED_CODES = new Set(["verification_record.permission_denied"]);
const SESSION_EXPIRED_DEBOUNCE_MS = 5000;
let sessionExpiredHandledAt = 0;

function invalidateSessionOnUnauthorized(status, data, meta = {}) {
  if (Number(status) !== 401) return;
  if (SESSION_EXPIRED_EXCLUDED_CODES.has(String((data && data.errorCode) || "").trim())) return;
  // 只有**明确带了凭据**的 401 才说明"服务端不认这个会话"。三种都不清本地会话：
  //   ①authAttached === false：这次请求根本没带令牌（启动早期还没取到令牌、或登录回调正在
  //     换令牌）。清掉的会是同一份 sessionStorage 里正在使用的 signInSession/PKCE，回调随即
  //     失败，登录永远完不成；
  //   ②authAttached === true 但没有组织作用域（登录回调刚完成、组织还没激活时就是这种）：
  //     后端对无组织令牌一律回 401（auth_context.py 要求 tenant 身份），与"会话被撤销"无关；
  //   ③authAttached === undefined：**不知道带没带**。用裸 fetch（而非 backendFetch）的调用点
  //     响应上没有这个标记——扩展交接那条链路就是（low-fan.js 的 handoff/consume）。旧判据
  //     只写了 === false / === true，undefined 两个都不命中，直接落到下面清会话：该端点只要
  //     回一次 401（它靠一次性 nonce 鉴权，本就不带会话令牌），已登录用户就会被登出。
  // ①②照旧清会话的话，用户看到的是"密码正确、却立刻被判未登录（Not authenticated.）"并被
  // 踢回首页——这正是管理员一直进不去的现场。
  if (meta.authAttached !== true) {
    if (meta.authAttached === undefined) {
      // 不知道就不动作，但要留痕：这条说明有调用点绕过了 backendFetch。
      console.warn("401 without auth-context markers; session left intact", {
        code: String((data && data.errorCode) || ""),
      });
    }
    return;
  }
  if (!String(meta.authOrganization || "")) return;
  const auth = globalThis.xhsSpaAuth;
  if (typeof auth?.signOutLocal !== "function") return;
  const now = Date.now();
  // 一个页面同时发多个请求时会有多个 401，只处理第一次。
  if (now - sessionExpiredHandledAt < SESSION_EXPIRED_DEBOUNCE_MS) return;
  sessionExpiredHandledAt = now;
  void (async () => {
    try {
      if (!(await auth.isAuthenticated?.().catch(() => false))) return;
      await auth.signOutLocal();
      toast("登录状态已失效，请重新登录", { duration: 5200 });
    } catch (error) {
      console.error("Session invalidation failed", error);
    }
  })();
}

function toast(message, options = {}) {
  const node = $("#toast");
  node.textContent = message;
  node.classList.toggle("error", options.kind === "error");
  node.classList.add("show");
  window.clearTimeout(toast.timer);
  toast.timer = window.setTimeout(() => {
    node.classList.remove("show");
    // 隐藏只是 opacity:0，文本不清空会被页面文本抓取与读屏软件当成当前提示。
    node.textContent = "";
  }, options.duration || 2200);
}

function setButtonLoading(button, loadingText) {
  if (!button) return () => {};
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = loadingText;
  return () => {
    button.disabled = false;
    button.textContent = originalText;
  };
}

// 小红书平台的发布上限，站内唯一的定义处：编辑区计数、加入发布列表、跳转发布页之前都读这里。
// 这几处过去各写各的（甚至根本没写），结果超限内容一路走到小红书发布页才被对方拒掉。
// 标题 20 字、正文 1000 字（含话题）——话题会被扩展追加进正文编辑器，所以一起算。
const XHS_TITLE_MAX_LENGTH = 20;
const XHS_BODY_MAX_LENGTH = 1000;
// 话题上限 8 个：这个数原本只活在 normalizeXhsTags 的 slice(0, 8) 里（AI 生成/链接导入时
// 静默截断，编辑区却从不提上限，用户只能自己发现标签莫名少了）。提到这里当唯一口径，
// 截断与计数共用同一个数。
const XHS_TAG_MAX_LENGTH = 8;

// 来源类目留空时后端会把它规整成「未分类」当展示兜底（选题详情照常显示，见
// viral/library.js 的「来源类目」一行）。它不是内容话题：任何生成、兜底或直接组装
// 标签的路径都不得把它写进草稿，判据集中在这一处（服务端同名常量在 viral_factory.py）。
const UNCATEGORIZED_CATEGORY = "未分类";

function isUncategorizedCategory(value) {
  return String(value ?? "").trim().replace(/^#+/, "") === UNCATEGORIZED_CATEGORY;
}

// 编辑区计数标签上「印出来」的上限，与上面三个真正的判据分开：真正的上限决定截断和超限标红，
// 这里只决定标签上写出来的数字（产品口径：标题 20 / 正文 1000 / 标签 10）。
// 话题这两条现在是分歧的——展示写 10，真正保留的仍是 8 个，所以「9/10 个」也可能被标红并丢掉
// 一个。标红与截断永远读真的那条（XHS_TAG_MAX_LENGTH），改展示不会动到它们。
const XHS_DISPLAY_TITLE_LIMIT = 20;
const XHS_DISPLAY_BODY_LIMIT = 1000;
const XHS_DISPLAY_TAG_LIMIT = 10;
// 图片这一条展示数与真实闸门一致（单篇 18 张，上传/加入草稿那边也是 18），只是那几个
// 地方还写着字面量，没有共用同一个名字，所以这里只补展示，不去动它们的判据。
const XHS_DISPLAY_IMAGE_LIMIT = 18;

// 同一份草稿上的生成运行态。每个生成入口都往这里登记、也都从这里读，不能再各自只锁
// 自己的按钮或 controller。按"会写哪份数据"分三类：
//   full  —— 一键生成整篇：整篇重写，和任何分区都互斥。
//   block —— 单个分区（标题 / 正文 / 标签 / 图片）的生成，按分区组计数。
//   image —— 单图生成队列（队列内部自己按 limit 并发）。
// 分区之间各写各的字段（标题写 title、正文写 body…），所以**不同分区可以同时跑**；只有写同一
// 份数据的分区才互斥：封面 / 内页 / 整组图片重绘都在改 draft.images，归为同一个 image 组。
// 这样用户在标题还在生成时点「生成正文」能真的跑起来，而不是被一个全局锁挡住。
const activeDraftGenerationRuns = new Map();

// 账号（当前浏览器会话）维度的 AI 任务互斥：同一时间只允许一个 AI 任务，**跨草稿**。
// 上面那份按草稿键的注册表只挡同一份草稿；用户实测的两份草稿各跑一个任务（一份在生图、
// 另一份在搭建）不会被它挡住。这层判据挂在 draftGenerationBusy / draftFullGenerationActive
// 这两个既有入口判据上，所有生成入口与按钮置灰不必逐处再改。计数按草稿键记，同一草稿的
// 多个分区任务不会互相解除对方的锁。
const activeAccountAiRuns = new Map();

function accountAiTaskOnOtherDraft(draftId) {
  const own = String(draftId || "");
  for (const [key, count] of activeAccountAiRuns) {
    if (count > 0 && key !== own) return true;
  }
  return false;
}

function accountAiTaskOwnerDraftId(draftId) {
  const own = String(draftId || "");
  for (const [key, count] of activeAccountAiRuns) {
    if (count > 0 && key !== own) return key;
  }
  return "";
}

function beginAccountAiRun(draftId) {
  const key = String(draftId || "");
  activeAccountAiRuns.set(key, (activeAccountAiRuns.get(key) || 0) + 1);
}

function endAccountAiRun(draftId) {
  const key = String(draftId || "");
  const next = (activeAccountAiRuns.get(key) || 0) - 1;
  if (next > 0) activeAccountAiRuns.set(key, next);
  else activeAccountAiRuns.delete(key);
}

// 点击时该说什么：别的草稿在跑时点名是哪一份，别让用户对着一个没反应的按钮猜。
function aiTaskRunningElsewhereMessage(draftId) {
  const ownerDraftId = accountAiTaskOwnerDraftId(draftId);
  if (!ownerDraftId) return "";
  const drafts = typeof state !== "undefined" && Array.isArray(state.drafts) ? state.drafts : [];
  const title = String(drafts.find((item) => String(item?.id || "") === ownerDraftId)?.title || "").trim();
  return title
    ? `「${title}」正在跑 AI 任务，请等它结束或先停止后再试`
    : "另一份笔记上已有 AI 任务在跑，请等它结束或先停止后再试";
}

const DRAFT_GENERATION_GROUPS = {
  title: "title",
  body: "body",
  tags: "tags",
  cover: "image",
  inner: "image",
  image: "image",
};

function draftGenerationGroup(block) {
  return DRAFT_GENERATION_GROUPS[String(block || "")] || "";
}

function draftGenerationCounters(draftId) {
  const key = String(draftId || "");
  let counters = activeDraftGenerationRuns.get(key);
  if (!counters) {
    counters = { full: 0, image: 0, blocks: {} };
    activeDraftGenerationRuns.set(key, counters);
  }
  return counters;
}

// 某个分区是否正在生成。图片组把单图队列也算进来：两条流程都在改 draft.images。
function draftBlockGenerationBusy(draftId, block) {
  const counters = activeDraftGenerationRuns.get(String(draftId || ""));
  const group = draftGenerationGroup(block);
  if (!counters || !group) return false;
  if ((counters.blocks[group] || 0) > 0) return true;
  return group === "image" && counters.image > 0;
}

function draftFullGenerationActive(draftId) {
  const counters = activeDraftGenerationRuns.get(String(draftId || ""));
  return Boolean(counters && counters.full > 0);
}

// 供"自己已经持有整篇运行"的批处理入口检查是否有单图任务插进来。
function draftImageGenerationActive(draftId) {
  const counters = activeDraftGenerationRuns.get(String(draftId || ""));
  return Boolean(counters && counters.image > 0);
}

// 只按这份草稿自己的运行态判断（按钮置灰用）。跨草稿的账号级互斥不参与置灰：
// 灰着又不说明原因，用户只会以为按钮坏了；那类冲突一律留到点击时给一句点名提示。
function draftGenerationBusyHere(draftId, kind = "exclusive") {
  const counters = activeDraftGenerationRuns.get(String(draftId || ""));
  if (!counters) return false;
  // 单图入口只和"整篇生成 / 图片分区"互斥：别的单图任务可以共享这份草稿（队列自己按 limit
  // 并发），文本分区在跑也不挡它（各写各的字段）。
  if (kind === "image") return counters.full > 0 || (counters.blocks.image || 0) > 0;
  return counters.full > 0
    || counters.image > 0
    || Object.values(counters.blocks).some((count) => count > 0);
}

function draftGenerationBusy(draftId, kind = "exclusive") {
  // 账号维度：别的草稿有 AI 任务在跑也算忙（入口据此给"哪份笔记在跑"的点击提示），
  // 但按钮置灰一律走上面那份只看本草稿的判据。
  if (accountAiTaskOnOtherDraft(draftId)) return true;
  return draftGenerationBusyHere(draftId, kind);
}

function beginDraftGenerationRun(draftId, kind = "exclusive", block = "") {
  const key = String(draftId || "");
  beginAccountAiRun(key);
  const counters = draftGenerationCounters(key);
  if (kind === "block") {
    const group = draftGenerationGroup(block) || "image";
    counters.blocks[group] = (counters.blocks[group] || 0) + 1;
    return { key, kind: "block", group };
  }
  const runKind = kind === "image" ? "image" : "full";
  counters[runKind] += 1;
  return { key, kind: runKind, group: "" };
}

function endDraftGenerationRun(run) {
  if (!run) return;
  endAccountAiRun(run.key);
  const counters = activeDraftGenerationRuns.get(run.key);
  if (!counters) return;
  if (run.kind === "block") {
    const group = run.group || "";
    if (group) counters.blocks[group] = Math.max(0, (counters.blocks[group] || 0) - 1);
  } else {
    counters[run.kind] = Math.max(0, (counters[run.kind] || 0) - 1);
  }
  const idle = counters.full <= 0
    && counters.image <= 0
    && !Object.values(counters.blocks).some((count) => count > 0);
  if (idle) activeDraftGenerationRuns.delete(run.key);
}

function setButtonCancelable(button, stopText, stopTitle) {
  if (!button) return () => {};
  const original = {
    text: button.textContent,
    title: button.title,
    ariaBusy: button.getAttribute("aria-busy"),
  };
  button.disabled = false;
  button.textContent = stopText;
  button.title = stopTitle || stopText;
  button.setAttribute("aria-busy", "true");
  button.classList.add("is-loading", "is-cancel-action");
  return () => {
    button.disabled = false;
    button.textContent = original.text;
    button.title = original.title;
    if (original.ariaBusy === null) button.removeAttribute("aria-busy");
    else button.setAttribute("aria-busy", original.ariaBusy);
    button.classList.remove("is-loading", "is-cancel-action");
  };
}

function abortError(message = "操作已停止") {
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}

function isAbortError(error) {
  return error?.name === "AbortError";
}

function isImagePageTimeoutError(error) {
  return /(?:timed?\s*out|timeout|超时|etimedout|und_err_(?:connect_)?timeout)/i.test(String(error?.message || ""));
}

function waitForImagePageRetry(delay, signal) {
  throwIfAborted(signal);
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(done, delay);
    function done() {
      signal?.removeEventListener("abort", stopped);
      resolve();
    }
    function stopped() {
      window.clearTimeout(timer);
      reject(abortError());
    }
    signal?.addEventListener("abort", stopped, { once: true });
  });
}

async function renderAiPageWithRetry(draft, job, note, options = {}) {
  let attempt = 0;
  while (true) {
    throwIfAborted(options.signal);
    try {
      return await postBackend("/api/templates/render-ai-page", {
        templateId: job.templateId,
        pageIndex: job.pageIndex,
        contentPart: job.contentPart,
        ...renderPageSelectorForJob(draft, job),
        note,
      }, { signal: options.signal });
    } catch (error) {
      if (isAbortError(error)) throw error;
      const retryDelay = IMAGE_PAGE_TIMEOUT_RETRY_DELAYS[attempt];
      if (!isImagePageTimeoutError(error) || retryDelay === undefined) {
        error.imageJobLabel = job.label;
        error.imageJobAttempts = attempt + 1;
        error.imageTimedOut = isImagePageTimeoutError(error);
        throw error;
      }
      attempt += 1;
      if (typeof options.onRetry === "function") {
        options.onRetry({ job, attempt: attempt + 1, delay: retryDelay, error });
      }
      await waitForImagePageRetry(retryDelay, options.signal);
    }
  }
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

// 上游接口是平台统一配置，入口只在平台管理员手里。给改不动的人弹出那个面板只是摆一个保存必被
// 回滚的表单，所以"打开面板"这件事统一从这里过：能配的人打开，不能配的人拿到一句该找谁的话。
// 单机/兼容模式没有鉴权，本机操作员仍算能配（同 canConfigureOrgAi）。
function openPlatformAiConfigOrExplain(type = "", reason = "尚未配置") {
  if (loginGateBlocksFeature(type === "image" ? "生图 AI 配置" : "文本 AI 配置")) return false;
  if (typeof canConfigurePlatformAi === "function" && !canConfigurePlatformAi()) {
    const label = type === "image" ? "生图" : "文本";
    toast(`上游${label} AI 由平台统一配置，当前${reason}；请联系平台管理员在「治理 → AI 上游与后处理」中配置。`);
    return false;
  }
  openAiConfig();
  return true;
}

// 闸门的同步入口在这里统一收一次：未登录 → 提示并拉起登录，返回 true 表示"别做这件事"。
// 状态未知时（启动早期）返回 false 放行，宁可让后端回它真实的 401。
function loginGateBlocksFeature(actionLabel) {
  return typeof requireSignedInForActionSync === "function" && !requireSignedInForActionSync(actionLabel);
}

function ensureAiConfigured(type) {
  // 未登录时 state.aiConfig 必然是空的（开机那次 /api/config 会 401），照着"没配置"去解释
  // 就会把用户引到平台管理员的配置面板——他要做的其实是先登录（见 auth/login-gate.js）。
  if (loginGateBlocksFeature(modelTypeLabel(type))) return false;
  const config = state.aiConfig?.[type] || {};
  const hasApiKey = Boolean(config.apiKey?.trim() || config.hasApiKey);
  if (config.apiUrl?.trim() && config.model?.trim() && hasApiKey) return true;
  const opened = openPlatformAiConfigOrExplain(type);
  if (opened) {
    toast(type === "image" ? "请完整配置生图 AI 的地址、密钥和模型" : "请完整配置文本 AI 的地址、密钥和模型");
  }
  return false;
}

function modelTypeLabel(type) {
  return type === "image" ? "生图 AI" : "文本 AI";
}

function modelValue(type) {
  return $(`#${type}Model`).value.trim();
}

function setModelValue(type, value) {
  const input = $(`#${type}Model`);
  input.value = value || "";
}

function draftPayload(draft) {
  return {
    workflow: draftWorkflow(draft),
    title: draft.title || "",
    body: draft.body || "",
    tags: draft.tags || "",
    source: draftSourceLabel(draft),
    origin: draft.origin || "",
    imageCount: (draft.images || []).length,
    knowledge: knowledgeForRequest(draft),
  };
}

async function postBackend(path, payload, options = {}) {
  let response;
  try {
    response = await backendFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: options.signal,
      keepalive: options.keepalive === true,
      credentials: options.credentials,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new Error(`无法连接后端（${backendBaseUrl}），请确认服务正在运行：${error.message || "Failed to fetch"}`);
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw backendError(data, response.status, response);
  }
  // 不止 /api/state 会推进 stateRevision：建草稿、加发布项、删模板（顺带清草稿引用）
  // 都会。只在 /api/state 路径对齐的话，这些接口之后的第一次自动保存必然 409，
  // 还会被误判成"另一个窗口改过"（现场那条冲突提示）。helper 对没有 revision 的
  // 响应天然无副作用，所以这里一律对齐。
  applyBackendStateRevision(data);
  if (data.async && data.statusUrl && options.waitForJob !== false) {
    if (typeof options.onJobStart === "function") options.onJobStart(data);
    return waitForBackendJob(data.statusUrl, options);
  }
  return data;
}

// 插件包下载也要过一遍封禁面（P132）。链接是 <a href="/extension-download.zip" download>，
// 浏览器发的是一条不带 Authorization 的裸导航，服务端那道封禁拦截根本看不到调用方是谁，
// 于是被封的账号照样能把包下走。这里把点击改成带凭据的 fetch：被拒时给话术，
// 而不是静默触发一次必然失败的导航（href 保留，脚本没跑起来时退回原行为）。
const EXTENSION_DOWNLOAD_PATH = "/extension-download.zip";

function extensionDownloadFileName(response) {
  const disposition = String(response.headers.get("Content-Disposition") || "");
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : "xhs-workstation-extension.zip";
}

async function downloadExtensionPackage() {
  let response;
  try {
    response = await backendFetch(EXTENSION_DOWNLOAD_PATH);
  } catch (error) {
    toast(`插件包下载失败：${error?.message || "无法连接后端"}`, { kind: "error", duration: 6000 });
    return;
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    toast(backendError(payload, response.status, response).message, { kind: "error", duration: 6000 });
    return;
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = extensionDownloadFileName(response);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // 立刻回收会让部分浏览器拿不到数据；下载是几秒内的事，留一分钟足够。
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function bindExtensionDownload() {
  document.addEventListener("click", (event) => {
    // 选择器从常量拼，别在这儿再把路径写一遍：写两遍就会漂，而漂了之后这个监听器
    // 匹配不上首页那个 <a>，点击退回浏览器的裸导航——被封账号照样能把包下走（P132）。
    const link = event.target?.closest?.(`a[href="${EXTENSION_DOWNLOAD_PATH}"]`);
    if (!link) return;
    event.preventDefault();
    void downloadExtensionPackage();
  });
}

// 200 但响应体读不出来（空 body、被截断、不是 JSON）不等于"服务端说这份数据是空的"。
// 这里过去把解析失败吞成 {}，调用方就把"这一次没读到"当成合法空数据走完：实测
// `GET /api/state` 连续两次 200 + 空 body 之后，工作台界面全空、hydrated 被置真、本机恢复
// 快照被清掉，紧接着的自动保存又把这套空状态写回服务端（P85 / RG4-R5）。空响应只能按
// "这次读失败了"处理，让调用方走自己的错误路径（重试或明确报错）。
function unreadableBackendPayloadError(response, detail) {
  // 借用 backendError 的既有形状：status/payload/errorCode 都在，401 清会话那条判定也照旧生效。
  const error = backendError({}, response.status, response);
  error.message = `服务端响应无法读取（HTTP ${response.status}）：${detail}`;
  return error;
}

async function readBackendPayload(response) {
  const raw = await response.text().catch(() => null);
  if (raw === null) throw unreadableBackendPayloadError(response, "响应体读取失败");
  const text = raw.trim();
  if (!text) throw unreadableBackendPayloadError(response, "响应体为空");
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (_error) {
    throw unreadableBackendPayloadError(response, "响应体不是 JSON");
  }
  if (!parsed || typeof parsed !== "object") throw unreadableBackendPayloadError(response, "响应体不是 JSON 对象");
  return parsed;
}

async function getBackend(path) {
  const response = await backendFetch(path);
  const data = await readBackendPayload(response);
  if (!response.ok || data.ok === false) {
    throw backendError(data, response.status, response);
  }
  return data;
}

// A freshly submitted job is authorized through OpenFGA, and those
// registration tuples are published asynchronously, so the very first status
// read can answer "Resource not found" while the job is already running.
// Consecutive status failures are retried inside this bounded window before the
// operation is reported as failed; a successful read restores the budget.
const JOB_STATUS_POLL_DELAY_MS = 2500;
// 固定间隔会把长任务拖成"持续轰炸"：实测模板整页 p50 62s、生图 52s，而固定 2.5s
// 轮询意味着一个模板任务要回轮 25 次、生图 21 次。后端是单进程，实测轮询吞吐上限
// 只有约 2.9 rps —— 10 个用户同时在等任务，光是"问好了没"就能把系统打满。
// 这里改成退避：首次仍用 2500ms（短任务体验完全不变），之后每次 ×1.5，上限 8s。
// 实测一个 62s 的模板任务，轮询次数从 25 次降到约 8 次（少 68%）。
const JOB_STATUS_POLL_DELAY_MAX_MS = 8000;
const JOB_STATUS_POLL_BACKOFF = 1.5;
const JOB_STATUS_RETRY_ATTEMPTS = 6;
const JOB_STATUS_RETRY_CODES = new Set([404, 408, 425, 429, 500, 502, 503, 504]);

function waitForJobStatusPoll(delay, signal) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, delay);
    if (!signal) return;
    signal.addEventListener("abort", () => {
      window.clearTimeout(timer);
      reject(new DOMException("The operation was aborted.", "AbortError"));
    }, { once: true });
  });
}

// 任务失败时状态接口只回错误码（例如 execution_failed），单看代码无法判断是哪一步出了问题。
// 失败前最后一次进度文案本来就会随状态一起下发，用它把"失败在哪一步"补回来，
// 否则用户只能看到一句没有信息量的错误码，服务端日志又没开文件日志。
// 已登记的码先翻成中文（execution_failed 这类英文常量不该出现在用户面前）；
// 没登记的码仍回退原文，保住"未知故障可排障"这条底线。
function jobFailureMessage(data, fallback = "任务失败") {
  const raw = String(data?.error || "").trim();
  // 老约定：状态接口没给 errorCode 时，error 原文本身就是机器码。
  const code = String(data?.errorCode || "").trim() || raw;
  const progress = data?.progress && typeof data.progress === "object" ? data.progress : {};
  const stage = String(progress.message || "").trim();
  const mapped = backendErrorCodeMessage(code);
  // 服务端内部持久化层的英文常量（"Canonical persistence operation failed" 等）先翻成中文：
  // 英文丢给用户看不懂，压成 execution_failed 又只剩一句"任务失败"（见 backend-errors.js）。
  const internalDetail = backendInternalErrorDetail(raw);
  // 登记过的码只是"人话版兜底"，真正的原因在 error 原文里：execution_failed 这类通用码本身
  // 不带任何信息（上游 403、模型只回了推理内容没回正文……），只显示翻译等于把唯一的线索丢掉。
  // 原文与码相同、或原文自己就是登记码、或已是上面登记的内部常量时不重复。
  const rawDetail = raw && raw !== code && raw !== mapped && !backendErrorCodeMessage(raw) && !internalDetail ? raw : "";
  // 逐页任务（封面/内页模板拆解）的"因"在页级状态里。这里整段**只报页**：
  // `progress.message` 是收尾闸门（"正在执行 7 月 28 日版兼容校验"），把它拼成"失败于"
  // 会让人以为校验本身坏了——真正的结论是"第 7 页没拆完，所以不许入库"（现场报障：用户和
  // 客服用同一句话来问"这是什么意思"）。页级原因还要过一遍人话映射，否则用户看到的是上游
  // 英文原话（"Upstream response stream ended before completion"）。
  const failedPages = (Array.isArray(progress.pageStatuses) ? progress.pageStatuses : [])
    .filter((item) => item && String(item.status || "") === "failed");
  if (failedPages.length) {
    const pages = [...new Set(failedPages.map((item) => Number(item.page) || 1))].sort((a, b) => a - b);
    const reason = humanizeFailureReason(
      (failedPages.find((item) => String(item.message || "").trim()) || {}).message
    );
    // 这一步专用的登记码（如 no_committable_pages）本身就是完整的一句话、并且自带下一步，
    // 不要和通用句式叠成两句；把页级信息挂在它后面即可。泛化的 execution_failed 只是
    // "某一步炸了"，在这句话里没有信息量，直接用页级句式。
    if (mapped && code !== "execution_failed") {
      const detail = pages.map((page) => `第 ${page} 页${reason ? `：${reason}` : ""}`).join("；");
      return `${mapped}（${detail}）`;
    }
    const who = `${pages.map((page) => `第 ${page} 页`).join("、")}没拆完`;
    const why = reason ? `（${reason}）` : "";
    return `${who}${why}，整份模板暂时不能入库。请重试失败页，或先保留已完成的页面入库。`;
  }
  const detail = internalDetail || rawDetail;
  const base = mapped || internalDetail || raw || code || String(fallback || "").trim() || "任务失败";
  const notes = [];
  if (stage) notes.push(`失败于：${stage}`);
  if (detail && !base.includes(detail)) notes.push(`原因：${detail}`);
  return notes.length ? `${base}（${notes.join("；")}）` : base;
}

// A task's terminal state is when the credits actually move (settle on success,
// release on failure/cancel), but the balance displays are event-driven.  Every
// terminal state therefore announces itself so the header/sidebar refresh
// instead of keeping the number read at boot (台账 P16).  A custom event keeps
// this file free of any dependency on the billing module (load order).
function notifyJobFinished(status, data) {
  try {
    window.dispatchEvent(new CustomEvent("xhs-job-finished", { detail: { status, data } }));
  } catch (_error) {
    // Announcing the end must never turn a finished task into an error.
  }
}

async function waitForBackendJob(statusUrl, options = {}) {
  const { signal, onProgress } = options;
  const timeoutMinutes = Math.max(1, Number(options.timeoutMinutes) || 15);
  const timeoutLabel = String(options.timeoutLabel || "任务");
  const deadline = Date.now() + timeoutMinutes * 60 * 1000;
  let retriesLeft = JOB_STATUS_RETRY_ATTEMPTS;
  let pollDelay = JOB_STATUS_POLL_DELAY_MS;
  // 单调递增，不因一次成功而重置：长任务本来就该越问越稀，重置会让退避失效。
  const nextPollDelay = () => {
    const current = pollDelay;
    pollDelay = Math.min(
      JOB_STATUS_POLL_DELAY_MAX_MS,
      Math.round(pollDelay * JOB_STATUS_POLL_BACKOFF),
    );
    return current;
  };
  while (Date.now() < deadline) {
    let response;
    let data = {};
    try {
      response = await backendFetch(statusUrl, { signal });
      data = await response.json().catch(() => ({}));
    } catch (error) {
      if (isAbortError(error) || retriesLeft <= 0) throw error;
      retriesLeft -= 1;
      await waitForJobStatusPoll(nextPollDelay(), signal);
      continue;
    }
    const completedWithItemResults = data.status === "completed" && Array.isArray(data.items);
    if (!response.ok || (data.ok === false && !completedWithItemResults)) {
      if (!JOB_STATUS_RETRY_CODES.has(response.status) || retriesLeft <= 0) {
        throw backendError(data, response.status, response);
      }
      retriesLeft -= 1;
      await waitForJobStatusPoll(nextPollDelay(), signal);
      continue;
    }
    retriesLeft = JOB_STATUS_RETRY_ATTEMPTS;
    if (data.progress && typeof onProgress === "function") onProgress(data.progress, data);
    if (data.status === "completed") {
      notifyJobFinished("completed", data);
      return data;
    }
    if (data.status === "cancelled") {
      notifyJobFinished("cancelled", data);
      throw abortError(data.error || "任务已中断");
    }
    if (data.status === "failed") {
      notifyJobFinished("failed", data);
      throw new Error(jobFailureMessage(data, "爆款分析任务失败"));
    }
    await waitForJobStatusPoll(nextPollDelay(), signal);
  }
  // 超时只说明客户端不看了：服务端可能刚好在这一刻结算完，余额仍要刷一次。
  notifyJobFinished("timeout", null);
  throw new Error(`${timeoutLabel}超过 ${timeoutMinutes} 分钟仍未完成，请检查服务器 AI 服务日志`);
}

async function deleteBackend(path) {
  const response = await backendFetch(path, { method: "DELETE" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw backendError(data, response.status, response);
  }
  // 删模板可能顺带清草稿引用并推进 stateRevision，服务端会把新值回带。
  applyBackendStateRevision(data);
  return data;
}

async function textAiImageInputs(draft, signal) {
  const mode = draft?.promptTemplateMode || promptModeForDraft(draft?.source, draft?.workflow);
  if (mode !== "image") return [];
  const sources = normalizeImages(draft?.images || []).filter(Boolean).slice(0, 3);
  if (!sources.length) return [];
  const inputs = await Promise.all(sources.map(async (src) => {
    try {
      return await imageInputSourceForAi(src, signal);
    } catch (error) {
      if (isAbortError(error)) throw error;
      return "";
    }
  }));
  return inputs.filter(Boolean);
}

async function runTextAi(task, prompt, draft, options = {}) {
  const imageInputs = options.imageInputs || await textAiImageInputs(draft, options.signal);
  const data = await postBackend("/api/ai/text", {
    task,
    prompt,
    draft: draftPayload(draft),
    knowledge: knowledgeForRequest(draft),
    imageInputs,
  }, options);
  return cleanAiText(data.text || "");
}

async function runTextAiBundle(prompts, draft, options = {}) {
  const imageInputs = options.imageInputs || await textAiImageInputs(draft, options.signal);
  const data = await postBackend("/api/ai/text-bundle", {
    prompts,
    draft: draftPayload(draft),
    knowledge: knowledgeForRequest(draft),
    imageInputs,
  }, options);
  return data.content && typeof data.content === "object" ? data.content : {};
}

async function runImageAi(prompt, options = {}) {
  const inputImages = options.inputImages || [];
  const data = await postBackend("/api/ai/image", {
    prompt,
    count: options.count || 1,
    size: options.size || "1024x1536",
    quality: options.quality || "medium",
    inputImages,
    preferEdit: options.preferEdit ?? inputImages.length > 0,
    // 站内生成要求"按租户配置处理后再出图"（面板里关掉的项就不做），并且后处理失败就让
    // 请求报错——不要把带供应商水印的图当成品返回。
    requireWatermarkRemoval: true,
  }, options);
  const images = data.images || [];
  const watermarkReports = Array.isArray(data.watermarkReports) ? data.watermarkReports : [];
  return {
    images,
    watermarkReports: images.map((_, index) => watermarkReports[index] || null),
  };
}

async function imageInputSourceForAi(src, signal) {
  const value = String(src || "");
  if (!value.startsWith("blob:")) return value;
  const response = await fetch(value, { signal });
  if (!response.ok) throw new Error("本地参考图读取失败");
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const abort = () => {
      reader.abort();
      reject(new DOMException("已停止读取本地图片", "AbortError"));
    };
    signal?.addEventListener("abort", abort, { once: true });
    reader.onload = () => {
      signal?.removeEventListener("abort", abort);
      resolve(String(reader.result || ""));
    };
    reader.onerror = () => {
      signal?.removeEventListener("abort", abort);
      reject(reader.error || new Error("本地参考图读取失败"));
    };
    reader.readAsDataURL(blob);
  });
}

function watermarkStatus(report) {
  if (!report || typeof report !== "object") return null;
  if (report.ok === false && report.retainedOriginal) {
    return { text: "水印处理失败 · 已保留原图", tone: "error", symbol: "!" };
  }
  if (Array.isArray(report.visibleRemoved) && report.visibleRemoved.length) {
    return { text: "已去除可见水印", tone: "success", symbol: "✓" };
  }
  if (report.metadata) {
    return { text: "ai水印已清理", tone: "success", symbol: "✓" };
  }
  if (report.processed) {
    return { text: "水印检查已完成", tone: "neutral", symbol: "✓" };
  }
  return null;
}

function renderWatermarkStatus(report) {
  const status = watermarkStatus(report);
  if (!status) return "";
  const completionLabel = status.tone === "success" ? `已完成去水印处理：${status.text}` : status.text;
  return `<span class="watermark-status ${status.tone}" title="${escapeHtml(completionLabel)}">${status.symbol} ${escapeHtml(status.text)}</span>`;
}

function aiProviderRequestConfig(type) {
  const provider = state.aiConfig?.[type] || {};
  const config = {
    apiUrl: provider.apiUrl || "",
    model: provider.model || "",
  };
  const apiKey = apiKeyDraftValue(type);
  if (apiKey !== undefined) config.apiKey = apiKey;
  return config;
}

async function putBackend(path, payload, options = {}) {
  const response = await backendFetch(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) throw backendError(data, response.status, response);
  if (data.async && data.statusUrl) return waitForBackendJob(data.statusUrl, options);
  return data;
}

function aiConfigUpdatePayload({ includeApiKeyChanges = false } = {}) {
  const providerConfig = (type) => {
    const provider = state.aiConfig?.[type] || {};
    const result = {
      apiUrl: provider.apiUrl || "",
      model: provider.model || "",
      models: Array.isArray(provider.models) ? provider.models : [],
    };
    if (includeApiKeyChanges && confirmedApiKeyTypes.has(type)) {
      if (provider.apiKey) result.apiKey = provider.apiKey;
      else result.clearApiKey = true;
    }
    return result;
  };
  // 这里只发这个面板管的东西：上游文本/生图接口与模型。知识库连接是组织级配置，由
  // 「团队治理 → 知识库连接」自己提交（web/js/org/knowledge-settings.js）；生图水印是平台侧
  // 口径，后端只接受平台管理员的改动。少发一段就不会顺手把别人管的那段覆盖掉。
  return {
    text: providerConfig("text"),
    image: providerConfig("image"),
    templateAnalysis: normalizeTemplateAnalysisConfig(state.aiConfig.templateAnalysis),
    watermark: {
      enabled: state.aiConfig.watermark?.enabled !== false,
      visible: state.aiConfig.watermark?.visible !== false,
      metadata: state.aiConfig.watermark?.metadata !== false,
    },
  };
}

async function syncAiConfigToBackend(options = {}) {
  const { includeApiKeyChanges = false, ...requestOptions } = options;
  const changedKeyTypes = includeApiKeyChanges
    ? ["text", "image"].filter((type) => confirmedApiKeyTypes.has(type))
    : [];
  const data = await postBackend(
    "/api/config",
    { config: aiConfigUpdatePayload({ includeApiKeyChanges }) },
    requestOptions,
  );
  if (data.config) {
    changedKeyTypes.forEach((type) => {
      confirmedApiKeyTypes.delete(type);
      apiKeyEditBackups[type] = null;
    });
    state.aiConfig.text = mergeAiProviderConfig(state.aiConfig.text, data.config.text);
    state.aiConfig.image = mergeAiProviderConfig(state.aiConfig.image, data.config.image);
    state.aiConfig.knowledge = mergeKnowledgeConfig(state.aiConfig.knowledge, data.config.knowledge);
    state.aiConfig.templateAnalysis = normalizeTemplateAnalysisConfig(data.config.templateAnalysis);
    state.aiConfig.watermark = { ...state.aiConfig.watermark, ...(data.config.watermark || {}) };
    renderApiKeyField("text", state.aiConfig.text);
    renderApiKeyField("image", state.aiConfig.image);
    knowledgeSecretDirty.dify = false;
    saveAiConfig();
  }
  return data.config || state.aiConfig;
}

// 生成流程只需要"服务端已经有配置"：它把本地副本回推只是为了让单机/兼容模式的本地配置生效。
// 租户级配置的写权限只给 Owner/Admin（后端 403），非管理员成员的组织里，这一步必须是无操作
// 而不是把生成一起打掉——所以 403 当作"没什么可推的"直接放过，其它错误照旧上抛。
async function syncAiConfigBeforeGeneration(options = {}) {
  try {
    return await syncAiConfigToBackend(options);
  } catch (error) {
    if (error?.status === 403) return state.aiConfig;
    throw error;
  }
}

// Live model-status line. It is intentionally NOT persisted and starts empty:
// a stored model list only proves "we fetched this once", never "the provider
// answers right now". Only a real fetch/test in this session writes here, and
// reopening the panel clears it again (see resetAiModelStatus).
const aiModelLiveStatus = { text: null, image: null };

function setAiModelStatus(type, message, tone = "") {
  aiModelLiveStatus[type] = message ? { message, tone } : null;
  renderAiModelStatus(type);
}

function renderAiModelStatus(type) {
  const node = $(`#${type}ModelStatus`);
  if (!node) return;
  const live = aiModelLiveStatus[type];
  // No text until this session produced a result: an empty line is the only
  // state that cannot be misread as "verified".
  node.textContent = live ? live.message : "";
  node.className = live && live.tone ? `model-fetch-status ${live.tone}` : "model-fetch-status";
}

function resetAiModelStatus() {
  aiModelLiveStatus.text = null;
  aiModelLiveStatus.image = null;
  renderAiModelStatus("text");
  renderAiModelStatus("image");
}
async function fetchAiModels(type) {
  collectAiConfigForm();
  const config = {
    apiUrl: $(`#${type}ApiUrl`).value.trim(),
    apiKey: apiKeyDraftValue(type),
    model: modelValue(type),
  };
  if (!config.apiUrl) {
    renderModelOptions(type, []);
    setAiModelStatus(type, "请先填写 API 地址");
    return;
  }
  setAiModelStatus(type, "正在获取上游模型...");
  try {
    const data = await postBackend("/api/models", {
      type,
      config: aiProviderRequestConfig(type),
    });
    const models = data.models || [];
    state.aiConfig[type].models = models;
    renderModelOptions(type, models);
    if (!config.model && models.length) {
      setModelValue(type, models[0]);
      state.aiConfig[type].model = models[0];
    } else if (config.model) {
      setModelValue(type, config.model);
      state.aiConfig[type].model = config.model;
    }
    saveAiConfig();
    if (models.length && data.modelsSource === "manual") {
      setAiModelStatus(type, `上游模型列表不可用，已保留手动模型 ${models[0]}，可直接测试或使用`, "warning");
    } else if (models.length && data.modelsSource === "probe") {
      setAiModelStatus(type, `上游未开放模型列表，已自动探测到 ${models.length} 个可用模型`, "success");
    } else if (models.length) {
      setAiModelStatus(type, `已获取 ${models.length} 个模型`, "success");
    } else {
      setAiModelStatus(type, "未返回模型，可手动填写");
    }
  } catch (error) {
    renderModelOptions(type, state.aiConfig[type].models || []);
    if (config.model && /403|Forbidden|模型列表/i.test(error.message)) {
      setModelValue(type, config.model);
      state.aiConfig[type].model = config.model;
      setAiModelStatus(type, `上游未开放模型列表，已保留手动模型 ${config.model}，可直接测试或保存`, "success");
    } else {
      setAiModelStatus(type, `${modelTypeLabel(type)}模型获取失败：${error.message}；请手动填写模型名后测试`, "error");
    }
  }
}

async function testAiModel(type) {
  collectAiConfigForm();
  const config = {
    apiUrl: $(`#${type}ApiUrl`).value.trim(),
    apiKey: apiKeyDraftValue(type),
    model: modelValue(type),
  };
  if (!config.apiUrl) {
    setAiModelStatus(type, "请先填写 API 地址", "error");
    return;
  }
  if (!config.model) {
    setAiModelStatus(type, "请先填写或选择要测试的模型", "error");
    return;
  }
  setAiModelStatus(type, "正在测试当前模型...");
  if (type === "image") clearImageModelTestResult();
  const restoreButton = type === "image"
    ? setButtonLoading($("#testImageModelBtn"), "正在生成测试图...")
    : () => {};
  try {
    const data = await postBackend("/api/ai/test-model", {
      type,
      confirmCharge: type === "image",
      config: aiProviderRequestConfig(type),
    });
    if (data.model) {
      setModelValue(type, data.model);
      state.aiConfig[type].model = data.model;
    }
    if (data.models?.length) {
      state.aiConfig[type].models = data.models;
      renderModelOptions(type, data.models);
    }
    if (type === "image" && data.testImage) {
      renderImageModelTestResult(data.testImage, data.model || config.model);
      setAiModelStatus(type, `真实生图测试成功：当前模型 ${data.model || config.model}`, "success");
    } else if (data.verified === false) {
      setAiModelStatus(type, data.message || `当前模型 ${data.model || config.model} 未发起实际调用`, "warning");
    } else {
      setAiModelStatus(
        type,
        data.modelsError
          ? `测试成功：当前模型 ${data.model || config.model}；模型列表接口不可用，可继续手动填写`
          : `测试成功：当前模型 ${data.model || config.model}`
        ,
        "success",
      );
    }
  } catch (error) {
    if (/503|暂时不可用|上游AI服务/i.test(error.message)) {
      setAiModelStatus(type, `测试未通过：当前模型 ${config.model} 的上游服务暂时不可用，请稍后重试或更换供应商`, "warning");
    } else {
      setAiModelStatus(type, `测试失败：${error.message}`, "error");
    }
  } finally {
    restoreButton();
  }
}

function clearImageModelTestResult() {
  const result = $("#imageModelTestResult");
  const image = $("#imageModelTestImage");
  const title = $("#imageModelTestTitle");
  const meta = $("#imageModelTestMeta");
  // Leave no trace of an earlier run: the card, its caption and the thumbnail
  // exist only while a result from this session is on screen.
  if (title) title.textContent = "";
  if (meta) meta.textContent = "";
  if (image) image.removeAttribute("src");
  if (result) result.classList.add("hidden");
}

function renderImageModelTestResult(imageUrl, model) {
  const result = $("#imageModelTestResult");
  const image = $("#imageModelTestImage");
  if (!result || !image || !imageUrl) return;
  image.src = imageUrl;
  const title = $("#imageModelTestTitle");
  if (title) title.textContent = "真实生图测试成功";
  $("#imageModelTestMeta").textContent = `${model || "当前模型"} · 1024 × 1024 · 低质量`;
  result.classList.remove("hidden");
}

function closeImageModelTestConfirm() {
  hideModal($("#imageModelTestConfirmModal"));
}

function requestImageModelTest() {
  collectAiConfigForm();
  const config = state.aiConfig?.image || {};
  if (!config.apiUrl?.trim()) {
    setAiModelStatus("image", "请先填写 API 地址", "error");
    return;
  }
  if (!config.model?.trim()) {
    setAiModelStatus("image", "请先填写或选择要测试的模型", "error");
    return;
  }
  $("#imageModelTestConfirmModel").textContent = `当前模型：${config.model}`;
  showModal($("#imageModelTestConfirmModal"));
}

function normalizedModelOptions(models) {
  return [...new Set((Array.isArray(models) ? models : [])
    .map((model) => String(model || "").trim())
    .filter(Boolean))];
}

function renderModelSuggestionMenu(type, models, { filter = true } = {}) {
  const menu = $(`#${type}ModelSuggestionMenu`);
  if (!menu) return;
  const query = filter ? modelValue(type).toLocaleLowerCase() : "";
  const items = normalizedModelOptions(models).filter((model) => !query || model.toLocaleLowerCase().includes(query));
  menu.innerHTML = items.length
    ? items.map((model) => `<button type="button" class="model-suggestion-option" role="option" data-model-suggestion="${escapeHtml(model)}">${escapeHtml(model)}</button>`).join("")
    : '<p class="model-suggestion-empty">暂无匹配模型，可继续手动输入</p>';
}

function setModelSuggestionMenuOpen(type, open, options = {}) {
  const menu = $(`#${type}ModelSuggestionMenu`);
  const input = $(`#${type}Model`);
  const button = $(`#${type}ModelSuggestionsBtn`);
  if (!menu || !input || !button) return;
  if (open) renderModelSuggestionMenu(type, state.aiConfig?.[type]?.models || [], options);
  menu.classList.toggle("hidden", !open);
  menu.dataset.open = String(Boolean(open));
  input.setAttribute("aria-expanded", String(Boolean(open)));
  button.setAttribute("aria-expanded", String(Boolean(open)));
}

function renderModelOptions(type, models) {
  const items = normalizedModelOptions(models);
  renderModelSuggestionMenu(type, items);
}

function cleanAiText(value) {
  return String(value || "")
    .trim()
    .replace(/^```(?:json)?/i, "")
    .replace(/```$/i, "")
    .trim()
    .replace(/^["“”']|["“”']$/g, "");
}

function describeCleanPlate(cleanPlate = {}) {
  if (cleanPlate.enabled) {
    return `清底图成功 ${Number(cleanPlate.count || 0)} 页`;
  }
  const status = cleanAiText(cleanPlate.status || "");
  if (!status) return "清底图未启用";
  if (status === "skipped:no-image-api" || status === "skipped:no-image-edit-api") {
    return "清底图跳过：未配置图片接口";
  }
  if (status === "skipped:no-pages") {
    return "清底图跳过：无可处理页面";
  }
  if (status.startsWith("failed")) {
    return "清底图失败";
  }
  if (status.startsWith("skipped")) {
    return `清底图跳过：${status.split(":", 2)[1] || "未执行"}`;
  }
  return `清底图：${status}`;
}

function templateGeneration(template) {
  return template?.generation || template?.params?.generation || null;
}

function describeTemplateAi(template) {
  const generation = templateGeneration(template);
  const aiStatus = cleanAiText(generation?.layout?.rawStatus || template?.aiStatus || "");
  const cleanPlate = template?.params?.cleanPlate || generation?.cleanPlate || {};
  const cleanPlateText = describeCleanPlate(cleanPlate);
  const layoutError = cleanAiText(generation?.layout?.error || "");
  const source = cleanAiText(generation?.source || "");
  const model = cleanAiText(generation?.layout?.model || "");
  let badge = "AI 未记录";
  let tone = "neutral";
  let layoutText = "生成来源未记录";
  if (source === "layered-layout" || aiStatus.includes("layered-v1")) {
    badge = "分层模板";
    tone = "success";
    layoutText = "分层模板生成";
  } else if (source === "ai-vision" || aiStatus.includes("ai-vision-layout")) {
    badge = "AI视觉生成";
    tone = "success";
    layoutText = `AI 视觉生成${model ? ` · ${model}` : ""}`;
  } else if (source === "local-fallback" || aiStatus.includes("ai-vision-failed")) {
    badge = "AI失败·本地兜底";
    tone = "warning";
    layoutText = `AI 视觉失败，已用本地算法生成${layoutError ? ` · ${layoutError}` : ""}`;
  } else if (source === "local-layout" || aiStatus.includes("local-layout")) {
    badge = "本地算法生成";
    tone = "neutral";
    layoutText = "本地算法生成";
  } else if (aiStatus) {
    badge = "状态已记录";
    tone = "warning";
    layoutText = aiStatus;
  }
  const details = `${layoutText}；${cleanPlateText}`;
  return { badge, tone, details, layoutText, cleanPlateText, error: layoutError };
}

function promptSetForTemplate(template, mode = "rewrite") {
  const normalized = mode === "image" ? "image" : "rewrite";
  const configured = template?.promptSets?.[normalized];
  if (configured && typeof configured === "object") return configured;
  if (template?.mode === normalized) return template;
  const fallback = defaultAiConfig.templates.find((item) => item.mode === normalized);
  return fallback?.promptSets?.[normalized] || fallback || {};
}

function promptTemplateForMode(mode = "rewrite") {
  return promptSetForTemplate(currentAiTemplate(), mode);
}

function promptModeForDraft(source = "", workflow = "original") {
  if (["upload", "image"].includes(String(source || "").toLowerCase())) return "image";
  if (["link", "plugin"].includes(String(source || "").toLowerCase())) return "rewrite";
  // A manually created rewrite task follows the mode the user currently selected.
  return state.aiConfig?.promptMode === "image" ? "image" : "rewrite";
}

function promptSnapshotForDraft(source = "", workflow = "original") {
  const template = promptTemplateForMode(promptModeForDraft(source, workflow));
  return Object.fromEntries(
    ["titlePrompt", "bodyPrompt", "tagsPrompt", "imagePrompt"].map((key) => [key, String(template?.[key] || "")])
  );
}

function activePrompt(key) {
  const template = currentAiTemplate();
  const promptSet = promptSetForTemplate(template, state.aiConfig?.promptMode || template?.mode || "rewrite");
  return String(promptSet?.[key] || "");
}

function currentPromptSnapshot() {
  return promptSnapshotForDraft("blank", state.activeWorkflow);
}

// 标题默认提示词从「40 字以内」收紧到「20 字以内」（小红书标题上限就是 20 字，见
// XHS_TITLE_MAX_LENGTH）。草稿创建时会把当时的提示词存进 promptSnapshot，老任务的快照里
// 还是 40 字，会一直按 40 字生成、到「加入发布列表」那一步才被拦下。所以这几条**已知的旧默认
// 文案**一律改读当前配置；用户自己改过的标题提示词不在名单里，保持原样。
const LEGACY_TITLE_PROMPTS_WITH_40 = new Set([
  "在原始标题和正文基础上，改写 1 条小红书标题。保留原意和真实信息，不新增原文没有的数字、价格、地点、功效或体验。标题要自然、有吸引力，40 字以内，只输出标题。",
  "请先仔细观察输入图片，再结合用户补充描述，生成 1 条适合小红书的标题。提炼图片中真实可见的主体、场景、动作、色彩或氛围，突出一个具体亮点，标题自然口语化、有画面感和点击欲，13-20 字，最多 40 字。不得臆测图片看不到的品牌、价格、地点、人物身份、功效、数据或经历，不加引号、编号和解释，只输出标题。",
  "请仔细观察输入图片，并结合用户补充描述，提炼画面中明确可见的信息，生成 1 条适合小红书的标题。先判断主题、场景和主体，再突出一个最有价值或最吸引人的点。不得臆测图片看不到的品牌、价格、地点、人物身份、功效、数据或经历；不把画面文字扩写成未经证实的事实。标题具体、口语自然、有点击欲，40 字以内，不加引号、编号或解释，只输出标题。",
]);

function configuredPromptForDraft(draft, key) {
  const template = promptTemplateForMode(
    draft?.promptTemplateMode || promptModeForDraft(draft?.source, draft?.workflow)
  );
  return String(template?.[key] || "");
}

function draftPromptValue(draft, key) {
  if (draft?.promptSnapshot && Object.prototype.hasOwnProperty.call(draft.promptSnapshot, key)) {
    const value = String(draft.promptSnapshot[key] || "");
    if (key === "bodyPrompt" && draft?.workflow === "original" && value.includes("在原始标题和正文基础上，改写一篇小红书笔记正文")) {
      return activePrompt("bodyPrompt");
    }
    if (key === "titlePrompt" && LEGACY_TITLE_PROMPTS_WITH_40.has(value.trim())) {
      return configuredPromptForDraft(draft, "titlePrompt");
    }
    return value;
  }
  return configuredPromptForDraft(draft, key);
}

function composePrompt(basePrompt, draft, extra = "") {
  const payload = draftPayload(draft);
  return `${basePrompt}

当前素材：
标题：${payload.title || "无"}
正文：
${payload.body || "无"}
标签：${payload.tags || "无"}
来源：${payload.source}
原链接：${payload.origin || "无"}
图片数量：${payload.imageCount}
${extra ? `\n${extra}` : ""}`.trim();
}

function clipXhsTitle(value, limit = XHS_TITLE_MAX_LENGTH) {
  // 与编辑区计数同口径（String.length）。优先在标点处收口，避免把最后一个词劈开。
  const text = String(value || "").split("\n").find(Boolean)?.trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, "") || "";
  if (text.length <= limit) return text;
  const head = text.slice(0, limit);
  let cut = -1;
  for (const mark of "，。！？；：、,.!?;:）)】」》") cut = Math.max(cut, head.lastIndexOf(mark));
  return (cut >= Math.max(2, Math.floor(limit / 2)) ? head.slice(0, cut + 1) : head).trim();
}

function titleCompressPrompt(title, draft) {
  const payload = draftPayload(draft);
  return [
    "把下面这条小红书标题压缩到 " + XHS_TITLE_MAX_LENGTH + " 个汉字以内（标点也算一个字），保留原意和钩子结构，不新增事实、数字或承诺，只输出标题。",
    "原标题：" + String(title || ""),
    "当前素材标题：" + (payload.title || "无"),
  ].join("\n");
}

// AI 标题的超限收口：超过 20 字先压缩重写一次，仍不合规就在标点处收口。
// 标题钩子渲染出的长度取决于钩子槽位取值，光靠提示词写"20 字以内"是软约束。
async function fitXhsTitle(value, draft, options = {}) {
  const first = String(value || "").split("\n").find(Boolean)?.trim() || "";
  if (first.length <= XHS_TITLE_MAX_LENGTH) return first;
  let compressed = "";
  try {
    compressed = String(await runTextAi("title", titleCompressPrompt(first, draft), draft, options) || "")
      .split("\n").find(Boolean)?.trim() || "";
  } catch (error) {
    if (isAbortError(error)) throw error;
  }
  if (compressed.length && compressed.length <= XHS_TITLE_MAX_LENGTH) return compressed;
  return clipXhsTitle(first);
}

function splitDraftPoints(text) {
  return String(text || "")
    .split(/[\n。；;.!?！？]+/)
    .map((item) => item.trim().replace(/^[-\d.、\s]+/, ""))
    .filter(Boolean);
}

function parseJsonObject(text) {
  const raw = cleanAiText(text || "");
  try {
    return JSON.parse(raw);
  } catch {
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]);
    } catch {
      return null;
    }
  }
}

function layerContentFromDraft(template, draft) {
  const points = splitDraftPoints(`${draft.body || ""}\n${draft.title || ""}`);
  const tagList = (draft.tags.match(/#[^\s#]+/g) || []).slice(0, 3);
  const content = {
    title: (draft.title || "主题速览").slice(0, 24),
    subtitle: (points[0] || "换一个主题后即可快速发布").slice(0, 34),
    badge: "快速套图",
    cornerText: "收藏版",
    footerNote: "发布前检查一遍标题和标签",
  };
  for (let index = 0; index < 3; index += 1) {
    content[`tag${index + 1}`] = tagList[index] || ["可收藏", "步骤清晰", "一键套图"][index];
  }
  for (let index = 0; index < 7; index += 1) {
    const point = points[index] || ["定位主题", "补齐基础", "拆解框架", "整理要点", "加入案例", "检查表达", "发布复盘"][index];
    content[`step${index + 1}Number`] = `${index + 1}`.padStart(2, "0");
    content[`step${index + 1}Title`] = point.slice(0, 14);
    content[`step${index + 1}Duration`] = `阶段 ${index + 1}`;
    content[`step${index + 1}Bullets`] = `${point.slice(14, 34) || "提炼一个关键动作"}\n替换主题后检查表达`;
  }
  const schema = template?.params?.contentSchema || [];
  schema.forEach((item) => {
    if (content[item.key] === undefined) content[item.key] = "";
  });
  return content;
}

function applyLayeredContent(draft, template, content) {
  const fields = ensureTemplateFields(draft, template);
  if (!fields || fields.type !== "layered-v1") return false;
  const page = activeLayeredPageFields(fields);
  page.content = { ...(page.content || {}), ...(content || {}) };
  draft.templateFields = fields;
  return true;
}

async function fillLayeredTemplateWithAi(draft, template, options = {}) {
  if (!draft || !template || !isLayeredTemplate(template)) return false;
  const fallback = layerContentFromDraft(template, draft);
  if (!state.aiConfig?.text?.apiUrl) {
    applyLayeredContent(draft, template, fallback);
    return true;
  }
  const schema = (template.params?.contentSchema || []).map((item) => ({
    key: item.key,
    label: item.label,
    maxChars: item.maxChars || 80,
    maxLines: item.maxLines || 1,
  }));
  try {
    const result = await runTextAi(
      "layered-template-content",
      `把当前小红书笔记填入分层模板槽位。只返回 JSON object，不要 Markdown。JSON 的 key 必须来自 schema；缺失时也要给空字符串。七个阶段要清晰、短、适合直接放在图上。\n\nschema:\n${JSON.stringify(schema, null, 2)}`,
      draft,
      options
    );
    const parsed = parseJsonObject(result);
    applyLayeredContent(draft, template, parsed && typeof parsed === "object" ? { ...fallback, ...parsed } : fallback);
    return true;
  } catch (error) {
    if (isAbortError(error)) throw error;
    applyLayeredContent(draft, template, fallback);
    return true;
  }
}


// ----- js/core/image-states.js -----
// 全站图片加载态。
//
// 用户看到的几类"破图占位"都出在这里：
//   ① 受保护图片（cached-image / template-asset / template-output / proxy-image）需要带凭据
//      取回，state.js 会先用 backendFetch 换成 object URL。浏览器直接去请求那个地址必然先
//      失败一次，这一帧画出来的就是破图图标；
//   ② 图片还在传输或解码、或者 <img> 刚插入而 src 还没落上时，元素里什么都没有——一个内容
//      为空的框，和"图坏了"没有区别。上传预览正好落在这类；
//   ③ ①里的图片在字节到达之前，src 上是 state.js 放的 1x1 透明占位图。占位图自己也会
//      complete 且 naturalWidth > 0，只按"有没有像素"判会把这整段等待判成 loaded——用户看到
//      的就是一块容器底色（实测：图片栏 8 张全白），和"图没出来"没有区别。
//
// 处理方式：每个 <img> 进 DOM 就先标成 loading，load 之后转 loaded，error 转 error；"正在
// 等水合"的受保护图片（含 src 上的占位图阶段）一直算 loading，只有水合自己放弃才算失败
// （第一次 401 不算最终失败，否则会闪一下"加载失败"又变回图）。样子由 styles/image-states.css
// 接管：loading 是骨架屏 + 转圈，error 是明确的「图片加载失败」。
//
// 一条 MutationObserver 覆盖所有渲染路径，40 个模板不用各写一遍 onerror/onload。装饰性
// 图标（aria-hidden 里的品牌图标）跳过，不给它闪骨架。

const IMAGE_STATE_ATTRIBUTE = "data-image-state";
// 「哪些地址必须带凭据取回、浏览器直接加载一定会先失败一次」的清单只有一份，在 state.js 的
// PROTECTED_IMAGE_PATH_PREFIXES 里；这里直接用那个裸名，不再抄一遍。模块清单里的脚本会被
// 拼进同一个作用域（AGENTS.md：别给单个文件加 type="module"，会切断裸名引用），而 state.js
// 在 load-order.json 里排在前面，所以这里读得到——顺序由 tests/test_image_states.js 钉住。
function imageStateOf(image) {
  return String(image?.getAttribute?.(IMAGE_STATE_ATTRIBUTE) || "");
}

function imageStateIsDecorative(image) {
  if (!image?.getAttribute) return true;
  if (image.hasAttribute(IMAGE_STATE_ATTRIBUTE + "-skip")) return true;
  return Boolean(image.closest?.('[aria-hidden="true"]'));
}

function imageStateIsProtected(image) {
  if (!image?.getAttribute) return false;
  if (image.hasAttribute("data-protected-image")) return true;
  const src = String(image.getAttribute("src") || "");
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false;
  const origin = typeof window !== "undefined" ? window.location?.href : undefined;
  let url;
  try {
    url = new URL(src, origin);
  } catch (_error) {
    return false;
  }
  return PROTECTED_IMAGE_PATH_PREFIXES.some((prefix) => (
    url.pathname === prefix.replace(/\/$/, "") || url.pathname.startsWith(prefix)
  ));
}

// 本次会话里已经成功画出过的地址。草稿列表、图库这类面板每次重渲染都会重建 DOM，新建的
// <img> 在同一次 tick 里 complete 未必为真，只认 complete 会让已经显示正常的图再闪一次骨架。
// 与 state.js 里受保护图片先解析成 blob 再同步换上同一个理由（少闪一帧）。
const IMAGE_STATE_LOADED_SOURCES = new Set();
const IMAGE_STATE_LOADED_SOURCE_LIMIT = 800;

function imageStateRememberLoaded(src) {
  const key = String(src || "").trim();
  if (!key) return;
  if (IMAGE_STATE_LOADED_SOURCES.size >= IMAGE_STATE_LOADED_SOURCE_LIMIT) {
    IMAGE_STATE_LOADED_SOURCES.clear();
  }
  IMAGE_STATE_LOADED_SOURCES.add(key);
}

function imageStateForgetLoaded(src) {
  IMAGE_STATE_LOADED_SOURCES.delete(String(src || "").trim());
}

// 受保护图片"正在等字节"的判据。state.js 在水合完成前把 src 换成 1x1 透明占位图
// （PROTECTED_IMAGE_PLACEHOLDER，同一 bundle 作用域、state.js 排在前面），原路径记在
// data-protected-image 上。占位图"加载成功"不等于真图到位，必须靠这两个属性一起认。
function imageStateIsAwaitingHydration(image) {
  if (!image?.getAttribute) return false;
  if (!image.getAttribute("data-protected-image")) return false;
  return String(image.getAttribute("src") || "") === PROTECTED_IMAGE_PLACEHOLDER;
}

// 进 DOM 时的初始态。已经解码完的直接 loaded（缓存命中不能闪骨架）；没有 src 的算 loading
// ——它马上就会被赋上，按"失败"处理等于给用户看一次假故障。
function imageStateOnInsert(image) {
  if (!image?.getAttribute) return "loading";
  // 等水合的整段窗口都算 loading：占位图会 complete，继续往下判就是假的 loaded（一片空白）。
  // 水合重试全部失败时 state.js 会打 data-protected-image-error，那时才转 error。
  if (imageStateIsAwaitingHydration(image)) {
    return image.hasAttribute("data-protected-image-error") ? "error" : "loading";
  }
  if (image.complete && Number(image.naturalWidth) > 0) return "loaded";
  const src = String(image.getAttribute("src") || "").trim();
  if (!src) return "loading";
  if (IMAGE_STATE_LOADED_SOURCES.has(src)) return "loaded";
  // complete 但没有像素：请求已经结束且失败（404/401/文件没了）。
  return image.complete ? "error" : "loading";
}

// 出错时的状态：等水合的受保护图片第一次 401 不是最终结果。只有水合自己放弃（state.js 会
// 打上 data-protected-image-error）才算真失败，那时属性观察会把状态重算成 error。
function imageStateOnError(image) {
  if (imageStateIsProtected(image) && !image?.hasAttribute?.("data-protected-image-error")) {
    return "loading";
  }
  return "error";
}

function setImageState(image, state) {
  const next = state === "loaded" || state === "error" ? state : "loading";
  if (imageStateOf(image) !== next) image.setAttribute(IMAGE_STATE_ATTRIBUTE, next);
}

function syncImageState(image) {
  if (!(image instanceof HTMLImageElement)) return;
  if (imageStateIsDecorative(image)) {
    image.removeAttribute(IMAGE_STATE_ATTRIBUTE);
    return;
  }
  setImageState(image, imageStateOnInsert(image));
}

function syncImageStateTree(node) {
  if (node instanceof HTMLImageElement) {
    syncImageState(node);
    return;
  }
  if (!(node instanceof Element)) return;
  node.querySelectorAll("img").forEach(syncImageState);
}

let imageStateObserver = null;

function startImageStates() {
  syncImageStateTree(document.body);
  // load / error 不冒泡，只能在捕获阶段收口到一处。
  document.addEventListener("load", (event) => {
    if (!(event.target instanceof HTMLImageElement)) return;
    // 占位图自己也会触发 load；把它当成"真图到位"会立刻抹掉骨架，等待期又变回空白。
    if (imageStateIsAwaitingHydration(event.target)) {
      setImageState(event.target, "loading");
      return;
    }
    imageStateRememberLoaded(event.target.getAttribute("src"));
    setImageState(event.target, "loaded");
  }, true);
  document.addEventListener("error", (event) => {
    if (!(event.target instanceof HTMLImageElement)) return;
    imageStateForgetLoaded(event.target.getAttribute("src"));
    setImageState(event.target, imageStateOnError(event.target));
  }, true);
  if (imageStateObserver) return;
  imageStateObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        if (mutation.target instanceof HTMLImageElement) syncImageState(mutation.target);
        continue;
      }
      mutation.addedNodes.forEach(syncImageStateTree);
    }
  });
  imageStateObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    // src 换了要重新算；data-protected-image-error 是水合放弃的信号，出现即转失败态。
    attributeFilter: ["src", "data-protected-image-error"],
  });
}

// ----- js/shell/workspace.js -----
function finishSectionTransition() {
  if (sectionTransitionCleanup) sectionTransitionCleanup();
}

function reduceUiMotion() {
  return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

function playUiMotion(target, motionClass = "ui-content-enter") {
  if (!target) return;
  const motionClasses = ["ui-content-enter", "ui-view-enter", "ui-choice-pop"];
  window.clearTimeout(uiMotionTimers.get(target));
  motionClasses.forEach((className) => target.classList.remove(className));
  if (reduceUiMotion()) return;
  void target.offsetWidth;
  target.classList.add(motionClass);
  const timer = window.setTimeout(() => {
    target.classList.remove(motionClass);
    uiMotionTimers.delete(target);
  }, 320);
  uiMotionTimers.set(target, timer);
}

function syncSegmentedIndicator(group, animate = true) {
  if (!group) return;
  const activeButton = group.querySelector("button.active");
  if (!activeButton || !group.getClientRects().length || !activeButton.getClientRects().length) return;
  group.classList.add("has-motion-indicator");
  if (!animate || reduceUiMotion()) group.classList.add("no-segment-motion");
  group.style.setProperty("--segment-x", `${activeButton.offsetLeft}px`);
  group.style.setProperty("--segment-width", `${activeButton.offsetWidth}px`);
  if (!animate || reduceUiMotion()) {
    window.requestAnimationFrame(() => group.classList.remove("no-segment-motion"));
  }
}

function syncSegmentedIndicators(root = document, animate = false) {
  root.querySelectorAll?.(".segmented, .prompt-mode-tabs").forEach((group) => syncSegmentedIndicator(group, animate));
}

function calculatePreviewLayout(availableHeight, availableWidth, fixedHeight = 1206, fixedWidth = 420) {
  const fitScale = Math.min(availableHeight / fixedHeight, availableWidth / fixedWidth);
  const scale = Math.max(0.36, fitScale);
  const verticalSurplus = Math.max(0, availableHeight - (fixedHeight * scale));
  const designSurplus = scale > 0 ? verticalSurplus / scale : 0;
  return {
    scale,
    contentHeight: fixedHeight + designSurplus,
    phoneHeight: 910,
    previewGap: 18 + designSurplus,
    compositionHeight: 278,
  };
}

function syncPreviewScale() {
  const panel = $(".preview-panel");
  if (!panel) return;
  if (!window.matchMedia?.("(min-width: 821px)").matches) {
    panel.style.setProperty("--preview-scale", "1");
    panel.style.setProperty("--preview-content-height", "1206px");
    panel.style.setProperty("--preview-phone-height", "910px");
    panel.style.setProperty("--preview-gap", "18px");
    panel.style.setProperty("--preview-composition-height", "278px");
    return;
  }
  if (!panel.getClientRects().length) return;
  const styles = window.getComputedStyle(panel);
  const verticalPadding = (Number.parseFloat(styles.paddingTop) || 0) + (Number.parseFloat(styles.paddingBottom) || 0);
  const horizontalPadding = (Number.parseFloat(styles.paddingLeft) || 0) + (Number.parseFloat(styles.paddingRight) || 0);
  const availableHeight = Math.max(0, panel.clientHeight - verticalPadding);
  const availableWidth = Math.max(0, panel.clientWidth - horizontalPadding);
  const fixedHeight = Number.parseFloat(styles.getPropertyValue("--preview-fixed-height")) || 1206;
  const fixedWidth = Number.parseFloat(styles.getPropertyValue("--preview-fixed-width")) || 420;
  const { scale, contentHeight, phoneHeight, previewGap, compositionHeight } = calculatePreviewLayout(availableHeight, availableWidth, fixedHeight, fixedWidth);
  panel.style.setProperty("--preview-scale", String(scale));
  panel.style.setProperty("--preview-content-height", `${contentHeight}px`);
  panel.style.setProperty("--preview-phone-height", `${phoneHeight}px`);
  panel.style.setProperty("--preview-gap", `${previewGap}px`);
  panel.style.setProperty("--preview-composition-height", `${compositionHeight}px`);
}

function showModal(modal) {
  if (!modal) return;
  const activeElement = document.activeElement;
  if (activeElement && activeElement !== document.body && !modal.contains(activeElement) && typeof activeElement.focus === "function") {
    modalReturnFocus.set(modal, activeElement);
  }
  window.clearTimeout(modalMotionTimers.get(modal));
  modalMotionTimers.delete(modal);
  modal.classList.remove("hidden", "is-closing", "is-opening");
  modal.setAttribute("aria-hidden", "false");
  if (!reduceUiMotion()) {
    void modal.offsetWidth;
    modal.classList.add("is-opening");
    const timer = window.setTimeout(() => {
      modal.classList.remove("is-opening");
      modalMotionTimers.delete(modal);
    }, 300);
    modalMotionTimers.set(modal, timer);
  }
  window.requestAnimationFrame(() => syncSegmentedIndicators(modal, false));
}

function hideModal(modal, afterClose) {
  if (!modal) return;
  window.clearTimeout(modalMotionTimers.get(modal));
  modalMotionTimers.delete(modal);
  modal.classList.remove("is-opening");
  modal.setAttribute("aria-hidden", "true");
  const finish = () => {
    modal.classList.add("hidden");
    modal.classList.remove("is-closing");
    modalMotionTimers.delete(modal);
    afterClose?.();
    const returnFocus = modalReturnFocus.get(modal);
    modalReturnFocus.delete(modal);
    if (returnFocus?.isConnected && typeof returnFocus.focus === "function") {
      window.requestAnimationFrame(() => returnFocus.focus());
    }
  };
  if (modal.classList.contains("hidden") || reduceUiMotion()) {
    finish();
    return;
  }
  modal.classList.add("is-closing");
  const timer = window.setTimeout(finish, 210);
  modalMotionTimers.set(modal, timer);
}

function lastSectionForWorkflow(workflow) {
  const sections = workflowSectionOptions[workflow];
  const remembered = state.lastWorkflowSections?.[workflow];
  return sections?.includes(remembered) ? remembered : defaultLastWorkflowSections[workflow];
}

const replicaIntakeCopy = {
  link: {
    workspaceTitle: "小红书链接快速改写",
    introTitle: "保留内容，只换一种视觉表达",
    introDescription: "导入原笔记后，系统保留标题、正文和图片信息，只轻调画面风格、颜色与字体。",
    route: ["导入笔记", "确认原稿", "生成新图"],
    boundaryTitle: "默认保护原内容",
    boundaryDescription: "不接入资料参考，不拆模板，不重新搭建笔记内容。生成时仅处理图片的视觉样式。",
    imageFooterHint: "图片会直接进入 AI 改写页",
    imageAction: "上传并创建任务",
  },
  image: {
    workspaceTitle: "图生文案",
    introTitle: "上传图片，快速生成小红书文案",
    introDescription: "从图片中识别主体、场景和细节，生成可直接发布的标题、正文与标签。",
    route: ["上传图片", "识别画面", "生成文案"],
    boundaryTitle: "只依据图片写作",
    boundaryDescription: "系统围绕图片中可确认的主体、场景和细节生成文案，不虚构图片之外的品牌、地点、价格或经历。",
    imageFooterHint: "图片会进入图生文案工作台",
    imageAction: "上传并创建文案任务",
  },
};

function syncReplicaIntakeCopy(mode = "link") {
  const copy = replicaIntakeCopy[mode === "image" ? "image" : "link"];
  const setText = (selector, value) => {
    const node = $(selector);
    if (node) node.textContent = value;
  };
  setText("#replicaIntroTitle", copy.introTitle);
  setText("#replicaIntroDescription", copy.introDescription);
  setText("#replicaBoundaryTitle", copy.boundaryTitle);
  setText("#replicaBoundaryDescription", copy.boundaryDescription);
  setText("#replicaImageImportFooterHint", copy.imageFooterHint);
  setText("#replicaImageImportAction", copy.imageAction);
  $$('[data-replica-route-label]').forEach((node, index) => {
    node.textContent = copy.route[index] || "";
  });
}

function setSection(section) {
  // 入口已收起的未完成板块（云端知识库）也不该被直接打开：旧会话里存过的 currentSection
  // 会把页面落在空壳上，这里统一兜回首页（见 js/core/unfinished-surfaces.js）。
  if (unfinishedSectionHidden(section)) section = "home";
  const activeSection = $(`#section-${section}`);
  if (!activeSection) return;

  const previousSection = $(".section.active");
  const sectionChanged = Boolean(previousSection && previousSection !== activeSection);
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const previousSectionName = previousSection?.id?.replace(/^section-/, "") || "";
  const previousWorkflow = state.currentSectionWorkflow;
  if (sectionChanged && workflowSectionOptions[previousWorkflow]?.includes(previousSectionName)) {
    state.lastWorkflowSections[previousWorkflow] = previousSectionName;
  }
  const isImportWorkspaceSwitch = ["intake", "original"].includes(previousSectionName)
    && ["intake", "original"].includes(section);
  const isHomeWorkspaceSwitch = previousSectionName === "home" || section === "home";
  finishSectionTransition();
  state.currentSection = section;
  if (section === "intake") state.activeWorkflow = "replica";
  if (section === "original" || section === "viral") state.activeWorkflow = "original";
  if (["image", "plugin", "knowledge", "publish", "auth-management", "admin-console"].includes(section)) state.activeWorkflow = "tools";
  if (workflowSectionOptions[state.activeWorkflow]?.includes(section)) {
    state.currentSectionWorkflow = state.activeWorkflow;
    state.lastWorkflowSections[state.activeWorkflow] = section;
    enteredWorkflowsThisSession.add(state.activeWorkflow);
  }
  if (section === "rewrite") ensureActiveWorkflowDraft();
  renderMetrics();
  const isToolsSection = ["image", "plugin", "knowledge", "publish", "auth-management", "admin-console"].includes(section);
  $(".app-shell")?.classList.toggle("home-mode", section === "home");
  $(".app-shell")?.classList.toggle("tools-mode", isToolsSection);
  const homeBrand = $("#homeBrandBtn");
  homeBrand?.classList.toggle("active", section === "home");
  if (homeBrand) {
    if (section === "home") homeBrand.setAttribute("aria-current", "page");
    else homeBrand.removeAttribute("aria-current");
  }

  if (sectionChanged && !reduceMotion && !isImportWorkspaceSwitch && !isHomeWorkspaceSwitch) {
    const sections = $$(".section");
    const previousIndex = sections.indexOf(previousSection);
    const nextIndex = sections.indexOf(activeSection);
    const workspace = $(".workspace");
    const directionClass = nextIndex < previousIndex ? "section-transition-backward" : "section-transition-forward";

    workspace?.classList.add(directionClass);
    previousSection.classList.remove("active", "is-entering");
    previousSection.classList.add("is-leaving");
    activeSection.classList.remove("is-leaving");
    activeSection.classList.add("active", "is-entering");

    let transitionTimer = 0;
    const cleanup = () => {
      activeSection.removeEventListener("animationend", handleAnimationEnd);
      window.clearTimeout(transitionTimer);
      previousSection.classList.remove("is-leaving");
      activeSection.classList.remove("is-entering");
      workspace?.classList.remove("section-transition-forward", "section-transition-backward");
      if (sectionTransitionCleanup === cleanup) sectionTransitionCleanup = null;
    };
    const handleAnimationEnd = (event) => {
      if (event.target === activeSection && event.animationName === "workspace-section-enter") cleanup();
    };
    activeSection.addEventListener("animationend", handleAnimationEnd);
    transitionTimer = window.setTimeout(cleanup, 360);
    sectionTransitionCleanup = cleanup;
  } else {
    $$(".section").forEach((node) => {
      node.classList.toggle("active", node === activeSection);
      node.classList.remove("is-entering", "is-leaving");
    });
  }

  if (section === "intake") {
    const activeTab = $("#section-intake [data-link-import-tab].active");
    syncReplicaIntakeCopy(activeTab?.dataset.linkImportTab || "link");
  }
  $$(".nav-item").forEach((node) => {
    const workflowMatches = !node.dataset.workflow || node.dataset.workflow === state.activeWorkflow;
    node.classList.toggle("active", node.dataset.section === section && workflowMatches);
  });
  $$("[data-sidebar-workflow]").forEach((group) => group.classList.toggle("hidden", group.dataset.sidebarWorkflow !== state.activeWorkflow));
  $$(".global-nav-item").forEach((node) => {
    const active = section !== "home" && (node.dataset.globalWorkflow === "tools"
      ? node.dataset.section === section
      : node.dataset.globalWorkflow === state.activeWorkflow);
    node.classList.toggle("active", active);
    if (active) node.setAttribute("aria-current", "page");
    else node.removeAttribute("aria-current");
  });
  // 治理入口是团队/平台两个页面的合并项，它自己没有 data-section。
  const governanceBtn = $("#governanceNavBtn");
  if (governanceBtn) {
    const governanceActive = section === "auth-management" || section === "admin-console";
    governanceBtn.classList.toggle("active", governanceActive);
    if (governanceActive) governanceBtn.setAttribute("aria-current", "page");
    else governanceBtn.removeAttribute("aria-current");
  }
  if (section === "home") renderHomeDashboard();
  if (section === "rewrite") {
    renderEditor({ preserveScroll: !sectionChanged });
    hydrateViralDraftSnapshots(currentDraft());
  }
  if (section === "viral") {
    renderViralFactory();
    loadLayoutTemplates();
    loadViralLibraries();
  }
  if (section === "image") {
    renderStyleGrid();
    renderImageStudioSelection();
    renderImageReferencePreview();
    if (!(state.viralLibraries.cover || []).length) loadViralLibraries();
  }
  if (section === "knowledge") {
    loadKnowledgeWorkspace();
  }
  if (section === "publish") renderPublishList();
  if (section === "auth-management") loadAuthManagement();
  if (section === "admin-console") {
    // 原生控制台：先取会话 cookie，再渲染当前视图。openAdminConsole() 是 iframe
    // 方案下线时删掉的，这里残留的调用会让整个入口抛 ReferenceError。
    // 没有平台管理员权限时（403）退回首页并收起入口，而不是留一个空壳页面。
    void ensureAdminSession()
      .then(() => renderAdminConsole())
      .catch(() => refreshAdminConsoleAccess());
  }
  window.requestAnimationFrame(() => {
    syncSegmentedIndicators(activeSection, false);
    if (section === "rewrite") syncPreviewScale();
  });
  activeSection.scrollTo({ top: 0, behavior: sectionChanged ? "auto" : "smooth" });
  if (sectionChanged && window.matchMedia?.("(max-width: 640px)").matches) {
    document.scrollingElement?.scrollTo?.({ top: 0, left: 0, behavior: "instant" });
  }
  saveBrowserState();
}

function toggleNavGroup(button) {
  const subgroup = $(`#${button.getAttribute("aria-controls")}`);
  if (!subgroup) return;
  const isExpanded = button.getAttribute("aria-expanded") === "true";
  button.setAttribute("aria-expanded", String(!isExpanded));
  subgroup.setAttribute("aria-hidden", String(isExpanded));
  subgroup.classList.toggle("is-collapsed", isExpanded);
}

function apiKeyDraftValue(type) {
  const input = $(`#${type}ApiKey`);
  const keyState = input?.dataset.keyState;
  if (keyState === "confirmed") {
    return state.aiConfig?.[type]?.apiKey || "";
  }
  if (keyState === "confirmed-empty") return "";
  if (keyState === "masked") return undefined;
  if (keyState === "editing-existing" && input.value === input.dataset.existingDisplay) {
    return apiKeyEditBackups[type]?.apiKey || undefined;
  }
  return input && !input.readOnly ? input.value.trim() : "";
}

function maskDraftApiKey(value) {
  const secret = String(value || "").trim();
  if (!secret) return "";
  // 长度按**码点**数，与 server/secret_masking.py 的 len() 对齐：String#length 与 slice 数的是
  // UTF-16 码元，星平面字符（emoji）会被算成 2、还会被从中间切断留下半个代理对——实测 7 个
  // emoji 掩成 "😀\ud83d••••••••😀"。契约表里那几个星平面/多字节用例就是钉这条的。
  const chars = Array.from(secret);
  if (chars.length <= 6) return `${chars[0]}${"•".repeat(6)}${chars[chars.length - 1]}`;
  const headLength = Math.min(7, Math.max(3, Math.floor(chars.length / 4)));
  const tailLength = Math.min(4, Math.max(2, Math.floor(chars.length / 6)));
  return `${chars.slice(0, headLength).join("")}${"•".repeat(8)}${chars.slice(-tailLength).join("")}`;
}

function renderApiKeyField(type, provider) {
  const input = $(`#${type}ApiKey`);
  const editButton = $(`[data-edit-api-key="${type}"]`);
  const confirmButton = $(`[data-confirm-api-key="${type}"]`);
  if (!input || !editButton || !confirmButton) return;
  delete input.dataset.existingDisplay;
  const draftKey = provider?.apiKey || "";
  const hasSavedKey = Boolean(provider?.hasApiKey && provider?.maskedApiKey);
  if (!draftKey && confirmedApiKeyTypes.has(type)) {
    input.type = "text";
    input.readOnly = true;
    input.value = "";
    input.placeholder = "未配置 API Key";
    input.dataset.keyState = "confirmed-empty";
    input.title = "已确认不配置 API Key，点击保存后清除本地后端密钥";
    confirmButton.disabled = true;
    confirmButton.classList.add("hidden");
    editButton.textContent = "设置密钥";
    editButton.classList.remove("hidden");
    return;
  }
  if (draftKey && confirmedApiKeyTypes.has(type)) {
    input.type = "text";
    input.readOnly = true;
    input.value = maskDraftApiKey(draftKey);
    input.placeholder = "";
    input.dataset.keyState = "confirmed";
    input.title = "密钥已确认，点击保存后写入本地后端";
    confirmButton.disabled = true;
    confirmButton.classList.add("hidden");
    editButton.textContent = "更换密钥";
    editButton.classList.remove("hidden");
    return;
  }
  if (draftKey) {
    input.type = "password";
    input.readOnly = false;
    input.value = draftKey;
    input.placeholder = "输入新的 API Key";
    input.dataset.keyState = "editing";
    input.title = "正在输入新密钥，保存后仅显示脱敏值";
    confirmButton.disabled = false;
    confirmButton.classList.remove("hidden");
    editButton.textContent = "取消";
    editButton.classList.remove("hidden");
    return;
  }
  if (hasSavedKey) {
    input.type = "text";
    input.readOnly = true;
    input.value = provider.maskedApiKey;
    input.placeholder = "";
    input.dataset.keyState = "masked";
    input.title = "中段已脱敏，完整密钥仅保存在本地后端";
    confirmButton.disabled = true;
    confirmButton.classList.add("hidden");
    editButton.textContent = "更换密钥";
    editButton.classList.remove("hidden");
    return;
  }
  input.type = "password";
  input.readOnly = false;
  input.value = "";
  input.placeholder = "sk-...";
  input.dataset.keyState = "empty";
  input.title = "输入 API Key";
  confirmButton.disabled = false;
  confirmButton.classList.remove("hidden");
  editButton.classList.add("hidden");
}

function setApiKeyEditing(type, editing) {
  const provider = state.aiConfig?.[type];
  if (!provider) return;
  if (editing) {
    const input = $(`#${type}ApiKey`);
    const wasConfirmed = confirmedApiKeyTypes.has(type);
    const existingDisplay = provider.apiKey
      ? maskDraftApiKey(provider.apiKey)
      : provider.maskedApiKey || "";
    apiKeyEditBackups[type] = {
      apiKey: provider.apiKey || "",
      hasApiKey: Boolean(provider.hasApiKey),
      maskedApiKey: provider.maskedApiKey || "",
      wasConfirmed,
    };
    confirmedApiKeyTypes.delete(type);
    input.readOnly = false;
    input.value = existingDisplay;
    input.placeholder = "输入新的 API Key";
    input.dataset.keyState = existingDisplay ? "editing-existing" : "editing";
    input.dataset.existingDisplay = existingDisplay;
    input.type = existingDisplay ? "text" : "password";
    input.title = existingDisplay
      ? "原密钥保持不变；直接输入即可整体替换"
      : "正在输入新密钥，保存后仅显示脱敏值";
    const confirmButton = $(`[data-confirm-api-key="${type}"]`);
    confirmButton.disabled = false;
    confirmButton.classList.remove("hidden");
    const editButton = $(`[data-edit-api-key="${type}"]`);
    editButton.textContent = "取消";
    editButton.classList.remove("hidden");
    input.focus();
    if (existingDisplay) input.select();
    return;
  }
  const backup = apiKeyEditBackups[type];
  provider.apiKey = backup?.apiKey || "";
  provider.hasApiKey = Boolean(backup?.hasApiKey);
  provider.maskedApiKey = backup?.maskedApiKey || "";
  if (backup?.wasConfirmed) confirmedApiKeyTypes.add(type);
  else confirmedApiKeyTypes.delete(type);
  apiKeyEditBackups[type] = null;
  renderApiKeyField(type, provider);
}

function confirmApiKeyEditing(type) {
  const provider = state.aiConfig?.[type];
  const input = $(`#${type}ApiKey`);
  if (!provider || !input || input.readOnly) return;
  const backup = apiKeyEditBackups[type];
  if (input.dataset.keyState === "editing-existing" && input.value === input.dataset.existingDisplay) {
    provider.apiKey = backup?.apiKey || "";
    provider.hasApiKey = Boolean(backup?.hasApiKey);
    provider.maskedApiKey = backup?.maskedApiKey || "";
    if (backup?.wasConfirmed) confirmedApiKeyTypes.add(type);
    else confirmedApiKeyTypes.delete(type);
    apiKeyEditBackups[type] = null;
    renderApiKeyField(type, provider);
    return;
  }
  const apiKey = input.value.trim();
  provider.apiKey = apiKey;
  provider.hasApiKey = false;
  provider.maskedApiKey = "";
  apiKeyEditBackups[type] = null;
  confirmedApiKeyTypes.add(type);
  renderApiKeyField(type, provider);
}

function renderAiConfigView() {
  const previousPanel = $('[data-ai-config-panel]:not(.hidden)');
  // 这个面板只对平台管理员开放（上游接口 + 生图水印都是平台侧配置），所以不用再按 tab 分权。
  const activeView = ["api", "watermark"].includes(state.aiConfigView) ? state.aiConfigView : "api";
  state.aiConfigView = activeView;
  $$('[data-ai-config-view]').forEach((button) => {
    const active = button.dataset.aiConfigView === activeView;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  $$('[data-ai-config-panel]').forEach((panel) => {
    panel.classList.toggle("hidden", panel.dataset.aiConfigPanel !== activeView);
  });
  const activePanel = $(`[data-ai-config-panel="${activeView}"]`);
  if (previousPanel && previousPanel !== activePanel) playUiMotion(activePanel, "ui-view-enter");
  const activeButton = $(`[data-ai-config-view="${activeView}"]`);
  if (previousPanel && previousPanel !== activePanel) playUiMotion(activeButton, "ui-choice-pop");
  if (activePanel) window.requestAnimationFrame(() => syncSegmentedIndicators(activePanel, false));
}

function setAiConfigView(view) {
  collectAiConfigForm();
  state.aiConfigView = view;
  renderAiConfigView();
}

let aiConfigOpenSeq = 0;

async function openAiConfig() {
  // The backend is the only source of truth for the API URL. The boot-time read
  // can fire before login settles (and 401), so re-read here: otherwise the card
  // would open with a blank configuration after a fresh login or org switch.
  const openSeq = ++aiConfigOpenSeq;
  if (typeof loadAiConfigFromBackend === "function") {
    await loadAiConfigFromBackend().catch(() => {});
  }
  if (openSeq !== aiConfigOpenSeq) return;
  aiConfigBeforeEdit = JSON.parse(JSON.stringify(state.aiConfig));
  clearImageModelTestResult();
  // Reopening the card always starts blank: a result from a previous visit
  // must not be presented as the state of this one.
  resetAiModelStatus();
  confirmedApiKeyTypes.clear();
  apiKeyEditBackups.text = null;
  apiKeyEditBackups.image = null;
  knowledgeSecretDirty.dify = false;
  state.aiConfigView = "api";
  renderAiConfig();
  showModal($("#aiConfigModal"));
}

function closeAiConfig({ discardChanges = true } = {}) {
  hideModal($("#aiConfigModal"));
  if (discardChanges && aiConfigBeforeEdit) {
    state.aiConfig = aiConfigBeforeEdit;
  }
  confirmedApiKeyTypes.clear();
  apiKeyEditBackups.text = null;
  apiKeyEditBackups.image = null;
  aiConfigBeforeEdit = null;
}

function collectAiConfigForm() {
  const previousText = state.aiConfig.text || {};
  const previousImage = state.aiConfig.image || {};
  state.aiConfig.text = {
    apiUrl: $("#textApiUrl").value.trim(),
    apiKey: apiKeyDraftValue("text"),
    model: modelValue("text"),
    models: previousText.models || [],
    hasApiKey: Boolean(previousText.hasApiKey),
    maskedApiKey: previousText.maskedApiKey || "",
  };
  state.aiConfig.image = {
    apiUrl: $("#imageApiUrl").value.trim(),
    apiKey: apiKeyDraftValue("image"),
    model: modelValue("image"),
    models: previousImage.models || [],
    hasApiKey: Boolean(previousImage.hasApiKey),
    maskedApiKey: previousImage.maskedApiKey || "",
  };
  state.aiConfig.templateAnalysis = normalizeTemplateAnalysisConfig({
    concurrency: $("#templateAnalysisConcurrency").value,
  });
  state.aiConfig.watermark = {
    ...(state.aiConfig.watermark || {}),
    enabled: $("#watermarkProcessingEnabled").checked,
    visible: $("#watermarkVisibleEnabled").checked,
    metadata: $("#watermarkMetadataEnabled").checked,
  };
}

function renderAiConfig() {
  const config = state.aiConfig;
  $("#textApiUrl").value = config.text.apiUrl || "";
  renderApiKeyField("text", config.text);
  $("#textModel").value = config.text.model || "";
  $("#templateAnalysisConcurrency").value = normalizeTemplateAnalysisConfig(config.templateAnalysis).concurrency;
  $("#imageApiUrl").value = config.image.apiUrl || "";
  renderApiKeyField("image", config.image);
  $("#imageModel").value = config.image.model || "";
  // 知识库连接是组织级配置，已拆到「团队治理 → 知识库连接」（web/js/org/knowledge-settings.js）：
  // 它和这里的上游 API/水印不是一回事，写入权限的来源也不同。
  const watermark = config.watermark || defaultAiConfig.watermark;
  $("#watermarkProcessingEnabled").checked = watermark.enabled !== false;
  $("#watermarkVisibleEnabled").checked = watermark.visible !== false;
  $("#watermarkMetadataEnabled").checked = watermark.metadata !== false;
  const watermarkRuntime = watermark.runtime || {};
  $("#watermarkRuntimeStatus").textContent = watermarkRuntime.lightweightReady
    ? `基础处理已就绪${watermarkRuntime.packageVersion ? ` · v${watermarkRuntime.packageVersion}` : ""}`
    : "基础处理依赖未安装，生成失败时仍会保留原图";
  $("#watermarkRuntimeStatus").className = `model-fetch-status ${watermarkRuntime.lightweightReady ? "success" : "warning"}`;
  renderModelOptions("text", config.text.models || []);
  renderModelOptions("image", config.image.models || []);
  // The model status line only reports a live fetch/test from this session.
  // A stored model list is not evidence that the provider answers right now,
  // so showing it here made an unverified configuration look healthy.
  renderAiModelStatus("text");
  renderAiModelStatus("image");
  const configuredProviders = [config.text, config.image].filter((provider) => (
    provider?.apiUrl && provider?.model && (provider?.hasApiKey || provider?.apiKey)
  )).length;
  $("#apiConfigTabStatus").textContent = `${configuredProviders}/2 接口已配置`;
  $("#watermarkTabStatus").textContent = watermark.enabled === false ? "当前未启用" : "后处理已启用";

  renderAiConfigView();
}


// ----- js/rewrite/drafts.js -----
function draftReferenceQueueKey(draftId = state.activeDraftId) {
  return String(draftId || "");
}

// ── 本地参考文件队列的单一读口 / 写口（方案 A 第一阶段） ──────────────────────
// 同一份待上传队列有两个副本：
//   ① state.localUploadQueue         —— 当前生效副本，渲染与增删都读它；
//   ② state.localUploadQueuesByDraft —— 按草稿 id 分桶的副本，切草稿时同步。
// 哪个副本没跟上，就会看到「切回去条目没了」这类分裂。这里把「按草稿取队列」和
// 「按草稿写队列」收成唯一入口；两个副本都保留、都同步，一个都不删——没有单一读口
// 之前删副本就是数据丢失。会话内的落桶/取桶由 persistActiveLocalUploadQueue /
// restoreLocalUploadQueueForDraft 走这两个口完成。
function storedLocalUploadQueueForDraft(draftId = state.activeDraftId) {
  const key = draftReferenceQueueKey(draftId);
  if (!key) return [];
  if (!(state.localUploadQueuesByDraft instanceof Map)) return [];
  return state.localUploadQueuesByDraft.get(key) || [];
}

// 单一读口：给定草稿的待上传队列。当前草稿以生效副本为准——增删都发生在它上面，
// 它可能比分桶副本新；其它草稿只能读分桶副本。
function localUploadQueueForDraft(draftId = state.activeDraftId) {
  const key = draftReferenceQueueKey(draftId);
  if (!key) return [];
  if (key === draftReferenceQueueKey(state.activeDraftId)) {
    return Array.isArray(state.localUploadQueue) ? state.localUploadQueue : [];
  }
  return storedLocalUploadQueueForDraft(draftId);
}

// 分桶副本的写口：按草稿 id 落一份。两个副本存的是同一个数组引用，
// 队列上的原位 push 两边都能看到；重新赋值（filter 等）后要调用这里的入口再落一次。
function writeStoredLocalUploadQueue(draftId, queue) {
  if (!(state.localUploadQueuesByDraft instanceof Map)) state.localUploadQueuesByDraft = new Map();
  const key = draftReferenceQueueKey(draftId);
  if (key) state.localUploadQueuesByDraft.set(key, queue || []);
}

// 单一写口：把队列写到指定草稿。目标就是当前草稿时，生效副本与分桶副本一起改——
// 只写一个正是「切草稿 / 切组织后条目丢失」的成因。
function setLocalUploadQueue(queue, draftId = state.activeDraftId) {
  const next = Array.isArray(queue) ? queue : [];
  const key = draftReferenceQueueKey(draftId);
  if (!key) return next;
  if (key === draftReferenceQueueKey(state.activeDraftId)) state.localUploadQueue = next;
  writeStoredLocalUploadQueue(draftId, next);
  return next;
}

function persistActiveLocalUploadQueue() {
  writeStoredLocalUploadQueue(state.activeDraftId, state.localUploadQueue);
}

function restoreLocalUploadQueueForDraft(draftId = state.activeDraftId) {
  state.localUploadQueue = storedLocalUploadQueueForDraft(draftId);
}

function setActiveDraftId(draftId) {
  const changed = String(state.activeDraftId || "") !== String(draftId || "");
  if (changed) persistActiveLocalUploadQueue();
  // 主体参考图属于上一条笔记的替换任务，跟着笔记走会把旧图当新主体（P69）。
  if (changed && typeof clearReplacementReferences === "function") clearReplacementReferences();
  state.activeDraftId = draftId || null;
  restoreLocalUploadQueueForDraft(state.activeDraftId);
}

function createDraft({ source, seed = "", images = [], origin = "", title, body, tags, workflow = "original" }) {
  state.activeWorkflow = workflow === "replica" ? "replica" : "original";
  const selectedPromptMode = typeof promptModeForDraft === "function"
    ? promptModeForDraft(source, workflow)
    : "rewrite";
  const selectedPromptSnapshot = typeof promptSnapshotForDraft === "function"
    ? promptSnapshotForDraft(source, workflow)
    : currentPromptSnapshot();
  const draft = {
    id: uid("draft"),
    taskNumber: nextDraftTaskNumber(),
    source,
    origin,
    workflow,
    title: title ?? "",
    body: body ?? "",
    tags: tags ?? "",
    images: normalizeImages(images),
    knowledgeSource: "local",
    knowledgeChannel: "pure-ai",
    promptTemplateMode: selectedPromptMode,
    promptSnapshot: currentPromptSnapshot(),
    status: "editing",
    createdAt: new Date().toISOString(),
  };
  draft.promptSnapshot = selectedPromptSnapshot;
  if (workflow === "replica") {
    draft.imagePromptCustomized = { cover: false, inner: false };
  }
  state.drafts.unshift(draft);
  if (typeof persistActiveLocalUploadQueue === "function") persistActiveLocalUploadQueue();
  state.activeDraftId = draft.id;
  restoreViralSelectionsFromDraft(draft);
  if (typeof restoreLocalUploadQueueForDraft === "function") restoreLocalUploadQueueForDraft(draft.id);
  else state.localUploadQueue = [];
  saveState();
  renderAll();
  return draft;
}

// 插件探测与"本机装没装"的展示统一在 core/extension-status.js 里：那边只有一份检测结果，
// 抓取/采集/首页状态行共用同一份，动作入口在检测没完成时会先等它出结果（见 requireCaptureExtension）。

// 采集页要「后台打开、不抢焦点、完事自己关」。网页的 window.open 做不到——浏览器
// 一定会把新标签切到前台，而只有扩展的 chrome.tabs.create({active:false}) 可以。
// 所以先把交接地址通过 postMessage 交给扩展的桥接脚本，扩展不在时才退回 window.open。
function requestCaptureTabViaExtension(handoffUrl, jobId, timeoutMs = 700) {
  return new Promise((resolve) => {
    const requestId = "capture-" + Date.now() + "-" + Math.random().toString(36).slice(2);
    let settled = false;
    const finish = (handled) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", handler);
      resolve(handled);
    };
    const handler = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.source !== "shuzuotai-workstation") return;
      if (data.type !== "open-capture-tab-result" || data.requestId !== requestId) return;
      finish(Boolean(data.ok));
    };
    window.addEventListener("message", handler);
    try {
      window.postMessage(
        { source: "shuzuotai-workstation", type: "open-capture-tab", url: handoffUrl, jobId, requestId },
        window.location.origin,
      );
    } catch (_error) {
      finish(false);
      return;
    }
    window.setTimeout(() => finish(false), timeoutMs);
  });
}

async function openImportNoteCollector(handoffUrl, jobId) {
  if (await requestCaptureTabViaExtension(handoffUrl, jobId)) return null;
  // 退路（插件在、但后台这次没开出标签页）：还是新标签页，但会短暂抢一次焦点。
  const win = window.open(handoffUrl, "_blank");
  if (!win) {
    // window.open 被浏览器拦掉时返回 null；以前这里直接咽下去，表现就是"点了没反应"。
    throw new Error("采集页没能打开：浏览器拦住了新标签页，请允许本站弹出窗口后重试");
  }
  try { win.blur(); } catch {}
  if (typeof refocusWorkstation === "function") refocusWorkstation();
  return win;
}

// 采集授权：这段文本一律由服务端下发（server/services/note_consent.py 的 contract），
// 前端不复刻一份——复刻就会出现"页面显示的"和"落库记录的"不是同一段文字，而这份记录的
// 全部意义就是它指的是用户真正看过的那句话。服务端在建任务时还会再拦一次，所以下面这个
// 弹层不是唯一防线，只是把拒绝的原因提前讲清楚。
const NOTE_CAPTURE_CONSENT_PATH = "/api/import-note/consent";
const NOTE_CAPTURE_CONSENT_DECLINED = "已取消采集：需要先同意采集授权说明";

let noteCaptureConsentPending = null;

function noteCaptureConsentElements() {
  return {
    modal: document.getElementById("noteCaptureConsentModal"),
    body: document.getElementById("noteCaptureConsentBody"),
    statement: document.getElementById("noteCaptureConsentStatement"),
    accept: document.getElementById("acceptNoteCaptureConsentBtn"),
    cancel: document.getElementById("cancelNoteCaptureConsentBtn"),
  };
}

function closeNoteCaptureConsentModal() {
  const modal = noteCaptureConsentElements().modal;
  if (!modal) return;
  modal.classList.add("hidden");
  modal.setAttribute("aria-hidden", "true");
}

// 同一个弹层同时只服务一次确认：连点两次「采集」不该叠出两个弹层，也不该让第二个调用者
// 以为自己被拒绝。后到的调用者等的是同一份结果。
function requestNoteCaptureConsent(consent) {
  if (noteCaptureConsentPending) return noteCaptureConsentPending;
  const elements = noteCaptureConsentElements();
  if (!elements.modal) return Promise.resolve(false);
  const contract = (consent && consent.contract) || {};
  if (elements.body) {
    elements.body.innerHTML = (contract.points || [])
      .map(function (point) { return "<p>" + escapeHtml(point) + "</p>"; })
      .join("");
  }
  if (elements.statement) elements.statement.textContent = contract.statement || "";
  elements.modal.classList.remove("hidden");
  elements.modal.setAttribute("aria-hidden", "false");
  noteCaptureConsentPending = new Promise(function (resolve) {
    const detach = function () {
      if (elements.accept) elements.accept.removeEventListener("click", onAccept);
      if (elements.cancel) elements.cancel.removeEventListener("click", onCancel);
    };
    const settle = function (accepted) {
      detach();
      noteCaptureConsentPending = null;
      closeNoteCaptureConsentModal();
      resolve(accepted);
    };
    const onAccept = async function () {
      if (elements.accept) elements.accept.disabled = true;
      try {
        await postBackend(NOTE_CAPTURE_CONSENT_PATH, {
          version: contract.version,
          textHash: contract.textHash,
        });
        settle(true);
      } catch (error) {
        // 没记下来的「同意」不算同意：留在弹层里让用户重试，而不是关掉当作已同意。
        toast("授权确认没有保存：" + ((error && error.message) || "请稍后重试"), { kind: "error", duration: 6000 });
        if (elements.accept) elements.accept.disabled = false;
      }
    };
    const onCancel = function () { settle(false); };
    if (elements.accept) elements.accept.addEventListener("click", onAccept);
    if (elements.cancel) elements.cancel.addEventListener("click", onCancel);
  });
  return noteCaptureConsentPending;
}

async function ensureNoteCaptureConsent() {
  const state = await getBackend(NOTE_CAPTURE_CONSENT_PATH);
  const consent = (state && state.consent) || {};
  if (!consent.required) return true;
  return await requestNoteCaptureConsent(consent);
}

// 两次采集之间的最小静止时间，与服务端 XHS_IMPORT_NOTE_MIN_GAP_SECONDS 的默认值一致。
// 服务端才是权威（超了回 429），这里先等够是为了让"连着点两次"变成一次正常的等待
// 而不是一次失败。存在 localStorage 里：同一浏览器里开两个工作站标签页也共用一个计时。
// 计时基准是上一次采集的**结束**时刻：一页从打开到关掉会占二十几到四十几秒
// （采集页读取前驻留 8~25 秒、关页前再驻留 5~15 秒，见 extension/note_capture.js），
// 从开始计时的话这条规则拦不住"两条笔记页同时开着"——那正是它要防的事。
const IMPORT_NOTE_MIN_GAP_MS = 10000;
const IMPORT_NOTE_LAST_CAPTURE_KEY = "shuzuotai:import-note:lastCaptureFinishedAt";
// 采集页会先驻留 8-25 秒才去读笔记（extension/note_capture.js 的 DWELL_BEFORE_READ_MS），
// 所以"任务还停在 queued"本来就有十几秒是正常的。这个阈值要盖住 页面加载 + 驻留上限 +
// 首次上报，否则每次正常采集都会先弹一句"插件还没接手"。
const IMPORT_NOTE_TAKEOVER_HINT_MS = 40000;

function readImportNoteLastCaptureFinishedAt() {
  try {
    return Number(window.localStorage.getItem(IMPORT_NOTE_LAST_CAPTURE_KEY) || 0) || 0;
  } catch (_error) {
    return 0;
  }
}

function markImportNoteCaptureFinished() {
  try {
    window.localStorage.setItem(IMPORT_NOTE_LAST_CAPTURE_KEY, String(Date.now()));
  } catch (_error) {
    /* 隐私模式/存储被禁：本地不记，服务端那条 429 仍然兜底 */
  }
}

// 条间间隔没到就**直接拒绝这次点击**，不在本地排队等待。
//
// 曾经改成"等够再自动开始"，但那是替用户做了一个他没再确认过的决定：他点一下，
// 十秒后系统自己开了一条采集。要的是"现在不行"——给一句话，不建任务，用户想采再点一次。
function importNoteGapSeconds() {
  const remaining = IMPORT_NOTE_MIN_GAP_MS - (Date.now() - readImportNoteLastCaptureFinishedAt());
  return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
}

function importNoteGapError() {
  const seconds = importNoteGapSeconds();
  if (seconds <= 0) return null;
  return noteCaptureNoticeError("两次采集至少间隔 10 秒，还需 " + seconds + " 秒再试");
}

// 采集进行中、或处在条间间隔里，用户又点了一次时该说什么。
// render.js 的 handleLinks 用这个替代原来的静默 return——那一下什么都不发生、
// 也没有提示，用户只能得出"点了没用、要等一会儿再点"的结论。
function linkImportBusyReason() {
  const seconds = importNoteGapSeconds();
  if (seconds > 0) {
    return "两次采集至少间隔 10 秒，还需 " + seconds + " 秒再试";
  }
  return "上一次采集还在进行中，请等它结束后再试";
}

// 链接导入已迁移到浏览器插件：建一个 import-note 任务 + 一次性交接，在后台标签页里
// 由插件逐条采集，前端轮询直到完成。返回的 job.results 与旧 /api/import-links 的 notes
// 同构（每条带 ok/title/body/images/tags/origin/error/message）。
// 取消采集：用户点按钮、采集页被关、或超时收尾都走这里。标在本地是为了让等待循环
// 立刻收场——服务端那条 429/过期规则只是兜底，不该让用户等它。
// 取消是用户主动做的事（关掉采集页），不是失败。调用方靠这个标记把它和真错误分开——
// 否则"已取消"会走进失败面板，被渲染成「这次采集没拿到内容」外加一套补救建议。
function noteCaptureNoticeError(message) {
  const error = new Error(message);
  error.noteCaptureNotice = true;
  return error;
}

function noteCaptureCancelledError(message) {
  const error = noteCaptureNoticeError(message);
  error.noteCaptureCancelled = true;
  return error;
}

// 采集期间发起按钮变成"取消采集"。它是收场动作、不是主操作，所以做成低对比的次级样式
// （见 editor.css 的 .is-capture-cancel）：不主动抢注意力，悬停时才显形。
function armNoteCaptureCancel(button, jobId) {
  if (!button) return () => {};
  const original = button.textContent;
  const onClick = async () => {
    if (button.dataset.noteCaptureCancelling === "1") return;
    button.dataset.noteCaptureCancelling = "1";
    button.disabled = true;
    button.textContent = "正在取消...";
    await cancelNoteCapture(jobId);
  };
  button.disabled = false;
  button.textContent = "取消采集";
  button.title = "取消本次采集";
  button.classList.add("is-capture-cancel");
  button.dataset.noteCaptureCancel = "1";
  button.addEventListener("click", onClick);
  return () => {
    button.removeEventListener("click", onClick);
    button.classList.remove("is-capture-cancel");
    delete button.dataset.noteCaptureCancel;
    delete button.dataset.noteCaptureCancelling;
    button.title = "";
    button.disabled = false;
    button.textContent = original;
  };
}

async function cancelNoteCapture(jobId) {
  const key = String(jobId || "").trim();
  if (!key) return false;
  try {
    await postBackend("/api/import-note/jobs/" + encodeURIComponent(key) + "/cancel", {});
  } catch (error) {
    /* 服务端那条过期规则兜底 */
  }
  return true;
}

async function captureNoteLinks(input, onProgress, options = {}) {
  // 授权先问，再查插件。插件那条提示是"去装/去重载"，在用户还不知道这个功能会拿他的
  // 登录态做什么之前就让他装东西，是本末倒置的。
  if (!(await ensureNoteCaptureConsent())) throw new Error(NOTE_CAPTURE_CONSENT_DECLINED);
  // 先确认插件在，再建任务。顺序反过来会留下一条没人接的 queued 任务：用户看到的是
  // 按钮转五分钟然后超时，而失败原因（插件没装/没重载）全程不可见。
  // 检测本身可能还没出结果（首页那次检测在登录后才发起），所以这里等的是**同一份**检测：
  // 等待期间如实提示"正在等待插件连接完成"，出结果再决定继续还是拦住——而不是自己另开一次
  // 900ms 探测，把"还没问完"误报成"插件没响应"。
  const failure = await requireCaptureExtension("抓取");
  if (failure) throw captureExtensionMissingError(failure);
  const payload = typeof input === "string" ? { text: input } : { urls: input };
  payload.workstationUrl = backendBaseUrl;
  // 间隔没到就到此为止，不建任务（建了再等会让任务停在 queued，界面上显示"插件没接管"）。
  const gapError = importNoteGapError();
  if (gapError) throw gapError;
  let created;
  try {
    created = await postBackend("/api/import-note/jobs", payload);
  } catch (error) {
    // 服务端才是权威：本地这一次读到"已同意"、服务端却说没有时（换了账号、确认记录
    // 被清掉、文案版本 bump），用服务端回的那份文本再问一次，然后重试一次。
    // 只重试一次——再失败就是服务端真的不肯放行，重复提交只会重复拿同一个拒绝。
    if (String((error && error.errorCode) || "") !== "note_capture_consent_required") throw error;
    const consent = error && error.payload ? error.payload.consent : null;
    if (!(await requestNoteCaptureConsent(consent))) throw new Error(NOTE_CAPTURE_CONSENT_DECLINED);
    created = await postBackend("/api/import-note/jobs", payload);
  }
  const jobId = created?.job?.id || "";
  const handoffUrl = created?.handoffUrl || "";
  if (!jobId || !handoffUrl) throw new Error("采集任务创建失败");
  await openImportNoteCollector(handoffUrl, jobId);
  const disarmCancel = armNoteCaptureCancel(options.button, jobId);
  try {
    return await waitForImportNoteJob(jobId, onProgress);
  } finally {
    disarmCancel();
    // 等它真的结束再计时。失败或超时也照样记：这一页确实开过了，那段占用是真的。
    markImportNoteCaptureFinished();
  }
}

// 插件每完成一次 /notes|/images|/complete 就会通过桥接脚本把最新 job 直接推到本页，
// 这里等它推过来；推不到（插件没重载、服务进程重启、本页导航过）才退回慢速兜底轮询。
function waitForImportNoteJobPush(jobId, timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (job) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      resolve(job);
    };
    const onMessage = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.source !== "shuzuotai-workstation") return;
      if (String(data.jobId || "") !== String(jobId)) return;
      // 用户把采集页关掉了。扩展不会自己取消——worker 凭据按设计只够上报进度，
      // 取消是工作站（用户会话）的动作——所以由扩展通知过来，这里带会话去取消。
      if (data.type === "import-note-abandoned") {
        finish({ abandoned: true });
        return;
      }
      if (data.type !== "import-note-update") return;
      if (data.job && typeof data.job === "object") finish(data.job);
    };
    window.addEventListener("message", onMessage);
    window.setTimeout(() => finish(null), timeoutMs);
  });
}

async function waitForImportNoteJob(jobId, onProgress) {
  const startedAt = Date.now();
  const deadline = startedAt + 5 * 60 * 1000;
  // 采集页要先驻留（读取前 8~25 秒、关页前 5~15 秒，见 extension/note_capture.js），
  // 再加上页面加载和图片下载，整套 20~45 秒；慢页面会更久。先把预期说出去，用户才
  // 不会因为"半天没反应"再点一次——重复点击正是这条链路最想避免的事，所以宁可把
  // 时间说过头：早到是惊喜，晚到就是用户自己开第二条。
  if (typeof toast === "function") {
    toast("正在浏览器中采集这条笔记，可能需要 1~3 分钟，请稍候…", { duration: 8000 });
  }
  let warnedNotStarted = false;
  for (;;) {
    let job = await waitForImportNoteJobPush(jobId, 2000);
    if (job && job.abandoned) {
      // 用户把采集页关掉了。扩展只负责通知，取消要带用户会话做（worker 凭据没有这个
      // 权限），所以在这里收尾：任务立刻进终态，不用干等 5 分钟的过期窗口，
      // 也不会再弹那句误导人的"插件还没接手"。
      await postBackend(
        "/api/import-note/jobs/" + encodeURIComponent(jobId) + "/cancel",
        {},
      ).catch(() => {});
      throw noteCaptureCancelledError("采集页已关闭，本次采集已取消");
    }
    if (!job) {
      job = (await getBackend(`/api/import-note/jobs/${encodeURIComponent(jobId)}`))?.job || {};
    }
    if (typeof onProgress === "function") onProgress(job);
    if (job.status === "completed") return job;
    // 用户按了取消按钮 → 服务端把任务置为 cancelled → 这里看到的就是自己刚做的事。
    // 必须带 notice 标记，否则它会和真失败一样走进「这次采集没拿到内容」那个面板。
    if (job.status === "cancelled") throw noteCaptureCancelledError("已取消这次采集");
    if (job.status === "failed") throw new Error(job.error || "采集失败");
    // 一直停在 queued = 插件根本没接手（采集页没开出来，或交接信息丢了）。以前这里会
    // 静默等到 5 分钟才超时，中间一句线索都没有，所以先给用户一句话——但要等过采集页
    // 的正常驻留（IMPORT_NOTE_TAKEOVER_HINT_MS），否则每次采集都误报一次。
    if (!warnedNotStarted && job.status === "queued" && Date.now() - startedAt > IMPORT_NOTE_TAKEOVER_HINT_MS) {
      warnedNotStarted = true;
      if (typeof toast === "function") {
        toast("浏览器插件还没接手这次采集：请确认插件已启用，并在扩展页重新加载一次后重试", { kind: "error", duration: 8000 });
      }
    }
    if (Date.now() > deadline) {
      // 超时就把任务收掉。不收的话它会一直挂在"进行中"：账号被服务端那条过期规则
      // 锁到 5 分钟，记录也永远留在索引里。取消本身失败不影响下面那句超时提示——
      // 服务端仍然兜底，用户不该因为清理没成功而看到另一个错误。
      await postBackend(
        "/api/import-note/jobs/" + encodeURIComponent(jobId) + "/cancel",
        {},
      ).catch(() => {});
      throw new Error("采集超时，请确认浏览器插件已安装并在小红书登录后重试");
    }
  }
}

function currentDraft() {
  const draft = state.drafts.find((item) => item.id === state.activeDraftId) || null;
  return draft && draftWorkflow(draft) === state.activeWorkflow ? draft : null;
}

function localReferenceSelectionFromDraft(draft) {
  if (draft?.knowledge?.mode !== "local") return [];
  return (draft.knowledge.fileReferences || [])
    .filter((item) => item.sourceId !== "upload")
    .map((item) => {
      const key = item.key || `${item.sourceId || ""}::${item.relativePath || item.filename || ""}`;
      const pageRange = String(item.pageRange || "");
      return {
        key,
        sourceId: item.sourceId || "",
        sourceName: item.sourceName || "本地文件",
        name: item.filename || item.name || "本地文件",
        relativePath: item.relativePath || item.filename || "",
        size: Number(item.size || 0),
        lastModified: Number(item.lastModified || 0),
        pageMode: pageRange ? "range" : "all",
        pageRanges: pageRange ? localPageRangesFromText(pageRange) : [],
        pageRange,
      };
    });
}

function syncLocalReferenceSelectionOwner(draft = currentDraft()) {
  const ownerId = draft?.id || "";
  if (state.localReferenceSelectionOwnerId === ownerId) return false;
  state.localUploadQueue = [];
  state.localReferenceSelection = localReferenceSelectionFromDraft(draft);
  state.localReferenceSelectionOwnerId = ownerId;
  return true;
}

function draftWorkflow(draft) {
  return draft?.workflow === "replica" ? "replica" : "original";
}

function workflowDrafts(workflow = state.activeWorkflow) {
  return state.drafts.filter((draft) => draftWorkflow(draft) === workflow);
}

function ensureActiveWorkflowDraft() {
  const active = state.drafts.find((draft) => draft.id === state.activeDraftId);
  if (active && draftWorkflow(active) === state.activeWorkflow) return active;
  const next = workflowDrafts()[0] || null;
  setActiveDraftId(next?.id || null);
  return next;
}

function isReplicaDraft(draft) {
  return draft?.workflow === "replica";
}

function isReplicaImageDraft(draft) {
  if (!isReplicaDraft(draft)) return false;
  return ["upload", "image"].includes(String(draft.source || "").toLowerCase())
    || draft.promptTemplateMode === "image";
}

// 一次生成到底有没有可用的输入素材。空草稿（没有标题、正文、标签，也没有图片）也能点
// 生成，会真实调用 AI 并真实扣费，所以按钮门控与生成入口共用这一条判据，后端
// /api/ai/text(-bundle) 用同一条规则拒绝（server/services/knowledge.py）。
function draftHasUsableContent(draft) {
  if (!draft) return false;
  if ([draft.title, draft.body, draft.tags].some((value) => String(value || "").trim())) return true;
  return normalizeImages(draft.images || []).length > 0;
}

const EMPTY_DRAFT_GENERATION_HINT = "先填写标题、正文，或加入图片素材，再生成";
// 拆解入库的缺料提示：它要的是"可拆的笔记"，只有提示词没有正文/图片时拆不出模板。
const EMPTY_DRAFT_TEMPLATE_IMPORT_HINT = "先导入笔记内容或加入图片，再拆解入库";

function syncRewriteWorkspaceChrome(draft) {
  const section = $("#section-rewrite");
  const replicaMode = state.activeWorkflow === "replica";
  const imageMode = isReplicaImageDraft(draft);
  section?.classList.toggle("replica-mode", replicaMode);
  section?.classList.toggle("replica-image-mode", imageMode);
  section?.classList.toggle("replica-link-mode", replicaMode && !imageMode);
  section?.classList.toggle("original-mode", !replicaMode);
}

function draftDisplayTitle(draft, maxLength = 48) {
  if (!draft) return "未命名草稿";
  const topic = draft.viralBrief?.topic || draft.executionPlan?.topicPackage?.topic || "";
  const rawTitle = draft.promptMode?.title ? (topic || "AI 改写草稿") : (draft.title || topic || "未命名草稿");
  const title = String(rawTitle).replace(/\s+/g, " ").trim();
  return title.length > maxLength ? `${title.slice(0, maxLength)}…` : title;
}

function sourceLabel(source) {
  return {
    link: "小红书链接",
    upload: "上传图片",
    image: "AI 生图",
    plugin: "浏览器插件",
    viral: "爆款组合",
    blank: "手动新建",
  }[source] || "素材";
}

function draftSourceLabel(draft) {
  if (isReplicaImageDraft(draft)) return "图生文案";
  if (isReplicaDraft(draft) && draft?.source === "link") return "链接快速改写";
  return sourceLabel(draft?.source);
}

function draftSourceVisual(source) {
  return {
    link: { key: "link", icon: "link-2" },
    upload: { key: "upload", icon: "images" },
    image: { key: "image", icon: "wand-sparkles" },
    plugin: { key: "plugin", icon: "puzzle" },
    viral: { key: "viral", icon: "library-big" },
    blank: { key: "blank", icon: "file-pen-line" },
  }[source] || { key: "default", icon: "file-text" };
}

function draftUsesGeneratedBlankTitle(draft) {
  if (!draft || draft.source !== "blank") return false;
  const normalized = String(draft.title || "").replace(/\s+/g, "").replace(/\|/g, "｜");
  return !normalized || normalized === "新笔记草稿｜手动创建";
}

function draftTaskTitle(draft) {
  const title = draftDisplayTitle(draft, 36);
  if (draftUsesGeneratedBlankTitle(draft) || title === "未命名草稿") {
    return "未命名任务";
  }
  return title;
}

function draftTaskAge(createdAt) {
  const timestamp = Date.parse(createdAt || "");
  if (!Number.isFinite(timestamp)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return new Date(timestamp).toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" });
}

function draftTaskStatus(draft) {
  if (draft.status === "queued") return { key: "queued", label: "待发布" };
  if (draftUsesGeneratedBlankTitle(draft)) return { key: "idle", label: "待命名" };
  if (draft.id === state.activeDraftId) return { key: "active", label: "编辑中" };
  return { key: "idle", label: "待编辑" };
}

function renderMetrics() {
  const metricDrafts = $("#metricDrafts");
  const metricPublish = $("#metricPublish");
  const draftCount = $("#draftCount");
  const deleteAllDraftsButton = $("#deleteAllDraftsBtn");
  if (metricDrafts) metricDrafts.textContent = state.drafts.length;
  if (metricPublish) metricPublish.textContent = state.publish.length;
  const creditsSide = $("#billingCreditsSide");
  if (creditsSide) creditsSide.textContent = billingCreditsText({ balance: billingState.balance });
  const visibleDrafts = workflowDrafts();
  if (draftCount) draftCount.textContent = `任务：${visibleDrafts.length}`;
  if (deleteAllDraftsButton) deleteAllDraftsButton.disabled = visibleDrafts.length === 0;
  renderHomeDashboard();
}

function renderHomeDashboard() {
  const recentList = $("#homeRecentDrafts");
  if (!recentList) return;
  const draftCount = $("#homeDraftCount");
  const publishCount = $("#homePublishCount");
  const imageCount = $("#homeImageCount");
  if (draftCount) draftCount.textContent = state.drafts.length;
  if (publishCount) publishCount.textContent = state.publish.length;
  if (imageCount) imageCount.textContent = state.generatedImages.length;

  const recentDrafts = [...state.drafts]
    .sort((a, b) => Date.parse(b.createdAt || "") - Date.parse(a.createdAt || ""))
    .slice(0, 3);
  recentList.innerHTML = recentDrafts.length
    ? recentDrafts.map((draft) => {
        const workflow = draftWorkflow(draft);
        const status = draftTaskStatus(draft);
        const age = draftTaskAge(draft.createdAt) || "最近编辑";
        return `<button type="button" class="home-recent-item" data-home-draft-id="${escapeHtml(draft.id)}" data-home-workflow="${workflow}">
          <span class="home-recent-icon"><i data-lucide="${workflow === "replica" ? "copy" : "blocks"}"></i></span>
          <span class="home-recent-copy"><strong>${escapeHtml(draftTaskTitle(draft))}</strong><small>${workflow === "replica" ? "快速笔记" : "原创笔记"} · ${escapeHtml(age)}</small></span>
          <span class="home-recent-status status-${status.key}">${status.label}</span>
          <i data-lucide="chevron-right" aria-hidden="true"></i>
        </button>`;
      }).join("")
    : `<div class="home-recent-empty"><span><i data-lucide="file-plus-2"></i></span><div><strong>还没有创作任务</strong><small>选择上方任一工作流开始第一篇内容。</small></div></div>`;
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function renderDraftList() {
  const list = $("#draftList");
  if (!list) return;
  const visibleDrafts = workflowDrafts();
  if (!visibleDrafts.length) {
    list.innerHTML = `<div class="empty-state"><strong>暂无${state.activeWorkflow === "replica" ? "快速笔记" : "原创"}任务</strong><span>从当前工作区的笔记导入页创建任务。</span></div>`;
    return;
  }
  list.innerHTML = visibleDrafts
    .map(
      (draft) => {
        const displayTitle = draftTaskTitle(draft);
        const images = currentPreviewImages(draft);
        const thumbnail = images[0] || "";
        const visual = draftSourceVisual(draft.source);
        const status = draftTaskStatus(draft);
        const age = draftTaskAge(draft.createdAt);
        const taskNumber = `#${String(Number(draft.taskNumber || 0)).padStart(2, "0")}`;
        const sourceName = draftSourceLabel(draft);
        const metadata = [status.label, sourceName, `${images.length} 图`, age].filter(Boolean).join(" · ");
        return `
        <article class="draft-item source-${visual.key} status-${status.key} ${draft.id === state.activeDraftId ? "active" : ""}" data-draft-id="${draft.id}" tabindex="0" role="button" aria-label="选择素材任务：${escapeHtml(displayTitle)}，编号 ${taskNumber}，${escapeHtml(metadata)}">
          <span class="draft-item-identity" title="${escapeHtml(sourceName)}" aria-hidden="true">
            ${thumbnail ? `<img src="${escapeHtml(thumbnail)}" alt="" loading="lazy" />` : `<i data-lucide="${visual.icon}"></i>`}
          </span>
          <div class="draft-item-copy">
            <strong title="${escapeHtml(displayTitle)}">${escapeHtml(displayTitle)}</strong>
            <span class="draft-item-meta" title="${escapeHtml(metadata)}">${escapeHtml(metadata)}</span>
          </div>
          <div class="draft-item-side">
            <span class="draft-item-number" aria-label="任务编号 ${taskNumber}">${taskNumber}</span>
            <button type="button" class="draft-delete-btn" data-delete-draft-id="${draft.id}" aria-label="删除任务：${escapeHtml(displayTitle)}" title="删除任务"><i data-lucide="trash-2"></i></button>
          </div>
        </article>
      `;
      }
    )
    .join("");
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function deleteDraft(draftId, { confirm = true } = {}) {
  const index = state.drafts.findIndex((draft) => draft.id === draftId);
  if (index < 0) return;
  const draft = state.drafts[index];
  if (confirm && typeof window !== "undefined" && typeof window.confirm === "function" && !window.confirm(`删除“${draftTaskTitle(draft)}”？此操作无法撤销。`)) return;
  const wasActive = state.activeDraftId === draftId;
  state.drafts.splice(index, 1);
  if (wasActive) {
    setActiveDraftId(workflowDrafts()[0]?.id || null);
    restoreViralSelectionsFromDraft(currentDraft());
  }
  saveState();
  renderAll();
  toast("素材任务已删除");
}

function clearCurrentRewriteDraft() {
  const draft = currentDraft();
  if (!draft) {
    toast("当前没有可清空的笔记");
    return;
  }
  if (!window.confirm("清空当前笔记？标题、正文、图片和模板选择都会移除。")) return;
  deleteDraft(draft.id, { confirm: false });
  setSection("rewrite");
}

function closeDeleteAllDraftsModal() {
  const modal = $("#deleteAllDraftsModal");
  if (modal) hideModal(modal);
}

function requestDeleteAllDrafts() {
  const visibleDrafts = workflowDrafts();
  if (!visibleDrafts.length) {
    toast("暂无素材任务可删除");
    return;
  }
  const count = visibleDrafts.length;
  $("#deleteAllDraftsCount").textContent = `${count} 个并行任务`;
  $("#confirmDeleteAllDraftsBtn").textContent = `确定删除 ${count} 个任务`;
  showModal($("#deleteAllDraftsModal"));
}

function deleteAllDrafts() {
  const visibleDrafts = workflowDrafts();
  if (!visibleDrafts.length) {
    toast("暂无素材任务可删除");
    return;
  }
  const workflow = state.activeWorkflow;
  state.drafts = state.drafts.filter((draft) => draftWorkflow(draft) !== workflow);
  if (typeof setActiveDraftId === "function") setActiveDraftId(null);
  else state.activeDraftId = null;
  saveState();
  closeDeleteAllDraftsModal();
  renderAll();
  toast("素材任务已全部删除");
}

function viralBriefPlaceholder(brief = {}) {
  return [
    brief.audience ? `目标读者：${brief.audience}` : "",
    brief.pain ? `要解决的问题：${brief.pain}` : "",
    brief.benefit ? `读者收获：${brief.benefit}` : "",
    brief.scenario ? `发布场景：${brief.scenario}` : "",
    brief.facts ? `事实资料：\n${brief.facts}` : "",
    brief.forbidden ? `禁止内容：${brief.forbidden}` : "",
  ].filter(Boolean).join("\n\n");
}

function draftViralLibraryItem(draft, kind) {
  const saved = draft?.librarySnapshots?.[kind];
  if (saved) return saved;
  const meta = viralLibraryMeta[kind];
  const selectedId = draft?.librarySelection?.[meta?.field];
  const current = (state.viralLibraries[kind] || []).find((item) => item.id === selectedId);
  if (current) return current;
  if (kind === "cover" || kind === "inner") {
    const reference = draft?.templateReferences?.[kind] || {};
    const templateId = reference.templateId || draft?.[`${kind}TemplateId`] || "";
    const template = layoutTemplateById(templateId);
    if (templateId) {
      return {
        id: selectedId || `${kind}:${templateId}`,
        name: `${template?.name || "已选视觉模板"} · ${kind === "cover" ? "封面" : "内页"}`,
        templateId,
        referenceImages: reference.images || template?.originalImages || [],
        aiImage: reference.aiImage || template?.aiImage || {},
      };
    }
  }
  return null;
}

function draftPlanReferenceImages(draft) {
  return draftPlannedPageEntries(draft).map((entry) => entry.src).filter(Boolean);
}

function selectedTextLibraryPreview(item, kind) {
  if (!item || !["topic", "title"].includes(kind)) return "";
  const zh = {
    uncategorized: "\u672a\u5206\u7c7b",
    pending: "\u5f85\u8865\u5145",
    topic: { category: "\u9009\u9898\u89d2\u5ea6", sample: "\u6837\u672c", audience: "\u4eba\u7fa4", pain: "\u75db\u70b9", benefit: "\u6536\u76ca" },
    title: { pattern: "\u7ed3\u6784", use: "\u7528\u9014", source: "\u6765\u6e90" },
  };
  const row = (label, value) => `<div><dt>${label}</dt><dd>${escapeHtml(value || zh.pending)}</dd></div>`;

  if (kind === "topic") {
    const topicPackage = topicPackageFromItem(item) || {};
    const classification = classifyTopicPackage(topicPackage);
    const facts = topicPackageFactsStatus(topicPackage);
    const pillars = (topicPackage.contentPillars || item.preview?.evidence || []).slice(0, 3);
    return `
      <div class="rewrite-text-library-preview">
        <div class="rewrite-card-meta"><span>${escapeHtml(classification.label)}</span><em>${zh.topic.sample} ${Number(topicPackage.performance?.sampleSize || item.performance?.sampleSize || 0)}</em></div>
        <strong class="rewrite-card-name">${escapeHtml(topicPackage.name || topicPackage.topic || "\u672a\u547d\u540d\u9009\u9898")}</strong>
        <dl class="rewrite-card-summary">
          ${row(zh.topic.audience, topicPackage.audience)}
          ${row(zh.topic.pain, topicPackage.pain)}
          ${row(zh.topic.benefit, topicPackage.benefit)}
        </dl>
        <div class="rewrite-card-pills">${pillars.map((pillar) => `<span>${escapeHtml(pillar)}</span>`).join("") || `<span>${zh.pending}</span>`}</div>
        <span class="topic-fact-status ${facts.ready ? "ready" : "missing"}">${escapeHtml(facts.label)}</span>
      </div>`;
  }

  const evidence = item.preview?.evidence || [];
  const pattern = item.pattern || item.summary || "\u4f7f\u7528\u901a\u7528\u6807\u9898\u7ed3\u6784";
  const sourceCount = (item.legacyAtomIds || []).length;
  const sampleSize = Number(item.performance?.sampleSize || 0);
  return `
    <div class="rewrite-text-library-preview">
      <div class="rewrite-card-meta"><span>\u6807\u9898\u7ed3\u6784</span><em>${zh.title.source} ${sourceCount} · ${zh.topic.sample} ${sampleSize}</em></div>
      <strong class="rewrite-card-name">${escapeHtml(item.name || "\u672a\u547d\u540d\u6807\u9898\u7ed3\u6784")}</strong>
      <dl class="rewrite-card-summary">
        ${row(zh.title.pattern, pattern)}
        ${row(zh.title.use, item.summary || "\u6839\u636e\u9009\u9898\u5185\u5bb9\u751f\u6210\u53ef\u66ff\u6362\u6807\u9898")}
        ${row(zh.title.source, sourceCount ? `${sourceCount} \u4e2a\u7ed3\u6784\u539f\u5b50` : "\u5f85\u8865\u5145")}
      </dl>
      <div class="rewrite-card-pills">${evidence.slice(0, 3).map((value) => `<span>${escapeHtml(value)}</span>`).join("") || `<span>\u53ef\u6309\u9009\u9898\u53d8\u91cf\u66ff\u6362</span>`}</div>
      <span class="topic-fact-status ready">\u5df2\u52a0\u5165\u5f53\u524d\u7ec4\u5408</span>
    </div>`;
}

function renderRewriteLibrarySelections(draft) {
  const labels = { topic: "选题", title: "标题", cover: "封面", inner: "内页" };
  const items = Object.keys(labels).map((kind) => ({ kind, item: draftViralLibraryItem(draft, kind) }));
  const selectedCount = items.filter(({ item }) => item).length;
  const displayName = (item, kind) => {
    if (!item) return kind === "topic" ? "等待选择" : "使用通用规则";
    if (kind === "topic") {
      const topicPackage = topicPackageFromItem(item) || {};
      return topicPackage.name || topicPackage.topic || item.name || "已选选题";
    }
    return item.name || (kind === "title" ? "已选标题结构" : "已选视觉模板");
  };
  const rolePreview = (item, kind) => {
    if (!item) return `<span class="rewrite-context-placeholder"><i data-lucide="plus" aria-hidden="true"></i></span>`;
    if (["cover", "inner"].includes(kind)) {
      const image = visualTemplateImages(item, kind)[0];
      if (image) {
        return `<span class="rewrite-context-thumb"><img src="${escapeHtml(image)}" alt="" loading="lazy" onerror="this.closest('.rewrite-context-thumb').classList.add('is-missing'); this.remove()" /><i data-lucide="image" aria-hidden="true"></i></span>`;
      }
    }
    return `<span class="rewrite-context-glyph"><i data-lucide="${kind === "topic" ? "lightbulb" : kind === "title" ? "text-cursor-input" : "image"}" aria-hidden="true"></i></span>`;
  };
  return `
    <div class="rewrite-context-summary">
      <span class="rewrite-context-icon" aria-hidden="true"><i data-lucide="layers-3"></i></span>
      <div class="rewrite-context-summary-copy">
        <strong>创作组合</strong>
        <span>${selectedCount} / 4 项就绪</span>
      </div>
      <div class="rewrite-context-progress" aria-label="模板组合完成 ${selectedCount} / 4">
        ${items.map(({ kind, item }) => `<span class="${kind} ${item ? "ready" : ""}" title="${labels[kind]}${item ? "已选择" : "未选择"}"></span>`).join("")}
      </div>
    </div>
    <div class="rewrite-context-roles">
      ${items.map(({ kind, item }) => `
        <button type="button" class="rewrite-context-role ${kind} ${item ? "selected" : "empty"}" ${item ? `data-template-detail-kind="${kind}" aria-label="查看${labels[kind]}模板详情"` : `data-open-library-kind="${kind}" aria-label="选择${labels[kind]}模板"`}>
          ${rolePreview(item, kind)}
          <div class="rewrite-context-role-copy">
            <span>${labels[kind]} <em>${kind === "topic" ? "必选" : "选填"}</em></span>
            <strong title="${escapeHtml(displayName(item, kind))}">${escapeHtml(displayName(item, kind))}</strong>
          </div>
        </button>`).join("")}
    </div>`;
}

function renderRewriteTemplateDetail(item, kind) {
  if (["topic", "title"].includes(kind)) return selectedTextLibraryPreview(item, kind);
  const images = visualTemplateImages(item, kind);
  const roleLabel = kind === "cover" ? "封面模板" : "内页模板";
  const generationLabel = item?.aiImage?.prompt ? "支持 AI 生成" : "版式参考";
  return `
    <div class="rewrite-template-detail-visual">
      ${selectedVisualTemplatePreview(item, kind)}
      <dl class="rewrite-template-detail-facts">
        <div><dt>模板角色</dt><dd>${roleLabel}</dd></div>
        <div><dt>可用页面</dt><dd>${images.length || 1} 张</dd></div>
        <div><dt>生成方式</dt><dd>${generationLabel}</dd></div>
        <div><dt>模板说明</dt><dd>${escapeHtml(item?.summary || "按所选模板版式生成当前笔记图片")}</dd></div>
      </dl>
    </div>`;
}

let rewriteTemplateDetailReturnFocus = null;
let rewriteTemplateDetailReturnFocusSelector = "";

function openRewriteTemplateDetail(kind) {
  if (!["topic", "title", "cover", "inner"].includes(kind)) return;
  const item = draftViralLibraryItem(currentDraft(), kind);
  if (!item) return;
  const activeElement = document.activeElement;
  rewriteTemplateDetailReturnFocus = activeElement && activeElement !== document.body && typeof activeElement.focus === "function"
    ? activeElement
    : null;
  rewriteTemplateDetailReturnFocusSelector = activeElement?.dataset?.templateDetailKind
    ? `[data-template-detail-kind="${activeElement.dataset.templateDetailKind}"]`
    : "";
  const labels = { topic: "选题", title: "标题", cover: "封面", inner: "内页" };
  const topicPackage = kind === "topic" ? topicPackageFromItem(item) || {} : {};
  const title = kind === "topic" ? topicPackage.name || topicPackage.topic || item.name : item.name;
  const summary = kind === "topic"
    ? topicPackage.coreClaim || item.summary || "当前内容使用的选题方向"
    : item.summary || (kind === "title" ? "当前内容使用的标题结构" : "当前内容使用的视觉模板");
  $("#rewriteTemplateDetailType").textContent = `${labels[kind]} · 当前组合`;
  $("#rewriteTemplateDetailTitle").textContent = title || `已选${labels[kind]}模板`;
  $("#rewriteTemplateDetailSummary").textContent = summary;
  $("#rewriteTemplateDetailBody").innerHTML = renderRewriteTemplateDetail(item, kind);
  $("#rewriteTemplateDetailLibraryBtn").dataset.openLibraryKind = kind;
  const modal = $("#rewriteTemplateDetailModal");
  showModal(modal);
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function closeRewriteTemplateDetail() {
  const modal = $("#rewriteTemplateDetailModal");
  if (!modal) return;
  const selector = rewriteTemplateDetailReturnFocusSelector;
  hideModal(modal, () => {
    $("#rewriteTemplateDetailBody").innerHTML = "";
    const returnFocus = rewriteTemplateDetailReturnFocus?.isConnected
      ? rewriteTemplateDetailReturnFocus
      : (selector ? document.querySelector(selector) : null);
    if (returnFocus && typeof returnFocus.focus === "function") {
      window.requestAnimationFrame(() => returnFocus.focus());
    }
    rewriteTemplateDetailReturnFocus = null;
    rewriteTemplateDetailReturnFocusSelector = "";
  });
  if (selector) window.setTimeout(() => document.querySelector(selector)?.focus(), 260);
}

function flushStateOnPageHide() {
  const now = Date.now();
  // visibilitychange and pagehide can fire together; one revision write is
  // enough and avoids creating an artificial 409 against ourselves.
  if (now - lastLifecycleStateFlushAt < 1000) return;
  lastLifecycleStateFlushAt = now;
  window.clearTimeout(saveState.backendTimer);
  saveBrowserState();
  if (state.stateWriteBlocked) return;
  if (typeof backendStateHydrated === "boolean" && !backendStateHydrated) return;
  if (typeof flushBackendStateSave === "function" && backendStateHydrated) {
    if (backendSaveDirty || backendSaveRunning) {
      flushBackendStateSave({ keepalive: true, singlePass: true });
    }
    return;
  }
  postBackend("/api/state", backendStateForWrite(), {
    keepalive: true,
    waitForJob: false,
  }).catch(() => {
    // The lightweight session snapshot remains the recovery path when the
    // browser terminates before a keepalive request reaches the backend.
  });
}

function armCloseGuard() {
  if (closeGuardArmed) return;
  closeGuardArmed = true;
  window.addEventListener("beforeunload", (event) => {
    if (!backendSaveDirty && !backendSaveRunning) return;
    event.preventDefault();
    event.returnValue = "";
  });
}

function compactLegacyExecutionPrompt(prompt) {
  const source = String(prompt || "");
  if (!source.includes("当前选题包：")) return source;
  return source
    .replace(/\n?当前选题包：\s*\{[\s\S]*?\}\s*(?=\n(?:标题结构|唯一可使用的事实)：)/, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function migrateLegacyExecutionPrompts(draft) {
  if (!draft?.executionPlan) return false;
  let changed = false;
  for (const key of ["titlePrompt", "bodyPrompt"]) {
    const contentKey = key.replace("Prompt", "");
    const originalPrompt = String(draft.executionPlan[key] || "").trim();
    if (draft.promptMode?.[contentKey] && String(draft[contentKey] || "").trim() === originalPrompt) {
      draft[contentKey] = "";
      changed = true;
    }
    const compacted = compactLegacyExecutionPrompt(draft.executionPlan[key]);
    if (compacted && compacted !== draft.executionPlan[key]) {
      draft.executionPlan[key] = compacted;
      changed = true;
    }
  }
  for (const key of ["title", "body", "tags"]) {
    const promptKey = `${key}Prompt`;
    if (draft.promptMode?.[key] && String(draft[key] || "").trim() === String(draft.executionPlan[promptKey] || "").trim()) {
      draft[key] = "";
      changed = true;
    }
  }
  return changed;
}

const draftAiBlocks = {
  title: { promptKey: "titlePrompt", panelId: "titleAiPanel", buttonId: "titleAiBtn", inputId: "titleBlockPrompt" },
  body: { promptKey: "bodyPrompt", panelId: "bodyAiPanel", buttonId: "bodyAiBtn", inputId: "bodyBlockPrompt" },
  tags: { promptKey: "tagsPrompt", panelId: "tagsAiPanel", buttonId: "tagsAiBtn", inputId: "tagsBlockPrompt" },
  image: { panelId: "imageAiPanel", buttonId: "polishImageBtn" },
};

function replicaImagePromptIsCustomized(draft, block) {
  if (draft?.workflow !== "replica" || !["cover", "inner"].includes(block)) return false;
  // 只有用户在任务里显式改过这条提示词，它才算任务自己的。以前这里还拿两个内置常量做比对，
  // 等于让隐藏文案参与判断；提示词只有一个来源：AI 配置（我的提示词）。
  return Boolean(draft.imagePromptCustomized?.[block]);
}

// "我的提示词"里当前生效的图片提示词。任务不缓存它：配置一改，当前任务就按新提示词生成。
function configuredImagePrompt(draft) {
  const mode = draft?.promptTemplateMode || state.aiConfig?.promptMode || "rewrite";
  const promptSet = typeof promptTemplateForMode === "function" ? promptTemplateForMode(mode) : null;
  return String(promptSet?.imagePrompt || "");
}

function draftAiPrompt(draft, block) {
  if (!draft) return "";
  if (block === "cover" || block === "inner") {
    if (draft.workflow === "replica" && !replicaImagePromptIsCustomized(draft, block)) {
      return configuredImagePrompt(draft);
    }
    if (draft.imageEditPrompts && Object.prototype.hasOwnProperty.call(draft.imageEditPrompts, block)) {
      return String(draft.imageEditPrompts[block] || "");
    }
    return draft.executionPlan?.[`${block}Prompt`]
      || draft.executionPlan?.imagePrompt
      || draftPromptValue(draft, "imagePrompt")
      || "";
  }
  const config = draftAiBlocks[block];
  return config ? (draft.executionPlan?.[config.promptKey] || draftPromptValue(draft, config.promptKey)) : "";
}

function draftAiBlockReady(draft, block) {
  if (block === "image") return Boolean(draftAiPrompt(draft, "cover") || draftAiPrompt(draft, "inner"));
  const key = draftAiBlocks[block]?.promptKey;
  return Boolean(key && draftAiPrompt(draft, block));
}

function draftAiBlockBuilt(draft, block) {
  const plan = draft?.executionPlan;
  if (!plan) return false;
  if (block === "image") return Boolean(plan.coverPrompt || plan.innerPrompt || plan.imagePrompt);
  const key = draftAiBlocks[block]?.promptKey;
  return Boolean(key && String(plan[key] || "").trim());
}

function conciseCaptionPhrase(value, maxChars = 32) {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^(?:第\s*\d+\s*页|P\s*\d+)\s*[-:：、.．]?\s*/i, "")
    .trim();
  if (!text || /^(?:正文|内页|详情|内容)$/.test(text)) return "";
  return text.slice(0, maxChars).replace(/[，,。；;：:、\s]+$/, "");
}

function legacyOriginalCaption(draft) {
  const plan = draft?.executionPlan && typeof draft.executionPlan === "object" ? draft.executionPlan : {};
  const topic = conciseCaptionPhrase(
    plan.topicPackage?.topic || draft?.viralBrief?.topic || plan.lockedCopySet?.topic || plan.topic,
    36,
  );
  const executions = Array.isArray(plan.seriesExecutions) ? plan.seriesExecutions : [];
  const activeExecution = executions.find((item) => String(item?.seriesId || "") === String(plan.activeSeriesId || ""))
    || executions[0]
    || {};
  const copies = Array.isArray(activeExecution.lockedPageCopies) && activeExecution.lockedPageCopies.length
    ? activeExecution.lockedPageCopies
    : (Array.isArray(plan.lockedPageCopies) ? plan.lockedPageCopies : []);
  const headlines = [];
  const seen = new Set();
  copies.forEach((item) => {
    if (!item || item.pageRole !== "inner") return;
    const headline = conciseCaptionPhrase(item.headline);
    const key = headline.replace(/[\s，,。；;：:、!?！？]+/g, "").toLowerCase();
    if (!headline || !key || seen.has(key)) return;
    seen.add(key);
    headlines.push(headline);
  });
  const subject = topic || headlines[0] || "这次整理的主题";
  const selected = headlines.length > 5
    ? [0, 1, 2, 3, 4].map((index) => headlines[Math.round(index * (headlines.length - 1) / 4)])
    : headlines;
  const sections = [`把「${subject}」整理成了一组按顺序阅读的图文笔记，先抓住框架，再看具体内容。`];
  if (selected.length) {
    sections.push(`这篇可以重点看：\n${selected.map((headline, index) => `${index + 1}. ${headline}`).join("\n")}`);
  }
  sections.push(headlines.length > 5 ? "其他内容也按顺序放在图中，建议从第一页开始看。" : "完整说明都放在图片里，建议按顺序看完。");
  sections.push("觉得有用可以先收藏，也欢迎说说你最想继续展开哪一部分。");
  return sections.join("\n\n");
}

function generatedContentForDraft(draft) {
  const generated = draft?.executionPlan?.generatedContent;
  const source = generated && typeof generated === "object" ? generated : {};
  const edits = draft?.generatedContentEdits && typeof draft.generatedContentEdits === "object"
    ? draft.generatedContentEdits
    : {};
  const isLegacyOriginal = draft?.workflow !== "replica" && !source.postCaptionVersion;
  const content = {
    title: Object.prototype.hasOwnProperty.call(edits, "title") ? String(edits.title || "") : String(source.title || ""),
    body: Object.prototype.hasOwnProperty.call(edits, "body")
      ? String(edits.body || "")
      : (isLegacyOriginal ? legacyOriginalCaption(draft) : String(source.body || "")),
    tags: Object.prototype.hasOwnProperty.call(edits, "tags") ? String(edits.tags || "") : String(source.tags || ""),
  };
  content.tags = normalizeXhsTags(content.tags, xhsTagFallbacksForDraft(draft));
  return content;
}

function canViewDraftAiBlock(draft, block) {
  if (!draft) return false;
  // Replica retains its prompt-editing controls. Generated-content review below
  // is an original-note workflow only.
  if (isReplicaDraft(draft)) return Boolean(draftAiBlocks[block]);
  const expansionStatus = String(draft.pageExpansionReview?.status || "");
  if (["pending", "replanning"].includes(expansionStatus)) return false;
  if (!hasReadyViralExecutionPlan(draft)) return false;
  if (block === "image") return Array.isArray(draft.executionPlan?.pageScript) && draft.executionPlan.pageScript.length > 0;
  return Boolean(generatedContentForDraft(draft)[block]);
}

// 面板里装的东西两种流程不一样，说明文案里点名"标题/正文/标签"时要用同一个口径。
function draftBlockContentLabel(block) {
  return block === "image" ? "图文" : block === "title" ? "标题" : block === "body" ? "正文" : "标签";
}

// 标题/正文/标签这三块面板在两种流程里装的是**不同东西**：
//   快速笔记（复刻）：装的是提示词本身，改它就是改下一次生成的要求；
//   原创笔记：装的是已经生成、等用户检查修改的文案。
// 静态 HTML 里那句「可检查修改，一键生成后写入正文」说的是后者；套到提示词上，用户会以为框里
// 那段就是生成结果。这里按流程换说明。图片面板的两套标题由 markup + CSS 切换，不在这里动。
function renderDraftBlockHeadNote(panel, draft, block) {
  if (!panel || block === "image") return;
  const note = panel.querySelector(".block-ai-panel-head span");
  if (!note) return;
  const target = draftBlockContentLabel(block);
  note.textContent = isReplicaDraft(draft)
    ? `可编辑提示词，一键生成后写入${target}`
    : `可检查修改，一键生成后写入${target}`;
}

// 块头那颗「点击查看…」也要反映"这一区正在生成"：生成中的文字原本只写在折叠面板底部的
// 生成按钮上，那块按钮常常在视野之外，用户点完看不到任何反馈。
function renderDraftBlockActionButton(draft, block, config) {
  const button = $(config?.buttonId ? `#${config.buttonId}` : "");
  if (!button) return;
  const contentLabel = draftBlockContentLabel(block);
  const promptMode = isReplicaDraft(draft);
  // 快速笔记里这块面板既能看提示词也能直接改（改完作用于下一次生成），所以按钮写「查看/编辑」；
  // 原创笔记打开的是已经生成的内容，仍旧叫「点击查看生成内容」。
  const buttonLabel = promptMode ? "查看/编辑提示词" : "点击查看生成内容";
  // 读屏与悬停文案把子块名插在中间，别拼成「查看/编辑提示词标题」。
  const actionLabel = promptMode ? `查看/编辑${contentLabel}提示词` : `${buttonLabel}${contentLabel}`;
  const busy = draftBlockGenerationBusy(draft.id, block);
  button.classList.toggle("is-generating", busy);
  button.textContent = busy ? "生成中…" : buttonLabel;
  button.setAttribute("aria-label", busy ? `${contentLabel}正在生成` : actionLabel);
  button.title = busy ? `${contentLabel}正在生成，请稍候` : actionLabel;
}

// 分区生成开始/结束时刷新一次可见反馈（按钮置灰 + 块头「生成中…」），不重排整个编辑器。
function renderDraftGenerationIndicators(draft = currentDraft()) {
  if (!draft) return;
  const draftFullActive = draftFullGenerationActive(draft.id);
  const hasUsableContent = draftHasUsableContent(draft);
  const hasDraftImages = normalizeImages(currentPreviewImages(draft)).length > 0;
  $$("[data-generate-ai-block]").forEach((button) => {
    const role = String(button.dataset.generateAiBlock || "");
    const blockBusy = draftBlockGenerationBusy(draft.id, role);
    const blocked = draftFullActive || blockBusy;
    button.classList.toggle("is-generating", blockBusy);
    if (role === "title" || role === "body" || role === "tags") {
      button.disabled = blocked || !hasUsableContent;
      button.title = !blocked && !hasUsableContent ? EMPTY_DRAFT_GENERATION_HINT : "";
      return;
    }
    button.disabled = blocked || !hasDraftImages;
    button.title = hasDraftImages ? "" : "当前没有可生成的页面图，请先加入或生成图片";
  });
  Object.entries(draftAiBlocks).forEach(([block, config]) => {
    const button = $(config?.buttonId ? `#${config.buttonId}` : "");
    if (!button || button.classList.contains("hidden")) return;
    renderDraftBlockActionButton(draft, block, config);
  });
}

function renderDraftAiBlocks(draft) {
  const planLocked = !isReplicaDraft(draft) && hasLockedKnowledgePlan(draft);
  if (state.openDraftAiBlock && !canViewDraftAiBlock(draft, state.openDraftAiBlock)) {
    state.openDraftAiBlock = "";
  }
  Object.entries(draftAiBlocks).forEach(([block, config]) => {
    const panel = $(`#${config.panelId}`);
    const button = $(`#${config.buttonId}`);
    const viewable = canViewDraftAiBlock(draft, block);
    const isOpen = viewable && state.openDraftAiBlock === block;
    const ready = draftAiBlockReady(draft, block);
    const built = draftAiBlockBuilt(draft, block);
    panel?.classList.toggle("hidden", !isOpen);
    button?.classList.toggle("hidden", !viewable);
    button?.classList.toggle("ai-ready", ready);
    button?.classList.toggle("ai-built", built);
    button?.classList.toggle("active", isOpen);
    button?.setAttribute("aria-expanded", String(isOpen));
    button?.setAttribute("aria-controls", config.panelId);
    renderDraftBlockActionButton(draft, block, config);
    renderDraftBlockHeadNote(panel, draft, block);
    if (config.inputId) {
      const input = $(`#${config.inputId}`);
      if (input) {
        const showingGeneratedContent = viewable && !isReplicaDraft(draft);
        input.readOnly = showingGeneratedContent ? false : planLocked;
        input.setAttribute("aria-readonly", String(input.readOnly));
        if (document.activeElement !== input) {
          input.value = showingGeneratedContent ? generatedContentForDraft(draft)[block] : draftAiPrompt(draft, block);
        }
        panel?.querySelector(".block-ai-panel-actions")?.classList.toggle("hidden", showingGeneratedContent);
      }
    }
  });
  const showingGeneratedImageContent = canViewDraftAiBlock(draft, "image") && !isReplicaDraft(draft);
  $("#imageGeneratedContentHeading")?.classList.toggle("hidden", !showingGeneratedImageContent);
  $("#imageAiPanel .replica-inline-prompt")?.classList.toggle("hidden", !isReplicaDraft(draft));
  $("#imageAiPanel .image-block-prompt-grid")?.classList.toggle("hidden", showingGeneratedImageContent);
  $("#imagePromptHeading")?.classList.toggle("hidden", showingGeneratedImageContent);
  const hasDraftImages = normalizeImages(currentPreviewImages(draft)).length > 0;
  const coverOpen = hasDraftImages || Boolean(draftAiPrompt(draft, "cover"));
  const innerOpen = hasDraftImages || Boolean(draftAiPrompt(draft, "inner"));
  $("#coverPromptField")?.classList.toggle("hidden", !coverOpen);
  $("#innerPromptField")?.classList.toggle("hidden", !innerOpen);
  const coverInput = $("#coverPromptInput");
  const innerInput = $("#innerPromptInput");
  [coverInput, innerInput].forEach((input) => {
    if (!input) return;
    input.readOnly = false;
    input.setAttribute("aria-readonly", "false");
  });
  if (coverInput && document.activeElement !== coverInput) coverInput.value = draftAiPrompt(draft, "cover");
  if (innerInput && document.activeElement !== innerInput) innerInput.value = draftAiPrompt(draft, "inner");
  // 「生成封面/生成内页」的作用对象是草稿里已有的页面图：没有图就没有可生成的目标。
  // 显示条件用的是"有图 或 提示词非空"，而复刻模式的提示词会回落到内置默认值（非空），
  // 所以零图片时按钮照样显示、点下去只在入口处弹一句 toast（P69 同族的按钮门控）。
  $$('[data-generate-ai-block="cover"], [data-generate-ai-block="inner"], [data-generate-ai-block="image"]').forEach((button) => {
    // 这一段在分区置灰循环之后跑，所以必须自己读一次运行态；否则会把上面刚算好的"整篇生成/
    // 本分区在跑 → 置灰"盖回可点。没有页面图那条判据保持原样。
    const role = String(button.dataset.generateAiBlock || "");
    const blocked = draftFullGenerationActive(draft.id) || draftBlockGenerationBusy(draft.id, role);
    button.disabled = blocked || !hasDraftImages;
    button.title = hasDraftImages ? "" : "当前没有可生成的页面图，请先加入或生成图片";
  });
  if (state.openDraftAiBlock === "image") renderVisualCopyPlan(draft);
}

function analyzedPageCountForReview(draft, review = draft?.pageExpansionReview) {
  const plannedInnerPages = Math.max(
    0,
    Number(draft?.executionPlan?.pagePlan?.stats?.innerPageCount)
      || Number(draft?.executionPlan?.seriesPlan?.stats?.totalInnerPages)
      || 0,
  );
  return Math.max(
    1,
    Number(review?.analyzedPageCount) || 0,
    Number(review?.estimatedPageCount) || 0,
    plannedInnerPages ? plannedInnerPages + 1 : 0,
  );
}

function hasAnalyzedPageCountForReview(draft, review = draft?.pageExpansionReview) {
  // A missing plan (dropped oversized job result, or an older record) must not
  // be reported as a real "需要 1 页" analysis: the count below is only a floor.
  return Boolean(
    Number(review?.analyzedPageCount) > 0
      || Number(review?.estimatedPageCount) > 0
      || Number(draft?.executionPlan?.pagePlan?.stats?.innerPageCount) > 0
      || Number(draft?.executionPlan?.seriesPlan?.stats?.totalInnerPages) > 0
  );
}

function renderPageExpansionReview(draft) {
  const mount = $("#pageExpansionReview");
  const summary = $("#pageExpansionReviewSummary");
  const input = $("#pageExpansionCountInput");
  if (!mount || !summary || !input) return;
  const review = draft?.pageExpansionReview;
  const pending = review?.status === "pending" && knowledgeChannelForDraft(draft) !== "pure-ai";
  mount.classList.toggle("hidden", !pending);
  if (!pending) return;
  const base = Math.max(1, Number(review.basePageCount) || 1);
  const analyzed = analyzedPageCountForReview(draft, review);
  const suggested = Math.min(MAX_DRAFT_TEMPLATE_PAGES, Math.max(base, analyzed));
  const selected = Math.max(base, Number(review.selectedPageCount) || base);
  const planningLabel = knowledgeChannelForDraft(draft) === "pure-ai" ? "AI 内容规划" : "完整资料分析";
  const analyzedOverCap = analyzed > MAX_DRAFT_TEMPLATE_PAGES;
  summary.textContent = hasAnalyzedPageCountForReview(draft, review)
    ? `${planningLabel}需要 ${analyzed} 页。笔记上限${MAX_DRAFT_TEMPLATE_PAGES}张图文${analyzedOverCap ? "（资料或模板不足时按实际页数生成）" : ""}`
    : `${planningLabel}结果未返回，请重新搭建笔记内容`;
  input.min = String(base);
  input.max = String(MAX_DRAFT_TEMPLATE_PAGES);
  input.value = String(Math.min(MAX_DRAFT_TEMPLATE_PAGES, selected));
  const suggestedButton = $("#useSuggestedPageCountBtn");
  if (suggestedButton) suggestedButton.textContent = analyzed > MAX_DRAFT_TEMPLATE_PAGES
    ? `最多 ${MAX_DRAFT_TEMPLATE_PAGES} 张图文`
    : `按 ${suggested} 页生成`;
}

function expansionTemplatePagesForReview(draft, count) {
  const selected = selectedViralLibraryItem("inner");
  const entries = imagePickerTemplatePageEntries(draft, selected, "inner");
  if (!entries.length) return Array.from({ length: count }, (_, index) => ({
    id: uid("expansion-page"),
    pageIndex: index,
    templateId: draft?.innerTemplateId || "",
    sourceKind: "inner",
    label: `内页 ${index + 1}`,
    referenceImage: "",
    sourcePageKey: templatePageKey(draft?.innerTemplateId || "", index),
  }));
  return Array.from({ length: count }, (_, index) => {
    const entry = entries[index % entries.length];
    return {
      id: uid("expansion-page"),
      pageIndex: Number(entry.pageIndex) || 0,
      templateId: entry.templateId || draft?.innerTemplateId || "",
      sourceKind: "inner",
      sourcePageKey: entry.sourcePageKey || templatePageKey(entry.templateId, entry.pageIndex),
      sourceTemplateName: entry.templateName || "",
      label: entry.label || `内页 ${index + 1}`,
      referenceImage: entry.src || "",
      orderKey: `expansion:${index + 1}:${entry.sourcePageKey || entry.pageIndex}`,
    };
  });
}

async function confirmPageExpansion() {
  const draft = currentDraft();
  const review = draft?.pageExpansionReview;
  const input = $("#pageExpansionCountInput");
  if (!draft || !review || review.status !== "pending" || !input) return;
  const min = Math.max(1, Number(review.basePageCount) || 1);
  const max = Math.max(min, Number(review.maxPageCount) || MAX_DRAFT_TEMPLATE_PAGES);
  const requested = Math.max(min, Math.min(max, Number(input.value) || Number(review.suggestedPageCount) || min));
  const pages = Array.isArray(review.pages) ? review.pages.slice(0, Math.max(0, requested - min)) : [];
  while (pages.length < Math.max(0, requested - min)) {
    pages.push(...expansionTemplatePagesForReview(draft, Math.max(0, requested - min) - pages.length));
  }
  draft.requestedPageCount = requested;
  // 把确认时已经算好的扩页真正落进 additionalTemplatePages。之前这些页只存在
  // review.pages 里，从不随请求发出；而 brief 的内页清单来自 additionalTemplatePages，
  // 于是服务端只按模板自带页数规划——用户要 18 页却只拿到 14 内页，truncate 上限
  // 校验因此报「指定 18 页 / 规划 15 页」（先有鸡还是先有蛋：能带回扩页的计划响应
  // 恰好被那次校验挡在门外）。这里物化后，brief.innerPageEntries 会带上 isAdditional
  // 条目，服务端据此额外生成内页 spec，规划才能补到用户确认的页数。
  const existingManualPages = (Array.isArray(draft.additionalTemplatePages) ? draft.additionalTemplatePages : [])
    .filter((item) => item && item.autoGenerated !== true && !/^auto-(?:inner-|series[-_])/.test(String(item.id || "")));
  const confirmedExpansionPages = pages
    .map((item, index) => ({
      id: String(item.id || `expansion-${index + 1}`),
      templateId: item.templateId || draft.innerTemplateId || "",
      pageIndex: Math.max(0, Number(item.pageIndex) || 0),
      label: item.label || `内页 ${index + 1}`,
      contentPart: "inner",
      sourceKind: "inner",
      sourcePageKey: item.sourcePageKey || templatePageKey(item.templateId || draft.innerTemplateId, Number(item.pageIndex) || 0),
      sourceTemplateName: item.sourceTemplateName || "",
      referenceImage: item.referenceImage || "",
      orderKey: item.orderKey || "",
      isAdditional: true,
      createdAt: new Date().toISOString(),
    }))
    .filter((item) => item.templateId);
  draft.additionalTemplatePages = [...existingManualPages, ...confirmedExpansionPages];
  // 让 draftImageJobs 在本次 compose 里就能看到这些页（与 applyKnowledgePageExpansion 收尾一致）。
  const confirmedAllJobs = draftImageJobs(draft, { includeExcluded: true });
  const confirmedInnerJobs = confirmedAllJobs.filter((job) => job.contentPart === "inner");
  draft.innerPageOrder = confirmedInnerJobs.map((job) => job.key);
  const confirmedCoverJobs = confirmedAllJobs.filter((job) => job.contentPart === "cover");
  draft.imagePageOrder = [...confirmedCoverJobs, ...confirmedInnerJobs].map((job) => job.key);
  draft.pageExpansionReview = {
    ...review,
    status: "confirmed",
    confirmedPageCount: requested,
    estimatedPageCount: requested,
    selectedPageCount: requested,
    expansionDecision: requested > min ? "expand" : "keep-current",
    additionalPageCount: Math.max(0, requested - min),
    pages,
    resumeFromRewrite: Boolean(review.resumeFromRewrite),
    confirmedAt: new Date().toISOString(),
  };
  draft.executionPlan = null;
  draft.viralRecipeId = "";
  draft.templateRenderMode = "ai-pending";
  saveState();
  renderEditor();
  // renderEditor rebuilds the task panel; restore an explicit running state
  // before handing control back to composeViralDraft so the continuation is
  // visible immediately after the page-count decision.
  state.viralComposeSourceDraftId = draft.id || state.viralComposeSourceDraftId;
  updateViralPromptTask(
    {
      stage: "generating",
      message: "正在继续生成逐页文字",
      totalPages: requested,
      completedPages: 0,
    },
    { ownerDraftId: draft.id || "" },
  );
  toast(`已确认生成前 ${requested} 页，正在移除后续任务并继续生成`);
  await composeViralDraft({
    preventDefault() {},
    currentTarget: $("#rewriteBtn"),
    expansionResume: true,
  });
}

async function keepCurrentPageCount() {
  const draft = currentDraft();
  const review = draft?.pageExpansionReview;
  if (!draft || !review || review.status !== "pending") return;
  const input = $("#pageExpansionCountInput");
  if (input) input.value = String(Math.max(1, Number(review.basePageCount) || 1));
  await confirmPageExpansion();
}

function cancelPageExpansionReview() {
  const draft = currentDraft();
  const review = draft?.pageExpansionReview;
  if (!draft || !review || review.status !== "pending") return;
  const backup = pageExpansionCancelBackups.get(String(draft.id || ""));
  if (backup?.previousDraftPlan) {
    draft.executionPlan = backup.previousDraftPlan.executionPlan;
    draft.viralRecipeId = backup.previousDraftPlan.viralRecipeId;
    draft.templateRenderMode = backup.previousDraftPlan.templateRenderMode;
    draft.imageGenerationError = backup.previousDraftPlan.imageGenerationError;
  } else if (draft.executionPlan?.generationStatus !== "complete") {
    draft.executionPlan = null;
    draft.viralRecipeId = "";
    draft.templateRenderMode = "ai-pending";
    draft.imageGenerationError = "";
  }
  draft.requestedPageCount = 0;
  delete draft.pageExpansionReview;
  pageExpansionCancelBackups.delete(String(draft.id || ""));
  state.viralLastGeneration = backup?.previousGeneration || null;
  state.viralComposeSourceDraftId = backup?.previousComposeSourceDraftId || null;
  updateViralPromptTask({}, { hide: true });
  saveState();
  renderEditor();
  toast("已停止搭建笔记内容，原计划已保留");
}

function useSuggestedPageCount() {
  const draft = currentDraft();
  const review = draft?.pageExpansionReview;
  if (!draft || !review || review.status !== "pending") return;
  const base = Math.max(1, Number(review.basePageCount) || 1);
  const suggested = Math.min(MAX_DRAFT_TEMPLATE_PAGES, Math.max(base, analyzedPageCountForReview(draft, review)));
  const pages = expansionTemplatePagesForReview(draft, suggested - base);
  review.selectedPageCount = suggested;
  review.expansionDecision = "expand";
  review.pages = pages;
  review.additionalPageCount = pages.length;
  review.estimatedPageCount = suggested;
  saveState();
  const input = $("#pageExpansionCountInput");
  if (input) input.value = String(suggested);
  confirmPageExpansion();
}

function addExpansionReviewPage() {
  const draft = currentDraft();
  const review = draft?.pageExpansionReview;
  if (!draft || !review || review.status !== "pending") return;
  const base = Math.max(1, Number(review.basePageCount) || 1);
  const pages = Array.isArray(review.pages) ? [...review.pages] : [];
  if (base + pages.length >= MAX_DRAFT_TEMPLATE_PAGES) {
    toast(`单篇最多 ${MAX_DRAFT_TEMPLATE_PAGES} 页`);
    return;
  }
  openInnerPagePicker({ forExpansion: true });
}

function toggleDraftAiBlock(block) {
  if (!draftAiBlocks[block]) return;
  if (!canViewDraftAiBlock(currentDraft(), block)) return;
  const opening = state.openDraftAiBlock !== block;
  state.openDraftAiBlock = opening ? block : "";
  renderDraftAiBlocks(currentDraft());
  if (opening) playUiMotion($(`#${draftAiBlocks[block].panelId}`), "ui-view-enter");
}

function updateDraftAiBlockPrompt(input) {
  const draft = currentDraft();
  const block = input?.dataset?.blockPrompt;
  if (!draft || !block) return;
  if (!isReplicaDraft(draft) && ["title", "body", "tags"].includes(block) && canViewDraftAiBlock(draft, block)) {
    const generated = draft.executionPlan?.generatedContent || {};
    const edits = { ...(draft.generatedContentEdits || {}) };
    if (input.value === String(generated[block] || "")) delete edits[block];
    else edits[block] = input.value;
    draft.generatedContentEdits = edits;
    saveState();
    return;
  }
  if (block === "cover" || block === "inner") {
    draft.imageEditPrompts = { ...(draft.imageEditPrompts || {}), [block]: input.value };
    if (draft.workflow === "replica") {
      draft.imagePromptCustomized = { ...(draft.imagePromptCustomized || {}), [block]: true };
    }
    if (draft.promptMode) draft.promptMode[block] = Boolean(input.value.trim());
    saveState();
    return;
  }
  if (hasLockedKnowledgePlan(draft)) {
    input.value = draftAiPrompt(draft, block);
    return;
  }
  const key = block === "cover" ? "coverPrompt" : block === "inner" ? "innerPrompt" : draftAiBlocks[block]?.promptKey;
  if (!key) return;
  if (draft.executionPlan) draft.executionPlan[key] = input.value;
  else draft.promptSnapshot = { ...(draft.promptSnapshot || currentPromptSnapshot()), [key]: input.value };
  if (draft.promptMode && ["title", "body", "tags", "cover", "inner"].includes(block)) draft.promptMode[block] = Boolean(input.value.trim());
  saveState();
  renderDraftAiBlocks(draft);
}

function updateDraftExecutionPrompt(input) {
  const draft = currentDraft();
  const key = input?.dataset?.draftExecutionPrompt;
  if (!draft?.executionPlan || !["titlePrompt", "bodyPrompt", "tagsPrompt", "imagePrompt", "coverPrompt", "innerPrompt"].includes(key)) return;
  if (hasLockedKnowledgePlan(draft)) {
    input.value = draft.executionPlan[key] || "";
    return;
  }
  draft.executionPlan[key] = input.value;
  saveState();
}

function visualExecutionPlanMatchesDraft(draft) {
  const plan = draft?.executionPlan;
  if (!plan) return false;
  if (draft?.executionPlanInvalidation) return false;
  return Number(plan.version || 0) === EXECUTION_BUNDLE_VERSION
    && plan.bundleType === EXECUTION_BUNDLE_TYPE
    && plan.pipelineContract === PIPELINE_CONTRACT;
}

function lockedCopySetGenerationComplete(lockedCopySet) {
  return Boolean(
    lockedCopySet?.generationStatus === "complete"
      || (!lockedCopySet?.generationStatus && lockedCopySet?.status === "passed")
  );
}

function hasLockedKnowledgePlan(draft) {
  const plan = draft?.executionPlan;
  const lockedCopySet = plan?.lockedCopySet;
  return Boolean(
    plan
      && plan.pipelineContract === PIPELINE_CONTRACT
      && plan.pageScriptSource === "text-ai-template-fit"
      && lockedCopySet?.kind === "LockedCopySetV2"
      && lockedCopySetGenerationComplete(lockedCopySet)
  );
}

function invalidateDraftExecutionPlan(draft, reason = "page-contract-changed") {
  if (!draft?.executionPlan || draft.executionPlanInvalidation) return false;
  draft.executionPlanInvalidation = {
    reason: String(reason || "page-contract-changed"),
    invalidatedAt: new Date().toISOString(),
    planVersion: Number(draft.executionPlan.version) || 0,
    // 失效前的渲染态。用户把顺序拖回原样时要把这份计划连同渲染态一起复原
    // （见 generate.js 的 restoreDraftPlanIfOrderMatches）。
    previousRenderMode: draft.templateRenderMode,
  };
  if (draft.templateRenderMode === "ai-full-page") draft.templateRenderMode = "ai-pending";
  return true;
}

function executionPlanForWrite(plan) {
  if (!plan || typeof plan !== "object") return plan;
  if (!visualExecutionPlanMatchesDraft({ executionPlan: plan })) {
    throw new Error("执行计划版本已失效，请重新搭建笔记内容");
  }
  return plan;
}

function lockedPageCopySignature(value) {
  const blocks = Array.isArray(value?.contentBlocks) ? value.contentBlocks : [];
  return JSON.stringify({
    headline: String(value?.headline || "").trim(),
    body: String(value?.body || "").trim(),
    contentBlocks: blocks.map((block) => ({
      id: String(block?.id || ""),
      role: String(block?.role || ""),
      label: String(block?.label || ""),
      text: String(block?.text || "").trim(),
      emphasis: String(block?.emphasis || ""),
      knowledgeType: String(block?.knowledgeType || ""),
      sourceType: String(block?.sourceType || ""),
      nodeIds: Array.isArray(block?.nodeIds) ? block.nodeIds.map((item) => String(item || "")) : [],
    })),
  });
}

function lockedKnowledgePlanIntegrity(draft) {
  const plan = draft?.executionPlan;
  const lockedCopySet = plan?.lockedCopySet;
  const pages = Array.isArray(plan?.pageScript) ? plan.pageScript : [];
  if (!hasLockedKnowledgePlan(draft) || !pages.length) return false;
  const activeSeriesId = String(plan.activeSeriesId || "");
  const series = Array.isArray(plan.pageTaskSet?.series)
    ? (plan.pageTaskSet.series.find((item) => String(item?.seriesId || "") === activeSeriesId)
      || plan.pageTaskSet.series[0])
    : null;
  const tasks = Array.isArray(plan.pageTasks) && plan.pageTasks.length
    ? plan.pageTasks
    : (Array.isArray(series?.tasks) ? series.tasks : []);
  const copies = Array.isArray(plan.lockedPageCopies) && plan.lockedPageCopies.length
    ? plan.lockedPageCopies
    : (Array.isArray(lockedCopySet?.series)
      ? ((lockedCopySet.series.find((item) => String(item?.seriesId || "") === activeSeriesId)
        || lockedCopySet.series[0])?.lockedPageCopies || [])
      : []);
  if (!tasks.length || tasks.length !== copies.length || pages.length !== tasks.length) return false;
  const tasksById = new Map(tasks.map((task) => [String(task?.id || ""), task]));
  const copiesByTaskId = new Map(copies.map((copy) => [String(copy?.taskId || ""), copy]));
  const pagesByTaskId = new Map(pages.map((page) => [String(page?.pageTaskId || ""), page]));
  if ([...tasksById.keys()].some((id) => !id) || tasksById.size !== tasks.length) return false;
  if ([...copiesByTaskId.keys()].some((id) => !id) || copiesByTaskId.size !== copies.length) return false;
  if ([...pagesByTaskId.keys()].some((id) => !id) || pagesByTaskId.size !== pages.length) return false;
  const coverCount = tasks.filter((task) => String(task?.pageRole || "").toLowerCase() === "cover").length;
  const innerCount = tasks.filter((task) => String(task?.pageRole || "").toLowerCase() === "inner").length;
  if (coverCount !== 1 || innerCount < 1 || String(tasks[0]?.pageRole || "").toLowerCase() !== "cover") return false;
  for (const [index, task] of tasks.entries()) {
    const taskId = String(task?.id || "");
    const copy = copiesByTaskId.get(taskId);
    const page = pagesByTaskId.get(taskId);
    if (!copy || !page || Number(task?.pageNumber) !== index + 1 || Number(page?.page) !== index + 1) return false;
    if (Number(copy?.pageNumber) !== index + 1 || String(copy?.pageRole || "") !== String(task?.pageRole || "")) return false;
    if (copy.kind !== "LockedPageCopyV2" || copy.status && copy.status !== "locked") return false;
    if (!String(copy.headline || "").trim() || !Array.isArray(copy.contentBlocks) || !copy.contentBlocks.length) return false;
    if (String(page.lockedPageCopyId || "") !== String(copy.id || "")) return false;
    if (copy.contentHash && String(page.lockedPageCopyHash || "") !== String(copy.contentHash)) return false;
    if (lockedPageCopySignature(page) !== lockedPageCopySignature(copy)) return false;
  }
  return true;
}

function visualPageRoleLabel(page, index) {
  const pageLabel = String(page?.pageLabel || page?.imageLabel || "").trim();
  if (pageLabel) return pageLabel;
  const role = String(page?.layoutRole || page?.role || "").toLowerCase();
  if (role.includes("cover") || role.includes("封面")) return "封面";
  const pageNumber = Math.max(1, Number(page?.page) || index + 1);
  return role ? `内页 ${Math.max(1, pageNumber - 1)}` : (index === 0 ? "封面" : `内页 ${index}`);
}

function hasVisualPageCopyEdits(draft) {
  return Object.values(draft?.pageCopyEdits || {}).some((edit) => (
    edit && typeof edit === "object" && (
      Object.prototype.hasOwnProperty.call(edit, "headline")
      || Object.keys(edit.contentBlocks || {}).length > 0
    )
  ));
}

// 「确认全部页面」是出图前的一次人工签收：面板里显示的每一页（锁定文案 + 用户改动）
// 都要被确认过，才允许提交生图。签收按"当前文字内容"的指纹记账，任何一次改动都会让
// 指纹失配、自动退回未确认；锁定态用户也能用同一颗按钮回到编辑态，不会卡死在锁定里。
function visualCopyPlanPages(draft) {
  return Array.isArray(draft?.executionPlan?.pageScript) ? draft.executionPlan.pageScript : [];
}

function hasVisualCopyPlan(draft) {
  return visualCopyPlanPages(draft).length > 0;
}

function visualCopyReviewSignature(draft) {
  const edits = draft?.pageCopyEdits && typeof draft.pageCopyEdits === "object" ? draft.pageCopyEdits : {};
  return JSON.stringify(visualCopyPlanPages(draft).map((page, index) => {
    const taskId = String(page?.pageTaskId || "");
    const pageEdit = edits[taskId] && typeof edits[taskId] === "object" ? edits[taskId] : {};
    const headline = Object.prototype.hasOwnProperty.call(pageEdit, "headline")
      ? String(pageEdit.headline || "")
      : String(page?.headline || "");
    const blockEdits = pageEdit.contentBlocks && typeof pageEdit.contentBlocks === "object"
      ? pageEdit.contentBlocks
      : {};
    const blocks = (Array.isArray(page?.contentBlocks) ? page.contentBlocks : [])
      .filter((block) => String(block?.role || "").toLowerCase() !== "title")
      .map((block) => {
        const blockId = String(block?.id || "");
        const text = Object.prototype.hasOwnProperty.call(blockEdits, blockId)
          ? String(blockEdits[blockId] || "")
          : String(block?.text || "");
        return [blockId, text];
      });
    return [Number(page?.page) || index + 1, taskId, headline, blocks];
  }));
}

// 只有已经锁定过文案的执行包（自带 LockedPageCopyV2）才需要人工签收：规划态
// （PageTaskV2，逐页文字还没生成）是要交给「一键生成」去落文案的，不能被这道门拦死。
// 没有任何改动的旧草稿仍按服务端锁定态放行，升级后不会突然不能出图。
function visualCopyPlanLockedByServer(draft) {
  return Boolean(
    visualExecutionPlanMatchesDraft(draft)
      && lockedKnowledgePlanIntegrity(draft)
      && !hasVisualPageCopyEdits(draft)
  );
}

function isVisualCopyLocked(draft) {
  if (!hasVisualCopyPlan(draft)) return true;
  const review = draft?.visualCopyReview;
  if (review && typeof review === "object") {
    if (review.locked === false) return false;
    return String(review.signature || "") === visualCopyReviewSignature(draft);
  }
  return visualCopyPlanLockedByServer(draft);
}

function visualCopyNeedsConfirmation(draft) {
  return Boolean(
    hasVisualCopyPlan(draft)
      && visualExecutionPlanMatchesDraft(draft)
      && lockedKnowledgePlanIntegrity(draft)
      && !isVisualCopyLocked(draft)
  );
}

function guardVisualCopyReview(draft) {
  if (!visualCopyNeedsConfirmation(draft)) return true;
  toast("请先确认全部页面文字，再提交出图");
  // 被拦下时同时把逐页文字面板打开，用户不用在整个页面里找入口。
  if (typeof canViewDraftAiBlock === "function" && canViewDraftAiBlock(draft, "image") && state.openDraftAiBlock !== "image") {
    toggleDraftAiBlock("image");
  }
  return false;
}

function updateVisualCopyStatus(draft) {
  const status = $("#visualCopyStatus");
  if (!status) return;
  const planMatchesDraft = visualExecutionPlanMatchesDraft(draft);
  const aiTemplateFit = lockedKnowledgePlanIntegrity(draft) && planMatchesDraft;
  const hasEdits = hasVisualPageCopyEdits(draft);
  const locked = planMatchesDraft && isVisualCopyLocked(draft);
  status.textContent = !planMatchesDraft
    ? "页面设置已改变，请重新搭建"
    : locked
      ? (hasEdits ? "文字已锁定，生图将使用当前文字" : "文字已锁定，可检查修改")
      : hasEdits
        ? "已修改，生图将使用当前文字"
        : aiTemplateFit ? "逐页文字已生成，可检查修改" : "等待一键生成后写入图中文字";
  status.classList.toggle("success", planMatchesDraft && (locked || aiTemplateFit || hasEdits));
  status.classList.toggle("warning", !planMatchesDraft);
  const button = $("#confirmVisualCopyBtn");
  if (!button) return;
  // 只有已锁定文案的执行包才谈得上"确认全部页面"：规划态的逐页文字还没落，
  // 首次「一键生成」会自己把它锁好，这里不给一个会白确认的按钮。
  const actionable = planMatchesDraft && hasVisualCopyPlan(draft) && lockedKnowledgePlanIntegrity(draft);
  button.classList.toggle("hidden", !actionable);
  button.disabled = !actionable;
  button.textContent = locked ? "继续修改" : "确认全部页面";
  button.dataset.visualCopyAction = locked ? "unlock" : "lock";
  button.title = locked
    ? "解除锁定后可继续修改逐页文字，改完需要重新确认"
    : "确认全部页面后，出图才会使用当前文字";
}

function confirmVisualCopyReview() {
  const draft = currentDraft();
  if (!draft || !hasVisualCopyPlan(draft)) return;
  draft.visualCopyReview = {
    locked: true,
    signature: visualCopyReviewSignature(draft),
    lockedAt: new Date().toISOString(),
  };
  saveState();
  renderEditor();
  toast("已确认全部页面文字，出图将使用当前文字");
}

function unlockVisualCopyReview() {
  const draft = currentDraft();
  if (!draft || !hasVisualCopyPlan(draft)) return;
  draft.visualCopyReview = {
    locked: false,
    signature: visualCopyReviewSignature(draft),
    unlockedAt: new Date().toISOString(),
  };
  saveState();
  renderEditor();
  toast("已解除锁定，可继续修改逐页文字");
}

function renderVisualCopyPlan(draft) {
  const mount = $("#visualCopyPlan");
  const pagesMount = $("#visualCopyPages");
  const pages = Array.isArray(draft?.executionPlan?.pageScript) ? draft.executionPlan.pageScript : [];
  if (!mount || !pagesMount || !pages.length) {
    mount?.classList.add("hidden");
    if (pagesMount) pagesMount.innerHTML = "";
    return;
  }
  mount.classList.remove("hidden");
  updateVisualCopyStatus(draft);
  pagesMount.innerHTML = pages.map((page, index) => {
    const taskId = String(page.pageTaskId || "");
    const pageEdit = draft?.pageCopyEdits?.[taskId] || {};
    const headline = Object.prototype.hasOwnProperty.call(pageEdit, "headline")
      ? String(pageEdit.headline || "")
      : String(page.headline || "");
    const contentBlocks = (Array.isArray(page.contentBlocks) ? page.contentBlocks : [])
      .filter((block) => String(block?.role || "").toLowerCase() !== "title");
    return `
    <article class="visual-copy-page">
      <div class="visual-copy-page-head">
        <div><strong>${escapeHtml(visualPageRoleLabel(page, index))}</strong><small>${escapeHtml(page.templateName || "通用版式")}</small></div>
        <span>第 ${Number(page.page) || index + 1} 页</span>
      </div>
      <label>
        <span>图片标题 <em>${headline.length} 字</em></span>
        <input type="text" value="${escapeHtml(headline)}" data-visual-copy-field="headline" data-visual-page-task-id="${escapeHtml(taskId)}" />
      </label>
      ${contentBlocks.length ? `
        <div class="visual-copy-blocks">
          ${contentBlocks.map((block) => {
            const blockId = String(block.id || "");
            const blockEdits = pageEdit.contentBlocks && typeof pageEdit.contentBlocks === "object" ? pageEdit.contentBlocks : {};
            const value = Object.prototype.hasOwnProperty.call(blockEdits, blockId)
              ? String(blockEdits[blockId] || "")
              : String(block.text || "");
            const label = block.label || block.knowledgeType || block.role || "内容";
            return `
              <label>
                <span><b>${escapeHtml(label)}</b><em>${value.length} 字</em></span>
                <textarea rows="4" data-visual-copy-field="block" data-visual-page-task-id="${escapeHtml(taskId)}" data-visual-block-id="${escapeHtml(blockId)}">${escapeHtml(value)}</textarea>
              </label>`;
          }).join("")}
        </div>` : ""}
    </article>`;
  }).join("");
}

function updateVisualPageCopy(input) {
  const draft = currentDraft();
  const taskId = String(input?.dataset?.visualPageTaskId || "");
  const field = String(input?.dataset?.visualCopyField || "");
  const page = (draft?.executionPlan?.pageScript || []).find((item) => String(item?.pageTaskId || "") === taskId);
  if (!draft || !page || !taskId || !field) return;
  const edits = draft.pageCopyEdits && typeof draft.pageCopyEdits === "object" ? { ...draft.pageCopyEdits } : {};
  const pageEdit = edits[taskId] && typeof edits[taskId] === "object"
    ? { ...edits[taskId], contentBlocks: { ...(edits[taskId].contentBlocks || {}) } }
    : { contentBlocks: {} };
  if (field === "headline") {
    if (input.value === String(page.headline || "")) delete pageEdit.headline;
    else pageEdit.headline = input.value;
  } else if (field === "block") {
    const blockId = String(input.dataset.visualBlockId || "");
    const block = (page.contentBlocks || []).find((item) => String(item?.id || "") === blockId);
    if (!block) return;
    if (input.value === String(block.text || "")) delete pageEdit.contentBlocks[blockId];
    else pageEdit.contentBlocks[blockId] = input.value;
  }
  if (!Object.prototype.hasOwnProperty.call(pageEdit, "headline") && !Object.keys(pageEdit.contentBlocks).length) delete edits[taskId];
  else edits[taskId] = pageEdit;
  draft.pageCopyEdits = edits;
  const counter = input.closest("label")?.querySelector("em");
  if (counter) counter.textContent = `${input.value.length} 字`;
  updateVisualCopyStatus(draft);
  saveState();
}

// #section-rewrite.section.active 自己是 overflow-y:hidden，scrollHeight 等于 clientHeight，
// scrollTop 恒为 0（实测：set scrollTop=500 读回仍是 0）；真正滚动编辑区内容的是里面的
// .editor-form（overflow-y:auto）。以前只保存/恢复外层，等于什么都没保存，所以生成期间
// 图片区一变矮（内容被渲染重置）浏览器把内层 scrollTop 夹小之后，用户的位置就永久丢了——
// 表现就是"生成图片时页面自动往上滑，滑回进度栏又被抬上去"。
function rewriteScrollTargets() {
  const section = $("#section-rewrite");
  if (!section) return [];
  return [section, ...section.querySelectorAll(".rewrite-layout, .editor-form")];
}

// 一帧之内 renderEditor 会被连调两次：一页的 finally 渲染完，同一微任务链立刻开始下一页
// 并再渲染一次（runConcurrentJobs 的 while 循环，中间没有机会跑 rAF）。而渲染途中
// resizeRewriteContentField 会把正文框先压成 0 高（强制重排），此刻浏览器会把
// .editor-form.scrollTop 跟着夹小；于是**第二次捕获读到的是夹小后的值**，它的 rAF 又比
// 第一次晚跑，最终把错值写回去 —— 每页一次，位置就逐页往上棘轮（P83 之后的第二个成因）。
// 所以：一帧只认**第一次**捕获，并在同一帧只排一次恢复。
let rewriteScrollRestoreFrame = 0;
let rewriteScrollPendingState = null;

function captureRewriteScrollPosition() {
  if (rewriteScrollPendingState) return rewriteScrollPendingState;
  const targets = rewriteScrollTargets();
  if (!targets.length) return null;
  rewriteScrollPendingState = targets.map((node) => {
    const top = node.scrollTop;
    // 生成期间内容会先变矮再长回来。贴底看进度时记住"贴底"，内容再长出来也留在底部。
    const pinnedToBottom = node.scrollHeight - node.clientHeight > 0
      && node.scrollHeight - node.clientHeight - top <= 2;
    return { node, top, pinnedToBottom };
  });
  return rewriteScrollPendingState;
}

function preserveRewriteScrollPosition(scrollState) {
  if (!Array.isArray(scrollState) || !scrollState.length) return;
  window.cancelAnimationFrame(rewriteScrollRestoreFrame);
  rewriteScrollRestoreFrame = window.requestAnimationFrame(() => {
    // 先释放：本帧的恢复已经排上，下一帧必须重新捕获，否则位置会被永久钉住。
    rewriteScrollPendingState = null;
    if (state.currentSection !== "rewrite") return;
    scrollState.forEach(({ node, top, pinnedToBottom }) => {
      if (!node?.isConnected) return;
      const max = Math.max(0, node.scrollHeight - node.clientHeight);
      node.scrollTop = pinnedToBottom ? max : Math.min(top, max);
    });
  });
}

// 编辑器里的标题/正文/标签是"尚未提交"的自由文本：它们跟着 state 走，DOM 却不会
// 因为 state 换了而自己清空。切组织后如果不主动清，上一个组织的内容会留在新组织的
// 视图里，用户在新组织点保存就把它写过去了。已输入的文本早已由 syncFormToDraft()
// 同步进原组织的草稿对象，所以清 DOM 不会丢内容。
function resetDraftEditorInputs() {
  ["#draftTitle", "#draftBody", "#draftTags"].forEach((selector) => {
    const field = $(selector);
    if (field) field.value = "";
  });
  if (typeof resizeRewriteContentFields === "function") resizeRewriteContentFields();
}

function renderEditor(options = {}) {
  const rewriteScrollState = options.preserveScroll === false ? null : captureRewriteScrollPosition();
  renderDraftList();
  const draft = currentDraft();
  syncRewriteViralPromptTaskVisibility(draft);
  syncLocalReferenceSelectionOwner(draft);
  syncRewriteWorkspaceChrome(draft);
  syncTemplateImportProgressToDraft(draft?.id || "");
  const form = $("#editorForm");
  const empty = $("#emptyEditor");
  if (!draft) {
    resetDraftEditorInputs();
    form.classList.add("hidden");
    empty.classList.remove("hidden");
    $("#viralDraftPlan")?.classList.add("hidden");
    Object.values(draftAiBlocks).forEach((config) => $(`#${config.panelId}`)?.classList.add("hidden"));
    $("#rewriteLibrarySelections")?.classList.add("hidden");
    $("#draftImages").innerHTML = "";
    $("#imageGenerationErrorStatus")?.classList.add("hidden");
    if ($("#imageGenerationErrorStatus")) $("#imageGenerationErrorStatus").innerHTML = "";
    renderKnowledgeEvidence(null);
    renderPreview(null);
    preserveRewriteScrollPosition(rewriteScrollState);
    return;
  }

  empty.classList.add("hidden");
  form.classList.remove("hidden");
  const replicaMode = isReplicaDraft(draft);
  const replicaImageMode = isReplicaImageDraft(draft);
  const replicaPrompt = $("#replicaVisualPrompt");
  if (replicaPrompt && document.activeElement !== replicaPrompt) {
    // 框里显示的就是这次生成会用的那条提示词：AI 配置里的当前值，或用户在任务里改成的那条。
    replicaPrompt.value = draftAiPrompt(draft, "cover");
  }
  [$("#draftTitle"), $("#draftBody"), $("#draftTags")].forEach((field) => {
    if (!field) return;
    field.readOnly = false;
    field.setAttribute("aria-readonly", "false");
  });
  renderKnowledgeEvidence(draft);
  const selectionSignature = viralSelectionSignature(draft.librarySelection);
  if (
    !draft.executionPlan
    && draft.librarySelection?.topicId
    && draft.viralSelectionAppliedSignature !== selectionSignature
  ) {
    applyViralSelectionPreviewToDraft(draft);
    saveState();
  }
  const viralMode = usesFourLibraryCompose(draft);
  const rewriteButton = $("#rewriteBtn");
  const templateDecompositionMode = isTemplateDecompositionDraft(draft);
  const templateImportAwaitingSelection = isTemplateImportAwaitingSelection(draft);
  const templateImportReady = isTemplateImportReady(draft);
  // 非图文计划流（快速笔记/复刻）直接拿草稿的标题、正文、标签和图片当输入，没有任何
  // 素材就生成等于凭空产出并扣费，所以按钮先灰掉并说明缺什么（P73）。
  const hasUsableContent = draftHasUsableContent(draft);
  // 模板已入库但没选选题的原创笔记不允许生成：按钮既要灰掉，也要说清为什么（P106）。
  const viralTopicMissing = draftNeedsViralTopic(draft);
  const rewriteReady = viralMode ? hasReadyViralExecutionPlan(draft) : (hasUsableContent && !viralTopicMissing);
  // 拆解入库也要有可拆的东西：只要"链接草稿且还没选模板"就亮按钮的话，空草稿点开弹窗、
  // 勾上选题/标题确认后真的会发起一次拆解任务（2026-09-15 实测会打到 /api/viral/analyze）。
  const templateDecompositionReady = templateDecompositionMode && hasUsableContent;
  const generationState = state.rewriteGeneration || {};
  const generationOwnerId = String(generationState.ownerDraftId || "");
  const isGenerating = Boolean(generationState.active && generationOwnerId === String(draft.id || ""));
  const isCancelling = Boolean(generationState.cancelling);
  // A拆解/入库 run is owned by the page session, not by the rewrite state.
  // Re-rendering the editor used to drop the cancel label while the click
  // handler still cancelled the running job, so the button lied.
  const templateImportRunning = typeof isTemplateImportRunningForDraft === "function"
    && isTemplateImportRunningForDraft(draft);
  // 拆解/入库也分「在跑」和「正在停」两种：生成任务早就有这个区分，拆解没有，于是用户按下
  // 中止后按钮既不换文案也不锁，看不出到底有没有生效，只能反复点。
  const templateImportCancelling = templateImportRunning
    && typeof isTemplateImportCancelling === "function"
    && isTemplateImportCancelling();
  const generationStage = viralMode
    ? (generationState.stage === "image" ? "正在生成图文图片..." : "正在生成图文内容...")
    : replicaImageMode ? "正在生成图片文案..." : "AI 改写中...";
  // 这份草稿上是否有任何生成流程在跑（一键生成 / 单字段 / 单视觉角色 / 单图）：只用来决定
  // 每张图片的"重试/重新生成"按钮是否可点，不再参与主按钮的置灰。
  const draftGenerating = draftGenerationBusyHere(draft.id);
  if (rewriteButton) {
    const rewriteBusy = isGenerating || templateImportRunning;
    // 有别的生成在跑时不再把主按钮置灰：灰着又没有任何说明，用户只能猜（"是不是生成过
    // 就不让再生成"）。按钮保持可点，点下去由 rewriteDraft 的守卫提示"已有任务在跑"。
    rewriteButton.disabled = templateImportCancelling
      || (!viralMode
        && !isGenerating
        && !templateImportRunning
        && !rewriteReady
        && !templateImportAwaitingSelection
        && !templateDecompositionReady);
    rewriteButton.classList.toggle("is-loading", rewriteBusy);
    rewriteButton.classList.toggle("is-cancel-action", rewriteBusy);
    // P110：拆解与生成共用这一个按钮、同一套 loading/取消样式，光看控件分不清在跑哪件事。
    // 除文案写明任务类型外，再挂上类型类名，让取消样式与进度条按类型走不同表现。
    rewriteButton.classList.toggle("is-task-decompose", templateImportRunning);
    rewriteButton.classList.toggle("is-task-generate", isGenerating);
    rewriteButton.setAttribute("aria-busy", String(rewriteBusy));
    rewriteButton.textContent = isGenerating
      ? (isCancelling ? "正在停止图文生成..." : "停止图文生成")
      : templateImportRunning
        ? (templateImportCancelling ? "正在中止拆解..." : "中止拆解")
        : templateDecompositionMode
        ? "拆解并入库模板"
        : templateImportAwaitingSelection
          ? "去模板库选择模板"
        : viralMode
        ? (rewriteReady ? "生成完整图文" : "按四库搭建内容")
        : templateImportReady
          ? (viralTopicMissing ? "请先选择选题" : "使用模板生成图文")
        : replicaMode
          ? (replicaImageMode ? "根据图片生成文案" : "按默认提示词生成完整图文")
          : "生成完整图文";
    rewriteButton.title = isGenerating
      ? `停止图文生成 · ${generationStage}`
      : templateImportRunning
        ? (templateImportCancelling ? "已请求中止，正在等当前页收尾" : "中止当前模板拆解任务")
        : templateImportAwaitingSelection
        ? "模板已入库，请先选择封面和内页模板"
        : viralTopicMissing
        ? "请先从选题库选择一个选题，再生成图文"
        : viralMode && !rewriteReady
        ? "按选题、标题、封面、内页的顺序补齐选择，再搭建笔记内容"
        : (rewriteReady ? "" : (hasUsableContent ? "请先搭建完整笔记内容" : EMPTY_DRAFT_GENERATION_HINT));
  }
  syncRewriteViralPromptButton(draft);
  // 分区生成只按"自己这一区"置灰：标题在跑不该锁住正文/标签/图片——它们各写各的字段，本来
  // 就能同时跑。整篇生成（要重写全部字段）仍然锁住所有分区；反过来任何一个分区在跑，整篇
  // 生成那颗按钮保持可点：点下去由入口守卫提示"已有任务在跑"，不再默默置灰。
  const draftFullActive = draftFullGenerationActive(draft.id);
  $$("[data-generate-ai-block]").forEach((button) => {
    const role = String(button.dataset.generateAiBlock || "");
    const blockBusy = draftBlockGenerationBusy(draft.id, role);
    button.classList.toggle("is-generating", blockBusy);
    const blocked = draftFullActive || blockBusy;
    if (role === "title" || role === "body" || role === "tags") {
      // 单字段 AI 也是生成入口：空草稿点下去等于凭空产出，后端 /api/ai/text 会以
      // ai_draft_empty 拒绝（P73）。入口用同一条判据先灰掉，别让用户以为任务已经开始。
      button.disabled = blocked || !hasUsableContent;
      button.title = !blocked && !hasUsableContent ? EMPTY_DRAFT_GENERATION_HINT : "";
      return;
    }
    button.disabled = blocked;
  });
  // A persisted "importing" flag without a runtime owner is an interrupted import
  // (reload / backend restart / crashed tab).  Clearing it keeps the primary button
  // label and its click action in agreement.
  if (typeof recoverInterruptedTemplateImport === "function" && recoverInterruptedTemplateImport(draft)) {
    saveState();
    toast("上次模板入库被中断，已重置该状态，可继续生成图文");
  }
  // Restore the last per-page拆解 result when nothing is running, so the panel
  // still shows which page failed (or succeeded) after a reload.
  if (typeof restoreTemplateImportProgressFromDraft === "function") {
    restoreTemplateImportProgressFromDraft(draft);
  }
  const generationStatus = $("#rewriteGenerationStatus");
  if (generationStatus) {
    generationStatus.classList.toggle("hidden", !isGenerating);
    if (!isGenerating) {
      generationStatus.innerHTML = "";
    } else if (generationState.stage === "image") {
      const total = Math.max(1, Number(generationState.total) || 1);
      const completed = Math.min(total, Math.max(0, Number(generationState.completed) || 0));
      const current = Math.min(total, completed + 1);
      const percent = Math.round((completed / total) * 100);
      const progressTitle = completed >= total
        ? `已完成 ${total} 张图片`
        : Number(generationState.concurrency) > 1
          ? `正在并行生成 · ${completed}/${total}`
          : `正在生成第 ${current} / ${total} 张`;
      generationStatus.innerHTML = `
        <div class="generation-progress-head">
          <strong><em class="progress-task-kind" data-task-kind="generate">图文生成</em>${progressTitle}</strong>
          <span>${escapeHtml(generationState.currentLabel || "图文图片")}</span>
        </div>
        <div class="generation-progress-track" role="progressbar" aria-label="图文生成进度：图片" aria-valuemin="0" aria-valuemax="${total}" aria-valuenow="${completed}">
          <span style="width:${percent}%"></span>
        </div>
        <div class="generation-progress-meta"><span>已完成 ${completed} 张</span><b>${percent}%</b></div>
      `;
    } else {
      generationStatus.innerHTML = `
        <div class="generation-progress-head"><strong><em class="progress-task-kind" data-task-kind="generate">图文生成</em>正在统一生成正文内容</strong><span>完成后开始并行生图</span></div>
        <div class="generation-progress-track indeterminate" aria-label="图文生成进度：正文"><span></span></div>
      `;
    }
  }
  $("#section-rewrite .ai-generate-row")?.classList.remove("hidden");
  const migratedPrompts = migrateLegacyExecutionPrompts(draft);
  const migratedTags = migrateGeneratedXhsTags(draft);
  if (migratedPrompts || migratedTags) saveState();
  $("#draftTitle").value = draft.title;
  $("#draftBody").value = draft.body;
  $("#draftTags").value = draft.tags;
  resizeRewriteContentFields();
  $("#draftBody").placeholder = draft.executionPlan ? viralBriefPlaceholder(draft.viralBrief) || "等待按执行计划生成正文" : "";
  const planMount = $("#viralDraftPlan");
  if (planMount) {
    planMount.classList.add("hidden");
    planMount.innerHTML = "";
  }
  renderDraftAiBlocks(draft);
  const selectionMount = $("#rewriteLibrarySelections");
  if (selectionMount) {
    const selectionHtml = renderRewriteLibrarySelections(draft);
    selectionMount.classList.toggle("hidden", !selectionHtml);
    selectionMount.innerHTML = selectionHtml;
    if (selectionHtml) window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
  }
  draft.images = normalizeImages(draft.images);
  const displayImages = currentPreviewImages(draft);
  const displayLabels = Array.isArray(draft.templateRenderImageLabels) ? draft.templateRenderImageLabels : [];
  const displayStates = Array.isArray(draft.templateRenderImageStates) ? draft.templateRenderImageStates : [];
  const displayWatermarkReports = Array.isArray(draft.templateRenderWatermarkReports) ? draft.templateRenderWatermarkReports : [];
  const allDraftImageJobs = draftPromptImageJobs(draft) || [];
  const failedImageJobs = draftFailedImageJobs(draft, allDraftImageJobs);
  const failedImageLabels = [...new Set(failedImageJobs.map((job) => job.label))];
  const imageErrorStatus = $("#imageGenerationErrorStatus");
  if (imageErrorStatus) {
    const showImageError = Boolean(draft.templateRenderMode === "ai-error" && failedImageJobs.length);
    const retryingCount = draftImageRegenerationCounts(draft);
    const isRetryingFailedImages = retryingCount.active + retryingCount.queued > 0;
    imageErrorStatus.classList.toggle("hidden", !showImageError);
    imageErrorStatus.innerHTML = showImageError ? `
      <div>
        <strong>${escapeHtml(draft.imageGenerationError || imageGenerationFailureMessage(failedImageLabels, displayLabels.length))}</strong>
        <span>失败页：${escapeHtml(failedImageLabels.join("、"))}</span>
      </div>
      <button type="button" class="ghost-btn" data-retry-failed-draft-images ${isRetryingFailedImages || !canRegenerateDraftImage(draft) || draftGenerating ? "disabled" : ""}>
        <i data-lucide="rotate-ccw" aria-hidden="true"></i>
        <span>${isRetryingFailedImages ? `正在重试 ${retryingCount.active + retryingCount.queued} 张` : `重试失败页（${failedImageJobs.length}）`}</span>
      </button>
    ` : "";
  }
  const referenceEntries = !displayImages.length && draft.templateRenderMode === "ai-pending"
    ? draftPlannedPageEntries(draft)
    : [];
  const expansionReview = draft.pageExpansionReview?.status === "pending" ? draft.pageExpansionReview : null;
  const expansionPages = expansionReview && Array.isArray(expansionReview.pages)
    ? expansionReview.pages
    : [];
  // 搭建笔记内容期间同样锁住单图按钮：搭建按开始那一刻的图序快照落计划，期间改图只会白改
  // 或让页与图错位（拖动守卫见 shell/events.js 的 #draftImages pointerdown）。
  const viralBuilding = typeof viralPromptBuildController !== "undefined"
    && Boolean(viralPromptBuildController)
    && typeof viralPromptTaskBelongsToRewriteDraft === "function"
    && viralPromptTaskBelongsToRewriteDraft(draft);
  const renderedImageCards = displayImages.map((src, index) => {
        const imageJob = draftImageJob(draft, index);
        const imageSortKey = imageJob ? `job:${imageJob.key}` : `asset:${index}`;
        const isReference = displayStates[index] === "reference";
        const imageLabel = displayLabels[index] || imageJob?.label || (index === 0 ? "封面" : `图 ${index + 1}`);
        const singleImageGenerating = state.singleImageGenerationKeys.has(`${draft.id || "draft"}:${index}`);
        // 单图按钮过去只认自己那张图的 key，整组生成在跑时仍然可点（P88 症状）。
        const imageCardLocked = singleImageGenerating || isGenerating || viralBuilding;
        const canRegenerate = Boolean(imageJob && canRegenerateDraftImage(draft));
        const regenerationStatus = draftImageRegenerationStatus(draft, imageJob);
        return `
        <div class="draft-image-item ${singleImageGenerating ? "is-generating" : "is-draggable-image"}" data-draft-image-target-key="${escapeHtml(imageSortKey)}" ${singleImageGenerating ? "" : `data-draft-image-drag-key="${escapeHtml(imageSortKey)}" title="单击放大预览，长按拖动调整顺序"`}>
          <button type="button" class="image-compare-card" data-draft-preview-image="${escapeHtml(src)}" data-draft-image-label="${escapeHtml(imageLabel)}" data-draft-image-key="${escapeHtml(imageJob ? draftImageJobIdentity(imageJob) : "")}" data-preview-title="${escapeHtml(imageLabel)} · ${draft.templateRenderMode === "ai-full-page" ? "AI 新图" : "当前图片"}" aria-label="放大预览${escapeHtml(imageLabel)}" title="单击放大，长按拖动">
            <img src="${escapeHtml(src)}" alt="当前预览图片 ${index + 1}" draggable="false" />
          </button>
          <div class="draft-image-card-footer">
            <span class="draft-image-card-label"><i data-lucide="grip-vertical" aria-hidden="true"></i>${escapeHtml(imageLabel)}</span>
            <strong>${singleImageGenerating ? "后台生成中" : isReference ? "待 AI 生成" : draft.templateRenderMode === "ai-full-page" ? "AI 新图" : "当前使用"}</strong>
            ${renderWatermarkStatus(displayWatermarkReports[index])}
          </div>
          <div class="draft-image-actions">
            <button type="button" class="image-action-btn single-image-ai-action" data-generate-single-draft-image="${index}" aria-label="为${escapeHtml(imageLabel)}进行 AI 生成" title="只生成并替换这一张图片" ${imageCardLocked ? "disabled" : ""}><i data-lucide="sparkles" aria-hidden="true"></i><span>${singleImageGenerating ? "生成中" : "AI 生成"}</span></button>
            <button type="button" class="image-action-btn single-image-edit-action" data-edit-single-draft-image="${index}" aria-label="编辑${escapeHtml(imageLabel)}" title="填写提示词并添加参考图" ${isGenerating ? "disabled" : ""}><i data-lucide="pencil" aria-hidden="true"></i><span>编辑</span></button>
            ${canRegenerate ? `<button type="button" class="image-action-btn ${regenerationStatus ? "is-active" : ""}" data-regenerate-draft-image="${index}" aria-label="${regenerationStatus === "active" ? "正在生成" : regenerationStatus === "queued" ? "排队等待生成" : "重新生成"}${escapeHtml(imageLabel)}" title="${regenerationStatus === "active" ? "正在生成" : regenerationStatus === "queued" ? "已加入生成队列" : "重新生成这一张"}" ${regenerationStatus || imageCardLocked ? "disabled" : ""}>${regenerationStatus === "active" ? "…" : "↻"}</button>` : ""}
            ${imageJob?.contentPart === "cover" ? "" : `<button type="button" class="image-action-btn danger" data-remove-draft-image="${index}" aria-label="删除${escapeHtml(imageLabel)}" title="删除这张图片" ${imageCardLocked ? "disabled" : ""}><i data-lucide="trash-2" aria-hidden="true"></i></button>`}
          </div>
        </div>`;
      }).join("");
  const failedImageCards = failedImageJobs.map((job) => `
    <div class="draft-image-item missing">
      <div class="draft-image-missing">
        <span>生成失败</span>
        <strong>${escapeHtml(job.label)}</strong>
        <small>其他图片已保留，可只重试这一张</small>
        ${canRegenerateDraftImage(draft)
          ? `<button type="button" class="ghost-btn" data-regenerate-draft-job="${escapeHtml(draftImageJobIdentity(job))}" aria-label="重新生成${escapeHtml(job.label)}" title="只重新生成这一张${escapeHtml(job.label)}" ${draftGenerating ? "disabled" : ""}><i data-lucide="rotate-ccw" aria-hidden="true"></i><span>重新生成</span></button>`
          : `<small>请先搭建完整的 AI 提示词</small>`}
      </div>
    </div>`).join("");
  const imageBoardContent = renderedImageCards || failedImageCards
    ? `${renderedImageCards}${failedImageCards}`
    : referenceEntries.map((entry, index) => `
        <div class="draft-image-item is-draggable-image" data-draft-image-target-key="${escapeHtml(`job:${entry.key}`)}" data-draft-image-drag-key="${escapeHtml(`job:${entry.key}`)}" title="单击放大预览，长按拖动调整顺序">
          <button type="button" class="image-compare-card planned-reference" data-draft-preview-image="${escapeHtml(entry.src)}" data-draft-image-label="${escapeHtml(entry.label)}" data-preview-title="${escapeHtml(entry.label)}参考图" aria-label="放大预览${escapeHtml(entry.label)}参考图" title="单击放大，长按拖动">
            <img src="${escapeHtml(entry.src)}" alt="待生成${escapeHtml(entry.label)}参考图" draggable="false" />
          </button>
          <div class="draft-image-card-footer">
            <span class="draft-image-card-label"><i data-lucide="grip-vertical" aria-hidden="true"></i>${escapeHtml(entry.label)}参考</span>
            <strong>待 AI 生成</strong>
          </div>
          <div class="draft-image-actions">
            ${entry.contentPart === "cover" ? "" : `<button type="button" class="image-action-btn danger" data-remove-draft-page="${escapeHtml(entry.key)}" aria-label="删除${escapeHtml(entry.label)}" title="后续不再生成这一页">×</button>`}
          </div>
        </div>`).join("");
  const expansionPlaceholderCards = expansionPages.map((page, index) => {
    const pageNumber = Math.max(1, Number(page?.page) || Number(expansionReview.basePageCount) + index + 1);
    const thumbnail = page?.referenceImage || "";
    const image = thumbnail
      ? `<img src="${escapeHtml(thumbnail)}" alt="预计第 ${pageNumber} 页模板预览" draggable="false" />`
      : `<div class="draft-image-expansion-placeholder-empty"><i data-lucide="image" aria-hidden="true"></i><span>暂无模板缩略图</span></div>`;
    return `<div class="draft-image-item expansion-placeholder" data-expansion-page-id="${escapeHtml(page?.id || "")}">
      <div class="draft-image-expansion-placeholder">
        <div class="draft-image-expansion-thumb">${image}</div>
        <div class="draft-image-expansion-copy"><strong>预计第 ${pageNumber} 页</strong><small>${escapeHtml(page?.sourceTemplateName || "实际内页模板")} · 待确认</small></div>
        <div class="draft-image-actions">
          <button type="button" class="image-action-btn" data-remove-expansion-page="${escapeHtml(page?.id || "")}" aria-label="删除预计第 ${pageNumber} 页" title="删除此占位页"><i data-lucide="trash-2" aria-hidden="true"></i></button>
        </div>
      </div>
    </div>`;
  }).join("");
  const addPageCard = expansionReview
    ? `<button type="button" class="image-board-add-card expansion-add-card" data-add-expansion-page aria-label="增加一页" title="从内页模板库选择一页"><i data-lucide="plus"></i><strong>增加一页</strong><span>从内页模板库选择</span></button>`
    : `<button type="button" class="image-board-add-card" data-open-inner-page-picker aria-label="增加图片" title="从模板库选择或上传图片"><i data-lucide="plus"></i><strong>增加图片</strong><span>从模板库选择或上传</span></button>`;
  replaceMarkupPreservingImages($("#draftImages"), `${imageBoardContent}${expansionPlaceholderCards}${addPageCard}`);
  renderPageExpansionReview(draft);
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
  updateEditorStats(draft);
  renderPreview(draft);
  preserveRewriteScrollPosition(rewriteScrollState);
}

function currentPreviewImages(draft) {
  if (!draft) return [];
  const renderedImages = draft.templateRenderImages || [];
  if (draft.executionPlan && draft.templateRenderMode === "ai-pending" && !renderedImages.length) return [];
  return renderedImages.length ? renderedImages : draft.images || [];
}

function singleImageRolePrompt(draft, role) {
  const prompt = String(draftAiPrompt(draft, role) || "").trim();
  if (!prompt || role !== "inner") return prompt;
  // The generated inner prompt contains a long list of every page. A single-page
  // edit only needs the shared scene context and visual contract, not that list.
  const contentStart = prompt.indexOf("\n内容：");
  const styleStart = prompt.indexOf("\n风格：", contentStart >= 0 ? contentStart : 0);
  if (contentStart >= 0 && styleStart > contentStart) {
    return `${prompt.slice(0, contentStart)}${prompt.slice(styleStart)}`.trim();
  }
  return prompt;
}

function openExtensionTutorial() {
  showModal($("#extensionTutorialModal"));
}

function closeExtensionTutorial() {
  hideModal($("#extensionTutorialModal"));
}

function singleImagePageContext(draft, imageIndex) {
  const pages = Array.isArray(draft?.executionPlan?.pageScript) ? draft.executionPlan.pageScript : [];
  const jobs = typeof draftPromptImageJobs === "function" ? (draftPromptImageJobs(draft) || []) : [];
  const displayLabel = Array.isArray(draft?.templateRenderImageLabels)
    ? String(draft.templateRenderImageLabels[imageIndex] || "")
    : "";
  // 用 slot 感知的 draftImageJob 取这一格对应的页：复用模板会出现多个同名"内页 1"，
  // 按 label 找第一个会把第二格也指到第一页的文案上。
  const job = typeof draftImageJob === "function"
    ? draftImageJob(draft, imageIndex)
    : (displayLabel && jobs.find((item) => String(item?.label || "") === displayLabel)) || jobs[imageIndex];
  const page = job && typeof plannedPageForImageJob === "function"
    ? plannedPageForImageJob(draft, job)
    : pages[imageIndex];
  if (!page || typeof page !== "object") return null;
  const taskId = String(page.pageTaskId || "");
  const pageEdit = taskId && draft?.pageCopyEdits?.[taskId] && typeof draft.pageCopyEdits[taskId] === "object"
    ? draft.pageCopyEdits[taskId]
    : {};
  let headline = Object.prototype.hasOwnProperty.call(pageEdit, "headline")
    ? String(pageEdit.headline || "").trim()
    : String(page.headline || "").trim();
  const blockEdits = pageEdit.contentBlocks && typeof pageEdit.contentBlocks === "object"
    ? pageEdit.contentBlocks
    : {};
  const contentBlocks = (Array.isArray(page.contentBlocks) ? page.contentBlocks : [])
    .filter((block) => String(block?.role || "").toLowerCase() !== "title")
    .map((block) => {
      const blockId = String(block?.id || "");
      const text = Object.prototype.hasOwnProperty.call(blockEdits, blockId)
        ? String(blockEdits[blockId] || "").trim()
        : String(block?.text || "").trim();
      return {
        label: String(block?.label || block?.knowledgeType || block?.role || "内容").trim(),
        role: String(block?.role || "").toLowerCase(),
        text,
      };
    })
    .filter((block) => block.text);
  if (!headline) {
    const titleBlock = (Array.isArray(page.contentBlocks) ? page.contentBlocks : [])
      .find((block) => String(block?.role || "").toLowerCase() === "title" && String(block?.text || "").trim());
    headline = titleBlock ? String(titleBlock.text).trim() : "";
  }
  return {
    page,
    headline,
    contentBlocks,
  };
}

function singleImageDefaultPrompt(draft, imageIndex, label = "") {
  const role = imageIndex === 0 ? "cover" : "inner";
  const pageLabel = label || (imageIndex === 0 ? "封面" : `图 ${imageIndex + 1}`);
  const pageContext = singleImagePageContext(draft, imageIndex);
  const rolePrompt = singleImageRolePrompt(draft, role);
  const page = pageContext?.page || {};
  const lockedPageText = pageContext?.contentBlocks
    ?.map((block) => `- ${block.label}：${block.text}`)
    .join("\n");
  // 快速笔记（复刻）的图片只是"要改的那张图"：标题、正文、标签本来就是从这张图里读出来的，
  // 拼进提示词等于让模型照着文案把画面文字重排一遍。它也没有分页计划，所以不再追加边界句、
  // 保留句——和「一键生成」「图片面板」同一口径，只发提示词本身。提示词为空时给一句最小默认，
  // 否则弹层打开就是空框、没法直接生成（图生文案那套的图片提示词默认就是空的）。
  const replicaDraft = isReplicaDraft(draft);
  if (replicaDraft && !pageContext) {
    return rolePrompt || "基于当前图片生成一张新的小红书图片，保持竖版画面清晰可读。";
  }
  const pageCopy = pageContext
    ? [
        "这是单页视觉编辑任务，只处理当前页面，不要合并其它页面、整篇正文或整组提示词中的内容。",
        pageContext.headline ? `本页标题（必须准确呈现）：${pageContext.headline}` : "",
        lockedPageText ? `本页锁定内容（必须完整使用，不得改写、补充或删减）：\n${lockedPageText}` : "",
        page.visualDirection ? `本页视觉方向：${String(page.visualDirection).trim()}` : "",
      ]
    : [
        "这是现有页面的视觉编辑任务，不是内容生成任务。必须逐字保留输入图中的全部文字、数字、标点、段落顺序和信息结构；只按视觉提示词调整字体、颜色、背景、材质、装饰或版式表现。",
        replicaDraft
          ? ""
          : `结合当前标题、正文和标签完成新的视觉表达。\n标题：${draft?.title || ""}\n正文：${draft?.body || ""}\n标签：${draft?.tags || ""}`,
      ];
  return [
    rolePrompt,
    "基于当前图片生成一张新的小红书图片。",
    `页面角色：${pageLabel}。`,
    ...pageCopy,
    // 上面刚要求"逐字保留全部文字"，这里不能再授权"修改画面文字"，两句会互相打架。
    "可在不改动画面文字内容的前提下，按提示词调整字体、颜色、排版、主体细节和构图，保持竖版画面清晰可读。",
  ].filter(Boolean).join("\n\n");
}

function replaceDraftImageAtIndex(draft, imageIndex, nextImage, watermarkReport = null, expectedImage = "") {
  if (!draft || !nextImage) return false;
  const renderedImages = normalizeImages(draft.templateRenderImages);
  const displayedImages = renderedImages.length ? renderedImages : normalizeImages(draft.images);
  if (expectedImage && displayedImages[imageIndex] !== expectedImage) {
    const matchingIndexes = displayedImages.reduce((indexes, image, index) => {
      if (image === expectedImage) indexes.push(index);
      return indexes;
    }, []);
    if (matchingIndexes.length !== 1) return false;
    imageIndex = matchingIndexes[0];
  }
  if (imageIndex < 0 || imageIndex >= displayedImages.length) return false;

  if (renderedImages.length) {
    draft.templateRenderImages = [...renderedImages];
    draft.templateRenderImages[imageIndex] = nextImage;
    draft.images = [...draft.templateRenderImages];
    const states = Array.isArray(draft.templateRenderImageStates) ? [...draft.templateRenderImageStates] : [];
    const reports = Array.isArray(draft.templateRenderWatermarkReports) ? [...draft.templateRenderWatermarkReports] : [];
    while (states.length < draft.templateRenderImages.length) states.push("generated");
    while (reports.length < draft.templateRenderImages.length) reports.push(null);
    states[imageIndex] = "generated";
    reports[imageIndex] = watermarkReport;
    draft.templateRenderImageStates = states;
    draft.templateRenderWatermarkReports = reports;
  } else {
    draft.images = [...displayedImages];
    draft.images[imageIndex] = nextImage;
    // 复刻（快速笔记）的草稿没有 templateRenderImages，所以单张 AI 生成必然走这条分支。
    // 原来 watermarkReport 在这里被丢掉：水印其实去了，但"去掉了"这件事没被记下来，
    // 界面上就不显示水印状态（实测：replica 草稿里连 templateRenderWatermarkReports 字段都没有）。
    // 只补报告数组并按图片数对齐，不动 templateRenderImageStates——那是模板渲染态的指示，
    // 这里的图是从笔记导入的原图，不该被标成 generated。
    const reports = Array.isArray(draft.templateRenderWatermarkReports)
      ? [...draft.templateRenderWatermarkReports]
      : [];
    while (reports.length < draft.images.length) reports.push(null);
    reports[imageIndex] = watermarkReport;
    draft.templateRenderWatermarkReports = reports;
  }

  draft.generatedAt = new Date().toISOString();
  const label = draft.templateRenderImageLabels?.[imageIndex] || (imageIndex === 0 ? "封面" : `图 ${imageIndex + 1}`);
  refreshImageViewerGeneratedImage(draft, draftImageJob(draft, imageIndex) || { label, key: "" }, nextImage, imageIndex);
  saveState();
  renderEditor();
  return true;
}

async function generateSingleDraftImageWithPrompt(draft, imageIndex, prompt, referenceSources = [], signal) {
  const promptValue = String(prompt || "");
  if (!promptValue.trim()) throw new Error("请先填写图片提示词");
  const currentImage = normalizeImages(currentPreviewImages(draft))[imageIndex];
  if (!currentImage) throw new Error("没有找到要生成的图片");
  const sources = [currentImage, ...referenceSources.filter(Boolean)].slice(0, MAX_IMAGE_INPUTS);
  const inputImages = await Promise.all(sources.map((src) => imageInputSourceForAi(src, signal)));
  if (!assertImageRequestFitsLimit(inputImages, promptValue.length)) throw new Error("参考图体积超过单次上限，请减少参考图后重试");
  await syncAiConfigBeforeGeneration();
  const result = await runImageAi(promptValue, {
    count: 1,
    size: "1024x1536",
    quality: "medium",
    inputImages,
    preferEdit: true,
    signal,
  });
  const nextImage = normalizeImages(result.images)[0];
  if (!nextImage) throw new Error("生图接口没有返回图片");
  if (!replaceDraftImageAtIndex(draft, imageIndex, nextImage, result.watermarkReports?.[0] || null, currentImage)) {
    throw new Error("目标图片已发生变化，请重新选择后再试");
  }
  return nextImage;
}

async function generateSingleDraftImage(imageIndex, button) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) return;
  if (!guardVisualCopyReview(draft)) return;
  // 账号维度：别的草稿在跑 AI 任务时点名拒绝（按钮不置灰，点了才知道为什么）。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  // 共享运行态：整组生成 / 单字段 / 单视觉角色在跑时不能再插单图任务。
  if (draftGenerationBusy(draft.id, "image")) {
    toast("这份笔记正在整组生成，请完成后再生成单张图片");
    return;
  }
  if (!ensureAiConfigured("image")) return;
  const images = normalizeImages(currentPreviewImages(draft));
  if (imageIndex < 0 || imageIndex >= images.length) return;
  const key = `${draft.id || "draft"}:${imageIndex}`;
  if (state.singleImageGenerationKeys.has(key)) {
    toast("这张图片正在生成");
    return;
  }
  const label = draft.templateRenderImageLabels?.[imageIndex] || (imageIndex === 0 ? "封面" : `图 ${imageIndex + 1}`);
  state.singleImageGenerationKeys.add(key);
  const generationRun = beginDraftGenerationRun(draft.id, "image");
  if (button) {
    button.disabled = true;
    button.innerHTML = "<span>生成中</span>";
  }
  // 块头那颗「查看/编辑提示词」读的是运行态，开始生成就得主动刷一次：否则在这张图上
  // 报完任务看不到任何反馈，要等用户自己点一下折叠按钮（那一下会重渲染）才会变成"生成中…"。
  renderDraftGenerationIndicators(draft);
  let nextImage = "";
  try {
    nextImage = await generateSingleDraftImageWithPrompt(draft, imageIndex, singleImageDefaultPrompt(draft, imageIndex, label));
    toast(`${label}已生成并替换`);
  } catch (error) {
    if (!isAbortError(error)) toast(friendlyImageError(error), { kind: "error", duration: 6500 });
  } finally {
    state.singleImageGenerationKeys.delete(key);
    endDraftGenerationRun(generationRun);
    // 卡片入口发起的生成同样要让开着的弹层跟着收回"生成中"。
    settleSingleImageEditorAfterRun(draft.id, imageIndex, nextImage);
    renderEditor();
  }
}

function renderSingleImageReferences() {
  const editor = state.singleImageEditor || {};
  const mount = $("#singleImageReferenceList");
  const count = $("#singleImageReferenceCount");
  if (count) count.textContent = `${editor.references?.length || 0} 张参考图`;
  if (!mount) return;
  mount.innerHTML = editor.references?.length
    ? editor.references.map((item, index) => `
        <div class="single-image-reference-item">
          <img src="${escapeHtml(item.src)}" alt="参考图 ${index + 1}" />
          <button type="button" data-remove-single-image-reference="${index}" aria-label="移除参考图 ${index + 1}" title="移除参考图"><i data-lucide="x" aria-hidden="true"></i></button>
        </div>`).join("")
    : '<span class="single-image-reference-empty">还没有添加参考图</span>';
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function resetSingleImageEditorControls() {
  $("#singleImagePrompt")?.removeAttribute("disabled");
  $("#singleImageReferenceInput")?.removeAttribute("disabled");
  const submitButton = $("#generateSingleImageBtn");
  if (!submitButton) return;
  submitButton.disabled = false;
  submitButton.innerHTML = '<i data-lucide="sparkles" aria-hidden="true"></i><span>生成并替换</span>';
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

// 一张图的"生成中"只认 state.singleImageGenerationKeys：不管是从弹层提交，还是从卡片上
// 那颗「AI 生成」发起，弹层外观都按它现算——两处各记一份必然走样（实测：状态留住了，
// 按钮却还是"生成并替换"）。
function applySingleImageEditorState() {
  const editor = state.singleImageEditor;
  if (!editor || !Number.isInteger(editor.imageIndex) || editor.imageIndex < 0) return;
  const running = state.singleImageGenerationKeys.has(`${editor.draftId || "draft"}:${editor.imageIndex}`);
  editor.generating = running;
  if (!running) {
    resetSingleImageEditorControls();
    return;
  }
  $("#singleImagePrompt")?.setAttribute("disabled", "disabled");
  $("#singleImageReferenceInput")?.setAttribute("disabled", "disabled");
  setButtonLoading($("#generateSingleImageBtn"), "生成中...");
}

// 一次单图生成结束时收尾：弹层正开着这张图→外观收回正常并换成新图；弹层是关着的→
// 把陈旧的编辑器状态清掉，别把"生成中"留给下一次打开。
function settleSingleImageEditorAfterRun(draftId, imageIndex, nextImage = "") {
  const editor = state.singleImageEditor;
  if (!editor || editor.draftId !== String(draftId || "") || editor.imageIndex !== imageIndex) return;
  if (nextImage) editor.source = nextImage;
  if (editor.open) {
    const preview = $("#singleImageEditorPreview");
    if (nextImage && preview) preview.src = nextImage;
    applySingleImageEditorState();
    return;
  }
  applySingleImageEditorState();
  state.singleImageEditor = { draftId: "", imageIndex: -1, label: "", source: "", prompt: "", references: [], generating: false, open: false };
}

function openSingleImageEditor(imageIndex) {
  const draft = syncFormToDraft() || currentDraft();
  const images = normalizeImages(currentPreviewImages(draft));
  if (!draft || imageIndex < 0 || imageIndex >= images.length) return;
  // 后台正在生成这张图也允许打开：入口不锁（用户要看/改提示词、要给别的图改）。
  // 真正防重复提交的是 submitSingleImageEdit 里的同 key 守卫。
  const label = draft.templateRenderImageLabels?.[imageIndex] || (imageIndex === 0 ? "封面" : `图 ${imageIndex + 1}`);
  const live = state.singleImageEditor;
  // 这张图还在后台生成、编辑器状态也还留着：原样复用——重开看到的和关闭那一刻完全一样
  // （提示词是刚提交的那条、输入框锁着、按钮是"生成中..."）。不重建、不重置控件。
  const resuming = Boolean(live && live.generating
    && live.draftId === (draft.id || "") && live.imageIndex === imageIndex);
  if (!resuming) {
    state.singleImageEditor = {
      draftId: draft.id || "",
      imageIndex,
      label,
      source: images[imageIndex],
      prompt: singleImageDefaultPrompt(draft, imageIndex, label),
      references: [],
      generating: false,
      open: false,
    };
  }
  const editor = state.singleImageEditor;
  editor.open = true;
  $("#singleImageEditorTitle").textContent = `编辑${editor.label || label}`;
  $("#singleImageEditorPreview").src = editor.source || images[imageIndex];
  $("#singleImageEditorPreview").alt = `${editor.label || label}当前图片`;
  $("#singleImagePrompt").value = editor.prompt;
  // 按真实运行态刷外观：这张图在生成中就锁输入、按钮显示"生成中..."，否则回到正常态。
  applySingleImageEditorState();
  renderSingleImageReferences();
  showModal($("#singleImageEditorModal"));
  window.requestAnimationFrame(() => $("#singleImagePrompt")?.focus());
}

function closeSingleImageEditor(options = {}) {
  const editor = state.singleImageEditor;
  const continuesInBackground = Boolean(editor?.generating);
  hideModal($("#singleImageEditorModal"), () => {
    if (state.singleImageEditor !== editor) return;
    editor.open = false;
    // 后台还在生成：整份编辑器状态和"生成中"的控件状态都留着，重开时原样接着用。
    if (continuesInBackground) return;
    $("#singleImageEditorPreview")?.removeAttribute("src");
    if ($("#singleImageReferenceInput")) $("#singleImageReferenceInput").value = "";
    resetSingleImageEditorControls();
    state.singleImageEditor = { draftId: "", imageIndex: -1, label: "", source: "", prompt: "", references: [], generating: false, open: false };
  });
  if (continuesInBackground && options.notify !== false) toast(`${editor.label || "图片"}已转入后台生成，完成后会自动替换`);
}

async function appendSingleImageReferences(files) {
  const imageFiles = Array.from(files || []).filter((file) => file.type?.startsWith("image/"));
  if (!imageFiles.length) return;
  const references = state.singleImageEditor.references || [];
  const available = Math.max(0, MAX_IMAGE_INPUTS - 1 - references.length);
  if (!available) return toast(`当前图片和参考图合计最多 ${MAX_IMAGE_INPUTS} 张`);
  const accepted = imageFiles.slice(0, available);
  const additions = await Promise.all(accepted.map(async (file) => ({ name: file.name, src: await readCompressedImageAsDataUrl(file) })));
  state.singleImageEditor.references = [...references, ...additions];
  renderSingleImageReferences();
  const skipped = imageFiles.length - accepted.length;
  toast(`已添加 ${accepted.length} 张参考图${skipped ? `，另有 ${skipped} 张超过上限` : ""}`);
}

function removeSingleImageReference(index) {
  const references = state.singleImageEditor.references || [];
  if (index < 0 || index >= references.length || state.singleImageEditor.generating) return;
  state.singleImageEditor.references = references.filter((_, itemIndex) => itemIndex !== index);
  renderSingleImageReferences();
}

async function submitSingleImageEdit(event) {
  event?.preventDefault();
  const editor = state.singleImageEditor;
  const draft = state.drafts.find((item) => item.id === editor?.draftId) || currentDraft();
  const prompt = $("#singleImagePrompt")?.value || "";
  if (!draft || editor?.imageIndex < 0) return;
  if (!prompt.trim()) {
    toast("请先填写图片提示词", { kind: "error" });
    $("#singleImagePrompt")?.focus();
    return;
  }
  if (!guardVisualCopyReview(draft)) return;
  if (!ensureAiConfigured("image")) return;
  // 账号维度：别的草稿在跑 AI 任务时点名拒绝（按钮不置灰，点了才知道为什么）。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  if (draftGenerationBusy(draft.id, "image")) {
    toast("这份笔记正在整组生成，请完成后再生成单张图片");
    return;
  }
  const key = `${draft.id || "draft"}:${editor.imageIndex}`;
  if (state.singleImageGenerationKeys.has(key)) return toast("这张图片正在后台生成");
  editor.prompt = prompt;
  editor.generating = true;
  state.singleImageGenerationKeys.add(key);
  // 参考图必须在关弹层之前取出来：closeSingleImageEditor 会把编辑器状态清空。
  const referenceImages = editor.references.map((item) => item.src);
  const generationRun = beginDraftGenerationRun(draft.id, "image");
  let nextImage = "";
  try {
    // 登记与 try 之间不留可抛错语句：这段窗口里抛一次，计数就永远减不回去，
    // 这份草稿的主按钮会被永久置灰。
    // 先把弹层切到"生成中"：关掉时这套控件状态会留着（closeSingleImageEditor 在后台
    // 继续跑时不清），用户重开看到的还是同一个"生成中"的弹层。
    applySingleImageEditorState();
    // 用户裁决（2026-09-22）：点「生成并替换」立刻关掉弹层——原来要留着弹层等生成完，
    // 用户被锁在弹层里干等。现在直接转后台：卡片显示「后台生成中」，完成后自动替换。
    renderEditor();
    closeSingleImageEditor();
    nextImage = await generateSingleDraftImageWithPrompt(draft, editor.imageIndex, editor.prompt, referenceImages);
    toast(`${editor.label}已按提示词生成并替换`);
  } catch (error) {
    if (!isAbortError(error)) toast(friendlyImageError(error), { kind: "error", duration: 6500 });
  } finally {
    editor.generating = false;
    state.singleImageGenerationKeys.delete(key);
    endDraftGenerationRun(generationRun);
    settleSingleImageEditorAfterRun(draft.id, editor.imageIndex, nextImage);
    renderEditor();
  }
}

function templateImagesForDraft(draft) {
  const baseImages = [...(draft?.images || [])];
  const fields = draft?.templateFields;
  if (!fields || fields.type === "toc-page" || fields.type === "poster-auto" || !Array.isArray(fields.pages)) {
    return baseImages;
  }
  fields.pages.forEach((page, index) => {
    if (page?.imageSrc) {
      baseImages[index] = page.imageSrc;
    }
  });
  return baseImages.filter(Boolean);
}

// 上限常量只有一份（client.js）。话题会被扩展追加进小红书的正文编辑器，所以正文长度把话题算进去，
// 站内的判据才和发布页一致。
function xhsPublishLimitChecks(draft) {
  const titleLength = String(draft?.title || "").trim().length;
  const tags = String(draft?.tags || "").trim();
  const bodyLength = String(draft?.body || "").trim().length + tags.length;
  return {
    title: { length: titleLength, max: XHS_TITLE_MAX_LENGTH, over: titleLength > XHS_TITLE_MAX_LENGTH },
    body: { length: bodyLength, max: XHS_BODY_MAX_LENGTH, over: bodyLength > XHS_BODY_MAX_LENGTH },
  };
}

function xhsPublishLimitMessages(draft) {
  const checks = xhsPublishLimitChecks(draft);
  return [
    checks.title.over ? `标题 ${checks.title.length} 字，超过小红书 ${checks.title.max} 字上限` : "",
    checks.body.over ? `正文 ${checks.body.length} 字（含话题），超过小红书 ${checks.body.max} 字上限` : "",
  ].filter(Boolean);
}

function updateEditorStats(draft = currentDraft()) {
  if (!draft) return;
  const limits = xhsPublishLimitChecks(draft);
  const titleMode = Boolean(draft.promptMode?.title);
  const bodyMode = Boolean(draft.promptMode?.body);
  const titleCount = $("#titleCount");
  const bodyCount = $("#bodyCount");
  // 计数标签写成「当前/上限」：只报个数时用户看不到还剩多少空间。上限读展示口径。
  titleCount.textContent = titleMode ? "标题提示词" : `${limits.title.length}/${XHS_DISPLAY_TITLE_LIMIT} 字`;
  bodyCount.textContent = bodyMode ? "正文提示词" : `${limits.body.length}/${XHS_DISPLAY_BODY_LIMIT} 字`;
  // 超限要在用户还能改的地方就说清楚，而不是等到小红书发布页被拒。
  titleCount.classList.toggle("is-over-limit", !titleMode && limits.title.over);
  bodyCount.classList.toggle("is-over-limit", !bodyMode && limits.body.over);
  titleCount.title = !titleMode && limits.title.over ? `超过小红书 ${limits.title.max} 字上限` : "";
  bodyCount.title = !bodyMode && limits.body.over ? `超过小红书 ${limits.body.max} 字上限（含话题）` : "";
  // 话题上限必须露出来：超过的部分在 AI 生成/链接导入时会被静默截掉，编辑区过去只报
  // 个数、不提上限，用户只能自己发现标签莫名少了（P141）。展示数与截断数现在按产品口径
  // 分开了（展示 10 / 真正保留 8），所以两句都写清楚：标签印 10，标红与截断仍读 8。
  const tagCount = (draft.tags.match(/#[^\s#]+/g) || []).length;
  const tagNode = $("#tagCount");
  tagNode.textContent = `${tagCount}/${XHS_DISPLAY_TAG_LIMIT} 个`;
  tagNode.classList.toggle("is-over-limit", tagCount > XHS_TAG_MAX_LENGTH);
  tagNode.title = tagCount > XHS_TAG_MAX_LENGTH
    ? `超过小红书 ${XHS_TAG_MAX_LENGTH} 个话题上限，超出的部分不会被采用`
    : `最多 ${XHS_DISPLAY_TAG_LIMIT} 个话题`;
  const generatedCount = currentPreviewImages(draft).length;
  const referenceCount = draft.executionPlan ? draftPlanReferenceImages(draft).length : 0;
  // 图片同样报「当前/上限」：18 是小清单篇的图片上限，跟上传那道的硬闸是同一个数。
  const baseCount = referenceCount && !generatedCount
    ? `0/${XHS_DISPLAY_IMAGE_LIMIT} 张成图 · ${referenceCount} 张参考`
    : `${generatedCount}/${XHS_DISPLAY_IMAGE_LIMIT} 张`;
  const regenerationCounts = draftImageRegenerationCounts(draft);
  const activity = [
    regenerationCounts.active ? `${regenerationCounts.active} 张生成中` : "",
    regenerationCounts.queued ? `${regenerationCounts.queued} 张排队中` : "",
  ].filter(Boolean).join(" · ");
  $("#imageCountPill").textContent = activity ? `${baseCount} · ${activity}` : baseCount;
}

function resizeRewriteContentField(field) {
  if (!field || !["draftTitle", "draftBody"].includes(field.id)) return;
  // 值没变就别重设高度：下面那两步是"先压成 0 高再量"，会强制同步重排，并让滚动容器
  // 的内容瞬态变矮（生成期间正文一个字符都没变，纯粹是白抖一次）。
  if (field.dataset.autosizeValue === field.value) return;
  field.dataset.autosizeValue = field.value;
  const minimumHeight = field.id === "draftTitle" ? 38 : 48;
  field.style.height = "0px";
  field.style.height = `${Math.max(minimumHeight, field.scrollHeight)}px`;
}

function resizeRewriteContentFields() {
  [$("#draftTitle"), $("#draftBody")].forEach(resizeRewriteContentField);
}

function renderPreview(draft) {
  const images = $("#previewImages");
  const dots = $("#previewDots");
  if (!draft) {
    images.innerHTML = "";
    dots.innerHTML = "";
    state.previewImageIndex = 0;
    state.previewImageCount = 0;
    applyPreviewImageIndex();
    $("#previewTitle").textContent = "标题预览";
    $("#previewBody").textContent = "正文会在这里实时预览。";
    $("#previewTags").textContent = "#标签";
    $("#previewAuthor").textContent = "薯作台";
    $("#previewTopic").textContent = "🔥 热点｜小红书笔记预览";
    return;
  }
  draft.images = normalizeImages(draft.images);
  const previewSources = currentPreviewImages(draft);
  const plannedReferences = draft.executionPlan ? draftPlanReferenceImages(draft) : [];
  const displaySources = previewSources.length ? previewSources : plannedReferences.length ? plannedReferences : sampleImages.slice(0, 1);
  state.previewImageCount = displaySources.length;
  state.previewImageIndex = Math.min(state.previewImageIndex, Math.max(0, displaySources.length - 1));
  const imageMarkup = displaySources
    .map((src) => `<img src="${escapeHtml(src)}" alt="预览图片" />`)
    .join("");
  replaceMarkupPreservingImages(images, imageMarkup);
  dots.innerHTML = displaySources.length > 1 ? displaySources.map((_, index) => `<i class="${index === state.previewImageIndex ? "active" : ""}"></i>`).join("") : "";
  applyPreviewImageIndex();
  const previewTitle = draft.promptMode?.title
    ? (draft.viralBrief?.topic || draft.executionPlan?.topicPackage?.topic || "等待生成标题")
    : (draft.title || "未命名标题");
  const previewBody = draft.promptMode?.body
    ? "正文将在生成后显示。"
    : (draft.body || "正文会在这里实时预览。");
  $("#previewTitle").textContent = previewTitle;
  $("#previewBody").textContent = previewBody;
  $("#previewTags").textContent = draft.tags || "#标签";
  $("#previewAuthor").textContent = draft.author || "薯作台";
  $("#previewTopic").textContent = `🔥 热点｜${previewTitle.slice(0, 22)}`;
}

function friendlyAiError(error, fallback = "AI 服务暂时不可用，请检查配置和网络后重试") {
  const message = String(error?.message || "").trim();
  if (!message) return fallback;
  if (/上游暂时不可达|网络连接失败|网络策略拒绝|Failed to fetch|NetworkError|fetch failed|超时/i.test(message)) return fallback;
  if (/401|invalid token|invalid api key|unauthorized|鉴权失败|密钥.*(?:无效|失效)/i.test(message)) return "文本 AI 密钥无效或已失效，请在 AI 配置中心更换 API Key 后重试";
  if (/401|403|api key|apikey|未配置|配置缺失/i.test(message)) return "AI 配置不可用，请检查 API 地址、密钥和模型后重试";
  return message.length > 120 ? `${message.slice(0, 120)}…` : message;
}

function friendlyImageError(error, mode = "") {
  const message = String(error?.message || "").trim();
  if (/安全政策|安全策略|安全审核|无法用于生成图像|不适合进行图像生成|content policy|safety|moderation/i.test(message)) {
    if (mode === "replace_person") {
      return "本次人物替换被生图服务的安全策略拦截。请勿进行真人换脸或使用未成年人参考图；可改为描述虚构成年人物后重试。";
    }
    return "本次请求被生图服务的安全策略拦截。请删减敏感内容或更换参考图后重试。";
  }
  // 后端给了机器码时，话术已经在 core/backend-errors.js 收口过一次（额度不足那条带着
  // required/available 的分档文案）。这里再按"文案里有没有余额/额度"去正则一遍，会把那份
  // 分档话术**打回**下面这句旧文案——而旧文案正是 C25 判为"更困惑"的写法（把会员已到期和
  // 积分 余额不足挤成一句，在从未开通过会员的账号上尤其误导）。所以只在没有机器码时兜底。
  if (!String(error?.errorCode || "").trim() && /429|rate.?limit|quota|余额|额度|配额/i.test(message)) {
    return "会员已到期或积分余额不足，请先续费或充值。";
  }
  // 体积类要认状态码与码值，不能只匹配报错文案：后端对超限请求体回的是
  // "Request body too large; max is N bytes"，既不含 413 也不含 payload too large，
  // 以前直接落到兜底话术，用户看到的是与体积无关的"服务不可用/连不上后端"（台账 P72）。
  if (
    error?.status === 413
    || error?.errorCode === "payload_too_large"
    || /413|payload too large|request entity too large|body too large|图片过大|体积超过/i.test(message)
  ) {
    return "参考图或本次请求体积超过服务器上限，请减少参考图张数（或改用更小的图片）后重试。";
  }
  if (/400|bad request|invalid_request|unsupported|不支持/i.test(message)) {
    return "生图接口不接受当前请求，请确认所选模型支持当前尺寸和参考图编辑。";
  }
  return friendlyAiError(error, "生图服务暂时不可用，请检查配置和网络后重试");
}

function draftFailedImageJobKeys(draft) {
  const saved = Array.isArray(draft?.imageGenerationFailedJobKeys) ? draft.imageGenerationFailedJobKeys : [];
  return saved.map((key) => String(key || "").trim()).filter(Boolean);
}

function draftFailedImageJobs(draft, jobs = draftPromptImageJobs(draft) || []) {
  // Pages from reused templates share labels, so failures are tracked per page key; the
  // label list is only a fallback for drafts saved before keys were recorded.
  const savedKeys = draftFailedImageJobKeys(draft);
  if (savedKeys.length) {
    const wantedKeys = new Set(savedKeys);
    const matched = jobs.filter((job) => wantedKeys.has(draftImageJobIdentity(job)));
    if (matched.length) return matched;
  }
  const validLabels = new Set(jobs.map((job) => job.label));
  const savedLabels = Array.isArray(draft?.imageGenerationFailedLabels)
    ? draft.imageGenerationFailedLabels.filter((label) => validLabels.has(label))
    : [];
  if (savedLabels.length) {
    const wantedLabels = new Set(savedLabels);
    return jobs.filter((job) => wantedLabels.has(job.label));
  }
  if (draft?.templateRenderMode !== "ai-error") return [];
  const { slotByJobKey } = draftImageSlots(draft, jobs);
  return jobs.filter((job) => !slotByJobKey.has(draftImageJobIdentity(job)));
}

function imageGenerationFailureMessage(failedLabels, completedCount, error = null) {
  const labels = [...new Set((failedLabels || []).filter(Boolean))];
  const failedText = labels.length ? labels.join("、") : "图片";
  const reason = error?.imageTimedOut || isImagePageTimeoutError(error) ? "生成超时" : "生成失败";
  return `${failedText}${reason}，其他 ${Math.max(0, Number(completedCount) || 0)} 张已保留，可单独重试`;
}

function updateDraftImageFailureState(draft, failedJobs, error = null) {
  const jobs = draftPromptImageJobs(draft) || [];
  const validKeys = new Set(jobs.map((job) => draftImageJobIdentity(job)));
  const seen = new Set();
  const failed = (failedJobs || []).filter((job) => {
    const key = draftImageJobIdentity(job);
    if (!key || !validKeys.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  draft.imageGenerationFailedJobKeys = failed.map((job) => draftImageJobIdentity(job));
  draft.imageGenerationFailedLabels = [...new Set(failed.map((job) => job.label))];
  if (!failed.length) {
    draft.imageGenerationError = "";
    draft.generationFailedStage = "";
    return;
  }
  const completedCount = Math.max(0, jobs.length - failed.length);
  draft.generationFailedStage = "image";
  draft.imageGenerationError = imageGenerationFailureMessage(draft.imageGenerationFailedLabels, completedCount, error);
}

function applyPreviewImageIndex() {
  const images = $("#previewImages");
  if (!images) return;
  images.style.setProperty("--preview-index", state.previewImageIndex || 0);
  $$("#previewDots i").forEach((dot, index) => {
    dot.classList.toggle("active", index === state.previewImageIndex);
  });
}

function setPreviewImageIndex(index) {
  const max = Math.max(0, (state.previewImageCount || 1) - 1);
  state.previewImageIndex = Math.max(0, Math.min(max, index));
  applyPreviewImageIndex();
}

function stepPreviewImage(direction) {
  if ((state.previewImageCount || 0) <= 1) return;
  setPreviewImageIndex((state.previewImageIndex || 0) + direction);
}

function startPreviewImageDrag(event) {
  const rail = event.target.closest("#previewImages");
  if (!rail || (state.previewImageCount || 0) <= 1) return;
  previewDrag = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    moved: false,
  };
  rail.setPointerCapture?.(event.pointerId);
}

function movePreviewImageDrag(event) {
  if (!previewDrag || event.pointerId !== previewDrag.pointerId) return;
  const dx = event.clientX - previewDrag.startX;
  const dy = event.clientY - previewDrag.startY;
  if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
    previewDrag.moved = true;
    event.preventDefault();
  }
}

function finishPreviewImageDrag(event) {
  if (!previewDrag || event.pointerId !== previewDrag.pointerId) return;
  const rail = event.target.closest("#previewImages") || $("#previewImages");
  const dx = event.clientX - previewDrag.startX;
  const moved = previewDrag.moved;
  previewDrag = null;
  rail?.releasePointerCapture?.(event.pointerId);
  if ((state.previewImageCount || 0) <= 1) return;
  if (Math.abs(dx) > 36) {
    stepPreviewImage(dx < 0 ? 1 : -1);
    return;
  }
  if (!moved && rail) {
    const rect = rail.getBoundingClientRect();
    stepPreviewImage(event.clientX < rect.left + rect.width / 2 ? -1 : 1);
  }
}

// ----- js/image/studio.js -----
function coverImageStudioTemplates() {
  return (state.viralLibraries.cover || []).map((item) => {
    const referenceImages = [...new Set((item.referenceImages || item.aiImage?.referenceImages || []).filter(Boolean))];
    const previewImages = (item.preview?.images || []).filter(Boolean);
    const displayImages = templateDisplayImages(referenceImages, previewImages);
    return {
      id: item.id,
      mode: "cover",
      name: String(item.name || "未命名封面模板").replace(/\s*·\s*封面$/, ""),
      desc: item.summary || `${referenceImages.length || previewImages.length || 1} 张封面参考`,
      preview: displayImages[0] || "",
      referenceImages: referenceImages.length ? referenceImages.slice(0, 2) : previewImages.slice(0, 1),
      ratio: "3:4",
      prompt: visualTemplateRolePrompt(item.aiImage, "cover"),
      templateId: item.templateId || "",
    };
  });
}

function editableSceneImageTemplates() {
  const deletedIds = new Set(state.deletedSceneImageTemplateIds || []);
  const builtIn = sceneImageTemplates
    .filter((item) => !deletedIds.has(item.id))
    .map((item) => ({ ...item, ...(state.imageTemplateOverrides[item.id] || {}) }));
  return [...builtIn, ...(state.customSceneImageTemplates || [])];
}

function imageStudioTemplates(mode = state.imageStudioMode) {
  return mode === "scene" ? editableSceneImageTemplates() : coverImageStudioTemplates();
}

function renderImageStudioModeCounts() {
  const coverCount = coverImageStudioTemplates().length;
  const sceneCount = editableSceneImageTemplates().length;
  const count = $("#imageTemplateCount");
  if (count) count.textContent = `封面模板 ${coverCount} 个 · 真实场景 ${sceneCount} 个`;
  $$('[data-image-studio-mode-count]').forEach((node) => {
    const mode = node.dataset.imageStudioModeCount;
    node.textContent = `${mode === "scene" ? sceneCount : coverCount} 个可用`;
  });
}

function activeImageStudioTemplate() {
  return [...coverImageStudioTemplates(), ...editableSceneImageTemplates()]
    .find((item) => item.id === state.selectedImageTemplateId) || null;
}

function renderImageStudioSelection() {
  const mount = $("#imageTemplateSelection");
  if (!mount) return;
  const template = activeImageStudioTemplate();
  if (!template) {
    mount.className = "image-template-selection hidden";
    mount.innerHTML = "";
    renderImageGenerationMode();
    return;
  }
  const templatePreview = template.preview || template.referenceImages?.[0] || "";
  const templateReferenceNumber = imageReferenceNumber("template", 0, template);
  mount.className = "image-template-selection selected";
  mount.innerHTML = `
    ${templatePreview ? `<span class="image-template-reference-preview">
      <img src="${escapeHtml(templatePreview)}" alt="${escapeHtml(template.name)}" />
      ${templateReferenceNumber ? `<b>图${templateReferenceNumber}</b>` : ""}
    </span>` : ""}
    <div>
      <span>${template.mode === "scene" ? "真实场景" : "封面库同步"}</span>
      <strong>${escapeHtml(template.name)}</strong>
      <small>${escapeHtml(template.desc)}</small>
    </div>
    <button type="button" class="mini-btn" data-clear-image-template aria-label="取消使用当前模板" title="取消模板">×</button>`;
  renderImageGenerationMode();
}

function renderImageReferencePreview() {
  const mount = $("#imageReferencePreview");
  if (!mount) return;
  const references = state.imageReferences || [];
  mount.classList.toggle("hidden", !references.length);
  mount.innerHTML = references.length
    ? referencePreviewMarkup(references, "image", "补充参考图", "用于补充主体、风格或细节")
    : "";
}

function renderReplacementReferencePreview() {
  const mount = $("#replacementReferencePreview");
  if (!mount) return;
  const references = state.replacementReferences || [];
  mount.classList.toggle("hidden", !references.length);
  mount.innerHTML = references.length
    ? referencePreviewMarkup(references, "replacement", "主体参考图", "共同定义新主体")
    : "";
}

// 主体参考图只属于"这一次替换"：换模式、换模板、换笔记之后它不再是当前任务的输入。
// 只增不减会让守卫以为用户已经给出了新主体，于是不填替换描述也能过，脚本还会拿旧图
// 当新主体（P69）。清空必须发生在切换动作里，而不是等生成前再判断。
function clearReplacementReferences() {
  if (!(state.replacementReferences || []).length) return false;
  state.replacementReferences = [];
  renderReplacementReferencePreview();
  return true;
}

function referencePreviewMarkup(references, kind, label, description) {
  const removeAttribute = kind === "replacement" ? "data-remove-replacement-reference" : "data-remove-image-reference";
  return `
    <div class="reference-preview-summary">
      <strong>${escapeHtml(label)} · ${references.length} 张</strong>
      <span>${escapeHtml(description)}</span>
    </div>
    <div class="reference-preview-strip">
      ${references.map((reference, index) => `
        <span class="reference-preview-item" title="图${imageReferenceNumber(kind, index) || index + 1} · ${escapeHtml(reference.name || label)}">
          <img src="${escapeHtml(reference.src)}" alt="${escapeHtml(reference.name || `${label} ${index + 1}`)}" />
          <b class="reference-preview-index">图${imageReferenceNumber(kind, index) || index + 1}</b>
          <button type="button" ${removeAttribute}="${index}" aria-label="移除${escapeHtml(reference.name || `${label} ${index + 1}`)}" title="移除">×</button>
        </span>`).join("")}
    </div>`;
}

function renderImageGenerationMode({ syncIndicator = true } = {}) {
  const template = activeImageStudioTemplate();
  const isScene = template?.mode === "scene";
  const mode = isScene ? state.imageGenerationMode : "create";
  const modeMount = $("#imageGenerationMode");
  const createFields = $("#imageCreateFields");
  const replacementFields = $("#imageReplacementFields");
  if (!modeMount || !createFields || !replacementFields) return;
  modeMount.classList.toggle("hidden", !isScene);
  $$('[data-image-generation-mode]').forEach((button) => {
    const active = button.dataset.imageGenerationMode === mode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  const replacing = isScene && mode !== "create";
  createFields.classList.toggle("hidden", replacing);
  replacementFields.classList.toggle("hidden", !replacing);
  const contentLabel = $("#imageContentLabel");
  if (contentLabel) contentLabel.textContent = isScene ? "画面内容" : "正文 / 画面内容";
  const contentInput = $("#imageContentInput");
  if (contentInput) {
    contentInput.placeholder = isScene
      ? "描述主体、场景、动作和氛围，不需要填写正文"
      : "写清楚这张图要表达的内容、主体、场景或必须出现的信息";
  }
  const personMode = mode === "replace_person";
  const targetLabel = $("#replacementTargetLabel");
  const targetInput = $("#replacementTargetInput");
  const descriptionLabel = $("#replacementDescriptionLabel");
  const descriptionInput = $("#replacementDescriptionInput");
  if (targetLabel) targetLabel.textContent = personMode ? "要替换的人物" : "要替换的物品";
  if (targetInput) targetInput.placeholder = personMode ? "例如：画面左侧穿白衣的人" : "例如：桌面中央的饮料瓶";
  if (descriptionLabel) descriptionLabel.textContent = personMode ? "替换人物要求" : "替换成";
  if (descriptionInput) {
    descriptionInput.placeholder = personMode
      ? "描述新人物的外观与服装，或上传人物参考图"
      : "描述新物品的颜色、材质和角度，或上传商品参考图";
  }
  $$("[data-preserve-object]").forEach((node) => node.classList.toggle("hidden", personMode));
  $$("[data-preserve-person]").forEach((node) => node.classList.toggle("hidden", !personMode));
  renderImageReferencePreview();
  renderReplacementReferencePreview();
  if (syncIndicator) syncSegmentedIndicator(modeMount.querySelector(".segmented"), false);
  // 模式、模板、参考图的每一次变化都会经过这里：生成按钮的可用性跟着现场条件走（P69）。
  syncGenerateImageButtonState();
}

function setImageGenerationMode(mode) {
  if (imageGenerationController) return;
  if (!["create", "replace_object", "replace_person"].includes(mode)) return;
  const changed = state.imageGenerationMode !== mode;
  state.imageGenerationMode = mode;
  if (changed) clearReplacementReferences();
  renderImageGenerationMode({ syncIndicator: !changed });
  if (changed) {
    const activeFields = mode === "create" ? $("#imageCreateFields") : $("#imageReplacementFields");
    playUiMotion(activeFields, "ui-view-enter");
    syncSegmentedIndicator($("#imageGenerationMode .segmented"), true);
  }
}

function renderImageStudioManageAction(templates) {
  const mount = $("#imageTemplateManageControls");
  if (!mount) return;
  const mode = state.imageStudioMode;
  const editing = Boolean(state.imageTemplateEditing[mode]);
  const currentImport = visualTemplateImportForKind(mode);
  if (!editing) {
    mount.innerHTML = `<div class="image-template-manage-actions"><button type="button" class="mini-btn ${currentImport ? "danger-btn is-cancel-action" : ""}" data-add-image-template="${mode}" ${currentImport ? 'aria-busy="true"' : ""}>${currentImport ? "停止上传" : "新增"}</button><button type="button" class="mini-btn" data-toggle-image-template-edit="${mode}" ${currentImport ? "disabled" : ""}>编辑</button></div>`;
    return;
  }
  const templateIds = templates.map((item) => item.id).filter(Boolean);
  const selectedIds = state.imageTemplateDeleteSelections[mode] || [];
  const allSelected = templateIds.length > 0 && templateIds.every((templateId) => selectedIds.includes(templateId));
  const importPending = Boolean(state.visualTemplateImport);
  mount.innerHTML = `
    <div class="visual-library-edit-toolbar image-template-edit-toolbar" role="toolbar" aria-label="${mode === "scene" ? "真实场景" : "封面"}模板批量操作">
      <span class="visual-library-selected-count">已选 ${selectedIds.length} 项</span>
      <label class="visual-library-select-all">
        <input type="checkbox" data-toggle-all-image-templates="${mode}" ${allSelected ? "checked" : ""} ${templateIds.length ? "" : "disabled"} />
        <span>全选</span>
      </label>
      <button type="button" class="mini-btn danger-btn" data-delete-selected-image-templates="${mode}" ${selectedIds.length ? "" : "disabled"}>删除</button>
      <span class="visual-library-edit-final-actions">
        <button type="button" class="mini-btn ${currentImport ? "danger-btn is-cancel-action" : ""}" data-add-image-template="${mode}" ${importPending && !currentImport ? "disabled" : ""}>${currentImport ? "停止上传" : "新增模板"}</button>
        <button type="button" class="mini-btn library-edit-done-btn" data-toggle-image-template-edit="${mode}">完成</button>
      </span>
    </div>`;
}

function toggleImageTemplateEdit(mode = state.imageStudioMode) {
  if (!["cover", "scene"].includes(mode)) return;
  state.imageTemplateEditing[mode] = !state.imageTemplateEditing[mode];
  state.imageTemplateDeleteSelections[mode] = [];
  renderStyleGrid();
}

function setImageTemplateDeleteSelection(mode, templateId, selected) {
  if (!state.imageTemplateEditing[mode] || !templateId) return;
  const selections = new Set(state.imageTemplateDeleteSelections[mode] || []);
  if (selected === undefined ? !selections.has(templateId) : selected) selections.add(templateId);
  else selections.delete(templateId);
  state.imageTemplateDeleteSelections[mode] = [...selections];
  renderStyleGrid();
}

function setAllImageTemplateDeleteSelections(mode, checked) {
  if (!state.imageTemplateEditing[mode]) return;
  state.imageTemplateDeleteSelections[mode] = checked ? imageStudioTemplates(mode).map((item) => item.id).filter(Boolean) : [];
  renderStyleGrid();
}

function openImageTemplateAddFlow(mode) {
  if (mode === "cover") openVisualTemplateFilePicker("cover");
  else if (mode === "scene") openSceneImageTemplateFilePicker();
}

function renderImageStudioAddCard(mode) {
  const pending = visualTemplateImportForKind(mode);
  if (pending) {
    return `
      <button type="button" class="image-template-card image-template-add-card is-loading is-cancel-action" data-add-image-template="${mode}" aria-busy="true" title="停止当前模板上传">
        <span class="image-template-visual"><span class="template-import-spinner" aria-hidden="true"></span></span>
        <span class="image-template-copy template-import-copy">
          <strong>停止上传</strong>
          <small class="template-import-file" title="${escapeHtml(pending.fileName)}">${escapeHtml(pending.stage)} · ${escapeHtml(pending.fileName)}</small>
        </span>
      </button>`;
  }
  return `
    <button type="button" class="image-template-card image-template-add-card" data-add-image-template="${mode}">
      <span class="image-template-visual"><span class="image-template-add-icon" aria-hidden="true">+</span></span>
      <span class="image-template-copy"><strong>${mode === "scene" ? "上传场景模板" : "新增封面模板"}</strong><small>${mode === "scene" ? "上传图片后自动分析并保存" : "导入图片并分析成封面模板"}</small></span>
    </button>`;
}

function renderStyleGrid({ syncIndicator = true } = {}) {
  const grid = $("#styleGrid");
  if (!grid) return;
  const templates = imageStudioTemplates();
  renderImageStudioModeCounts();
  const editing = Boolean(state.imageTemplateEditing[state.imageStudioMode]);
  if (editing) {
    const availableIds = new Set(templates.map((item) => item.id));
    state.imageTemplateDeleteSelections[state.imageStudioMode] = (state.imageTemplateDeleteSelections[state.imageStudioMode] || [])
      .filter((templateId) => availableIds.has(templateId));
  }
  renderImageStudioManageAction(templates);
  $$("[data-image-studio-mode]").forEach((button) => {
    const active = button.dataset.imageStudioMode === state.imageStudioMode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  if (syncIndicator) syncSegmentedIndicator($(".image-template-tabs"), false);
  const pendingImport = visualTemplateImportForKind(state.imageStudioMode);
  if (!templates.length && !editing && !pendingImport) {
    grid.innerHTML = `<div class="empty-state compact"><strong>${state.imageStudioMode === "scene" ? "暂无真实场景模板" : "封面库暂无模板"}</strong><span>${state.imageStudioMode === "scene" ? "点击新增上传图片，系统会自动分析并保存。" : "点击新增导入封面模板，或从原创笔记的封面库同步。"}</span></div>`;
    return;
  }
  const addCard = editing || pendingImport ? renderImageStudioAddCard(state.imageStudioMode) : "";
  grid.innerHTML = addCard + templates
    .map((item) => {
      const active = item.id === state.selectedImageTemplateId;
      const itemAttributes = editing
        ? `data-toggle-image-template-selection="${escapeHtml(item.id)}" role="group"`
        : `data-image-template-id="${escapeHtml(item.id)}" aria-pressed="${active}"`;
      return `
      <${editing ? "article" : "button"} type="button" class="image-template-card ${editing ? "is-editing" : ""} ${(state.imageTemplateDeleteSelections[state.imageStudioMode] || []).includes(item.id) ? "is-batch-selected" : ""} ${active ? "active" : ""}" ${itemAttributes}>
        <span class="image-template-visual">
          ${item.preview ? `<img src="${escapeHtml(item.preview)}" alt="${escapeHtml(item.name)}" />` : `<span class="image-template-no-preview">暂无预览</span>`}
          <em>${item.mode === "scene" ? "真实场景" : "封面模板"}</em>
          ${active && !editing ? `<span class="image-template-selected-mark" aria-hidden="true"><i data-lucide="check"></i></span>` : ""}
          ${editing ? `<span class="image-template-card-manage-controls">
            <label class="image-template-check"><input type="checkbox" data-image-template-checkbox="${escapeHtml(item.id)}" ${(state.imageTemplateDeleteSelections[state.imageStudioMode] || []).includes(item.id) ? "checked" : ""} /><span>选择</span></label>
            <span class="image-template-card-actions">
              <button type="button" class="mini-btn" data-edit-image-template="${escapeHtml(item.id)}">编辑</button>
              <button type="button" class="mini-btn danger-btn" data-delete-image-template="${escapeHtml(item.id)}">删除</button>
            </span>
          </span>` : ""}
        </span>
        <span class="image-template-copy">
          <strong>${escapeHtml(item.name)}</strong>
          <small>${escapeHtml(item.desc)}</small>
        </span>
      </${editing ? "article" : "button"}>`;
    })
    .join("");
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function setImageStudioMode(mode) {
  if (imageGenerationController) return;
  if (!["cover", "scene"].includes(mode) || state.imageStudioMode === mode) return;
  state.imageStudioMode = mode;
  renderStyleGrid({ syncIndicator: false });
  playUiMotion($("#styleGrid"), "ui-content-enter");
  syncSegmentedIndicator($(".image-template-tabs"), true);
}

function selectImageStudioTemplate(templateId) {
  if (imageGenerationController) return;
  const template = [...coverImageStudioTemplates(), ...editableSceneImageTemplates()].find((item) => item.id === templateId);
  if (!template) return;
  // 再点一次已选中的模板 = 取消选择：模板置空，回到「自由创作」。
  // 之前只能「换」不能「退」，想不用模板得去右上角点 ×，很容易漏掉。
  if (String(state.selectedImageTemplateId || "") === String(template.id || "")) {
    clearImageStudioTemplate();
    toast("已取消模板，回到自由创作");
    return;
  }
  clearReplacementReferences();
  state.selectedImageTemplateId = template.id;
  state.imageStudioMode = template.mode;
  state.selectedStyle = template.id;
  $("#promptInput").value = template.prompt || "";
  if (template.ratio) $("#ratioSelect").value = template.ratio;
  renderStyleGrid();
  renderImageStudioSelection();
  toast(`已套用「${template.name}」`);
}

function clearImageStudioTemplate() {
  if (imageGenerationController) return;
  clearReplacementReferences();
  state.selectedImageTemplateId = "";
  state.selectedStyle = "";
  $("#promptInput").value = "";
  renderStyleGrid();
  renderImageStudioSelection();
}

function imageInputEntries(template = activeImageStudioTemplate(), generationMode = template?.mode === "scene" ? state.imageGenerationMode : "create") {
  const templateEntries = (template?.referenceImages || []).map((src, sourceIndex) => ({
    kind: "template",
    sourceIndex,
    src,
    name: `${template?.name || "当前模板"}参考图${sourceIndex + 1}`,
    sourceLabel: "模板参考",
  }));
  const userEntries = (state.imageReferences || []).map((item, sourceIndex) => ({
    ...item,
    kind: "image",
    sourceIndex,
    sourceLabel: "补充参考",
  }));
  const replacementEntries = (state.replacementReferences || []).map((item, sourceIndex) => ({
    ...item,
    kind: "replacement",
    sourceIndex,
    sourceLabel: "主体参考",
  }));
  const entries = generationMode !== "create"
    ? [...templateEntries.slice(0, 1), ...replacementEntries, ...userEntries]
    : [...userEntries, ...templateEntries];
  return entries.filter((entry) => entry.src).map((entry, index) => ({ ...entry, number: index + 1 }));
}

function imageInputCandidates(template = activeImageStudioTemplate(), generationMode = template?.mode === "scene" ? state.imageGenerationMode : "create") {
  return imageInputEntries(template, generationMode).map((entry) => entry.src);
}

function imageReferenceNumber(kind, sourceIndex, template = activeImageStudioTemplate()) {
  return imageInputEntries(template).find((entry) => entry.kind === kind && entry.sourceIndex === sourceIndex)?.number || 0;
}

function findImageReferenceMention(value, caret) {
  const head = String(value || "").slice(0, Math.max(0, Number(caret) || 0));
  const triggerIndex = head.lastIndexOf("@");
  if (triggerIndex < 0) return null;
  const query = head.slice(triggerIndex + 1);
  if (query.length > 24 || /[@\s，。；：、,.!！?？;:]/.test(query)) return null;
  return { triggerIndex, query };
}

function applyImageReferenceMention(value, caret, triggerIndex, number) {
  const current = String(value || "");
  const safeCaret = Math.max(triggerIndex + 1, Math.min(current.length, Number(caret) || 0));
  const before = current.slice(0, triggerIndex);
  const after = current.slice(safeCaret);
  const leading = before && !/[\s（(\[【，。；：、]$/.test(before) ? " " : "";
  const trailing = after && /^[\s，。；：、,.!！?？;:）)\]】]/.test(after) ? "" : " ";
  const token = `@图${number}`;
  return {
    value: `${before}${leading}${token}${trailing}${after}`,
    caret: before.length + leading.length + token.length + trailing.length,
  };
}

function closeImageReferenceMentionMenu() {
  const menu = $("#imageReferenceMentionMenu");
  const field = imageReferenceMentionState?.field;
  if (field) {
    field.setAttribute("aria-expanded", "false");
    field.removeAttribute("aria-activedescendant");
  }
  if (menu) {
    menu.classList.add("hidden");
    menu.innerHTML = "";
  }
  imageReferenceMentionState = null;
}

function positionImageReferenceMentionMenu() {
  const menu = $("#imageReferenceMentionMenu");
  const field = imageReferenceMentionState?.field;
  if (!menu || !field || menu.classList.contains("hidden")) return;
  const rect = field.getBoundingClientRect();
  const width = Math.min(336, Math.max(236, rect.width));
  const left = Math.max(10, Math.min(window.innerWidth - width - 10, rect.left));
  const menuHeight = Math.min(menu.scrollHeight, 286);
  const below = window.innerHeight - rect.bottom;
  const top = below >= Math.min(menuHeight + 8, 180)
    ? rect.bottom + 6
    : Math.max(10, rect.top - menuHeight - 6);
  menu.style.width = `${width}px`;
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

function filteredImageReferenceMentionEntries(query = "") {
  const normalized = String(query || "").trim().toLowerCase();
  return imageInputEntries().filter((entry) => {
    if (!normalized) return true;
    return [`图${entry.number}`, String(entry.number), entry.name, entry.sourceLabel]
      .some((value) => String(value || "").toLowerCase().includes(normalized));
  });
}

function renderImageReferenceMentionMenu() {
  const menu = $("#imageReferenceMentionMenu");
  const stateEntry = imageReferenceMentionState;
  if (!menu || !stateEntry?.field) return;
  const entries = filteredImageReferenceMentionEntries(stateEntry.query);
  if (!entries.length) {
    closeImageReferenceMentionMenu();
    return;
  }
  stateEntry.entries = entries;
  stateEntry.activeIndex = Math.max(0, Math.min(stateEntry.activeIndex || 0, entries.length - 1));
  menu.innerHTML = entries.map((entry, index) => `
    <button type="button" id="image-reference-option-${entry.number}" role="option" data-insert-image-reference="${entry.number}" aria-selected="${index === stateEntry.activeIndex}">
      <img src="${escapeHtml(entry.src)}" alt="" />
      <strong>图${entry.number}</strong>
      <span>${escapeHtml(entry.sourceLabel)}${entry.name ? ` · ${escapeHtml(entry.name)}` : ""}</span>
    </button>`).join("");
  menu.classList.remove("hidden");
  stateEntry.field.setAttribute("aria-expanded", "true");
  stateEntry.field.setAttribute("aria-activedescendant", `image-reference-option-${entries[stateEntry.activeIndex].number}`);
  positionImageReferenceMentionMenu();
}

function openImageReferenceMentionMenu(field) {
  const match = findImageReferenceMention(field.value, field.selectionStart);
  if (!match || !imageInputEntries().length) {
    closeImageReferenceMentionMenu();
    return;
  }
  imageReferenceMentionState = {
    field,
    triggerIndex: match.triggerIndex,
    query: match.query,
    activeIndex: 0,
    entries: [],
  };
  renderImageReferenceMentionMenu();
}

function insertImageReferenceMention(number) {
  const stateEntry = imageReferenceMentionState;
  if (!stateEntry?.field) return;
  const field = stateEntry.field;
  const result = applyImageReferenceMention(field.value, field.selectionStart, stateEntry.triggerIndex, number);
  field.value = result.value;
  field.focus();
  field.setSelectionRange(result.caret, result.caret);
  field.dispatchEvent(new Event("input", { bubbles: true }));
  closeImageReferenceMentionMenu();
}

function bindImageReferenceMentionInputs() {
  const menu = $("#imageReferenceMentionMenu");
  $$('[data-image-reference-mention]').forEach((field) => {
    field.setAttribute("aria-haspopup", "listbox");
    field.setAttribute("aria-controls", "imageReferenceMentionMenu");
    field.setAttribute("aria-expanded", "false");
    field.addEventListener("input", () => openImageReferenceMentionMenu(field));
    field.addEventListener("keydown", (event) => {
      if (!imageReferenceMentionState || imageReferenceMentionState.field !== field) return;
      const entries = imageReferenceMentionState.entries || [];
      if (["ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        const delta = event.key === "ArrowDown" ? 1 : -1;
        imageReferenceMentionState.activeIndex = (imageReferenceMentionState.activeIndex + delta + entries.length) % entries.length;
        renderImageReferenceMentionMenu();
      } else if (["Enter", "Tab"].includes(event.key)) {
        event.preventDefault();
        insertImageReferenceMention(entries[imageReferenceMentionState.activeIndex]?.number);
      } else if (event.key === "Escape") {
        event.preventDefault();
        closeImageReferenceMentionMenu();
      }
    });
    field.addEventListener("blur", () => window.setTimeout(() => {
      if (document.activeElement?.closest?.("#imageReferenceMentionMenu")) return;
      closeImageReferenceMentionMenu();
    }, 0));
  });
  menu?.addEventListener("mousedown", (event) => {
    const option = event.target.closest("[data-insert-image-reference]");
    if (!option) return;
    event.preventDefault();
    insertImageReferenceMention(Number(option.dataset.insertImageReference));
  });
  window.addEventListener("resize", positionImageReferenceMentionMenu);
  window.addEventListener("scroll", positionImageReferenceMentionMenu, true);
}

async function appendImageReferences(files, kind) {
  const imageFiles = Array.from(files || []).filter((file) => file.type?.startsWith("image/"));
  if (!imageFiles.length) return;
  const available = Math.max(0, MAX_IMAGE_INPUTS - imageInputCandidates().length);
  if (!available) {
    toast(`单次最多使用 ${MAX_IMAGE_INPUTS} 张输入图，请先移除部分参考图`);
    return;
  }
  const accepted = imageFiles.slice(0, available);
  // 参考图按原图内嵌时，十几张 Photos 就能把整包 JSON 顶过服务端的 25MB 上限。
  const references = await Promise.all(accepted.map(async (file) => ({
    name: file.name,
    src: await readCompressedImageAsDataUrl(file),
  })));
  const key = kind === "replacement" ? "replacementReferences" : "imageReferences";
  state[key] = [...(state[key] || []), ...references];
  renderImageStudioSelection();
  const skipped = imageFiles.length - accepted.length;
  toast(`已加入 ${accepted.length} 张参考图${skipped ? `，另有 ${skipped} 张超过单次上限` : ""}`);
}

function renderGeneratedGrid() {
  const latestSection = $("#latestGeneratedSection");
  const latestGrid = $("#latestGeneratedGrid");
  const savedGrid = $("#generatedGrid");
  const latestCount = $("#latestGeneratedCount");
  const savedCount = $("#imageLibraryCount");
  const savedBadge = $("#savedImageLibraryBadge");
  const hasLatestGeneratedImages = state.latestGeneratedImages.length > 0;
  latestSection?.classList.toggle("hidden", !hasLatestGeneratedImages);
  latestSection?.setAttribute("aria-hidden", String(!hasLatestGeneratedImages));
  if (latestCount) latestCount.textContent = `${state.latestGeneratedImages.length} 张图片`;
  if (savedCount) savedCount.textContent = `${state.generatedImages.length} 张图片`;
  if (savedBadge) savedBadge.textContent = state.generatedImages.length;

  const cards = (items, saved) => items.map((item, index) => {
    const alreadySaved = saved || state.generatedImages.some((candidate) => candidate.src === item.src);
    return `
        <article class="generated-card ${saved ? "saved-generated-card" : ""}">
          ${saved ? "" : `<button type="button" class="generated-card-remove" data-requires-capability="imageGalleryWrite" data-delete-latest-generated-id="${item.id}" aria-label="删除${escapeHtml(item.title)}" title="删除这张图片"><span aria-hidden="true">×</span></button>`}
          <button type="button" class="generated-preview" data-preview-generated-id="${item.id}" data-generated-scope="${saved ? "saved" : "latest"}" aria-label="预览${escapeHtml(item.title)}" title="放大预览">
            <img src="${escapeHtml(item.src)}" alt="${escapeHtml(item.title)}" />
          </button>
          ${saved ? `
            <div>
              ${renderWatermarkStatus(item.watermarkReport)}
              <div class="generated-card-actions has-delete has-download">
              <button type="button" class="ghost-btn" data-download-generated-id="${item.id}" data-download-generated-index="${index}" aria-label="下载${escapeHtml(item.title)}" title="下载这张图片"><i data-lucide="download" aria-hidden="true"></i><span>下载</span></button>
              <button type="button" class="primary-btn" data-open-generated-assignment="${item.id}"><i data-lucide="image-plus" aria-hidden="true"></i><span>加入任务</span></button>
              <button type="button" class="generated-delete-btn" data-delete-generated-id="${item.id}" aria-label="从已保存图片中删除${escapeHtml(item.title)}" title="删除已保存图片"><i data-lucide="trash-2" aria-hidden="true"></i></button>
              </div>
            </div>` : `
            <div>
              <strong>${escapeHtml(item.title)}</strong>
              <span>${escapeHtml(item.templateName || "自由创作")} · ${escapeHtml(item.content || item.prompt)}</span>
              ${renderWatermarkStatus(item.watermarkReport)}
              <div class="generated-card-actions has-download">
                <button type="button" class="ghost-btn" data-download-generated-id="${item.id}" data-download-generated-index="${index}" aria-label="下载${escapeHtml(item.title)}" title="下载这张图片"><i data-lucide="download" aria-hidden="true"></i><span>下载</span></button>
                <button type="button" class="ghost-btn" data-requires-capability="imageGalleryWrite" data-save-generated-id="${item.id}" ${alreadySaved ? "disabled" : ""}><i data-lucide="save" aria-hidden="true"></i><span>${alreadySaved ? "已保存" : "保存图片"}</span></button>
                <button type="button" class="primary-btn" data-open-generated-assignment="${item.id}"><i data-lucide="image-plus" aria-hidden="true"></i><span>加入任务</span></button>
              </div>
            </div>`}
        </article>
      `;
  }).join("");

  latestGrid.innerHTML = hasLatestGeneratedImages ? cards(state.latestGeneratedImages, false) : "";
  savedGrid.innerHTML = state.generatedImages.length
    ? cards(state.generatedImages, true)
    : `<div class="generated-empty"><strong>还没有保存图片</strong><span>点击本次生成图片上的“保存图片”，会独立存放到这里。</span></div>`;
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function setSavedImageLibraryOpen(open) {
  const modal = $("#savedImageLibraryModal");
  if (!modal) return;
  if (open) renderGeneratedGrid();
  if (open) showModal(modal);
  else hideModal(modal);
}

function generatedImageById(generatedId) {
  return [...state.latestGeneratedImages, ...state.generatedImages].find((item) => item.id === generatedId) || null;
}

// 下载文件名只保留中文与数字，不出现英文缩写、URL 片段或空格，避免存成乱名。
function generatedImageDownloadName(item, ordinal = 0) {
  const seq = String(ordinal + 1);
  const template = String(item?.templateName || "").trim();
  const templateClean = template && template !== "自由创作"
    ? template.replace(/[^\u4e00-\u9fa5]/g, "").slice(0, 16)
    : "";
  const parts = ["图片素材", seq];
  if (templateClean) parts.push(templateClean);
  return parts.join("-") + ".png";
}

async function downloadGeneratedImage(generatedId, ordinal = 0) {
  const item = generatedImageById(generatedId);
  if (!item) return;
  const src = String(item.src || "");
  if (!src) {
    toast("这张图片没有可下载的内容", { kind: "error" });
    return;
  }
  try {
    let blob;
    const requestPath = protectedImageRequestPath(src);
    if (requestPath) {
      // 受保护图片（cached-image / proxy-image / 模板资产）需要 Bearer 鉴权，走统一的水合通道。
      blob = await loadProtectedImageBlob(requestPath);
    } else {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`读取失败（${response.status}）`);
      blob = await response.blob();
    }
    const filename = generatedImageDownloadName(item, ordinal);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    toast(`已开始下载 ${filename}`);
    window.setTimeout(() => { link.remove(); URL.revokeObjectURL(url); }, 1000);
  } catch (error) {
    toast(`下载失败：${error?.message || "请稍后重试"}`, { kind: "error", duration: 5000 });
  }
}

function saveGeneratedImage(generatedId) {
  const generated = state.latestGeneratedImages.find((item) => item.id === generatedId);
  if (!generated) return "missing";
  if (state.generatedImages.some((item) => item.src === generated.src)) return "duplicate";
  state.generatedImages.unshift({ ...generated, savedAt: new Date().toISOString() });
  return "saved";
}

async function persistGeneratedImage(image) {
  const result = await postBackend("/api/generated-images", { action: "save", image });
  if (Array.isArray(result.generatedImages)) state.generatedImages = result.generatedImages;
  return result;
}

function addGeneratedImageToCurrentDraft(generated, role = "inner", targetDraft = null) {
  const draft = targetDraft || currentDraft();
  if (!draft || !generated?.src) return { status: "missing-draft", draft: null };
  const src = normalizeImageSrc(generated.src);
  const targetRole = role === "cover" ? "cover" : "inner";
  let existingIndex = -1;

  const usesTemplateImages = Boolean(draft.templateRenderMode) || normalizeImages(draft.templateRenderImages).length > 0;
  if (usesTemplateImages) {
    const renderedImages = normalizeImages(draft.templateRenderImages);
    const baseImages = renderedImages.length ? renderedImages : normalizeImages(draft.images);
    const existingLabels = Array.isArray(draft.templateRenderImageLabels) ? draft.templateRenderImageLabels : [];
    const existingStates = Array.isArray(draft.templateRenderImageStates) ? draft.templateRenderImageStates : [];
    const existingReports = Array.isArray(draft.templateRenderWatermarkReports) ? draft.templateRenderWatermarkReports : [];
    existingIndex = baseImages.indexOf(src);
    if (existingIndex < 0 && baseImages.length >= MAX_DRAFT_TEMPLATE_PAGES) {
      return { status: "full", draft, role: targetRole };
    }
    if ((targetRole === "cover" && existingIndex === 0) || (targetRole === "inner" && existingIndex > 0)) {
      return { status: "duplicate", draft, role: targetRole };
    }
    const entries = baseImages.map((image, index) => ({
      src: image,
      label: existingLabels[index] || (index === 0 ? "封面" : `图 ${index + 1}`),
      state: existingStates[index] || "generated",
      report: existingReports[index] || null,
    }));
    const existing = existingIndex >= 0 ? entries.splice(existingIndex, 1)[0] : null;
    const entry = {
      src,
      label: targetRole === "cover" ? "封面素材" : "内页素材",
      state: existing?.state || "generated",
      report: existing?.report || generated.watermarkReport || null,
    };
    if (targetRole === "cover") entries.unshift(entry);
    else entries.push(entry);
    entries.forEach((item, index) => {
      if (index === 0) {
        if (item.label.endsWith("素材")) item.label = "封面素材";
        else if (item.label === "内页") item.label = "封面";
        return;
      }
      if (item.label === "封面" || item.label === "封面素材") item.label = "内页素材";
    });
    draft.templateRenderImages = entries.map((item) => item.src);
    draft.templateRenderImageLabels = entries.map((item) => item.label);
    draft.templateRenderImageStates = entries.map((item) => item.state);
    draft.templateRenderWatermarkReports = entries.map((item) => item.report);
    draft.images = [...draft.templateRenderImages];
  } else {
    const images = normalizeImages(draft.images);
    existingIndex = images.indexOf(src);
    if (existingIndex < 0 && images.length >= MAX_DRAFT_TEMPLATE_PAGES) {
      return { status: "full", draft, role: targetRole };
    }
    if ((targetRole === "cover" && existingIndex === 0) || (targetRole === "inner" && existingIndex > 0)) {
      return { status: "duplicate", draft, role: targetRole };
    }
    if (existingIndex >= 0) images.splice(existingIndex, 1);
    if (targetRole === "cover") images.unshift(src);
    else images.push(src);
    draft.images = images;
  }
  draft.imageLibraryAssetIds = [...new Set([...(draft.imageLibraryAssetIds || []), generated.id].filter(Boolean))];
  return { status: existingIndex >= 0 ? "moved" : "added", draft, role: targetRole };
}

function removeGeneratedImage(generatedId) {
  const previousLength = state.generatedImages.length;
  state.generatedImages = state.generatedImages.filter((item) => item.id !== generatedId);
  return state.generatedImages.length < previousLength;
}

async function deleteSavedGeneratedImage(image) {
  const result = await postBackend("/api/generated-images", {
    action: "delete",
    id: image?.id || "",
    src: image?.src || "",
  });
  if (Array.isArray(result.generatedImages)) state.generatedImages = result.generatedImages;
  return result;
}

function removeLatestGeneratedImage(generatedId) {
  const previousLength = state.latestGeneratedImages.length;
  state.latestGeneratedImages = state.latestGeneratedImages.filter((item) => item.id !== generatedId);
  return state.latestGeneratedImages.length < previousLength;
}


// ----- js/publish/list.js -----
function publishStatusMeta(item) {
  const status = String(item?.publishStatus || "");
  if (status === "published") return { className: "published", label: "已发布到小红书" };
  if (status === "failed") return { className: "failed", label: "发布失败" };
  if (status === "opened") return { className: "opened", label: "已打开小红书发布页" };
  return { className: "", label: "待发布" };
}

// P121：一次发布是"点按钮 → 落库 → 建任务 → 导航标签页"的一段在途过程，期间同一条
// 绝不能再被点第二次（会重复建任务，还会让第二条把第一条已经打开的发布页顶掉）。
// 在途状态不能只靠按钮的 disabled：prepare 一返回 renderPublishList() 就整表重建按钮，
// 那个禁用态会被抹掉。所以它必须是独立于 DOM 的运行态，由渲染时重新算出来。
const publishAttemptsInFlight = new Set();

function publishAttemptInFlight(itemId) {
  return publishAttemptsInFlight.has(String(itemId ?? "").trim());
}

// 就地改写按钮，不整表重渲染：重渲染会把「保存表现」表单里还没提交的输入一起抹掉。
function applyPublishAttemptState(itemIds) {
  const ids = (Array.isArray(itemIds) ? itemIds : [itemIds]).map((id) => String(id ?? "").trim()).filter(Boolean);
  if (!ids.length) return;
  $$("#publishList [data-publish-xhs]").forEach((button) => {
    const id = String(button.dataset.publishXhs || "");
    if (!ids.includes(id)) return;
    if (publishAttemptInFlight(id)) {
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
    } else {
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  });
}

// 批量删除只在页面里维护勾选态：它不是要发给服务端的数据，刷新后回到"未选"。
// 切换勾选不重渲染整表——重渲染会把「保存表现」表单里还没提交的输入一起抹掉。
const publishSelectedIds = new Set();

function publishSelectedCount() {
  return state.publish.filter((item) => publishSelectedIds.has(String(item.id))).length;
}

function renderPublishBulkBar() {
  const bar = $("#publishBulkBar");
  if (!bar) return;
  const total = state.publish.length;
  const count = publishSelectedCount();
  bar.hidden = total === 0;
  const countNode = $("#publishSelectedCount");
  if (countNode) countNode.textContent = `已选 ${count} 条`;
  const deleteButton = $("#bulkDeletePublishBtn");
  if (deleteButton) {
    deleteButton.disabled = count === 0;
    deleteButton.textContent = count ? `删除所选（${count}）` : "删除所选";
  }
  const selectAll = $("#publishSelectAll");
  if (selectAll) {
    selectAll.checked = total > 0 && count === total;
    selectAll.indeterminate = count > 0 && count < total;
  }
}

function setPublishSelection(itemId, checked, itemNode) {
  const id = String(itemId ?? "").trim();
  if (!id) return;
  if (checked) publishSelectedIds.add(id);
  else publishSelectedIds.delete(id);
  itemNode?.classList.toggle("is-selected", checked);
  renderPublishBulkBar();
}

function selectAllPublish(checked) {
  publishSelectedIds.clear();
  $$("#publishList .publish-item").forEach((item) => {
    const box = item.querySelector("[data-publish-select]");
    const id = String(box?.dataset.publishSelect || "");
    if (checked && id) publishSelectedIds.add(id);
    if (box) box.checked = checked;
    item.classList.toggle("is-selected", checked && Boolean(id));
  });
  renderPublishBulkBar();
}

function renderPublishList() {
  const list = $("#publishList");
  // 条目可能已经被删掉：勾选集合里不留幽灵 id。
  const existingIds = new Set(state.publish.map((item) => String(item.id)));
  [...publishSelectedIds].forEach((id) => { if (!existingIds.has(id)) publishSelectedIds.delete(id); });
  if (!state.publish.length) {
    list.innerHTML = `<div class="empty-state"><strong>发布列表为空</strong><span>在 AI 改写页点击“加入发布列表”后会出现在这里。</span></div>`;
    renderPublishBulkBar();
    return;
  }
  list.innerHTML = state.publish
    .map(
      (item) => {
        const publishStatus = publishStatusMeta(item);
        const failed = publishStatus.className === "failed";
        const failureReason = String(item.publishError || "").trim();
        // 超限内容填不进小红书的发布页，发布按钮会直接拒绝，这里说明是哪一项。
        const limitProblems = xhsPublishLimitMessages(item);
        const selected = publishSelectedIds.has(String(item.id));
        return `
        <article class="publish-item${selected ? " is-selected" : ""}">
          <label class="publish-select" title="选择这条内容">
            <input type="checkbox" data-publish-select="${item.id}"${selected ? " checked" : ""} aria-label="选择「${escapeHtml(item.title)}」" />
          </label>
          <img src="${item.images[0] || sampleImages[0]}" alt="发布封面" />
          <div>
            <strong>${escapeHtml(item.title)}</strong>
            <p>${escapeHtml(item.body.slice(0, 72))}${item.body.length > 72 ? "..." : ""}</p>
            <span class="publish-status ${publishStatus.className}">${publishStatus.label}</span>
            ${limitProblems.length ? `<span class="publish-error" role="alert">超出小红书上限：${escapeHtml(limitProblems.join("；"))}，修改后重新加入发布列表</span>` : ""}
            ${failed ? `<span class="publish-error" role="alert">原因：${escapeHtml(failureReason || "发布扩展未回报具体原因，请在小红书发布页确认后重试。")}</span>` : ""}
          </div>
          <div class="publish-actions">
            <button type="button" class="mini-btn edit-btn" data-edit-publish="${item.id}">编辑</button>
            <button class="mini-btn"${publishAttemptInFlight(item.id) ? ' disabled aria-busy="true"' : ""} data-publish-xhs="${item.id}">发布</button>
            ${failed ? `<button type="button" class="mini-btn retry-btn" data-retry-publish="${item.id}">重试</button>` : ""}
            <button type="button" class="mini-btn danger-btn" data-delete-publish="${item.id}">删除</button>
          </div>
          ${item.viralGenerationId ? `
            <div class="performance-form" data-performance-item="${item.id}">
              <label>曝光<input type="number" min="0" data-performance-metric="impressions" placeholder="0" /></label>
              <label>作者粉丝<input type="number" min="0" data-performance-metric="followers" placeholder="0" /></label>
              <label>点赞<input type="number" min="0" data-performance-metric="likes" placeholder="0" /></label>
              <label>收藏<input type="number" min="0" data-performance-metric="collects" placeholder="0" /></label>
              <label>评论<input type="number" min="0" data-performance-metric="comments" placeholder="0" /></label>
              <label>分享<input type="number" min="0" data-performance-metric="shares" placeholder="0" /></label>
              <label>发布小时<input type="number" min="0" step="0.5" data-performance-metric="hoursSincePublish" placeholder="0" /></label>
              <button type="button" class="mini-btn" data-save-performance="${item.id}">保存表现</button>
              ${item.performanceSummary ? `<span class="performance-summary">互动率 ${(Number(item.performanceSummary.engagementRate || 0) * 100).toFixed(2)}% · 收藏/赞 ${(Number(item.performanceSummary.collectRate || 0) * 100).toFixed(1)}% · ${escapeHtml(item.performanceBenchmark?.message || "等待同赛道基线")}</span>` : ""}
            </div>` : ""}
        </article>
      `;
      }
    )
    .join("");
  renderPublishBulkBar();
}

function currentLayoutTemplate() {
  return state.layoutTemplates.find((item) => item.id === state.activeLayoutTemplateId) || state.layoutTemplates[0] || null;
}

function layoutTemplateById(templateId) {
  return state.layoutTemplates.find((item) => item.id === templateId) || null;
}

function draftLayoutTemplate(draft = currentDraft()) {
  return draft?.layoutTemplateId ? layoutTemplateById(draft.layoutTemplateId) : null;
}

function isTocTemplate(template) {
  return (template?.params?.layout?.cover || "").toLowerCase() === "toc-page";
}

function isPosterAutoTemplate(template) {
  return ["poster-auto", "poster-ai"].includes((template?.params?.layout?.cover || "").toLowerCase());
}

function templateEditablePages(template) {
  return Array.isArray(template?.params?.editablePages) ? template.params.editablePages : [];
}

function safeHex(value, fallback = "#ff2442") {
  return /^#[0-9a-fA-F]{6}$/.test(String(value || "")) ? value : fallback;
}

function ensureTemplateFields(draft, template) {
  if (!draft || !template) return null;
  const needsToc = isTocTemplate(template);
  const needsPoster = isPosterAutoTemplate(template);
  const needsLayered = isLayeredTemplate(template);
  if (needsLayered && (!draft.templateFields || draft.templateFields.type !== "layered-v1" || !Array.isArray(draft.templateFields.pages))) {
    draft.templateFields = defaultLayeredPageFields(template);
  }
  if (needsToc && (!draft.templateFields || draft.templateFields.type !== "toc-page" || !Array.isArray(draft.templateFields.items))) {
    draft.templateFields = defaultTocTemplateFields();
  }
  if (needsPoster && (!draft.templateFields || draft.templateFields.type !== "poster-auto" || !Array.isArray(draft.templateFields.pages))) {
    draft.templateFields = defaultPosterTemplateFields(template);
  }
  if (!needsLayered && !needsToc && (!draft.templateFields || draft.templateFields.type === "toc-page" || draft.templateFields.type === "layered-v1" || !Array.isArray(draft.templateFields.pages))) {
    draft.templateFields = needsPoster ? defaultPosterTemplateFields(template) : defaultGenericTemplateFields();
  }
  if ((needsPoster || needsLayered) && !draft.templateFields.themeId) {
    draft.templateFields.themeId = "original";
  }
  draft.templateFields.activePage = Number(draft.templateFields.activePage || 0);
  return draft.templateFields;
}

function activeLayeredPageFields(fields) {
  const index = Math.max(0, Number(fields?.activePage || 0));
  if (!Array.isArray(fields.pages)) fields.pages = [];
  if (!fields.pages[index]) fields.pages[index] = { content: {}, layers: {} };
  if (!fields.pages[index].content) fields.pages[index].content = {};
  if (!fields.pages[index].layers) fields.pages[index].layers = {};
  return fields.pages[index];
}

function imageViewerPageJob(draft, pageKey, label) {
  const jobs = draftPromptImageJobs(draft) || [];
  if (pageKey) {
    const keyed = jobs.find((job) => draftImageJobIdentity(job) === pageKey);
    if (keyed) return keyed;
  }
  return label ? jobs.find((job) => job.label === label) || null : null;
}

function renderImageViewer() {
  const viewer = state.imageViewer || { images: [], index: 0, title: "图片预览", zoom: 1, fit: true, draftId: "", draftImageLabels: [] };
  const images = Array.isArray(viewer.images) ? viewer.images.filter(Boolean) : [];
  if (!images.length) return;
  viewer.index = Math.min(Math.max(Number(viewer.index) || 0, 0), images.length - 1);
  viewer.zoom = Math.min(2.5, Math.max(0.5, Number(viewer.zoom) || 1));
  const title = images.length > 1 ? `${viewer.title} · ${viewer.index + 1}/${images.length}` : viewer.title;
  const image = $("#imageViewerImg");
  const imageViewerTitle = $("#imageViewerTitle");
  if (imageViewerTitle) imageViewerTitle.textContent = title;
  if (image) {
    image.src = images[viewer.index];
    image.alt = title;
    image.classList.toggle("is-fit", viewer.fit !== false);
    image.style.width = viewer.fit !== false ? "" : `${Math.round(viewer.zoom * 100)}%`;
  }
  const zoomOut = $("#imageViewerZoomOutBtn");
  const zoomIn = $("#imageViewerZoomInBtn");
  const fit = $("#imageViewerFitBtn");
  const regenerate = $("#imageViewerRegenerateBtn");
  if (zoomOut) zoomOut.disabled = viewer.fit === false && viewer.zoom <= 0.5;
  if (zoomIn) zoomIn.disabled = viewer.fit === false && viewer.zoom >= 2.5;
  if (fit) fit.classList.toggle("active", viewer.fit !== false);
  if (regenerate) {
    const draft = (state.drafts || []).find((item) => item.id === viewer.draftId) || null;
    const label = viewer.draftImageLabels?.[viewer.index] || "";
    const pageKey = String(viewer.draftImageKeys?.[viewer.index] || "");
    // Pages from reused templates share labels: resolve the previewed page by its key first.
    const job = draft ? imageViewerPageJob(draft, pageKey, label) : null;
    const canRegenerate = Boolean(job);
    const regenerationStatus = canRegenerate ? draftImageRegenerationStatus(draft, job) : "";
    regenerate.classList.toggle("hidden", !canRegenerate);
    regenerate.disabled = Boolean(regenerationStatus);
    regenerate.textContent = regenerationStatus === "active" ? "… 生成中" : regenerationStatus === "queued" ? "↻ 排队中" : "↻ 重新生成";
    regenerate.title = regenerationStatus === "active" ? "当前图片正在生成" : regenerationStatus === "queued" ? "当前图片已加入生成队列" : "重新生成当前图片";
  }
  const prev = $("#imageViewerPrevBtn");
  const next = $("#imageViewerNextBtn");
  const dots = $("#imageViewerDots");
  if (prev) prev.classList.toggle("hidden", images.length < 2);
  if (next) next.classList.toggle("hidden", images.length < 2);
  if (dots) {
    dots.innerHTML = images.length > 1
      ? images.map((src, index) => `<button type="button" class="image-viewer-dot ${index === viewer.index ? "active" : ""}" data-image-viewer-dot="${index}" aria-label="第 ${index + 1} 页"></button>`).join("")
      : "";
  }
}

function openImageViewerSet(images, index = 0, title = "图片预览", options = {}) {
  const modal = $("#imageViewerModal");
  if (!modal) return;
  state.imageViewer = {
    images: [...new Set((images || []).filter(Boolean))],
    index,
    title,
    zoom: 1,
    fit: true,
    draftId: options.draftId || "",
    draftImageLabels: Array.isArray(options.draftImageLabels) ? [...options.draftImageLabels] : [],
    draftImageKeys: Array.isArray(options.draftImageKeys) ? [...options.draftImageKeys] : [],
  };
  if (!state.imageViewer.images.length) return;
  renderImageViewer();
  showModal(modal);
}

function closeImageViewer() {
  const modal = $("#imageViewerModal");
  if (!modal) return;
  hideModal(modal, () => {
    $("#imageViewerImg")?.removeAttribute("src");
    state.imageViewer = { images: [], index: 0, title: "图片预览", zoom: 1, fit: true, draftId: "", draftImageLabels: [], draftImageKeys: [] };
  });
}

function moveImageViewer(delta) {
  const images = state.imageViewer?.images || [];
  if (images.length < 2) return;
  state.imageViewer.index = (state.imageViewer.index + delta + images.length) % images.length;
  state.imageViewer.fit = true;
  state.imageViewer.zoom = 1;
  renderImageViewer();
}

function setImageViewerZoom(delta = 0) {
  if (!state.imageViewer?.images?.length) return;
  if (!delta) {
    state.imageViewer.fit = true;
    state.imageViewer.zoom = 1;
  } else {
    const start = state.imageViewer.fit === false ? Number(state.imageViewer.zoom) || 1 : 1;
    state.imageViewer.fit = false;
    state.imageViewer.zoom = Math.min(2.5, Math.max(0.5, start + delta));
  }
  renderImageViewer();
}

function updateSelectedTemplateGallery(gallery, index) {
  if (!gallery) return;
  const pages = [...gallery.querySelectorAll("button[data-library-preview-image]")];
  if (!pages.length) return;
  const nextIndex = (Number(index) + pages.length) % pages.length;
  gallery.dataset.galleryIndex = String(nextIndex);
  pages.forEach((page, pageIndex) => {
    const active = pageIndex === nextIndex;
    page.classList.toggle("active", active);
    page.setAttribute("aria-hidden", active ? "false" : "true");
  });
  gallery.querySelectorAll("[data-gallery-dot]").forEach((dot, dotIndex) => {
    dot.classList.toggle("active", dotIndex === nextIndex);
    dot.setAttribute("aria-current", dotIndex === nextIndex ? "true" : "false");
  });
}

function handleTemplateGalleryEvent(event) {
  const control = event.target.closest("[data-gallery-prev], [data-gallery-next], [data-gallery-dot]");
  const gallery = event.target.closest("[data-template-gallery]");
  if (!control || !gallery) return false;
  const current = Number(gallery.dataset.galleryIndex || 0);
  const delta = control.hasAttribute("data-gallery-prev") ? -1 : control.hasAttribute("data-gallery-next") ? 1 : 0;
  const target = control.hasAttribute("data-gallery-dot") ? Number(control.dataset.galleryDot) : current + delta;
  updateSelectedTemplateGallery(gallery, target);
  event.preventDefault();
  event.stopPropagation();
  return true;
}

function previewImagesFromGallery(button) {
  const gallery = button?.closest("[data-template-gallery]");
  if (!gallery) return null;
  const images = [...gallery.querySelectorAll("button[data-library-preview-image] img")].map((image) => image.currentSrc || image.src).filter(Boolean);
  return images.length > 1 ? images : null;
}

// 「导出我的作品」（5.7）：把当前组织的草稿文案与图片打包带走。
// 服务端只打包记录里引用过的图片，缺文件也照样产出，跳过清单写在 manifest.json。
async function exportWorks() {
  const button = $("#exportWorksBtn");
  if (button) button.disabled = true;
  try {
    const response = await backendFetch("/api/export/works.zip");
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // 走错误边界：没登录与登录失效是两件事，话术由它分开（见 core/client.js）。
      throw backendError(data, response.status, response);
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    const matched = /filename="([^"]+)"/.exec(disposition);
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = matched ? matched[1] : "works.zip";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast("作品已开始下载（文案 + 图片）");
  } catch (error) {
    toast(error?.message || "导出失败", { kind: "error", duration: 6000 });
  } finally {
    if (button) button.disabled = false;
  }
}


// ----- js/knowledge/local-files.js -----
function localFileTypeIcon(type) {
  return type === "pdf" ? "file-type-2" : type === "docx" ? "file-text" : type === "txt" ? "notebook-text" : "file-code-2";
}

function localFileDirectoryPath(entry) {
  const relativePath = String(entry?.relativePath || "").replace(/\\/g, "/");
  const separatorIndex = relativePath.lastIndexOf("/");
  return separatorIndex >= 0 ? relativePath.slice(0, separatorIndex) : "";
}

// 云端资料的唯一入口判据：知识库（Dify）能不能用。
// 本地资料不走这条路——上传本地文件只受余额限制，与 Dify 配置无关。
// 未就绪时给一句人能照做的话，别让入口留在那里点下去报上游原文。
function knowledgeCloudGate(status) {
  const value = status && typeof status === "object" ? status : {};
  // 未登录时状态接口一定回 401：既不是"没配置"，也不是"没权限"，更不是"正在读取"——
  // 那个请求永远不会成功，页签会一直停在"请稍候，正在确认知识库连接状态"。先把登录这件事
  // 说清楚（见 auth/login-gate.js）。状态还没读到、并且已经确认没登录，就算这一类。
  const signedOut = value.signedOut
    || (!Object.prototype.hasOwnProperty.call(value, "configured")
      && typeof loginGateSignedOut === "function"
      && loginGateSignedOut());
  if (signedOut) {
    return { ready: false, title: "请先登录后再使用云端知识库", hint: "登录后即可读取本组织的知识库" };
  }
  if (value.unavailable) {
    return { ready: false, title: "知识库尚未就绪，或当前账号没有访问权限", hint: "请联系管理员确认知识库连接与访问权限" };
  }
  if (!Object.prototype.hasOwnProperty.call(value, "configured")) {
    return { ready: false, title: "正在读取知识库状态", hint: "请稍候，正在确认知识库连接状态" };
  }
  if (value.enabled !== true) {
    return { ready: false, title: "知识库当前未启用", hint: "请先在 AI 配置中心启用并配置 Dify 知识库" };
  }
  if (value.configured !== true) {
    return { ready: false, title: "知识库配置未完成", hint: "请先在 AI 配置中心填写 Dify API 地址、Dataset ID 与密钥" };
  }
  if (value.initialized !== true) {
    return { ready: false, title: "知识库尚未初始化", hint: "请先在知识库完成索引初始化" };
  }
  if (value.requiresRebuild) {
    return { ready: false, title: "索引配置已变化，需要重建全部", hint: "请先重建知识库索引，再使用云端资料" };
  }
  return { ready: true, title: "知识库已启用", hint: "" };
}

function reverseTopicSelectedCount() {
  return new Set([
    ...state.reverseTopicUploadQueue.map((item) => `local:${item.id}`),
    ...state.reverseTopicCloudSelection.map((id) => `cloud:${id}`),
  ]).size;
}

function reverseTopicCloudSelectedCount() {
  return state.reverseTopicCloudSelection.length;
}

function renderReverseTopicCloudDocuments() {
  const list = $("#reverseTopicCloudList");
  if (!list) return;
  const gate = knowledgeCloudGate(state.knowledgeStatus);
  if (!gate.ready) {
    list.innerHTML = `<div class="reverse-topic-cloud-empty"><i data-lucide="cloud-off"></i><strong>${escapeHtml(gate.title)}</strong><span>${escapeHtml(gate.hint)}</span></div>`;
    refreshIcons();
    return;
  }
  if (state.reverseTopicCloudLoading) {
    list.innerHTML = `<div class="reverse-topic-cloud-empty"><i data-lucide="loader-circle"></i><strong>正在读取云端资料</strong><span>请稍候，正在同步可用于生成的资料。</span></div>`;
    refreshIcons();
    return;
  }
  const documents = state.reverseTopicCloudDocuments || [];
  if (!documents.length) {
    list.innerHTML = `<div class="reverse-topic-cloud-empty"><i data-lucide="cloud-off"></i><strong>暂无可用云端资料</strong><span>请先在知识库上传资料并完成索引。</span></div>`;
    refreshIcons();
    return;
  }
  const selected = new Set(state.reverseTopicCloudSelection || []);
  list.innerHTML = documents.map((document) => {
    const id = String(document.id || "");
    const checked = selected.has(id);
    const metadata = [document.subject, document.chapter, document.documentType, document.year].filter(Boolean).join(" · ");
    return `<label class="reverse-topic-cloud-item ${checked ? "selected" : ""}">
      <input type="radio" name="reverse-topic-cloud-document" data-reverse-topic-cloud-id="${escapeHtml(id)}" ${checked ? "checked" : ""} />
      <span class="reverse-topic-cloud-check"><i data-lucide="${checked ? "check-circle-2" : "circle"}"></i></span>
      <span class="reverse-topic-cloud-copy"><strong title="${escapeHtml(document.filename || "未命名资料")}">${escapeHtml(document.filename || "未命名资料")}</strong><small>${escapeHtml(metadata || "云端知识库")}${Number(document.word_count || 0) ? ` · ${Number(document.word_count).toLocaleString("zh-CN")} 字` : ""}</small></span>
      <span class="reverse-topic-cloud-state">${escapeHtml(document.status === "ready" ? "可用" : document.status || "可用")}</span>
    </label>`;
  }).join("");
  refreshIcons();
}

async function loadReverseTopicCloudDocuments({ silent = false } = {}) {
  state.reverseTopicCloudLoading = true;
  renderReverseTopicCloudDocuments();
  try {
    const statusData = await getBackend("/api/knowledge/status");
    state.knowledgeStatus = statusData.status || state.knowledgeStatus || {};
    const gate = knowledgeCloudGate(state.knowledgeStatus);
    // 未配置是一种"入口不该可用"的状态，不是加载失败：不给错误弹窗，直接把原因写进列表。
    if (!gate.ready) {
      state.reverseTopicCloudDocuments = [];
      state.reverseTopicCloudSelection = [];
      if (!silent) toast(gate.hint, { kind: "warning", duration: 6000 });
      return;
    }
    const data = await getBackend("/api/knowledge/documents?status=ready");
    const byId = new Map();
    (data.documents || []).forEach((document) => {
      if (document?.id && !byId.has(document.id)) byId.set(document.id, document);
    });
    state.reverseTopicCloudDocuments = [...byId.values()];
    const available = new Set(state.reverseTopicCloudDocuments.map((document) => String(document.id)));
    state.reverseTopicCloudSelection = state.reverseTopicCloudSelection.filter((id) => available.has(String(id)));
  } catch (error) {
    state.reverseTopicCloudDocuments = [];
    // 未登录时状态接口必然 401：这不是"云端资料加载失败"，是还没登录。照实标记，门控就会
    // 把话说成"请先登录"，而不是让页签一直停在"正在读取知识库状态"（同 workspace.js 的处置）。
    if (Number(error?.status) === 401) {
      state.knowledgeStatus = { unavailable: true, signedOut: true };
      if (!silent) toast(loginGateActionMessage("云端资料"), { kind: "error", duration: 6000 });
    } else if (!silent) {
      toast(`云端资料加载失败：${error.message}`, { kind: "error", duration: 6000 });
    }
  } finally {
    state.reverseTopicCloudLoading = false;
    renderReverseTopicSelection();
  }
}

function setReverseTopicSource(source = "local") {
  if (state.reverseTopicGeneration?.active) return;
  const nextSource = source === "cloud" ? "cloud" : "local";
  const gate = knowledgeCloudGate(state.knowledgeStatus);
  if (nextSource === "cloud" && !gate.ready) {
    // 「云端资料」页签在未配置时是禁用态；这里兜住键盘/程序化切换，别静默进入空面板。
    toast(gate.hint, { kind: "warning", duration: 6000 });
    return;
  }
  if (nextSource !== state.reverseTopicSource) {
    if (nextSource === "cloud") state.reverseTopicUploadQueue = [];
    else state.reverseTopicCloudSelection = [];
  }
  state.reverseTopicSource = nextSource;
  renderReverseTopicSelection();
  if (state.reverseTopicSource === "cloud" && !state.reverseTopicCloudDocuments.length && !state.reverseTopicCloudLoading) {
    loadReverseTopicCloudDocuments();
  }
}

function renderReverseTopicSelection() {
  const localCount = state.reverseTopicUploadQueue.length;
  const cloudCount = reverseTopicCloudSelectedCount();
  const active = state.reverseTopicGeneration?.active === true;
  const mode = state.reverseTopicGeneration?.mode === "broad" ? "broad" : "focused";
  const gate = knowledgeCloudGate(state.knowledgeStatus);
  // 云端入口未就绪时回落本地：否则这个弹层会停在"云端已选 0 份"的死路里。
  if (!gate.ready && state.reverseTopicSource === "cloud") state.reverseTopicSource = "local";
  const source = state.reverseTopicSource === "cloud" ? "cloud" : "local";
  const selectedCount = reverseTopicSelectedCount();
  $$('[data-reverse-topic-source]').forEach((button) => {
    const isCloud = button.dataset.reverseTopicSource === "cloud";
    const gated = isCloud && !gate.ready;
    const selected = button.dataset.reverseTopicSource === source;
    button.classList.toggle("active", selected);
    button.setAttribute("aria-selected", String(selected));
    button.disabled = active || gated;
    if (isCloud) {
      button.classList.toggle("is-gated", gated);
      button.setAttribute("aria-disabled", String(gated));
      button.title = gated ? gate.hint : "";
    }
  });
  $("#reverseTopicLocalPanel")?.classList.toggle("hidden", source !== "local");
  $("#reverseTopicCloudPanel")?.classList.toggle("hidden", source !== "cloud");
  const summary = $("#reverseTopicSelectionSummary");
  if (summary) summary.textContent = active
    ? (state.reverseTopicGeneration.stage || "正在读取所选资料")
    : `已选 ${selectedCount} 份${source === "cloud" ? "云端" : "本地"}资料`;
  const count = $("#localTopicSourceCount");
  if (count) count.textContent = `${localCount} / ${MAX_REVERSE_TOPIC_FILES}`;
  const cloudCountNode = $("#reverseTopicCloudCount");
  if (cloudCountNode) cloudCountNode.textContent = `已选 ${cloudCount} / ${MAX_REVERSE_TOPIC_FILES}`;
  $$('[data-reverse-topic-mode]').forEach((button) => {
    const selected = button.dataset.reverseTopicMode === mode;
    button.classList.toggle("active", selected);
    button.setAttribute("aria-pressed", String(selected));
    button.disabled = active;
  });
  const clear = $("#clearLocalTopicSourcesBtn");
  if (clear) clear.disabled = selectedCount === 0 || active;
  const add = $("#addLocalTopicSourceBtn");
  if (add) add.disabled = active || source !== "local" || selectedCount >= MAX_REVERSE_TOPIC_FILES;
  const refreshCloud = $("#refreshReverseTopicCloudBtn");
  if (refreshCloud) refreshCloud.disabled = active || state.reverseTopicCloudLoading || !gate.ready;
  const generate = $("#generateReverseTopicBtn");
  if (generate) {
    generate.disabled = reverseTopicSelectedCount() === 0 || active;
    generate.classList.toggle("is-loading", active);
    generate.setAttribute("aria-busy", String(active));
    generate.innerHTML = active
      ? `<i data-lucide="loader-circle"></i><span>正在生成${mode === "broad" ? "宽泛" : "聚焦"}主题</span>`
      : `<i data-lucide="sparkles"></i><span>生成${mode === "broad" ? "宽泛" : "聚焦"}主题</span>`;
  }
  const status = $("#localTopicGenerationStatus");
  if (status) status.textContent = active ? (state.reverseTopicGeneration.stage || "正在处理") : "";
  renderLocalUploadQueue($("#localTopicSourceList"), state.reverseTopicUploadQueue, "reverse");
  renderReverseTopicCloudDocuments();
  refreshIcons();
}

function setReverseTopicMode(mode = "focused") {
  if (state.reverseTopicGeneration?.active) return;
  state.reverseTopicGeneration.mode = mode === "broad" ? "broad" : "focused";
  renderReverseTopicSelection();
}

// 云端入口的可用性取决于实时知识库状态；不刷新就只会停在"读取中"的禁用态。
// 读不到时保持现状（入口继续禁用），不拿旧结论放开云端。
async function refreshKnowledgeStatus() {
  try {
    const statusData = await getBackend("/api/knowledge/status");
    state.knowledgeStatus = statusData.status || state.knowledgeStatus || {};
  } catch (error) {
    // 未登录时状态接口必然 401：那不是"还在读取"，就是没登录。照实标记，否则云端页签会
    // 永远停在「正在读取知识库状态」（见 auth/login-gate.js）。
    if (Number(error?.status) === 401) state.knowledgeStatus = { unavailable: true, signedOut: true };
    // 其它情况读不到就什么都不改：状态未知时门控本来就是禁用的。
  }
  renderReverseTopicSelection();
  return state.knowledgeStatus;
}

function openLocalTopicGenerator(source = "local") {
  if (!state.reverseTopicGeneration?.mode) state.reverseTopicGeneration.mode = "focused";
  setReverseTopicSource(source);
  renderReverseTopicSelection();
  showModal($("#localTopicGeneratorModal"));
  refreshKnowledgeStatus().then(() => {
    if (state.reverseTopicSource === "cloud") loadReverseTopicCloudDocuments({ silent: true });
  });
}

function closeLocalTopicGenerator() {
  hideModal($("#localTopicGeneratorModal"));
}


function setKnowledgeSource(source = "local") {
  state.knowledgeSource = source === "local" ? "local" : "cloud";
  const activeDraft = currentDraft();
  if (activeDraft) activeDraft.knowledgeSource = state.knowledgeSource;
  saveState();
  $$("[data-knowledge-source]").forEach((button) => {
    const active = button.dataset.knowledgeSource === state.knowledgeSource;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  if (state.knowledgeSource !== "local") loadKnowledgeWorkspace();
  refreshIcons();
}




function imageAssignmentTasks(workflow) {
  return state.drafts
    .filter((draft) => draftWorkflow(draft) === workflow)
    .sort((a, b) => Date.parse(b.createdAt || "") - Date.parse(a.createdAt || ""));
}

function renderImageAssignmentModal() {
  const assignment = state.imageAssignment;
  const generated = assignment ? generatedImageById(assignment.generatedId) : null;
  const preview = $("#imageAssignmentPreview");
  const tasks = $("#imageAssignmentTasks");
  if (!assignment || !generated || !preview || !tasks) return;
  preview.innerHTML = `<img src="${escapeHtml(generated.src)}" alt="${escapeHtml(generated.title || "待加入图片")}" /><span>${escapeHtml(generated.title || "AI 图片素材")}</span>`;
  $$("[data-image-assignment-workflow]").forEach((button) => {
    const active = button.dataset.imageAssignmentWorkflow === assignment.workflow;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  const candidates = imageAssignmentTasks(assignment.workflow);
  if (!candidates.length) {
    tasks.innerHTML = `<div class="image-assignment-empty"><i data-lucide="file-plus-2"></i><strong>这个分区还没有任务</strong><span>先创建一篇${assignment.workflow === "replica" ? "快速笔记" : "原创笔记"}，再回来加入图片。</span></div>`;
    $("#confirmImageAssignmentBtn").disabled = true;
  } else {
    if (!candidates.some((draft) => draft.id === assignment.draftId)) assignment.draftId = candidates[0].id;
    tasks.innerHTML = candidates.map((draft) => {
      const selected = draft.id === assignment.draftId;
      const imageCount = normalizeImages(draft.templateRenderImages?.length ? draft.templateRenderImages : draft.images).length;
      return `<button type="button" class="image-assignment-task ${selected ? "selected" : ""}" data-image-assignment-draft="${escapeHtml(draft.id)}" aria-pressed="${selected}">
        <span class="image-assignment-task-radio"><i data-lucide="${selected ? "check-circle-2" : "circle"}"></i></span>
        <span><strong>${escapeHtml(draftTaskTitle(draft))}</strong><small>#${String(Number(draft.taskNumber || 0)).padStart(2, "0")} · ${imageCount} 张图片</small></span>
      </button>`;
    }).join("");
    $("#confirmImageAssignmentBtn").disabled = false;
  }
  const roleInput = $(`input[name="imageAssignmentRole"][value="${assignment.role || "inner"}"]`);
  $$('[data-image-assignment-role]').forEach((option) => {
    const selected = option.dataset.imageAssignmentRole === (assignment.role || "inner");
    option.classList.toggle("selected", selected);
    option.setAttribute("aria-checked", String(selected));
  });
  if (roleInput) roleInput.checked = true;
  refreshIcons();
}

function openImageAssignment(generatedId) {
  const generated = generatedImageById(generatedId);
  if (!generated?.src) return;
  const activeDraft = state.drafts.find((draft) => draft.id === state.activeDraftId);
  const preferredWorkflow = activeDraft ? draftWorkflow(activeDraft) : (state.activeWorkflow === "original" ? "original" : "replica");
  const candidates = imageAssignmentTasks(preferredWorkflow);
  state.imageAssignment = {
    generatedId,
    workflow: candidates.length ? preferredWorkflow : (imageAssignmentTasks(preferredWorkflow === "replica" ? "original" : "replica").length ? (preferredWorkflow === "replica" ? "original" : "replica") : preferredWorkflow),
    draftId: candidates[0]?.id || "",
    role: "inner",
  };
  renderImageAssignmentModal();
  showModal($("#imageAssignmentModal"));
}

function closeImageAssignment() {
  state.imageAssignment = null;
  hideModal($("#imageAssignmentModal"));
}

function knowledgeReviewNode(id) {
  return document.getElementById(`${id}Inline`) || document.getElementById(id);
}

function confirmImageAssignment() {
  const assignment = state.imageAssignment;
  const generated = assignment ? generatedImageById(assignment.generatedId) : null;
  const draft = assignment?.draftId ? state.drafts.find((item) => item.id === assignment.draftId) : null;
  if (!generated || !draft) return;
  const role = $("input[name=\"imageAssignmentRole\"]:checked")?.value || "inner";
  const result = addGeneratedImageToCurrentDraft(generated, role, draft);
  if (result.status === "full") {
    toast(`该任务已保留 ${MAX_DRAFT_TEMPLATE_PAGES} 张图片，请先删除一张再加入`, { kind: "error" });
    return;
  }
  if (result.status === "duplicate") {
    toast(`这张图片已经是该任务的${role === "cover" ? "封面" : "内页"}`);
    return;
  }
  state.activeWorkflow = draftWorkflow(draft);
  setActiveDraftId(draft.id);
  saveState();
  closeImageAssignment();
  setSavedImageLibraryOpen(false);
  renderEditor();
  renderDraftList();
  setSection("rewrite");
  toast(`图片已加入${draftWorkflow(draft) === "replica" ? "快速笔记" : "原创笔记"}任务的${role === "cover" ? "封面" : "内页"}`);
}


function renderLocalReferencePageControl(item) {
  if (item.type !== "pdf") return `<span class="local-page-fulltext">全文</span>`;
  if (item.pageInspecting) return `<span class="local-page-loading">正在读取页数</span>`;
  if (!item.pageCount && !item.pageInspectError) return `<span class="local-page-loading">勾选后读取页数</span>`;
  const pageInfo = item.pageCount ? `共 ${item.pageCount} 页` : (item.pageInspectError || "页数未知");
  const max = item.pageCount || 9999;
  const ranges = normalizeDirectoryLocalPageRanges(item);
  return `<div class="local-page-scope"><div class="local-page-scope-top"><span>参考范围</span><span class="local-page-count">${escapeHtml(pageInfo)}</span></div><div class="local-page-modes"><button type="button" class="${item.pageMode === "all" ? "active" : ""}" data-local-page-mode="all" data-local-reference-key="${escapeHtml(item.key)}">全文</button><button type="button" class="${item.pageMode === "range" ? "active" : ""}" data-local-page-mode="range" data-local-reference-key="${escapeHtml(item.key)}">指定页码</button></div>${item.pageMode === "range" ? `<div class="local-page-fields">${ranges.map((range, rangeIndex) => `<div class="local-page-range-row"><label class="local-page-bound">从 <input type="number" min="1" max="${max}" value="${range.start}" data-local-page-index="${rangeIndex}" data-local-page-start="${escapeHtml(item.key)}" /> 页</label><span class="local-page-between">到</span><label class="local-page-bound"><input type="number" min="${range.start}" max="${max}" value="${range.end}" data-local-page-index="${rangeIndex}" data-local-page-end="${escapeHtml(item.key)}" /> 页</label><span class="local-range-actions"><span class="local-range-remove-slot">${ranges.length > 1 ? `<button type="button" class="local-range-action" data-local-page-remove="${escapeHtml(item.key)}" data-local-page-index="${rangeIndex}" title="删除这段范围" aria-label="删除这段范围">-</button>` : ""}</span><span class="local-range-add-slot">${rangeIndex === ranges.length - 1 ? `<button type="button" class="local-range-action" data-local-page-add="${escapeHtml(item.key)}" title="增加一段页码范围" aria-label="增加一段页码范围">+</button>` : ""}</span></span></div>`).join("")}</div>` : ""}</div>`;
}

function renderLocalReferenceRow(entry, selected, variant = "evidence") {
  const pageControl = renderLocalReferencePageControl(entry);
  return `<div class="${variant === "picker" ? "local-file-picker-row" : "knowledge-evidence-file-row"} ${selected ? "selected" : ""}"><input type="checkbox" data-local-reference-key="${escapeHtml(entry.key)}" ${selected ? "checked" : ""} /><span class="local-file-icon"><i data-lucide="${localFileTypeIcon(entry.type)}"></i></span><span class="${variant === "picker" ? "local-file-picker-copy" : "knowledge-evidence-file-copy"}"><strong>${escapeHtml(entry.name)}</strong><small title="${escapeHtml(entry.relativePath)}">${escapeHtml(entry.sourceName)}${localFileDirectoryPath(entry) ? ` / ${escapeHtml(localFileDirectoryPath(entry))}` : ""}</small></span><span class="local-file-reference-pages">${pageControl}</span><span class="knowledge-evidence-file-size">${escapeHtml(formatKnowledgeFileSize(entry.size))}</span></div>`;
}

function localReferenceSignature(items) {
  return (items || []).map((item) => `${item.key}|${item.lastModified}|${item.size}|${item.pageRange || ""}`).sort().join("::");
}

function normalizeDirectoryLocalPageRanges(item) {
  const max = Math.max(1, Number(item?.pageCount) || 9999);
  const source = Array.isArray(item?.pageRanges) && item.pageRanges.length
    ? item.pageRanges
    : [{ start: 1, end: Number(item?.pageCount) || 1 }];
  return source.map((range) => {
    const start = Math.max(1, Math.min(max, Number(range?.start) || 1));
    const end = Math.max(start, Math.min(max, Number(range?.end) || start));
    return { start, end };
  });
}

function localPageRangesFromText(value) {
  const raw = String(value || "").trim();
  if (!raw) return [];
  return raw.split(/[,，;；]+/).map((token) => {
    const match = token.trim().match(/^(\d+)(?:\s*[-~至]\s*(\d+))?$/);
    if (!match) return null;
    const start = Math.max(1, Number(match[1]));
    const end = Math.max(start, Number(match[2] || match[1]));
    return { start, end };
  }).filter(Boolean);
}

function localReferencePageRangeValue(item) {
  return String(item?.pageRange || localReferencePageRange(item) || "");
}

// 本地语料库只把解析成功的文件写进 `files`，解析失败的进 `errors`。
// 「已确认 N 份参考资料」必须与这两半对齐：失败的 PDF 即使还在本次选择列表里，
// 也不能冒充已确认资料（C18）。
function localReferenceCorpusPath(value) {
  return String(value || "")
    .replace(/\\/g, "/")
    .split("/")
    .filter((part) => part && part !== ".")
    .join("/")
    .slice(0, 500);
}

function confirmedLocalReferenceFiles(files, corpus) {
  const records = Array.isArray(files) ? files : [];
  const corpusFiles = Array.isArray(corpus?.files) ? corpus.files : [];
  if (!corpusFiles.length) return records;
  const succeededPaths = new Set(corpusFiles.map((item) => localReferenceCorpusPath(item?.relativePath)).filter(Boolean));
  const succeededNames = new Set(corpusFiles.map((item) => String(item?.filename || "")).filter(Boolean));
  const omissions = Array.isArray(corpus?.errors) ? corpus.errors : [];
  const omittedPaths = new Set(omissions.map((item) => localReferenceCorpusPath(item?.relativePath)).filter(Boolean));
  const omittedNames = new Set(omissions.map((item) => String(item?.filename || "")).filter(Boolean));
  const pathOf = (record) => localReferenceCorpusPath(
    `${record?.item?.sourceName || ""}/${record?.item?.relativePath || ""}`
  );
  return records.filter((record) => {
    const path = pathOf(record);
    if (omittedPaths.has(path)) return false;
    if (succeededPaths.has(path)) return true;
    // 服务器清单与前端选择在极少数路径差异下对不上时，按文件名再兜一次；
    // 名称也落在失败清单里就绝不能算「已确认」。
    const name = String(record?.item?.name || record?.file?.name || "");
    if (omittedNames.has(name)) return false;
    return succeededNames.has(name);
  });
}

// 解析失败的确认必须是界面内的弹层，不能依赖浏览器原生 confirm：
// 原生弹窗在部分运行环境里会被静默接受或禁用，用户就看不到「明确排除」这一步。
let localOmissionConfirmResolver = null;

function settleLocalOmissionConfirm(accepted) {
  const modal = typeof document !== "undefined" ? document.getElementById("localOmissionConfirmModal") : null;
  const resolve = localOmissionConfirmResolver;
  localOmissionConfirmResolver = null;
  if (modal && typeof hideModal === "function") hideModal(modal);
  if (resolve) resolve(accepted);
}

function localOmissionConfirmModal() {
  let modal = document.getElementById("localOmissionConfirmModal");
  if (modal) return modal;
  modal = document.createElement("div");
  modal.className = "modal hidden";
  modal.id = "localOmissionConfirmModal";
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = `<div class="modal-card template-delete-card" role="dialog" aria-modal="true" aria-labelledby="localOmissionConfirmTitle">
    <button class="modal-close" data-local-omission-decision="cancel" type="button" aria-label="关闭"><i data-lucide="x"></i></button>
    <p class="eyebrow">资料解析确认</p>
    <h2 id="localOmissionConfirmTitle">有资料解析失败</h2>
    <div class="template-delete-summary"><span>未解析成功</span><strong id="localOmissionConfirmCount">0 份</strong></div>
    <p class="template-delete-impact" id="localOmissionConfirmList"></p>
    <p class="template-delete-warning">点「排除并继续」即表示明确排除这些文件：它们不会进入本次参考资料，也不会参与生成。</p>
    <div class="template-delete-actions">
      <button type="button" class="ghost-btn" data-local-omission-decision="cancel">取消，返回修改</button>
      <button type="button" class="primary-btn danger-action" data-local-omission-decision="accept">排除并继续</button>
    </div>
  </div>`;
  document.body.appendChild(modal);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) {
      settleLocalOmissionConfirm(false);
      return;
    }
    const decision = event.target?.closest?.("[data-local-omission-decision]");
    if (decision) settleLocalOmissionConfirm(decision.dataset.localOmissionDecision === "accept");
  });
  return modal;
}

function confirmLocalOmissionExclusion(omissions) {
  const items = (Array.isArray(omissions) ? omissions : []).filter((item) => item && typeof item === "object");
  if (typeof document === "undefined" || typeof showModal !== "function" || typeof hideModal !== "function") {
    // 极简壳（无弹层实现）才回落到原生确认；正常工作站永远走界面内弹层。
    return Promise.resolve(
      typeof window !== "undefined" && typeof window.confirm === "function"
        ? window.confirm(`${items.length || 1} 个文件解析失败。是否明确排除这些文件，并仅使用其余成功资料继续？`)
        : false
    );
  }
  const modal = localOmissionConfirmModal();
  if (localOmissionConfirmResolver) {
    localOmissionConfirmResolver(false);
    localOmissionConfirmResolver = null;
  }
  const count = document.getElementById("localOmissionConfirmCount");
  if (count) count.textContent = `${items.length || 1} 份`;
  const list = document.getElementById("localOmissionConfirmList");
  if (list) {
    const labels = items.slice(0, 5).map((item) => `${item.filename || item.relativePath || "未命名文件"}（${item.error || "解析失败"}）`);
    list.textContent = labels.length
      ? `未解析：${labels.join("、")}${items.length > labels.length ? ` 等 ${items.length} 项` : ""}`
      : "有文件未能解析出可用正文。";
  }
  showModal(modal);
  if (typeof refreshIcons === "function") refreshIcons();
  return new Promise((resolve) => { localOmissionConfirmResolver = resolve; });
}


async function prepareLocalUploadQueue(queue, topic = "") {
  const selection = (queue || []).map((item) => ({ key: item.id, sourceId: "upload", sourceName: "本地上传", name: item.file.name, relativePath: item.file.name, size: item.file.size, lastModified: item.file.lastModified, file: item.file, pageRange: localReferencePageRange(item) }));
  if (!selection.length) return null;
  const body = new FormData();
  const files = [];
  for (const item of selection) {
    const file = item.file;
    if (file.size > MAX_LOCAL_REFERENCE_FILE_BYTES) throw new Error(`${file.name} 超过 100MB，无法作为本地资料`);
    files.push({ item, file });
    body.append("files", file, file.name);
  }
  body.append("relativePaths", JSON.stringify(files.map(({ item }) => `${item.sourceName}/${item.relativePath}`)));
  body.append("modifiedAt", JSON.stringify(files.map(({ file }) => file.lastModified)));
  body.append("pageRanges", JSON.stringify(files.map(({ item }) => item.pageRange || "")));
  body.append("topic", topic || "");
  const response = await backendFetch("/api/knowledge/local-files/prepare", { method: "POST", body });
  const result = await response.json().catch(() => ({}));
  // 失败报文统一在 HTTP 边界翻成人话（见 core/client.js）：本地上传撞上未登录时，
  // 「接口返回 401」既没说清原因，也没告诉用户下一步做什么。
  if (!response.ok || result.ok === false) throw backendError(result, response.status, response);
  let corpus = result.corpus && typeof result.corpus === "object" ? result.corpus : null;
  if (!result.corpusId || !corpus) throw new Error("资料语料库创建成功但未返回有效编号");
  if (corpus.status === "partial") {
    const omissions = Array.isArray(result.errors) ? result.errors : [];
    // 解析失败必须在界面内被明确排除后才能继续；失败文件不进「已确认参考资料」（C18）。
    const accepted = await confirmLocalOmissionExclusion(omissions);
    if (!accepted) return false;
    const confirmed = await postBackend(`/api/knowledge/corpora/${encodeURIComponent(result.corpusId)}/confirm`, {});
    corpus = confirmed.corpus && typeof confirmed.corpus === "object" ? confirmed.corpus : null;
    if (!corpus || corpus.status !== "ready_with_omissions") throw new Error("资料遗漏确认未能持久化，请重新选择文件");
  }
  return {
    corpusId: result.corpusId,
    corpus,
    files,
    errors: Array.isArray(result.errors) ? result.errors : [],
    signature: localReferenceSignature(selection),
  };
}

async function prepareSelectedLocalReferences(draft, topic = "") {
  const ownerId = draft?.id || "";
  const ownsWorkingSelection = state.localReferenceSelectionOwnerId === ownerId;
  const uploadSelection = (ownsWorkingSelection ? state.localUploadQueue : []).map((item) => ({
    key: item.id,
    sourceId: "upload",
    sourceName: "本地上传",
    name: item.file.name,
    relativePath: item.file.name,
    size: item.file.size,
    lastModified: item.file.lastModified,
    file: item.file,
    pageRange: localReferencePageRange(item),
  }));
  const selection = ownsWorkingSelection
    ? (uploadSelection.length ? uploadSelection : [...state.localReferenceSelection])
    : [];
  if (!selection.length) return true;
  const signature = localReferenceSignature(selection);
  const currentKnowledge = draft?.knowledge || state.pendingLocalKnowledge;
  if (currentKnowledge?.mode === "local" && currentKnowledge.referenceSignature === signature && !currentKnowledge.stale) return true;
  const body = new FormData();
  const files = [];
  for (const item of selection) {
    const file = item.file;
    if (!file) throw new Error(`${item.name} 已不可用，请重新选择文件`);
    if (file.size > 20 * 1024 * 1024) throw new Error(`${file.name} 超过 20MB，无法作为参考资料`);
    files.push({ item, file });
    body.append("files", file, file.name);
  }
  body.append("relativePaths", JSON.stringify(files.map(({ item }) => `${item.sourceName}/${item.relativePath}`)));
  body.append("modifiedAt", JSON.stringify(files.map(({ file }) => file.lastModified)));
  body.append("pageRanges", JSON.stringify(files.map(({ item }) => localReferencePageRangeValue(item))));
  body.append("topic", topic || "");
  const response = await backendFetch("/api/knowledge/local-files/prepare", { method: "POST", body });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.ok === false) throw backendError(result, response.status, response);
  let corpus = result.corpus && typeof result.corpus === "object" ? result.corpus : null;
  if (!result.corpusId || !corpus) throw new Error("资料语料库创建成功但未返回有效编号");
  if (corpus.status === "partial") {
    const omissions = Array.isArray(result.errors) ? result.errors : [];
    // 解析失败必须在界面内被明确排除后才能继续；失败文件不进「已确认参考资料」（C18）。
    const accepted = await confirmLocalOmissionExclusion(omissions);
    if (!accepted) return false;
    const confirmed = await postBackend(`/api/knowledge/corpora/${encodeURIComponent(result.corpusId)}/confirm`, {});
    corpus = confirmed.corpus && typeof confirmed.corpus === "object" ? confirmed.corpus : null;
    if (!corpus || corpus.status !== "ready_with_omissions") throw new Error("资料遗漏确认未能持久化，请重新选择文件");
  }
  // 「已确认 N 份参考资料」只数语料库里真正解析成功的文件：失败的文件留在
  // omittedSources 里，不能因为还在本次选择列表里就算成已确认（C18）。
  const confirmedFiles = confirmedLocalReferenceFiles(files, corpus);
  const knowledge = {
    decision: "grounded",
    mode: "local",
    corpusId: result.corpusId,
    corpus,
    fileReferences: confirmedFiles.map(({ item, file }) => ({
      key: item.key,
      sourceId: item.sourceId,
      sourceName: item.sourceName,
      filename: file.name,
      relativePath: item.relativePath,
      size: file.size,
      lastModified: file.lastModified,
      pageRange: localReferencePageRangeValue(item),
    })),
    sources: confirmedFiles.map(({ item }) => ({ sourceName: item.name, sourcePath: `${item.sourceName}/${item.relativePath}`, sourceType: "local" })),
    referenceSignature: signature,
    topicSignature: draft ? knowledgeTopicSignatureForDraft(draft) : "",
    omittedSources: Array.isArray(corpus.errors) ? corpus.errors.map((item) => ({ ...item })) : [],
    stale: false,
    confirmedAt: new Date().toISOString(),
  };
  if (draft) draft.knowledge = knowledge;
  else state.pendingLocalKnowledge = knowledge;
  saveState();
  if (result.errors?.length) toast(`已确认排除 ${result.errors.length} 个解析失败文件`, { kind: "error" });
  return true;
}

async function generateReverseTopic(mode = "focused") {
  mode = (mode || state.reverseTopicGeneration?.mode) === "broad" ? "broad" : "focused";
  const modeLabel = mode === "broad" ? "宽泛主题" : "聚焦主题";
  if (state.reverseTopicGeneration?.active || reverseTopicSelectedCount() === 0) return;
  if (!ensureAiConfigured("text")) return;
  state.reverseTopicGeneration = { active: true, stage: "正在读取所选资料", mode };
  renderReverseTopicSelection();
  // 选题书架里立刻落一张"正在生成"的占位卡：弹层可以随时关掉，关掉之后四库里再没有任何
  // 线索（现场：不点进弹层压根不知道在生成）。
  syncReverseTopicGenerationCard();
  try {
    let localCorpusId = "";
    const usingCloudSource = state.reverseTopicSource === "cloud";
    if (!usingCloudSource) {
      if (state.reverseTopicUploadQueue.length) {
        state.reverseTopicGeneration.stage = "正在读取本地上传资料";
        renderReverseTopicSelection();
        syncReverseTopicGenerationCard();
        const prepared = await prepareLocalUploadQueue(state.reverseTopicUploadQueue, "逆向选题");
        if (!prepared) return;
        localCorpusId = prepared.corpusId;
      }
    }
    const cloudDocumentIds = usingCloudSource
      ? [...new Set(state.reverseTopicCloudSelection.map((id) => String(id || "").trim()).filter(Boolean))].slice(0, MAX_REVERSE_TOPIC_FILES)
      : [];
    const data = await postBackend("/api/knowledge/reverse-topic", {
      mode,
      localCorpusId,
      cloudDocumentIds,
    }, {
      onProgress(progress) {
        state.reverseTopicGeneration.stage = progress?.message || `正在生成${modeLabel}`;
        renderReverseTopicSelection();
        syncReverseTopicGenerationCard();
      },
    });
    applyTopicPackageLibraryResponse(data);
    const packageId = String(data.topicPackage?.id || "");
    const libraryItem = (state.viralLibraries.topic || []).find((item) => topicPackageIdentity(item) === packageId);
    if (libraryItem) state.viralLibrarySelections.topicId = libraryItem.id;
    const coverage = data.coverage || {};
    state.reverseTopicUploadQueue = [];
    state.reverseTopicCloudSelection = [];
    state.reverseTopicSelection = { local: [], cloud: [] };
    hideModal($("#localTopicGeneratorModal"));
    setSection("viral");
    setViralWorkbench("topic");
    renderFourLibraryWorkbench();
    toast(
      mode === "broad"
        ? `已生成宽泛主题，覆盖 ${Number(coverage.coveredSourceCount || 0)}/${Number(coverage.selectedSourceCount || 0)} 份资料的主要范围`
        : `已生成聚焦主题，中心思想串联 ${Number(coverage.coveredSourceCount || 0)}/${Number(coverage.selectedSourceCount || 0)} 份资料的主要内容`,
      { duration: 5000 },
    );
  } catch (error) {
    if (!isAbortError(error)) {
      const authenticationFailed = /401|invalid token|invalid api key|unauthorized|鉴权失败|密钥.*(?:无效|失效)/i.test(String(error?.message || ""));
      // 密钥被拒时要修的是上游配置，那同样只有平台管理员能动（普通成员只会拿到整段回滚）。
      if (authenticationFailed) openPlatformAiConfigOrExplain("text", "密钥被上游拒绝");
      toast(`逆向选题生成失败：${friendlyAiError(error)}`, { kind: "error", duration: 7000 });
    }
  } finally {
    state.reverseTopicGeneration = { active: false, stage: "", mode };
    renderReverseTopicSelection();
    // 收尾（成功或失败）都要把占位卡撤掉：它只是"正在生成"的牌子，不是一条选题。
    syncReverseTopicGenerationCard();
  }
}

function knowledgeIsEnabled() {
  return state.aiConfig?.knowledge?.enabled === true;
}

function knowledgeChannelForDraft(draft) {
  return draft?.knowledgeChannel === "reference" ? "reference" : "pure-ai";
}

function knowledgeSourceForDraft(draft) {
  // 云端资料入口已收起（见 js/core/unfinished-surfaces.js）：一律按本地资料解释草稿，
  // 免得旧草稿里存着的 "cloud" 把资料参照卡锁在一个再也切不回去的来源上。
  if (!CLOUD_KNOWLEDGE_ENABLED) return "local";
  if (draft?.knowledgeSource === "cloud") return "cloud";
  if (draft?.knowledgeSource === "local") return "local";
  return draft?.knowledge?.mode && draft.knowledge.mode !== "local" ? "cloud" : "local";
}

function knowledgeMatchesSelectedSource(draft) {
  const knowledge = draft?.knowledge;
  if (!knowledge) return false;
  return knowledgeSourceForDraft(draft) === (knowledge.mode === "local" ? "local" : "cloud");
}

function cloudKnowledgeSelectionsReady(knowledge) {
  if (!knowledge || knowledge.mode === "local") return true;
  const selections = Array.isArray(knowledge.selections) ? knowledge.selections : [];
  if (!selections.length || selections.length > MAX_CLOUD_REFERENCE_FILES) return false;
  return selections.every((item) => item && typeof item === "object" && Boolean(
    String(item.documentId || "").trim() || String(item.overrideText || "").trim()
  ));
}

function knowledgeForRequest(draft) {
  if (isReplicaDraft(draft) || draft?.knowledgeChannel === "pure-ai") {
    return {
      decision: "fallback",
      mode: "pure-ai",
      corpusId: "",
      query: "",
      selections: [],
    };
  }
  const knowledge = draft?.knowledge || (!draft ? state.pendingLocalKnowledge : null);
  if (!knowledge || knowledge.stale || !["grounded", "fallback"].includes(knowledge.decision)) return null;
  if (draft && !knowledgeMatchesSelectedSource(draft)) return null;
  if (knowledge.mode !== "local" && knowledge.decision === "grounded" && !cloudKnowledgeSelectionsReady(knowledge)) return null;
  if (knowledge.mode === "local") {
    return {
      decision: "grounded",
      mode: "local",
      corpusId: knowledge.corpusId || "",
    };
  }
  return {
    decision: knowledge.decision,
    mode: knowledge.mode || "",
    corpusId: knowledge.corpusId || "",
    query: knowledge.query || "",
    selections: (knowledge.selections || []).map((item) => ({
      chunkId: item.chunkId,
      documentId: item.documentId || "",
      sourceName: item.sourceName || "",
      sourcePage: Number(item.sourcePage || 0),
      title: item.title || "",
      score: Number(item.score || 0),
      overrideText: item.overrideText || "",
      subject: item.subject || "",
      chapter: item.chapter || "",
      documentType: item.documentType || "",
      year: item.year || "",
      matchedNeeds: Array.isArray(item.matchedNeeds) ? item.matchedNeeds.slice(0, 6) : [],
    })),
  };
}

function compactKnowledgeSearchQuery(value, maxChars = 240) {
  return String(value || "").replace(/\s+/g, " ").trim().slice(0, maxChars).trim();
}

function knowledgeTopicPackageForDraft(draft) {
  const brief = draft?.viralBrief || {};
  return brief.topicPackage || draft?.executionPlan?.topicPackage || brief;
}

function knowledgePreferredDocumentIdsForDraft(draft) {
  const topicPackage = knowledgeTopicPackageForDraft(draft);
  const directIds = (topicPackage.sourceDocumentIds || []).map((value) => String(value || "").trim()).filter(Boolean);
  if (directIds.length) return [...new Set(directIds)].slice(0, 20);
  const evidence = (topicPackage.sourceEvidence || []).join("\n");
  if (!evidence) return [];
  return (state.knowledgeDocuments || [])
    .filter((document) => document?.id && document?.filename && evidence.includes(document.filename))
    .map((document) => String(document.id))
    .filter((value, index, items) => items.indexOf(value) === index)
    .slice(0, 20);
}

function knowledgeQueryForDraft(draft) {
  const topicPackage = knowledgeTopicPackageForDraft(draft);
  const parts = [
    topicPackage.topic || draft?.title,
    ...(topicPackage.keywords || []).slice(0, 8),
    ...(topicPackage.contentPillars || []).slice(0, 6),
    ...(topicPackage.retrievalSeeds || []).slice(0, 8),
    ...(topicPackage.sourceEvidence || []).slice(0, 3),
  ].map((value) => String(value || "").replace(/\s+/g, " ").trim()).filter(Boolean);
  return compactKnowledgeSearchQuery([...new Set(parts)].join("；"));
}

function knowledgeTopicSignatureForDraft(draft) {
  const brief = knowledgeTopicPackageForDraft(draft);
  const topic = String(brief.topic || "").trim();
  return [topic || draft?.title, brief.audience, brief.pain, brief.benefit]
    .map((value) => String(value || "").replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("|")
    .slice(0, 1000);
}

function markKnowledgeStaleIfTopicChanged(draft, previousSignature = "") {
  if (!draft?.knowledge || !["grounded", "fallback"].includes(draft.knowledge.decision)) return false;
  const confirmedSignature = draft.knowledge.topicSignature || previousSignature;
  const currentSignature = knowledgeTopicSignatureForDraft(draft);
  if (!confirmedSignature || !currentSignature || confirmedSignature === currentSignature) return false;
  draft.knowledge.stale = true;
  return true;
}

function cloudKnowledgeEvidenceStatus(knowledge) {
  if (!knowledgeIsEnabled()) return {
    tone: "disabled",
    icon: "book-open",
    title: "云端知识库未启用",
    detail: "在 AI 配置中连接 Dify 后，可为当前改写加入云端资料。",
    action: "配置云端知识库",
    actionAttr: "data-open-knowledge-config",
  };
  if (knowledge?.stale && knowledge?.mode !== "local") return {
    tone: "stale",
    icon: "refresh-ccw",
    title: "云端依据需要重新选择",
    detail: `选题已变化，原来确认的 ${Number(knowledge.sources?.length || 0)} 条依据不会自动带入本次生成。`,
    action: "重新选择资料",
    actionAttr: "data-review-knowledge",
  };
  if (knowledge?.decision === "grounded" && knowledge?.mode !== "local") {
    const sources = knowledge.sources || [];
    return {
      tone: "grounded",
      icon: "badge-check",
      title: `已确认 ${sources.length} 条云端知识依据`,
      detail: sources.map((item) => `${item.sourceName || "云端资料"}${item.sourcePage ? ` · 第${item.sourcePage}页` : ""}`).join(" · ") || knowledge.query || "已选择云端资料",
      action: "查看与调整",
      actionAttr: "data-review-knowledge",
    };
  }
  if (knowledge?.decision === "fallback" && knowledge?.mode !== "local") return {
    tone: "fallback",
    icon: "sparkles",
    title: "本次不使用云端资料",
    detail: "模型可以创作通用内容，但不能虚构公司事实、数据或案例。",
    action: "重新选择",
    actionAttr: "data-review-knowledge",
  };
  return {
    tone: "pending",
    icon: "files",
    title: "还没有确认云端知识依据",
    detail: "从 Dify 已完成索引的文件中选择本次要参考的资料。",
    action: "选择云端资料",
    actionAttr: "data-review-knowledge",
  };
}

function localUploadQueueForScope(scope = "reference") {
  return scope === "reverse" ? state.reverseTopicUploadQueue : state.localUploadQueue;
}

function localUploadItemById(id) {
  for (const scope of ["reference", "reverse"]) {
    const item = localUploadQueueForScope(scope).find((candidate) => candidate.id === id);
    if (item) return { item, scope };
  }
  return { item: null, scope: "" };
}

function refreshLocalUploadSurfaces() {
  renderLocalUploadQueue();
  renderKnowledgeEvidence(currentDraft());
  renderReverseTopicSelection();
}

function addLocalReferenceFiles(fileList, scope = "reference") {
  syncLocalReferenceSelectionOwner(currentDraft());
  const files = Array.from(fileList || []).filter((file) => LOCAL_FILE_SUFFIX_RE.test(file.name));
  if (!files.length) return toast("请选择 PDF、DOCX、TXT 或 Markdown 文件", { kind: "error" });
  const queue = localUploadQueueForScope(scope);
  const cloudCount = scope === "reverse" ? reverseTopicCloudSelectedCount() : 0;
  const maxFiles = scope === "reverse" ? MAX_REVERSE_TOPIC_FILES : MAX_LOCAL_REFERENCE_FILES;
  const capacity = Math.max(0, maxFiles - queue.length - cloudCount);
  const existing = new Set(queue.map((item) => `${item.file.name}|${item.file.size}|${item.file.lastModified}`));
  const added = [];
  files.slice(0, capacity).forEach((file) => {
    if (file.size > MAX_LOCAL_REFERENCE_FILE_BYTES) return toast(`${file.name} 超过 100MB，无法作为本地资料`, { kind: "error" });
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (!existing.has(key)) {
      const isPdf = /\.pdf$/i.test(file.name);
      const item = { id: uid(scope === "reverse" ? "reverse-upload" : "local-upload"), file, pageMode: isPdf ? "range" : "all", pageRanges: [{ start: 1, end: isPdf ? 3 : null }], pageCount: null, pageInspecting: isPdf, pageInspectError: "" };
      queue.push(item);
      existing.add(key);
      added.push(item);
    }
  });
  if (scope !== "reverse") {
    state.localReferenceSelection = queue.map((item) => ({
      key: item.id,
      sourceId: "upload",
      sourceName: "本地上传",
      name: item.file.name,
      relativePath: item.file.name,
      size: item.file.size,
      lastModified: item.file.lastModified,
      pageRange: localReferencePageRange(item),
    }));
  }
  if (files.length > capacity) toast(scope === "reverse"
    ? `选题提炼只能选择 ${MAX_REVERSE_TOPIC_FILES} 份资料`
    : `本次最多选择 ${MAX_LOCAL_REFERENCE_FILES} 份本地资料`, { kind: "error" });
  refreshLocalUploadSurfaces();
  if (added.some((item) => item.pageInspecting)) inspectLocalReferenceFiles(added);
}

async function inspectLocalReferenceFiles(items) {
  const pdfItems = (items || []).filter((item) => item.pageInspecting);
  if (!pdfItems.length) return;
  const body = new FormData();
  pdfItems.forEach((item) => body.append("files", item.file, item.file.name));
  try {
    const response = await backendFetch("/api/knowledge/local-files/inspect", { method: "POST", body });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.ok === false) throw backendError(result, response.status, response);
    const inspected = Array.isArray(result.files) ? result.files : [];
    pdfItems.forEach((item, index) => {
      const meta = inspected[index] || {};
      item.pageCount = Number(meta.pageCount) > 0 ? Number(meta.pageCount) : null;
      item.pageInspectError = String(meta.error || "");
      item.pageInspecting = false;
      if (item.pageCount) item.pageRanges = [{ start: 1, end: Math.min(3, item.pageCount) }];
    });
  } catch (error) {
    pdfItems.forEach((item) => { item.pageInspecting = false; item.pageInspectError = error.message; });
  }
  refreshLocalUploadSurfaces();
}

function removeLocalReferenceFile(id) {
  const { scope } = localUploadItemById(id);
  if (scope === "reverse") state.reverseTopicUploadQueue = state.reverseTopicUploadQueue.filter((item) => item.id !== id);
  else {
    state.localUploadQueue = state.localUploadQueue.filter((item) => item.id !== id);
    state.localReferenceSelection = state.localReferenceSelection.filter((item) => item.key !== id);
    persistActiveLocalUploadQueue();
  }
  refreshLocalUploadSurfaces();
}

function updateLocalReferencePageMode(id, mode) {
  const { item } = localUploadItemById(id);
  if (!item || !/\.pdf$/i.test(item.file.name) || item.pageInspecting) return;
  item.pageMode = mode === "range" ? "range" : "all";
  if (item.pageMode === "range") {
    item.pageRanges = normalizeLocalPageRanges(item);
  }
}

function normalizeLocalPageRanges(item) {
  const max = item?.pageCount || 9999;
  const ranges = Array.isArray(item?.pageRanges) && item.pageRanges.length ? item.pageRanges : [{ start: 1, end: item?.pageCount || 1 }];
  return ranges.map((range) => {
    const start = Math.max(1, Math.min(max, Number(range?.start) || 1));
    const end = Math.max(start, Math.min(max, Number(range?.end) || start));
    return { start, end };
  });
}

function addLocalReferencePageRange(id) {
  const { item } = localUploadItemById(id);
  if (!item || item.pageMode !== "range") return;
  const ranges = normalizeLocalPageRanges(item);
  const last = ranges[ranges.length - 1];
  const max = item.pageCount || 9999;
  const next = last.end + 1;
  if (next > max) return toast("已到文件最后一页，不能再增加范围", { kind: "warning" });
  ranges.push({ start: next, end: next });
  item.pageRanges = ranges;
  const selected = state.localReferenceSelection.find((entry) => entry.key === id);
  if (selected) selected.pageRange = localReferencePageRange(item);
  refreshLocalUploadSurfaces();
}

function removeLocalReferencePageRange(id, index) {
  const { item } = localUploadItemById(id);
  if (!item || item.pageMode !== "range") return;
  const ranges = normalizeLocalPageRanges(item);
  if (ranges.length <= 1) return;
  ranges.splice(index, 1);
  item.pageRanges = ranges;
  const selected = state.localReferenceSelection.find((entry) => entry.key === id);
  if (selected) selected.pageRange = localReferencePageRange(item);
  refreshLocalUploadSurfaces();
}

function updateLocalReferencePageField(id, index, field, value) {
  const { item } = localUploadItemById(id);
  if (!item || item.pageMode !== "range") return "";
  const ranges = normalizeLocalPageRanges(item);
  const current = ranges[index] || ranges[0];
  const max = item.pageCount || 9999;
  const next = Math.max(1, Math.min(max, Number(value) || 1));
  if (field === "start") current.start = Math.min(next, current.end);
  if (field === "end") current.end = Math.max(current.start, next);
  item.pageRanges = ranges;
  const selected = state.localReferenceSelection.find((entry) => entry.key === id);
  if (selected) selected.pageRange = localReferencePageRange(item);
  return String(field === "start" ? current.start : current.end);
}

function localReferencePageRange(item) {
  if (!item || item.pageMode !== "range") return "";
  return normalizeLocalPageRanges(item).map(({ start, end }) => start === end ? String(start) : `${start}-${end}`).join(",");
}

function renderLocalUploadQueue(target = null, queue = state.localUploadQueue, scope = "reference") {
  const mount = target;
  if (!mount) return;
  const activeQueue = scope === "reverse" ? state.reverseTopicUploadQueue : queue;
  if (!activeQueue.length) {
    mount.innerHTML = `<button type="button" class="local-upload-empty" data-local-upload-dropzone="${scope}"><span class="local-upload-empty-icon"><i data-lucide="upload-cloud"></i></span><strong>拖入文件，或点击选择</strong><span>支持 PDF、DOCX、TXT、Markdown</span><span class="local-upload-empty-hint">PDF 会自动读取页数，再选择起止页码</span></button>`;
  } else {
    mount.innerHTML = activeQueue.map((item) => {
      const isPdf = /\.pdf$/i.test(item.file.name);
      const pageInfo = !isPdf ? "非 PDF 文件 · 使用全文" : item.pageInspecting ? "正在读取页数…" : item.pageCount ? `共 ${item.pageCount} 页` : (item.pageInspectError ? `页数读取失败：${item.pageInspectError}` : "页数未知");
      const pageControls = !isPdf
        ? `<span class="local-page-fulltext">全文</span>`
        : item.pageInspecting
          ? `<span class="local-page-loading">正在读取页数</span>`
          : `<div class="local-page-scope"><div class="local-page-scope-top"><span>参考范围</span><span class="local-page-count">${escapeHtml(pageInfo)}</span></div><div class="local-page-modes"><button type="button" class="${item.pageMode === "all" ? "active" : ""}" data-local-page-mode="all" data-local-upload-id="${escapeHtml(item.id)}">全文</button><button type="button" class="${item.pageMode === "range" ? "active" : ""}" data-local-page-mode="range" data-local-upload-id="${escapeHtml(item.id)}">指定页码</button></div>${item.pageMode === "range" ? `<div class="local-page-fields">${normalizeLocalPageRanges(item).map((range, rangeIndex, ranges) => `<div class="local-page-range-row"><label class="local-page-bound">从 <input type="number" min="1" max="${item.pageCount || 9999}" value="${range.start}" data-local-page-index="${rangeIndex}" data-local-page-start="${escapeHtml(item.id)}" /> 页</label><span class="local-page-between">到</span><label class="local-page-bound"><input type="number" min="${range.start}" max="${item.pageCount || 9999}" value="${range.end}" data-local-page-index="${rangeIndex}" data-local-page-end="${escapeHtml(item.id)}" /> 页</label><span class="local-range-actions"><span class="local-range-remove-slot">${ranges.length > 1 ? `<button type="button" class="local-range-action local-page-remove" data-local-page-remove="${escapeHtml(item.id)}" data-local-page-index="${rangeIndex}" title="删除这段范围" aria-label="删除这段范围">-</button>` : ""}</span><span class="local-range-add-slot">${rangeIndex === ranges.length - 1 ? `<button type="button" class="local-range-action local-page-add" data-local-page-add="${escapeHtml(item.id)}" title="增加一段页码范围" aria-label="增加一段页码范围">+</button>` : ""}</span></span></div>`).join("")}</div>` : ""}</div>`;
      return `<div class="local-reference-upload-row"><span class="local-file-icon"><i data-lucide="${isPdf ? "file-type-2" : "file-text"}"></i></span><div class="local-reference-upload-copy"><strong>${escapeHtml(item.file.name)}</strong><small>${escapeHtml(formatKnowledgeFileSize(item.file.size))} · ${escapeHtml(pageInfo)}</small></div><div class="local-page-control">${pageControls}</div><button type="button" class="local-file-remove-action" data-remove-local-upload="${escapeHtml(item.id)}" title="移除文件" aria-label="移除文件">×</button></div>`;
    }).join("");
  }
  refreshIcons();
}


// ----- js/knowledge/workspace.js -----
function renderKnowledgeEvidenceLocalFiles(draft) {
  const evidenceMount = $("#knowledgeEvidenceLocalFiles");
  renderLocalUploadQueue(evidenceMount || null);
  const selectedCount = state.localReferenceSelection.length;
  const confirmedCount = draft?.knowledge?.mode === "local" && !draft.knowledge.stale ? (draft.knowledge.fileReferences || []).length : 0;
  const count = $("#knowledgeEvidenceLocalCount");
  if (count) count.textContent = `已选 ${state.localUploadQueue.length || selectedCount || confirmedCount}/${MAX_LOCAL_REFERENCE_FILES}`;
  const confirm = $("#confirmEvidenceLocalFilesBtn");
  if (confirm) confirm.disabled = !draft || selectedCount === 0;
}

async function confirmEvidenceLocalFiles() {
  const draft = currentDraft();
  if (!draft) return toast("请先创建或选择一个改写任务");
  if (!state.localUploadQueue.length) return toast("请先选择至少一个本地文件");
  const button = $("#confirmEvidenceLocalFilesBtn");
  if (button) { button.disabled = true; button.dataset.busy = "true"; }
  try {
    const prepared = await prepareSelectedLocalReferences(draft, knowledgeQueryForDraft(draft));
    if (!prepared) return;
    state.knowledgeSource = "local";
    draft.knowledgeSource = "local";
    state.knowledgeEvidenceCollapsed = true;
    saveState();
    renderEditor();
    toast("本地参考资料已确认", { kind: "success" });
  } catch (error) {
    toast(`本地资料读取失败：${error.message}`, { kind: "error", duration: 5200 });
  } finally {
    if (button) { button.dataset.busy = "false"; button.disabled = state.localUploadQueue.length === 0; }
  }
}

function renderKnowledgeEvidence(draft) {
  const mount = $("#knowledgeEvidence");
  if (!mount) return;
  if (!draft || isReplicaDraft(draft)) {
    mount.className = "knowledge-evidence hidden";
    mount.innerHTML = "";
    return;
  }
  const pureAi = knowledgeChannelForDraft(draft) === "pure-ai";
  const knowledge = draft?.knowledge;
  const selectedSource = knowledgeSourceForDraft(draft);
  state.knowledgeSource = selectedSource;
  const cloud = cloudKnowledgeEvidenceStatus(knowledge);
  const localConfirmed = knowledge?.mode === "local" && !knowledge.stale;
  const localStale = knowledge?.mode === "local" && knowledge.stale;
  const cloudReferenceCount = knowledge?.mode !== "local" && knowledge?.decision === "grounded"
    ? (knowledge.sources || knowledge.selections || []).length
    : 0;
  const confirmedReferenceCount = knowledge?.mode === "local"
    ? (knowledge.fileReferences || []).length
    : cloudReferenceCount;
  const collapsed = state.knowledgeEvidenceCollapsed !== false;
  const cloudReviewContent = cloud.tone === "disabled"
    ? `<div class="knowledge-evidence-cloud-status disabled"><span class="knowledge-evidence-status-icon"><i data-lucide="book-open"></i></span><div><strong>${escapeHtml(cloud.title)}</strong><span>${escapeHtml(cloud.detail)}</span></div><button type="button" class="ghost-btn" data-open-knowledge-config>配置云端知识库</button></div>`
    : `<div class="knowledge-review-inline" aria-label="选择云端资料">
        <div class="knowledge-evidence-file-head"><strong>选择文件</strong><span class="knowledge-evidence-file-count" id="knowledgeEvidenceCloudCount">已选 0/0</span></div>
        <div class="knowledge-evidence-local-files knowledge-evidence-cloud-files" id="knowledgeEvidenceCloudFiles"></div>
        <div id="knowledgeReviewResultsInline" class="hidden" aria-hidden="true"></div>
        <div class="knowledge-review-actions knowledge-review-actions-inline"><button type="button" class="ghost-btn ${state.knowledgeReview?.allowFallback === false ? "hidden" : ""}" data-leave-cloud-knowledge>返回本地资料</button><button type="button" class="primary-btn" id="confirmKnowledgeBtnInline" data-confirm-knowledge-inline disabled>确认云端资料</button></div>
      </div>`;
  mount.className = `knowledge-evidence ${localConfirmed ? "is-grounded" : localStale ? "is-stale" : "is-pending"} ${pureAi ? "is-pure-ai" : ""} ${collapsed ? "is-collapsed" : "is-expanded"}`;
  mount.innerHTML = `<header class="knowledge-evidence-head">
    <button type="button" class="knowledge-evidence-toggle" data-toggle-knowledge-evidence aria-expanded="${!collapsed}" aria-controls="knowledgeEvidenceBody">
      <span class="knowledge-evidence-title"><span class="knowledge-evidence-icon"><i data-lucide="book-open-check"></i></span><span><strong>资料参照</strong><span class="knowledge-evidence-summary">${pureAi ? "当前使用纯 AI 通道" : confirmedReferenceCount ? `已确认 ${confirmedReferenceCount} 份参考资料` : "尚未确认参考资料"}</span></span></span>
    </button>
    <button type="button" class="knowledge-channel-switch ${pureAi ? "is-pure-ai" : ""}" data-toggle-knowledge-channel role="switch" aria-checked="${!pureAi}" aria-label="${pureAi ? "切换为资料参照通道" : "切换为纯 AI 通道"}" title="开启使用资料参照，关闭使用纯 AI">
      <span class="knowledge-channel-switch-track" aria-hidden="true"><span></span></span><span class="knowledge-channel-switch-label">${pureAi ? "纯 AI" : "资料通道"}</span>
    </button>
    <button type="button" class="knowledge-evidence-collapse-btn" data-toggle-knowledge-evidence aria-expanded="${!collapsed}" aria-controls="knowledgeEvidenceBody" aria-label="${collapsed ? "展开资料参照" : "收起资料参照"}" title="${collapsed ? "展开" : "收起"}"><i class="knowledge-evidence-chevron" data-lucide="chevron-down" aria-hidden="true"></i></button>
  </header>
  <div id="knowledgeEvidenceBody" class="knowledge-evidence-body" ${collapsed ? "hidden" : ""}>
  <div class="knowledge-evidence-source-tabs" role="tablist" aria-label="本次生成的知识来源">
    <button type="button" class="${selectedSource === "local" ? "active" : ""}" data-knowledge-source="local" role="tab" aria-selected="${selectedSource === "local"}"><i data-lucide="hard-drive"></i><span>本地文件</span><em>默认</em></button>
    <button type="button" class="${selectedSource === "cloud" ? "active" : ""} ${CLOUD_KNOWLEDGE_ENABLED ? "" : "hidden"}" data-knowledge-source="cloud" role="tab" aria-selected="${selectedSource === "cloud"}"><i data-lucide="cloud"></i><span>云端资料</span></button>
  </div>
  <div class="knowledge-evidence-panels">
    <section class="knowledge-evidence-panel knowledge-evidence-local-panel ${state.knowledgeSource === "local" ? "" : "hidden"}" data-knowledge-source-panel="local">
      <div class="knowledge-evidence-panel-head"><div><strong>本地参考资料</strong><span>上传需要的文件，并为 PDF 指定本次参考页码；仅用于本次生成。</span></div><span class="knowledge-evidence-count" id="knowledgeEvidenceLocalCount"></span></div>
      <div class="knowledge-evidence-local-browser"><div class="knowledge-evidence-local-files"><div class="knowledge-evidence-file-head"><strong>上传文件并指定范围</strong><button type="button" class="ghost-btn" data-knowledge-evidence-add-file><i data-lucide="file-plus-2"></i><span>选择文件</span></button></div><div id="knowledgeEvidenceLocalFiles"></div></div></div>
      <div class="knowledge-evidence-local-actions"><button type="button" class="primary-btn" id="confirmEvidenceLocalFilesBtn" data-confirm-evidence-local>确认本地资料</button></div>
    </section>
    <section class="knowledge-evidence-panel knowledge-evidence-cloud-panel ${selectedSource === "cloud" ? "" : "hidden"}" data-knowledge-source-panel="cloud">
      <div class="knowledge-evidence-panel-head"><div><strong>云端资料（需手动选择）</strong><span>勾选并确认的文件才会用于本次生成。</span></div><span class="knowledge-evidence-state ${cloud.tone}"><i data-lucide="${cloud.icon}"></i>${cloud.tone === "grounded" ? "已确认" : cloud.tone === "disabled" ? "未启用" : cloud.tone === "stale" ? "需重选" : "待确认"}</span></div>
      ${cloudReviewContent}
    </section>
  </div></div>`;
  renderKnowledgeEvidenceLocalFiles(draft);
  if (selectedSource === "cloud") renderKnowledgeCloudDocuments(draft);
  refreshIcons();
  if (selectedSource === "cloud" && knowledgeIsEnabled() && !state.knowledgeCloudDocuments.length && !state.knowledgeCloudLoading && !state.knowledgeCloudLoadAttempted) {
    loadKnowledgeCloudDocuments({ silent: true });
  }
}

function toggleKnowledgeChannel() {
  const draft = currentDraft();
  if (!draft) return;
  const next = knowledgeChannelForDraft(draft) === "pure-ai" ? "reference" : "pure-ai";
  draft.knowledgeChannel = next;
  if (next === "pure-ai") {
    draft.requestedPageCount = 0;
    delete draft.pageExpansionReview;
  }
  invalidateDraftExecutionPlan(draft, `knowledge-channel-${next}`);
  saveState();
  renderEditor();
  toast(next === "pure-ai" ? "已切换为纯 AI 通道" : "已切换为资料参照通道");
}

function renderKnowledgeReviewResults() {
  const review = state.knowledgeReview;
  const results = review.results || [];
  const selectionCount = Object.keys(review.selected || {}).length;
  const selectionNode = knowledgeReviewNode("knowledgeReviewSelectionCount");
  const confirmNode = knowledgeReviewNode("confirmKnowledgeBtn");
  if (selectionNode) selectionNode.textContent = `已选 ${selectionCount} 条`;
  if (confirmNode) confirmNode.disabled = !selectionCount;
  const resultsNode = knowledgeReviewNode("knowledgeReviewResults");
  if (!resultsNode) return;
  if (!results.length) {
    const emptyHint = review.allowFallback
      ? "调整检索词，或选择不用知识库继续搭建。"
      : "调整检索词，或改用本地参考资料；知识图文不能在无资料的情况下搭建。";
    resultsNode.innerHTML = review.loading ? "" : `<div class="knowledge-review-empty"><i data-lucide="file-search"></i><strong>没有可确认的资料</strong><span>${emptyHint}</span></div>`;
    refreshIcons();
    return;
  }
  const renderResult = (item) => {
    const selected = review.selected[item.chunkId];
    const percent = Math.max(0, Math.min(100, Math.round(Number(item.score || 0) * 100)));
    const metadataText = [item.subject, item.chapter, item.documentType, item.year].filter(Boolean).join(" · ");
    return `<article class="knowledge-result ${selected ? "selected" : ""} ${item.weak ? "is-weak" : ""}" data-knowledge-result="${escapeHtml(item.chunkId)}">
      <label class="knowledge-result-select"><input type="checkbox" data-knowledge-select="${escapeHtml(item.chunkId)}" ${selected ? "checked" : ""} /><span></span></label>
      <div class="knowledge-result-body"><div class="knowledge-result-meta"><strong>${escapeHtml(item.title || item.sourceName || "未命名资料")}</strong><span title="${escapeHtml(metadataText)}">${escapeHtml(item.sourceName)}${item.sourcePage ? ` · 第${item.sourcePage}页` : ""}${metadataText ? ` · ${escapeHtml(metadataText)}` : ""}</span><em>${percent}%${item.weak ? " · 低相关" : ""}</em></div>
      <textarea data-knowledge-override="${escapeHtml(item.chunkId)}" ${selected ? "" : "disabled"}>${escapeHtml(selected?.overrideText ?? item.content ?? "")}</textarea></div>
    </article>`;
  };
  const strongResults = results.filter((item) => !item.weak);
  const weakResults = results.filter((item) => item.weak);
  resultsNode.innerHTML = `${strongResults.map(renderResult).join("")}${weakResults.length ? `<details class="knowledge-weak-results"><summary>低相关结果 · ${weakResults.length} 条</summary>${weakResults.map(renderResult).join("")}</details>` : ""}`;
  refreshIcons();
}

async function runKnowledgeReviewSearch(options = {}) {
  const preserveExisting = options?.preserveExisting === true;
  const queryInput = knowledgeReviewNode("knowledgeReviewQuery");
  const query = compactKnowledgeSearchQuery(queryInput?.value || state.knowledgeReview?.query || "");
  if (!query) return toast("请输入要检索的主题");
  if (queryInput) queryInput.value = query;
  const review = state.knowledgeReview;
  review.query = query;
  review.loading = true;
  review.results = [];
  const pendingSelections = preserveExisting ? [...(review.pendingSelections || [])] : [];
  review.selected = {};
  const statusNode = knowledgeReviewNode("knowledgeReviewStatus");
  if (statusNode) statusNode.textContent = "正在生成查询向量并检索资料...";
  renderKnowledgeReviewResults();
  try {
    const data = await postBackend("/api/knowledge/search", {
      query,
      limit: 20,
      preferredDocumentIds: review.preferredDocumentIds || [],
    });
    review.results = data.results || [];
    review.needs = data.needs || [];
    review.coverage = data.coverage || [];
    review.metadataFilters = data.metadataFilters || {};
    if (pendingSelections.length) {
      const selectionById = new Map(pendingSelections.map((item) => [item.chunkId, item]));
      review.results.forEach((item) => {
        const saved = selectionById.get(item.chunkId);
        if (saved) review.selected[item.chunkId] = { ...item, overrideText: saved.overrideText || item.content || "" };
      });
    }
    review.pendingSelections = [];
    const strongCount = review.results.filter((item) => !item.weak).length;
    const coveredNeeds = review.coverage.filter((item) => Number(item.matchCount || 0) > 0).length;
    if (statusNode) statusNode.textContent = review.results.length
      ? `拆分 ${review.needs.length || 1} 个知识问题，${coveredNeeds || review.needs.length || 1} 个已命中；合并 ${review.results.length} 条资料，其中 ${strongCount} 条达到相关度门槛`
      : review.allowFallback
        ? "没有找到相关资料，可以调整检索词或不用知识库继续"
        : "没有找到相关资料，请调整检索词或改用本地参考资料";
  } catch (error) {
    if (statusNode) statusNode.textContent = `检索失败：${error.message}`;
    toast(`知识库检索失败：${error.message}`, { kind: "error" });
  } finally {
    review.loading = false;
    renderKnowledgeReviewResults();
  }
}

function openKnowledgeReview(draft = currentDraft(), continuation = null, { allowFallback = true, preserveSource = false } = {}) {
  if (!draft) return;
  const confirmedDocuments = new Map();
  (draft.knowledge?.selections || []).forEach((item) => {
    const id = String(item?.documentId || "");
    if (id) confirmedDocuments.set(id, { ...item, documentId: id });
  });
  state.knowledgeReview = {
    query: draft.knowledge?.stale ? knowledgeQueryForDraft(draft) : (draft.knowledge?.query || knowledgeQueryForDraft(draft)),
    documents: state.knowledgeCloudDocuments || [],
    results: [],
    selected: Object.fromEntries(confirmedDocuments),
    pendingSelections: [],
    preferredDocumentIds: knowledgePreferredDocumentIdsForDraft(draft),
    needs: [],
    coverage: [],
    metadataFilters: {},
    continuation,
    allowFallback,
    loading: false,
  };
  if (!preserveSource) state.knowledgeSource = "cloud";
  state.knowledgeEvidenceCollapsed = false;
  renderKnowledgeEvidence(draft);
  if (!state.knowledgeCloudDocuments?.length) loadKnowledgeCloudDocuments({ silent: true });
}

function closeKnowledgeReview() {
  state.knowledgeReview.continuation = null;
}

function leaveCloudKnowledgeSource() {
  const draft = currentDraft();
  state.knowledgeSource = "local";
  if (draft) draft.knowledgeSource = "local";
  state.knowledgeReview.continuation = null;
  saveState();
  renderKnowledgeEvidence(draft);
  toast("已返回本地资料，本次不会使用云端依据");
}

function confirmKnowledgeDecision(decision) {
  const draft = currentDraft();
  if (!draft) return closeKnowledgeReview();
  const review = state.knowledgeReview;
  if (decision === "fallback" && review.allowFallback === false) {
    toast("知识图文必须先确认可用参考资料", { kind: "error" });
    return;
  }
  const selectedItems = Object.values(review.selected || {});
  if (decision === "grounded" && !selectedItems.length) return;
  draft.knowledge = {
    decision,
    mode: "cloud",
    query: review.query || (knowledgeReviewNode("knowledgeReviewQuery")?.value || "").trim(),
    selections: decision === "grounded" ? selectedItems.map((item) => ({
      chunkId: "",
      documentId: item.documentId || item.id || "",
      sourceName: item.sourceName || item.filename || "",
      sourcePage: Number(item.sourcePage || 0),
      title: item.title || item.filename || "",
      score: Number(item.score || 0),
      overrideText: "",
      subject: item.subject || "",
      chapter: item.chapter || "",
      documentType: item.documentType || "",
      year: item.year || "",
      matchedNeeds: Array.isArray(item.matchedNeeds) ? item.matchedNeeds.slice(0, 6) : [],
    })) : [],
    sources: decision === "grounded" ? selectedItems.map((item) => ({
      chunkId: "",
      documentId: item.documentId || item.id || "",
      sourceName: item.sourceName || item.filename || "",
      sourcePage: Number(item.sourcePage || 0),
      title: item.title || item.filename || "",
      score: Number(item.score || 0),
      subject: item.subject || "",
      chapter: item.chapter || "",
      documentType: item.documentType || "",
      year: item.year || "",
    })) : [],
    needs: Array.isArray(review.needs) ? [...review.needs] : [],
    metadataFilters: { ...(review.metadataFilters || {}) },
    topicSignature: knowledgeTopicSignatureForDraft(draft),
    stale: false,
    confirmedAt: new Date().toISOString(),
  };
  state.knowledgeSource = "cloud";
  draft.knowledgeSource = "cloud";
  state.knowledgeEvidenceCollapsed = true;
  const continuation = review.continuation;
  saveState();
  hideModal($("#knowledgeReviewModal"));
  renderEditor();
  state.knowledgeReview.continuation = null;
  if (typeof continuation === "function") window.setTimeout(continuation, 0);
}

function requireKnowledgeDecision(draft, continuation) {
  if (isReplicaDraft(draft)) return false;
  if (knowledgeChannelForDraft(draft) === "pure-ai") return false;
  if (["grounded", "fallback"].includes(draft?.knowledge?.decision) && !draft?.knowledge?.stale && knowledgeMatchesSelectedSource(draft)
    && (draft.knowledge.decision !== "grounded" || cloudKnowledgeSelectionsReady(draft.knowledge))) return false;
  if (!knowledgeIsEnabled()) return false;
  if (knowledgeSourceForDraft(draft) !== "cloud") {
    state.knowledgeEvidenceCollapsed = false;
    renderKnowledgeEvidence(draft);
    toast(knowledgeLocalOnlyHint(), { kind: "warning", duration: 6200 });
    return true;
  }
  openKnowledgeReview(draft, continuation);
  return true;
}

function knowledgeStatusLabel(status) {
  return {
    ready: ["可检索", "success"], indexing: ["处理中", "processing"], error: ["异常", "error"], deleting: ["删除中", "processing"],
  }[status] || [status || "未知", "neutral"];
}

function formatKnowledgeUploadedAt(value) {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime())
    ? String(value || "-")
    : date.toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
}

function knowledgeFolderOptions(selectedId = "", excludedId = "") {
  const folders = (state.knowledgeFolders || []).filter((item) => item.id !== excludedId);
  return `<option value="">根目录</option>${folders.map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === selectedId ? "selected" : ""}>${escapeHtml(item.path)}</option>`).join("")}`;
}

function knowledgeFolderDocumentTotals(folders = []) {
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));
  const childrenByParentId = new Map();
  folders.forEach((folder) => {
    if (!folder.parent_id || !foldersById.has(folder.parent_id)) return;
    const children = childrenByParentId.get(folder.parent_id) || [];
    children.push(folder.id);
    childrenByParentId.set(folder.parent_id, children);
  });
  const totals = new Map();
  const visiting = new Set();
  const totalFor = (folderId) => {
    if (totals.has(folderId)) return totals.get(folderId);
    if (visiting.has(folderId)) return 0;
    visiting.add(folderId);
    const folder = foldersById.get(folderId);
    const total = Number(folder?.document_count || 0)
      + (childrenByParentId.get(folderId) || []).reduce((sum, childId) => sum + totalFor(childId), 0);
    visiting.delete(folderId);
    totals.set(folderId, total);
    return total;
  };
  folders.forEach((folder) => totalFor(folder.id));
  return totals;
}

function renderKnowledgeFolderTree() {
  const folders = state.knowledgeFolders || [];
  const selectedPath = $("#knowledgeFolderFilter")?.value || "";
  const folderById = new Map(folders.map((folder) => [folder.id, folder]));
  const parentIds = new Set(folders.map((folder) => folder.parent_id).filter(Boolean));
  const validIds = new Set(folders.map((folder) => folder.id));
  const collapsedIds = new Set((state.knowledgeCollapsedFolderIds || []).filter((id) => validIds.has(id) && parentIds.has(id)));
  const documentTotals = knowledgeFolderDocumentTotals(folders);
  state.knowledgeCollapsedFolderIds = [...collapsedIds];
  const isHidden = (folder) => {
    let parentId = folder.parent_id;
    while (parentId) {
      if (collapsedIds.has(parentId)) return true;
      parentId = folderById.get(parentId)?.parent_id || "";
    }
    return false;
  };
  $("#knowledgeFolderTree").innerHTML = folders.filter((folder) => !isHidden(folder)).map((folder) => {
    const hasChildren = parentIds.has(folder.id);
    const collapsed = collapsedIds.has(folder.id);
    return `
    <div class="knowledge-folder-row ${folder.path === selectedPath ? "active" : ""}" style="--folder-depth:${Math.max(0, Number(folder.depth || 1) - 1)}">
      ${hasChildren
        ? `<button type="button" class="knowledge-folder-toggle" data-toggle-knowledge-folder="${escapeHtml(folder.id)}" aria-expanded="${String(!collapsed)}" title="${collapsed ? "展开子目录" : "折叠子目录"}" aria-label="${collapsed ? "展开" : "折叠"}${escapeHtml(folder.name)}"><i data-lucide="chevron-${collapsed ? "right" : "down"}"></i></button>`
        : `<span class="knowledge-folder-toggle-spacer" aria-hidden="true"></span>`}
      <button type="button" class="knowledge-folder-select" data-knowledge-folder-filter="${escapeHtml(folder.path)}" title="${escapeHtml(folder.path)}"><i data-lucide="${hasChildren && !collapsed ? "folder-open" : "folder"}"></i><span>${escapeHtml(folder.name)}</span><em>${Number(documentTotals.get(folder.id) || 0)}</em></button>
      <div class="knowledge-folder-actions"><button type="button" class="icon-btn" data-edit-knowledge-folder="${escapeHtml(folder.id)}" title="编辑目录" aria-label="编辑目录"><i data-lucide="pencil"></i></button><button type="button" class="icon-btn danger-btn" data-delete-knowledge-folder="${escapeHtml(folder.id)}" title="删除该目录及其子目录" aria-label="删除该目录及其子目录"><i data-lucide="trash-2"></i></button></div>
    </div>`;
  }).join("") || `<p class="knowledge-folder-empty">还没有目录</p>`;
}

function toggleKnowledgeFolder(folderId) {
  const collapsedIds = new Set(state.knowledgeCollapsedFolderIds || []);
  if (collapsedIds.has(folderId)) collapsedIds.delete(folderId);
  else collapsedIds.add(folderId);
  state.knowledgeCollapsedFolderIds = [...collapsedIds];
  saveState();
  renderKnowledgeFolderTree();
  refreshIcons();
}

function renderKnowledgeWorkspace() {
  const status = state.knowledgeStatus || {};
  const gate = knowledgeCloudGate(status);
  // unavailable 只留给「已配置但当前账号读不到 dataset」：服务端有意合并"还没建/无权"，
  // 面板就照实说"尚未就绪或无权限"。未配置走 gate，那句话能让人直接去改配置。
  const unavailable = status.unavailable === true;
  $("#knowledgeDocumentCount").textContent = Number(status.documents || 0);
  $("#knowledgeChunkCount").textContent = Number(status.chunks || 0);
  $("#knowledgeConnectionTitle").textContent = gate.title;
  $("#knowledgeConnectionDetail").textContent = unavailable
    ? gate.hint
    : status.configured
    ? `Dify · ${status.datasetId || "未配置 Dataset ID"}`
    : gate.hint;
  $("#knowledgeSecurityWarning").classList.toggle("hidden", !status.insecure);
  const managedExternally = status.managedExternally === true;
  const uploadReady = Boolean(status.enabled && status.configured && status.initialized && !status.requiresRebuild && !managedExternally);
  ["knowledgeFileInput", "knowledgeFolderInput"].forEach((id) => {
    const input = $(`#${id}`);
    if (!input) return;
    input.disabled = !uploadReady;
    const control = input.closest("label");
    control?.classList.toggle("is-disabled", !uploadReady);
    control?.setAttribute("aria-disabled", String(!uploadReady));
    control?.setAttribute("title", uploadReady ? "" : "请先完成知识库连接测试和初始化");
  });
  const openUploadButton = $("#openKnowledgeUploadBtn");
  if (openUploadButton) {
    openUploadButton.disabled = !uploadReady;
    openUploadButton.title = uploadReady ? "" : "请先完成 Dify 知识库连接配置";
  }
  $("#newKnowledgeNoteBtn").disabled = !uploadReady;
  $("#newKnowledgeNoteBtn").classList.toggle("hidden", !status.configured);
  ["#newKnowledgeFolderBtn", "#syncKnowledgeFoldersBtn", "#reindexAllKnowledgeBtn"].forEach((selector) => {
    const button = $(selector);
    if (button) button.disabled = selector === "#reindexAllKnowledgeBtn" && knowledgeReindexController
      ? true
      : managedExternally || !status.configured;
  });
  const cancelReindex = $("#cancelReindexAllKnowledgeBtn");
  if (cancelReindex) {
    cancelReindex.classList.toggle("hidden", !knowledgeReindexController);
    cancelReindex.disabled = !knowledgeReindexController;
  }
  const documents = state.knowledgeDocuments || [];
  const folderSelect = $("#knowledgeFolderFilter");
  if (folderSelect) {
    const selectedFolder = folderSelect.value;
    const folders = state.knowledgeFolders || [];
    const paths = folders.map((item) => item.path);
    folderSelect.innerHTML = `<option value="">全部目录</option>${folders.map((item) => `<option value="${escapeHtml(item.path)}">${escapeHtml(item.path)}</option>`).join("")}`;
    folderSelect.value = paths.includes(selectedFolder) ? selectedFolder : "";
  }
  renderKnowledgeFolderTree();
  const categorySelect = $("#knowledgeCategoryFilter");
  if (categorySelect) {
    const selectedCategory = categorySelect.value;
    const categories = [...new Set(documents.map((item) => item.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "zh-CN"));
    categorySelect.innerHTML = `<option value="">全部分类</option>${categories.map((item) => `<option value="${escapeHtml(item)}">${escapeHtml(item)}</option>`).join("")}`;
    categorySelect.value = categories.includes(selectedCategory) ? selectedCategory : "";
  }
  $("#reindexAllKnowledgeBtn").disabled = Boolean(knowledgeReindexController) || !status.configured;
  $("#reindexAllKnowledgeBtn").classList.toggle("hidden", !status.configured);
  $("#knowledgeEmpty").classList.toggle("hidden", documents.length > 0);
  $("#knowledgeDocumentRows").innerHTML = documents.map((document) => {
    const [label, tone] = knowledgeStatusLabel(document.status);
    const tags = [document.category, document.tags].filter(Boolean).join(" · ") || "未分类";
    const escapedError = escapeHtml(document.error || "");
    return `<tr>
      <td data-label="资料"><span class="knowledge-document-kind"><i data-lucide="${document.kind === "note" ? "notebook-pen" : "file-text"}"></i></span><div><strong>${escapeHtml(document.filename)}</strong><small title="${escapeHtml(document.folder_path || "根目录")}">${escapeHtml(document.folder_path || "根目录")}${Number(document.location_count || 1) > 1 ? ` · ${Number(document.location_count)} 个目录共享` : ""} · 上传于 ${escapeHtml(formatKnowledgeUploadedAt(document.created_at))}</small></div></td>
      <td data-label="分类与标签"><span>${escapeHtml(tags)}</span></td><td data-label="版本">v${Number(document.version || 1)}</td><td data-label="字数">${Number(document.word_count || 0).toLocaleString("zh-CN")}</td>
      <td data-label="状态"><span class="knowledge-state ${tone}" ${escapedError ? `title="${escapedError}"` : ""}>${label}</span>${document.directory_sync_status === "error" ? `<small class="knowledge-directory-error" title="${escapeHtml(document.directory_sync_error || "目录同步失败")}">目录未同步</small>` : ""}</td>
      <td data-label="操作"><div class="knowledge-row-actions"><button type="button" class="icon-btn" data-knowledge-chunks="${escapeHtml(document.id)}" title="查看 Dify 片段" aria-label="查看 Dify 片段"><i data-lucide="list-tree"></i></button>${document.kind === "note" ? `<button type="button" class="icon-btn" data-edit-knowledge-note="${escapeHtml(document.id)}" title="编辑知识卡" aria-label="编辑知识卡"><i data-lucide="pencil"></i></button>` : ""}<button type="button" class="icon-btn" data-move-knowledge-document="${escapeHtml(document.id)}" data-move-knowledge-folder="${escapeHtml(document.folder_id || "")}" title="移动当前目录映射" aria-label="移动当前目录映射"><i data-lucide="folder-input"></i></button><button type="button" class="icon-btn danger-btn" data-delete-knowledge="${escapeHtml(document.id)}" title="删除资料" aria-label="删除资料"><i data-lucide="trash-2"></i></button></div></td>
    </tr>`;
  }).join("");
  refreshIcons();
}

async function loadKnowledgeWorkspace() {
  try {
    const params = new URLSearchParams();
    const query = $("#knowledgeSearchInput")?.value.trim();
    const statusFilter = $("#knowledgeStatusFilter")?.value;
    const categoryFilter = $("#knowledgeCategoryFilter")?.value;
    const folderFilter = $("#knowledgeFolderFilter")?.value;
    if (query) params.set("query", query);
    if (statusFilter) params.set("status", statusFilter);
    if (categoryFilter) params.set("category", categoryFilter);
    if (folderFilter) params.set("folder", folderFilter);
    const statusData = await getBackend("/api/knowledge/status");
    state.knowledgeStatus = statusData.status || {};
    const gate = knowledgeCloudGate(state.knowledgeStatus);
    if (!gate.ready) {
      // 未配置/未启用时知识库没有 dataset 投影，再去拉列表只会拿到 resource_not_found 的
      // 模糊话术；直接把空态与可照做的原因摆出来。
      state.knowledgeDocuments = [];
      state.knowledgeFolders = [];
      state.knowledgeStatus.documents = 0;
      state.knowledgeStatus.chunks = 0;
      renderKnowledgeWorkspace();
      return;
    }
    const documentsData = await getBackend(`/api/knowledge/documents${params.toString() ? `?${params}` : ""}`).catch((error) => ({ loadError: error }));
    if (!documentsData.loadError) {
      state.knowledgeDocuments = documentsData.documents || [];
      state.knowledgeFolders = documentsData.folders || [];
    }
    const uniqueDocuments = new Map(state.knowledgeDocuments.map((item) => [item.id, item]));
    state.knowledgeStatus.documents = uniqueDocuments.size;
    state.knowledgeStatus.chunks = [...uniqueDocuments.values()].reduce((total, item) => total + Number(item.word_count || 0), 0);
    renderKnowledgeWorkspace();
    if (documentsData.loadError) toast(`Dify 资料列表暂时不可用：${documentsData.loadError.message}`, { kind: "error" });
  } catch (error) {
    // 未登录（或登录已失效）时整条知识库链都会 401：这既不是"加载失败"，也不是"没配置/
    // 没权限"，别让用户去查一个不存在的配置问题（见 auth/login-gate.js）。
    if (Number(error?.status) === 401) {
      state.knowledgeStatus = { unavailable: true, signedOut: true };
      state.knowledgeDocuments = [];
      state.knowledgeFolders = [];
      renderKnowledgeWorkspace();
      return;
    }
    // 到了这里说明知识库「已配置」而 dataset 读不到：后端对「资源不在 canonical 投影里」
    // 只回 resource_not_found（不存在/无权故意不区分）。这既不是故障也不是用户操作错，
    // 按"尚未就绪或无权"呈现空态，而不是弹出上游英文常量。
    if (error?.errorCode === "resource_not_found") {
      state.knowledgeStatus = { unavailable: true };
      state.knowledgeDocuments = [];
      state.knowledgeFolders = [];
      renderKnowledgeWorkspace();
      return;
    }
    toast(`知识库加载失败：${error.message}`, { kind: "error" });
  }
}

function formatKnowledgeFileSize(bytes) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function renderKnowledgeUploadQueue() {
  const queue = state.knowledgeUploadQueue || [];
  $("#knowledgeUploadQueueSummary").textContent = `${queue.length} 个文件 · ${formatKnowledgeFileSize(queue.reduce((total, item) => total + Number(item.file?.size || 0), 0))}`;
  $("#knowledgeUploadQueue").innerHTML = queue.length
    ? queue.map((item) => `<div class="knowledge-upload-queue-item"><i data-lucide="file-text"></i><div><strong>${escapeHtml(item.name)}</strong><small title="${escapeHtml(item.relativePath)}">${escapeHtml(item.relativePath)}</small></div><span>${escapeHtml(formatKnowledgeFileSize(item.file?.size))}</span><button type="button" class="icon-btn" data-remove-knowledge-upload="${escapeHtml(item.id)}" title="移除" aria-label="移除 ${escapeHtml(item.name)}"><i data-lucide="x"></i></button></div>`).join("")
    : `<div class="knowledge-upload-queue-empty">选择文件或文件夹后，将在这里显示上传队列</div>`;
  $("#startKnowledgeUploadBtn").disabled = !queue.length;
  $("#clearKnowledgeUploadBtn").disabled = !queue.length;
  refreshIcons();
}

function addKnowledgeUploadFiles(files) {
  const selected = [...(files || [])];
  const supported = selected.filter((file) => /\.(pdf|docx|txt|md|markdown)$/i.test(file.name || ""));
  const withinLimit = supported.filter((file) => Number(file.size || 0) <= 20 * 1024 * 1024);
  const unsupportedCount = selected.length - supported.length;
  const oversizedCount = supported.length - withinLimit.length;
  if (unsupportedCount) toast(`已跳过 ${unsupportedCount} 个不支持的文件`);
  if (oversizedCount) toast(`已跳过 ${oversizedCount} 个超过 20MB 的文件`, { kind: "error" });
  const existing = new Set((state.knowledgeUploadQueue || []).map((item) => item.key));
  const additions = [];
  let queuedBytes = (state.knowledgeUploadQueue || []).reduce((total, item) => total + Number(item.file?.size || 0), 0);
  let batchOversizedCount = 0;
  withinLimit.forEach((file) => {
    const relativePath = String(file.webkitRelativePath || file.name || "").replace(/\\/g, "/");
    const key = `${relativePath.toLowerCase()}::${file.size}::${file.lastModified}`;
    if (existing.has(key) || state.knowledgeUploadQueue.length + additions.length >= 50) return;
    if (queuedBytes + Number(file.size || 0) > 100 * 1024 * 1024) { batchOversizedCount += 1; return; }
    existing.add(key);
    queuedBytes += Number(file.size || 0);
    additions.push({ id: uid("knowledge-upload"), key, file, name: file.name, relativePath });
  });
  state.knowledgeUploadQueue = [...(state.knowledgeUploadQueue || []), ...additions];
  if (withinLimit.length > additions.length && state.knowledgeUploadQueue.length >= 50) toast("单次最多加入 50 个文件");
  if (batchOversizedCount) toast(`单次上传总量上限为 100MB，已跳过 ${batchOversizedCount} 个文件`);
  renderKnowledgeUploadQueue();
  $("#knowledgeFileInput").value = "";
  $("#knowledgeFolderInput").value = "";
}

function openKnowledgeUploadModal() {
  // 未登录时状态接口会 401、状态对象必然读不到，"去配置 Dify 连接"就是把用户指错方向
  // （见 auth/login-gate.js）。
  if (typeof loginGateBlocksFeature === "function" && loginGateBlocksFeature("上传云端资料")) return;
  const status = state.knowledgeStatus || {};
  if (!(status.enabled && status.configured && status.initialized) || status.requiresRebuild) {
    return toast("请先在 AI 配置中完成 Dify 知识库连接", { kind: "error" });
  }
  $("#knowledgeUploadTarget").innerHTML = knowledgeFolderOptions(state.knowledgeUploadOptions?.targetFolderId || "");
  $("#knowledgeUploadConflict").value = state.knowledgeUploadOptions?.conflictPolicy || "version";
  $("#knowledgeUploadSubject").value = state.knowledgeUploadOptions?.metadata?.subject || "";
  $("#knowledgeUploadChapter").value = state.knowledgeUploadOptions?.metadata?.chapter || "";
  $("#knowledgeUploadDocumentType").value = state.knowledgeUploadOptions?.metadata?.documentType || "";
  $("#knowledgeUploadYear").value = state.knowledgeUploadOptions?.metadata?.year || "";
  renderKnowledgeUploadQueue();
  showModal($("#knowledgeUploadModal"));
}

function clearKnowledgeUploadQueue() {
  state.knowledgeUploadQueue = [];
  renderKnowledgeUploadQueue();
}

function renderKnowledgeUploadProgress(detail = {}) {
  const progress = { ...(state.knowledgeUploadProgress || {}), ...(detail || {}) };
  state.knowledgeUploadProgress = progress;
  const total = Number(progress.total || 0);
  const completed = Number(progress.completed || 0);
  const skipped = Number(progress.skipped || 0);
  const failed = Number(progress.failed || 0);
  const done = completed + skipped + failed;
  const percent = Math.max(0, Math.min(100, Number(progress.percent ?? (total ? Math.round(done / total * 100) : 0))));
  const finished = progress.stage === "completed" || progress.stage === "failed";
  $("#knowledgeUploadProgress").classList.remove("hidden");
  $("#knowledgeUploadProgressTitle").textContent = finished ? "批量上传已完成" : "正在批量上传到 Dify";
  $("#knowledgeUploadProgressSummary").textContent = `${done} / ${total} · 成功 ${completed}${skipped ? ` · 跳过 ${skipped}` : ""}${failed ? ` · 失败 ${failed}` : ""}`;
  $("#knowledgeUploadProgressPercent").textContent = `${percent}%`;
  $("#knowledgeUploadProgressBar").style.width = `${percent}%`;
  const iconByStatus = { completed: "circle-check", failed: "circle-x", skipped: "circle-minus", uploading: "loader-circle", queued: "clock-3" };
  $("#knowledgeUploadProgressList").innerHTML = (progress.items || []).map((item) => `<div class="knowledge-upload-progress-item ${escapeHtml(item.status || "queued")}"><i data-lucide="${iconByStatus[item.status] || "clock-3"}"></i><strong title="${escapeHtml(item.relativePath || item.name)}">${escapeHtml(item.relativePath || item.name)}</strong><span title="${escapeHtml(item.message || "")}">${escapeHtml(item.message || "")}</span></div>`).join("");
  $("#retryKnowledgeUploadBtn").classList.toggle("hidden", !finished || !state.knowledgeUploadFailures.length);
  refreshIcons();
}

async function startKnowledgeBatchUpload(uploadQueue = null, savedOptions = null) {
  const queue = Array.isArray(uploadQueue) ? uploadQueue : [...(state.knowledgeUploadQueue || [])];
  if (!queue.length) return;
  const options = savedOptions || {
    targetFolderId: $("#knowledgeUploadTarget").value,
    conflictPolicy: $("#knowledgeUploadConflict").value,
    metadata: {
      subject: $("#knowledgeUploadSubject").value.trim(),
      chapter: $("#knowledgeUploadChapter").value.trim(),
      documentType: $("#knowledgeUploadDocumentType").value.trim(),
      year: $("#knowledgeUploadYear").value.trim(),
    },
  };
  state.knowledgeUploadOptions = { ...options };
  state.knowledgeUploadFailures = [];
  const initialItems = queue.map((item, index) => ({ index, name: item.name, relativePath: item.relativePath, status: "queued", message: "等待上传" }));
  renderKnowledgeUploadProgress({ stage: "uploading", total: queue.length, completed: 0, skipped: 0, failed: 0, percent: 0, items: initialItems });
  hideModal($("#knowledgeUploadModal"));
  const done = setButtonLoading($("#startKnowledgeUploadBtn"), "上传中...");
  try {
    const body = new FormData();
    queue.forEach((item) => body.append("files", item.file, item.name));
    body.append("relativePaths", JSON.stringify(queue.map((item) => item.relativePath)));
    body.append("targetFolderId", options.targetFolderId || "");
    body.append("conflictPolicy", options.conflictPolicy || "version");
    body.append("metadata", JSON.stringify(options.metadata || {}));
    const response = await backendFetch("/api/knowledge/uploads", { method: "POST", body });
    const data = await response.json().catch(() => ({}));
    // 走错误边界：401（没登录 / 会话失效）与 403 的处置要和其它请求一致，话术也才分得清
    // "没登录"和"登录失效"（见 core/client.js 的 backendErrorMessage）。
    if (!response.ok || data.ok === false) throw backendError(data, response.status, response);
    const job = data.async && data.statusUrl
      ? await waitForBackendJob(data.statusUrl, { onProgress: (progress) => renderKnowledgeUploadProgress(progress) })
      : data;
    const result = job.result || job;
    state.knowledgeUploadFailures = (result.items || [])
      .filter((item) => item.status === "failed" && queue[item.index])
      .map((item) => queue[item.index]);
    renderKnowledgeUploadProgress({ ...result, stage: "completed", percent: 100 });
    if (!uploadQueue) clearKnowledgeUploadQueue();
    await loadKnowledgeWorkspace();
    toast(result.failed ? `上传完成：成功 ${result.completed || 0}，失败 ${result.failed}` : `已提交 ${result.completed || 0} 份资料到 Dify` , { kind: result.failed ? "error" : "success", duration: 6000 });
  } catch (error) {
    state.knowledgeUploadFailures = queue;
    renderKnowledgeUploadProgress({
      stage: "failed", total: queue.length, completed: 0, failed: queue.length, percent: 100,
      items: queue.map((item, index) => ({ index, name: item.name, relativePath: item.relativePath, status: "failed", message: error.message })),
    });
    toast(`批量上传失败：${error.message}`, { kind: "error", duration: 7000 });
  } finally {
    done();
  }
}

function retryKnowledgeUploadFailures() {
  const failures = [...(state.knowledgeUploadFailures || [])];
  if (failures.length) startKnowledgeBatchUpload(failures, state.knowledgeUploadOptions);
}

function openKnowledgeNote(document = null) {
  $("#knowledgeNoteId").value = document?.id || "";
  $("#knowledgeNoteTitle").textContent = document ? "编辑知识卡" : "新建知识卡";
  $("#knowledgeNoteName").value = document?.filename || "";
  $("#knowledgeNoteCategory").value = document?.category || "";
  $("#knowledgeNoteFolder").value = document?.folder_path || "";
  $("#knowledgeNoteTags").value = document?.tags || "";
  $("#knowledgeNoteSubject").value = document?.subject || "";
  $("#knowledgeNoteChapter").value = document?.chapter || "";
  $("#knowledgeNoteDocumentType").value = document?.documentType || "";
  $("#knowledgeNoteYear").value = document?.year || "";
  $("#knowledgeNoteContent").value = document?.note_content || "";
  showModal($("#knowledgeNoteModal"));
}

async function saveKnowledgeNote(event) {
  event.preventDefault();
  const id = $("#knowledgeNoteId").value;
  const payload = {
    title: $("#knowledgeNoteName").value.trim(), content: $("#knowledgeNoteContent").value.trim(),
    category: $("#knowledgeNoteCategory").value.trim(), tags: $("#knowledgeNoteTags").value.trim(),
    folderPath: $("#knowledgeNoteFolder").value.trim(),
    metadata: {
      subject: $("#knowledgeNoteSubject").value.trim(),
      chapter: $("#knowledgeNoteChapter").value.trim(),
      documentType: $("#knowledgeNoteDocumentType").value.trim(),
      year: $("#knowledgeNoteYear").value.trim(),
    },
  };
  try {
    if (id) await putBackend(`/api/knowledge/notes/${id}`, payload);
    else await postBackend("/api/knowledge/notes", payload);
    hideModal($("#knowledgeNoteModal"));
    await loadKnowledgeWorkspace();
    toast(id ? "知识卡已更新并重建索引" : "知识卡已创建并完成索引");
  } catch (error) {
    toast(`知识卡保存失败：${error.message}`, { kind: "error" });
  }
}

async function showKnowledgeChunks(documentId) {
  try {
    const data = await getBackend(`/api/knowledge/documents/${encodeURIComponent(documentId)}/chunks`);
    $("#knowledgeChunksTitle").textContent = data.document?.filename || "资料片段";
    $("#knowledgeChunkList").innerHTML = (data.chunks || []).map((chunk, index) => `<article><span>${chunk.source_page ? `第 ${chunk.source_page} 页` : `片段 ${index + 1}`}</span><strong>${escapeHtml(chunk.title || data.document?.filename || "")}</strong><p>${escapeHtml(chunk.content)}</p></article>`).join("") || `<div class="knowledge-review-empty"><strong>暂无可查看片段</strong></div>`;
    showModal($("#knowledgeChunksModal"));
  } catch (error) {
    toast(`片段加载失败：${error.message}`, { kind: "error" });
  }
}

async function reindexKnowledgeDocument(documentId) {
  try {
    await postBackend(`/api/knowledge/documents/${encodeURIComponent(documentId)}/reindex`, {});
    await loadKnowledgeWorkspace();
    toast("索引已重建");
  } catch (error) {
    toast(`重建失败：${error.message}`, { kind: "error" });
  }
}

async function reindexAllKnowledge() {
  if (knowledgeReindexController) return;
  const controller = new AbortController();
  knowledgeReindexController = controller;
  const button = $("#reindexAllKnowledgeBtn");
  const cancelButton = $("#cancelReindexAllKnowledgeBtn");
  const done = setButtonLoading(button, "重建中...");
  if (cancelButton) { cancelButton.classList.remove("hidden"); cancelButton.disabled = false; }
  try {
    const result = await postBackend("/api/knowledge/reindex", {}, { signal: controller.signal });
    await loadKnowledgeWorkspace();
    const failed = Number(result.errors?.length || 0);
    toast(failed ? `Dify 索引检查完成，${failed} 份资料需处理` : `Dify 已维护 ${result.completed}/${result.total} 份资料的索引`);
  } catch (error) {
    if (isAbortError(error)) toast("已取消索引重建");
    else toast(`Dify 索引检查失败：${error.message}`, { kind: "error", duration: 6500 });
  } finally {
    if (knowledgeReindexController === controller) knowledgeReindexController = null;
    done();
    if (cancelButton) { cancelButton.classList.add("hidden"); cancelButton.disabled = true; }
    const status = state.knowledgeStatus;
    if (status) renderKnowledgeWorkspace(status);
  }
}

function cancelReindexAllKnowledge() {
  if (!knowledgeReindexController || knowledgeReindexController.signal.aborted) return;
  knowledgeReindexController.abort();
}

async function deleteKnowledgeDocument(documentId) {
  const document = state.knowledgeDocuments.find((item) => item.id === documentId);
  if (!window.confirm(`永久删除“${document?.filename || "这份资料"}”及其全部索引？`)) return;
  try {
    await deleteBackend(`/api/knowledge/documents/${encodeURIComponent(documentId)}`);
    await loadKnowledgeWorkspace();
    toast("资料及索引已删除");
  } catch (error) {
    toast(`删除失败：${error.message}`, { kind: "error" });
  }
}

function openKnowledgeFolderEditor(folder = null) {
  $("#knowledgeFolderId").value = folder?.id || "";
  $("#knowledgeFolderModalTitle").textContent = folder ? "编辑目录" : "新建目录";
  $("#knowledgeFolderName").value = folder?.name || "";
  $("#knowledgeFolderParent").innerHTML = knowledgeFolderOptions(folder?.parent_id || "", folder?.id || "");
  showModal($("#knowledgeFolderModal"));
}

async function saveKnowledgeFolder(event) {
  event.preventDefault();
  const id = $("#knowledgeFolderId").value;
  const payload = { name: $("#knowledgeFolderName").value.trim(), parentId: $("#knowledgeFolderParent").value };
  try {
    const result = id
      ? await putBackend(`/api/knowledge/folders/${encodeURIComponent(id)}`, payload)
      : await postBackend("/api/knowledge/folders", payload);
    hideModal($("#knowledgeFolderModal"));
    await loadKnowledgeWorkspace();
    toast(result.partial ? "目录更新不完整" : (id ? "目录已更新" : "目录已创建"));
  } catch (error) {
    toast(`目录保存失败：${error.message}`, { kind: "error" });
  }
}

async function deleteKnowledgeFolder(folderId) {
  const folder = (state.knowledgeFolders || []).find((item) => item.id === folderId);
  const subtree = (state.knowledgeFolders || []).filter((item) => item.path === folder?.path || item.path.startsWith(`${folder?.path}/`));
  const childCount = Math.max(0, subtree.length - 1);
  const detail = childCount ? `及其 ${childCount} 个子目录` : "";
  const mappingCount = subtree.reduce((total, item) => total + Number(item.document_count || 0), 0);
  const fileDetail = mappingCount
    ? `\n目录内 ${mappingCount} 个文件映射将同步移除。仅存在于这些目录的云端文件会被删除；仍被其他目录引用的同一文件会保留。`
    : "";
  if (!window.confirm(`删除目录“${folder?.path || "该目录"}”${detail}？${fileDetail}`)) return;
  try {
    const result = await deleteBackend(`/api/knowledge/folders/${encodeURIComponent(folderId)}`);
    const deletion = result.deleted && typeof result.deleted === "object" ? result.deleted : {};
    if (deletion.partial) {
      await loadKnowledgeWorkspace();
      const failed = Array.isArray(deletion.errors) ? deletion.errors.length : 0;
      toast(`目录删除未完成：${failed} 个云端文件删除失败，目录已保留，可稍后重试`, { kind: "error", duration: 7000 });
      return;
    }
    const removedIds = new Set(deletion.deletedFolderIds || subtree.map((item) => item.id));
    state.knowledgeCollapsedFolderIds = (state.knowledgeCollapsedFolderIds || []).filter((id) => !removedIds.has(id));
    const selectedPath = $("#knowledgeFolderFilter")?.value || "";
    if (folder && (selectedPath === folder.path || selectedPath.startsWith(`${folder.path}/`))) {
      $("#knowledgeFolderFilter").value = "";
    }
    saveState();
    await loadKnowledgeWorkspace();
    const deletedFiles = Number(deletion.deletedDocuments || 0);
    const retainedFiles = Number(deletion.detachedDocuments || 0);
    const fileSummary = [
      deletedFiles ? `删除 ${deletedFiles} 个云端文件` : "",
      retainedFiles ? `保留 ${retainedFiles} 个共享文件` : "",
    ].filter(Boolean).join("，");
    toast(`${childCount ? `目录及 ${childCount} 个子目录已删除` : "目录已删除"}${fileSummary ? `，${fileSummary}` : ""}`);
  } catch (error) {
    toast(`目录删除失败：${error.message}`, { kind: "error" });
  }
}

function openKnowledgeMove(documentId, sourceFolderId = "") {
  const document = state.knowledgeDocuments.find((item) => item.id === documentId);
  if (!document) return;
  $("#knowledgeMoveDocumentId").value = documentId;
  $("#knowledgeMoveSourceFolderId").value = sourceFolderId || document.folder_id || "";
  $("#knowledgeMoveTarget").innerHTML = knowledgeFolderOptions(sourceFolderId || document.folder_id || "");
  showModal($("#knowledgeMoveModal"));
}

async function moveKnowledgeDocument(event) {
  event.preventDefault();
  const documentId = $("#knowledgeMoveDocumentId").value;
  try {
    await putBackend(`/api/knowledge/documents/${encodeURIComponent(documentId)}/folder`, {
      folderId: $("#knowledgeMoveTarget").value,
      sourceFolderId: $("#knowledgeMoveSourceFolderId").value,
    });
    hideModal($("#knowledgeMoveModal"));
    await loadKnowledgeWorkspace();
    toast("资料已移动");
  } catch (error) {
    await loadKnowledgeWorkspace();
    toast(`资料移动失败：${error.message}`, { kind: "error", duration: 6500 });
  }
}

async function syncKnowledgeDirectories() {
  const done = setButtonLoading($("#syncKnowledgeFoldersBtn"), "同步中...");
  try {
    const result = await postBackend("/api/knowledge/folders/sync", {});
    await loadKnowledgeWorkspace();
    toast(result.partial ? `目录同步完成，${result.errors.length} 份资料需重试` : `已同步 ${result.completed} 份资料的目录`);
  } catch (error) {
    toast(`目录同步失败：${error.message}`, { kind: "error" });
  } finally {
    done();
  }
}


// ----- js/shell/render.js -----
function renderAll() {
  renderMetrics();
  renderEditor();
  renderKnowledgeWorkspace();
  renderViralFactory();
  renderStyleGrid();
  renderImageStudioSelection();
  renderImageReferencePreview();
  renderGeneratedGrid();
  renderPublishList();
}

function syncFormToDraft() {
  const draft = currentDraft();
  if (!draft) return null;
  const previousTopicSignature = knowledgeTopicSignatureForDraft(draft);
  draft.title = $("#draftTitle").value.trim();
  draft.body = $("#draftBody").value.trim();
  draft.tags = $("#draftTags").value.trim();
  markKnowledgeStaleIfTopicChanged(draft, previousTopicSignature);
  saveState();
  renderPreview(draft);
  updateEditorStats(draft);
  renderDraftList();
  return draft;
}

async function improveTitle() {
  const draft = syncFormToDraft();
  if (!draft) return;
  // 账号维度：别的草稿在跑 AI 任务时点名拒绝。按钮不置灰——点了才知道为什么。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  // 分区生成只和自己这一区互斥：标题在跑时正文/标签/图片各跑各的；整篇生成（要重写
  // 全部字段）仍然独占整份草稿。
  if (draftFullGenerationActive(draft.id)) {
    toast("整篇生成正在跑，请等它结束后再单独生成标题");
    return;
  }
  if (draftBlockGenerationBusy(draft.id, "title")) {
    toast("标题正在生成中，请稍候");
    return;
  }
  // 空草稿没有任何可改写的素材（P73）：入口灰按钮之外再拦一次，避免别的入口直接调到这里。
  if (!draftHasUsableContent(draft)) return void toast(EMPTY_DRAFT_GENERATION_HINT, { kind: "error", duration: 5000 });
  if (!isReplicaDraft(draft) && requireKnowledgeDecision(draft, improveTitle)) return;
  if (!ensureAiConfigured("text")) return;
  const button = $('[data-generate-ai-block="title"]');
  const done = setButtonLoading(button, "生成中...");
  const titleRun = beginDraftGenerationRun(draft.id, "block", "title");
  try {
    // 登记之后、try 之前的任何一次抛错都让计数永远减不回去（主按钮被永久置灰），
    // 所以这句必须待在 try 里面。
    renderDraftGenerationIndicators(draft);
    const basePrompt = $("#titleBlockPrompt")?.value.trim() || draftAiPrompt(draft, "title");
    if (!basePrompt) return toast("标题提示词为空");
    const prompt = composePrompt(basePrompt, draft);
    const result = await runTextAi("title", prompt, draft);
    draft.title = await fitXhsTitle(result, draft) || draft.title;
    if (draft.promptMode) draft.promptMode.title = false;
    saveState();
    renderEditor();
    toast("标题已完成 AI 修改");
  } catch (error) {
    toast(`标题 AI 失败：${friendlyAiError(error)}`);
  } finally {
    endDraftGenerationRun(titleRun);
    done();
    renderDraftGenerationIndicators(draft);
    // 分区生成期间主按钮会因"有生成在跑"被置灰；收尾必须把它也刷回来，
    // 否则它就一直停在灰色（"任务一结束反而锁了"）。
    renderEditor();
  }
}

async function improveBody() {
  const draft = syncFormToDraft();
  if (!draft) return;
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  if (draftFullGenerationActive(draft.id)) {
    toast("整篇生成正在跑，请等它结束后再单独生成正文");
    return;
  }
  if (draftBlockGenerationBusy(draft.id, "body")) {
    toast("正文正在生成中，请稍候");
    return;
  }
  if (!draftHasUsableContent(draft)) return void toast(EMPTY_DRAFT_GENERATION_HINT, { kind: "error", duration: 5000 });
  if (!isReplicaDraft(draft) && requireKnowledgeDecision(draft, improveBody)) return;
  if (!ensureAiConfigured("text")) return;
  const button = $('[data-generate-ai-block="body"]');
  const done = setButtonLoading(button, "生成中...");
  const bodyRun = beginDraftGenerationRun(draft.id, "block", "body");
  try {
    renderDraftGenerationIndicators(draft);
    const basePrompt = $("#bodyBlockPrompt")?.value.trim() || draftAiPrompt(draft, "body");
    if (!basePrompt) return toast("正文提示词为空");
    const prompt = composePrompt(basePrompt, draft);
    const result = await runTextAi("body", prompt, draft);
    draft.body = result || draft.body;
    if (draft.promptMode) draft.promptMode.body = false;
    saveState();
    renderEditor();
    toast("正文已完成 AI 修改");
  } catch (error) {
    toast(`正文 AI 失败：${friendlyAiError(error)}`);
  } finally {
    endDraftGenerationRun(bodyRun);
    done();
    renderDraftGenerationIndicators(draft);
    // 分区生成期间主按钮会因"有生成在跑"被置灰；收尾必须把它也刷回来，
    // 否则它就一直停在灰色（"任务一结束反而锁了"）。
    renderEditor();
  }
}

function normalizeXhsTags(value, fallbackValues = []) {
  const cleanTag = (raw) => {
    const tag = String(raw || "")
      .replace(/^#+/, "")
      .trim()
      .replace(/^[，。；;、,!?！？：:\"'“”‘’《》【】\[\]（）()]+|[，。；;、,!?！？：:\"'“”‘’《》【】\[\]（）()]+$/g, "")
      .replace(/\s+/g, "");
    // 来源类目留空时的展示兜底不是内容话题：AI 输出和兜底词表里都不能留下 #未分类。
    if (tag === UNCATEGORIZED_CATEGORY) return "";
    if (tag.length < 2 || tag.length > 16) return "";
    if (/[，。；;、,!?！？：:\"'“”‘’《》【】\[\]（）()]/.test(tag)) return "";
    if (/(为统一视角|串起|本文|这篇|生成|标签|只输出|最常考|规则框架)/.test(tag)) return "";
    return tag;
  };
  const fragments = (raw) => {
    const text = String(raw || "").trim();
    const quoted = [...text.matchAll(/[“‘《【]([^”’》】]+)[”’》】]/g)].map((match) => match[1]);
    return [...quoted, ...text.split(/[+＋|｜/／、，,；;：:\n]+/)]
      .flatMap((item) => item.split(/[+＋|｜/／、，,；;：:]+/))
      .map(cleanTag)
      .filter(Boolean);
  };
  const text = String(value || "").trim();
  const hashtags = [...text.matchAll(/#\s*([^\s#]+)/g)].map((match) => match[1]);
  const generated = (hashtags.length ? hashtags : text.split(/[\s，,；;、]+/))
    .map(cleanTag)
    .filter(Boolean);
  const fallbacks = fallbackValues.flatMap(fragments);
  return [...new Set([...generated, ...fallbacks])]
    .slice(0, XHS_TAG_MAX_LENGTH)
    .map((tag) => `#${tag}`)
    .join(" ");
}

function xhsTagFallbacksForDraft(draft) {
  return [
    draft?.viralBrief?.category,
    ...(draft?.executionPlan?.topicPackage?.keywords || []),
    draft?.viralBrief?.topic,
    draft?.executionPlan?.topicPackage?.topic,
    "知识分享",
    "学习笔记",
    "干货分享",
    "经验分享",
  ];
}

function migrateGeneratedXhsTags(draft) {
  const generated = draft?.executionPlan?.generatedContent;
  const generatedTags = String(generated?.tags || "").trim();
  if (!generatedTags) return false;
  const normalized = normalizeXhsTags(generatedTags, xhsTagFallbacksForDraft(draft));
  if (!normalized || normalized === generatedTags) return false;
  generated.tags = normalized;
  return true;
}

async function improveTags() {
  const draft = syncFormToDraft();
  if (!draft) return;
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  if (draftFullGenerationActive(draft.id)) {
    toast("整篇生成正在跑，请等它结束后再单独生成标签");
    return;
  }
  if (draftBlockGenerationBusy(draft.id, "tags")) {
    toast("标签正在生成中，请稍候");
    return;
  }
  if (!draftHasUsableContent(draft)) return void toast(EMPTY_DRAFT_GENERATION_HINT, { kind: "error", duration: 5000 });
  if (!isReplicaDraft(draft) && requireKnowledgeDecision(draft, improveTags)) return;
  if (!ensureAiConfigured("text")) return;
  const button = $('[data-generate-ai-block="tags"]');
  const done = setButtonLoading(button, "生成中...");
  const tagsRun = beginDraftGenerationRun(draft.id, "block", "tags");
  try {
    renderDraftGenerationIndicators(draft);
    const basePrompt = $("#tagsBlockPrompt")?.value.trim() || draftAiPrompt(draft, "tags");
    if (!basePrompt) return toast("标签提示词为空");
    const prompt = composePrompt(basePrompt, draft);
    const result = await runTextAi("tags", prompt, draft);
    draft.tags = normalizeXhsTags(result, xhsTagFallbacksForDraft(draft)) || draft.tags;
    if (draft.promptMode) draft.promptMode.tags = false;
    saveState();
    renderEditor();
    toast("标签已完成 AI 修改");
  } catch (error) {
    toast(`标签 AI 失败：${friendlyAiError(error)}`);
  } finally {
    endDraftGenerationRun(tagsRun);
    done();
    renderDraftGenerationIndicators(draft);
    // 分区生成期间主按钮会因"有生成在跑"被置灰；收尾必须把它也刷回来，
    // 否则它就一直停在灰色（"任务一结束反而锁了"）。
    renderEditor();
  }
}

// 粘贴进来的链接里夹一个空格/换行（从聊天、笔记、PDF 里复制时最常见）时，后端抽链接的
// 正则按空白截断：`…?xsec_token=AB CD` 会被收成 `…?xsec_token=AB`，拿着半截 token 去抓
// 必然落到 404/登录页，用户看到的却是"链接失效"，根本想不到是那个空白（P133）。
//
// 一行里只有一个链接起点、且空白之后仍然全是链接允许的字符时，把空白拼回去——那本来就是
// 复制时被折断的同一串链接。空白之后是中文等非链接字符时不碰：分享文案的尾巴
// （"…完整链接 复制后打开"）本来就该留在链接外面，一行两条链接也该照旧按空白切开。
const IMPORT_URL_HEAD_RE = /https?:\/\/\S+/i;
const IMPORT_URL_TAIL_RE = /^\s+(\S+)([\s\S]*)$/;
const IMPORT_URL_TAIL_CHARS_RE = /^[0-9A-Za-z%&=._~:/?#@!$()*+,;+-]+$/;

function joinSpacedImportUrl(line) {
  const head = String(line || "").match(IMPORT_URL_HEAD_RE);
  if (!head) return "";
  const before = String(line).slice(0, head.index);
  let url = head[0];
  let rest = String(line).slice(head.index + head[0].length);
  let joined = false;
  for (;;) {
    const next = rest.match(IMPORT_URL_TAIL_RE);
    if (!next) break;
    const candidate = next[1];
    if (!IMPORT_URL_TAIL_CHARS_RE.test(candidate)) break;
    if (/^https?:\/\//i.test(candidate)) break;
    url += candidate;
    rest = next[2];
    joined = true;
  }
  // 没拼过就返回空串：调用方据此判定"这一行没动过"。
  return joined ? `${before}${url}${rest}` : "";
}

// 返回拼接过后的文本与拼接处数：拼接发生在用户看不见的地方，必须回写到输入框并说一声，
// 否则用户会以为我们擅自改了他粘的东西。
function normalizeImportLinkInput(value) {
  let repaired = 0;
  const text = String(value || "")
    .trim()
    .split("\n")
    .map((line) => {
      if ((line.match(/https?:\/\//gi) || []).length !== 1) return line;
      const joined = joinSpacedImportUrl(line);
      if (!joined) return line;
      repaired += 1;
      return joined;
    })
    .join("\n")
    .trim();
  return { text, repaired };
}

async function handleLinks() {
  const normalized = normalizeImportLinkInput($("#linkInput").value);
  const inputText = normalized.text;
  if (!inputText) {
    toast("先粘贴至少一条小红书链接");
    return;
  }
  if (normalized.repaired) $("#linkInput").value = inputText;
  const button = $("#parseLinksBtn");
  // 按钮 disabled 与异步之间仍可能连点两次（DevTools 里常见两条同毫秒 import-links）。
  // 但不能静默吞掉：用户在采集进行中或那 10 秒条间间隔里再点一次，界面毫无反应，
  // 只会以为"点了没用"。说清楚现在卡在哪一步、还要多久。
  if (button?.dataset.linkImportBusy === "1") {
    toast(linkImportBusyReason(), { duration: 4000 });
    return;
  }
  // 上一次导入留下的失败提示条在这里清掉：它属于上一次导入，不该跨到这一次。
  clearLinkImportFallback();

  const originalText = button.textContent;
  button.dataset.linkImportBusy = "1";
  button.disabled = true;
  button.textContent = "正在抓取内容...";
  toast(normalized.repaired ? "链接里夹着空白，已自动拼接后再抓取" : "正在识别并抓取小红书链接");

  try {
    const job = await captureNoteLinks(inputText, (job) => {
      // 采集期间这个按钮归"取消采集"用，别把它的字覆盖掉。
      if (button.dataset.noteCaptureCancel === "1") return;
      const captured = Number(job?.progress?.captured || 0);
      const total = Number(job?.progress?.total || 0);
      button.textContent = total ? `正在浏览器中采集 ${captured}/${total}...` : "正在浏览器中采集...";
    }, { button });
    const notes = job.results || [];
    if (!notes.length) throw new Error("导入接口没有返回任何笔记结果");
    const list = notes;
    const imported = list
      .map((note, index) => ({ note, index }))
      .filter(({ note }) => note.ok !== false);
    const failed = list.filter((note) => note.ok === false);
    imported
      .slice()
      .reverse()
      .forEach(({ note, index }) => {
        const origin = note.origin || note.url || "";
        const importedFields = normalizeImportedNoteFields(note.body ?? "", note.tags ?? "");
        const importedTitle = normalizeImportedNoteTitle(note.title ?? "", importedFields.body);
        createDraft({
          source: "link",
          workflow: "replica",
          seed: importedTitle || `参考链接 ${index + 1}`,
          origin,
          title: importedTitle,
          body: importedFields.body,
          tags: importedFields.tags,
          images: normalizeImages(note.images).slice(0, 18),
        });
      });

    if (imported.length) {
      clearLinkImportFallback();
      $("#linkInput").value = "";
      setSection("rewrite");
      toast(`已创建 ${imported.length} 个链接改写任务${failed.length ? `，${failed.length} 条笔记导入失败` : ""}`);
    } else {
      // 优先后端整理过的中文 message：error 是给日志看的原始异常（可能是英文的
      // HTTP Error / Connection refused），拿它当用户提示等于把内部细节丢到界面上。
      const reason = failed[0]?.message || failed[0]?.error || "没有拿到这条笔记的内容";
      showLinkImportFallback(reason, failed[0]?.errorCode || "");
    }
  } catch (error) {
    if (error?.noteCaptureNotice || error?.noteCaptureCancelled) {
      // 用户自己按的取消：一句中性提示就够了，不该弹失败面板和补救建议。
      toast(error.message || "已取消这次采集", { duration: 3000 });
    } else {
      // 插件没装走"缺插件"的说法，采集节奏的 429 走"这次根本不该开跑"的说法：两者的标题、
      // 下一步都跟抓取失败不同（见 showLinkImportFallback）。errorCode 与报文必须一起带过去，
      // 否则 429 会掉进「这次采集没拿到内容 + 去小红书确认笔记可见」那套建议里。
      showLinkImportFallback(error.message, error?.extensionMissing
        ? { kind: "extension", heading: error.extensionHeading, hint: error.extensionHint }
        : { errorCode: error?.errorCode, payload: error?.payload });
    }
  } finally {
    button.disabled = false;
    button.textContent = originalText;
    delete button.dataset.linkImportBusy;
  }
}

// 提示条的生命周期绑定「这一次导入」：开始新的导入先清掉上一次的，成功则不再留着。
// 以前只建不删，失败原因会一直挂在页面上（重试成功后看到的还是上一次的旧原因）。
function clearLinkImportFallback() {
  document.querySelector("[data-link-import-fallback]")?.remove();
}

// 失败原因里裹着直抓用的原始 URL（含 xsec_token）、百分号转义的登录跳转和上游英文短语。
// 那些是写给日志的，不是写给用户的：屏幕上只剩「链接 + 转义串 + upstream requires
// verification」，用户既看不出发生了什么，也不知道下一步该做什么。展示层在这里把它们
// 换成它实际代表的事。
function readableLinkImportReason(reason) {
  return String(reason || "")
    .replace(/https?:\/\/\S+/gi, " ")
    .replace(/%[0-9A-Fa-f]{2}(?:%[0-9A-Fa-f]{2})+/g, " ")
    .replace(/upstream requires verification:?/gi, "小红书把这次抓取跳到了登录或验证页，没有给出笔记内容")
    .replace(/HTTP Error (\d{3})/gi, "小红书返回了 $1")
    .replace(/Connection refused|URLError|TimeoutError/gi, "后端连不上小红书")
    .replace(/[A-Za-z]:\\[^\s]+/g, " ")
    .replace(/链接直抓失败：|原因：/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

// 粘贴的内容里连一条笔记链接的形状都没有（搜索结果页、用户主页、站点首页都能贴进来），
// 直抓必然失败。与其让用户对着「抓取失败 + 一串 URL」猜，不如先把这句说在前面。
function linkImportShapeHint(text) {
  const lines = String(text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return "";
  const looksLikeNote = lines.some((line) =>
    /xhslink\.(com|cn)|\/discovery\/item\/|\/explore\/|\/item\/|noteId=|note_id=/i.test(line),
  );
  return looksLikeNote
    ? ""
    : "粘贴的内容里没有笔记链接：搜索结果页、用户主页或首页都抓不到笔记，请打开具体笔记后用「分享 → 复制链接」再粘贴。";
}

// 采集节奏的限制（服务端 429 import_note_rate_limited）不是"抓取失败"：内容根本没被抓过，
// 是这一次不该开跑。5 条规则共用这个码，服务端用 rateLimitedReason 说清是哪条，这里按它
// 给标题和下一步。绝不能复用「这次采集没拿到内容 + 去小红书确认笔记可见」——那是把用户支去
// 修一个不存在的问题，而真正该做的是等够时间，或者另走一条不占用采集节奏的路。
const LINK_IMPORT_RATE_LIMIT_HEADINGS = {
  running: "上一次采集还没结束",
  gap: "两次采集挨得太近",
  per_minute: "采集过于频繁",
  per_day: "今天已达采集上限",
  image_cooldown: "图片下载被限流，采集已暂停",
};

function linkImportWaitText(seconds) {
  const value = Math.max(0, Math.round(Number(seconds) || 0));
  if (!value) return "";
  return value >= 60 ? `${Math.max(1, Math.round(value / 60))} 分钟` : `${value} 秒`;
}

function linkImportRateLimitHeading(reason) {
  return LINK_IMPORT_RATE_LIMIT_HEADINGS[String(reason || "")] || "采集现在不能开始";
}

function linkImportRateLimitAdvice(reason, seconds, alternative) {
  const wait = linkImportWaitText(seconds);
  const retryHint = wait ? `请等约 ${wait}后再试` : "请稍后再试";
  if (String(reason) === "running") {
    return `上一次采集还在浏览器里进行，等它结束后再试；如果采集页已经关掉，稍等片刻直接重试即可。也可以${alternative}`;
  }
  if (String(reason) === "image_cooldown") {
    return `小红书正在拒绝这台浏览器下载图片，等冷却结束后再试。也可以${alternative}`;
  }
  if (String(reason) === "per_day") {
    return `今天的额度已经用完，请明天再试。也可以${alternative}`;
  }
  return `${retryHint}。也可以${alternative}`;
}

function showLinkImportFallback(reason, options = {}) {
  // 兼容两种调用：旧的第二参是 errorCode 字符串；新产品线传 { kind:"extension", ... }。
  // "没装插件"、"采集节奏没放行"和"这次抓取失败"是三类事，提示必须分开。
  let errorCode = "";
  let extensionMissing = false;
  let extensionHeading = "";
  let extensionHint = "";
  let rateLimited = false;
  let rateLimitedReason = "";
  let retryAfterSeconds = 0;
  if (typeof options === "string") {
    errorCode = options;
    rateLimited = errorCode === "import_note_rate_limited";
  } else if (options && typeof options === "object") {
    errorCode = String(options.errorCode || "");
    extensionMissing = options.kind === "extension";
    extensionHeading = String(options.heading || "");
    extensionHint = String(options.hint || "");
    const payload = options.payload && typeof options.payload === "object" ? options.payload : {};
    const code = errorCode || String(payload.errorCode || "");
    rateLimited = code === "import_note_rate_limited";
    if (rateLimited) {
      rateLimitedReason = String(payload.rateLimitedReason || "");
      retryAfterSeconds = Number(payload.retryAfterSeconds || 0) || 0;
    }
  }
  // 节奏限制下输入框里的链接形状根本不参与判断：能走到 429 说明链接已经被服务端收下了。
  const shapeHint = extensionMissing || rateLimited
    ? ""
    : linkImportShapeHint($("#linkInput")?.value || "");
  let readableReason = readableLinkImportReason(reason);
  // 链接形状不对时，下面那句 advice 已经把"改用图生文案 / 新建空白笔记"说全了；
  // 后端 reason 里自带的那一行再留一遍，同一条提示里就会出现两次同样的话。
  if (shapeHint) readableReason = readableReason.replace(/[^\n]*改用「上传图片生成文案」[^\n]*/g, "").trim();
  reason =
    [shapeHint, readableReason].filter(Boolean).join("\n") ||
    "小红书没有返回这条笔记的内容";
  const safeReason = String(reason || "未知错误");
  // 后端 reason 有一半的分支自己就带「可以改用…」那句（见 import_error_message 的
  // alternative），无条件再补一遍会让同一句话在一条提示里出现两次；已有就不重复。
  const reasonHasAlternative = String(reason).includes("改用「上传图片生成文案」");
  const shell = document.querySelector(".link-import-shell");
  if (!shell) {
    if (extensionMissing || rateLimited) {
      // 这两类都不该被说成"抓取失败"：一个是插件不在，一个是这次根本没开跑。
      toast(safeReason);
    } else {
      toast(`链接抓取失败：${safeReason}。请在小红书页面登录后重试，或改用图片生成文案`);
    }
    return;
  }
  let notice = shell.querySelector("[data-link-import-fallback]");
  if (!notice) {
    notice = document.createElement("div");
    notice.dataset.linkImportFallback = "1";
    notice.className = "link-import-fallback";
    shell.appendChild(notice);
  }
  // 直抓失败这一支不推给插件兜底：插件是整条导入链路的前置条件，缺它的时候在更前面就被
  // 判成"未检测到插件安装"了（见 handleLinks 的捕获分支），走到这里说明插件是好的。
  const alternative = "改用下面的「上传图片生成文案」，或到「AI 改写」页新建空白笔记，手动填充内容。";
  let advice;
  let heading;
  if (extensionMissing) {
    heading = extensionHeading || options.heading || "未检测到插件安装";
    // 与 test_extension_detection_gate 钉的文案同源：优先失败对象自带的 hint，否则用安装指引。
    const hint = extensionHint || options.hint || CAPTURE_EXTENSION_INSTALL_HINT;
    advice = `${hint}也可以${alternative}`;
  } else if (rateLimited) {
    // 服务端说了是哪条节奏规则、还要等多久，就不再让用户对着"抓取失败"猜。
    heading = linkImportRateLimitHeading(rateLimitedReason);
    advice = linkImportRateLimitAdvice(rateLimitedReason, retryAfterSeconds, alternative);
  } else if (shapeHint) {
    heading = "这次采集没拿到内容";
    advice = `换一条笔记链接重试；仍然不行时，${alternative}`;
  } else if (reasonHasAlternative) {
    heading = "这次采集没拿到内容";
    advice = "请在小红书页面确认笔记可见后重试。";
  } else {
    heading = "这次采集没拿到内容";
    advice = `请在小红书页面确认笔记可见后重试；仍然失败时，${alternative}`;
  }
  notice.innerHTML = `<strong>${escapeHtml(heading)}</strong><span>${escapeHtml(reason)}</span><p>${advice}</p>`;
  toast(
    extensionMissing || rateLimited
      ? safeReason
      : `链接抓取失败：${safeReason}。已给出可用的替代做法`,
    { kind: "error" },
  );
}

function fileToObject(file) {
  return {
    file,
    src: URL.createObjectURL(file),
    name: file.name,
    path: file.webkitRelativePath || file.name,
  };
}

// 目录上传的任务粒度是**子目录**：一次选中的文件夹里，每个子目录各成一条任务。
// webkitRelativePath 形如「所选目录/子目录/图片.png」——第一段对这次选择里的每个文件都一样
// （它就是用户选的那个目录），按它分组恒为 1 组，下面「多个目录要先确认」的分支永远走不到，
// 于是"一次选 N 个子目录"既没有数量预览、也不会被拆成 N 条任务（P57）。
// 图片直接放在所选目录下（只有两段）时仍归"所选目录"这一组；拖拽进来的文件没有
// webkitRelativePath，退回文件名（每个文件一组，与既有行为一致）。
function folderGroupKey(file) {
  const parts = String(file.webkitRelativePath || file.name || "").split("/").filter(Boolean);
  if (parts.length >= 3) return parts[1];
  return parts[0] || "未命名笔记";
}

function uploadFilesForWorkflow(workflow = state.activeWorkflow) {
  return state.uploadFilesByWorkflow[workflow === "replica" ? "replica" : "original"] || [];
}

function renderUploadPreview(selector = "#uploadPreview", workflow = state.activeWorkflow) {
  const files = uploadFilesForWorkflow(workflow).slice(0, 18);
  const preview = $(selector);
  if (!preview) return;
  preview.innerHTML = files
    .map((item, index) => `
      <figure class="upload-preview-item">
        <img class="thumb" src="${escapeHtml(item.src)}" alt="${escapeHtml(item.name)}" />
        <button type="button" class="upload-preview-remove" data-remove-pending-upload="${workflow === "replica" ? "replica" : "original"}" data-upload-index="${index}" aria-label="删除${escapeHtml(item.name)}" title="删除这张图片"><i data-lucide="x" aria-hidden="true"></i></button>
      </figure>`)
    .join("");
  window.lucide?.createIcons({ attrs: { "stroke-width": 2 } });
}

// 18 张是服务端硬上限（server/http_routes/image.py）。选完还没上传时就能判的两件事必须说清：
// 一张都不是图片 → 格式错误（与 server/services/local_files.py 的 IMAGE_UPLOAD_SUFFIX_ERROR 同文，
// 不能报文档类文案）；图片超过剩余额度 → 上限提示。过去非图片被静默过滤后统一报上限，
// 传一个 .txt 会得到与文件类型无关的提示。
const IMAGE_UPLOAD_SUFFIX_ERROR = "图片仅支持 JPG、PNG、WEBP、GIF 格式";

function handleImageInput(files, previewSelector = "#uploadPreview", workflow = state.activeWorkflow) {
  const normalizedWorkflow = workflow === "replica" ? "replica" : "original";
  const current = uploadFilesForWorkflow(normalizedWorkflow);
  const list = Array.from(files || []);
  if (!list.length) return;
  const images = list.filter((item) => String(item?.type || "").startsWith("image/"));
  if (!images.length) {
    toast(IMAGE_UPLOAD_SUFFIX_ERROR, { kind: "error", duration: 6000 });
    return;
  }
  const available = Math.max(0, 18 - current.length);
  const accepted = images.slice(0, available);
  accepted.forEach((file) => current.push(fileToObject(file)));
  state.uploadFilesByWorkflow[normalizedWorkflow] = current;
  renderUploadPreview(previewSelector, normalizedWorkflow);
  if (images.length > available) {
    toast("单篇最多上传 18 张图片");
    return;
  }
  toast(`已添加 ${accepted.length} 张图片，共 ${current.length} 张`);
}

function removePendingUploadImage(workflow, index, previewSelector) {
  const normalizedWorkflow = workflow === "replica" ? "replica" : "original";
  const files = uploadFilesForWorkflow(normalizedWorkflow);
  if (!Number.isInteger(index) || index < 0 || index >= files.length) return;
  const [removed] = files.splice(index, 1);
  if (removed?.src?.startsWith("blob:")) URL.revokeObjectURL(removed.src);
  state.uploadFilesByWorkflow[normalizedWorkflow] = files;
  renderUploadPreview(previewSelector, normalizedWorkflow);
  toast(`已删除 1 张图片，剩余 ${files.length} 张`);
}

// 单次请求最多带 18 张（后端硬上限），但"18 张原图"在慢链路上会拖过上游写超时（网关 504）。
// 先把每张压进字节预算（compressedImageForUpload），再按小批上传：每批是独立请求，网关的
// 写超时只作用于单批，不会把整次导入判死；返回顺序与选择顺序一致。
const IMPORT_UPLOAD_BATCH_SIZE = 6;

async function persistImportedImageFiles(items) {
  const selected = (items || []).filter((item) => item?.file).slice(0, 18);
  if (!selected.length) return [];
  const prepared = [];
  for (const item of selected) {
    prepared.push(await compressedImageForUpload(item.file));
  }
  const images = [];
  for (let start = 0; start < prepared.length; start += IMPORT_UPLOAD_BATCH_SIZE) {
    const batch = prepared.slice(start, start + IMPORT_UPLOAD_BATCH_SIZE);
    const body = new FormData();
    batch.forEach((file) => body.append("files", file, file.name));
    const response = await backendFetch("/api/assets/import-images", { method: "POST", body });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.ok === false) throw backendError(result, response.status, response);
    const batchImages = normalizeImages(result.images || []);
    if (batchImages.length !== batch.length) throw new Error("部分图片未能保存，请重新导入");
    images.push(...batchImages);
  }
  if (images.length !== selected.length) throw new Error("部分图片未能保存，请重新导入");
  return images;
}

// 图生文案的提交链原本没有防重入：慢操作是"逐个子目录 await 上传"，这期间按钮和文件夹输入都还能再点，
// 每次点击都会各自走完流程并各建一个草稿。同页的链接导入（parseLinksBtn 自己加锁）与模板入库
// （runtime owner 守卫）都有闸门，这里属遗漏。提交链共用一把锁。
let imageSubmitInFlight = false;

function setImageSubmitBusy(busy) {
  [
    "#imageInput",
    "#folderInput",
    "#uploadToRewriteBtn",
  ].forEach((selector) => {
    const node = $(selector);
    if (node) node.disabled = busy;
  });
}

// 图片导入只剩「图生文案」一条路（原创笔记那页的图片导入已随整改移除，拆解只认 source=link），
// 所以上传的忙碌态只有 #uploadToRewriteBtn 这一个宿主。
// P117：上传链路过去只有"开始/结束"两个状态——提交时把控件置灰、不改按钮文案、也不给
// spinner 或 aria-busy，网慢/大图时用户只能盯着灰按钮猜。上传本体是一次 fetch + FormData，
// 拿不到真实字节进度，所以这里先做可感知的忙碌态：文案 + 旋转图标 + aria-busy，
// 成功/失败统一在 finally 复位，绝不留下永久 loading。
function beginUploadFeedback(button, count) {
  if (!button) return () => {};
  const original = button.innerHTML;
  button.setAttribute("aria-busy", "true");
  button.classList.add("is-loading");
  button.innerHTML = `<i data-lucide="loader-circle" aria-hidden="true"></i><span>正在上传 ${count} 张...</span>`;
  refreshIcons();
  return () => {
    button.innerHTML = original;
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    refreshIcons();
  };
}

function handleFolderInput(files, workflow = state.activeWorkflow) {
  return handleFolderInputAsync(files, workflow);
}

async function handleFolderInputAsync(files, workflow = state.activeWorkflow) {
  // 文件夹入口只属于快速笔记的「图生文案」，这里没有第二个输入框要清。
  const input = $(workflow === "replica" ? "#folderInput" : "");
  const groups = new Map();
  Array.from(files)
    .filter((file) => file.type.startsWith("image/"))
    .forEach((file) => {
      const folder = folderGroupKey(file);
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder).push(fileToObject(file));
    });
  // 文件已经取到本地引用，可以安全清空输入框：不清的话再选同一个文件夹不会再触发 change。
  if (input) input.value = "";
  if (imageSubmitInFlight) {
    toast("正在创建任务，请稍候", { duration: 3000 });
    return;
  }

  if (!groups.size) {
    toast("文件夹里没有识别到图片");
    return;
  }
  // 一次选到多个子目录会逐目录建任务，先让用户确认数量，避免一次误选建出十几个草稿。
  // 分组键见 folderGroupKey：每个子目录一组，直接放在所选目录下的图片算"所选目录"这一组。
  if (groups.size > 1 && !window.confirm(`文件夹里有 ${groups.size} 个目录，将创建 ${groups.size} 个任务。继续？`)) {
    return;
  }

  imageSubmitInFlight = true;
  setImageSubmitBusy(true);
  const imageCount = Array.from(groups.values()).reduce((sum, items) => sum + items.length, 0);
  const endUploadFeedback = beginUploadFeedback($("#uploadToRewriteBtn"), imageCount);
  try {
    for (const [folder, items] of groups.entries()) {
      const images = await persistImportedImageFiles(items);
      createDraft({
        source: "upload",
        workflow,
        seed: folder,
        images,
      });
    }
    setSection("rewrite");
    toast(workflow === "replica"
      ? `已创建 ${groups.size} 个图生文案任务`
      : `已创建 ${groups.size} 个文件夹改写任务`);
  } catch (error) {
    toast(`图片保存失败：${error.message}`, { kind: "error" });
  } finally {
    imageSubmitInFlight = false;
    setImageSubmitBusy(false);
    endUploadFeedback();
  }
}

async function uploadToRewrite(previewSelector = "#uploadPreview", workflow = state.activeWorkflow) {
  const normalizedWorkflow = workflow === "replica" ? "replica" : "original";
  const uploadFiles = uploadFilesForWorkflow(normalizedWorkflow);
  if (!uploadFiles.length) {
    toast("请先选择图片");
    return;
  }
  if (imageSubmitInFlight) {
    toast("正在创建任务，请稍候", { duration: 3000 });
    return;
  }
  imageSubmitInFlight = true;
  setImageSubmitBusy(true);
  const endUploadFeedback = beginUploadFeedback($("#uploadToRewriteBtn"), uploadFiles.length);
  try {
    const images = await persistImportedImageFiles(uploadFiles);
    state.activeWorkflow = normalizedWorkflow;
    createDraft({
      source: "upload",
      workflow: normalizedWorkflow,
      seed: `${uploadFiles.length} 张图片`,
      images,
    });
    uploadFiles.forEach((item) => {
      if (item.src?.startsWith("blob:")) URL.revokeObjectURL(item.src);
    });
    state.uploadFilesByWorkflow[normalizedWorkflow] = [];
    renderUploadPreview(previewSelector, normalizedWorkflow);
    setSection("rewrite");
    toast(normalizedWorkflow === "replica"
      ? "图片已保存并进入图生文案工作台"
      : "图片已保存并进入 AI 改写页");
  } catch (error) {
    toast(`图片保存失败：${error.message}`, { kind: "error" });
  } finally {
    imageSubmitInFlight = false;
    setImageSubmitBusy(false);
    endUploadFeedback();
  }
}

function selectedImageSize() {
  const ratio = $("#ratioSelect").value;
  if (ratio === "1:1") return "1024x1024";
  if (ratio === "4:3") return "1536x1024";
  return "1024x1536";
}

function selectedImageQualityLabel(value) {
  return { low: "标准", medium: "高清", high: "精细" }[value] || "高清";
}

function imageGenerationControls() {
  return $$([
    "#imageContentInput",
    "#promptInput",
    "#replacementTargetInput",
    "#replacementDescriptionInput",
    "#referenceInput",
    "#replacementReferenceInput",
    "#ratioSelect",
    "#resolutionSelect",
    "#optimizePromptBtn",
    '#imageReplacementFields input[type="checkbox"]',
    '[data-group="count"] button',
    "[data-image-generation-mode]",
    "[data-image-studio-mode]",
    "#imageTemplateSelection button",
    "#styleGrid button",
    "#styleGrid input",
    "#imageReferencePreview button",
    "#replacementReferencePreview button",
    "#imageTemplateShelfActions button",
    "#imageTemplateShelfActions input",
  ].join(", "));
}

// 生成按钮的启用条件与生成入口的守卫是同一件事：以前只有"生成中"会禁用按钮，缺字段
// 只在点击后 toast，用户看不出到底缺什么（P69）。
function imageGenerationBlockedReason() {
  const template = activeImageStudioTemplate();
  const generationMode = template?.mode === "scene" ? state.imageGenerationMode : "create";
  const replacing = generationMode !== "create";
  if (replacing) {
    if (!$("#replacementTargetInput").value.trim()) {
      return `请填写要替换的${generationMode === "replace_person" ? "人物" : "物品"}`;
    }
    if (!$("#replacementDescriptionInput").value.trim() && !(state.replacementReferences || []).length) {
      return "请描述替换内容，或上传替换参考图";
    }
    return "";
  }
  const content = $("#imageContentInput").value.trim();
  if (!content && !$("#promptInput").value.trim()) return "先填写画面内容";
  if (template && !content) return `请填写本次${template.mode === "scene" ? "画面内容" : "正文或画面内容"}`;
  return "";
}

function syncGenerateImageButtonState() {
  const button = $("#generateImageBtn");
  if (!button) return;
  // 生成中由 setImageGenerationRunning 接管按钮，别在这里把它放回可用。
  if (imageGenerationController) return;
  const reason = imageGenerationBlockedReason();
  button.disabled = Boolean(reason);
  button.title = reason;
}

function setImageGenerationRunning(running) {
  const section = $("#section-image");
  const panel = $("#section-image .image-composer-panel");
  const generateButton = $("#generateImageBtn");
  const generateLabel = $("#imageGenerationButtonLabel");
  const spinner = generateButton.querySelector(".image-generation-spinner");
  const stopButton = $("#stopImageGenerationBtn");
  const stopLabel = $("#stopImageGenerationLabel");

  section.classList.toggle("is-image-generating", running);
  panel.setAttribute("aria-busy", String(running));
  generateButton.disabled = running;
  generateButton.classList.toggle("is-loading", running);
  generateLabel.textContent = running ? "生成中..." : "生成图片";
  spinner.classList.toggle("hidden", !running);
  stopButton.classList.toggle("hidden", !running);
  stopButton.disabled = false;
  stopLabel.textContent = "中止";

  if (running) {
    closeImageReferenceMentionMenu();
    imageGenerationControlStates.clear();
    imageGenerationControls().forEach((control) => {
      imageGenerationControlStates.set(control, control.disabled);
      control.disabled = true;
    });
    return;
  }
  imageGenerationControlStates.forEach((disabled, control) => {
    if (control.isConnected) control.disabled = disabled;
  });
  imageGenerationControlStates.clear();
  // 一次生成结束后现场的字段可能已经被改过，把按钮态重新算一遍。
  syncGenerateImageButtonState();
}

function stopImageGeneration() {
  const controller = imageGenerationController;
  if (!controller || controller.signal.aborted) return;
  const stopButton = $("#stopImageGenerationBtn");
  stopButton.disabled = true;
  $("#stopImageGenerationLabel").textContent = "正在中止...";
  controller.abort();
}

async function generateImages() {
  if (imageGenerationController) return;
  // 按钮禁用与这里的守卫共用同一条判据，脚本点击也走同一条路径（P69）。
  const blockedReason = imageGenerationBlockedReason();
  if (blockedReason) {
    toast(blockedReason);
    return;
  }
  const content = $("#imageContentInput").value.trim();
  const visualPrompt = $("#promptInput").value.trim();
  const template = activeImageStudioTemplate();
  const generationMode = template?.mode === "scene" ? state.imageGenerationMode : "create";
  const replacing = generationMode !== "create";
  const replacementTarget = $("#replacementTargetInput").value.trim();
  const replacementDescription = $("#replacementDescriptionInput").value.trim();
  const preserveValues = $$("#imageReplacementFields .image-preserve-options input:checked").map((input) => input.value);
  if (!ensureAiConfigured("image")) return;
  const ratio = $("#ratioSelect").value;
  const quality = $("#resolutionSelect").value;
  const controller = new AbortController();
  imageGenerationController = controller;
  setImageGenerationRunning(true);
  try {
    const preserveLabels = {
      background: "背景环境",
      composition: "构图和机位",
      lighting: "原有光线",
      other_people: "其他人物",
      pose: "动作姿势",
      clothing: "原有服装",
    };
    const modeRule = replacing
      ? [
          `这是局部${generationMode === "replace_person" ? "人物" : "物品"}替换任务，不是重新构图。`,
          `在第一张场景参考图中找到并只替换：${replacementTarget}。`,
          replacementDescription ? `替换为：${replacementDescription}。` : "替换主体以场景图之后的主体参考图为准。",
          (state.replacementReferences || []).length
            ? `场景图之后的 ${state.replacementReferences.length} 张输入图都是主体参考图，请综合它们的服装、造型、材质和体态，只参考主体本身，不复制各自背景。`
            : "",
          generationMode === "replace_person"
            ? "画面中的人物必须是虚构成年人；人物参考图只用于发型、服装、体态等非身份特征，不复制可识别面部或真实身份。"
            : "",
          preserveValues.length ? `必须保持不变：${preserveValues.map((value) => preserveLabels[value] || value).join("、")}。` : "",
          "新主体需要与原场景的透视、尺寸、遮挡、光线和阴影自然融合，不要留下原主体残影。",
        ].filter(Boolean).join("\n")
      : template?.mode === "scene"
        ? "生成真实摄影图片，不要在画面中添加标题、正文、平台 UI 或装饰性文字。"
        : "生成可直接发布的小红书封面；用户内容中的文字必须清楚准确，不得擅自增加事实。";
    const inputEntries = imageInputEntries(template, generationMode);
    const inputImages = inputEntries.map((entry) => entry.src);
    const referenceIndexRule = inputEntries.length
      ? `输入图片编号与上传顺序严格对应：${inputEntries.map((entry) => `@图${entry.number}（${entry.sourceLabel}）`).join("、")}。提示词中的 @图N 只对应第 N 张输入图片。`
      : "";
    const finalPrompt = [
      activePrompt("imagePrompt"),
      referenceIndexRule,
      modeRule,
      visualPrompt ? `视觉模板要求：\n${visualPrompt}` : "",
      !replacing && content ? `本次正文或画面内容：\n${content}` : "",
      `比例：${ratio}\n清晰度：${selectedImageQualityLabel(quality)}`,
    ].filter(Boolean).join("\n\n");
    if (inputImages.length > MAX_IMAGE_INPUTS) {
      toast(`当前共有 ${inputImages.length} 张输入图，单次最多 ${MAX_IMAGE_INPUTS} 张，请先移除部分参考图`);
      return;
    }
    if (!assertImageRequestFitsLimit(inputImages, finalPrompt.length)) return;
    const imageResult = await runImageAi(finalPrompt, {
      count: state.selectedCount,
      size: selectedImageSize(),
      quality,
      inputImages,
      preferEdit: inputImages.length > 0,
      signal: controller.signal,
    });
    const generatedAt = new Date().toISOString();
    const generatedImages = imageResult.images.map((src, index) => ({
      id: uid("img"),
      title: `AI 图片素材 ${index + 1}`,
      prompt: visualPrompt,
      content: replacing ? `${replacementTarget} → ${replacementDescription || "参考图主体"}` : content,
      generationMode,
      replacementTarget,
      replacementDescription,
      preserveValues,
      style: template?.id || state.selectedStyle,
      templateId: template?.templateId || template?.id || "",
      templateName: template?.name || "",
      templateMode: template?.mode || "free",
      src,
      watermarkReport: imageResult.watermarkReports[index] || null,
      createdAt: generatedAt,
    }));
    state.latestGeneratedImages = generatedImages;
    renderGeneratedGrid();
    toast(`已生成 ${generatedImages.length} 张图片，可保存后加入任意创作任务`);
  } catch (error) {
    if (isAbortError(error)) {
      toast("已中止图片生成，提示词和模板已解锁");
    } else {
      toast(friendlyImageError(error, generationMode), { kind: "error", duration: 6500 });
    }
  } finally {
    if (imageGenerationController === controller) imageGenerationController = null;
    setImageGenerationRunning(false);
  }
}

async function optimizePrompt() {
  const template = activeImageStudioTemplate();
  const replacing = template?.mode === "scene" && state.imageGenerationMode !== "create";
  const field = replacing ? $("#replacementDescriptionInput") : $("#imageContentInput");
  const current = field.value.trim();
  if (!current) {
    field.value = replacing
      ? (state.imageGenerationMode === "replace_person" ? "自然真实的年轻女性，面向镜头，服装与场景协调" : "一只白色陶瓷咖啡杯，杯口朝上，尺寸和透视与原物品一致")
      : "一杯抹茶拿铁放在木桌上，旁边有打开的书和手写便签，自然光从窗边照进来，画面真实、主体清晰";
    toast("已填入示例内容");
    return;
  }
  if (!ensureAiConfigured("text")) return;
  const done = setButtonLoading($("#optimizePromptBtn"), "优化中...");
  try {
    const result = await runTextAi(
      "image-prompt",
      `请把下面这段${replacing ? "局部替换主体" : "生图"}描述优化得更明确。保留用户原意，补充必要的外观、材质、比例和光线要求。只输出优化后的描述。\n\n${current}`,
      { title: current, body: current, tags: "", source: "image", images: [] }
    );
    field.value = result || current;
    toast("画面描述已优化");
  } catch (error) {
    toast(`提示词优化失败：${error.message}`);
  } finally {
    done();
  }
}


// ----- js/rewrite/generate.js -----
// 生成是增量落库的：文本一次写完即存，图片每完成一张就写回并保存。中止时不能整体
// 回滚，否则会把用户已经拿到手的产出一起清掉。这里记录「最近一次已落库的产出」，
// 中止时只按它复原产出字段，未产出的元数据仍回到生成前的快照。
let rewriteCommittedOutput = null;

function draftOutputSnapshot(draft) {
  return {
    // 文本和图片一样是增量落库的，"已落地产出"必须把文本一起记下来：
    // 只记图片的话，中止时文本就只能整体退回生成前（P74）。
    title: draft.title,
    body: draft.body,
    tags: draft.tags,
    images: [...(draft.images || [])],
    templateRenderImages: [...(draft.templateRenderImages || [])],
    templateRenderImageLabels: [...(draft.templateRenderImageLabels || [])],
    templateRenderImageStates: [...(draft.templateRenderImageStates || [])],
    templateRenderWatermarkReports: [...(draft.templateRenderWatermarkReports || [])],
    generatedAt: draft.generatedAt,
  };
}

// 中止时把「产出字段」复原到最近一次已落库的产出，而不是生成前：文本先落库、
// 图片生成到一半被中止时，整体退回生成前会把新文案一起丢掉，提示却写着
// 「其余内容未改动」（P74）。没有 committed（快照缺失）时退回生成前快照，
// 语义等于"本次什么都没落库"。
function restoreCommittedRewriteOutput(draft, beforeGeneration, committed) {
  const source = committed || draftOutputSnapshot(beforeGeneration);
  draft.title = source.title;
  draft.body = source.body;
  draft.tags = source.tags;
  draft.images = [...source.images];
  draft.templateRenderImages = [...source.templateRenderImages];
  draft.templateRenderImageLabels = [...source.templateRenderImageLabels];
  draft.templateRenderImageStates = [...source.templateRenderImageStates];
  draft.templateRenderWatermarkReports = [...source.templateRenderWatermarkReports];
  draft.generatedAt = source.generatedAt;
  return source;
}

async function rewriteDraft() {
  if (rewriteGenerationController) {
    if (String(rewriteGenerationOwnerDraftId || "") !== String(state.activeDraftId || "")) {
      toast("另一个快速笔记任务正在生成，请先切回该任务");
      return;
    }
    if (!rewriteGenerationController.signal.aborted) {
      const controller = rewriteGenerationController;
      state.rewriteGeneration = { ...state.rewriteGeneration, cancelling: true };
      controller.abort();
      // Release the visible control immediately. The running task keeps its
      // own controller and restores the draft in its finally block.
      if (rewriteGenerationController === controller) rewriteGenerationController = null;
      state.rewriteGeneration = { active: false, ownerDraftId: "", cancelling: false, stage: "text", completed: 0, total: 0, currentLabel: "" };
      renderEditor();
      toast("正在停止生成笔记...");
    }
    return;
  }
  const draft = syncFormToDraft();
  if (!draft) return;
  // 搭建笔记内容也登记进同一套每草稿注册表（full）：它写的是同一份 executionPlan，
  // 与生成同时跑会互相覆盖。单独给一句指名道姓的话，别让用户对着"生成任务"猜。
  if (typeof viralPromptBuildController !== "undefined"
    && viralPromptBuildController
    && typeof viralPromptTaskBelongsToRewriteDraft === "function"
    && viralPromptTaskBelongsToRewriteDraft(draft)) {
    toast("正在搭建笔记内容，请等它结束或先点「停止搭建」后再生成");
    return;
  }
  // 账号维度：同一时间只允许一个 AI 任务，跨草稿也挡；提示点名是哪一份笔记在跑。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  // 单字段 / 单图 / 单视觉角色正在跑时，一键生成也必须被挡住：几条流程都在写同一份草稿。
  if (draftGenerationBusy(draft.id)) {
    toast("这份笔记上已有生成任务在跑，请等它结束后再试");
    return;
  }
  // 原创笔记走的是"选题 → 模板 → 生成"这条链：缺选题就没有执行计划，生成会退回拿草稿
  // 原文直接改写，与按钮上那句"使用模板生成图文"说的不是一回事（台账 P106）。入口按钮的
  // 置灰与这里是同一条判据 draftNeedsViralTopic；提示沿用"搭建笔记内容"入口的说法。
  if (draftNeedsViralTopic(draft)) {
    toast("请先从选题库选择一个选题");
    return;
  }
  const replicaMode = isReplicaDraft(draft);
  const replicaImageMode = isReplicaImageDraft(draft);
  updateViralPromptTask({}, { hide: true });
  // A complete original-note plan remains usable after a refresh even though
  // transient local-reference session data is deliberately not persisted.
  const hasReusableViralExecutionPlan = !replicaMode
    && isViralCombinationDraft(draft)
    && hasReadyViralExecutionPlan(draft);
  if (!replicaMode && !hasReusableViralExecutionPlan && requireKnowledgeDecision(draft, rewriteDraft)) return;
  if (isViralCombinationDraft(draft) && !hasReadyViralExecutionPlan(draft)) {
    toast("当前图文还没有完整的正式执行包，请先确认资料并搭建笔记内容");
    return;
  }
  // 逐页文字改过但还没签收时先用同一道门拦下；规划态（文字还没落）不受影响。
  if (!guardVisualCopyReview(draft)) return;
  // 空草稿（无标题/正文/标签/图片）也会真实调用 AI 并真实扣费（P73）。入口、按钮门控
  // 与后端 /api/ai/text(-bundle) 共用同一条判据，避免前端拦、后端放的分叉。
  if (!hasReusableViralExecutionPlan && !draftHasUsableContent(draft)) {
    toast(EMPTY_DRAFT_GENERATION_HINT, { kind: "error", duration: 5000 });
    return;
  }
  let executionPlan = !replicaMode && visualExecutionPlanMatchesDraft(draft) ? draft.executionPlan : null;
  if (!ensureAiConfigured("text")) return;
  const pendingGeneratedContent = executionPlan ? generatedContentForDraft(draft) : null;
  const standardRewrite = !executionPlan;
  const standardImagePrompts = standardRewrite ? {
    cover: draftAiPrompt(draft, "cover").trim(),
    inner: draftAiPrompt(draft, "inner").trim(),
  } : { cover: "", inner: "" };
  if ((executionPlan && draft.coverTemplateId && draft.innerTemplateId) || standardImagePrompts.cover || standardImagePrompts.inner) {
    if (!ensureAiConfigured("image")) return;
  }
  const controller = new AbortController();
  const beforeGeneration = {
    title: draft.title,
    body: draft.body,
    tags: draft.tags,
    generatedContentEdits: draft.generatedContentEdits ? { ...draft.generatedContentEdits } : draft.generatedContentEdits,
    promptMode: draft.promptMode ? { ...draft.promptMode } : draft.promptMode,
    templateFields: draft.templateFields ? JSON.parse(JSON.stringify(draft.templateFields)) : draft.templateFields,
    templateRenderMode: draft.templateRenderMode,
    imageGenerationError: draft.imageGenerationError,
    generationFailedStage: draft.generationFailedStage,
    images: [...(draft.images || [])],
    templateRenderImages: [...(draft.templateRenderImages || [])],
    templateRenderImageLabels: [...(draft.templateRenderImageLabels || [])],
    templateRenderImageStates: [...(draft.templateRenderImageStates || [])],
    templateRenderWatermarkReports: [...(draft.templateRenderWatermarkReports || [])],
    imageGenerationFailedLabels: [...(draft.imageGenerationFailedLabels || [])],
    imageGenerationFailedJobKeys: [...(draft.imageGenerationFailedJobKeys || [])],
    generatedAt: draft.generatedAt,
    hasSourceImages: Array.isArray(draft.sourceImages),
    sourceImages: [...(draft.sourceImages || [])],
    executionPlan: draft.executionPlan ? JSON.parse(JSON.stringify(draft.executionPlan)) : draft.executionPlan,
  };
  // 生成前即视为「已落地产出」：中止于零产出时，复原结果与改动前完全一致。
  rewriteCommittedOutput = draftOutputSnapshot(draft);
  rewriteGenerationController = controller;
  rewriteGenerationOwnerDraftId = draft.id || "";
  const generationRun = beginDraftGenerationRun(draft.id, "exclusive");
  state.rewriteGeneration = { active: true, ownerDraftId: rewriteGenerationOwnerDraftId, cancelling: false, stage: "text", completed: 0, total: 0, currentLabel: "" };
  let generationStage = "text";
  let textGenerationFailures = [];
  try {
    // 登记与 try 之间不留可抛错语句：这段窗口里抛一次，计数就永远减不回去，
    // 这份草稿的主按钮会被永久置灰。
    renderEditor();
    state.activeWorkflow = draftWorkflow(draft);
    if (executionPlan && draft.coverTemplateId && draft.innerTemplateId && !hasReadyViralImagePlan(draft)) {
      if (!draft.viralRecipeId) throw new Error("当前笔记内容计划缺少配方编号，请重新搭建笔记内容");
      state.rewriteGeneration = { ...state.rewriteGeneration, stage: "text", currentLabel: "正在生成并锁定文案" };
      renderEditor();
      const lockedData = await postBackend("/api/viral/combinations/lock-copy", {
        recipeId: draft.viralRecipeId,
      }, {
        signal: controller.signal,
        timeoutMinutes: 25,
        timeoutLabel: "逐页文案任务",
      });
      throwIfAborted(controller.signal);
      const linkedPlans = linkedSeriesData(lockedData);
      const linkedPlan = linkedPlans.find((entry) => (
        draft.seriesId && String(entry.execution?.seriesId || entry.plan?.activeSeriesId || "") === String(draft.seriesId)
      )) || linkedPlans[0];
      if (!linkedPlan) throw new Error("文案生成完成，但没有返回可用的执行计划");
      applyViralPlanToDraft(draft, linkedPlan.data);
      executionPlan = draft.executionPlan;
      if (!hasReadyViralImagePlan(draft)) throw new Error("逐页文案尚未完整锁定，请重试一键生成");
    }
    if (executionPlan && draft.coverTemplateId && draft.innerTemplateId) {
      if (!Array.isArray(draft.sourceImages) || !draft.sourceImages.length) {
        draft.sourceImages = [...(draft.images || [])];
      }
      draft.templateRenderMode = "ai-pending";
      draft.imageGenerationError = "";
      draft.imageGenerationFailedLabels = [];
      draft.imageGenerationFailedJobKeys = [];
      saveState();
      renderEditor();
    }
    const unifiedContent = executionPlan?.generatedContent && typeof executionPlan.generatedContent === "object"
      ? executionPlan.generatedContent
      : null;
    let title;
    let body;
    let tags;
    if (pendingGeneratedContent?.title && pendingGeneratedContent?.body) {
      title = pendingGeneratedContent.title;
      body = pendingGeneratedContent.body;
      tags = pendingGeneratedContent.tags;
    }
    if (unifiedContent?.title && unifiedContent?.body) {
      title = title || unifiedContent.title;
      body = body || unifiedContent.body;
      tags = tags || unifiedContent.tags || "";
    } else if (!title || !body) {
      const titlePrompt = executionPlan?.titlePrompt || draftPromptValue(draft, "titlePrompt");
      const bodyPrompt = executionPlan?.bodyPrompt || draftPromptValue(draft, "bodyPrompt");
      const tagsPrompt = executionPlan?.tagsPrompt || draftPromptValue(draft, "tagsPrompt");
      if (replicaImageMode) {
        const imageInputs = await textAiImageInputs(draft, controller.signal);
        const prompts = { title: titlePrompt, body: bodyPrompt, tags: tagsPrompt };
        const generated = { title: "", body: "", tags: "" };
        let bundleError = null;
        state.rewriteGeneration = { ...state.rewriteGeneration, currentLabel: "正在一次生成标题、正文和标签" };
        renderEditor();
        try {
          Object.assign(generated, await runTextAiBundle(prompts, draft, {
            signal: controller.signal,
            imageInputs,
          }));
        } catch (error) {
          if (isAbortError(error)) throw error;
          bundleError = error;
        }
        const fallbackFields = [
          ["title", titlePrompt, "正在补齐标题"],
          ["body", bodyPrompt, "正在补齐正文"],
          ["tags", tagsPrompt, "正在补齐标签"],
        ].filter(([field]) => !String(generated[field] || "").trim());
        for (const [field, fieldPrompt, label] of fallbackFields) {
          throwIfAborted(controller.signal);
          state.rewriteGeneration = { ...state.rewriteGeneration, currentLabel: label };
          renderEditor();
          try {
            const fallbackDraft = {
              ...draft,
              title: generated.title || draft.title,
              body: generated.body || draft.body,
              tags: generated.tags || draft.tags,
            };
            generated[field] = await runTextAi(
              field,
              composePrompt(fieldPrompt, fallbackDraft),
              fallbackDraft,
              { signal: controller.signal, imageInputs },
            );
          } catch (error) {
            if (isAbortError(error)) throw error;
            textGenerationFailures.push(`${field === "title" ? "标题" : field === "body" ? "正文" : "标签"}: ${error.message}`);
          }
        }
        title = generated.title || draft.title;
        body = generated.body || draft.body;
        tags = generated.tags || draft.tags;
        if (!title && !body) {
          throw bundleError || new Error("图生文案没有返回可用的标题或正文");
        }
      } else {
        state.rewriteGeneration = { ...state.rewriteGeneration, currentLabel: "正在生成标题" };
        renderEditor();
        title = await runTextAi("title", composePrompt(titlePrompt, draft), draft, { signal: controller.signal });
        throwIfAborted(controller.signal);
        state.rewriteGeneration = { ...state.rewriteGeneration, currentLabel: "正在生成正文" };
        renderEditor();
        body = await runTextAi("body", composePrompt(bodyPrompt, draft), draft, { signal: controller.signal });
        throwIfAborted(controller.signal);
        state.rewriteGeneration = { ...state.rewriteGeneration, currentLabel: "正在生成标签" };
        renderEditor();
        tags = await runTextAi("tags", composePrompt(tagsPrompt, draft), draft, { signal: controller.signal });
      }
    }
    throwIfAborted(controller.signal);
    draft.generatedContentEdits = {};
    draft.title = await fitXhsTitle(title, draft, { signal: controller.signal }) || draft.title;
    draft.body = body || draft.body;
    draft.tags = normalizeXhsTags(tags, xhsTagFallbacksForDraft(draft)) || draft.tags;
    if (draft.promptMode) {
      draft.promptMode.title = false;
      draft.promptMode.body = false;
      draft.promptMode.tags = false;
    }
    if (standardRewrite && (standardImagePrompts.cover || standardImagePrompts.inner)) {
      generationStage = "image";
      const sourceImages = normalizeImages(
        Array.isArray(draft.sourceImages) && draft.sourceImages.length ? draft.sourceImages : draft.images || []
      ).slice(0, 18);
      if (!Array.isArray(draft.sourceImages) || !draft.sourceImages.length) draft.sourceImages = [...sourceImages];
      const imageTargets = sourceImages.length ? sourceImages : [""];
      const bodyPoints = splitDraftPoints(draft.body);
      state.rewriteGeneration = {
        ...state.rewriteGeneration,
        stage: "image",
        completed: 0,
        total: imageTargets.length,
        concurrency: Math.min(IMAGE_GENERATION_CONCURRENCY, imageTargets.length),
      };
      saveState();
      // 文本到这里已经落库（上面刚写完就是这次 saveState 存下去的），所以"已落地产出"
      // 要立刻跟上：图片阶段被中止时，回滚到的是这份新文案，不是生成前的旧文本（P74）。
      rewriteCommittedOutput = draftOutputSnapshot(draft);
      renderEditor();
      const generatedImages = new Array(imageTargets.length);
      const watermarkReports = new Array(imageTargets.length).fill(null);
      draft.images = [...sourceImages];
      draft.templateRenderImages = [];
      draft.templateRenderImageLabels = [];
      draft.templateRenderImageStates = [];
      draft.templateRenderWatermarkReports = new Array(draft.images.length).fill(null);
      await runConcurrentJobs(imageTargets.map((src, index) => ({ src, index })), Math.min(IMAGE_GENERATION_CONCURRENCY, imageTargets.length), async ({ src, index }) => {
        throwIfAborted(controller.signal);
        const focus = bodyPoints[index % Math.max(1, bodyPoints.length)] || draft.body || draft.title;
        const rolePrompt = (index === 0
          ? standardImagePrompts.cover || standardImagePrompts.inner
          : standardImagePrompts.inner || standardImagePrompts.cover) || "";
        // 复刻（快速笔记）只执行提示词本身。这里曾为每一页追加「这是第 N/M 张原
        // 笔记图片的编辑任务」「当前生成后的文案可作为画面内容参考：标题/正文/标签」
        // 「提示词中的明确修改要求优先于原图内容」三段硬编码指令：注入的文案逐页
        // 完全相同，出图必然同质化；同时「修改要求优先于原图内容」配合整篇新文案
        // 会让模型改写原图文字，原文就没了。除提示词外只保留「没有输入参考图」这一
        // 条事实说明。原创（original）路径的逐页差异化指令保持不变。
        const imagePrompt = replicaMode
          ? [
              rolePrompt,
              src ? "" : "当前没有输入参考图，请按提示词生成原创画面。",
            ].filter(Boolean).join("\n\n")
          : [
              rolePrompt,
              `请为以下新文案生成第 ${index + 1}/${imageTargets.length} 张小红书图片。`,
              index === 0 ? `本张作为封面，突出标题：${draft.title}` : `本张作为内页，重点表达：${focus}`,
              `正文：${draft.body}\n标签：${draft.tags}`,
              src ? "结合输入图片完成编辑和新的视觉表达。" : "当前没有输入参考图，请按提示词生成原创画面。",
            ].filter(Boolean).join("\n\n");
        const inputImage = src ? await imageInputSourceForAi(src, controller.signal) : "";
        const imageResult = await runImageAi(imagePrompt, {
          count: 1,
          size: "1024x1536",
          quality: "medium",
          inputImages: inputImage ? [inputImage] : [],
          preferEdit: Boolean(inputImage),
          signal: controller.signal,
        });
        if (!imageResult.images?.[0]) throw new Error(`第 ${index + 1} 张图片未返回结果`);
        generatedImages[index] = imageResult.images[0];
        watermarkReports[index] = imageResult.watermarkReports?.[0] || null;
        draft.images = imageTargets.map((original, imageIndex) => generatedImages[imageIndex] || original).filter(Boolean);
        draft.templateRenderWatermarkReports = watermarkReports.slice(0, draft.images.length);
        draft.generatedAt = new Date().toISOString();
        state.rewriteGeneration.completed += 1;
        saveState();
        rewriteCommittedOutput = draftOutputSnapshot(draft);
        if (state.currentSection === "rewrite") renderEditor();
      });
      draft.images = generatedImages.filter(Boolean);
      draft.templateRenderImages = [];
      draft.templateRenderImageLabels = [];
      draft.templateRenderImageStates = [];
      draft.templateRenderWatermarkReports = watermarkReports.slice(0, draft.images.length);
      draft.generatedAt = new Date().toISOString();
      if (draft.promptMode) {
        draft.promptMode.cover = false;
        draft.promptMode.inner = false;
      }
      saveState();
      renderEditor();
      toast(replicaMode
        ? (replicaImageMode
          ? (textGenerationFailures.length
            ? `图生文案已完成，部分字段失败：${textGenerationFailures.join("；")}`
            : "已根据图片生成标题、正文和标签")
          : `已按提示词改写文案并完成 ${draft.images.length} 张图片`)
        : `已按默认提示词生成新文案和 ${draft.images.length} 张配图`);
      return;
    }
    if (executionPlan && draft.coverTemplateId && draft.innerTemplateId) {
      generationStage = "image";
      state.rewriteGeneration.stage = "image";
      saveState();
      // 同上：锁定好的文案已经落库，套模板阶段被中止时要留在草稿里（P74）。
      rewriteCommittedOutput = draftOutputSnapshot(draft);
      renderEditor();
      await applyTemplateCombination(draft.coverTemplateId, draft.innerTemplateId, draft.templateSetId || "", { requireAi: true, signal: controller.signal });
      if (draft.promptMode) {
        draft.promptMode.cover = false;
        draft.promptMode.inner = false;
      }
      saveState();
      renderEditor();
      toast("已按爆款执行计划生成 1 份完整图文");
      return;
    }
    const template = draftLayoutTemplate(draft);
    if (template && isLayeredTemplate(template)) {
      await fillLayeredTemplateWithAi(draft, template, { signal: controller.signal });
      saveState();
      renderEditor();
      await updateTemplatePreviewFromFields({ signal: controller.signal });
      return;
    }
    saveState();
    renderEditor();
    toast(replicaImageMode ? "已根据图片生成标题、正文和标签" : "已完成一键 AI 生成");
  } catch (error) {
    if (isAbortError(error)) {
      // 设置类字段（编辑痕迹、提示词模式、模板字段、失败标记）整体回到生成前；
      // 产出字段（文案与图片）只回到「最近一次已落库」，见 restoreCommittedRewriteOutput。
      draft.generatedContentEdits = beforeGeneration.generatedContentEdits
        ? { ...beforeGeneration.generatedContentEdits }
        : beforeGeneration.generatedContentEdits;
      draft.promptMode = beforeGeneration.promptMode ? { ...beforeGeneration.promptMode } : beforeGeneration.promptMode;
      draft.templateFields = beforeGeneration.templateFields ? JSON.parse(JSON.stringify(beforeGeneration.templateFields)) : beforeGeneration.templateFields;
      draft.templateRenderMode = beforeGeneration.templateRenderMode;
      draft.imageGenerationError = beforeGeneration.imageGenerationError;
      draft.generationFailedStage = beforeGeneration.generationFailedStage;
      const committed = restoreCommittedRewriteOutput(draft, beforeGeneration, rewriteCommittedOutput);
      draft.imageGenerationFailedLabels = [...beforeGeneration.imageGenerationFailedLabels];
      draft.imageGenerationFailedJobKeys = [...beforeGeneration.imageGenerationFailedJobKeys];
      draft.executionPlan = beforeGeneration.executionPlan
        ? JSON.parse(JSON.stringify(beforeGeneration.executionPlan))
        : beforeGeneration.executionPlan;
      if (beforeGeneration.hasSourceImages) draft.sourceImages = [...beforeGeneration.sourceImages];
      else delete draft.sourceImages;
      saveState();
      renderEditor();
      const retainedImages = committed.images.filter((src) => !beforeGeneration.images.includes(src)).length;
      const retainedText = committed.title !== beforeGeneration.title
        || committed.body !== beforeGeneration.body
        || committed.tags !== beforeGeneration.tags;
      const kept = [
        retainedImages ? `${retainedImages} 张图片` : "",
        retainedText ? "新文案" : "",
      ].filter(Boolean).join("与");
      toast(kept
        ? `已停止生成，本次已完成的${kept}已保留，其余内容未改动`
        : "已停止生成笔记，原内容已保留");
      return;
    }
    if (executionPlan && draft.coverTemplateId && draft.innerTemplateId) {
      draft.templateRenderMode = "ai-error";
      draft.generationFailedStage = generationStage;
      if (!(generationStage === "image" && (draft.imageGenerationFailedLabels?.length || draft.imageGenerationFailedJobKeys?.length))) {
        draft.imageGenerationError = `${generationStage === "image" ? "图片" : "文本"}接口：${error.message || "调用失败"}`;
      }
      saveState();
      renderEditor();
    }
    toast(`一键 AI 生成失败：${error.message}`);
  } finally {
    endDraftGenerationRun(generationRun);
    if (rewriteGenerationController === controller) rewriteGenerationController = null;
    if (!rewriteGenerationController) rewriteGenerationOwnerDraftId = "";
    state.rewriteGeneration = { active: false, ownerDraftId: "", cancelling: false, stage: "text", completed: 0, total: 0, currentLabel: "" };
    if (state.currentSection === "rewrite") renderEditor();
  }
}

async function generateVisualRole(role) {
  const draft = syncFormToDraft();
  if (!draft) return;
  if (!guardVisualCopyReview(draft)) return;
  // cover/inner：按各自的提示词逐页重绘；image：用「图片提示词」把当前所有页面图整组重绘。
  // 快速笔记只有一条图片提示词（封面/内页提示词框在复刻模式下被隐藏），它的图片块因此
  // 需要一个自己的生成动作，否则那一块只剩输入框、没有「生成」，与标题/正文/标签不一致。
  const allImages = role === "image";
  const roleLabel = allImages ? "图片" : role === "cover" ? "封面" : "内页";
  // 账号维度：别的草稿在跑 AI 任务时点名拒绝（按钮不置灰，点了才知道为什么）。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  if (draftFullGenerationActive(draft.id)) {
    toast(`整篇生成正在跑，请等它结束后再单独生成${roleLabel}`);
    return;
  }
  if (draftBlockGenerationBusy(draft.id, role)) {
    toast(`${roleLabel}正在生成中，请稍候`);
    return;
  }
  if (!ensureAiConfigured("image")) return;
  const promptInput = allImages
    ? $("#replicaVisualPrompt")
    : role === "cover" ? $("#coverPromptInput") : $("#innerPromptInput");
  const rolePrompt = promptInput?.value.trim() || draftAiPrompt(draft, allImages ? "cover" : role);
  if (!rolePrompt) {
    toast(`${roleLabel}提示词为空`);
    return;
  }
  const hasLockedPagePlan = hasReadyViralImagePlan(draft);
  const currentImages = normalizeImages(currentPreviewImages(draft));
  const targetPages = currentImages
    .map((src, index) => ({ src, index, label: index === 0 ? "封面" : `内页 ${index}` }))
    .filter(({ index }) => allImages ? true : role === "cover" ? index === 0 : index > 0);
  if (!targetPages.length) {
    toast(`当前没有可生成的${roleLabel}`);
    return;
  }
  const button = $(`[data-generate-ai-block="${role}"]`);
  const done = setButtonLoading(button, "生成中...");
  const roleRun = beginDraftGenerationRun(draft.id, "block", role);
  try {
    renderDraftGenerationIndicators(draft);
    // 整组生成时封面与内页共用这条提示词（复刻模式本来就只有一条），两边一起记下来。
    const promptWasCustomized = allImages
      ? Boolean(draft.imagePromptCustomized?.cover || draft.imagePromptCustomized?.inner)
      : replicaImagePromptIsCustomized(draft, role);
    draft.imageEditPrompts = { ...(draft.imageEditPrompts || {}) };
    if (allImages) {
      draft.imageEditPrompts.cover = rolePrompt;
      draft.imageEditPrompts.inner = rolePrompt;
    } else {
      draft.imageEditPrompts[role] = rolePrompt;
    }
    if (isReplicaDraft(draft)) {
      draft.imagePromptCustomized = { ...(draft.imagePromptCustomized || {}) };
      if (allImages) {
        draft.imagePromptCustomized.cover = promptWasCustomized;
        draft.imagePromptCustomized.inner = promptWasCustomized;
      } else {
        draft.imagePromptCustomized[role] = promptWasCustomized;
      }
    }
    const nextImages = [...currentImages];
    const nextLabels = Array.isArray(draft.templateRenderImageLabels) && draft.templateRenderImageLabels.length
      ? [...draft.templateRenderImageLabels]
      : currentImages.map((_, index) => index === 0 ? "封面" : `内页 ${index}`);
    const nextStates = Array.isArray(draft.templateRenderImageStates) ? [...draft.templateRenderImageStates] : [];
    const nextReports = Array.isArray(draft.templateRenderWatermarkReports) ? [...draft.templateRenderWatermarkReports] : [];
    await runConcurrentJobs(targetPages, Math.min(IMAGE_GENERATION_CONCURRENCY, targetPages.length), async ({ src, index, label }) => {
      const targetPage = hasLockedPagePlan
        ? (draft.executionPlan?.pageScript || [])[index] || {}
        : {};
      const lockedPageText = (targetPage.contentBlocks || [])
        .filter((block) => String(block?.text || "").trim())
        .map((block) => `- ${block.label || block.knowledgeType || block.role || "内容"}：${String(block.text).trim()}`)
        .join("\n");
      // 快速笔记（复刻）只执行提示词本身：图片就是"要改的那张图"，标题/正文/标签本来就
      // 是从图里读出来的，而且它只有一条图片提示词、不带其它页信息——逐页追加的边界句和
      // 保留句在这里是空转。与「一键生成」「编辑图弹层」保持同一口径。
      const replicaPromptOnly = isReplicaDraft(draft) && !hasLockedPagePlan;
      const preserveExistingContent = !hasLockedPagePlan
        ? "这是现有页面的视觉编辑任务，不是内容生成任务。必须逐字保留输入图中的全部文字、数字、标点、段落顺序和信息结构，不新增、不删减、不改写任何内容；只按视觉提示词调整字体、颜色、背景、材质、装饰或版式表现。"
        : "";
      const prompt = replicaPromptOnly
        ? rolePrompt
        : [
            rolePrompt,
            `当前只处理${label}，不要合并其它页面内容。`,
            preserveExistingContent,
            targetPage.visualDirection ? `本页视觉方向：${targetPage.visualDirection}` : "",
            lockedPageText ? `本页必须完整使用以下锁定文字，不得改写、补充或删减：\n${lockedPageText}` : "",
            hasLockedPagePlan
              ? (index === 0
                ? "封面保留原模板的构图、标题层级、主体关系和视觉焦点；颜色、字体、背景材质与装饰细节按用户视觉调整执行。"
                : INNER_VISUAL_SYSTEM_RULE)
              : "以输入图为唯一页面底稿，未在视觉提示词中要求变更的元素保持不变。",
          ].filter(Boolean).join("\n");
      const referenceImage = src;
      const imageResult = await runImageAi(prompt, {
        count: 1,
        size: "1024x1536",
        inputImages: referenceImage ? [referenceImage] : [],
        preferEdit: Boolean(referenceImage),
      });
      if (!imageResult.images?.[0]) throw new Error(`${label}的生图接口没有返回图片`);
      nextImages[index] = imageResult.images[0];
      nextLabels[index] = label;
      nextStates[index] = "generated";
      nextReports[index] = imageResult.watermarkReports?.[0] || null;
      draft.images = nextImages.filter(Boolean);
      draft.templateRenderImages = [...draft.images];
      draft.templateRenderImageLabels = nextLabels.slice(0, draft.images.length);
      draft.templateRenderImageStates = nextStates.slice(0, draft.images.length);
      draft.templateRenderWatermarkReports = nextReports.slice(0, draft.images.length);
      draft.generatedAt = new Date().toISOString();
      saveState();
      if (state.currentSection === "rewrite") renderEditor();
    });
    draft.images = nextImages.filter(Boolean);
    draft.templateRenderImages = [...draft.images];
    draft.templateRenderImageLabels = nextLabels.slice(0, draft.images.length);
    draft.templateRenderImageStates = nextStates.slice(0, draft.images.length);
    draft.templateRenderWatermarkReports = nextReports.slice(0, draft.images.length);
    draft.templateRenderMode = "ai-full-page";
    if (draft.promptMode) {
      if (allImages) {
        draft.promptMode.cover = false;
        draft.promptMode.inner = false;
      } else {
        draft.promptMode[role] = false;
      }
    }
    saveState();
    renderEditor();
    const doneLabel = allImages
      ? `${targetPages.length} 张图片`
      : role === "cover" ? "封面" : `${targetPages.length} 张内页`;
    toast(hasLockedPagePlan
      ? `${doneLabel}已按锁定文案重新生成`
      : `${doneLabel}已按提示词重绘，页面内容保持不变`);
  } catch (error) {
    toast(friendlyImageError(error), { kind: "error", duration: 6500 });
  } finally {
    endDraftGenerationRun(roleRun);
    done();
    renderDraftGenerationIndicators(draft);
    // 图片生成（封面/内页/整组）期间主按钮被置灰；收尾补一次重绘把它刷回来。
    renderEditor();
  }
}

function sendToPublish() {
  const draft = syncFormToDraft();
  if (!draft) return;
  // 发布列表的入口校验：标题、正文、图片全空的草稿曾在列表里显示成「已就绪」，
  // 点发布才发现没东西可发。判据与后端 normalize_publish_task_items 一致。
  const hasPublishContent = Boolean(
    String(draft.title || "").trim()
      || String(draft.body || "").trim()
      || normalizeImages(currentPreviewImages(draft)).length
  );
  if (!hasPublishContent) {
    toast("标题、正文或图片至少要有一项，才能加入发布列表", { kind: "error", duration: 6500 });
    return;
  }
  if (state.publish.some((item) => item.draftId && item.draftId === draft.id)) {
    toast("这条内容已经在发布列表里了，重复加入会在发布时重复发布", { kind: "error", duration: 6500 });
    return;
  }
  if (requiresViralTemplateFit(draft) && !hasReadyViralImagePlan(draft)) {
    toast("当前逐页文字或页面计划已失效，请重新搭建后再发布");
    return;
  }
  // 超限内容一旦进了发布列表，用户只会在小红书发布页被拒——在还能改的地方拦住。
  const limitProblems = xhsPublishLimitMessages(draft);
  if (limitProblems.length) {
    toast(`${limitProblems.join("；")}。请先在编辑区精简后再加入发布列表`, { kind: "error", duration: 7000 });
    return;
  }
  state.publish.unshift({
    id: uid("pub"),
    draftId: draft.id,
    title: draft.title,
    body: draft.body,
    tags: draft.tags,
    images: currentPreviewImages(draft),
    layoutTemplateId: draft.layoutTemplateId || "",
    coverTemplateId: draft.coverTemplateId || "",
    innerTemplateId: draft.innerTemplateId || "",
    templateSetId: draft.templateSetId || "",
    viralAnalysisId: draft.viralAnalysisId || "",
    viralRecipeId: draft.viralRecipeId || "",
    viralGenerationId: draft.viralGenerationId || "",
    viralAtomIds: draft.viralAtomIds || [],
    topicPackageId: draft.topicPackageId || draft.executionPlan?.topicPackage?.id || "",
    category: draft.viralCategory || (draft.tags?.match(/#[^\s#]+/) || ["未分类"])[0].replace(/^#/, ""),
    publishStatus: "ready",
    createdAt: new Date().toISOString(),
  });
  draft.status = "queued";
  saveState();
  renderAll();
  setSection("publish");
  toast("已加入发布列表");
}

// 单条删除从条目自己的「删除」按钮进来，批量删除从列表上方的「删除所选」进来（带 id 调用）。
// 不带 id 的调用仍然是安全的空操作：没有"隐式选中"这回事。
function removePublishItems(itemIds = null) {
  const selectedIds = [...new Set((Array.isArray(itemIds) ? itemIds : [itemIds])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean))];
  if (!selectedIds.length) return;
  const removed = state.publish.filter((item) => selectedIds.includes(item.id));
  if (!removed.length) return;
  if (typeof window !== "undefined" && typeof window.confirm === "function") {
    const message = removed.length === 1
      ? "删除这条待发布内容？此操作无法撤销。"
      : `删除选中的 ${removed.length} 条待发布内容？此操作无法撤销。`;
    if (!window.confirm(message)) return;
  }
  const affectedDraftIds = new Set(removed.map((item) => item.draftId).filter(Boolean));
  state.publish = state.publish.filter((item) => !selectedIds.includes(item.id));
  state.drafts = state.drafts.map((draft) => (
    affectedDraftIds.has(draft.id) && !state.publish.some((item) => item.draftId === draft.id)
      ? { ...draft, status: "editing" }
      : draft
  ));
  saveState();
  renderMetrics();
  renderDraftList();
  renderPublishList();
  toast(removed.length === 1 ? "已从发布列表删除" : `已从发布列表删除 ${removed.length} 条内容`);
}

async function loadLayoutTemplates() {
  try {
    const data = await getBackend("/api/templates");
    state.layoutTemplates = data.templates || [];
    reconcileRecoveredTemplateImport();
    if (!currentLayoutTemplate()) {
      state.activeLayoutTemplateId = state.layoutTemplates[0]?.id || null;
    }
    loadViralLibraries();
  } catch {
    state.layoutTemplates = [];
  }
}

async function retryViralDraftImages(button) {
  const draft = syncFormToDraft();
  if (!hasReadyViralImagePlan(draft)) {
    toast("当前草稿没有完整的爆款视觉执行计划");
    return;
  }
  if (!guardVisualCopyReview(draft)) return;
  // 账号维度：别的草稿在跑 AI 任务时点名拒绝（按钮不置灰，点了才知道为什么）。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  if (draftGenerationBusy(draft.id)) {
    toast("这份笔记上已有生成任务在跑，请等它结束后再试");
    return;
  }
  if (!ensureAiConfigured("image")) return;
  const done = button ? setButtonLoading(button, "AI 生图中...") : () => {};
  const retryRun = beginDraftGenerationRun(draft.id, "exclusive");
  try {
    if (!Array.isArray(draft.sourceImages) || !draft.sourceImages.length) {
      draft.sourceImages = [...(draft.images || [])];
    }
    draft.templateRenderMode = "ai-pending";
    draft.generationFailedStage = "";
    draft.imageGenerationError = "";
    draft.imageGenerationFailedLabels = [];
    draft.imageGenerationFailedJobKeys = [];
    saveState();
    renderEditor();
    await applyTemplateCombination(draft.coverTemplateId, draft.innerTemplateId, draft.templateSetId || "", { requireAi: true });
    toast("AI 新图已生成并替换参考图");
  } catch (error) {
    draft.templateRenderMode = "ai-error";
    draft.generationFailedStage = "image";
    draft.imageGenerationError = `图片接口：${error.message || "调用失败"}`;
    saveState();
    renderEditor();
    toast(friendlyImageError(error), { kind: "error", duration: 6500 });
  } finally {
    endDraftGenerationRun(retryRun);
    done();
  }
}

function templateAiPageIndexes(template, part = "all") {
  const references = template?.aiImage?.referenceImages || template?.originalImages || [];
  const pageCount = Math.max(1, Math.min(18, Number(template?.aiImage?.pageCount || references.length || 1)));
  const indexes = Array.from({ length: pageCount }, (_, index) => index);
  if (part === "all") return indexes;
  const roles = Array.isArray(template?.templatePageRoles) ? template.templatePageRoles : [];
  const matching = indexes.filter((index) => roles[index] === part);
  return matching.length ? matching : [0];
}

function templatePageKey(templateId, pageIndex) {
  return `${String(templateId || "")}:${Number(pageIndex)}`;
}

function draftExcludedTemplatePageKeys(draft) {
  return new Set(
    (Array.isArray(draft?.excludedTemplatePages) ? draft.excludedTemplatePages : [])
      .map((key) => String(key || "").trim())
      .filter(Boolean)
  );
}

function isDraftTemplatePageExcluded(draft, job) {
  const excluded = draftExcludedTemplatePageKeys(draft);
  return job?.isAdditional
    ? excluded.has(String(job.key || ""))
    : excluded.has(templatePageKey(job?.templateId, job?.pageIndex));
}

async function handleOriginalLink() {
  const input = $("#originalLinkInput");
  // 与快速笔记同一套：链接里夹空白会被后端按空白截断，先拼回再发（见 render.js 的
  // normalizeImportLinkInput）。
  const normalized = normalizeImportLinkInput(input?.value || "");
  const inputText = normalized.text;
  if (!inputText) return toast("请先粘贴至少一条小红书链接");
  if (normalized.repaired && input) input.value = inputText;
  const button = $("#originalImportBtn");
  const done = setButtonLoading(button, "正在导入...");
  try {
    state.activeWorkflow = "original";
    const job = await captureNoteLinks(inputText, (job) => {
      // 采集期间这个按钮归"取消采集"用，别把它的字覆盖掉。
      if (button.dataset.noteCaptureCancel === "1") return;
      const captured = Number(job?.progress?.captured || 0);
      const total = Number(job?.progress?.total || 0);
      button.textContent = total ? `正在浏览器中采集 ${captured}/${total}...` : "正在浏览器中采集...";
    }, { button });
    const notes = job.results || [];
    const imported = notes.map((note, index) => ({ note, index })).filter(({ note }) => note?.ok !== false);
    if (!imported.length) throw new Error(notes[0]?.message || notes[0]?.error || "没有抓取到可用内容");
    imported.slice().reverse().forEach(({ note, index }) => {
      const importedFields = normalizeImportedNoteFields(note.body ?? "", note.tags ?? "");
      const importedTitle = normalizeImportedNoteTitle(note.title ?? "", importedFields.body);
      createDraft({
        source: "link",
        workflow: "original",
        seed: importedTitle || `原创参考笔记 ${index + 1}`,
        origin: note.origin || note.url || "",
        title: importedTitle,
        body: importedFields.body,
        tags: importedFields.tags,
        images: normalizeImages(note.images).slice(0, 18),
      });
    });
    input.value = "";
    setSection("rewrite");
    toast(`已创建 ${imported.length} 个原创改写任务，可拆模板或使用四库组合`);
  } catch (error) {
    // 用户自己按的取消不是失败：中性提示，别套"导入失败"。
    // 插件没装这条诊断自带下一步（去首页装、刷新本页），再套一层"原创笔记导入失败"只是重复。
    toast(
      error?.noteCaptureNotice || error?.noteCaptureCancelled
        ? error.message
        : error?.extensionMissing
          ? error.message
          : `原创笔记导入失败：${error.message}`,
      { kind: error?.noteCaptureCancelled ? "info" : "error" },
    );
  } finally {
    done();
  }
}

function buildAiTemplateJobs(coverTemplate, innerTemplate) {
  const makeJob = (template, pageIndex, contentPart, label) => {
    const references = template?.aiImage?.referenceImages || template?.originalImages || [];
    return {
      templateId: template.id,
      pageIndex,
      contentPart,
      label,
      referenceImage: references[pageIndex] || "",
      key: templatePageKey(template.id, pageIndex),
    };
  };
  if (coverTemplate.id === innerTemplate.id) {
    const roles = Array.isArray(coverTemplate.templatePageRoles) ? coverTemplate.templatePageRoles : [];
    return templateAiPageIndexes(coverTemplate).map((pageIndex, index) => {
      const isCover = roles[pageIndex] === "cover" || (!roles.length && index === 0);
      return makeJob(
        coverTemplate,
        pageIndex,
        isCover ? "cover" : "inner",
        isCover ? "封面" : `内页 ${pageIndex + 1}`
      );
    });
  }
  const coverIndex = templateAiPageIndexes(coverTemplate, "cover")[0];
  const innerIndexes = templateAiPageIndexes(innerTemplate, "inner");
  return [
    makeJob(coverTemplate, coverIndex, "cover", "封面"),
    ...innerIndexes.map((pageIndex) => makeJob(innerTemplate, pageIndex, "inner", `内页 ${pageIndex + 1}`)),
  ];
}

function orderDraftInnerJobs(draft, jobs) {
  const innerJobs = jobs.filter((job) => job.contentPart === "inner");
  if (!innerJobs.length || !Array.isArray(draft?.innerPageOrder)) return jobs;
  const order = new Map(draft.innerPageOrder.map((key, index) => [String(key), index]));
  const orderedInner = [...innerJobs].sort((left, right) => {
    const leftIndex = order.has(left.key) ? order.get(left.key) : Number.MAX_SAFE_INTEGER;
    const rightIndex = order.has(right.key) ? order.get(right.key) : Number.MAX_SAFE_INTEGER;
    return leftIndex - rightIndex;
  });
  let innerIndex = 0;
  return jobs.map((job) => job.contentPart === "inner" ? orderedInner[innerIndex++] : job);
}

function orderDraftImageJobs(draft, jobs) {
  if (!jobs.length || !Array.isArray(draft?.imagePageOrder) || !draft.imagePageOrder.length) return jobs;
  const keys = new Set(jobs.map((job) => job.key));
  const orderedKeys = draft.imagePageOrder.map(String).filter((key) => keys.has(key));
  const coverKey = jobs.find((job) => job.contentPart === "cover")?.key;
  if (coverKey && !orderedKeys.includes(coverKey)) return jobs;
  const jobsByKey = new Map(jobs.map((job) => [job.key, job]));
  const ordered = orderedKeys.map((key) => jobsByKey.get(key)).filter(Boolean);
  const claimed = new Set(orderedKeys);
  return [...ordered, ...jobs.filter((job) => !claimed.has(job.key))];
}

const MAX_DRAFT_TEMPLATE_PAGES = 18;

function draftAdditionalTemplatePages(draft) {
  return Array.isArray(draft?.additionalTemplatePages)
    ? draft.additionalTemplatePages.filter((item) => item && Number.isInteger(Number(item.pageIndex)))
    : [];
}

function templateFromLibraryItem(item, sourceKind = "inner") {
  if (!item?.templateId) return null;
  const loaded = layoutTemplateById(item.templateId);
  if (loaded) return loaded;
  const source = item.aiImage || {};
  const scopedImages = item.referenceImages?.length ? item.referenceImages : source.referenceImages || [];
  const sourcePageIndexes = Array.isArray(source.sourcePageIndexes)
    ? source.sourcePageIndexes.map(Number).filter((index) => Number.isInteger(index) && index >= 0)
    : [];
  const pageCount = sourcePageIndexes.length
    ? Math.max(...sourcePageIndexes) + 1
    : Math.max(Number(source.pageCount || 0), scopedImages.length, 1);
  const references = Array(pageCount).fill("");
  const roles = Array(pageCount).fill("");
  const indexes = sourcePageIndexes.length
    ? sourcePageIndexes
    : Array.from({ length: scopedImages.length }, (_, index) => index);
  indexes.forEach((pageIndex, index) => {
    references[pageIndex] = scopedImages[index] || "";
    roles[pageIndex] = sourceKind;
  });
  return {
    ...item,
    id: item.templateId,
    aiImage: { ...source, pageCount, referenceImages: references, sourcePageIndexes },
    originalImages: references,
    templatePageRoles: roles,
  };
}

function templateFromInnerLibraryItem(item) {
  return templateFromLibraryItem(item, "inner");
}

function imagePickerTemplateLibraryItems(draft, sourceKind = "inner") {
  const items = [...(state.viralLibraries[sourceKind] || [])].filter((item) => item?.templateId);
  const currentTemplateId = String(draft?.[`${sourceKind}TemplateId`] || draft?.templateReferences?.[sourceKind]?.templateId || "");
  const currentItem = draftViralLibraryItem(draft, sourceKind);
  if (currentTemplateId && !items.some((item) => String(item.templateId) === currentTemplateId)) {
    const currentTemplate = layoutTemplateById(currentTemplateId);
    items.unshift(currentItem || {
      id: `${sourceKind}:${currentTemplateId}`,
      templateId: currentTemplateId,
      name: currentTemplate?.name || `当前${sourceKind === "cover" ? "封面" : "内页"}模板`,
      referenceImages: currentTemplate?.originalImages || [],
      aiImage: currentTemplate?.aiImage || {},
    });
  }
  const unique = new Map();
  items.forEach((item) => {
    if (!unique.has(String(item.templateId))) unique.set(String(item.templateId), item);
  });
  return [...unique.values()].sort((left, right) => {
    const leftCurrent = String(left.templateId) === currentTemplateId ? 0 : 1;
    const rightCurrent = String(right.templateId) === currentTemplateId ? 0 : 1;
    return leftCurrent - rightCurrent;
  }).filter((item) => {
    const template = templateFromLibraryItem(item, sourceKind);
    return template && templateAiPageIndexes(template, sourceKind).length > 0;
  });
}

function innerPageTemplateLibraryItems(draft) {
  return imagePickerTemplateLibraryItems(draft, "inner");
}

function imagePickerTemplatePageEntries(draft, templateItem = null, sourceKind = "inner") {
  const template = templateItem
    ? templateFromLibraryItem(templateItem, sourceKind)
    : draftTemplateForJobs(draft, sourceKind);
  if (!template) return [];
  const sourceItem = templateItem || (state.viralLibraries[sourceKind] || []).find((item) => String(item.templateId) === String(template.id));
  const scopedReferences = sourceItem?.referenceImages?.length
    ? sourceItem.referenceImages
    : sourceItem?.aiImage?.referenceImages || [];
  const sourcePageIndexes = Array.isArray(sourceItem?.aiImage?.sourcePageIndexes)
    ? sourceItem.aiImage.sourcePageIndexes.map(Number).filter((index) => Number.isInteger(index) && index >= 0)
    : [];
  const references = template?.aiImage?.referenceImages || template?.originalImages || [];
  const rendered = template?.renderedImages || [];
  const indexes = templateAiPageIndexes(template, sourceKind);
  const additionalPages = draftAdditionalTemplatePages(draft);
  return indexes.map((pageIndex, index) => {
    const sourcePageKey = templatePageKey(template.id, pageIndex);
    const scopedIndex = sourcePageIndexes.indexOf(pageIndex);
    const usageCount = additionalPages.filter((item) => (
      String(item.templateId || template.id) === String(template.id)
      && Number(item.pageIndex) === pageIndex
      && String(item.sourceKind || "inner") === sourceKind
    )).length;
    return {
      templateId: template.id,
      pageIndex,
      sourcePageKey,
      pickerKey: `${sourceKind}:${sourcePageKey}`,
      sourceKind,
      src: (scopedIndex >= 0 ? scopedReferences[scopedIndex] : "") || references[pageIndex] || rendered[pageIndex] || "",
      label: sourceKind === "cover" ? `封面 ${pageIndex + 1}` : `内页 ${pageIndex + 1}`,
      templateName: sourceItem?.name || template.name || `${sourceKind === "cover" ? "封面" : "内页"}模板`,
      usageCount,
    };
  });
}

function innerTemplatePageEntries(draft, templateItem = null) {
  return imagePickerTemplatePageEntries(draft, templateItem, "inner");
}

function imagePickerVisibleEntries(draft, sourceKind = "inner", selectedTemplateItem = null) {
  if (sourceKind === "cover") {
    return imagePickerTemplateLibraryItems(draft, "cover")
      .flatMap((item) => imagePickerTemplatePageEntries(draft, item, "cover"));
  }
  return imagePickerTemplatePageEntries(draft, selectedTemplateItem, "inner");
}

function templateSourceForJobs(draft, templateId, sourceKind = "inner") {
  if (String(templateId || "") === String(draft?.[`${sourceKind}TemplateId`] || "")) {
    return draftTemplateForJobs(draft, sourceKind);
  }
  const item = (state.viralLibraries[sourceKind] || []).find((candidate) => String(candidate.templateId) === String(templateId));
  return templateFromLibraryItem(item, sourceKind);
}

function innerTemplateSourceForJobs(draft, templateId) {
  return templateSourceForJobs(draft, templateId, "inner");
}

function draftTemplateForJobs(draft, kind) {
  const templateId = draft?.[`${kind}TemplateId`] || draft?.templateReferences?.[kind]?.templateId || "";
  if (!templateId) return null;
  const loaded = layoutTemplateById(templateId);
  if (loaded) return loaded;
  const reference = draft?.templateReferences?.[kind] || {};
  const snapshot = draftViralLibraryItem(draft, kind) || {};
  const images = reference.images || snapshot.referenceImages || [];
  const aiImage = { ...(snapshot.aiImage || {}), ...(reference.aiImage || {}) };
  aiImage.referenceImages = aiImage.referenceImages?.length ? aiImage.referenceImages : images;
  const sourcePageIndexes = Array.isArray(aiImage.sourcePageIndexes)
    ? aiImage.sourcePageIndexes.map(Number).filter((index) => Number.isInteger(index) && index >= 0)
    : [];
  const pageCount = sourcePageIndexes.length
    ? Math.max(...sourcePageIndexes) + 1
    : Number(aiImage.pageCount || aiImage.referenceImages.length || 1);
  const expandedReferences = sourcePageIndexes.length
    ? Array(pageCount).fill("")
    : [...aiImage.referenceImages];
  if (sourcePageIndexes.length) {
    sourcePageIndexes.forEach((sourceIndex, index) => {
      expandedReferences[sourceIndex] = aiImage.referenceImages[index] || "";
    });
  }
  aiImage.referenceImages = expandedReferences;
  aiImage.pageCount = pageCount;
  const roles = Array(pageCount).fill("");
  (sourcePageIndexes.length ? sourcePageIndexes : Array.from({ length: pageCount }, (_, index) => index))
    .forEach((index) => { roles[index] = kind; });
  return {
    id: templateId,
    aiImage,
    originalImages: expandedReferences,
    templatePageRoles: roles,
  };
}

function draftImageJobs(draft, { includeExcluded = false } = {}) {
  let coverTemplate = draftTemplateForJobs(draft, "cover");
  let innerTemplate = draftTemplateForJobs(draft, "inner");
  let jobs = [];
  if (coverTemplate && innerTemplate && coverTemplate.id === innerTemplate.id && coverTemplate !== innerTemplate) {
    const pageCount = Math.max(coverTemplate.aiImage.pageCount, innerTemplate.aiImage.pageCount);
    const references = Array(pageCount).fill("");
    const roles = Array(pageCount).fill("");
    [coverTemplate, innerTemplate].forEach((template) => {
      template.aiImage.referenceImages.forEach((src, index) => { if (src) references[index] = src; });
      template.templatePageRoles.forEach((role, index) => { if (role) roles[index] = role; });
    });
    coverTemplate = {
      ...coverTemplate,
      aiImage: { ...coverTemplate.aiImage, pageCount, referenceImages: references },
      originalImages: references,
      templatePageRoles: roles,
    };
    innerTemplate = coverTemplate;
  }
  if (coverTemplate && innerTemplate) {
    jobs = buildAiTemplateJobs(coverTemplate, innerTemplate);
  } else if (coverTemplate || innerTemplate) {
    const template = coverTemplate || innerTemplate;
    const contentPart = coverTemplate ? "cover" : "inner";
    const references = template?.aiImage?.referenceImages || template?.originalImages || [];
    jobs = templateAiPageIndexes(template, contentPart).map((pageIndex, index) => ({
      templateId: template.id,
      pageIndex,
      contentPart,
       label: contentPart === "cover" ? "封面" : `内页 ${pageIndex + 1}`,
      referenceImage: references[pageIndex] || "",
      key: templatePageKey(template.id, pageIndex),
    }));
  }
  const additionalJobs = draftAdditionalTemplatePages(draft)
    .map((item, index) => {
      const pageIndex = Number(item.pageIndex);
      const sourceKind = item.sourceKind === "cover" ? "cover" : "inner";
      const sourceTemplate = templateSourceForJobs(draft, item.templateId, sourceKind);
      if (!sourceTemplate && !item.referenceImage) return null;
      const references = sourceTemplate?.aiImage?.referenceImages || sourceTemplate?.originalImages || [];
      const templateId = sourceTemplate?.id || item.templateId;
      const key = `additional:${item.id || index}:${sourceKind === "cover" ? "cover:" : ""}${templatePageKey(templateId, pageIndex)}`;
      return {
        templateId,
        pageIndex,
        contentPart: "inner",
        label: sourceKind === "cover" ? (item.label || `封面素材 ${pageIndex + 1}`) : `内页 ${pageIndex + 1}`,
        referenceImage: item.referenceImage || references[pageIndex] || "",
        key,
        // 这个键必须和执行包 pageScript 的 orderKey 完全一致。以前优先用
        // item.orderKey（expansion:N:...），它和执行包对不上，单页生图就退化成
        // "按模板页序号找第一页"——一个模板页承载多个扩展页时全部命中同一页，
        // 于是生成出多张一模一样的图。
        orderKey: key,
        sourcePageKey: templatePageKey(templateId, pageIndex),
        sourceKind,
        sourceTemplateName: item.sourceTemplateName || sourceTemplate?.name || "",
        additionalId: item.id || String(index),
        isAdditional: true,
        promptParticipation: draft?.workflow === "replica"
          || (sourceKind === "inner" && item.promptParticipation !== "placeholder") ? "body" : "placeholder",
      };
    }).filter(Boolean);
  jobs = orderDraftImageJobs(draft, orderDraftInnerJobs(draft, [...jobs, ...additionalJobs]));
  if (!includeExcluded) jobs = jobs.filter((job) => !isDraftTemplatePageExcluded(draft, job));
  // 页签以执行包为准：扩页后一个模板页承载多页，"内页 ${模板页序号}" 会重复，
  // 界面上就会出现好几个「内页 2」。执行包里每页都带自己的 pageLabel。
  return jobs.map((job) => {
    if (job.contentPart === "cover") return job;
    const planned = plannedPageForImageJob(draft, job);
    const label = String(planned?.pageLabel || planned?.imageLabel || "");
    return label && label !== job.label ? { ...job, label } : job;
  });
}

function draftPromptImageJobs(draft, options = {}) {
  const jobs = draftImageJobs(draft, options);
  return draft?.workflow === "replica" ? jobs : jobs.filter((job) => job.promptParticipation !== "placeholder");
}

function draftPlannedPageEntries(draft, options = {}) {
  return draftImageJobs(draft, options)
    .map((job) => ({ ...job, src: job.referenceImage }))
    .filter((entry) => entry.src);
}

function renderInnerPagePicker() {
  const picker = state.innerPagePicker;
  const draft = picker?.draftId ? state.drafts.find((item) => item.id === picker.draftId) : currentDraft();
  const grid = $("#innerPagePickerGrid");
  if (!grid || !draft || !picker) return;
  const sourceKind = picker.sourceKind === "cover" || picker.sourceKind === "upload" ? picker.sourceKind : "inner";
  const isUpload = sourceKind === "upload";
  const isCoverGallery = sourceKind === "cover";
  const replicaPicker = draft?.workflow === "replica";
  $("#innerPagePickerModal .inner-page-picker-card")?.classList.toggle("is-replica-picker", replicaPicker);
  $$("[data-image-picker-source]").forEach((button) => {
    const active = button.dataset.imagePickerSource === sourceKind;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  $("#innerPagePickerTitle").textContent = "增加图片";
  $("#innerPagePickerSummary").textContent = replicaPicker
    ? (isUpload ? "选择本地图片，追加到当前快速笔记任务。" : `从${sourceKind === "cover" ? "封面" : "内页"}模板库选择图片，可重复加入。`)
    : sourceKind === "inner"
      ? "内页模板会参与正文拆页和逐页笔记内容搭建。"
      : `${isUpload ? "本地图片" : "封面库图片"}仅作为占位图，不参与正文生成和逐页笔记内容搭建。`;
  const templateBody = $("#innerPagePickerTemplateBody");
  templateBody?.classList.toggle("hidden", isUpload);
  templateBody?.classList.toggle("is-cover-gallery", isCoverGallery);
  $("#imagePickerUploadPanel")?.classList.toggle("hidden", !isUpload);

  if (isUpload) {
    const uploads = Array.isArray(picker.uploadFiles) ? picker.uploadFiles : [];
    // 图片在选择时就已落到资源库（草稿只留 URL），所以这里是「待加入」而不是「待上传」。
    $("#innerPagePickerCount").textContent = `${uploads.length} 张待加入`;
    $("#innerPagePickerSelectedCount").textContent = `已选择 ${uploads.length} 张`;
    $("#confirmInnerPagePickerBtn").disabled = !uploads.length;
    const uploadGrid = $("#imagePickerUploadGrid");
    if (uploadGrid) {
      uploadGrid.innerHTML = uploads.length
        ? uploads.map((item) => `<article class="image-picker-upload-item">
            <img src="${escapeHtml(item.src)}" alt="${escapeHtml(item.name || "上传图片")}" />
            <span>${escapeHtml(item.name || "上传图片")}</span>
            <button type="button" data-remove-image-picker-upload="${escapeHtml(item.id)}" aria-label="移除${escapeHtml(item.name || "上传图片")}" title="移除"><i data-lucide="x" aria-hidden="true"></i></button>
          </article>`).join("")
        : `<div class="image-picker-upload-empty"><i data-lucide="images" aria-hidden="true"></i><strong>还没有选择图片</strong><span>支持 JPG、PNG、WebP 等常见格式</span></div>`;
    }
    window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
    return;
  }

  const libraryItems = imagePickerTemplateLibraryItems(draft, sourceKind);
  const currentTemplateId = draft?.[`${sourceKind}TemplateId`] || draft?.templateReferences?.[sourceKind]?.templateId || "";
  const selectedTemplateId = picker.selectedTemplateId || currentTemplateId;
  const selectedTemplateItem = isCoverGallery
    ? null
    : libraryItems.find((item) => String(item.templateId) === String(selectedTemplateId)) || libraryItems[0];
  if (!isCoverGallery && selectedTemplateItem && picker.selectedTemplateId !== selectedTemplateItem.templateId) {
    picker.selectedTemplateId = selectedTemplateItem.templateId;
    picker.selectedKeys = new Set();
  }
  const entries = imagePickerVisibleEntries(draft, sourceKind, selectedTemplateItem);
  const selectedKeys = picker.selectedKeys instanceof Set ? picker.selectedKeys : new Set();
  const selectedCount = entries.filter((entry) => selectedKeys.has(entry.pickerKey) || selectedKeys.has(entry.sourcePageKey)).length;
  $("#innerPagePickerCount").textContent = `${entries.length} 张可选图片`;
  $("#innerPagePickerSelectedCount").textContent = `已选择 ${selectedCount} 张`;
  $("#confirmInnerPagePickerBtn").disabled = !selectedCount || !entries.length;
  $("#imagePickerTemplateSourceLabel").textContent = sourceKind === "cover" ? "封面模板库" : "内页模板库";
  $("#innerPageTemplateLibrary")?.setAttribute("aria-label", sourceKind === "cover" ? "封面模板库" : "内页模板库");
  const libraryMount = $("#innerPageTemplateLibrary");
  if (libraryMount) {
    libraryMount.innerHTML = isCoverGallery
      ? ""
      : libraryItems.length
      ? libraryItems.map((item) => {
        const active = String(item.templateId) === String(selectedTemplateItem?.templateId || "");
        const preview = item.referenceImages?.[0] || item.preview?.images?.[0] || item.aiImage?.referenceImages?.[0] || "";
        const pageCount = imagePickerTemplatePageEntries(draft, item, sourceKind).length;
        return `<button type="button" class="inner-page-template-library-item ${active ? "selected" : ""}" data-image-picker-template-id="${escapeHtml(item.templateId)}" aria-pressed="${active ? "true" : "false"}">
          ${preview ? `<img src="${escapeHtml(preview)}" alt="${escapeHtml(item.name || "图片模板")}" loading="lazy" onerror="this.closest('.inner-page-template-library-item').classList.add('is-missing'); this.remove()" />` : `<span class="inner-page-template-library-empty">暂无预览</span>`}
          <span class="inner-page-template-library-copy"><strong>${escapeHtml(item.name || "未命名模板")}</strong><small>${pageCount} 张可选</small></span>
        </button>`;
      }).join("")
      : `<div class="inner-page-template-library-empty">暂无可用${sourceKind === "cover" ? "封面" : "内页"}模板</div>`;
  }
  if (!entries.length) {
    grid.innerHTML = `<div class="inner-page-picker-empty">${isCoverGallery ? "封面库暂无可用图片" : "当前模板没有可用图片"}</div>`;
    return;
  }
  grid.innerHTML = entries.map((entry) => {
    const selected = selectedKeys.has(entry.pickerKey) || selectedKeys.has(entry.sourcePageKey);
    return `
    <button type="button" class="inner-page-picker-item ${selected ? "selected" : ""}" data-inner-page-picker-key="${escapeHtml(entry.pickerKey)}" aria-pressed="${selected ? "true" : "false"}">
      ${entry.src
        ? `<img src="${escapeHtml(entry.src)}" alt="${escapeHtml(entry.label)}" loading="lazy" onerror="this.closest('.inner-page-picker-item').classList.add('is-missing'); this.remove()" />`
        : `<span class="inner-page-picker-empty">暂无预览</span>`}
      <span class="inner-page-picker-check"><i data-lucide="${selected ? "check" : "plus"}" aria-hidden="true"></i></span>
      <span class="inner-page-picker-item-meta"><strong title="${escapeHtml(isCoverGallery ? entry.templateName : entry.label)}">${escapeHtml(isCoverGallery ? entry.templateName : entry.label)}</strong><span>${entry.usageCount ? `已加 ${entry.usageCount} 次` : (isCoverGallery ? "封面" : "可加入")}</span></span>
    </button>`;
  }).join("");
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function openInnerPagePicker(options = {}) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) return;
  if ((state.rewriteGeneration?.active && String(state.rewriteGeneration.ownerDraftId || "") === String(draft.id || "")) || state.regeneratingDraftImageKey) {
    toast("图片生成中，完成后再增加图片");
    return;
  }
  const sourceKind = options.forExpansion || draft?.workflow !== "replica" ? "inner" : "upload";
  const selectedTemplateId = sourceKind === "upload" ? "" : (
    draft.innerTemplateId
    || draft.templateReferences?.inner?.templateId
    || imagePickerTemplateLibraryItems(draft, "inner")[0]?.templateId
    || ""
  );
  state.innerPagePicker = {
    draftId: draft.id,
    sourceKind,
    selectedTemplateId,
    selectedKeys: new Set(),
    uploadFiles: [],
    expansionReview: Boolean(options.forExpansion),
    maxSelections: options.forExpansion ? 1 : 0,
  };
  renderInnerPagePicker();
  showModal($("#innerPagePickerModal"));
  if (!(state.viralLibraries.inner || []).length || !(state.viralLibraries.cover || []).length) {
    loadViralLibraries({ recommend: false });
  }
}

function closeInnerPagePicker() {
  const modal = $("#innerPagePickerModal");
  if (!modal) return;
  hideModal(modal, () => {
    state.innerPagePicker = null;
    $("#innerPagePickerGrid").innerHTML = "";
    $("#imagePickerUploadGrid").innerHTML = "";
    $("#imagePickerUploadInput").value = "";
  });
}

function appendUploadedImagesToDraft(draft, uploads) {
  const candidates = (uploads || []).filter((item) => item?.src);
  if (!draft || !candidates.length) return { added: 0, skipped: 0 };
  const renderedImages = normalizeImages(draft.templateRenderImages);
  const usesTemplateImages = Boolean(draft.templateRenderMode) || renderedImages.length > 0;
  const baseImages = usesTemplateImages
    ? (renderedImages.length ? renderedImages : normalizeImages(draft.images))
    : normalizeImages(draft.images);
  const available = Math.max(0, MAX_DRAFT_TEMPLATE_PAGES - baseImages.length);
  const eligible = candidates
    .filter((item, index, items) => !baseImages.includes(item.src) && items.findIndex((candidate) => candidate.src === item.src) === index);
  const additions = eligible.slice(0, available);
  if (usesTemplateImages && additions.length) {
    const labels = Array.isArray(draft.templateRenderImageLabels) ? [...draft.templateRenderImageLabels] : [];
    const states = Array.isArray(draft.templateRenderImageStates) ? [...draft.templateRenderImageStates] : [];
    const reports = Array.isArray(draft.templateRenderWatermarkReports) ? [...draft.templateRenderWatermarkReports] : [];
    const jobs = draftImageJobs(draft);
    while (labels.length < baseImages.length) {
      labels.push(jobs[labels.length]?.label || (labels.length === 0 ? "封面" : `图 ${labels.length + 1}`));
    }
    while (states.length < baseImages.length) states.push("reference");
    while (reports.length < baseImages.length) reports.push(null);
    additions.forEach((item) => {
      baseImages.push(item.src);
      labels.push("上传素材");
      states.push("reference");
      reports.push(null);
    });
    draft.templateRenderImages = baseImages;
    draft.templateRenderImageLabels = labels;
    draft.templateRenderImageStates = states;
    draft.templateRenderWatermarkReports = reports;
    draft.images = [...baseImages];
  } else if (additions.length) {
    draft.images = [...baseImages, ...additions.map((item) => item.src)];
  }
  if (draft?.workflow === "replica") {
    draft.sourceImages = [...new Set([...(draft.sourceImages || []), ...additions.map((item) => item.src)])];
  } else {
    draft.promptPlaceholderImages = [...new Set([
      ...normalizeImages(draft.promptPlaceholderImages),
      ...additions.map((item) => item.src),
    ])];
  }
  return { added: additions.length, skipped: candidates.length - additions.length, capped: eligible.length - additions.length };
}

function addSelectedInnerPages() {
  const picker = state.innerPagePicker;
  const draft = picker?.draftId ? state.drafts.find((item) => item.id === picker.draftId) : currentDraft();
  if (!draft) return;
  if (picker.expansionReview) {
    const selectedTemplateItem = imagePickerTemplateLibraryItems(draft, "inner")
      .find((item) => String(item.templateId) === String(picker.selectedTemplateId));
    const entries = imagePickerTemplatePageEntries(draft, selectedTemplateItem, "inner");
    const selectedEntries = entries.filter((entry) => picker.selectedKeys.has(entry.pickerKey) || picker.selectedKeys.has(entry.sourcePageKey));
    const review = draft.pageExpansionReview;
    if (!review || review.status !== "pending" || !selectedEntries.length) return;
    const pages = Array.isArray(review.pages) ? [...review.pages] : [];
    const beforeCount = pages.length;
    selectedEntries.forEach((entry) => pages.push({
      id: uid("expansion-page"), pageIndex: Number(entry.pageIndex) || 0, templateId: entry.templateId,
      sourceKind: "inner", sourcePageKey: entry.sourcePageKey, sourceTemplateName: entry.templateName || "",
      label: entry.label || `内页 ${pages.length + 1}`, referenceImage: entry.src || "",
      orderKey: `expansion:${pages.length + 1}:${entry.sourcePageKey}`,
    }));
    const maxAdditional = Math.max(0, MAX_DRAFT_TEMPLATE_PAGES - (Number(review.basePageCount) || 1));
    review.pages = pages.slice(0, maxAdditional);
    review.additionalPageCount = review.pages.length;
    review.estimatedPageCount = (Number(review.basePageCount) || 1) + review.pages.length;
    saveState(); closeInnerPagePicker(); renderEditor();
    toast(`已增加 ${Math.max(0, review.pages.length - beforeCount)} 个待确认页`);
    return;
  }
  if (picker.sourceKind === "upload") {
    const result = appendUploadedImagesToDraft(draft, picker.uploadFiles);
    if (!result.added) {
      // 此前直接 return：额度用尽或全是重复图时用户「点了没反应」。
      toast(result.capped
        ? `当前图文已有 ${MAX_DRAFT_TEMPLATE_PAGES} 张图片，达到单篇上限，未加入新图片；请先删除部分图片再添加`
        : "所选图片已在当前图文中，未重复加入");
      renderInnerPagePicker();
      return;
    }
    saveState();
    closeInnerPagePicker();
    renderEditor();
    toast(result.skipped ? `已增加 ${result.added} 张图片，另有 ${result.skipped} 张未加入（最多保留 ${MAX_DRAFT_TEMPLATE_PAGES} 张）` : `已增加 ${result.added} 张图片`);
    return;
  }
  if (!(picker.selectedKeys instanceof Set) || !picker.selectedKeys.size) return;
  const sourceKind = picker.sourceKind === "cover" ? "cover" : "inner";
  const libraryItems = imagePickerTemplateLibraryItems(draft, sourceKind);
  const selectedTemplateItem = libraryItems.find((item) => String(item.templateId) === String(picker.selectedTemplateId));
  const entries = imagePickerVisibleEntries(draft, sourceKind, selectedTemplateItem);
  const selectedEntries = entries.filter((entry) => picker.selectedKeys.has(entry.pickerKey) || picker.selectedKeys.has(entry.sourcePageKey));
  const previousJobs = draftImageJobs(draft);
  if (!selectedEntries.length) return;
  const baseJobs = draftImageJobs({ ...draft, additionalTemplatePages: [] }, { includeExcluded: true });
  const excluded = draftExcludedTemplatePageKeys(draft);
  const additions = draftAdditionalTemplatePages(draft).map((item) => ({ ...item }));
  let added = 0;
  let restored = 0;
  let skipped = 0;
  selectedEntries.forEach((entry) => {
    const baseJob = baseJobs.find((job) => job.key === entry.sourcePageKey && job.contentPart === sourceKind);
    if (baseJob && excluded.has(entry.sourcePageKey)) {
      excluded.delete(entry.sourcePageKey);
      restored += 1;
      return;
    }
    const currentJobs = draftImageJobs({ ...draft, additionalTemplatePages: additions }, { includeExcluded: true });
    if (currentJobs.length >= MAX_DRAFT_TEMPLATE_PAGES) {
      skipped += 1;
      return;
    }
    additions.push({
      id: uid("image-page"),
      templateId: entry.templateId,
      pageIndex: entry.pageIndex,
      label: sourceKind === "cover" ? `封面素材 ${entry.pageIndex + 1}` : entry.label,
      contentPart: "inner",
      sourceKind,
      promptParticipation: draft?.workflow === "replica" || sourceKind === "inner" ? "body" : "placeholder",
      sourcePageKey: entry.sourcePageKey,
      sourceTemplateName: entry.templateName || "",
      referenceImage: entry.src || "",
      createdAt: new Date().toISOString(),
    });
    added += 1;
  });
  draft.additionalTemplatePages = additions;
  if (draft?.workflow !== "replica") {
    draft.promptPlaceholderImages = [...new Set([
      ...normalizeImages(draft.promptPlaceholderImages),
      ...additions.filter((item) => item.promptParticipation === "placeholder").map((item) => item.referenceImage),
    ])].filter(Boolean);
  }
  draft.excludedTemplatePages = [...excluded];
  const jobs = draftImageJobs(draft);
  reorderDraftAssetArrays(draft, previousJobs, jobs, { includeNewReferences: true });
  if (added || restored) invalidateDraftExecutionPlan(draft, "page-set-changed");
  draft.templateRenderMode = draft.templateRenderMode || "ai-pending";
  saveState();
  closeInnerPagePicker();
  renderEditor();
  if (skipped) toast(`已增加 ${added + restored} 张图片，最多保留 ${MAX_DRAFT_TEMPLATE_PAGES} 张`);
  else toast(`已增加 ${added + restored} 张图片`);
}

function toggleInnerPagePickerItem(key) {
  const picker = state.innerPagePicker;
  if (!picker) return;
  if (!(picker.selectedKeys instanceof Set)) picker.selectedKeys = new Set();
  if (picker.selectedKeys.has(key)) picker.selectedKeys.delete(key);
  else {
    if (Number(picker.maxSelections) === 1) picker.selectedKeys.clear();
    picker.selectedKeys.add(key);
  }
  renderInnerPagePicker();
}

function selectInnerPagePickerTemplate(templateId) {
  const picker = state.innerPagePicker;
  if (!picker || !templateId || String(picker.selectedTemplateId) === String(templateId)) return;
  picker.selectedTemplateId = templateId;
  picker.selectedKeys = new Set();
  renderInnerPagePicker();
}

function selectImagePickerSource(sourceKind) {
  const picker = state.innerPagePicker;
  const draft = picker?.draftId ? state.drafts.find((item) => item.id === picker.draftId) : currentDraft();
  if (!picker || !draft || !["cover", "inner", "upload"].includes(sourceKind) || picker.sourceKind === sourceKind) return;
  picker.sourceKind = sourceKind;
  picker.selectedKeys = new Set();
  picker.selectedTemplateId = sourceKind === "upload" ? "" : (
    draft?.[`${sourceKind}TemplateId`]
    || draft?.templateReferences?.[sourceKind]?.templateId
    || imagePickerTemplateLibraryItems(draft, sourceKind)[0]?.templateId
    || ""
  );
  renderInnerPagePicker();
}

async function handleImagePickerUpload(files) {
  const picker = state.innerPagePicker;
  if (!picker) return;
  const existing = Array.isArray(picker.uploadFiles) ? picker.uploadFiles : [];
  const available = Math.max(0, MAX_DRAFT_TEMPLATE_PAGES - existing.length);
  const imageFiles = Array.from(files || []).filter((file) => file.type.startsWith("image/"));
  const accepted = imageFiles.slice(0, available);
  $("#imagePickerUploadInput").value = "";
  const skipped = imageFiles.length - accepted.length;
  if (!accepted.length) {
    // 额度为 0 时此前没有 else 分支：uploads 为空数组，函数直接走到末尾，用户点了没有反馈。
    toast(`已达到单篇 ${MAX_DRAFT_TEMPLATE_PAGES} 张上限，未加入新图片；请先移除部分图片再添加`);
    return;
  }
  // 这些图片会被写进草稿并随 /api/state 落库。内嵌原图 data URL 会让单条草稿超过服务端的
  // 记录上限（2MB）与整档上限（8MB），整次保存被拒——重登后连同其它改动一起消失。
  // 因此走和主上传流程同一条资源接口，草稿里只留 URL。
  let uploads = [];
  try {
    const images = await persistImportedImageFiles(accepted.map((file) => ({ file, name: file.name })));
    uploads = images.map((src, index) => ({ id: uid("upload-image"), name: accepted[index]?.name || "", src }));
  } catch (error) {
    toast(`图片保存失败：${error.message}`, { kind: "error", duration: 6500 });
    return;
  }
  picker.uploadFiles = [...existing, ...uploads];
  renderInnerPagePicker();
  const total = picker.uploadFiles.length;
  toast(skipped
    ? `已选择 ${total} 张图片，另有 ${skipped} 张超过单篇 ${MAX_DRAFT_TEMPLATE_PAGES} 张上限未被加入`
    : `已选择 ${total} 张图片`);
}

function removeImagePickerUpload(uploadId) {
  const picker = state.innerPagePicker;
  if (!picker) return;
  picker.uploadFiles = (picker.uploadFiles || []).filter((item) => String(item.id) !== String(uploadId));
  renderInnerPagePicker();
}

function draftImageJobIdentity(job) {
  if (!job) return "";
  const key = String(job.key || "").trim();
  if (key) return key;
  if (job.templateId !== undefined && job.templateId !== null && job.templateId !== "") {
    return templatePageKey(job.templateId, job.pageIndex);
  }
  return String(job.label || "").trim();
}

function draftImageSlots(draft, jobs = draftPromptImageJobs(draft) || []) {
  const renderedImages = normalizeImages(draft?.templateRenderImages);
  const images = renderedImages.length ? renderedImages : normalizeImages(draft?.images);
  const labels = Array.isArray(draft?.templateRenderImageLabels) ? draft.templateRenderImageLabels : [];
  const states = Array.isArray(draft?.templateRenderImageStates) ? draft.templateRenderImageStates : [];
  const reports = Array.isArray(draft?.templateRenderWatermarkReports) ? draft.templateRenderWatermarkReports : [];
  const slotLabels = images.map((_, index) => String(labels[index] || jobs[index]?.label || "").trim());
  // Pages generated from reused templates share display labels (「内页 1」…), so a label alone
  // cannot identify an image. Match each page to its own slot by label occurrence order.
  const pendingSlots = new Map();
  slotLabels.forEach((label, index) => {
    if (!pendingSlots.has(label)) pendingSlots.set(label, []);
    pendingSlots.get(label).push(index);
  });
  const slotByJobKey = new Map();
  const jobBySlot = new Map();
  jobs.forEach((job) => {
    const jobKey = draftImageJobIdentity(job);
    const queue = jobKey ? pendingSlots.get(String(job?.label || "").trim()) : null;
    if (!queue || !queue.length) return;
    const slot = queue.shift();
    slotByJobKey.set(jobKey, slot);
    jobBySlot.set(slot, job);
  });
  return {
    images,
    labels: slotLabels,
    states: images.map((_, index) => states[index] || "generated"),
    reports: images.map((_, index) => reports[index] || null),
    slotByJobKey,
    jobBySlot,
  };
}

function draftImageJob(draft, imageIndex) {
  const jobs = draftPromptImageJobs(draft) || [];
  const label = draft.templateRenderImageLabels?.[imageIndex];
  if (label) {
    if (draft?.workflow !== "replica" && String(label).endsWith("素材")) return null;
    const slotJob = draftImageSlots(draft, jobs).jobBySlot.get(imageIndex);
    if (slotJob) return slotJob;
  }
  return jobs[imageIndex] || null;
}

// 面板上每个条目的顺序快照，key 为空表示这条图没有对应的模板页（上传图/素材）。
function draftDisplayJobEntries(draft) {
  const jobs = draftPromptImageJobs(draft) || [];
  const slots = draftImageSlots(draft, jobs);
  return slots.images.map((src, index) => {
    const slotJob = slots.jobBySlot.get(index);
    return {
      key: slotJob ? draftImageJobIdentity(slotJob) : "",
      src,
      label: slots.labels[index] || "",
      state: slots.states[index] || "generated",
      report: slots.reports[index] || null,
    };
  });
}

// 按"页（job）"重建图片数组时，没有 job 的图（上传图、素材）过去被统一追加到末尾，
// 用户刚把它们拖到前面，一次重新生成/整组生成就把位置丢了。这里改成按拖动时的锚点
// 插回：锚点 = 旧顺序里它前面最近的那个 job；锚点不在了就放最前，同一锚点下的多张
// 图保持原相对顺序。
function mergeDraftImageAssets(previousEntries, nextEntries, assetEntries = []) {
  if (!assetEntries.length) return nextEntries;
  const anchorByEntry = new Map();
  let anchorKey = "";
  (previousEntries || []).forEach((entry) => {
    if (!entry) return;
    if (entry.key) anchorKey = entry.key;
    else anchorByEntry.set(entry, anchorKey);
  });
  const merged = [...nextEntries];
  assetEntries.forEach((entry) => {
    const anchor = anchorByEntry.has(entry) ? anchorByEntry.get(entry) : (entry.anchorKey || "");
    const base = anchor ? merged.findIndex((item) => item.key === anchor) : -1;
    if (anchor && base < 0) {
      // 锚点页这次不在了（换了模板/页集合）：退回旧行为放末尾，别把封面挤到后面去。
      merged.push(entry);
      return;
    }
    let at = base >= 0 ? base + 1 : 0;
    while (at < merged.length && !merged[at].key) at += 1;
    merged.splice(at, 0, entry);
  });
  return merged;
}

function draftImageMaterialEntries(draft) {
  const renderedImages = normalizeImages(draft?.templateRenderImages);
  const images = renderedImages.length ? renderedImages : normalizeImages(draft?.images);
  const labels = Array.isArray(draft?.templateRenderImageLabels) ? draft.templateRenderImageLabels : [];
  const states = Array.isArray(draft?.templateRenderImageStates) ? draft.templateRenderImageStates : [];
  const reports = Array.isArray(draft?.templateRenderWatermarkReports) ? draft.templateRenderWatermarkReports : [];
  return images.map((src, index) => ({
    src,
    label: labels[index] || "",
    state: states[index] || "generated",
    report: reports[index] || null,
  })).filter((entry) => entry.label.endsWith("素材"));
}

function appendDraftImageMaterialEntries(draft, materialEntries) {
  if (!draft || !Array.isArray(materialEntries) || !materialEntries.length) return;
  const images = normalizeImages(draft.templateRenderImages);
  const labels = Array.isArray(draft.templateRenderImageLabels) ? [...draft.templateRenderImageLabels] : [];
  const states = Array.isArray(draft.templateRenderImageStates) ? [...draft.templateRenderImageStates] : [];
  const reports = Array.isArray(draft.templateRenderWatermarkReports) ? [...draft.templateRenderWatermarkReports] : [];
  const existing = new Set(images);
  while (labels.length < images.length) labels.push("");
  while (states.length < images.length) states.push("generated");
  while (reports.length < images.length) reports.push(null);
  materialEntries.forEach((entry) => {
    if (!entry?.src || existing.has(entry.src)) return;
    existing.add(entry.src);
    images.push(entry.src);
    labels.push(entry.label || "内页素材");
    states.push(entry.state || "generated");
    reports.push(entry.report || null);
  });
  draft.templateRenderImages = images;
  draft.templateRenderImageLabels = labels;
  draft.templateRenderImageStates = states;
  draft.templateRenderWatermarkReports = reports;
  draft.images = [...images];
}

function reorderDraftAssetArrays(draft, oldJobs, nextJobs, { includeNewReferences = false } = {}) {
  const rendered = normalizeImages(draft.templateRenderImages);
  const images = rendered.length ? rendered : normalizeImages(draft.images);
  if (!images.length && !includeNewReferences) return;
  const labels = Array.isArray(draft.templateRenderImageLabels) ? draft.templateRenderImageLabels : [];
  const states = Array.isArray(draft.templateRenderImageStates) ? draft.templateRenderImageStates : [];
  const reports = Array.isArray(draft.templateRenderWatermarkReports) ? draft.templateRenderWatermarkReports : [];
  const jobsByLabel = new Map();
  oldJobs.forEach((job) => {
    if (!jobsByLabel.has(job.label)) jobsByLabel.set(job.label, []);
    jobsByLabel.get(job.label).push(job);
  });
  const claimedKeys = new Set();
  const entryByKey = new Map();
  const previousEntries = [];
  const unmatchedEntries = [];
  images.forEach((src, index) => {
    const label = labels[index] || "";
    let job = null;
    const isMaterial = label.endsWith("素材");
    if (!isMaterial) {
      const labelQueue = jobsByLabel.get(label);
      while (labelQueue?.length && !job) {
        const candidate = labelQueue.shift();
        if (!claimedKeys.has(candidate.key)) job = candidate;
      }
      if (!job && oldJobs[index] && !claimedKeys.has(oldJobs[index].key)) job = oldJobs[index];
      if (!job && !label) job = oldJobs.find((candidate) => !claimedKeys.has(candidate.key)) || null;
    }
    const entry = {
      key: job?.key || "",
      src,
      label: label || job?.label || "",
      state: states[index] || "reference",
      report: reports[index] || null,
    };
    previousEntries.push(entry);
    if (job?.key) {
      claimedKeys.add(job.key);
      entryByKey.set(job.key, entry);
    } else {
      unmatchedEntries.push(entry);
    }
  });
  const jobEntries = nextJobs.map((job) => entryByKey.get(job.key) || (includeNewReferences && job.referenceImage ? {
    key: job.key,
    src: job.referenceImage,
    label: job.label,
    state: "reference",
    report: null,
  } : null)).filter(Boolean);
  // 没有 job 的图按拖动锚点插回，别甩到末尾。
  const entries = mergeDraftImageAssets(previousEntries, jobEntries, unmatchedEntries);
  if (rendered.length) {
    draft.templateRenderImages = entries.map((entry) => entry.src);
    draft.templateRenderImageLabels = entries.map((entry) => entry.label);
    draft.templateRenderImageStates = entries.map((entry) => entry.state);
    draft.templateRenderWatermarkReports = entries.map((entry) => entry.report);
    draft.images = [...draft.templateRenderImages];
  } else {
    draft.images = entries.map((entry) => entry.src);
  }
}

function applyKnowledgePageExpansion(draft, plan) {
  const expansion = plan?.pageExpansion;
  const expansionPages = Array.isArray(expansion?.additionalPages)
    ? expansion.additionalPages
    : Array.isArray(expansion?.additionalTemplatePages) ? expansion.additionalTemplatePages : [];
  if (!draft || plan?.pipelineContract !== PIPELINE_CONTRACT) return;
  const manualPages = draftAdditionalTemplatePages(draft)
    .filter((item) => (
      item.autoGenerated !== true
      && !/^auto-(?:inner-|series[-_])/.test(String(item.id || ""))
    ));
  const reviewPending = expansionPages.length > 0 && draft.pageExpansionReview?.status !== "confirmed";
  const activeExpansionPages = reviewPending
    ? []
    : expansionPages.slice(0, MAX_DRAFT_TEMPLATE_PAGES);
  const autoPages = activeExpansionPages.map((item, index) => ({
    id: String(item.id || `auto-inner-${index + 1}`),
    templateId: item.templateId || draft.innerTemplateId || "",
    pageIndex: Math.max(0, Number(item.pageIndex) || 0),
    label: item.label || `内页 ${index + 1}`,
    contentPart: "inner",
    sourceKind: "inner",
    sourcePageKey: item.sourcePageKey || templatePageKey(item.templateId || draft.innerTemplateId, Number(item.pageIndex) || 0),
    sourceTemplateName: item.sourceTemplateName || "",
    referenceImage: item.referenceImage || "",
    orderKey: item.orderKey || "",
    isAdditional: true,
    autoGenerated: true,
    createdAt: new Date().toISOString(),
  })).filter((item) => item.templateId);
  draft.additionalTemplatePages = [...manualPages, ...autoPages];
  const allJobs = draftImageJobs(draft, { includeExcluded: true });
  const innerJobs = allJobs.filter((job) => job.contentPart === "inner");
  draft.innerPageOrder = innerJobs.map((job) => job.key);
  const coverJobs = allJobs.filter((job) => job.contentPart === "cover");
  draft.imagePageOrder = [...coverJobs, ...innerJobs].map((job) => job.key);
}

// 按执行计划的页序，把这批图片任务排成"计划期望的顺序"；任何一页对不上就返回 null。
// 抽出来给 alignDraftImageJobsToExecutionPlan 和拖拽回原位后的恢复判断共用。
function draftJobOrderForExecutionPlan(draft, plan, jobs = draftImageJobs(draft, { includeExcluded: true })) {
  const pages = Array.isArray(plan?.pageScript) ? plan.pageScript : [];
  if (!draft || plan?.pipelineContract !== PIPELINE_CONTRACT || !pages.length) return null;
  const excluded = draftExcludedTemplatePageKeys(draft);
  const used = new Set();
  const ordered = [];
  for (const page of pages) {
    const expectedPart = page?.templateRole === "cover" || Number(page?.page) === 1 ? "cover" : "inner";
    const pageIndex = Math.max(0, Number(page?.templatePage || 1) - 1);
    const orderKey = String(page?.orderKey || "");
    const matching = jobs.find((job) => (
      !used.has(job.key)
      && !isDraftTemplatePageExcluded(draft, job)
      && orderKey
      && String(job.orderKey || job.key || "") === orderKey
    )) || jobs.find((job) => (
      !used.has(job.key)
      && !isDraftTemplatePageExcluded(draft, job)
      && String(job.templateId || "") === String(page?.templateId || "")
      && Number(job.pageIndex) === pageIndex
      && job.contentPart === expectedPart
    ));
    if (!matching) return null;
    used.add(matching.key);
    ordered.push(matching);
  }
  const remaining = jobs.filter((job) => !used.has(job.key) && !excluded.has(
    job.isAdditional ? String(job.key || "") : templatePageKey(job.templateId, job.pageIndex)
  ));
  return [...ordered, ...remaining];
}

function alignDraftImageJobsToExecutionPlan(draft, plan) {
  if (!draft) return false;
  const allOrdered = draftJobOrderForExecutionPlan(draft, plan);
  if (!allOrdered) return false;
  draft.imagePageOrder = allOrdered.map((job) => job.key);
  draft.innerPageOrder = allOrdered.filter((job) => job.contentPart === "inner").map((job) => job.key);
  return true;
}

// 拖拽只换序时，如果用户又把顺序拖回和计划完全一致，这份计划其实没被破坏：撤掉"仅因
// 换序"的失效标记，并把渲染态也还原，别逼用户白白重新搭建一遍。
// 只认 page-order-changed；页数/页集合变过（page-set-changed）不靠拖回来恢复。
function restoreDraftPlanIfOrderMatches(draft) {
  const invalidation = draft?.executionPlanInvalidation;
  if (!draft?.executionPlan || invalidation?.reason !== "page-order-changed") return false;
  const jobs = draftImageJobs(draft) || [];
  if (!jobs.length) return false;
  const expected = draftJobOrderForExecutionPlan(draft, draft.executionPlan, jobs);
  if (!expected || expected.length !== jobs.length) return false;
  if (jobs.some((job, index) => job.key !== expected[index].key)) return false;
  delete draft.executionPlanInvalidation;
  if (invalidation.previousRenderMode && draft.templateRenderMode === "ai-pending") {
    draft.templateRenderMode = invalidation.previousRenderMode;
  }
  return true;
}

function requireGroundedKnowledgeDecision(draft, continuation) {
  if (knowledgeChannelForDraft(draft) === "pure-ai") return false;
  if (draft?.knowledge?.decision === "grounded" && !draft?.knowledge?.stale && knowledgeMatchesSelectedSource(draft)
    && cloudKnowledgeSelectionsReady(draft.knowledge)) return false;
  if (draft?.knowledge?.decision === "fallback" && draft?.knowledge?.mode === "pure-ai" && !draft?.knowledge?.stale && knowledgeMatchesSelectedSource(draft)) return false;
  if (!knowledgeIsEnabled()) {
    toast("知识图文必须先选择本地参考资料，或在 AI 配置中启用云端知识库", { kind: "error", duration: 7000 });
    return true;
  }
  if (knowledgeSourceForDraft(draft) !== "cloud") {
    state.knowledgeEvidenceCollapsed = false;
    renderKnowledgeEvidence(draft);
    toast(knowledgeLocalOnlyHint(), { kind: "warning", duration: 6200 });
    return true;
  }
  openKnowledgeReview(draft, continuation, { allowFallback: false });
  return true;
}

function draftImageSortEntries(draft, jobs = draftImageJobs(draft)) {
  const images = currentPreviewImages(draft);
  if (!images.length) {
    return jobs.map((job, index) => ({ key: `job:${job.key}`, jobKey: job.key, sourceIndex: index }));
  }
  return images.map((_, index) => {
    const job = draftImageJob(draft, index);
    return {
      key: job ? `job:${job.key}` : `asset:${index}`,
      jobKey: job?.key || "",
      sourceIndex: index,
    };
  });
}

// 拖动图片只重排了编辑器看到的数组，但生成流程（复刻/图生文案、没有正式执行计划的
// 原创）读的是 draft.sourceImages：不跟着排，用户刚拖好的顺序会在生成时按加入时的
// 原顺序写回去（"图片又回到一开始拖进来的位置"）。removeDraftImage 早在删图时就同步
// 了这份名单，这里补上移动那一半。
// 按图片地址取而不是按下标：两份名单不一定一一对应——sourceImages 里可能有面板上不
// 显示的图（占位图、已排除页），面板上也可能有它没有的图（模板图、素材图）。
function reorderDraftSourceImages(draft, nextImages) {
  const sourceImages = Array.isArray(draft.sourceImages) ? draft.sourceImages : null;
  if (!sourceImages || !sourceImages.length) return;
  const remaining = [...sourceImages];
  const ordered = [];
  (nextImages || []).forEach((src) => {
    const index = remaining.indexOf(src);
    if (index >= 0) ordered.push(remaining.splice(index, 1)[0]);
  });
  // 不在当前画面里的源图保持原相对次序，排在后面。
  draft.sourceImages = [...ordered, ...remaining];
}

function reorderDraftImageArrays(draft, sourceIndexes) {
  const reorder = (items) => sourceIndexes.map((index) => items[index]);
  const renderedImages = Array.isArray(draft.templateRenderImages) ? draft.templateRenderImages : [];
  if (renderedImages.length) {
    draft.templateRenderImages = reorder(renderedImages);
    draft.images = [...draft.templateRenderImages];
  } else if (Array.isArray(draft.images)) {
    draft.images = reorder(draft.images);
  }
  if (Array.isArray(draft.templateRenderImageLabels)) {
    draft.templateRenderImageLabels = reorder(draft.templateRenderImageLabels);
  }
  if (Array.isArray(draft.templateRenderImageStates)) {
    draft.templateRenderImageStates = reorder(draft.templateRenderImageStates);
  }
  if (Array.isArray(draft.templateRenderWatermarkReports)) {
    draft.templateRenderWatermarkReports = reorder(draft.templateRenderWatermarkReports);
  }
  reorderDraftSourceImages(draft, currentPreviewImages(draft));
}

function cloudKnowledgeDocumentsForReview() {
  return state.knowledgeReview?.documents?.length
    ? state.knowledgeReview.documents
    : state.knowledgeCloudDocuments || [];
}

function renderKnowledgeCloudDocuments(draft = currentDraft()) {
  const mount = $("#knowledgeEvidenceCloudFiles");
  if (!mount) return;
  const review = state.knowledgeReview || {};
  if (!review.documents?.length && !Object.keys(review.selected || {}).length && draft?.knowledge?.mode === "cloud" && !draft.knowledge.stale) {
    const confirmed = {};
    (draft.knowledge.selections || []).forEach((item) => {
      const id = String(item?.documentId || "");
      if (id) confirmed[id] = { ...item, documentId: id };
    });
    review.selected = confirmed;
  }
  review.selected = Object.fromEntries(Object.entries(review.selected || {}).slice(0, MAX_CLOUD_REFERENCE_FILES));
  const selected = review.selected || {};
  const documents = cloudKnowledgeDocumentsForReview();
  const count = $("#knowledgeEvidenceCloudCount");
  const confirm = $("#confirmKnowledgeBtnInline");
  const selectedCount = Object.keys(selected).length;
  if (count) count.textContent = `已选 ${selectedCount}/${MAX_CLOUD_REFERENCE_FILES}`;
  if (confirm) confirm.disabled = selectedCount === 0 || state.knowledgeCloudLoading;
  if (state.knowledgeCloudLoading) {
    mount.innerHTML = `<div class="knowledge-evidence-local-empty"><i data-lucide="loader-circle"></i><strong>正在读取云端资料</strong><span>请稍候，正在同步可用于本次生成的文件。</span></div>`;
    refreshIcons();
    return;
  }
  if (!documents.length) {
    mount.innerHTML = `<div class="knowledge-evidence-local-empty"><i data-lucide="cloud-off"></i><strong>暂无可用云端文件</strong><span>请先在知识库上传资料并完成索引。</span></div>`;
    refreshIcons();
    return;
  }
  mount.innerHTML = documents.map((document) => {
    const id = String(document.id || "");
    const checked = Boolean(selected[id]);
    const metadata = [document.folder_path || "根目录", document.category, document.documentType, document.year].filter(Boolean).join(" · ");
    const status = document.status === "ready" ? "可用" : (document.status || "可用");
    const locked = selectedCount >= MAX_CLOUD_REFERENCE_FILES && !checked;
    return `<label class="knowledge-evidence-file-row ${checked ? "selected" : ""} ${locked ? "is-disabled" : ""}">
      <input type="checkbox" data-knowledge-cloud-select="${escapeHtml(id)}" ${checked ? "checked" : ""} ${locked ? "disabled" : ""} />
      <span class="local-file-icon"><i data-lucide="file-text"></i></span>
      <span class="knowledge-evidence-file-copy"><strong title="${escapeHtml(document.filename || "未命名资料")}">${escapeHtml(document.filename || "未命名资料")}</strong><small>${escapeHtml(metadata || "云端知识库")} · ${escapeHtml(status)}</small></span>
      <span class="knowledge-evidence-file-size">${Number(document.word_count || 0) ? `${Number(document.word_count).toLocaleString("zh-CN")} 字` : ""}</span>
    </label>`;
  }).join("");
  refreshIcons();
}

async function loadKnowledgeCloudDocuments({ silent = false } = {}) {
  state.knowledgeCloudLoadAttempted = true;
  state.knowledgeCloudLoading = true;
  renderKnowledgeCloudDocuments(currentDraft());
  try {
    const statusData = await getBackend("/api/knowledge/status");
    state.knowledgeStatus = statusData.status || state.knowledgeStatus || {};
    const gate = knowledgeCloudGate(state.knowledgeStatus);
    if (!gate.ready) {
      // 未就绪是入口状态，不是加载故障：清空云端选择并说明原因，别把"请先配置"包进
      // 「加载失败」里（原来的那句 throws 永远走不到，见 P127）。
      state.knowledgeCloudDocuments = [];
      state.knowledgeReview.documents = [];
      state.knowledgeReview.selected = {};
      if (!silent) toast(gate.hint, { kind: "warning", duration: 6000 });
      return;
    }
    const data = await getBackend("/api/knowledge/documents?status=ready");
    const byId = new Map();
    (data.documents || []).forEach((document) => {
      const id = String(document?.id || "");
      if (id && !byId.has(id)) byId.set(id, document);
    });
    state.knowledgeCloudDocuments = [...byId.values()];
    state.knowledgeReview.documents = state.knowledgeCloudDocuments;
    const available = new Set(state.knowledgeCloudDocuments.map((document) => String(document.id)));
    state.knowledgeReview.selected = Object.fromEntries(
      Object.entries(state.knowledgeReview.selected || {}).filter(([id]) => available.has(String(id)))
    );
  } catch (error) {
    state.knowledgeCloudDocuments = [];
    state.knowledgeReview.documents = [];
    if (!silent) toast(`云端资料加载失败：${error.message}`, { kind: "error", duration: 6000 });
  } finally {
    state.knowledgeCloudLoading = false;
    renderKnowledgeEvidence(currentDraft());
  }
}

function moveDraftImage(draft, fromKey, toKey, placeAfter = false) {
  if (!draft || !fromKey || !toKey || fromKey === toKey) return false;
  const oldJobs = draftImageJobs(draft);
  const entries = draftImageSortEntries(draft, oldJobs);
  const jobsByKey = new Map(oldJobs.map((job) => [job.key, job]));
  const coverEntry = entries.find((entry) => jobsByKey.get(entry.jobKey)?.contentPart === "cover");
  if (coverEntry && fromKey === coverEntry.key) return false;
  const previousKeys = entries.map((entry) => entry.key);
  const fromIndex = previousKeys.indexOf(fromKey);
  if (fromIndex < 0 || !previousKeys.includes(toKey)) return false;
  const orderedEntries = [...entries];
  const [movedEntry] = orderedEntries.splice(fromIndex, 1);
  const targetIndex = orderedEntries.findIndex((entry) => entry.key === toKey);
  orderedEntries.splice(targetIndex + (placeAfter ? 1 : 0), 0, movedEntry);
  if (coverEntry && orderedEntries[0]?.key !== coverEntry.key) return false;
  if (orderedEntries.every((entry, index) => entry.key === previousKeys[index])) return false;

  if (currentPreviewImages(draft).length) {
    reorderDraftImageArrays(draft, orderedEntries.map((entry) => entry.sourceIndex));
  }
  const jobKeys = orderedEntries.map((entry) => entry.jobKey).filter(Boolean);
  oldJobs.forEach((job) => { if (!jobKeys.includes(job.key)) jobKeys.push(job.key); });
  draft.imagePageOrder = jobKeys;
  const innerKeys = jobKeys.filter((key) => jobsByKey.get(key)?.contentPart === "inner");
  draft.innerPageOrder = innerKeys;
  invalidateDraftExecutionPlan(draft, "page-order-changed");
  // 只是换序、且用户最终又拖回与计划一致的顺序时，这份计划没被破坏，原地恢复。
  restoreDraftPlanIfOrderMatches(draft);
  if (draft.viralBrief) {
    draft.viralBrief = {
      ...draft.viralBrief,
      imagePageOrder: [...jobKeys],
      innerPageOrder: [...innerKeys],
    };
  }
  return true;
}

function moveDraftInnerPage(draft, fromKey, toKey, placeAfter = false) {
  const normalizeKey = (key) => String(key || "").startsWith("job:") ? String(key) : `job:${key}`;
  return moveDraftImage(draft, normalizeKey(fromKey), normalizeKey(toKey), placeAfter);
}

function animateDraftImageReflow(board, mutate) {
  const items = [...board.children].filter((item) => !item.classList.contains("is-dragging"));
  const before = new Map(items.map((item) => [item, item.getBoundingClientRect()]));
  mutate();
  if (reduceUiMotion()) return;
  [...board.children].forEach((item) => {
    const first = before.get(item);
    if (!first || typeof item.animate !== "function") return;
    const last = item.getBoundingClientRect();
    const deltaX = first.left - last.left;
    if (Math.abs(deltaX) < 1) return;
    item.animate(
      [{ transform: `translateX(${deltaX}px)` }, { transform: "translateX(0)" }],
      { duration: 190, easing: "cubic-bezier(.2,.8,.2,1)" }
    );
  });
}

function createDraftImageDragPreview(card, clientX, clientY) {
  const rect = card.getBoundingClientRect();
  const preview = card.cloneNode(true);
  preview.classList.remove("is-dragging", "is-drag-over");
  preview.classList.add("draft-image-drag-preview");
  preview.removeAttribute("draggable");
  preview.querySelectorAll("[draggable]").forEach((item) => item.removeAttribute("draggable"));
  preview.style.width = `${rect.width}px`;
  preview.style.height = `${rect.height}px`;
  draftImageDrag.offsetX = Math.max(0, Math.min(rect.width, clientX - rect.left));
  draftImageDrag.offsetY = Math.max(0, Math.min(rect.height, clientY - rect.top));
  preview.style.left = `${clientX - draftImageDrag.offsetX}px`;
  preview.style.top = `${clientY - draftImageDrag.offsetY}px`;
  document.body.appendChild(preview);
  return preview;
}

function activateDraftImagePointerDrag(clientX, clientY) {
  const { board, card } = draftImageDrag;
  if (!board || !card || draftImageDrag.active) return;
  draftImageDrag.active = true;
  draftImageDrag.preview = createDraftImageDragPreview(card, clientX, clientY);
  draftImageDrag.placeholder = document.createElement("div");
  draftImageDrag.placeholder.className = "draft-image-drop-slot";
  draftImageDrag.placeholder.setAttribute("aria-hidden", "true");
  draftImageDrag.placeholder.innerHTML = '<i data-lucide="move-horizontal" aria-hidden="true"></i>';
  draftImageDrag.placeholder.style.height = `${card.getBoundingClientRect().height}px`;
  board.insertBefore(draftImageDrag.placeholder, card);
  board.classList.add("is-sorting");
  card.classList.add("is-dragging");
  window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
}

function updateDraftImagePointerPreview(clientX, clientY) {
  if (!draftImageDrag.preview) return;
  draftImageDrag.preview.style.left = `${clientX - draftImageDrag.offsetX}px`;
  draftImageDrag.preview.style.top = `${clientY - draftImageDrag.offsetY}px`;
}

function positionDraftImageDropSlot(board, card, placeAfter) {
  const placeholder = draftImageDrag.placeholder;
  if (!placeholder || !card || card === draftImageDrag.card) return;
  const anchor = placeAfter ? card.nextSibling : card;
  if (anchor === placeholder || (!anchor && placeholder === board.lastElementChild)) return;
  animateDraftImageReflow(board, () => board.insertBefore(placeholder, anchor));
}

function cleanupDraftImageDrag() {
  if (draftImageDrag.longPressTimer) {
    clearTimeout(draftImageDrag.longPressTimer);
    draftImageDrag.longPressTimer = null;
  }
  if (draftImageDrag.handle && draftImageDrag.pointerId !== null) {
    try {
      if (draftImageDrag.handle.hasPointerCapture?.(draftImageDrag.pointerId)) {
        draftImageDrag.handle.releasePointerCapture(draftImageDrag.pointerId);
      }
    } catch {}
  }
  draftImageDrag.card?.closest(".image-board")?.classList.remove("is-sorting");
  draftImageDrag.card?.classList.remove("is-dragging");
  draftImageDrag.preview?.remove();
  draftImageDrag.placeholder?.remove();
  draftImageDrag.active = false;
  draftImageDrag.board = null;
  draftImageDrag.card = null;
  draftImageDrag.handle = null;
  draftImageDrag.preview = null;
  draftImageDrag.placeholder = null;
  draftImageDrag.pointerId = null;
  draftImageDrag.startX = 0;
  draftImageDrag.startY = 0;
  draftImageDrag.offsetX = 0;
  draftImageDrag.offsetY = 0;
  draftImageDrag.targetKey = "";
  draftImageDrag.placeAfter = false;
  draftImageDrag.pressActive = false;
  draftImageDrag.longPressFired = false;
  state.draggedDraftImageKey = "";
}

function templateGenerationNote(draft) {
  const editedVisualPrompts = draft.imageEditPrompts && typeof draft.imageEditPrompts === "object"
    ? draft.imageEditPrompts
    : {};
  const placeholderImages = new Set(draft?.workflow === "replica" ? [] : normalizeImages(draft.promptPlaceholderImages));
  const sourceImages = normalizeImages(draft.sourceImages).filter((src) => !placeholderImages.has(src));
  const taskImages = sourceImages.length
    ? sourceImages
    : templateImagesForDraft(draft).filter((src) => !placeholderImages.has(src));
  return {
    title: draft.title,
    body: draft.body,
    tags: draft.tags,
    images: taskImages,
    innerTemplateFields: draft.templateFields || {},
    executionPlan: draft.executionPlan || {},
    pageCopyEdits: draft.pageCopyEdits || {},
    viralBrief: draft.viralBrief || {},
    visualRolePrompts: Object.fromEntries(
      ["cover", "inner"].map((role) => {
        if (draft.workflow === "replica") return [role, draftAiPrompt(draft, role).trim()];
        if (!Object.prototype.hasOwnProperty.call(editedVisualPrompts, role)) return [role, ""];
        return [role, String(editedVisualPrompts[role] || "").trim()];
      }).filter(([, prompt]) => prompt)
    ),
    knowledge: typeof knowledgeForRequest === "function" ? knowledgeForRequest(draft) : (draft.knowledge || {}),
  };
}

function plannedPageForImageJob(draft, job) {
  const pages = Array.isArray(draft?.executionPlan?.pageScript) ? draft.executionPlan.pageScript : [];
  if (!pages.length || !job) return {};
  const orderKey = String(job.orderKey || job.key || "");
  return pages.find((item) => item?.orderKey && String(item.orderKey) === orderKey)
    || pages.find((item) => item?.sourcePageKey && item.sourcePageKey === job.sourcePageKey
      && (!orderKey || String(item.orderKey || "") === orderKey))
    || pages.find((item) => String(item?.templateId || "") === String(job.templateId || "")
      && Number(item?.templatePage) - 1 === Number(job.pageIndex))
    || {};
}

function renderPageSelectorForJob(draft, job) {
  const page = plannedPageForImageJob(draft, job);
  return {
    orderKey: job?.orderKey || job?.key || page.orderKey || "",
    pageTaskId: page.pageTaskId || "",
    lockedPageCopyId: page.lockedPageCopyId || "",
    seriesId: page.seriesId || draft?.executionPlan?.activeSeriesId || "",
    pageNumber: Number(page.page) || 0,
  };
}

function draftImageRegenerationRuntime() {
  if (!Array.isArray(state.draftImageRegenerationQueue)) state.draftImageRegenerationQueue = [];
  if (!(state.draftImageRegenerationActive instanceof Map)) state.draftImageRegenerationActive = new Map();
  return {
    queue: state.draftImageRegenerationQueue,
    active: state.draftImageRegenerationActive,
    limit: Math.max(1, Number(state.draftImageRegenerationLimit) || 3),
  };
}

function draftImageRegenerationKey(draft, job) {
  const identity = typeof job === "string" ? job : draftImageJobIdentity(job);
  return `${draft?.id || "current"}:${identity || "image"}`;
}

function draftImageRegenerationStatus(draft, job) {
  if (!draft || !job) return "";
  const runtime = draftImageRegenerationRuntime();
  const key = draftImageRegenerationKey(draft, job);
  if (runtime.active.has(key)) return "active";
  return runtime.queue.some((task) => task.key === key) ? "queued" : "";
}

function draftImageRegenerationCounts(draft) {
  if (!draft) return { active: 0, queued: 0 };
  const runtime = draftImageRegenerationRuntime();
  const belongsToDraft = (task) => task.draft === draft || (draft.id && task.draft?.id === draft.id);
  return {
    active: [...runtime.active.values()].filter(belongsToDraft).length,
    queued: runtime.queue.filter(belongsToDraft).length,
  };
}

function hasReadyViralImagePlan(draft) {
  const plan = draft?.executionPlan;
  if (!plan) return false;
  if (!visualExecutionPlanMatchesDraft(draft)) return false;
  if (!(draft.coverTemplateId && draft.innerTemplateId)) return false;
  const basisNodes = Array.isArray(plan.knowledgeBasis?.nodes) ? plan.knowledgeBasis.nodes : [];
  const basisNodeIds = basisNodes.map((node) => String(node?.id || ""));
  if (plan.knowledgeBasis?.kind !== "KnowledgeBasisV2" || !basisNodes.length) return false;
  if (basisNodeIds.some((id) => !id) || new Set(basisNodeIds).size !== basisNodeIds.length) return false;
  if (plan.pageTaskSet?.kind !== "PageTaskSetV2") return false;
  if (plan.pageScriptContract !== "LockedPageCopyV2") return false;
  if (!lockedKnowledgePlanIntegrity(draft)) return false;
  return Boolean(
    plan.pageScriptSource === "text-ai-template-fit"
      && plan.pipelineContract === PIPELINE_CONTRACT
      && (plan.coverPrompt || plan.imagePrompt)
      && (plan.innerPrompt || plan.imagePrompt)
  );
}

function canRegenerateDraftImage(draft) {
  return hasReadyViralImagePlan(draft);
}

function updateDraftImageRegenerationState(runtime = draftImageRegenerationRuntime()) {
  const firstActive = runtime.active.values().next().value;
  state.regeneratingDraftImageKey = firstActive?.job?.label || runtime.queue[0]?.job?.label || "";
  if (state.currentSection === "rewrite" || typeof state.currentSection === "undefined") renderEditor();
  if (typeof renderImageViewer === "function") renderImageViewer();
}

function refreshImageViewerGeneratedImage(draft, job, src, fallbackIndex = -1) {
  const viewer = state.imageViewer;
  if (!viewer || viewer.draftId !== (draft?.id || "") || !Array.isArray(viewer.images)) return false;
  const jobKey = draftImageJobIdentity(job);
  const keys = Array.isArray(viewer.draftImageKeys) ? viewer.draftImageKeys.map(String) : [];
  const labels = Array.isArray(viewer.draftImageLabels) ? viewer.draftImageLabels : [];
  let viewerIndex = jobKey ? keys.indexOf(jobKey) : -1;
  if (viewerIndex < 0 && job?.label) {
    // Legacy viewers carry labels only: match the first entry that has no key of its own.
    viewerIndex = labels.findIndex((label, index) => label === job.label && !keys[index]);
  }
  if (viewerIndex < 0 && Number.isInteger(fallbackIndex) && fallbackIndex >= 0) viewerIndex = fallbackIndex;
  if (viewerIndex < 0 || viewerIndex >= viewer.images.length) return false;
  viewer.images[viewerIndex] = src;
  if (typeof renderImageViewer === "function") renderImageViewer();
  return true;
}

async function performDraftImageRegeneration(task) {
  const { draft, job } = task;
  const previousEntries = draftDisplayJobEntries(draft);
  if (!state.draftImageRegenerationConfigPromise) {
    state.draftImageRegenerationConfigPromise = syncAiConfigBeforeGeneration();
  }
  await state.draftImageRegenerationConfigPromise;
  const pageData = await renderAiPageWithRetry(draft, job, templateGenerationNote(draft), {
    onRetry: ({ attempt }) => {
      task.retryAttempt = attempt;
      updateDraftImageRegenerationState();
      toast(`${job.label}生成超时，正在进行第 ${attempt} 次尝试`);
    },
  });
  const image = normalizeImages(pageData.images || [pageData.image])[0];
  const watermarkReport = Array.isArray(pageData.watermarkReports) ? pageData.watermarkReports[0] || null : null;
  if (!image) throw new Error(`${job.label}的生图接口没有返回图片`);
  const cacheKey = Date.now();
  const nextImage = `${image}${image.includes("?") ? "&" : "?"}v=${cacheKey}`;
  const jobs = draftPromptImageJobs(draft) || [];
  const targetKey = draftImageJobIdentity(job);
  const slots = draftImageSlots(draft, jobs);
  // Rebuild every slot from its own page (never from its label) so pages generated from
  // reused templates that share a display label keep their own image.
  const nextEntries = [];
  jobs.forEach((item) => {
    const itemKey = draftImageJobIdentity(item);
    if (itemKey && itemKey === targetKey) {
      nextEntries.push({ key: itemKey, label: item.label, src: nextImage, state: "generated", report: watermarkReport });
      return;
    }
    const slot = slots.slotByJobKey.get(itemKey);
    if (slot === undefined) return;
    nextEntries.push({
      key: itemKey,
      label: slots.labels[slot] || item.label,
      src: slots.images[slot],
      state: slots.states[slot] || "generated",
      report: slots.reports[slot] || null,
    });
  });
  // 没有 job 的图（上传图/素材）按原锚点插回，别甩到末尾。
  const mergedEntries = mergeDraftImageAssets(
    previousEntries,
    nextEntries,
    previousEntries.filter((entry) => !entry.key),
  );
  draft.templateRenderImages = mergedEntries.map((entry) => entry.src);
  draft.templateRenderImageLabels = mergedEntries.map((entry) => entry.label);
  draft.templateRenderImageStates = mergedEntries.map((entry) => entry.state);
  draft.templateRenderWatermarkReports = mergedEntries.map((entry) => entry.report);
  draft.images = [...draft.templateRenderImages];
  const remainingFailedJobs = draftFailedImageJobs(draft, jobs)
    .filter((item) => draftImageJobIdentity(item) !== targetKey);
  const readySlots = draftImageSlots(draft, jobs).slotByJobKey;
  const allImagesReady = remainingFailedJobs.length === 0
    && jobs.every((item) => readySlots.has(draftImageJobIdentity(item)));
  draft.templateRenderMode = allImagesReady ? "ai-full-page" : "ai-error";
  updateDraftImageFailureState(draft, remainingFailedJobs);
  draft.generatedAt = new Date().toISOString();
  saveState();
  refreshImageViewerGeneratedImage(draft, job, nextImage);
  toast(`${job.label}已重新生成`);
  return { image: nextImage, label: job.label };
}

function processDraftImageRegenerationQueue() {
  const runtime = draftImageRegenerationRuntime();
  while (runtime.active.size < runtime.limit && runtime.queue.length) {
    const task = runtime.queue.shift();
    runtime.active.set(task.key, task);
    updateDraftImageRegenerationState(runtime);
    let taskResult = null;
    Promise.resolve()
      .then(() => performDraftImageRegeneration(task))
      .then((result) => { taskResult = result; })
      .catch((error) => {
        task.draft.templateRenderMode = "ai-error";
        updateDraftImageFailureState(task.draft, [...draftFailedImageJobs(task.draft), task.job], error);
        saveState();
        renderEditor();
        toast(friendlyImageError(error), { kind: "error", duration: 6500 });
      })
      .finally(() => {
        runtime.active.delete(task.key);
        endDraftGenerationRun(task.generationRun);
        if (!runtime.active.size && !runtime.queue.length) state.draftImageRegenerationConfigPromise = null;
        updateDraftImageRegenerationState(runtime);
        renderEditor();
        task.resolve(taskResult);
        processDraftImageRegenerationQueue();
      });
  }
}

async function regenerateDraftImage(imageIndex, button, requestedPage = "") {
  const draft = syncFormToDraft() || currentDraft();
  const jobs = draftPromptImageJobs(draft) || [];
  const requested = String(requestedPage || "");
  // Callers pass the page key; labels stay supported for legacy saved state and old markup.
  const job = requested
    ? jobs.find((item) => draftImageJobIdentity(item) === requested) || jobs.find((item) => item.label === requested)
    : draftImageJob(draft, imageIndex);
  if (!draft || !job) {
    toast("这张图片没有可复用的模板生成任务");
    return;
  }
  if (!guardVisualCopyReview(draft)) return;
  // 账号维度：别的草稿在跑 AI 任务时点名拒绝（按钮不置灰，点了才知道为什么）。
  const elsewhereTaskMessage = typeof aiTaskRunningElsewhereMessage === "function"
    ? aiTaskRunningElsewhereMessage(draft.id)
    : "";
  if (elsewhereTaskMessage) {
    toast(elsewhereTaskMessage);
    return;
  }
  // 共享运行态：一键生成 / 单字段 / 单视觉角色在跑时不能再插单图任务（反向不对称是 P88 的原始症状）。
  if (draftGenerationBusy(draft.id, "image")) {
    toast("这份笔记正在整组生成，请完成后再添加单张任务");
    return;
  }
  if (!canRegenerateDraftImage(draft)) {
    toast("请先搭建完整的 AI 提示词，再重新生成图片");
    return;
  }
  if (!ensureAiConfigured("image")) return;
  const runtime = draftImageRegenerationRuntime();
  const key = draftImageRegenerationKey(draft, job);
  const existing = runtime.active.get(key) || runtime.queue.find((task) => task.key === key);
  if (existing) {
    toast(`${job.label}已在生成队列中`);
    return existing.promise;
  }
  let resolveTask;
  const promise = new Promise((resolve) => { resolveTask = resolve; });
  const queued = runtime.active.size >= runtime.limit;
  // 入队即登记，出队（含失败）才释放：排队期间不能让一键生成挤进来写同一份草稿。
  const generationRun = beginDraftGenerationRun(draft.id, "image");
  runtime.queue.push({ key, draft, job, button, promise, resolve: resolveTask, generationRun });
  updateDraftImageRegenerationState(runtime);
  if (queued) toast(`${job.label}已加入队列，前面的图片完成后会自动生成`);
  processDraftImageRegenerationQueue();
  return promise;
}

async function retryFailedDraftImages(button = null) {
  const draft = syncFormToDraft() || currentDraft();
  const jobs = draftPromptImageJobs(draft) || [];
  const failedJobs = draftFailedImageJobs(draft, jobs);
  if (!draft || !failedJobs.length) {
    toast("当前没有需要重试的失败页");
    return;
  }
  if (!guardVisualCopyReview(draft)) return;
  if (!canRegenerateDraftImage(draft)) {
    toast("请先搭建完整的 AI 提示词，再重试失败页");
    return;
  }
  if (!ensureAiConfigured("image")) return;
  if (button) button.disabled = true;
  const results = await Promise.all(failedJobs.map((job) => regenerateDraftImage(-1, null, draftImageJobIdentity(job))));
  const remainingJobs = draftFailedImageJobs(draft, jobs);
  if (!remainingJobs.length) {
    toast(`失败的 ${failedJobs.length} 张图片已全部补齐`);
  } else if (results.some(Boolean)) {
    toast(`已补齐 ${failedJobs.length - remainingJobs.length} 张，仍有 ${remainingJobs.length} 张失败`, { kind: "error", duration: 6500 });
  }
  renderEditor();
}

function excludeDraftTemplatePage(draft, job) {
  if (!draft || !job) return false;
  if (job.contentPart === "cover") {
    toast("封面页面必须保留");
    return false;
  }
  const excluded = draftExcludedTemplatePageKeys(draft);
  excluded.add(job.key || templatePageKey(job.templateId, job.pageIndex));
  draft.excludedTemplatePages = [...excluded];
  invalidateDraftExecutionPlan(draft, "page-set-changed");
  return true;
}

function removeAdditionalTemplatePage(draft, job) {
  if (!draft || !job?.isAdditional) return false;
  const additionalId = String(job.additionalId || "");
  const before = draftAdditionalTemplatePages(draft).length;
  draft.additionalTemplatePages = draftAdditionalTemplatePages(draft)
    .filter((item, index) => String(item.id || index) !== additionalId);
  const changed = draft.additionalTemplatePages.length !== before;
  if (changed && Array.isArray(draft.promptPlaceholderImages)) {
    draft.promptPlaceholderImages = draft.promptPlaceholderImages.filter((src) => src !== job.referenceImage);
  }
  if (changed) invalidateDraftExecutionPlan(draft, "page-set-changed");
  return changed;
}

function removeDraftTemplatePageByKey(pageKey) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) return;
  if ((state.rewriteGeneration?.active && String(state.rewriteGeneration.ownerDraftId || "") === String(draft.id || "")) || state.regeneratingDraftImageKey) {
    toast("图片生成中，完成后才能删除");
    return;
  }
  const job = draftImageJobs(draft, { includeExcluded: true }).find((item) => item.key === pageKey);
  if (!job) return;
  if (job.isAdditional) {
    if (!removeAdditionalTemplatePage(draft, job)) return;
  } else if (!excludeDraftTemplatePage(draft, job)) return;
  const allJobs = draftImageJobs(draft, { includeExcluded: true });
  const imageIndex = allJobs.findIndex((item) => item.key === pageKey);
  if (imageIndex >= 0 && Array.isArray(draft.images)) {
    draft.images = draft.images.filter((_, index) => index !== imageIndex);
  }
  state.previewImageIndex = Math.min(state.previewImageIndex, Math.max(0, currentPreviewImages(draft).length - 1));
  saveState();
  renderEditor();
  toast(`${job.label}已从当前草稿排除，后续不会生成文案或图片`);
}

function removeDraftImage(imageIndex) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) return;
  if ((state.rewriteGeneration?.active && String(state.rewriteGeneration.ownerDraftId || "") === String(draft.id || "")) || state.regeneratingDraftImageKey) {
    toast("图片生成中，完成后才能删除");
    return;
  }
  const job = draftImageJob(draft, imageIndex);
  if (job?.isAdditional) {
    if (!removeAdditionalTemplatePage(draft, job)) return;
  } else if (job && !excludeDraftTemplatePage(draft, job)) return;
  const renderedImages = normalizeImages(draft.templateRenderImages);
  let removedImage = "";
  if (renderedImages.length) {
    if (imageIndex < 0 || imageIndex >= renderedImages.length) return;
    removedImage = renderedImages[imageIndex];
    draft.templateRenderImages = renderedImages.filter((_, index) => index !== imageIndex);
    draft.templateRenderImageLabels = (draft.templateRenderImageLabels || []).filter((_, index) => index !== imageIndex);
    draft.templateRenderImageStates = (draft.templateRenderImageStates || []).filter((_, index) => index !== imageIndex);
    draft.templateRenderWatermarkReports = (draft.templateRenderWatermarkReports || []).filter((_, index) => index !== imageIndex);
    draft.images = [...draft.templateRenderImages];
  } else {
    const images = normalizeImages(draft.images);
    if (imageIndex < 0 || imageIndex >= images.length) return;
    removedImage = images[imageIndex];
    draft.images = images.filter((_, index) => index !== imageIndex);
    if (!job && Array.isArray(draft.sourceImages)) {
      draft.sourceImages = draft.sourceImages.filter((src) => src !== removedImage);
    }
  }
  if (removedImage && Array.isArray(draft.promptPlaceholderImages)) {
    draft.promptPlaceholderImages = draft.promptPlaceholderImages.filter((src) => src !== removedImage);
  }
  state.previewImageIndex = Math.min(state.previewImageIndex, Math.max(0, currentPreviewImages(draft).length - 1));
  saveState();
  renderEditor();
  toast(job ? `${job.label}已从当前草稿排除，后续不会生成文案或图片` : "图片已从当前笔记中删除");
}

async function runConcurrentJobs(items, limit, worker, options = {}) {
  let nextIndex = 0;
  let firstError = null;
  const signal = options.signal;
  const workerCount = Math.min(Math.max(1, Number(limit) || 1), items.length);
  const runners = Array.from({ length: workerCount }, async () => {
    while (true) {
      if (signal?.aborted) return;
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) return;
      try {
        await worker(items[index], index);
      } catch (error) {
        if (!firstError) firstError = error;
        if (isAbortError(error)) return;
      }
    }
  });
  await Promise.all(runners);
  throwIfAborted(signal);
  if (firstError) throw firstError;
}

async function applyTemplateCombination(coverTemplateId, innerTemplateId, templateSetId = "", options = {}) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) {
    toast("请先选择一个草稿");
    return;
  }
  if (state.rewriteGeneration?.active && String(state.rewriteGeneration.ownerDraftId || "") !== String(draft.id || "")) {
    toast("另一个快速笔记任务正在生成，请等待完成后再套用模板");
    return { ok: false, error: "另一个草稿正在生成" };
  }
  // 套用模板会整批写这份草稿的图片与文案，不能和单图生成叠在一起。
  // 调用方（一键生成 / 重试失败页）自己持有 exclusive 运行，所以这里只查 image 运行。
  if (draftImageGenerationActive(draft.id)) {
    toast("有单张图片正在生成，请等它结束后再套用模板");
    return { ok: false, error: "当前草稿有单图任务" };
  }
  const coverTemplate = layoutTemplateById(coverTemplateId);
  const innerTemplate = layoutTemplateById(innerTemplateId);
  if (!coverTemplate || !innerTemplate) {
    toast("封面模板或内页模板不存在");
    return;
  }
  let ownsGenerationState = false;
  let useAi = false;
  let aiJobs = [];
  let generatedDisplayImages = [];
  let generatedWatermarkReports = [];
  const materialEntries = draftImageMaterialEntries(draft);
  const previousJobEntries = draftDisplayJobEntries(draft);
  const previousImageState = {
    images: normalizeImages(draft.images),
    renderedImages: normalizeImages(draft.templateRenderImages),
    labels: Array.isArray(draft.templateRenderImageLabels) ? [...draft.templateRenderImageLabels] : [],
    states: Array.isArray(draft.templateRenderImageStates) ? [...draft.templateRenderImageStates] : [],
    watermarkReports: Array.isArray(draft.templateRenderWatermarkReports) ? [...draft.templateRenderWatermarkReports] : [],
    mode: draft.templateRenderMode,
    error: draft.imageGenerationError,
    failedLabels: [...(draft.imageGenerationFailedLabels || [])],
    failedJobKeys: [...(draft.imageGenerationFailedJobKeys || [])],
    generatedAt: draft.generatedAt,
  };
  try {
    useAi = Boolean(
      state.aiConfig?.image?.apiUrl &&
      coverTemplate.aiImage?.enabled !== false &&
      innerTemplate.aiImage?.enabled !== false
    );
    if (options.requireAi && !useAi) {
      throw new Error("当前视觉模板未启用 AI 生图，未使用原模板图代替");
    }
    if (!useAi) {
      ensureTemplateFields(draft, innerTemplate);
      await fillLayeredTemplateWithAi(draft, innerTemplate, { signal: options.signal });
    }
    if (useAi) await syncAiConfigBeforeGeneration({ signal: options.signal });
    throwIfAborted(options.signal);
    const note = templateGenerationNote(draft);
    let data;
    if (useAi) {
      aiJobs = draftPromptImageJobs({ ...draft, coverTemplateId, innerTemplateId });
      if (!aiJobs.length) throw new Error("当前草稿没有可生成的模板页");
      const generatedImages = Array(aiJobs.length).fill("");
      generatedDisplayImages = Array(aiJobs.length).fill("");
      generatedWatermarkReports = Array(aiJobs.length).fill(null);
      const activeLabels = new Set();
      let completedImages = 0;
      draft.imageGenerationFailedLabels = [];
      draft.imageGenerationFailedJobKeys = [];
      ownsGenerationState = !state.rewriteGeneration?.active;
      state.rewriteGeneration = {
        ...state.rewriteGeneration,
        active: true,
        ownerDraftId: draft.id || "",
        stage: "image",
        completed: 0,
        total: aiJobs.length,
        concurrency: Math.min(IMAGE_GENERATION_CONCURRENCY, aiJobs.length),
        currentLabel: "准备并行生成",
      };
      renderEditor();
      await runConcurrentJobs(aiJobs, IMAGE_GENERATION_CONCURRENCY, async (job, index) => {
        activeLabels.add(job.label);
        state.rewriteGeneration.currentLabel = `生成中：${[...activeLabels].join("、")}`;
        renderEditor();
        try {
          const pageData = await renderAiPageWithRetry(draft, job, note, {
            signal: options.signal,
            onRetry: ({ attempt }) => {
              state.rewriteGeneration.currentLabel = `${job.label}生成超时，正在第 ${attempt} 次尝试`;
              renderEditor();
            },
          });
          const image = normalizeImages(pageData.images || [pageData.image])[0];
          if (!image) throw new Error(`${job.label}的生图接口没有返回图片`);
          generatedImages[index] = image;
          generatedDisplayImages[index] = imageUrlWithCacheVersion(image);
          generatedWatermarkReports[index] = Array.isArray(pageData.watermarkReports)
            ? pageData.watermarkReports[0] || null
            : null;
          completedImages += 1;
          const completedEntries = generatedImages
            .map((src, pageIndex) => ({
              key: aiJobs[pageIndex] ? draftImageJobIdentity(aiJobs[pageIndex]) : "",
              src: generatedDisplayImages[pageIndex],
              label: aiJobs[pageIndex].label,
              state: "generated",
              report: generatedWatermarkReports[pageIndex] || null,
            }))
            .filter((item) => item.src);
          const partialEntries = mergeDraftImageAssets(
            previousJobEntries,
            completedEntries,
            previousJobEntries.filter((entry) => !entry.key),
          );
          draft.templateRenderMode = "ai-generating";
          draft.templateRenderImages = partialEntries.map((entry) => entry.src);
          draft.templateRenderImageLabels = partialEntries.map((entry) => entry.label);
          draft.templateRenderImageStates = partialEntries.map((entry) => entry.state);
          draft.templateRenderWatermarkReports = partialEntries.map((entry) => entry.report);
          draft.images = [...draft.templateRenderImages];
          state.rewriteGeneration.completed = completedImages;
          saveState();
          rewriteCommittedOutput = draftOutputSnapshot(draft);
        } finally {
          activeLabels.delete(job.label);
          state.rewriteGeneration.currentLabel = activeLabels.size
            ? `生成中：${[...activeLabels].join("、")}`
            : (completedImages === aiJobs.length ? "全部图片已完成" : "正在调度下一张");
          renderEditor();
        }
      }, { signal: options.signal });
      data = {
        images: generatedImages,
        watermarkReports: generatedWatermarkReports,
        mode: "ai-full-page",
        combination: {
          templateSetId: coverTemplateId === innerTemplateId ? coverTemplateId : "custom",
          isCustom: coverTemplateId !== innerTemplateId,
        },
      };
    } else {
      data = await postBackend("/api/templates/render-combination", {
        coverTemplateId,
        innerTemplateId,
        note,
      }, { signal: options.signal });
    }
    const returnedImages = normalizeImages(data.images || []);
    if (useAi && data.mode !== "ai-full-page") {
      throw new Error("后端未确认 AI 成图结果，已拒绝使用模板原图代替");
    }
    if (useAi && !returnedImages.length) {
      throw new Error("生图接口没有返回新的图片");
    }
    const cacheKey = Date.now();
    draft.layoutTemplateId = innerTemplateId;
    draft.coverTemplateId = coverTemplateId;
    draft.innerTemplateId = innerTemplateId;
    draft.templateSetId = templateSetId || data.combination?.templateSetId || "custom";
    draft.templateCombinationCustom = Boolean(data.combination?.isCustom);
    draft.templateRenderMode = useAi ? "ai-full-page" : "local-rule";
    draft.imageGenerationError = "";
    draft.imageGenerationFailedLabels = [];
    if (useAi) {
      const finalJobEntries = returnedImages.map((src, index) => ({
        key: aiJobs[index] ? draftImageJobIdentity(aiJobs[index]) : "",
        src: generatedDisplayImages[index] || imageUrlWithCacheVersion(src, cacheKey),
        label: aiJobs[index]?.label || "",
        state: "generated",
        report: Array.isArray(data.watermarkReports) ? data.watermarkReports[index] || null : null,
      }));
      const finalEntries = mergeDraftImageAssets(
        previousJobEntries,
        finalJobEntries,
        previousJobEntries.filter((entry) => !entry.key),
      );
      draft.templateRenderImages = finalEntries.map((entry) => entry.src);
      draft.templateRenderImageLabels = finalEntries.map((entry) => entry.label);
      draft.templateRenderImageStates = finalEntries.map((entry) => entry.state);
      draft.templateRenderWatermarkReports = finalEntries.map((entry) => entry.report);
      draft.images = [...draft.templateRenderImages];
      draft.generatedAt = new Date().toISOString();
    } else {
      // 本地规则套模板按模板固定顺序出图，没有页任务可对齐，保持原有行为。
      draft.templateRenderImages = returnedImages.map((src) => imageUrlWithCacheVersion(src, cacheKey));
      draft.templateRenderImageLabels = [];
      draft.templateRenderImageStates = [];
      draft.templateRenderWatermarkReports = [];
      appendDraftImageMaterialEntries(draft, materialEntries);
    }
    state.activeLayoutTemplateId = innerTemplateId;
    saveState();
    renderAll();
    setSection("rewrite");
    toast(draft.templateCombinationCustom ? "已套用自定义封面与内页组合" : "已套用成套封面与内页模板");
    return { ok: true, mode: draft.templateRenderMode, images: [...draft.templateRenderImages] };
  } catch (error) {
    if (isAbortError(error)) {
      draft.images = [...previousImageState.images];
      draft.templateRenderImages = [...previousImageState.renderedImages];
      draft.templateRenderImageLabels = [...previousImageState.labels];
      draft.templateRenderImageStates = [...previousImageState.states];
      draft.templateRenderWatermarkReports = [...previousImageState.watermarkReports];
      draft.templateRenderMode = previousImageState.mode;
      draft.imageGenerationError = previousImageState.error;
      draft.imageGenerationFailedLabels = [...(previousImageState.failedLabels || [])];
      draft.imageGenerationFailedJobKeys = [...(previousImageState.failedJobKeys || [])];
      draft.generatedAt = previousImageState.generatedAt;
      saveState();
      renderEditor();
      throw error;
    }
    if (useAi) {
      const previousImages = previousImageState.renderedImages.length
        ? previousImageState.renderedImages
        : previousImageState.images;
      const retainedEntries = (aiJobs.length
        ? aiJobs.map((job, index) => ({
            key: draftImageJobIdentity(job),
            label: job.label,
            src: generatedDisplayImages[index] || previousImages[index] || "",
            state: "generated",
            report: generatedDisplayImages[index]
              ? generatedWatermarkReports[index] || null
              : previousImageState.watermarkReports[index] || null,
          }))
        : previousImages.map((src, index) => ({
            key: "",
            label: previousImageState.labels[index] || (index === 0 ? "封面" : `图 ${index + 1}`),
            src,
            state: "generated",
            report: previousImageState.watermarkReports[index] || null,
          })))
        .filter((entry) => entry.src);
      const mergedRetained = mergeDraftImageAssets(
        previousJobEntries,
        retainedEntries,
        previousJobEntries.filter((entry) => !entry.key),
      );
      draft.templateRenderImages = mergedRetained.map((entry) => entry.src);
      draft.templateRenderImageLabels = mergedRetained.map((entry) => entry.label);
      draft.templateRenderImageStates = mergedRetained.map((entry) => entry.state);
      draft.templateRenderWatermarkReports = mergedRetained.map((entry) => entry.report);
      draft.images = [...draft.templateRenderImages];
      draft.templateRenderMode = "ai-error";
      const failedJobs = aiJobs.filter((_, index) => !generatedDisplayImages[index]);
      const timedOutJob = failedJobs.length || !error?.imageJobLabel
        ? null
        : aiJobs.find((job) => job.label === error.imageJobLabel) || null;
      updateDraftImageFailureState(draft, failedJobs.length ? failedJobs : [timedOutJob].filter(Boolean), error);
      saveState();
      renderEditor();
    }
    if (options.requireAi) throw error;
    toast(`套用模板组合失败：${error.message}`);
    return { ok: false, error: error.message || "模板组合失败" };
  } finally {
    if (ownsGenerationState) {
      state.rewriteGeneration = { active: false, ownerDraftId: "", stage: "text", completed: 0, total: 0, currentLabel: "" };
      if (state.currentSection === "rewrite") renderEditor();
    }
  }
}

async function updateTemplatePreviewFromFields(options = {}) {
  const draft = syncFormToDraft();
  if (!draft?.layoutTemplateId) {
    toast("请先套用一个模板");
    return;
  }
  const requestId = ++templatePreviewRequestId;
  try {
    const combinationActive = draft.coverTemplateId && draft.innerTemplateId;
    const data = await postBackend(combinationActive ? "/api/templates/render-combination" : "/api/templates/render", combinationActive
      ? {
          coverTemplateId: draft.coverTemplateId,
          innerTemplateId: draft.innerTemplateId,
          note: {
            title: draft.title,
            body: draft.body,
            tags: draft.tags,
            images: templateImagesForDraft(draft),
            innerTemplateFields: draft.templateFields || {},
          },
        }
      : {
          templateId: draft.layoutTemplateId,
          note: {
            title: draft.title,
            body: draft.body,
            tags: draft.tags,
            images: templateImagesForDraft(draft),
            templateFields: draft.templateFields || {},
          },
        }, options);
    if (requestId !== templatePreviewRequestId) return;
    const cacheKey = Date.now();
    draft.templateRenderImages = (data.images || []).map((src) => `${src}${src.includes("?") ? "&" : "?"}v=${cacheKey}`);
    draft.templateRenderWatermarkReports = [];
    if (data.template) {
      state.layoutTemplates = state.layoutTemplates.map((item) => (item.id === draft.layoutTemplateId ? data.template : item));
    }
    saveState();
    renderEditor();
    toast("模板预览已更新");
  } catch (error) {
    if (isAbortError(error)) throw error;
    toast(`更新模板预览失败：${error.message}`);
  }
}

async function savePublishPerformance(itemId) {
  const item = state.publish.find((entry) => entry.id === itemId);
  const panel = $(`[data-performance-item="${CSS.escape(itemId)}"]`);
  if (!item || !panel) return;
  const metrics = Object.fromEntries(
    Array.from(panel.querySelectorAll("[data-performance-metric]")).map((input) => [input.dataset.performanceMetric, Number(input.value || 0)])
  );
  const button = panel.querySelector("[data-save-performance]");
  const done = setButtonLoading(button, "保存中...");
  try {
    const data = await postBackend("/api/viral/performance", {
      noteId: item.draftId,
      generationId: item.viralGenerationId,
      recipeId: item.viralRecipeId,
      atomIds: item.viralAtomIds || [],
      topicPackageId: item.topicPackageId || "",
      metrics,
      category: item.category || "未分类",
    });
    item.performanceSummary = data.snapshot?.rates || {};
    item.performanceBenchmark = data.snapshot?.benchmark || {};
    saveState();
    await loadViralAtoms();
    renderPublishList();
    toast("发布表现已回流到模板原子统计");
  } catch (error) {
    toast(`保存表现失败：${error.message}`);
  } finally {
    done();
  }
}

function readImageAsDataUrl(file, signal) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const onAbort = () => {
      if (reader.readyState === FileReader.LOADING) reader.abort();
      cleanup();
      reject(abortError("已停止上传模板"));
    };
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    reader.onload = () => {
      cleanup();
      resolve(String(reader.result || ""));
    };
    reader.onerror = () => {
      cleanup();
      reject(new Error("图片读取失败"));
    };
    reader.readAsDataURL(file);
  });
}

// 参考图和场景图过去按原图内嵌 data URL，一张手机照片就有几 MB：整包 JSON 会顶到
// 服务端 XHS_MAX_REQUEST_BYTES（25MB）被拒，写进草稿后还会顶到状态落库的单条 2MB /
// 整档 8MB 上限，导致整次保存失败。参考图只用来看构图、光线和风格，所以统一在入队时
// 降采样后重新编码；已经足够小的图保持原样，避免无谓的画质损失。
const COMPRESSED_IMAGE_MAX_SIDE = 1536;
const COMPRESSED_IMAGE_QUALITY = 0.82;
const COMPRESSED_IMAGE_MIN_BYTES = 600 * 1024;
// 单张参考图的目标体积（编码后的字节数）。只按长边+品质压缩时，2MB 级高细节照片仍会
// 剩下近 1MB，15 张就是十几 MB：请求虽能被服务端受理（未触 25MB 请求体上限），但上传
// 要在慢链路上持续几十秒，上游按固定秒数给的写超时照样会掐断（P72 实测：15 张 →
// 19MB 请求体 → 38s 后 execution_failed，服务端错误是「网络连接失败：请求超时」）。
// 所以再压一条字节预算：超了先降品质，再降长边，直到落进预算。
const COMPRESSED_IMAGE_TARGET_BYTES = 320 * 1024;

function loadImageElementForCompression(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("图片读取失败"));
    };
    image.src = url;
  });
}

async function readCompressedImageAsDataUrl(file, signal) {
  if (!file || !String(file.type || "").startsWith("image/") || Number(file.size) <= COMPRESSED_IMAGE_MIN_BYTES) {
    return readImageAsDataUrl(file, signal);
  }
  let image;
  try {
    image = await loadImageElementForCompression(file);
  } catch (_error) {
    return readImageAsDataUrl(file, signal);
  }
  throwIfAborted(signal);
  const sourceWidth = Number(image.naturalWidth || image.width) || 0;
  const sourceHeight = Number(image.naturalHeight || image.height) || 0;
  const canvas = document.createElement("canvas");
  if (!sourceWidth || !sourceHeight) return readImageAsDataUrl(file, signal);
  const context = canvas.getContext("2d");
  if (!context) return readImageAsDataUrl(file, signal);
  const targetChars = Math.round(COMPRESSED_IMAGE_TARGET_BYTES * (4 / 3));
  // 降档顺序：先只降品质，再连长边一起降。命中预算就停，避免为体积反复重编码。
  const steps = [
    [COMPRESSED_IMAGE_MAX_SIDE, COMPRESSED_IMAGE_QUALITY],
    [COMPRESSED_IMAGE_MAX_SIDE, 0.62],
    [1152, 0.62],
    [1024, 0.55],
  ];
  let best = "";
  for (const [maxSide, quality] of steps) {
    throwIfAborted(signal);
    const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));
    // PNG/WebP 可能带透明通道，JPEG 没有：先铺白底，免得透明区域变黑。
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    let encoded = "";
    try {
      encoded = canvas.toDataURL("image/jpeg", quality);
    } catch (_error) {
      encoded = "";
    }
    if (!encoded) continue;
    if (!best || encoded.length < best.length) best = encoded;
    if (encoded.length <= targetChars) return encoded;
  }
  // base64 体积约为原字节数的 4/3；压不小就退回原图，不为压缩牺牲可用性。
  if (!best || best.length >= Number(file.size) * (4 / 3)) {
    return readImageAsDataUrl(file, signal);
  }
  return best;
}

function dataUrlToBlob(dataUrl) {
  const match = /^data:([^;,]+)(;base64)?,([\s\S]*)$/.exec(String(dataUrl || ""));
  if (!match) return null;
  const mime = String(match[1] || "").trim() || "application/octet-stream";
  try {
    if (!match[2]) return new Blob([decodeURIComponent(match[3] || "")], { type: mime });
    const binary = atob(match[3] || "");
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type: mime });
  } catch (_error) {
    return null;
  }
}

// 上传口（图生文案 / 编辑器加图）过去直接把原图塞进 multipart：18 张手机照片就是二三十 MB，
// 在慢链路上会把请求拖过上游写超时（P72 实测同类问题：19MB 请求体 → 38.5s → 网关掐断）。
// 参考图入队已经有同一套降档压缩（readCompressedImageAsDataUrl），这里复用它把每张压进
// 字节预算再上传；压不小或本来就很小的图退回原文件，避免无谓的画质损失与后缀变换。
const UPLOAD_IMAGE_MIME_EXTENSIONS = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
};

async function compressedImageForUpload(file, signal) {
  if (!file || !String(file.type || "").startsWith("image/")) return file;
  // GIF 可能是动画：画布重编码会把它压成单帧，宁可不压。
  if (String(file.type).toLowerCase() === "image/gif") return file;
  let dataUrl = "";
  try {
    dataUrl = await readCompressedImageAsDataUrl(file, signal);
  } catch (_error) {
    return file;
  }
  const blob = dataUrlToBlob(dataUrl);
  if (!blob || !blob.size) return file;
  if (blob.size >= Number(file.size || 0)) return file;
  const extension = UPLOAD_IMAGE_MIME_EXTENSIONS[String(blob.type || "").toLowerCase()];
  if (!extension) return file;
  const base = String(file.name || "image").replace(/\.[^./\\]+$/, "") || "image";
  try {
    return new File([blob], base + extension, {
      type: blob.type,
      lastModified: Number(file.lastModified) || Date.now(),
    });
  } catch (_error) {
    return file;
  }
}

// 整包 JSON 体积预检：与后端 XHS_MAX_REQUEST_BYTES 同源，超限直接给出可执行的原因，
// 不要等请求被服务端拒掉再显示一句与体积无关的报错。
function assertImageRequestFitsLimit(inputImages, extraChars = 0) {
  const imageChars = (inputImages || []).reduce((total, src) => total + String(src || "").length, 0);
  const estimated = imageChars + Number(extraChars || 0);
  if (estimated <= MAX_REQUEST_BYTES) return true;
  const megabytes = (estimated / (1024 * 1024)).toFixed(1);
  toast(`本次参考图合计约 ${megabytes}MB，超过服务端 ${Math.round(MAX_REQUEST_BYTES / (1024 * 1024))}MB 的单次上限；请减少参考图张数后重试`, { kind: "error", duration: 8000 });
  return false;
}

function visualTemplateImportForKind(kind) {
  return state.visualTemplateImport?.kind === kind ? state.visualTemplateImport : null;
}

function refreshVisualTemplateImportUI(kind) {
  renderVisualTemplateImportButtons();
  if (kind === "cover" || kind === "inner") renderViralLibraryShelf(kind);
  if (kind === "cover" || kind === "scene") renderStyleGrid();
}

function renderVisualTemplateImportButtons() {
  const pending = state.visualTemplateImport;
  ["cover", "inner"].forEach((kind) => {
    const button = $(`#import${kind === "cover" ? "Cover" : "Inner"}TemplateBtn`);
    if (!button) return;
    const isCurrent = pending?.kind === kind;
    button.disabled = Boolean(pending) && !isCurrent;
    button.textContent = isCurrent ? "停止上传" : "导入单张图片";
    button.title = isCurrent ? "停止当前模板上传" : "";
    button.classList.toggle("is-cancel-action", isCurrent);
    button.setAttribute("aria-busy", String(isCurrent));
  });
}

function setVisualTemplateImportStage(kind, fileName, stage) {
  state.visualTemplateImport = { kind, fileName, stage };
  refreshVisualTemplateImportUI(kind);
}

function clearVisualTemplateImport(kind) {
  if (state.visualTemplateImport?.kind === kind) state.visualTemplateImport = null;
  refreshVisualTemplateImportUI(kind);
}

// 只 abort 浏览器这一侧的请求并不会停掉服务端任务：worker 还会继续跑，跑完照样把模板
// 写进封面/内页库。用户看到的就是"点了停止上传、界面也说停了，过一会儿那张图还是出现"
// （第三张分析完成后 loadViralLibraries 一次把积压的三张全放出来）。所以除了 abort，
// 还必须拿任务 id 显式取消服务端任务——与内容模板拆解的中止同一入口。
function cancelVisualTemplateImportJob(jobId) {
  const id = String(jobId || "").trim();
  if (!id) return;
  void postBackend("/api/jobs/cancel", { jobId: id }).catch((error) => {
    toast(`停止上传请求未送达：${error.message}（界面已停止，服务端可能仍在收尾）`, {
      kind: "error",
      duration: 8000,
    });
  });
}

function openVisualTemplateFilePicker(kind) {
  const pending = state.visualTemplateImport;
  if (pending) {
    if (pending.kind === kind && visualTemplateImportController) {
      // 先取 id 再 abort：abort 会立刻让导入流程走 finally 清掉这个 id。
      const jobId = visualTemplateImportJobId;
      setVisualTemplateImportStage(kind, pending.fileName, "正在停止上传");
      visualTemplateImportController.abort();
      cancelVisualTemplateImportJob(jobId);
      toast("正在停止模板上传...");
      return;
    }
    toast(`${pending.stage}，请稍候`, { duration: 3500 });
    return;
  }
  const input = kind === "cover" ? $("#viralCoverTemplateFile") : $("#viralInnerTemplateFile");
  input?.click();
}

function openSceneImageTemplateFilePicker() {
  const pending = state.visualTemplateImport;
  if (pending) {
    if (pending.kind === "scene" && visualTemplateImportController) {
      const jobId = visualTemplateImportJobId;
      setVisualTemplateImportStage("scene", pending.fileName, "正在停止上传");
      visualTemplateImportController.abort();
      cancelVisualTemplateImportJob(jobId);
      toast("正在停止模板上传...");
      return;
    }
    toast(`${pending.stage}，请稍候`, { duration: 3500 });
    return;
  }
  $("#imageSceneTemplateFile")?.click();
}

async function importSingleSceneTemplate(event) {
  const file = event?.target?.files?.[0];
  if (!file) return;
  if (state.visualTemplateImport) {
    toast(`${state.visualTemplateImport.stage}，请稍候`, { duration: 3500 });
    event.target.value = "";
    return;
  }
  const fileInputs = [$("#imageSceneTemplateFile"), $("#viralCoverTemplateFile"), $("#viralInnerTemplateFile")].filter(Boolean);
  const controller = new AbortController();
  visualTemplateImportController = controller;
  fileInputs.forEach((input) => { input.disabled = true; });
  setVisualTemplateImportStage("scene", file.name, "正在读取图片");
  toast(`已开始分析真实场景“${file.name}”`, { duration: 4500 });
  try {
    const image = await readCompressedImageAsDataUrl(file, controller.signal);
    setVisualTemplateImportStage("scene", file.name, "正在上传并分析场景");
    const title = file.name.replace(/\.[^.]+$/, "") || "真实场景模板";
    const data = await postBackend("/api/scene-templates/analyze", {
      draft: { id: `scene-upload-${Date.now()}`, title, images: [image] },
    }, {
      signal: controller.signal,
      onJobStart: (job) => {
        // 停止上传要靠这个 id 取消服务端任务；abort 只能停浏览器这一侧。
        if (visualTemplateImportController === controller) visualTemplateImportJobId = String(job.jobId || "");
      },
    });
    throwIfAborted(controller.signal);
    const template = data.sceneTemplate;
    if (!template?.prompt || !(template.referenceImages || []).length) throw new Error("AI 未返回有效的真实场景模板");
    setVisualTemplateImportStage("scene", file.name, "正在同步到模板库");
    state.customSceneImageTemplates = [{
      id: template.id || uid("scene-template"),
      mode: "scene",
      name: String(template.name || title || "真实场景模板"),
      desc: String(template.desc || "AI 根据上传图片分析的真实摄影场景"),
      ratio: template.ratio || "3:4",
      prompt: String(template.prompt).trim(),
      preview: template.preview || template.referenceImages[0],
      referenceImages: template.referenceImages,
    }, ...(state.customSceneImageTemplates || [])];
    state.selectedImageTemplateId = state.customSceneImageTemplates[0].id;
    state.imageStudioMode = "scene";
    saveState();
    renderStyleGrid();
    renderImageStudioSelection();
    toast("真实场景模板已自动分析并保存");
  } catch (error) {
    if (isAbortError(error)) toast("已停止上传模板");
    else toast(`真实场景模板分析失败，未入库：${error.message}`, { kind: "error", duration: 9000 });
  } finally {
    if (visualTemplateImportController === controller) {
      visualTemplateImportController = null;
      visualTemplateImportJobId = "";
    }
    event.target.value = "";
    fileInputs.forEach((input) => { input.disabled = false; });
    clearVisualTemplateImport("scene");
  }
}

async function importSingleVisualTemplate(event, kind) {
  const file = event?.target?.files?.[0];
  if (!file) return;
  if (state.visualTemplateImport) {
    toast(`${state.visualTemplateImport.stage}，请稍候`, { duration: 3500 });
    event.target.value = "";
    return;
  }
  const isCover = kind === "cover";
  const fileInputs = [$("#viralCoverTemplateFile"), $("#viralInnerTemplateFile")].filter(Boolean);
  const controller = new AbortController();
  visualTemplateImportController = controller;
  fileInputs.forEach((input) => { input.disabled = true; });
  setVisualTemplateImportStage(kind, file.name, "正在读取图片");
  toast(`已开始处理“${file.name}”`, { duration: 4500 });
  try {
    const image = await readCompressedImageAsDataUrl(file, controller.signal);
    setVisualTemplateImportStage(kind, file.name, "正在上传并分析版式");
    const draft = {
      id: `${kind}-upload-${Date.now()}`,
      title: file.name.replace(/\.[^.]+$/, "") || `单图${isCover ? "封面" : "内页"}模板`,
      body: "",
      tags: "",
      images: [image],
      templateRole: kind,
    };
    const data = await postBackend("/api/templates/generate", {
      draft,
      engine: "poster-auto",
      templateRole: kind,
      templatePageRoles: [kind],
    }, {
      signal: controller.signal,
      onJobStart: (job) => {
        // 停止上传要靠这个 id 取消服务端任务；abort 只能停浏览器这一侧，
        // 服务端 worker 会继续跑完并把模板写进库。
        if (visualTemplateImportController === controller) visualTemplateImportJobId = String(job.jobId || "");
      },
    });
    throwIfAborted(controller.signal);
    const template = data.template;
    if (!template) throw new Error("模板引擎未返回封面模板");
    state.layoutTemplates = data.templates || [template, ...state.layoutTemplates];
    state.activeLayoutTemplateId = template.id;
    setVisualTemplateImportStage(kind, file.name, "正在同步到模板库");
    await loadViralLibraries({ recommend: false });
    setViralWorkbench(kind);
    toast(`单图${isCover ? "封面" : "内页"}已拆解并加入${isCover ? "封面" : "内页"}库`);
  } catch (error) {
    if (isAbortError(error)) toast("已停止上传模板");
    else toast(`${isCover ? "封面" : "内页"}模板拆解失败，未入库：${error.message}`, { kind: "error", duration: 9000 });
  } finally {
    if (visualTemplateImportController === controller) {
      visualTemplateImportController = null;
      visualTemplateImportJobId = "";
    }
    event.target.value = "";
    fileInputs.forEach((input) => { input.disabled = false; });
    clearVisualTemplateImport(kind);
  }
}

function importSingleCoverTemplate(event) {
  return importSingleVisualTemplate(event, "cover");
}

function importSingleInnerTemplate(event) {
  return importSingleVisualTemplate(event, "inner");
}

async function generateViralVisualTemplate(button, templatePageRoles = null, { recommend = true, onProgress = null, requestOptions = null } = {}) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft || !(draft.images || []).length) {
    toast("当前笔记没有图片，无法生成原创笔记视觉模板");
    return;
  }
  if (!Array.isArray(templatePageRoles)) {
    const selection = await chooseTemplatePageRoles(draft.images || []);
    if (!selection) return;
    templatePageRoles = selection.pageRoles;
  }
  if (state.aiConfig?.text?.apiUrl) {
    try {
      await syncAiConfigBeforeGeneration();
    } catch (error) {
      toast(`AI 配置同步失败：${error.message}`);
      return;
    }
  }
  const done = button ? setButtonLoading(button, "正在生成...") : () => {};
  try {
    const data = await postBackend(
      "/api/templates/generate",
      { draft, engine: "poster-auto", templatePageRoles, pageVisualPromptsEnabled: false },
      requestOptions || { onProgress },
    );
    const template = data.template || null;
    state.layoutTemplates = data.templates || (template ? [template, ...state.layoutTemplates] : state.layoutTemplates);
    if (!template) throw new Error("模板引擎没有返回可用模板");
    state.activeLayoutTemplateId = template.id;
    draft.layoutTemplateId = template.id;
    saveState();
    await loadViralLibraries({ recommend, preferredAtomIds: Object.values(state.viralSelections || {}) });
    const templateAi = describeTemplateAi(template);
    toast(`${templateAi.badge}，已返回 ${template.renderedImages?.length || 0} 张模板预览`);
    return template;
  } catch (error) {
    if (button) {
      toast(`视觉模板拆解失败，服务器已停止入库：${error.message}`, { kind: "error", duration: 9000 });
    }
    throw error;
  } finally {
    done();
  }
}

let templateRoleDialogResolver = null;

function selectedTemplateImportKinds() {
  return $$('[data-template-import-kind]:checked').map((input) => input.value);
}

function syncTemplateVisualImportOptions() {
  const kinds = selectedTemplateImportKinds();
  const visualOptions = $("#templateVisualImportOptions");
  const hasVisual = kinds.includes("cover") || kinds.includes("inner");
  const splitVisual = kinds.includes("cover") && kinds.includes("inner");
  if (visualOptions) visualOptions.classList.toggle("hidden", !hasVisual);
  const roleTools = $(".template-role-tools");
  if (roleTools) roleTools.classList.toggle("hidden", !splitVisual);
  const rolePages = $("#templateRolePages");
  if (rolePages) rolePages.classList.toggle("hidden", !splitVisual);
}

function templateRolePageMarkup(images) {
  return images.map((src, index) => {
    const suggestedRole = index === 0 ? "cover" : "inner";
    return `
      <article class="template-role-page">
        <img src="${escapeHtml(src)}" alt="第 ${index + 1} 张模板图片" />
        <strong>第 ${index + 1} 张</strong>
        <div class="template-role-toggle" role="radiogroup" aria-label="第 ${index + 1} 张模板类型">
          <label><input type="radio" name="templatePageRole-${index}" value="cover" ${suggestedRole === "cover" ? "checked" : ""} /><span>封面</span></label>
          <label><input type="radio" name="templatePageRole-${index}" value="inner" ${suggestedRole === "inner" ? "checked" : ""} /><span>内页</span></label>
        </div>
      </article>`;
  }).join("");
}

function chooseTemplatePageRoles(images) {
  const modal = $("#templateRoleModal");
  if (!modal) return Promise.resolve({
    pageRoles: images.map((_, index) => index === 0 ? "cover" : "inner"),
    templateKinds: selectedTemplateImportKinds(),
  });
  $$('[data-template-import-kind]').forEach((input) => {
    input.checked = input.value === "cover" || (input.value === "inner" && images.length > 1);
    input.disabled = !images.length && (input.value === "cover" || input.value === "inner");
    if (input.disabled) input.checked = false;
  });
  $("#templateRolePages").innerHTML = templateRolePageMarkup(images);
  syncTemplateVisualImportOptions();
  refreshIcons();
  showModal(modal);
  return new Promise((resolve) => {
    templateRoleDialogResolver = resolve;
  });
}

function selectedTemplatePageRoles() {
  const kinds = selectedTemplateImportKinds();
  return $$("#templateRolePages .template-role-page").map((page, index) => {
    const selected = page.querySelector(`input[name='templatePageRole-${index}']:checked`)?.value || "inner";
    if (kinds.includes("cover") && !kinds.includes("inner")) return "cover";
    if (kinds.includes("inner") && !kinds.includes("cover")) return "inner";
    return selected;
  });
}

function templateRoleSelectionError(templateKinds = [], pageRoles = []) {
  if (!templateKinds.length) return "请至少选择一种要入库的模板";
  const roles = new Set(pageRoles);
  if (templateKinds.includes("cover") && !roles.has("cover")) {
    return "已勾选封面模板，但没有图片标记为封面。请标记至少一张封面图，或取消封面模板。";
  }
  if (templateKinds.includes("inner") && !roles.has("inner")) {
    return "已勾选内页模板，但没有图片标记为内页。请标记至少一张内页图，或取消内页模板。";
  }
  return "";
}

function setAllTemplatePageRoles(role) {
  $$(`#templateRolePages input[value='${role}']`).forEach((input) => {
    input.checked = true;
  });
}

function closeTemplateRoleDialog(selection = null) {
  const modal = $("#templateRoleModal");
  if (modal) hideModal(modal);
  const resolve = templateRoleDialogResolver;
  templateRoleDialogResolver = null;
  if (resolve) resolve(selection);
}


// ----- js/viral/library.js -----
function renderViralAtomLibrary() {
  const mount = $("#viralAtomLibrary");
  const count = $("#viralAtomCount");
  if (!mount || !count) return;
  const query = (state.viralAtomQuery || "").toLowerCase();
  const typeFilter = state.viralAtomTypeFilter || "";
  const atoms = (state.viralAtoms || []).filter((atom) => {
    if (typeFilter && atom.type !== typeFilter) return false;
    if (!query) return true;
    return `${atom.name} ${atom.pattern} ${(atom.evidence || []).join(" ")}`.toLowerCase().includes(query);
  });
  const selectedCount = Object.values(state.viralSelections || {}).filter(Boolean).length;
  count.textContent = `${selectedCount} 个底层原子`;
  if (!atoms.length) {
    mount.innerHTML = `<div class="viral-empty">分析笔记后，可在高级设置中查看和调整底层原子。</div>`;
    return;
  }
  const typeOrder = Object.keys(state.viralAtomTypes);
  mount.innerHTML = typeOrder
    .flatMap((type) => atoms.filter((atom) => atom.type === type))
    .map(
      (atom) => `
        <article class="viral-atom ${atom.status === "favorite" ? "favorite" : ""}">
          <label>
            <input type="radio" name="viral-atom-${escapeHtml(atom.type)}" value="${escapeHtml(atom.id)}" data-viral-atom-type="${escapeHtml(atom.type)}" ${state.viralSelections[atom.type] === atom.id ? "checked" : ""} />
            <strong>${escapeHtml(atom.typeLabel || state.viralAtomTypes[atom.type] || atom.type)} · ${escapeHtml(atom.name)}</strong>
            <span class="viral-atom-pattern">${escapeHtml(atom.pattern)}</span>
            <span class="viral-atom-meta">置信度 ${Math.round((Number(atom.confidence) || 0) * 100)}% · 来源 ${atom.sourceNoteIds?.length || 1} 篇 · 效果样本 ${atom.performance?.sampleSize || 0}</span>
            <span class="viral-evidence" title="${escapeHtml((atom.evidence || []).join("；"))}">证据：${escapeHtml((atom.evidence || []).join("；") || "暂无")}</span>
          </label>
          <div class="viral-atom-actions">
            <button type="button" data-atom-status="${atom.status === "favorite" ? "approved" : "favorite"}" data-atom-id="${escapeHtml(atom.id)}">${atom.status === "favorite" ? "取消收藏" : "收藏"}</button>
            <button type="button" data-atom-status="archived" data-atom-id="${escapeHtml(atom.id)}">归档</button>
          </div>
        </article>`
    )
    .join("");
}

function selectedViralTemplateIds(draft) {
  return {
    cover: draft?.coverTemplateId || draft?.viralBrief?.coverTemplateId || draft?.librarySnapshots?.cover?.templateId || "",
    inner: draft?.innerTemplateId || draft?.viralBrief?.innerTemplateId || draft?.librarySnapshots?.inner?.templateId || "",
  };
}

function isTemplateDecompositionDraft(draft) {
  if (!draft || isReplicaDraft(draft) || draftWorkflow(draft) !== "original" || draft.source !== "link") return false;
  if (draft.templateImportStatus === "imported" || draft.templateImportStatus === "ready") return false;
  const ids = selectedViralTemplateIds(draft);
  const selection = draft.librarySelection || {};
  const hasTemplate = Boolean(
    ids.cover
      || ids.inner
      || selection.coverId
      || selection.innerId
      || draft.templateRenderMode
      || draft.layoutTemplateId
  );
  return !hasTemplate && !isViralCombinationDraft(draft);
}

function removeExpansionReviewPage(pageId) {
  const draft = syncFormToDraft() || currentDraft();
  const review = draft?.pageExpansionReview;
  if (!draft || !review || review.status !== "pending") return;
  const before = Array.isArray(review.pages) ? review.pages : [];
  const pages = before.filter((page, index) => String(page.id || index) !== String(pageId));
  if (pages.length === before.length) return;
  review.pages = pages;
  review.additionalPageCount = pages.length;
  review.estimatedPageCount = Math.max(Number(review.basePageCount) || 1, (Number(review.basePageCount) || 1) + pages.length);
  saveState();
  renderEditor();
  toast("已删除该待确认页");
}

function isTemplateImportAwaitingSelection(draft) {
  return Boolean(draft && draft.templateImportStatus === "imported");
}

function isTemplateImportReady(draft) {
  return Boolean(draft && draft.templateImportStatus === "ready");
}

// 缺选题就不能生成的原创笔记：已经进入模板链（模板已入库、可生成）但还没选选题的草稿。
// 这类草稿判不出 viralMode（isViralCombinationDraft 要 topicId/recipe/plan 之一），于是
// 「可生成」退化成"内容非空"，按钮写着"使用模板生成图文"，点下去却走拿草稿原文直接改写的
// 老路（台账 P106）。判据与"搭建笔记内容"入口的选题守卫同义（factory.js 里同样提示
// "请先从选题库选择一个选题"）。快速笔记（复刻）与未进入模板链的草稿不在内。
function draftNeedsViralTopic(draft) {
  if (!draft || isReplicaDraft(draft) || draft.executionPlan) return false;
  if (draft.librarySelection?.topicId || draft.viralBrief?.topic || draft.viralRecipeId) return false;
  return isTemplateImportReady(draft);
}

function openTemplateLibraryForDraft() {
  state.activeWorkflow = "original";
  setSection("viral");
  setViralWorkbench("cover");
}

function requiresViralTemplateFit(draft) {
  const ids = selectedViralTemplateIds(draft);
  return Boolean(ids.cover && ids.inner);
}

function isViralCombinationDraft(draft) {
  return Boolean(
    draft
      && (
        draft.source === "viral"
        || draft.librarySelection?.topicId
        || draft.viralBrief?.topic
        || draft.viralRecipeId
        || draft.executionPlan
      )
  );
}

function hasReadyViralExecutionPlan(draft) {
  if (!draft || !requiresViralTemplateFit(draft)) return false;
  const plan = draft.executionPlan;
  const generated = plan?.generatedContent;
  const hasGeneratedCopy = Boolean(generated?.title && generated?.body);
  const hasPromptedCopy = Boolean(plan?.titlePrompt && plan?.bodyPrompt && plan?.tagsPrompt);
  const hasPlannedPages = Boolean(
    visualExecutionPlanMatchesDraft(draft)
      && plan?.pageTaskSet?.kind === "PageTaskSetV2"
      && (plan?.pageTaskSet?.stats?.taskCount || plan?.pageTaskSet?.series?.some((series) => series?.tasks?.length))
  );
  return Boolean(
    (hasReadyViralImagePlan(draft) || hasPlannedPages)
      && (hasGeneratedCopy || hasPromptedCopy)
  );
}

async function copyExecutionPlanPart(key) {
  const result = state.viralLastGeneration || {};
  const plan = result.executionPlan || result.generation?.executionPlan || result.recipe?.compiledOutput || {};
  const value = plan[key];
  if (value === undefined) return;
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  try {
    await navigator.clipboard.writeText(text);
    toast("执行指令已复制");
  } catch {
    toast("复制失败，请手动选择文本");
  }
}

const viralLibraryMeta = {
  topic: { label: "选题库", field: "topicId", index: "01", empty: "先分析笔记或选择一个选题" },
  title: { label: "标题库", field: "titleId", index: "02", empty: "等待匹配标题结构" },
  cover: { label: "封面模板库", field: "coverId", index: "03", empty: "暂无可用封面模板" },
  inner: { label: "内页模板库", field: "innerId", index: "04", empty: "暂无可用内页模板" },
};

let viralComposeLibraryGuideDraftId = "";

function beginViralComposeLibraryGuide(draftId) {
  viralComposeLibraryGuideDraftId = String(draftId || "");
}

function usesFourLibraryCompose(draft) {
  return Boolean(draft && !isReplicaDraft(draft) && (isViralCombinationDraft(draft)
    || draftNeedsViralTopic(draft)
    || (draft.source === "blank" && draftWorkflow(draft) === "original")));
}

function cancelViralComposeLibraryGuide() {
  viralComposeLibraryGuideDraftId = "";
}

function continueViralComposeLibraryGuide() {
  if (!viralComposeLibraryGuideDraftId) return false;
  const draft = currentDraft();
  if (!draft || String(draft.id || "") !== viralComposeLibraryGuideDraftId) {
    cancelViralComposeLibraryGuide();
    return false;
  }
  const nextKind = ["topic", "title", "cover", "inner"].find((kind) => !selectedViralLibraryItem(kind));
  if (nextKind) {
    const group = viralLibraryGroupForKind(nextKind);
    state.viralActiveLibraryKinds[group] = nextKind;
    setSection("viral");
    renderFourLibraryWorkbench();
    setViralWorkbench(group);
    toast(`请选择${viralLibraryMeta[nextKind].label}，选择后将自动进入下一项`);
    return true;
  }
  cancelViralComposeLibraryGuide();
  setSection("rewrite");
  toast("四库已选齐，请点击「按四库搭建内容」继续");
  return true;
}

function selectedViralLibraryItem(kind) {
  const meta = viralLibraryMeta[kind];
  return (state.viralLibraries[kind] || []).find((item) => item.id === state.viralLibrarySelections[meta.field]) || null;
}

function compactViralLibraryItem(item) {
  if (!item) return null;
  return {
    id: item.id || "",
    name: item.name || "",
    summary: item.summary || "",
    templateId: item.templateId || "",
    templateRole: item.templateRole || item.referenceRole || "",
    referenceRole: item.referenceRole || "",
    referenceImages: item.referenceImages || [],
    preview: item.preview || {},
    aiImage: item.aiImage || {},
    templateContract: item.templateContract || item.params?.templateContract || {},
    pattern: item.pattern || "",
    performance: item.performance || {},
    legacyAtomIds: item.legacyAtomIds || [],
    topicPackage: item.topicPackage || (item.kind === "topic" ? item : null),
  };
}

function restoreViralSelectionsFromDraft(draft = currentDraft()) {
  if (!draft) {
    const empty = { topicId: "", titleId: "", coverId: "", innerId: "", templateSetId: "" };
    const changed = Object.keys(empty).some((key) => state.viralLibrarySelections[key] !== empty[key]);
    state.viralLibrarySelections = empty;
    return changed;
  }
  const saved = draft.librarySelection || {};
  const snapshots = draft.librarySnapshots || {};
  const findItem = (kind, id, templateId) => {
    const items = state.viralLibraries[kind] || [];
    return items.find((item) => item.id === id)
      || (templateId ? items.find((item) => item.templateId === templateId) : null)
      || null;
  };

  const topic = findItem("topic", saved.topicId, snapshots.topic?.id);
  const title = findItem("title", saved.titleId, snapshots.title?.id);
  // Older drafts may only retain the raw template id (or a snapshot may be
  // missing after a library refresh). Fall back to the draft's canonical
  // template ids so a valid selection is not cleared during restoration.
  const cover = findItem("cover", saved.coverId, snapshots.cover?.templateId || draft.coverTemplateId);
  const inner = findItem("inner", saved.innerId, snapshots.inner?.templateId || draft.innerTemplateId);
  const restoredId = (kind, item, savedId, snapshotId, fallback = "") => {
    if (item?.id) return item.id;
    if ((state.viralLibraries[kind] || []).length) return "";
    return savedId || snapshotId || fallback;
  };
  const next = {
    topicId: restoredId("topic", topic, saved.topicId, snapshots.topic?.id),
    titleId: restoredId("title", title, saved.titleId, snapshots.title?.id),
    coverId: restoredId("cover", cover, saved.coverId, "", snapshots.cover?.templateId ? `cover:${snapshots.cover.templateId}` : ""),
    innerId: restoredId("inner", inner, saved.innerId, "", snapshots.inner?.templateId ? `inner:${snapshots.inner.templateId}` : ""),
    templateSetId: saved.templateSetId || cover?.templateSetId || inner?.templateSetId || draft.templateSetId || "",
  };
  const changed = Object.keys(next).some((key) => state.viralLibrarySelections[key] !== next[key]);
  state.viralLibrarySelections = next;
  return changed;
}

function viralSelectionSignature(selection = {}) {
  return ["topicId", "titleId", "coverId", "innerId", "templateSetId"]
    .map((key) => String(selection?.[key] || ""))
    .join("|");
}

function applyViralSelectionPreviewToDraft(draft) {
  const topicItem = selectedViralLibraryItem("topic") || draftViralLibraryItem(draft, "topic");
  if (!draft) return;
  const materialEntries = draftImageMaterialEntries(draft);
  const currentImages = normalizeImages(draft.images);
  const storedSourceImages = normalizeImages(draft.sourceImages);
  const preservedSourceImages = storedSourceImages.length
    ? storedSourceImages
    : (draft.source !== "viral" ? currentImages : []);
  const previousInnerTemplateId = draft.innerTemplateId || "";
  const previousAdditionalTemplatePages = draftAdditionalTemplatePages(draft).map((item) => ({ ...item }));

  const topicPackage = topicPackageFromItem(topicItem) || {};
  const coverItem = selectedViralLibraryItem("cover") || draftViralLibraryItem(draft, "cover");
  const innerItem = selectedViralLibraryItem("inner") || draftViralLibraryItem(draft, "inner");
  const previewBody = [
    topicPackage.coreClaim,
    ...(topicPackage.verifiedFacts || []),
    ...(topicPackage.contentPillars || []),
    topicPackage.benefit,
  ].map((value) => String(value || "").trim()).filter((value, index, items) => value && items.indexOf(value) === index);
  const previewTags = [topicPackage.category, ...(topicPackage.keywords || [])]
    .map((value) => String(value || "").trim().replace(/^#+/, ""))
    // 来源类目留空时后端会给「未分类」兜底：它只配出现在选题详情里，不能变成草稿标签。
    .filter((value, index, items) => value && !isUncategorizedCategory(value) && items.indexOf(value) === index)
    .slice(0, 8)
    .map((value) => `#${value}`);
  if (topicItem) {
    draft.source = "viral";
    draft.origin = "";
    draft.title = topicPackage.topic || topicPackage.name || topicItem.name || "待生成选题";
    draft.body = previewBody.join("\n");
    draft.tags = previewTags.join(" ");
  }
  draft.sourceImages = [...preservedSourceImages];
  draft.additionalTemplatePages = previousAdditionalTemplatePages;
  draft.templateRenderMode = coverItem || innerItem ? "ai-pending" : "";
  draft.imageGenerationError = "";
  draft.generationFailedStage = "";
  draft.imageGenerationFailedLabels = [];
  draft.imageGenerationFailedJobKeys = [];
  draft.promptMode = null;
  draft.templateFields = null;
  draft.coverTemplateId = coverItem?.templateId || "";
  draft.innerTemplateId = innerItem?.templateId || "";
  if (draft.templateImportStatus === "imported" && draft.coverTemplateId && draft.innerTemplateId) {
    draft.templateImportStatus = "ready";
    draft.templateImportBoundAt = new Date().toISOString();
  }
  if (previousInnerTemplateId !== draft.innerTemplateId && Array.isArray(draft.innerPageOrder)) {
    draft.innerPageOrder = draft.innerPageOrder.filter((key) => (
      String(key).startsWith("additional:")
      || !previousInnerTemplateId
      || !String(key).startsWith(`${previousInnerTemplateId}:`)
    ));
  }
  draft.layoutTemplateId = draft.innerTemplateId;
  draft.templateSetId = state.viralLibrarySelections.templateSetId || "";
  draft.templateReferences = {
    cover: { templateId: draft.coverTemplateId, images: coverItem?.referenceImages || [], aiImage: coverItem?.aiImage || {} },
    inner: { templateId: draft.innerTemplateId, images: innerItem?.referenceImages || [], aiImage: innerItem?.aiImage || {} },
  };
  const plannedEntries = draftPlannedPageEntries(draft);
  // 只有拿到可用的执行计划时才重算图集。重算的保留集只看 sourceImages，而编辑器里
  // 「增加图片」加进来的图只写在 promptPlaceholderImages / templateRenderImages 上，
  // 无计划时重算会把它们清空——这正是「点搭建笔记内容后刚加的图片消失」的成因。
  if (plannedEntries.length) {
    draft.templateRenderImages = [];
    draft.templateRenderImageLabels = [];
    draft.templateRenderImageStates = [];
    draft.templateRenderWatermarkReports = [];
  }
  draft.images = plannedEntries.length
    ? plannedEntries.map((entry) => entry.src)
    : (preservedSourceImages.length ? [...preservedSourceImages] : [...currentImages]);
  if (plannedEntries.length && materialEntries.length) {
    draft.templateRenderImages = [...draft.images];
    draft.templateRenderImageLabels = plannedEntries.map((entry) => entry.label);
    draft.templateRenderImageStates = plannedEntries.map(() => "reference");
    draft.templateRenderWatermarkReports = plannedEntries.map(() => null);
    appendDraftImageMaterialEntries(draft, materialEntries);
  }
  draft.viralSelectionAppliedSignature = viralSelectionSignature(draft.librarySelection);
}

function syncViralSelectionsToCurrentDraft({ invalidatePlan = true } = {}) {
  const draft = currentDraft();
  if (!draft) return;
  const previousTopicSignature = knowledgeTopicSignatureForDraft(draft);
  draft.librarySelection = { ...state.viralLibrarySelections };
  draft.librarySnapshots = Object.fromEntries(
    Object.keys(viralLibraryMeta).map((kind) => [kind, compactViralLibraryItem(selectedViralLibraryItem(kind))])
  );
  draft.viralBrief = collectViralBrief();
  applyViralSelectionPreviewToDraft(draft);
  markKnowledgeStaleIfTopicChanged(draft, previousTopicSignature);
  if (invalidatePlan) {
    draft.executionPlan = null;
    draft.viralRecipeId = "";
  }
  // 这次选择往槽里写的是"新任务"：上一次模板入库（拆解）的进度记录到这里就该退场。
  // 放在这里而不是每个选择入口上，是因为选题、标题、封面、内页四条入口最终都走这里；
  // 记录清理本身有守卫，正在拆解的那一轮不会被顺手清掉。
  if (typeof retireTemplateImportRecordForDraft === "function") retireTemplateImportRecordForDraft(draft);
  saveState();
  if (state.currentSection === "rewrite") renderEditor();
}

async function hydrateViralDraftSnapshots(draft) {
  if (!draft?.executionPlan || draft.librarySnapshots || viralSnapshotLoads.has(draft.id)) return;
  viralSnapshotLoads.add(draft.id);
  try {
    const data = await getBackend("/api/viral/libraries");
    if (data.libraries) {
      state.viralLibraries = normalizedViralLibraries(data.libraries);
    }
    draft.librarySnapshots = Object.fromEntries(
      Object.keys(viralLibraryMeta).map((kind) => {
        const meta = viralLibraryMeta[kind];
        const selectedId = draft.librarySelection?.[meta.field];
        const item = (state.viralLibraries[kind] || []).find((candidate) => candidate.id === selectedId);
        return [kind, compactViralLibraryItem(item)];
      })
    );
    saveState();
    if (state.activeDraftId === draft.id && state.currentSection === "rewrite") renderEditor();
  } catch {
    // Template references stored on the draft remain available as the visual fallback.
  } finally {
    viralSnapshotLoads.delete(draft.id);
  }
}

function libraryItemPreview(item, kind, compact = false) {
  const image = item?.preview?.images?.[0];
  if (image) return `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.name || viralLibraryMeta[kind].label)}" />`;
  const evidence = item?.preview?.evidence?.[0] || item?.summary || viralLibraryMeta[kind].empty;
  return `<span class="library-text-preview">${escapeHtml(compact ? String(evidence).slice(0, 44) : evidence)}</span>`;
}

function libraryOriginalImages(item, kind) {
  const references = [...new Set((item?.referenceImages || []).filter(Boolean))];
  if (item?.referenceRole === kind) return references;
  if (kind === "cover") return references.slice(0, 1);
  if (kind === "inner") return references.slice(1);
  return [];
}

function isTemplateCleanPlate(src) {
  return /(?:^|\/)clean_\d+\.[a-z0-9]+(?:[?#].*)?$/i.test(String(src || ""));
}

function templateDisplayImages(originals = [], rendered = []) {
  const pageCount = Math.max(originals.length, rendered.length);
  return Array.from({ length: pageCount }, (_, index) => {
    const original = originals[index] || "";
    const preview = rendered[index] || "";
    if (original && !isTemplateCleanPlate(original)) return original;
    return preview || original;
  }).filter(Boolean);
}

function visualTemplateImages(item, kind) {
  if (!item || !["cover", "inner"].includes(kind)) return [];
  const originals = libraryOriginalImages(item, kind);
  const rendered = (item?.preview?.images || []).filter(Boolean);
  return templateDisplayImages(originals, rendered);
}

function selectedVisualTemplatePreview(item, kind) {
  if (!item || !["cover", "inner"].includes(kind)) return libraryItemPreview(item, kind, true);
  const images = visualTemplateImages(item, kind);
  if (!images.length) return libraryItemPreview(item, kind, true);
  return `
    <div class="selected-template-gallery ${images.length === 1 ? "single" : "multiple"}">
      ${images.map((src, index) => `
        <button type="button" data-library-preview-image="${escapeHtml(src)}" data-preview-title="${escapeHtml(item.name || "视觉模板")} · ${kind === "cover" ? "封面" : `内页 ${index + 1}`}">
          <img src="${escapeHtml(src)}" alt="${escapeHtml(item.name || "视觉模板")} ${index + 1}" onerror="const button=this.closest('button'); button.classList.add('is-missing'); button.disabled=true; this.remove()" />
          <span class="selected-template-image-fallback"><i data-lucide="image-off" aria-hidden="true"></i><em>预览暂不可用</em></span>
        </button>`).join("")}
    </div>`;
}

function visualTemplateLibraryCarousel(item, kind) {
  const images = visualTemplateImages(item, kind);
  if (!images.length) return libraryItemPreview(item, kind, true);
  return `
    <div class="visual-template-carousel ${images.length === 1 ? "single" : "multiple"}" data-template-gallery data-gallery-index="0">
      ${images.map((src, index) => `
        <button type="button" class="visual-template-page ${index === 0 ? "active" : ""}" data-library-preview-image="${escapeHtml(src)}" data-preview-title="${escapeHtml(item.name || "视觉模板")} · ${kind === "cover" ? "封面" : `内页 ${index + 1}`}" aria-hidden="${index === 0 ? "false" : "true"}">
          <img src="${escapeHtml(src)}" alt="${escapeHtml(item.name || "视觉模板")} ${index + 1}" />
        </button>`).join("")}
      ${images.length > 1 ? `<div class="selected-template-gallery-nav">
        <button type="button" class="gallery-arrow" data-gallery-prev aria-label="上一页"><i data-lucide="chevron-left" aria-hidden="true"></i></button>
        <button type="button" class="gallery-arrow" data-gallery-next aria-label="下一页"><i data-lucide="chevron-right" aria-hidden="true"></i></button>
      </div>
      <div class="selected-template-gallery-dots" role="tablist" aria-label="模板页面">
        ${images.map((src, index) => `<button type="button" class="gallery-dot ${index === 0 ? "active" : ""}" data-gallery-dot="${index}" aria-label="第 ${index + 1} 页" aria-current="${index === 0 ? "true" : "false"}"></button>`).join("")}
      </div>` : ""}
    </div>`;
}

function topicPackageFromItem(item) {
  return item?.topicPackage && typeof item.topicPackage === "object" ? item.topicPackage : item;
}

function topicPackageIdentity(item) {
  return String(topicPackageFromItem(item)?.id || item?.id || "").trim();
}

function activeTopicLibraryItems(items = []) {
  return items.filter((item) => !archivedTopicPackageIds.has(topicPackageIdentity(item)));
}

function titleTemplateAtomIdentity(item) {
  return String(item?.legacyAtomIds?.[0] || item?.atomId || "").trim();
}

function activeTitleLibraryItems(items = []) {
  return items.filter((item) => !archivedTitleAtomIds.has(titleTemplateAtomIdentity(item)));
}

function activeViralAtoms(atoms = []) {
  return atoms.filter((atom) => !archivedTitleAtomIds.has(String(atom?.id || "")));
}

function normalizedViralLibraries(libraries = {}) {
  const normalized = { topic: [], title: [], cover: [], inner: [], ...libraries };
  normalized.topic = activeTopicLibraryItems(normalized.topic);
  normalized.title = activeTitleLibraryItems(normalized.title);
  return normalized;
}

const topicTrackRules = [
  {
    id: "career",
    label: "职场求职",
    strong: ["职场", "求职", "面试", "简历", "领导", "同事", "薪资", "试用期", "转行", "offer", "汇报", "跨部门"],
    related: ["工作", "协作", "职业", "岗位", "空窗期", "谈薪", "入职", "离职"],
  },
  {
    id: "social",
    label: "人际关系",
    strong: ["室友", "朋友", "亲子", "父母", "家庭关系", "人际", "社交", "合租", "同住", "家人"],
    related: ["相处", "边界", "沟通", "友情", "改约", "家庭", "请求"],
  },
  {
    id: "romance",
    label: "婚恋情感",
    strong: ["相亲", "择偶", "婚恋", "恋爱", "脱单", "约会", "单身", "结婚"],
    related: ["伴侣", "感情", "亲密关系", "对象", "关系投入"],
  },
  {
    id: "fitness",
    label: "健身运动",
    strong: ["健身", "训练", "器械", "运动", "热身", "动作学习"],
    related: ["健身房", "恢复", "重量", "拉伸", "体能"],
  },
  {
    id: "home",
    label: "家居生活",
    strong: ["装修", "家居", "收纳", "交房", "施工", "软装", "硬装"],
    related: ["空间", "户型", "家务", "家装"],
  },
  {
    id: "food",
    label: "美食烹饪",
    strong: ["做饭", "备餐", "食材", "买菜", "厨房", "烹饪", "菜谱"],
    related: ["饮食", "独居", "食材保存"],
  },
  {
    id: "content",
    label: "内容运营",
    strong: ["小红书运营", "内容运营", "账号运营", "选题策划", "博主", "流量", "发布笔记", "账号规划"],
    related: ["xiaohongshu", "小红书", "运营", "内容", "账号", "笔记"],
  },
  {
    id: "learning",
    label: "学习成长",
    strong: ["学习", "备考", "考公", "考试", "读书", "课程", "自律"],
    related: ["成长", "规划", "复盘", "行动标准", "方法", "新手"],
  },
  { id: "other", label: "其他", strong: [], related: [] },
];

const topicFormatRules = [
  {
    id: "script",
    label: "沟通话术",
    strong: ["话术", "回答", "表达包", "沟通模板", "请示", "催进度", "谈薪", "解释"],
    related: ["怎么说", "回复", "开口", "句式", "替换", "求职表达"],
  },
  {
    id: "checklist",
    label: "自查清单",
    strong: ["清单", "收藏卡", "备忘录", "自查单", "分级表", "对照包", "收藏模板"],
    related: ["收藏", "检查", "对照", "步骤", "模板"],
  },
  {
    id: "analysis",
    label: "分析避坑",
    weight: 5,
    strong: ["识别卡", "拆解", "翻译器", "避坑", "矛盾组合", "正反对照"],
    related: ["判断", "分析", "误区", "风险", "条件"],
  },
  {
    id: "guide",
    label: "入门攻略",
    weight: 3,
    strong: ["入门", "新手", "起步", "攻略", "规划包", "安排", "顺序", "流程"],
    related: ["如何", "方法", "教程", "前置", "训练计划", "行动标准"],
  },
  {
    id: "review",
    label: "复盘记录",
    weight: 6,
    strong: ["复盘", "记录"],
    related: ["反馈", "跟踪", "日志"],
  },
  { id: "insight", label: "经验观点", strong: ["观点", "经验", "故事"], related: [] },
];

function topicPackageClassificationText(topicPackage) {
  return [
    topicPackage?.name,
    topicPackage?.category,
    topicPackage?.topic,
    topicPackage?.audience,
    topicPackage?.scenario,
    topicPackage?.pain,
    topicPackage?.benefit,
    topicPackage?.coreClaim,
    ...(topicPackage?.keywords || []),
    ...(topicPackage?.contentPillars || []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function topicPackageFormatText(topicPackage) {
  return [topicPackage?.name, topicPackage?.topic, ...(topicPackage?.keywords || [])].filter(Boolean).join(" ").toLowerCase();
}

function topicRuleByValue(rules, value) {
  const normalized = String(value || "").trim().toLowerCase();
  return rules.find((rule) => rule.id === normalized || rule.label.toLowerCase() === normalized) || null;
}

function inferTopicRule(topicPackage, rules, overrideField, sourceText = "") {
  const explicitRule = topicRuleByValue(rules, topicPackage?.[overrideField]);
  if (explicitRule) return explicitRule;
  const text = sourceText || topicPackageClassificationText(topicPackage);
  let bestRule = rules[rules.length - 1];
  let bestScore = 0;
  rules.slice(0, -1).forEach((rule) => {
    const strongScore = rule.strong.reduce((score, keyword) => score + (text.includes(keyword) ? Number(rule.weight || 4) : 0), 0);
    const relatedScore = rule.related.reduce((score, keyword) => score + (text.includes(keyword) ? 1 : 0), 0);
    const score = strongScore + relatedScore;
    if (score > bestScore) {
      bestRule = rule;
      bestScore = score;
    }
  });
  return bestRule;
}

function classifyTopicPackage(item) {
  const topicPackage = topicPackageFromItem(item) || {};
  const track = inferTopicRule(topicPackage, topicTrackRules, "contentTrack");
  const format = inferTopicRule(topicPackage, topicFormatRules, "contentFormat", topicPackageFormatText(topicPackage));
  return { track, format, label: `${track.label} · ${format.label}` };
}

function topicPackageSearchText(item) {
  const topicPackage = topicPackageFromItem(item) || {};
  const classification = classifyTopicPackage(topicPackage);
  return `${topicPackageClassificationText(topicPackage)} ${classification.track.label} ${classification.format.label}`.toLowerCase();
}

function topicPackageMatchesFilters(item) {
  const query = state.viralTopicQuery.trim().toLowerCase();
  const classification = classifyTopicPackage(item);
  return (!query || topicPackageSearchText(item).includes(query))
    && (!state.viralTopicTrack || classification.track.id === state.viralTopicTrack)
    && (!state.viralTopicFormat || classification.format.id === state.viralTopicFormat);
}

function topicRuleOptions(items, rules, dimension) {
  const counts = new Map();
  items.forEach((item) => {
    const rule = classifyTopicPackage(item)[dimension];
    counts.set(rule.id, (counts.get(rule.id) || 0) + 1);
  });
  return rules.filter((rule) => counts.has(rule.id)).map((rule) => ({ ...rule, count: counts.get(rule.id) }));
}

function topicPackageFactsStatus(topicPackage) {
  const required = topicPackage?.factRequirements || [];
  const verified = topicPackage?.verifiedFacts || [];
  if (!required.length) return { label: "事实资料无需补充", ready: true };
  if (verified.length >= required.length) return { label: `事实资料已齐全 · ${verified.length} 条`, ready: true };
  return { label: `待补充事实 · ${required.length - verified.length} 项`, ready: false };
}

function topicPackageStoredDetails(item) {
  const topicPackage = topicPackageFromItem(item);
  const classification = classifyTopicPackage(topicPackage);
  const performance = topicPackage.performance || {};
  const section = (title, values) => (values || []).length
    ? `<div class="library-stored-section"><strong>${title}</strong>${values.map((value) => `<p>${escapeHtml(value)}</p>`).join("")}</div>`
    : "";
  return `
    <details class="library-card-details topic-package-details">
      <summary>展开完整选题包</summary>
      <div class="library-card-detail-body">
        <dl class="library-card-metadata">
          <div><dt>内容分类</dt><dd>${escapeHtml(classification.label)}</dd></div>
          <div><dt>来源类目</dt><dd>${escapeHtml(topicPackage.category || "未分类")}</dd></div>
          <div><dt>核心观点</dt><dd>${escapeHtml(topicPackage.coreClaim || "待补充")}</dd></div>
          <div><dt>使用场景</dt><dd>${escapeHtml(topicPackage.scenario || "待补充")}</dd></div>
          <div><dt>表现数据</dt><dd>样本 ${Number(performance.sampleSize || 0)} · 平均互动率 ${(Number(performance.avgEngagementRate || 0) * 100).toFixed(2)}% · 平均收藏率 ${(Number(performance.avgCollectRate || 0) * 100).toFixed(2)}%</dd></div>
        </dl>
        ${section("内容大纲", topicPackage.contentPillars)}
        ${section("封面钩子候选", topicPackage.coverHooks)}
        ${section("关键词", topicPackage.keywords)}
        ${section("已验证事实", topicPackage.verifiedFacts)}
        ${section("待补充事实", topicPackage.factRequirements)}
        ${section("禁止照搬项", topicPackage.forbidden)}
        ${section("来源机制", topicPackage.mechanismAtomIds)}
        ${section("来源证据", topicPackage.sourceEvidence)}
      </div>
    </details>`;
}

function reverseTopicCardSources(topicPackage) {
  const structured = Array.isArray(topicPackage?.reverseTopicSources)
    ? topicPackage.reverseTopicSources.filter((item) => item && typeof item === "object" && String(item.filename || "").trim())
    : [];
  if (structured.length) return structured.map((item) => ({
    filename: String(item.filename || "").trim(),
    relativePath: String(item.relativePath || item.filename || "").trim(),
    pageRange: String(item.pageRange || "").trim(),
    sourceType: String(item.sourceType || "local").trim() || "local",
    rangeRecorded: true,
  }));
  return (topicPackage?.sourceEvidence || []).map((value) => {
    const sourceName = String(value || "").split(":", 1)[0].trim();
    const filename = sourceName.split(/[\\/]/).filter(Boolean).pop() || sourceName;
    return { filename, relativePath: sourceName, pageRange: "", rangeRecorded: false };
  }).filter((item) => item.filename);
}

function reverseTopicCardProvenance(topicPackage) {
  if (!topicPackage?.reverseTopicMode) return "";
  const sources = reverseTopicCardSources(topicPackage);
  const rows = sources.length
    ? sources.map((source) => {
        const pageRange = source.pageRange.replace(/\s*,\s*/g, "、");
        const rangeLabel = source.sourceType === "cloud"
          ? "云端全文"
          : source.rangeRecorded ? (pageRange ? `第 ${pageRange} 页` : "全文") : "范围未记录";
        const title = `${source.relativePath || source.filename} · ${rangeLabel}`;
        return `<div class="reverse-topic-source-line" title="${escapeHtml(title)}"><i data-lucide="${source.sourceType === "cloud" ? "cloud" : "file-text"}"></i><span>${escapeHtml(source.filename)}</span><em>${escapeHtml(rangeLabel)}</em></div>`;
      }).join("")
    : `<div class="reverse-topic-source-line is-empty"><i data-lucide="file-question"></i><span>来源文件未记录</span></div>`;
  return `<div class="reverse-topic-source-ledger"><div class="reverse-topic-source-ledger-head"><span>参考文件</span><em>${sources.length} 份</em></div><div class="reverse-topic-source-lines">${rows}</div></div>`;
}

function topicPackagePendingCard(item, topicPackage) {
  const pendingName = escapeHtml(topicPackage.name || topicPackage.topic || "新选题");
  return `
    <article class="library-item-card topic-library-card topic-package-card is-pending" data-topic-package-card="${escapeHtml(item.id)}" aria-busy="true" aria-label="正在创建选题">
      <div class="topic-package-main">
        <div class="topic-package-heading">
          <div class="topic-package-heading-labels"><span>创建中</span></div>
          <em class="library-card-pending-badge">正在保存</em>
        </div>
        <strong>${pendingName}</strong>
        <div class="topic-card-focus">
          <div><span>适合</span><p class="pending-line" aria-hidden="true"></p></div>
          <div class="primary"><span>价值</span><p class="pending-line" aria-hidden="true"></p></div>
        </div>
      </div>
      <div class="topic-package-actions">
        <button type="button" class="primary-btn" disabled>创建中…</button>
      </div>
    </article>`;
}

// 反向选题（「从资料提炼选题」）是长任务：进度过去只画在弹层里，关掉弹层或在四库里就完全
// 看不出在生成（现场：不点进弹层压根不知道在跑）。这里把它做成选题书架里的一张占位卡，
// 阶段文案跟着服务端进度走。
//
// 占位卡**不进** state.viralLibraries.topic：它不是一条选题，塞进数据会被当成真选题去选中、
// 去统计、跟着草稿持久化，还得在每个成功/失败分支里记得删。渲染时按
// state.reverseTopicGeneration.active 现插一张，任务一结束自然消失，也不用清理。
function reverseTopicGenerationStage() {
  const generation = state.reverseTopicGeneration || {};
  if (generation.active !== true) return "";
  const stage = String(generation.stage || "").trim();
  if (stage) return stage;
  return generation.mode === "broad" ? "正在生成宽泛主题" : "正在生成聚焦主题";
}

let reverseTopicShelfStage = "";

// 阶段文案没变就不重画：重画会重建搜索框，用户正在里面打字时会被抢焦点。
function syncReverseTopicGenerationCard() {
  const stage = reverseTopicGenerationStage();
  if (stage === reverseTopicShelfStage) return;
  reverseTopicShelfStage = stage;
  renderViralLibraryShelf("topic");
}

function reverseTopicPendingCard(stageText) {
  const stage = escapeHtml(String(stageText || "正在从资料提炼选题"));
  return `
    <article class="library-item-card topic-library-card topic-package-card is-pending" aria-busy="true" aria-label="${stage}">
      <div class="topic-package-main">
        <div class="topic-package-heading">
          <div class="topic-package-heading-labels"><span>选题生成中</span></div>
          <em class="library-card-pending-badge">正在生成</em>
        </div>
        <strong>${stage}</strong>
        <div class="topic-card-focus">
          <div><span>适合</span><p class="pending-line" aria-hidden="true"></p></div>
          <div class="primary"><span>价值</span><p class="pending-line" aria-hidden="true"></p></div>
        </div>
      </div>
      <div class="topic-package-actions">
        <button type="button" class="primary-btn" disabled>生成中…</button>
      </div>
    </article>`;
}

function topicPackageCard(item) {
  const topicPackage = topicPackageFromItem(item);
  const packageId = topicPackageIdentity(item);
  if (pendingTopicPackageSaves.has(packageId)) return topicPackagePendingCard(item, topicPackage);
  const classification = classifyTopicPackage(topicPackage);
  const selected = state.viralLibrarySelections.topicId === item.id;
  const editing = Boolean(state.textLibraryEditing.topic);
  const archiving = pendingTopicPackageArchives.has(packageId);
  const reverseMode = topicPackage.reverseTopicMode === "broad" ? "broad" : topicPackage.reverseTopicMode === "focused" ? "focused" : "";
  return `
    <article class="library-item-card topic-library-card topic-package-card ${selected ? "selected" : ""} ${archiving ? "is-deleting" : ""}" data-topic-package-card="${escapeHtml(item.id)}" ${archiving ? `aria-busy="true"` : ""} ${editing ? `role="group"` : `data-select-library-item="${escapeHtml(item.id)}" data-library-kind="topic" role="button" tabindex="0" aria-pressed="${selected ? "true" : "false"}"`}>
      <div class="topic-package-main">
        <div class="topic-package-heading">
          <div class="topic-package-heading-labels"><span title="${escapeHtml(topicPackage.category || "未分类")}">${escapeHtml(classification.label)}</span>${reverseMode ? `<em class="reverse-topic-mode-badge ${reverseMode}"><i data-lucide="${reverseMode === "broad" ? "scan-text" : "focus"}"></i>${reverseMode === "broad" ? "宽泛主题" : "聚焦主题"}</em>` : ""}</div>
          ${selected ? `<em class="library-card-selected-badge">已选</em>` : ""}
        </div>
        <strong>${escapeHtml(topicPackage.name || topicPackage.topic || "未命名选题")}</strong>
        ${reverseTopicCardProvenance(topicPackage)}
        <div class="topic-card-focus">
          <div><span>适合</span><p title="${escapeHtml(topicPackage.audience || "未指定")}">${escapeHtml(topicPackage.audience || "未指定（按主题推断）")}</p></div>
          <div class="primary"><span>价值</span><p title="${escapeHtml(topicPackage.benefit || topicPackage.coreClaim || "未指定")}">${escapeHtml(topicPackage.benefit || topicPackage.coreClaim || "未指定（按主题推断）")}</p></div>
        </div>
      </div>
      <div class="topic-package-actions">
        ${editing ? `
          <button type="button" class="primary-btn" data-edit-topic-package="${escapeHtml(item.id)}">编辑</button>
          <button type="button" class="mini-btn danger-btn" data-archive-topic-package="${escapeHtml(item.id)}" ${archiving ? "disabled" : ""}>${archiving ? "删除中" : "删除"}</button>` : `
        <button type="button" class="primary-btn ${selected ? "is-selected" : ""}" data-select-library-item="${escapeHtml(item.id)}" data-library-kind="topic">${selected ? `<i data-lucide="check"></i><span>已选择</span>` : "选择选题"}</button>
        <details class="topic-card-more">
          <summary aria-label="更多操作" title="更多操作"><i data-lucide="more-horizontal"></i></summary>
          <div class="topic-card-menu" role="menu">
            <button type="button" class="mini-btn" data-edit-topic-package="${escapeHtml(item.id)}">编辑</button>
            <button type="button" class="mini-btn" data-copy-topic-package="${escapeHtml(item.id)}">复制</button>
            <button type="button" class="mini-btn danger-btn" data-archive-topic-package="${escapeHtml(item.id)}" ${archiving ? "disabled" : ""}>${archiving ? "删除中" : "删除"}</button>
          </div>
        </details>`}
      </div>
      ${topicPackageStoredDetails(item)}
    </article>`;
}

function titleLibraryPendingCard(item) {
  const pendingName = escapeHtml(item.name || "新标题模板");
  return `
    <article class="library-item-card title-library-card topic-package-card is-pending" aria-busy="true" aria-label="正在创建标题模板">
      <div class="topic-package-main">
        <div class="topic-package-heading">
          <span>标题结构</span>
          <em class="library-card-pending-badge">正在保存</em>
        </div>
        <strong>${pendingName}</strong>
        <div class="topic-card-focus">
          <div class="primary"><span>公式</span><p class="pending-line" aria-hidden="true"></p></div>
          <div><span>示例</span><p class="pending-line" aria-hidden="true"></p></div>
        </div>
      </div>
      <div class="topic-package-actions">
        <button type="button" class="primary-btn" disabled>创建中…</button>
      </div>
    </article>`;
}

function titleLibraryCard(item) {
  const selected = state.viralLibrarySelections.titleId === item.id;
  const editing = Boolean(state.textLibraryEditing.title);
  const atomId = titleTemplateAtomIdentity(item);
  if (pendingTitleTemplateSaves.has(atomId)) return titleLibraryPendingCard(item);
  const archiving = pendingTitleTemplateArchives.has(atomId);
  const evidence = item?.preview?.evidence || [];
  const pattern = item?.pattern || item?.summary || "使用通用标题结构";
  const example = evidence.find(Boolean) || "按选题替换变量后即可使用";
  return `
    <article class="library-item-card title-library-card topic-package-card ${selected ? "selected" : ""} ${archiving ? "is-deleting" : ""}" ${archiving ? `aria-busy="true"` : ""} ${editing ? `role="group"` : `data-select-library-item="${escapeHtml(item.id)}" data-library-kind="title" role="button" tabindex="0" aria-pressed="${selected ? "true" : "false"}"`}>
      <div class="topic-package-main">
        <div class="topic-package-heading">
          <span>标题结构</span>
          ${selected ? `<em class="library-card-selected-badge">已选</em>` : ""}
        </div>
        <strong>${escapeHtml(item.name || "未命名标题结构")}</strong>
        <div class="topic-card-focus">
          <div class="primary"><span>公式</span><p title="${escapeHtml(pattern)}">${escapeHtml(pattern)}</p></div>
          <div><span>示例</span><p title="${escapeHtml(example)}">${escapeHtml(example)}</p></div>
        </div>
      </div>
      <div class="topic-package-actions">
        ${editing ? `
          <button type="button" class="primary-btn" data-edit-title-template="${escapeHtml(item.id)}">编辑</button>
          <button type="button" class="mini-btn danger-btn" data-archive-title-template="${escapeHtml(item.id)}" ${archiving ? "disabled" : ""}>${archiving ? "删除中" : "删除"}</button>` : `
          <button type="button" class="primary-btn ${selected ? "is-selected" : ""}" data-select-library-item="${escapeHtml(item.id)}" data-library-kind="title">${selected ? `<i data-lucide="check"></i><span>已选择</span>` : "选择标题"}</button>`}
      </div>
      ${renderLibraryStoredDetails(item, "title")}
    </article>`;
}

function renderLibraryStoredDetails(item, kind) {
  const originals = libraryOriginalImages(item, kind);
  const evidence = item?.preview?.evidence || [];
  const schema = item?.aiImage?.textSchema || {};
  const allSchemaPages = Array.isArray(schema.pages) ? schema.pages : [];
  const schemaPages = kind === "cover"
    ? allSchemaPages.filter((page, index) => page?.role === "cover" || (!page?.role && index === 0)).slice(0, 1)
    : allSchemaPages.filter((page, index) => page?.role === "inner" || Number(page?.index ?? index) > 0);
  const structureGroups = item?.viralStructure?.groups || {};
  const structureItems = Array.isArray(structureGroups[kind]) ? structureGroups[kind] : [];
  const fieldCount = schemaPages.reduce((total, page) => total + (page?.fields || []).length, 0);
  const basePrompt = visualTemplateRolePrompt(item?.aiImage, kind);
  const scopePrompt = kind === "cover"
    ? "本模板仅用于封面。只生成首张封面，不要生成或描述内页；重点保持封面构图、标题层级和首屏点击信息。"
    : `本模板仅用于内页。只生成正文内容页，不要生成或描述封面。\n${INNER_VISUAL_SYSTEM_RULE}`;
  const prompt = ["cover", "inner"].includes(kind) && basePrompt
    ? (basePrompt.includes("本模板仅用于") || basePrompt.includes(INNER_VISUAL_SYSTEM_RULE)
      ? basePrompt
      : `${scopePrompt}\n\n${basePrompt}`.trim())
    : basePrompt;
  return `
    <details class="library-card-details">
      <summary>展开全部储存内容</summary>
      <div class="library-card-detail-body">
        <dl class="library-card-metadata">
          <div><dt>模板摘要</dt><dd>${escapeHtml(item.summary || "暂无")}</dd></div>
          <div><dt>模板编号</dt><dd>${escapeHtml(item.templateId || item.id || "暂无")}</dd></div>
          <div><dt>储存内容</dt><dd>${kind === "cover" || kind === "inner" ? `${originals.length} 张原图 · ${schemaPages.length} 页文字结构 · ${fieldCount} 个可编辑槽位` : `${(item.legacyAtomIds || []).length} 个结构原子 · ${evidence.length} 条来源证据`}</dd></div>
        </dl>
        ${originals.length ? `
          <div class="library-original-preview">
            <strong>原图等比例预览</strong>
            <div>${originals.map((src, index) => `<button type="button" data-library-preview-image="${escapeHtml(src)}" data-preview-title="${escapeHtml(item.name || "模板")} · 原图 ${index + 1}"><img src="${escapeHtml(src)}" alt="原图预览 ${index + 1}" /></button>`).join("")}</div>
          </div>` : ""}
        ${schemaPages.length ? `
          <div class="library-stored-section"><strong>可编辑文字结构</strong>${schemaPages.map((page, index) => `<p>${kind === "cover" ? "封面" : `内页 ${index + 1}`}：${(page.fields || []).map((field) => `${field.label || field.key}（${Number(field.maxChars || 0)}字/${Number(field.maxLines || 1)}行）`).join("、") || "无文字槽位"}</p>`).join("")}</div>` : ""}
        ${structureItems.length ? `<div class="library-stored-section"><strong>内容结构</strong>${structureItems.map((part) => `<p>${escapeHtml(part.name || part.type || "结构")}：${escapeHtml(part.pattern || "")}</p>`).join("")}</div>` : ""}
        ${evidence.length ? `<div class="library-stored-section"><strong>来源证据</strong>${evidence.map((text) => `<p>${escapeHtml(text)}</p>`).join("")}</div>` : ""}
        ${prompt ? `<div class="library-stored-section"><strong>AI 成图提示词</strong><pre>${escapeHtml(prompt)}</pre></div>` : ""}
      </div>
    </details>`;
}

function libraryRecommendationReason(kind, item) {
  if (!item) return "等待选择";
  if (kind === "topic") return "匹配当前笔记的人群、场景与痛点";
  if (kind === "title") return "根据选题的点击目标推荐";
  if (kind === "cover") return item.templateSetId ? "优先采用成套模板首图" : "匹配当前视觉风格";
  const cover = selectedViralLibraryItem("cover");
  return cover?.templateSetId && cover.templateSetId === item.templateSetId ? "与当前封面属于同一套模板" : "按正文结构与页间节奏匹配";
}

const viralLibraryGroups = {
  topic: { kinds: ["topic"], shelfId: "viralTopicLibraryShelf", panelId: "viralTopicWorkbench" },
  title: { kinds: ["title"], shelfId: "viralTitleLibraryShelf", panelId: "viralTitleWorkbench" },
  cover: { kinds: ["cover"], shelfId: "viralCoverLibraryShelf", panelId: "viralCoverWorkbench" },
  inner: { kinds: ["inner"], shelfId: "viralInnerLibraryShelf", panelId: "viralInnerWorkbench" },
};

function viralLibraryGroupForKind(kind) {
  return viralLibraryGroups[kind] ? kind : "topic";
}

function setViralWorkbench(workbench) {
  if (!viralLibraryGroups[workbench]) workbench = "topic";
  const previousPanel = $(".viral-library-workspace:not(.hidden)");
  state.viralActiveWorkbench = workbench;
  Object.entries(viralLibraryGroups).forEach(([name, config]) => {
    const panel = $(`#${config.panelId}`);
    if (panel) {
      const active = name === workbench;
      panel.classList.toggle("active", active);
      panel.classList.toggle("hidden", !active);
      panel.setAttribute("aria-hidden", String(!active));
    }
  });
  $$("[data-viral-nav-kind]").forEach((button) => {
    button.classList.toggle("active", button.dataset.viralNavKind === workbench);
  });
  renderViralLibraryNavStatus();
  const activePanel = $(`#${viralLibraryGroups[workbench].panelId}`);
  if (previousPanel && previousPanel !== activePanel) {
    playUiMotion(activePanel, "ui-view-enter");
    if (window.matchMedia?.("(max-width: 640px)").matches) {
      document.scrollingElement?.scrollTo?.({ top: 0, left: 0, behavior: "instant" });
    }
  }
  renderViralWorkflowRoute();
}

// 四库推荐顺序：选题必选，标题/封面/内页按需搭配。引导文案与"下一步"都以它为准，
// 避免和判据各说一套。
const viralLibraryOrder = ["topic", "title", "cover", "inner"];

function viralNextStep(kind) {
  const labels = { topic: "选题库", title: "标题库", cover: "封面库", inner: "内页库" };
  if (!selectedViralLibraryItem("topic")) {
    return kind === "topic"
      ? { hint: "选题必选：从下面选一个开始" }
      : { label: "先回选题库选一个选题", kind: "topic" };
  }
  const next = viralLibraryOrder[viralLibraryOrder.indexOf(kind) + 1];
  if (next) return { label: `下一步：${labels[next]}`, kind: next };
  return { label: "下一步：去搭建笔记内容", action: "compose" };
}

function renderViralNextStepAction(kind) {
  const step = viralNextStep(kind);
  if (step.hint) return `<span class="shelf-next-step is-hint">${escapeHtml(step.hint)}</span>`;
  if (step.action === "compose") {
    return `<button type="button" class="primary-btn mini-btn shelf-next-step" data-viral-next-action="compose">${escapeHtml(step.label)}</button>`;
  }
  return `<button type="button" class="mini-btn shelf-next-step" data-viral-next-kind="${escapeHtml(step.kind)}">${escapeHtml(step.label)}</button>`;
}

function renderViralWorkflowRoute() {
  const mount = $("#viralWorkflowRoute");
  if (!mount) return;
  const active = state.viralActiveWorkbench || "topic";
  mount.querySelectorAll("[data-viral-route-kind]").forEach((node) => {
    const kind = node.dataset.viralRouteKind;
    node.classList.toggle("active", kind === active);
    node.classList.toggle("done", Boolean(selectedViralLibraryItem(kind)));
  });
  const compose = mount.querySelector("[data-viral-route-action]");
  if (compose) {
    // 「搭建内容」是终点、不是一个库：只标"就绪"，不标 active。
    // 否则站在内页库时会有两个实心红点，看不出当前在第几步。
    compose.classList.toggle("done", Boolean(selectedViralLibraryItem("topic")));
  }
}

function renderViralLibraryNavStatus() {
  Object.keys(viralLibraryGroups).forEach((kind) => {
    const meta = viralLibraryMeta[kind];
    const item = selectedViralLibraryItem(kind);
    document.querySelectorAll(`[data-viral-nav-kind="${kind}"]`).forEach((button) => {
      button.classList.toggle("is-library-selected", Boolean(item));
      const detail = item?.name ? `：${item.name}` : "";
      button.title = item
        ? `已选择${meta.label}${detail}`
        : kind === "topic"
          ? "尚未选择选题（必选）"
          : viralComposeLibraryGuideDraftId
            ? `尚未选择${meta.label}（本次搭建必选）`
            : `尚未选择${meta.label}（选填）`;
    });
  });
}

// Committed markup per mount. Re-rendering identical markup replaced every
// <img> in the cover/inner library, re-decoding the previews and making them
// flicker on each redundant render (library load, template load, atoms load).
const libraryCommittedMarkup = new WeakMap();

function commitLibraryMarkup(mount, html) {
  if (!mount) return false;
  if (libraryCommittedMarkup.get(mount) === html) return false;
  libraryCommittedMarkup.set(mount, html);
  mount.innerHTML = html;
  return true;
}

function renderVisualLibraryAddCard(kind) {
  if (kind !== "cover" && kind !== "inner") return "";
  const label = kind === "cover" ? "封面" : "内页";
  const pending = visualTemplateImportForKind(kind);
  if (pending) {
    return `
      <button type="button" class="library-add-card is-loading is-cancel-action is-pending" data-add-visual-template="${kind}" aria-busy="true" title="停止当前模板上传">
        <span class="template-import-spinner" aria-hidden="true"></span>
        <em class="library-card-pending-badge">${escapeHtml(pending.stage)}</em>
        <strong>${escapeHtml(pending.fileName)}</strong>
        <small class="template-import-file" title="${escapeHtml(pending.fileName)}">正在拆解为${label}模板，完成后自动替换这张卡片 · 点击停止上传</small>
        <span class="pending-line" aria-hidden="true"></span>
      </button>`;
  }
  return `
    <button type="button" class="library-add-card" data-add-visual-template="${kind}">
      <span class="library-add-icon" aria-hidden="true">+</span>
      <strong>新增${label}模板</strong>
      <small>导入单张图片并分析为独立模板</small>
    </button>`;
}

function renderVisualLibraryManageAction(kind) {
  if (kind !== "cover" && kind !== "inner") return "";
  const editing = Boolean(state.visualLibraryEditing[kind]);
  const currentImport = visualTemplateImportForKind(kind);
  if (!editing) {
    return `<div class="library-manage-actions"><button type="button" class="mini-btn library-manage-btn ${currentImport ? "danger-btn is-cancel-action" : ""}" data-add-visual-template="${kind}" ${currentImport ? 'aria-busy="true"' : ""}>${currentImport ? "停止上传" : "新增"}</button><button type="button" class="mini-btn library-manage-btn" data-toggle-visual-library-edit="${kind}" ${currentImport ? "disabled" : ""}>编辑</button></div>`;
  }
  const templateIds = (state.viralLibraries[kind] || []).map((item) => item.templateId).filter(Boolean);
  const selectedIds = state.visualLibraryDeleteSelections[kind] || [];
  const allSelected = templateIds.length > 0 && templateIds.every((templateId) => selectedIds.includes(templateId));
  const importPending = Boolean(state.visualTemplateImport);
  return `
    <div class="visual-library-edit-toolbar" role="toolbar" aria-label="${kind === "cover" ? "封面" : "内页"}模板批量操作">
      <span class="visual-library-selected-count">已选 ${selectedIds.length} 项</span>
      <label class="visual-library-select-all">
        <input type="checkbox" data-toggle-all-visual-templates="${kind}" ${allSelected ? "checked" : ""} ${templateIds.length ? "" : "disabled"} />
        <span>全选</span>
      </label>
      <button type="button" class="mini-btn danger-btn" data-delete-selected-visual-templates="${kind}" ${selectedIds.length ? "" : "disabled"}>删除</button>
      <span class="visual-library-edit-final-actions">
        <button type="button" class="mini-btn ${currentImport ? "danger-btn is-cancel-action" : ""}" data-add-visual-template="${kind}" ${importPending && !currentImport ? "disabled" : ""}>${currentImport ? "停止上传" : "新增模板"}</button>
        <button type="button" class="mini-btn library-edit-done-btn" data-toggle-visual-library-edit="${kind}">完成</button>
      </span>
    </div>`;
}

function renderTextLibraryManageAction(kind) {
  if (kind !== "topic" && kind !== "title") return "";
  const editing = Boolean(state.textLibraryEditing[kind]);
  if (!editing) return `<div class="library-manage-actions"><button type="button" class="mini-btn library-manage-btn" data-add-text-template="${kind}">新增</button><button type="button" class="mini-btn library-manage-btn" data-toggle-text-library-edit="${kind}">编辑</button></div>`;
  return `
    <div class="visual-library-edit-toolbar" role="toolbar" aria-label="${kind === "topic" ? "选题" : "标题"}模板操作">
      <button type="button" class="mini-btn" data-add-text-template="${kind}">新增模板</button>
      <button type="button" class="mini-btn library-edit-done-btn" data-toggle-text-library-edit="${kind}">完成</button>
    </div>`;
}

function renderLibraryManageAction(kind) {
  const manage = kind === "cover" || kind === "inner" ? renderVisualLibraryManageAction(kind) : renderTextLibraryManageAction(kind);
  if (kind !== "topic" || state.textLibraryEditing[kind]) return manage;
  return `<div class="topic-library-source-actions"><button type="button" class="mini-btn local-topic-library-entry" data-open-local-topic-generator><i data-lucide="files"></i><span>从资料提炼选题</span></button></div>${manage}`;
}

function renderTextLibraryAddCard(kind) {
  if (kind !== "topic" && kind !== "title") return "";
  return `
    <button type="button" class="library-add-card text-library-add-card" data-add-text-template="${kind}">
      <span class="library-add-icon" aria-hidden="true">+</span>
      <strong>新增${kind === "topic" ? "选题" : "标题"}模板</strong>
      <small>${kind === "topic" ? "建立可直接生成内容的选题包" : "保存可反复套用的标题公式"}</small>
    </button>`;
}

function toggleTextLibraryEdit(kind) {
  if (kind !== "topic" && kind !== "title") return;
  state.textLibraryEditing[kind] = !state.textLibraryEditing[kind];
  renderViralLibraryShelf(kind);
}

function toggleVisualLibraryEdit(kind) {
  if (kind !== "cover" && kind !== "inner") return;
  state.visualLibraryEditing[kind] = !state.visualLibraryEditing[kind];
  state.visualLibraryDeleteSelections[kind] = [];
  renderViralLibraryShelf(kind);
}

function toggleVisualTemplateDeleteSelection(kind, templateId) {
  if (!state.visualLibraryEditing[kind] || !templateId) return;
  const selected = new Set(state.visualLibraryDeleteSelections[kind] || []);
  if (selected.has(templateId)) selected.delete(templateId);
  else selected.add(templateId);
  state.visualLibraryDeleteSelections[kind] = [...selected];
  renderViralLibraryShelf(kind);
}

function toggleAllVisualTemplateDeleteSelections(kind, checked) {
  if (!state.visualLibraryEditing[kind]) return;
  state.visualLibraryDeleteSelections[kind] = checked
    ? [...new Set((state.viralLibraries[kind] || []).map((item) => item.templateId).filter(Boolean))]
    : [];
  renderViralLibraryShelf(kind);
}

function renderViralLibraryShelf(group) {
  const config = viralLibraryGroups[group];
  const mount = config ? $(`#${config.shelfId}`) : null;
  if (!mount) return;
  const kind = state.viralActiveLibraryKinds[group] || config.kinds[0];
  const meta = viralLibraryMeta[kind];
  const sourceItems = state.viralLibraries[kind] || [];
  const editingVisualLibrary = (kind === "cover" || kind === "inner") && Boolean(state.visualLibraryEditing[kind]);
  // 选题库要能看出"正在从资料提炼"：没有这条，用户在弹层里提交后关掉弹层就完全没线索。
  const pendingReverseTopic = kind === "topic" ? reverseTopicGenerationStage() : "";
  if (editingVisualLibrary) {
    const availableTemplateIds = new Set(sourceItems.map((item) => item.templateId).filter(Boolean));
    state.visualLibraryDeleteSelections[kind] = (state.visualLibraryDeleteSelections[kind] || []).filter((templateId) => availableTemplateIds.has(templateId));
  }
  const isTopicLibrary = kind === "topic";
  if (!sourceItems.length) {
    const isVisual = kind === "cover" || kind === "inner";
    const editingTextLibrary = !isVisual && Boolean(state.textLibraryEditing[kind]);
    const pendingVisualImport = isVisual && Boolean(visualTemplateImportForKind(kind));
    commitLibraryMarkup(mount, `<div class="library-shelf-head ${(editingVisualLibrary || editingTextLibrary) ? "is-editing" : ""}"><strong>${meta.label}</strong><div class="library-shelf-head-actions">${renderLibraryManageAction(kind)}${renderViralNextStepAction(kind)}</div></div>${editingVisualLibrary || pendingVisualImport ? `<div class="library-card-grid visual-template-grid ${editingVisualLibrary ? "is-editing" : ""}">${renderVisualLibraryAddCard(kind)}</div>` : editingTextLibrary ? `<div class="library-card-grid topic-package-grid is-editing">${renderTextLibraryAddCard(kind)}</div>` : pendingReverseTopic ? `<div class="library-card-grid topic-package-grid">${reverseTopicPendingCard(pendingReverseTopic)}</div>` : `<div class="visual-library-empty" role="status"><i data-lucide="layers" aria-hidden="true"></i><strong>暂无${escapeHtml(meta.label)}内容</strong><span>点击右上角“新增”，添加第一个模板。</span></div>`}`);
    refreshIcons();
    return;
  }
  const trackOptions = isTopicLibrary ? topicRuleOptions(sourceItems, topicTrackRules, "track") : [];
  const formatOptions = isTopicLibrary ? topicRuleOptions(sourceItems, topicFormatRules, "format") : [];
  if (state.viralTopicTrack && !trackOptions.some((option) => option.id === state.viralTopicTrack)) state.viralTopicTrack = "";
  if (state.viralTopicFormat && !formatOptions.some((option) => option.id === state.viralTopicFormat)) state.viralTopicFormat = "";
  commitLibraryMarkup(mount, `
    <div class="library-shelf-head ${(editingVisualLibrary || state.textLibraryEditing[kind]) ? "is-editing" : ""}"><strong>${meta.label}</strong><div class="library-shelf-head-actions">${renderLibraryManageAction(kind)}${renderViralNextStepAction(kind)}</div></div>
    ${isTopicLibrary ? `<div class="topic-library-tools">
      <input type="search" data-topic-package-search placeholder="搜索选题、人群、痛点或关键词" value="${escapeHtml(state.viralTopicQuery)}" />
      <select data-topic-package-track aria-label="按内容赛道筛选"><option value="">全部赛道 · ${sourceItems.length}</option>${trackOptions.map((option) => `<option value="${option.id}" ${state.viralTopicTrack === option.id ? "selected" : ""}>${escapeHtml(option.label)} · ${option.count}</option>`).join("")}</select>
      <select data-topic-package-format aria-label="按内容类型筛选"><option value="">全部类型 · ${sourceItems.length}</option>${formatOptions.map((option) => `<option value="${option.id}" ${state.viralTopicFormat === option.id ? "selected" : ""}>${escapeHtml(option.label)} · ${option.count}</option>`).join("")}</select>
      <div class="topic-filter-status"><span data-topic-filter-count></span><button type="button" data-clear-topic-filters class="topic-filter-clear hidden">清除</button></div>
    </div>` : ""}
    ${isTopicLibrary ? `<div class="topic-filter-empty hidden" data-topic-filter-empty><strong>没有匹配的选题</strong><span>换一个赛道、内容类型或搜索词。</span><button type="button" class="ghost-btn" data-clear-topic-filters>清除筛选</button></div>` : ""}
    <div class="library-card-grid ${kind === "topic" || kind === "title" ? "topic-package-grid" : ""} ${isTopicLibrary ? "is-topic-library-grid" : ""} ${kind === "cover" || kind === "inner" ? "visual-template-grid" : ""} ${editingVisualLibrary ? "is-editing" : ""}">
      ${editingVisualLibrary || (kind === "cover" || kind === "inner") && visualTemplateImportForKind(kind) ? renderVisualLibraryAddCard(kind) : ""}
      ${state.textLibraryEditing[kind] ? renderTextLibraryAddCard(kind) : ""}
      ${pendingReverseTopic ? reverseTopicPendingCard(pendingReverseTopic) : ""}
      ${sourceItems.map((item) => kind === "topic" ? topicPackageCard(item) : kind === "title" ? titleLibraryCard(item) : `
        <article class="library-item-card ${kind}-library-card ${kind === "cover" || kind === "inner" ? "visual-template-card" : ""} ${!editingVisualLibrary && state.viralLibrarySelections[meta.field] === item.id ? "selected" : ""} ${editingVisualLibrary ? "is-editing" : ""} ${(state.visualLibraryDeleteSelections[kind] || []).includes(item.templateId) ? "is-batch-selected" : ""}" ${editingVisualLibrary ? `data-toggle-visual-template-selection="${escapeHtml(item.templateId || "")}" role="group"` : `data-select-library-item="${escapeHtml(item.id)}" data-library-kind="${kind}" role="button" tabindex="0" aria-pressed="${state.viralLibrarySelections[meta.field] === item.id ? "true" : "false"}"`}>
          ${editingVisualLibrary ? `<div class="visual-template-edit-controls">
            <label class="visual-template-check"><input type="checkbox" data-visual-template-checkbox="${escapeHtml(item.templateId || "")}" ${(state.visualLibraryDeleteSelections[kind] || []).includes(item.templateId) ? "checked" : ""} /><span>选择</span></label>
            <span class="visual-template-item-actions">
              <button type="button" class="mini-btn visual-template-edit-btn" data-manage-edit-visual-template="${escapeHtml(item.templateId || "")}" data-template-edit-scope="${kind}">编辑</button>
              <button type="button" class="mini-btn danger-btn visual-template-delete-btn" data-manage-delete-visual-template="${escapeHtml(item.templateId || "")}" data-template-delete-scope="${kind}">删除</button>
            </span>
          </div>` : ""}
          <div class="visual-template-card-copy">
          <div class="visual-template-card-head">
            <strong>${escapeHtml(item.name || "未命名")}</strong>
            </div>
            <small>${kind === "cover" || kind === "inner" ? `${visualTemplateImages(item, kind).length || 1} 张图 · ${kind === "cover" ? "封面模板" : "内页模板"}` : escapeHtml(item.summary || "")}</small>
          </div>
          <div class="library-item-visual visual-template-card-preview">
            <em class="visual-template-kind-badge">${kind === "cover" ? "封面模板" : "内页模板"}</em>
            ${visualTemplateLibraryCarousel(item, kind)}
          </div>
          ${!editingVisualLibrary ? `<div class="topic-package-actions"><button type="button" class="primary-btn ${state.viralLibrarySelections[meta.field] === item.id ? "is-selected" : ""}" data-select-library-item="${escapeHtml(item.id)}" data-library-kind="${kind}">${state.viralLibrarySelections[meta.field] === item.id ? `<i data-lucide="check"></i><span>已选择</span>` : kind === "cover" ? "选择封面" : "选择内页"}</button></div>` : ""}
        </article>`).join("")}
    </div>`);
  if (isTopicLibrary) filterVisibleTopicPackageCards();
  refreshIcons();
}

function filterVisibleTopicPackageCards() {
  let visibleCount = 0;
  $$('[data-topic-package-card]').forEach((card) => {
    const item = (state.viralLibraries.topic || []).find((candidate) => candidate.id === card.dataset.topicPackageCard);
    const visible = Boolean(item && topicPackageMatchesFilters(item));
    card.classList.toggle("hidden", !visible);
    if (visible) visibleCount += 1;
  });
  const total = (state.viralLibraries.topic || []).length;
  const count = $("[data-topic-filter-count]");
  if (count) count.textContent = `显示 ${visibleCount} / ${total}`;
  const hasFilters = Boolean(state.viralTopicQuery.trim() || state.viralTopicTrack || state.viralTopicFormat);
  $$('[data-clear-topic-filters]').forEach((button) => button.classList.toggle("hidden", !hasFilters));
  const empty = $("[data-topic-filter-empty]");
  if (empty) empty.classList.toggle("hidden", visibleCount > 0);
}

function clearTopicPackageFilters() {
  state.viralTopicQuery = "";
  state.viralTopicTrack = "";
  state.viralTopicFormat = "";
  const shelf = $("#viralTopicLibraryShelf");
  const search = shelf?.querySelector("[data-topic-package-search]");
  const track = shelf?.querySelector("[data-topic-package-track]");
  const format = shelf?.querySelector("[data-topic-package-format]");
  if (search) search.value = "";
  if (track) track.value = "";
  if (format) format.value = "";
  filterVisibleTopicPackageCards();
}

function localTemplatePageRoles(template, pageCount = 1) {
  const saved = Array.isArray(template?.templatePageRoles) ? template.templatePageRoles.slice(0, pageCount) : [];
  if (saved.length === pageCount && saved.every((role) => role === "cover" || role === "inner")) return saved;
  const templateRole = template?.templateRole || template?.referenceRole || "set";
  if (templateRole === "cover" || templateRole === "inner") return Array(pageCount).fill(templateRole);
  return Array.from({ length: pageCount }, (_, index) => index === 0 ? "cover" : "inner");
}

function localTemplateImagesForRole(template, images, kind) {
  const roles = localTemplatePageRoles(template, Math.max(1, images.length));
  return images.filter((image, index) => image && roles[index] === kind);
}

function localScopedVisualAiImage(template, kind) {
  const source = template?.aiImage || {};
  const allPages = Array.isArray(source.textSchema?.pages) ? source.textSchema.pages : [];
  const allImages = template?.originalImages || [];
  const pageRoles = localTemplatePageRoles(template, Math.max(1, allPages.length, allImages.length));
  const pages = allPages.flatMap((page, index) => pageRoles[index] === kind ? [{ ...page, role: kind }] : []);
  const referenceImages = localTemplateImagesForRole(template, allImages, kind);
  const sourcePageIndexes = allImages
    .map((image, index) => image && pageRoles[index] === kind ? index : -1)
    .filter((index) => index >= 0);
  const instruction = kind === "cover"
    ? "本模板仅用于封面。只生成首张封面，不要生成或描述内页；重点保持封面构图、标题层级和首屏点击信息。"
    : "本模板仅用于内页。只生成正文内容页，不要生成或描述封面。";
  const rolePrompts = source.rolePrompts && typeof source.rolePrompts === "object" ? source.rolePrompts : {};
  const legacyInnerPrompt = String(template?.params?.rolePrompts?.inner || "").trim();
  const rolePrompt = String(rolePrompts[kind] || (kind === "cover" ? source.prompt : legacyInnerPrompt) || "").trim();
  const scopedRolePrompt = visualTemplateRolePrompt(
    { ...source, rolePrompts: rolePrompt ? { [kind]: rolePrompt } : {} },
    kind,
    rolePrompt
  );
  return {
    ...source,
    prompt: `${instruction}\n\n${scopedRolePrompt}`.trim(),
    rolePrompts: scopedRolePrompt ? { [kind]: scopedRolePrompt } : {},
    pageCount: referenceImages.length || pages.length,
    referenceImages,
    sourcePageIndexes,
    textSchema: {
      ...(source.textSchema || {}),
      pages,
      totalFields: pages.reduce((total, page) => total + (page?.fields || []).length, 0),
    },
  };
}

function localScopedViralStructure(template, kind) {
  const source = template?.viralStructure || {};
  return {
    ...source,
    groups: { [kind]: Array.isArray(source.groups?.[kind]) ? source.groups[kind] : [] },
  };
}

function localTemplateHasInnerPart(template) {
  const images = template?.renderedImages || template?.originalImages || [];
  if (localTemplatePageRoles(template, Math.max(1, images.length)).includes("inner")) return true;
  const pages = template?.aiImage?.textSchema?.pages || [];
  return pages.some((page, index) => page?.role === "inner" || Number(page?.index ?? index) > 0);
}

function localFourLibraries(atoms = [], templates = state.layoutTemplates) {
  const activeAtoms = atoms.filter((atom) => atom.status !== "archived");
  const sourceKeys = [...new Set(activeAtoms.flatMap((atom) => atom.sourceNoteIds || []).filter(Boolean))];
  const itemFromAtoms = (kind, grouped, sourceKey = "") => ({
    id: `local-${kind}-${sourceKey || grouped.map((atom) => atom.id).join("-")}`,
    kind,
    name: grouped.map((atom) => atom.name).filter(Boolean).slice(0, 2).join(" · ") || viralLibraryMeta[kind].label,
    summary: grouped.map((atom) => atom.pattern).filter(Boolean).slice(0, 3).join(kind === "inner" ? " → " : "；"),
    preview: { evidence: grouped.flatMap((atom) => atom.evidence || []).slice(0, 3) },
    legacyAtomIds: grouped.map((atom) => atom.id),
    legacyAtomTypes: grouped.map((atom) => atom.type),
    performance: grouped.reduce((summary, atom) => ({ sampleSize: summary.sampleSize + Number(atom.performance?.sampleSize || 0) }), { sampleSize: 0 }),
    status: "active",
    sourceKey,
  });
  const groupedBySource = (kind, types) => sourceKeys.flatMap((sourceKey) => {
    const grouped = types.map((type) => activeAtoms.find((atom) => atom.type === type && (atom.sourceNoteIds || []).includes(sourceKey))).filter(Boolean);
    return grouped.length ? [itemFromAtoms(kind, grouped, sourceKey)] : [];
  });
  const visualAtoms = activeAtoms.filter((atom) => atom.type === "visual_style");
  const innerTypes = ["opening_hook", "body_framework", "content_component", "interaction_ending", "visual_narrative"];
  const innerBlueprints = groupedBySource("inner", innerTypes);
  const coverTemplates = (templates || []).filter((template) => {
    const images = template.renderedImages || template.originalImages || [];
    return localTemplatePageRoles(template, Math.max(1, images.length)).includes("cover");
  }).map((template) => ({
    id: `cover:${template.id}`,
    kind: "cover",
    name: `${template.name || template.sourceTitle || "未命名模板"} · 封面`,
    summary: template.description || `${template.params?.layout?.cover || "封面版式"} · 主色 ${template.params?.style?.primary || "沿用原模板"}`,
    preview: { images: localTemplateImagesForRole(template, template.renderedImages || template.originalImages || [], "cover").slice(0, 6), pageCount: localTemplateImagesForRole(template, template.renderedImages || template.originalImages || [], "cover").length },
    legacyAtomIds: visualAtoms.slice(0, 1).map((atom) => atom.id),
    performance: { sampleSize: 0 },
    templateId: template.id,
    templateSetId: template.id,
    templateRole: template.templateRole || template.referenceRole || "set",
    scope: template.scope || "viral-factory",
    templateType: template.templateType || "visual",
    referenceRole: "cover",
    aiImage: localScopedVisualAiImage(template, "cover"),
    viralStructure: localScopedViralStructure(template, "cover"),
    referenceImages: localTemplateImagesForRole(template, template.originalImages || [], "cover"),
    status: "active",
  }));
  const innerTemplates = (templates || []).filter(localTemplateHasInnerPart).map((template) => {
    const blueprint = innerBlueprints.find((item) => item.sourceKey === template.sourceDraftId) || innerBlueprints[0] || {};
    const images = template.renderedImages || template.originalImages || [];
    const templateRole = template.templateRole || template.referenceRole || "set";
    const innerImages = localTemplateImagesForRole(template, images, "inner");
    return {
      id: `inner:${template.id}`,
      kind: "inner",
      name: `${template.name || template.sourceTitle || "未命名模板"} · 内页`,
      summary: template.description || `${Math.max(1, innerImages.length)} 页内页 · ${template.params?.layout?.page || "沿用成套版式"}`,
      preview: { images: innerImages.length ? innerImages.slice(0, 6) : images.slice(0, 1), pageCount: Math.max(1, innerImages.length) },
      legacyAtomIds: blueprint.legacyAtomIds || [],
      legacyAtomTypes: blueprint.legacyAtomTypes || [],
      performance: blueprint.performance || { sampleSize: 0 },
      templateId: template.id,
      templateSetId: template.id,
      templateRole,
      scope: template.scope || "viral-factory",
      templateType: template.templateType || "visual",
      referenceRole: "inner",
      aiImage: localScopedVisualAiImage(template, "inner"),
      viralStructure: localScopedViralStructure(template, "inner"),
      referenceImages: localTemplateImagesForRole(template, template.originalImages || [], "inner"),
      status: "active",
    };
  });
  return {
    topic: (state.viralTopicPackages || []).filter((item) => item.status !== "archived").map((topicPackage) => ({
      ...topicPackage, kind: "topic", topicPackage, summary: topicPackageSummary(topicPackage),
      preview: { evidence: (topicPackage.contentPillars || []).slice(0, 4) }, legacyAtomIds: topicPackage.mechanismAtomIds || [],
    })),
    title: activeAtoms.filter((atom) => atom.type === "title_formula").map((atom) => itemFromAtoms("title", [atom], atom.sourceNoteIds?.[0] || "")),
    cover: coverTemplates.length ? coverTemplates : visualAtoms.map((atom) => itemFromAtoms("cover", [atom], atom.sourceNoteIds?.[0] || "")),
    inner: innerTemplates.length ? innerTemplates : innerBlueprints,
  };
}

function renderFourLibraryWorkbench() {
  Object.keys(viralLibraryGroups).forEach((kind) => {
    renderViralLibraryShelf(kind);
  });
  renderViralWorkflowRoute();
  renderViralLibraryNavStatus();
}

function bestLibraryItem(kind, preferredAtomIds = []) {
  const items = state.viralLibraries[kind] || [];
  if (!items.length) return null;
  const preferred = new Set(preferredAtomIds);
  const cover = selectedViralLibraryItem("cover");
  return [...items].sort((a, b) => {
    const score = (item) => {
      const overlap = (item.legacyAtomIds || []).filter((id) => preferred.has(id)).length;
      const sameSet = kind === "inner" && cover?.templateSetId && cover.templateSetId === item.templateSetId ? 1 : 0;
      return overlap * 1000 + sameSet * 500 + Number(item.performance?.sampleSize || 0);
    };
    return score(b) - score(a);
  })[0];
}

function recommendViralLibraries(preferredAtomIds = Object.values(state.viralSelections || {})) {
  ["topic", "title", "cover", "inner"].forEach((kind) => {
    const item = bestLibraryItem(kind, preferredAtomIds);
    state.viralLibrarySelections[viralLibraryMeta[kind].field] = item?.id || "";
  });
  const cover = selectedViralLibraryItem("cover");
  const inner = selectedViralLibraryItem("inner");
  syncTopicPackageToBrief(topicPackageFromItem(selectedViralLibraryItem("topic")));
  state.viralLibrarySelections.templateSetId = cover?.templateSetId === inner?.templateSetId ? cover?.templateSetId || "" : "custom";
  syncViralSelectionsToCurrentDraft();
  renderFourLibraryWorkbench();
}

function syncTopicPackageToBrief(topicPackage) {
  if (!topicPackage) return;
  const values = {
    viralTopic: topicPackage.topic || "",
    viralAudience: topicPackage.audience || "",
    viralScenario: topicPackage.scenario || "",
    viralPain: topicPackage.pain || "",
    viralBenefit: topicPackage.benefit || "",
    viralCategory: topicPackage.category || "",
    viralFacts: (topicPackage.verifiedFacts || []).join("\n"),
    viralForbidden: (topicPackage.forbidden || []).join("\n"),
  };
  Object.entries(values).forEach(([id, value]) => {
    const field = $(`#${id}`);
    if (field) field.value = value;
  });
}

function topicPackageLines(value) {
  return String(value || "").split(/\n+/).map((item) => item.trim()).filter(Boolean);
}

function populateTopicRuleSelect(selector, rules, value) {
  const select = $(selector);
  if (!select) return;
  const selectedRule = topicRuleByValue(rules, value);
  select.innerHTML = `<option value="">自动判断</option>${rules.map((rule) => `<option value="${rule.id}">${escapeHtml(rule.label)}</option>`).join("")}`;
  select.value = selectedRule?.id || "";
}

function openTopicPackageEditor(item, options = {}) {
  const topicPackage = topicPackageFromItem(item) || {};
  const values = {
    topicPackageId: topicPackage.id || "", topicPackageName: topicPackage.name || "",
    topicPackageCategory: topicPackage.category || "", topicPackageTopic: topicPackage.topic || "",
    topicPackageAudience: topicPackage.audience || "", topicPackageScenario: topicPackage.scenario || "",
    topicPackagePain: topicPackage.pain || "", topicPackageBenefit: topicPackage.benefit || "",
    topicPackageCoreClaim: topicPackage.coreClaim || "", topicPackagePillars: (topicPackage.contentPillars || []).join("\n"),
    topicPackageRequirements: (topicPackage.factRequirements || []).join("\n"),
    topicPackageVerifiedFacts: (topicPackage.verifiedFacts || []).join("\n"),
    topicPackageHooks: (topicPackage.coverHooks || []).join("\n"), topicPackageKeywords: (topicPackage.keywords || []).join("\n"),
    topicPackageForbidden: (topicPackage.forbidden || []).join("\n"),
  };
  Object.entries(values).forEach(([id, value]) => { if ($(`#${id}`)) $(`#${id}`).value = value; });
  populateTopicRuleSelect("#topicPackageTrack", topicTrackRules, topicPackage.contentTrack);
  populateTopicRuleSelect("#topicPackageFormat", topicFormatRules, topicPackage.contentFormat);
  const title = $("#topicPackageEditorTitle");
  if (title) title.textContent = topicPackage.id ? "编辑选题" : "新增选题";
  const eyebrow = $("#topicPackageEditorEyebrow");
  if (eyebrow) eyebrow.textContent = topicPackage.id ? "选题库 · 完整编辑" : "选题库 · 手动新建";
  const expandAll = Boolean(options.expand);
  const advanced = $("#topicPackageAdvanced");
  if (advanced) advanced.open = Boolean(topicPackage.id) || expandAll;
  // 新建时"补充信息"保持折叠：默认只要求一个必填字段，减轻填写压力。
  const optional = $("#topicPackageOptional");
  if (optional) optional.open = Boolean(topicPackage.id) || expandAll;
  const saveButton = $("#saveTopicPackageBtn");
  if (saveButton) saveButton.textContent = topicPackage.id ? "保存修改" : "创建选题";
  showModal($("#topicPackageModal"));
  window.setTimeout(() => $("#topicPackageTopic")?.focus(), 0);
}

function closeTopicPackageEditor() {
  hideModal($("#topicPackageModal"));
}

function topicPackageSummary(topicPackage) {
  const audience = String(topicPackage?.audience || "").trim();
  const value = String(topicPackage?.benefit || topicPackage?.coreClaim || "").trim();
  const summary = [audience, value].filter(Boolean).join(" · ");
  return summary || String(topicPackage?.topic || topicPackage?.name || "待补充").trim();
}

const topicPackagePreviewFields = {
  topicPackageAudience: (preview) => preview.audience || "",
  topicPackageBenefit: (preview) => preview.benefit || "",
  topicPackageScenario: (preview) => preview.scenario || "",
  topicPackagePain: (preview) => preview.pain || "",
  topicPackageCoreClaim: (preview) => preview.coreClaim || "",
  topicPackagePillars: (preview) => (preview.contentPillars || []).join("\n"),
  topicPackageRequirements: (preview) => (preview.factRequirements || []).join("\n"),
  topicPackageHooks: (preview) => (preview.coverHooks || []).join("\n"),
  topicPackageKeywords: (preview) => (preview.keywords || []).join("\n"),
  topicPackageForbidden: (preview) => (preview.forbidden || []).join("\n"),
};

function applyTopicPackagePreview(preview) {
  Object.entries(topicPackagePreviewFields).forEach(([id, read]) => {
    const field = $("#" + id);
    if (!field) return;
    const value = read(preview);
    // 只回填非空字段，不覆盖用户已经写下的内容。
    if (value) field.value = value;
  });
  // 自动完善后展开"补充信息"，让用户看到补了什么、能直接改。
  const optional = $("#topicPackageOptional");
  if (optional) optional.open = true;
}

async function autoCompleteTopicPackage() {
  const button = $("#autoCompleteTopicPackageBtn");
  const topicPackage = collectTopicPackageEditor();
  if (!topicPackage.topic) {
    toast("先写下想做什么选题，再点自动完善", { kind: "error" });
    $("#topicPackageTopic")?.focus();
    return;
  }
  if (button) {
    button.disabled = true;
    button.classList.add("is-loading");
  }
  try {
    const data = await postBackend("/api/viral/topic-packages/save", {
      topicPackage: { ...topicPackage, preview: true },
    });
    // 旧后端会把 preview 当普通保存：既不补全，还会偷偷存一条空选题。
    // 用 previewOnly 标记区分，宁可明确报错也不要"点了没反应"。
    if (data.previewOnly !== true) {
      throw new Error("后端未返回自动完善结果，请重启后端后再试");
    }
    applyTopicPackagePreview(topicPackageFromItem(data.topicPackage) || {});
    toast("已按主题补全，可以直接改或创建");
  } catch (error) {
    toast("自动完善失败：" + error.message, { kind: "error" });
  } finally {
    if (button) {
      button.disabled = false;
      button.classList.remove("is-loading");
    }
  }
}

function fillTopicExample(value) {
  const topic = String(value || "").trim();
  if (!topic) return;
  const field = $("#topicPackageTopic");
  if (field) {
    field.value = topic;
    field.focus();
  }
}

function collectTopicPackageEditor() {
  const id = $("#topicPackageId").value.trim();
  const topic = $("#topicPackageTopic").value.trim();
  const direction = $("#topicPackageBenefit").value.trim();
  return {
    id, name: $("#topicPackageName").value.trim(),
    category: $("#topicPackageCategory").value.trim(), topic,
    contentTrack: $("#topicPackageTrack").value, contentFormat: $("#topicPackageFormat").value,
    audience: $("#topicPackageAudience").value.trim(), scenario: $("#topicPackageScenario").value.trim(),
    pain: $("#topicPackagePain").value.trim(), benefit: $("#topicPackageBenefit").value.trim(),
    coreClaim: $("#topicPackageCoreClaim").value.trim(), contentPillars: topicPackageLines($("#topicPackagePillars").value),
    factRequirements: topicPackageLines($("#topicPackageRequirements").value), verifiedFacts: topicPackageLines($("#topicPackageVerifiedFacts").value),
    coverHooks: topicPackageLines($("#topicPackageHooks").value), keywords: topicPackageLines($("#topicPackageKeywords").value),
    forbidden: topicPackageLines($("#topicPackageForbidden").value),
    ...(!id ? { creationMode: "guided", manualDirection: direction } : {}),
  };
}

function applyTopicPackageLibraryResponse(data) {
  if (data.libraries) state.viralLibraries = normalizedViralLibraries(data.libraries);
  state.viralTopicPackages = (state.viralLibraries.topic || []).map(topicPackageFromItem).filter((item) => item?.id);
  renderFourLibraryWorkbench();
}

function topicPackageOptimisticItem(topicPackage) {
  const tempId = "pending-topic-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const optimistic = {
    ...topicPackage,
    id: tempId,
    name: topicPackage.name || topicPackage.topic || "未命名选题",
    status: "active",
  };
  return {
    ...optimistic,
    kind: "topic",
    summary: topicPackageSummary(optimistic),
    preview: { evidence: [] },
    legacyAtomIds: [],
    topicPackage: optimistic,
  };
}

async function saveTopicPackageEditor(event) {
  event?.preventDefault();
  const topicPackage = collectTopicPackageEditor();
  const editing = Boolean(topicPackage.id);
  const endpoint = editing ? "/api/viral/topic-packages/update" : "/api/viral/topic-packages/save";
  const payload = editing ? { id: topicPackage.id, changes: topicPackage } : { topicPackage };
  const submitButton = $("#saveTopicPackageBtn");
  let optimisticId = "";
  if (!editing) {
    // 保存要跑字段补齐 + 取整库快照，可能几秒；先落一张占位卡再关弹层，
    // 让用户在列表里立刻看到"正在创建"，而不是对着没变化的界面干等。
    const optimistic = topicPackageOptimisticItem(topicPackage);
    optimisticId = optimistic.id;
    pendingTopicPackageSaves.add(optimisticId);
    state.viralLibraries.topic = [optimistic, ...(state.viralLibraries.topic || [])];
    state.viralTopicPackages = state.viralLibraries.topic.map(topicPackageFromItem).filter((item) => item?.id);
    closeTopicPackageEditor();
    renderFourLibraryWorkbench();
  } else if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "保存中…";
  }
  try {
    const data = await postBackend(endpoint, payload);
    if (optimisticId) pendingTopicPackageSaves.delete(optimisticId);
    if (data.topicPackage?.id) archivedTopicPackageIds.delete(String(data.topicPackage.id));
    applyTopicPackageLibraryResponse(data);
    closeTopicPackageEditor();
    toast("选题包已保存");
  } catch (error) {
    if (optimisticId) {
      pendingTopicPackageSaves.delete(optimisticId);
      state.viralLibraries.topic = (state.viralLibraries.topic || []).filter((item) => item.id !== optimisticId);
      state.viralTopicPackages = state.viralLibraries.topic.map(topicPackageFromItem).filter((item) => item?.id);
      renderFourLibraryWorkbench();
      // 失败不该让用户重填：把已填内容放回表单。
      openTopicPackageEditor({ topicPackage }, { expand: true });
    }
    toast("保存选题包失败：" + error.message, { kind: "error" });
  } finally {
    if (editing && submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "保存修改";
    }
  }
}

function titleTemplateAtom(item) {
  const atomId = item?.legacyAtomIds?.[0] || "";
  return state.viralAtoms.find((atom) => atom.id === atomId) || null;
}

function applyTitleFormulaSuggestion(data) {
  const pattern = String(data?.pattern || "").trim();
  if (!pattern) return false;
  const patternField = $("#titleTemplatePattern");
  if (patternField) patternField.value = pattern;
  // 示例和限制只在留空时补：不覆盖用户已经写下的内容。
  const exampleField = $("#titleTemplateExample");
  const examples = (data?.exampleTitles || []).filter(Boolean);
  if (exampleField && !exampleField.value.trim() && examples.length) exampleField.value = examples.join("\n");
  const rulesField = $("#titleTemplateRisks");
  const rules = (data?.riskRules || []).filter(Boolean);
  if (rulesField && !rulesField.value.trim() && rules.length) rulesField.value = rules.join("\n");
  return true;
}

function setTitleFormulaBusy(busy) {
  const button = $("#autoTitleFormulaBtn");
  if (button) {
    button.disabled = busy;
    button.classList.toggle("is-loading", busy);
    const label = button.querySelector("span");
    if (label) label.textContent = busy ? "生成中…" : "自动生成";
  }
  // 独立的状态行：按钮上的转圈容易被忽略，这里给一句明确的进度说明。
  const status = $("#titleFormulaStatus");
  if (status) status.textContent = busy ? "AI 正在生成标题公式，请稍候…" : "";
}

async function autoGenerateTitleFormula() {
  const name = $("#titleTemplateName").value.trim();
  if (!name) {
    toast("先写下模板名称，再生成标题公式", { kind: "error" });
    $("#titleTemplateName")?.focus();
    return;
  }
  setTitleFormulaBusy(true);
  try {
    const data = await postBackend("/api/viral/title-formulas/generate", { name });
    if (!applyTitleFormulaSuggestion(data)) throw new Error("后端没有返回标题公式");
    // 三种结果分开说：AI 成功、AI 没能抽象出槽位（退回规则模板）、AI 不可用。
    if (data.source === "ai") {
      toast("已生成标题公式");
    } else if (data.fallbackReason === "ai-slotless") {
      toast("AI 没能把标题抽象出槽位，已换成规则模板，建议手动改一版");
    } else {
      toast("AI 不可用，已按规则生成标题公式");
    }
  } catch (error) {
    toast("生成标题公式失败：" + error.message, { kind: "error" });
  } finally {
    setTitleFormulaBusy(false);
  }
}

function openTitleTemplateEditor(item = null) {
  const atom = titleTemplateAtom(item) || {};
  $("#titleTemplateAtomId").value = atom.id || "";
  $("#titleTemplateName").value = atom.name || item?.name || "";
  $("#titleTemplatePattern").value = atom.pattern || item?.summary || "";
  $("#titleTemplateExample").value = (atom.evidence || item?.preview?.evidence || []).join("\n");
  $("#titleTemplateRisks").value = (atom.riskRules || []).join("\n");
  $("#titleTemplateEditorTitle").textContent = atom.id ? "编辑标题模板" : "新增标题模板";
  const eyebrow = $("#titleTemplateEditorEyebrow");
  if (eyebrow) eyebrow.textContent = atom.id ? "标题库 · 完整编辑" : "标题库 · 手动新建";
  const optional = $("#titleTemplateOptional");
  if (optional) optional.open = Boolean(atom.id);
  showModal($("#titleTemplateModal"));
}

function closeTitleTemplateEditor() {
  hideModal($("#titleTemplateModal"));
}

function restoreTitleTemplateDraft(atom) {
  openTitleTemplateEditor(null);
  $("#titleTemplateName").value = atom.name || "";
  $("#titleTemplatePattern").value = atom.pattern || "";
  $("#titleTemplateExample").value = (atom.evidence || []).join("\n");
  $("#titleTemplateRisks").value = (atom.riskRules || []).join("\n");
}

async function saveTitleTemplateEditor(event) {
  event?.preventDefault();
  const id = $("#titleTemplateAtomId").value.trim();
  const atom = {
    type: "title_formula",
    name: $("#titleTemplateName").value.trim(),
    pattern: $("#titleTemplatePattern").value.trim(),
    evidence: topicPackageLines($("#titleTemplateExample").value),
    riskRules: topicPackageLines($("#titleTemplateRisks").value),
    intent: ["点击"],
    applicableCategories: ["通用"],
  };
  let optimisticId = "";
  if (!id) {
    // 与选题库一致：先落一张占位卡再关弹层，别让新卡片凭空出现。
    optimisticId = "pending-title-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const optimistic = {
      id: optimisticId,
      atomId: optimisticId,
      kind: "title",
      legacyAtomIds: [optimisticId],
      legacyAtomTypes: ["title_formula"],
      name: atom.name || "未命名标题结构",
      pattern: atom.pattern,
      summary: atom.pattern,
      preview: { evidence: atom.evidence || [] },
      intent: atom.intent,
      riskRules: atom.riskRules,
      status: "active",
      performance: { sampleSize: 0 },
    };
    pendingTitleTemplateSaves.add(optimisticId);
    state.viralLibraries.title = [optimistic, ...(state.viralLibraries.title || [])];
    closeTitleTemplateEditor();
    renderFourLibraryWorkbench();
  }
  try {
    const data = id
      ? await postBackend("/api/viral/atoms/update", { atomId: id, changes: atom })
      : await postBackend("/api/viral/atoms/save", { atom });
    if (optimisticId) pendingTitleTemplateSaves.delete(optimisticId);
    if (data.atom?.id) archivedTitleAtomIds.delete(String(data.atom.id));
    if (data.libraries) state.viralLibraries = normalizedViralLibraries(data.libraries);
    await loadViralAtoms();
    renderFourLibraryWorkbench();
    closeTitleTemplateEditor();
    toast("标题模板已保存");
  } catch (error) {
    if (optimisticId) {
      pendingTitleTemplateSaves.delete(optimisticId);
      state.viralLibraries.title = (state.viralLibraries.title || []).filter((item) => item.id !== optimisticId);
      renderFourLibraryWorkbench();
      // 失败不该让用户重填：把已填内容放回表单。
      restoreTitleTemplateDraft(atom);
    }
    toast(`保存标题模板失败：${error.message}`, { kind: "error" });
  }
}

async function archiveTitleTemplate(itemId) {
  const item = (state.viralLibraries.title || []).find((candidate) => candidate.id === itemId);
  const atomId = titleTemplateAtomIdentity(item);
  if (!item || !atomId || pendingTitleTemplateArchives.has(atomId)) return;
  const itemName = item.name || "未命名标题结构";
  pendingTitleTemplateArchives.add(atomId);
  renderViralLibraryShelf("title");
  try {
    const data = await postBackend("/api/viral/atoms/update", { atomId, changes: { status: "archived" } });
    archivedTitleAtomIds.add(atomId);
    state.viralAtoms = activeViralAtoms(state.viralAtoms);
    if (data.libraries) state.viralLibraries = normalizedViralLibraries(data.libraries);
    if (state.viralLibrarySelections.titleId === itemId) state.viralLibrarySelections.titleId = "";
    renderFourLibraryWorkbench();
    await loadViralAtoms();
    renderFourLibraryWorkbench();
    toast(`已删除标题模板「${itemName}」`);
  } catch (error) {
    pendingTitleTemplateArchives.delete(atomId);
    renderViralLibraryShelf("title");
    toast(`删除标题模板失败：${error.message}`, { kind: "error" });
  } finally {
    pendingTitleTemplateArchives.delete(atomId);
  }
}

function imageSizeToRatio(size) {
  if (size === "1024x1024") return "1:1";
  if (size === "1536x1024") return "4:3";
  return "3:4";
}

function imageRatioToSize(ratio) {
  if (ratio === "1:1") return "1024x1024";
  if (ratio === "4:3") return "1536x1024";
  return "1024x1536";
}

const INNER_VISUAL_SYSTEM_RULE = "内页默认继承原模板的背景材质、配色、字体气质、装饰语言、保护区域与安全画布；用户视觉调整明确要求修改颜色、字体、背景、材质或装饰时，以用户调整为准。新文字依据当前知识内容在安全画布内自由布局，按内容量自然安排层级、栏数和密度。旧文字坐标、行数、字数、槽位和原内容密度不得作为新页面约束，也不得套用封面构图。";

function removeLegacyInnerPromptRules(value) {
  return String(value || "")
    .replace(/内页共用编辑原则：必须以当前输入的内页原图作为本页编辑底稿，保留[^。]*只替换[^。]*。?/g, "")
    .replace(/禁止把某一页独有的标题位置[^。]*。?/g, "")
    .replace(/禁止采用封面的大标题版式。?/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function visualTemplateRolePrompt(aiImage, role, fallback = "") {
  const rolePrompts = aiImage?.rolePrompts && typeof aiImage.rolePrompts === "object" ? aiImage.rolePrompts : {};
  if (role === "inner") {
    const supplemental = removeLegacyInnerPromptRules(rolePrompts.inner || fallback);
    return [INNER_VISUAL_SYSTEM_RULE, supplemental && supplemental !== INNER_VISUAL_SYSTEM_RULE ? supplemental : ""]
      .filter(Boolean)
      .join("\n\n")
      .trim();
  }
  return String(rolePrompts?.[role] || fallback || aiImage?.prompt || "").trim();
}

function visualLibraryItemForImageEditor(item, kind) {
  if (!item || !["cover", "inner"].includes(kind)) return null;
  return {
    ...item,
    mode: kind,
    name: String(item.name || "").replace(/\s*·\s*(封面|内页)$/, ""),
    desc: item.summary || "",
    prompt: visualTemplateRolePrompt(item.aiImage, kind),
    ratio: imageSizeToRatio(item.aiImage?.size),
  };
}

function openImageTemplateEditor(item = null) {
  const isBackendTemplate = ["cover", "inner"].includes(item?.mode) && item?.templateId;
  const backendTemplate = isBackendTemplate ? state.layoutTemplates.find((template) => template.id === item.templateId) : null;
  $("#imageTemplateEditorId").value = item?.id || "";
  $("#imageTemplateEditorMode").value = item?.mode || "scene";
  $("#imageTemplateEditorBackendId").value = item?.templateId || "";
  $("#imageTemplateEditorName").value = backendTemplate?.name || item?.name || "";
  $("#imageTemplateEditorDesc").value = backendTemplate?.description || item?.desc || "";
  $("#imageTemplateEditorRatio").value = item?.ratio || imageSizeToRatio(backendTemplate?.aiImage?.size);
  const editorRole = item?.mode || "scene";
  const promptValue = ["cover", "inner"].includes(editorRole)
    ? visualTemplateRolePrompt(backendTemplate?.aiImage, editorRole, item?.prompt)
    : item?.prompt || "";
  $("#imageTemplateEditorPrompt").value = promptValue;
  $("#imageTemplateEditorPromptLabel").textContent = ["cover", "inner"].includes(editorRole)
    ? `${editorRole === "inner" ? "内页" : "封面"}公共视觉规则`
    : "公共生图规则";
  $("#imageTemplateEditorTitle").textContent = item ? "编辑图片模板" : "新增场景模板";
  showModal($("#imageTemplateModal"));
}

function closeImageTemplateEditor() {
  hideModal($("#imageTemplateModal"));
}

async function saveImageTemplateEditor(event) {
  event?.preventDefault();
  const id = $("#imageTemplateEditorId").value.trim();
  const mode = $("#imageTemplateEditorMode").value || "scene";
  const backendId = $("#imageTemplateEditorBackendId").value.trim();
  const values = {
    name: $("#imageTemplateEditorName").value.trim(),
    desc: $("#imageTemplateEditorDesc").value.trim(),
    ratio: $("#imageTemplateEditorRatio").value,
    prompt: $("#imageTemplateEditorPrompt").value.trim(),
  };
  try {
    if (["cover", "inner"].includes(mode) && backendId) {
      const data = await postBackend("/api/templates/update", {
        templateId: backendId,
        changes: {
          name: values.name,
          description: values.desc,
          aiImageRole: mode,
          aiImage: {
            rolePrompts: { [mode]: values.prompt },
            size: imageRatioToSize(values.ratio),
            ...(mode === "inner" ? { pageVisualPromptsEnabled: false } : {}),
          },
        },
      });
      state.layoutTemplates = data.templates || state.layoutTemplates;
      await loadViralLibraries();
    } else {
      const baseItem = sceneImageTemplates.find((item) => item.id === id);
      if (baseItem) {
        state.imageTemplateOverrides[id] = values;
      } else if (id) {
        state.customSceneImageTemplates = state.customSceneImageTemplates.map((item) => item.id === id ? { ...item, ...values } : item);
      } else {
        state.customSceneImageTemplates.push({
          id: uid("scene-template"),
          mode: "scene",
          preview: "",
          referenceImages: [],
          ...values,
        });
      }
      saveState();
    }
    renderStyleGrid();
    renderImageStudioSelection();
    closeImageTemplateEditor();
    toast("图片模板已保存");
  } catch (error) {
    toast(`保存图片模板失败：${error.message}`, { kind: "error" });
  }
}

async function copyTopicPackage(itemId) {
  const item = (state.viralLibraries.topic || []).find((candidate) => candidate.id === itemId);
  const source = topicPackageFromItem(item);
  if (!source) return;
  try {
    const copy = { ...source, id: "", name: `${source.name || source.topic} · 副本`, performance: { sampleSize: 0, avgEngagementRate: 0, avgCollectRate: 0 } };
    const data = await postBackend("/api/viral/topic-packages/save", { topicPackage: copy });
    applyTopicPackageLibraryResponse(data);
    toast("已复制为新选题");
  } catch (error) {
    toast(`复制选题失败：${error.message}`);
  }
}

async function archiveTopicPackage(itemId) {
  const item = (state.viralLibraries.topic || []).find((candidate) => candidate.id === itemId);
  const packageId = topicPackageIdentity(item);
  if (!item || !packageId || pendingTopicPackageArchives.has(packageId)) return;
  const itemName = topicPackageFromItem(item)?.name || topicPackageFromItem(item)?.topic || "未命名选题";
  pendingTopicPackageArchives.add(packageId);
  renderViralLibraryShelf("topic");
  try {
    const data = await postBackend("/api/viral/topic-packages/archive", { id: packageId });
    archivedTopicPackageIds.add(packageId);
    if (state.viralLibrarySelections.topicId === item.id) state.viralLibrarySelections.topicId = "";
    applyTopicPackageLibraryResponse(data);
    toast(`已删除选题模板「${itemName}」`);
  } catch (error) {
    pendingTopicPackageArchives.delete(packageId);
    renderViralLibraryShelf("topic");
    toast(`删除选题模板失败：${error.message}`, { kind: "error" });
  } finally {
    pendingTopicPackageArchives.delete(packageId);
  }
}

// 拆解/入库进行中不能换四库：选库（以及清空选择）都会走 syncViralSelectionsToCurrentDraft，
// 把草稿的标题/正文/标签按选题包重写、并把执行计划与配方编号清空（见 applyViralSelectionPreviewToDraft）。
// 拆解流程此刻正拿着同一份草稿在写，两边一起写的结果就是用户看到的"草稿内容被覆盖"。
// 查看四库不受影响，只是这一下不生效——要用就等它结束，或先点「中止拆解」。
function viralLibraryWriteBlockedByImport() {
  if (typeof isTemplateImportRunningForDraft !== "function") return false;
  if (!isTemplateImportRunningForDraft(currentDraft())) return false;
  toast("模板拆解进行中，暂不能更换四库模板；请等它结束或先点「中止拆解」", { kind: "warning", duration: 6000 });
  return true;
}

async function selectViralLibraryItem(kind, itemId) {
  if (viralLibraryWriteBlockedByImport()) return;
  const meta = viralLibraryMeta[kind];
  let item = (state.viralLibraries[kind] || []).find((candidate) => candidate.id === itemId);
  if (!meta || !item) return;
  if (state.viralLibrarySelections[meta.field] === itemId) {
    clearViralLibraryItem(kind);
    return;
  }
  if (["cover", "inner"].includes(kind) && item.templateContract?.requiresEnrichment === true) {
    try {
      toast("首次使用旧模板，正在补全新版视觉合同");
      const data = await postBackend("/api/templates/enrich-contract", { templateId: item.templateId });
      if (data.libraries) state.viralLibraries = normalizedViralLibraries(data.libraries);
      else await loadViralLibraries({ recommend: false });
      item = (state.viralLibraries[kind] || []).find((candidate) => candidate.id === itemId);
      if (!item || item.templateContract?.requiresEnrichment === true) throw new Error("模板视觉合同补全后仍未就绪");
    } catch (error) {
      toast(`模板视觉合同补全失败：${error.message}`, { kind: "error", duration: 9000 });
      return;
    }
  }
  let createdDraft = false;
  if (kind === "topic" && !currentDraft()) {
    const topicPackage = topicPackageFromItem(item);
    createDraft({
      source: "viral",
      seed: topicPackage.topic || topicPackage.name || "选题组合",
      images: [],
    });
    createdDraft = true;
  }
  state.viralLibrarySelections[meta.field] = itemId;
  if (kind === "topic") {
    const item = selectedViralLibraryItem("topic");
    syncTopicPackageToBrief(topicPackageFromItem(item));
    state.viralLastGeneration = null;
  }
  const cover = selectedViralLibraryItem("cover");
  const inner = selectedViralLibraryItem("inner");
  state.viralLibrarySelections.templateSetId = cover?.templateSetId === inner?.templateSetId ? cover?.templateSetId || "" : "custom";
  syncViralSelectionsToCurrentDraft();
  renderFourLibraryWorkbench();
  const continuedGuide = continueViralComposeLibraryGuide();
  if (createdDraft) {
    if (!continuedGuide) {
      setSection("rewrite");
      toast("已根据选题创建 AI 改写任务");
    }
  } else if (!continuedGuide) {
    toast(`已选择「${item.name || meta.label}」`);
  }
}

function clearViralLibraryItem(kind) {
  if (viralLibraryWriteBlockedByImport()) return;
  cancelViralComposeLibraryGuide();
  const meta = viralLibraryMeta[kind];
  if (!meta) return;
  state.viralLibrarySelections[meta.field] = "";
  if (kind === "topic") {
    state.viralLastGeneration = null;
  }
  // P93：取消选择过去只清了 state，草稿上仍留着 coverTemplateId/innerTemplateId 与
  // templateReferences。随后 applyViralSelectionPreviewToDraft 会用「当前选中 || 从草稿复原」
  // 把它们读回来再写一遍，下次载入库又按 coverTemplateId 恢复选择——于是"取消不生效"。
  // 显式取消就必须把草稿侧的落点一起清掉。
  if (kind === "cover" || kind === "inner") {
    const draft = currentDraft();
    if (draft) {
      draft[`${kind}TemplateId`] = "";
      if (draft.templateReferences?.[kind]) draft.templateReferences[kind] = { templateId: "", images: [], aiImage: {} };
    }
  }
  const cover = selectedViralLibraryItem("cover");
  const inner = selectedViralLibraryItem("inner");
  state.viralLibrarySelections.templateSetId = cover?.templateSetId === inner?.templateSetId ? cover?.templateSetId || "" : "custom";
  syncViralSelectionsToCurrentDraft();
  renderFourLibraryWorkbench();
}


// ----- js/viral/factory.js -----
function renderViralFactory() {
  renderFourLibraryWorkbench();
  renderViralAtomLibrary();
  setViralWorkbench(state.viralActiveWorkbench || "topic");
}

async function loadViralAtoms() {
  try {
    const data = await getBackend("/api/viral/atoms");
    state.viralAtoms = activeViralAtoms(data.atoms || []);
    state.viralAtomTypes = data.types || state.viralAtomTypes;
    renderViralAtomLibrary();
  } catch {
    state.viralAtoms = [];
    renderViralAtomLibrary();
  }
}

// 兜底渲染收成一个入口：接口失败、游客跳过两条路径共用，免得两处各抄一遍慢慢漂开。
function renderLocalViralLibrariesFallback({ recommend = false, preferredAtomIds = [] } = {}) {
  state.viralLibraries = localFourLibraries(state.viralAtoms || []);
  restoreViralSelectionsFromDraft(currentDraft());
  if (recommend && Object.values(state.viralLibraries).some((items) => items.length)) {
    recommendViralLibraries(preferredAtomIds);
  } else {
    renderFourLibraryWorkbench();
  }
  renderStyleGrid();
  renderImageStudioSelection();
  if (state.innerPagePicker) renderInnerPagePicker();
}

async function loadViralLibraries({ recommend = false, preferredAtomIds = [] } = {}) {
  // 未登录的游客不该发这条读请求：OIDC 下 /api/viral/libraries 必鉴权，发出去只会拿到 401/403，
  // 界面再把「没登录」当成「四库加载失败，请稍后重试或刷新页面」——用户既登不进去，也刷不出数据。
  // 本地兜底库本来就是游客能看到的全部内容；这与开机那几个被动读走同一道闸门（auth/login-gate.js）。
  if (typeof whenLoginGateReady === "function") {
    const loginGate = await whenLoginGateReady();
    if (loginGate?.active && loginGate.known && !loginGate.signedIn) {
      renderLocalViralLibrariesFallback({ recommend, preferredAtomIds });
      return;
    }
  }
  try {
    const data = await getBackend("/api/viral/libraries");
    if (!data.libraries || !["topic", "title", "cover", "inner"].some((kind) => Array.isArray(data.libraries[kind]))) {
      throw new Error("当前后端未返回四库数据");
    }
    state.viralLibraries = normalizedViralLibraries(data.libraries || {});
    state.viralTopicPackages = (state.viralLibraries.topic || []).map(topicPackageFromItem).filter((item) => item?.id);
    restoreViralSelectionsFromDraft(currentDraft());
    const selectionsValid = Object.entries(viralLibraryMeta).every(([kind, meta]) => {
      const selected = state.viralLibrarySelections[meta.field];
      return !selected || (state.viralLibraries[kind] || []).some((item) => item.id === selected);
    });
    if (recommend) {
      recommendViralLibraries(preferredAtomIds);
    } else {
      if (!selectionsValid) {
        state.viralLibrarySelections = { topicId: "", titleId: "", coverId: "", innerId: "", templateSetId: "" };
      }
      renderFourLibraryWorkbench();
    }
    renderStyleGrid();
    renderImageStudioSelection();
    if (state.innerPagePicker) renderInnerPagePicker();
  } catch (error) {
    renderLocalViralLibrariesFallback({ recommend, preferredAtomIds });
    const status = Number(error?.status);
    // 401/403 = 没登录 / 凭据没有租户作用域：这是登录状态，不是四库故障（auth/login-gate.js）。
    // 页面自己发起的被动读失败本就该安静，别让游客照着「刷新页面」刷一个永远不会成功的请求。
    if (status === 401 || status === 403) return;
    // 接口失败时原来只静默回退到本地兜底库：用户看到的是"四库里的东西凭空少了/没了"，
    // 完全不知道是加载失败，也无从判断该刷新还是该重试。这里如实说一句。
    // 连后端都连不上时 fetch 抛的是 TypeError（没有 status）：这时说「稍后重试或刷新页面」是误导，
    // 刷新多少次都一样，该确认的是服务在不在。
    toast(
      status
        ? "四库加载失败，已暂时显示本地内容；请稍后重试或刷新页面"
        : `无法连接后端（${typeof backendBaseUrl === "string" ? backendBaseUrl : "本机"}），已暂时显示本地内容；请确认服务正在运行`,
      { kind: "error", duration: 6000 },
    );
  }
}

async function analyzeCurrentViralNote(options = {}) {
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) {
    toast("请先在 AI 改写页选择一篇笔记");
    setSection("rewrite");
    return;
  }
  try {
    const metrics = draft.metrics || {};
    const requestedCategory = draft.viralCategory || draft.viralBrief?.category || (draft.tags?.match(/#[^\s#]+/) || [""])[0].replace(/^#/, "");
    let data = await postBackend("/api/viral/analyze", {
      note: { ...draft, metrics },
      category: requestedCategory,
      verifiedFacts: String(draft.viralBrief?.facts || "").trim(),
      intent: "收藏",
      count: 4,
      templateKinds: options.templateKinds || ["topic", "title"],
    }, options);
    state.viralAnalysis = data.analysis || null;
    state.viralSelections = Object.fromEntries((data.atoms || []).map((atom) => [atom.type, atom.id]));
    state.viralAdvancedOverrides = {};
    state.viralLibrarySelections = { topicId: "", titleId: "", coverId: "", innerId: "", templateSetId: "" };
    state.viralLastGeneration = null;
    syncViralSelectionsToCurrentDraft();
    await loadViralAtoms();
    if (data.libraries) {
      state.viralLibraries = normalizedViralLibraries(data.libraries);
      state.viralTopicPackages = data.topicPackages || (state.viralLibraries.topic || []).map(topicPackageFromItem).filter((item) => item?.id);
      renderFourLibraryWorkbench();
    } else {
      await loadViralLibraries({ preferredAtomIds: (data.atoms || []).map((atom) => atom.id) });
    }
    renderViralFactory();
    const fallbackReason = state.viralAnalysis?.aiError ? `；AI 回退：${state.viralAnalysis.aiError}` : "";
    toast(`${state.viralAnalysis?.sourceLabel || "分析"}完成，已生成所选内容模板${fallbackReason}`);
    return data;
  } catch (error) {
    if (isAbortError(error)) throw error;
    toast(`爆款分析失败：${error.message}`);
    return null;
  }
}

const templateImportStageLabels = {
  "content-analysis": "正在生成内容模板",
  preparing: "正在准备模板图片",
  "analysis-preparing": "正在准备视觉拆解",
  analyzing: "正在拆解图片版式",
  retrying: "正在手动重试所选页面",
  "analysis-complete": "图片拆解与本地校验已完成",
  cleaning: "正在处理模板底图",
  rendering: "正在渲染模板预览",
  validating: "正在执行最终校验",
  saving: "正在写入模板库",
  completed: "模板已成功入库",
  failed: "模板拆解失败",
  cancelling: "正在中止拆解",
  cancelled: "模板拆解已中断",
};
templateImportStageLabels["validating-local"] = "正在执行本地结构校验";
templateImportStageLabels.fallback = "正在使用本地版式兜底";
let templateImportProgressState = {};
let templateImportController = null;
let templateImportJobId = "";
let templateImportDraftId = "";
// 「用户已经按下中止，取消正在收尾」——这是运行态，不是持久化状态：它只用来让按钮立刻
// 变成「正在停止…」并锁住，避免用户在等收尾的这段时间里反复点、看起来像没反应。
let templateImportCancelling = false;

function isTemplateImportCancelling() {
  return templateImportCancelling === true;
}

function isTemplateImportRunningForDraft(draft) {
  if (!templateImportController) return false;
  const ownerDraftId = String(templateImportDraftId || "");
  if (!ownerDraftId) return true;
  return ownerDraftId === String(draft?.id || "");
}

// ``templateImportStatus`` is persisted with the draft, but "an import is running" is a
// runtime fact owned by this page session.  A reload, a backend restart or a crashed tab
// left the flag at "importing" forever, and #rewriteBtn then still read
// 「生成 1 份完整图文」 while its click started a template import: the user pressed
// 生成 and got the 「选择要入库的模板」 dialog instead, and cancelling that dialog did
// not clear the flag either, so every following click reopened it.
function recoverInterruptedTemplateImport(draft) {
  if (!draft || String(draft.templateImportStatus || "") !== "importing") return false;
  if (isTemplateImportRunningForDraft(draft)) return false;
  draft.templateImportStatus = "";
  // The panel only reads this when the interrupted run reported per-page状态, so it
  // explains those leftovers instead of showing a bare「模板拆解失败」.
  draft.templateImportError = "上次模板入库被中断，请重新执行模板入库";
  return true;
}

function clearTemplateImportProgress() {
  templateImportProgressState = {};
  const taskNode = $("#templateImportCurrentTask");
  if (taskNode) {
    taskNode.textContent = "";
    taskNode.title = "";
    taskNode.classList.add("hidden");
    taskNode.classList.remove("is-error", "is-complete", "is-active");
  }
  const mount = $("#templateImportProgress");
  if (mount) {
    mount.classList.add("hidden");
    mount.classList.remove("is-error", "is-fallback");
  }
  const pageStatuses = $("#templateImportPageStatuses");
  if (pageStatuses) {
    pageStatuses.innerHTML = "";
    pageStatuses.classList.add("hidden");
  }
  $("#templateImportRetryActions")?.classList.add("hidden");
  $("#templateImportCommitActions")?.classList.add("hidden");
  const commitHintNode = $("#templateImportCommitHint");
  if (commitHintNode) commitHintNode.textContent = "";
  const selectAll = $("#templateImportSelectAllFailed");
  if (selectAll) selectAll.checked = false;
  const retryButton = $("#templateImportRetrySelectedBtn");
  if (retryButton) retryButton.disabled = true;
  const detail = $("#templateImportProgressDetail");
  if (detail) {
    detail.textContent = "";
    detail.classList.add("hidden");
  }
  const bar = $("#templateImportProgressBar");
  if (bar) {
    bar.innerHTML = "";
    bar.classList.add("hidden");
  }
}

function hasTemplateImportProgress() {
  const current = templateImportProgressState || {};
  return Boolean(
    String(current.stage || "").trim()
    || String(current.message || "").trim()
    || (Array.isArray(current.pageStatuses) && current.pageStatuses.length)
    || String(current.jobId || "").trim()
  );
}

// 面板是"那次任务"的实况。任务还在跑时，切去别的页签/草稿只能把它**藏起来**，绝不能清掉
// 内存状态：运行态来自另一处（templateImportController / templateImportDraftId），清完就会
// 出现"主按钮还写着停止拆解，进度却空了"的不一致（现场报障：切了个页面回来进度不见）。
// 而且逐页结果要等任务收尾才写进草稿（importCurrentDraftToViralFactory 的 finally），
// 那一刻草稿里连快照都还没有——清掉就真的无处可恢复。
function templateImportProgressOwnedByLiveRun() {
  if (!templateImportController) return false;
  const runningDraftId = String(templateImportDraftId || "");
  if (!runningDraftId) return true;
  return runningDraftId === String(templateImportProgressState.draftId || "");
}

function hideTemplateImportProgressPanel() {
  [
    "#templateImportCurrentTask",
    "#templateImportProgress",
    "#templateImportPageStatuses",
    "#templateImportRetryActions",
    "#templateImportCommitActions",
    "#templateImportProgressDetail",
    "#templateImportProgressBar",
  ].forEach((selector) => $(selector)?.classList.add("hidden"));
}

function syncTemplateImportProgressToDraft(draftId) {
  if (!hasTemplateImportProgress()) return;
  const ownerDraftId = String(templateImportProgressState.draftId || "");
  if (ownerDraftId === String(draftId || "")) return;
  if (templateImportProgressOwnedByLiveRun()) {
    // 任务还在跑，只是当前视图不是它的草稿：藏起来，回到那份草稿时会重新画出来
    // （见 restoreTemplateImportProgressFromDraft）。
    hideTemplateImportProgressPanel();
    return;
  }
  clearTemplateImportProgress();
}

// 拆解记录是"那一次模板入库任务"的记录，不是这份草稿的固定资产。槽里一写进新任务——用户在
// 模板库重新选了选题、标题、封面或内页——它描述的就是上一份内容了：还挂在槽上，用户会以为
// 这次新任务也走同一条老进度。所以入库已经成功之后，只要槽换了内容就把记录退场。
//
// 两处来源必须一起丢，缺一处都等于没丢：
//   1. 运行时的面板（templateImportProgressState）只活到本次页面会话；
//   2. 逐页结果还会随草稿持久化，下一次 renderEditor 的
//      restoreTemplateImportProgressFromDraft 会把它原样放回面板——用户看到的就是
//      「选了新模板，旧的拆解进度和逐页结果又回来了」。
//
// 只在入库"成功"之后退场：失败记录里带着「重试失败页」这个唯一入口，拆解没成之前它不是
// 记录而是待处理的活；正在跑的那一轮更是实况（面板同时承担进度、取消与失败页重试），
// 结束前不动它。
function templateImportRecordSucceeded(draft, current = templateImportProgressState || {}) {
  const status = String(draft?.templateImportStatus || "");
  if (status === "imported" || status === "ready") return true;
  // 没有草稿状态可靠时看面板自己：reconcileRecoveredTemplateImport 会把已经入库成功、
  // 只是任务结果丢了的失败面板改写成完成态，那时草稿上仍是 "failed"。
  return ["completed", "fallback"].includes(String(current.stage || ""));
}

function retireTemplateImportRecordForDraft(draft = currentDraft()) {
  if (!draft || isTemplateImportRunningForDraft(draft)) return false;
  if (!templateImportRecordSucceeded(draft)) return false;
  const draftId = String(draft.id || "");
  const ownsLivePanel = Boolean(draftId)
    && draftId === String(templateImportProgressState.draftId || "")
    && hasTemplateImportProgress();
  const ownsSnapshot = Boolean(
    Array.isArray(draft.templateImportPageStatuses) && draft.templateImportPageStatuses.length,
  );
  if (!ownsLivePanel && !ownsSnapshot) return false;
  if (ownsSnapshot) draft.templateImportPageStatuses = [];
  if (ownsLivePanel) clearTemplateImportProgress();
  return true;
}

function templateImportProgressReporter(draftId) {
  const ownerDraftId = String(draftId || "");
  return (progress = {}, options = {}) => {
    if (!ownerDraftId || String(state.activeDraftId || "") !== ownerDraftId) return;
    updateTemplateImportProgress({ ...progress, draftId: ownerDraftId }, options);
  };
}

function templateImportFailureDetail(error) {
  // The job read contract only exposes a stable error code, so the actionable
  // reason has to come from the per-page拆解 statuses the worker reported.
  const message = String(error?.message || "").trim() || "模板入库失败";
  const reasons = (Array.isArray(templateImportProgressState.pageStatuses) ? templateImportProgressState.pageStatuses : [])
    .filter((item) => item?.status === "failed" && String(item.message || "").trim())
    .map((item) => `第 ${Number(item.page) || 1} 页：${humanizeFailureReason(item.message)}`);
  const unique = [...new Set(reasons)].slice(0, 2);
  if (!unique.length) return message;
  // message 自己已经带上页级原因时不要再拼一遍：jobFailureMessage 给的本来就是页级人话，
  // 再追加一次会让同一句原因在一行提示里出现两遍（现场那条"原因：…"后面又跟一个"第 7 页：…"）。
  const missing = unique.filter((line) => !message.includes(line.slice(line.indexOf("：") + 1)));
  if (!missing.length) return message.slice(0, 240);
  return `${message}（${missing.join("；")}）`.slice(0, 240);
}

function templateImportFailureLabel(stage) {
  return ["analysis-complete", "cleaning", "rendering", "validating", "saving"].includes(String(stage || ""))
    ? "图片拆解成功，但模板入库失败"
    : "模板拆解失败";
}

function templateImportRetryReady(current = {}, items = current.pageStatuses || []) {
  // ``cancelled`` 也要算就绪：中止后剩下的页在服务端被标成 failed（等待手动重试），
  // 而"继续完成剩余页"走的正是同一条 retry-pages 通道。过去只认 completed/failed，
  // 于是中止后已完成的部分既没入库、也没有续跑的入口（台账 P99）。
  if (!["completed", "failed", "cancelled"].includes(String(current.stage || "")) || !String(current.templateId || "").trim()) return false;
  const statuses = Array.isArray(items) ? items.map((item) => String(item?.status || "")) : [];
  return statuses.some((status) => status === "failed")
    && statuses.every((status) => status === "success" || status === "failed");
}

// 「保留入库」的显示条件：这次拆解没走完（中止 / 失败 / 重试用尽后仍失败），但确实有页面
// 拆成功了。正在跑的时候不给——那等于鼓励用户放弃剩下还在分析的页；全部成功时也不给，
// 模板本来就已经入库了。
function templateImportCommitReady(current = {}, items = current.pageStatuses || []) {
  const stage = String(current?.stage || "");
  if (!["cancelled", "failed", "completed"].includes(stage)) return false;
  if (!templateIdFromImportProgress(current)) return false;
  const statuses = Array.isArray(items) ? items.map((item) => String(item?.status || "")) : [];
  const successful = statuses.filter((status) => status === "success").length;
  if (!successful) return false;
  // 中止时即使此刻没有失败页也要给：还没轮到的页已经被放弃，那些就是"没完成的部分"。
  return stage === "cancelled" || statuses.some((status) => status === "failed");
}

const templateImportPageStatusLabels = {
  pending: "等待拆解",
  analyzing: "拆解中",
  retrying: "重试中",
  success: "成功",
  failed: "失败",
};

function templateImportPageStatusEntries(items = [], { allowRetry = false } = {}) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    const requestedStatus = String(item?.status || "");
    const status = templateImportPageStatusLabels[requestedStatus] ? requestedStatus : "pending";
    const page = Math.max(1, Number(item?.page) || index + 1);
    const message = String(item?.message || templateImportPageStatusLabels[status]);
    return {
      status,
      page,
      message,
      image: String(item?.image || ""),
      // 页级失败原因也要过一遍人话映射：上游给的是英文原话（现场："Upstream response
      // stream ended before completion"），直接摆在页卡上没人看得懂。
      visibleMessage: status === "failed" ? `失败原因：${humanizeFailureReason(message)}` : templateImportPageStatusLabels[status],
      retryVisible: status === "failed" && allowRetry,
    };
  });
}

function templateImportPageStatusNode() {
  const node = document.createElement("div");
  node.className = "template-import-page-status";
  node.setAttribute("role", "listitem");
  node.innerHTML = [
    '<span class="template-import-page-thumb"><img alt="" loading="lazy" /></span>',
    '<span class="template-import-page-copy"><strong></strong><small></small></span>',
    '<input type="checkbox" class="hidden" />',
    '<i aria-hidden="true"></i>',
  ].join("");
  return node;
}

function templateImportPageStatusSync(mount, items = [], { allowRetry = false } = {}) {
  // Patch the existing rows instead of rebuilding them: a rebuilt <img> is
  // re-decoded on every progress update, which made the whole list flicker
  // while the pages were being analyzed.
  if (!mount) return;
  const entries = templateImportPageStatusEntries(items, { allowRetry });
  while (mount.childElementCount > entries.length) mount.lastElementChild.remove();
  entries.forEach((entry, index) => {
    let node = mount.children[index];
    if (!node || node.tagName !== "DIV") {
      node = templateImportPageStatusNode();
      if (mount.children[index]) mount.replaceChild(node, mount.children[index]);
      else mount.appendChild(node);
    }
    node.className = `template-import-page-status is-${entry.status}`;
    node.title = entry.message;
    const image = node.querySelector("img");
    if (image) {
      // Protected images are hydrated by a MutationObserver that replaces src
      // with a blob URL and then refuses to hydrate the same element again.
      // Comparing against the live src attribute would therefore overwrite the
      // blob with the protected URL and leave a broken <img>, so remember the
      // requested source instead and never touch src once it matches.
      const wanted = entry.image;
      const thumbnailRetries = Number(image.dataset.templateThumbRetry || 0);
      if ((image.dataset.templateThumbSource || "") !== wanted) {
        image.dataset.templateThumbSource = wanted;
        image.dataset.templateThumbRetry = "0";
        if (wanted) {
          image.setAttribute("src", wanted);
          image.removeAttribute("data-protected-image-error");
        } else {
          image.removeAttribute("src");
        }
      } else if (wanted && image.hasAttribute("data-protected-image-error") && thumbnailRetries < 3) {
        // A dropped fetch used to heal itself because the row was rebuilt on
        // every update; retry the hydration a few times instead of leaving a
        // permanently broken thumbnail.
        image.dataset.templateThumbRetry = String(thumbnailRetries + 1);
        image.removeAttribute("data-protected-image-error");
        image.setAttribute("src", wanted);
      }
      image.alt = wanted ? `第 ${entry.page} 张原图` : "";
    }
    const title = node.querySelector("strong");
    if (title) title.textContent = `第 ${entry.page} 张`;
    const copy = node.querySelector("small");
    if (copy) copy.textContent = entry.visibleMessage;
    const checkbox = node.querySelector("input");
    if (checkbox) {
      checkbox.dataset.templateRetryPage = String(entry.page - 1);
      checkbox.setAttribute("aria-label", `选择第 ${entry.page} 张失败页`);
      checkbox.checked = entry.retryVisible;
      checkbox.classList.toggle("hidden", !entry.retryVisible);
    }
  });
}

function templateImportPageStatusMarkup(items = [], options = {}) {
  const host = document.createElement("div");
  templateImportPageStatusSync(host, items, options);
  return host.innerHTML;
}

function templateImportPhaseKey(current = {}) {
  return `${String(current?.stage || "")}:${String(current?.phase || "")}`;
}

function templateImportTaskText(current = {}) {
  const total = Math.max(0, Number(current.totalImages) || 0);
  const analyzed = Math.min(total, Math.max(0, Number(current.analyzedImages) || 0));
  const pageStatuses = Array.isArray(current.pageStatuses) ? current.pageStatuses : [];
  const successfulPageCount = pageStatuses.filter((item) => item?.status === "success").length;
  const failedPageCount = pageStatuses.filter((item) => item?.status === "failed").length;
  const retrying = current.stage === "retrying" && current.manualRetry === true;
  const stageLabel = templateImportStageLabels[current.stage] || "模板拆解处理中";
  let taskText = String(current.message || "").trim() || stageLabel;
  if (retrying) {
    taskText = `正在重试第 ${Number(current.retryPage) || Number(current.currentImage) || 1} 张图片`;
  } else if (current.stage === "completed") {
    taskText = failedPageCount
      ? `模板部分入库：成功 ${successfulPageCount} 张，失败 ${failedPageCount} 张`
      : String(current.message || "").trim() || "模板已成功入库";
  } else if (current.stage === "failed") {
    taskText = String(current.message || "").trim() || "模板入库未完成";
  } else if (total && ["analyzing", "validating-local", "retrying"].includes(current.stage)) {
    taskText = String(current.message || "").trim() || `${stageLabel}（${analyzed}/${total}）`;
  }
  // A拆解 runs for minutes on one long upstream call, so the row must show
  // both how far the server got and that the task is still alive.
  const percent = templateImportPercent(current);
  if (percent !== null && !["completed", "failed", "cancelled"].includes(String(current.stage || ""))) {
    taskText = `${taskText} · ${percent}%`;
  }
  return taskText;
}

function templateImportPercent(current = {}) {
  const stage = String(current?.stage || "");
  if (["failed", "cancelled"].includes(stage)) return null;
  if (stage === "completed") return 100;
  const raw = Number(current?.percent);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  return Math.max(1, Math.min(100, Math.round(raw)));
}

function renderTemplateImportProgressBar(current = {}) {
  const mount = $("#templateImportProgressBar");
  if (!mount) return;
  const percent = templateImportPercent(current);
  if (percent === null) {
    mount.innerHTML = "";
    mount.classList.add("hidden");
    return;
  }
  let track = mount.querySelector(".generation-progress-track");
  if (!track) {
    // P110：拆解与图文生成共用同一套进度条样式，aria-label 必须写明任务类型，
    // 屏幕阅读器才能区分当前跑的是哪一件事。
    mount.innerHTML = '<div class="generation-progress-track" role="progressbar" aria-label="模板拆解进度" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><span style="width:0%"></span></div>';
    track = mount.querySelector(".generation-progress-track");
  }
  // Reuse the same track so its CSS width transition animates instead of
  // restarting from zero on every progress update.
  track.setAttribute("aria-valuenow", String(percent));
  const fill = track.firstElementChild;
  if (fill) fill.style.width = `${percent}%`;
  mount.classList.remove("hidden");
}

function renderTemplateImportProgressText() {
  const node = $("#templateImportCurrentTask");
  if (!node || !hasTemplateImportProgress()) return;
  const taskText = templateImportTaskText(templateImportProgressState);
  node.textContent = taskText;
  node.title = taskText;
  renderTemplateImportProgressBar(templateImportProgressState);
}

function templateIdFromImportProgress(current = {}) {
  const directId = String(current.templateId || "").trim();
  if (directId) return directId;
  return (Array.isArray(current.pageStatuses) ? current.pageStatuses : [])
    .map((item) => String(item?.image || "").match(/\/api\/template-asset\/([^/]+)\//)?.[1] || "")
    .find(Boolean) || "";
}

function updateTemplateImportProgress(progress = {}, { reset = false } = {}) {
  const mount = $("#templateImportProgress");
  const taskNode = $("#templateImportCurrentTask");
  if (!mount || !taskNode) return;
  const previousPhaseKey = templateImportPhaseKey(templateImportProgressState);
  templateImportProgressState = { ...(reset ? {} : templateImportProgressState), ...progress };
  if (!String(templateImportProgressState.stage || "").trim()
    && !String(templateImportProgressState.message || "").trim()
    && !(Array.isArray(templateImportProgressState.pageStatuses) && templateImportProgressState.pageStatuses.length)) {
    clearTemplateImportProgress();
    return;
  }
  const inferredTemplateId = templateIdFromImportProgress(templateImportProgressState);
  if (inferredTemplateId) templateImportProgressState.templateId = inferredTemplateId;
  const recovered = recoveredTemplateForImportProgress(templateImportProgressState);
  if (recovered) {
    const recoveredStatuses = Array.isArray(templateImportProgressState.pageStatuses)
      ? templateImportProgressState.pageStatuses
      : [];
    const recoveredPageCount = Math.max(
      0,
      Number(templateImportProgressState.totalImages) || recoveredStatuses.length,
    );
    templateImportProgressState = {
      ...templateImportProgressState,
      stage: "completed",
      message: "模板已恢复入库，共 " + recoveredPageCount + " 页",
      percent: 100,
      templateId: recovered.id,
      totalImages: recoveredPageCount,
      analyzedImages: recoveredPageCount,
      failedStage: "",
    };
  }
  const current = templateImportProgressState;
  if (!Number(current.startedAt) || templateImportPhaseKey(current) !== previousPhaseKey) {
    current.startedAt = Date.now();
  }
  const pageStatuses = Array.isArray(current.pageStatuses) ? current.pageStatuses : [];
  const failedPageCount = pageStatuses.filter((item) => item?.status === "failed").length;
  const retryReady = templateImportRetryReady(current, pageStatuses);
  const retrying = current.stage === "retrying" && current.manualRetry === true;
  const cancellableStages = ["content-analysis", "preparing", "analysis-preparing", "analyzing", "validating-local", "retrying", "cancelling"];
  const active = cancellableStages.includes(current.stage) && Boolean(templateImportJobId);
  const taskText = templateImportTaskText(current);
  taskNode.textContent = taskText;
  taskNode.title = taskText;
  renderTemplateImportProgressBar(current);
  taskNode.classList.remove("hidden");
  taskNode.classList.toggle("is-error", ["failed", "cancelled"].includes(current.stage));
  taskNode.classList.toggle("is-complete", current.stage === "completed");
  taskNode.classList.toggle("is-active", !["failed", "completed", "cancelled"].includes(current.stage));
  mount.classList.toggle("is-error", ["failed", "cancelled"].includes(current.stage));
  mount.classList.toggle("is-fallback", current.stage === "fallback");
  // 面板里不再有自己的中止按钮（P100）：中止唯一入口是编辑区主按钮，文案与运行态由
  // drafts.js 统一渲染，这里再写一次就会多出「停止拆解」这个说法。
  const pageStatusMount = $("#templateImportPageStatuses");
  templateImportPageStatusSync(pageStatusMount, pageStatuses, { allowRetry: retryReady });
  pageStatusMount.classList.toggle("hidden", !pageStatuses.length);
  const retryActions = $("#templateImportRetryActions");
  retryActions.classList.toggle("hidden", !retryReady);
  $("#templateImportSelectAllFailed").checked = retryReady;
  $("#templateImportRetrySelectedBtn").disabled = !retryReady;
  // 中止后的这些页是"还没轮到拆解"，不是"拆解失败"：按钮得说清它做的是接着把没做完的页
  // 跑完（成功页已经在服务端的 pending 里，不会重复提交，台账 P99）。
  $("#templateImportRetrySelectedBtn").textContent = current.stage === "cancelled"
    ? `继续完成剩余页（${failedPageCount}）`
    : failedPageCount ? `重试失败页（${failedPageCount}）` : "重试失败页";
  // 中止/失败但已有页面拆成功时的另一条出路：把已完成的页先入库，不必等剩下的页（P99）。
  const commitReady = templateImportCommitReady(current, pageStatuses);
  const commitActions = $("#templateImportCommitActions");
  if (commitActions) {
    commitActions.classList.toggle("hidden", !commitReady);
    const successfulPageCount = pageStatuses.filter((item) => item?.status === "success").length;
    const commitButton = $("#templateImportCommitBtn");
    if (commitButton) {
      commitButton.disabled = !commitReady || Boolean(templateImportController);
      commitButton.textContent = `保留已完成的 ${successfulPageCount} 页并入库`;
    }
    const commitHint = $("#templateImportCommitHint");
    if (commitHint) {
      commitHint.textContent = commitReady
        ? "失败与未完成的页面不会入库，模板只由已拆解的页面组成"
        : "";
    }
  }
  const detailNode = $("#templateImportProgressDetail");
  const detail = current.stage === "failed" || current.stage === "cancelled"
    ? String(current.message || "模板处理失败")
    : current.stage === "fallback"
      ? `AI 拆解未完成，已使用本地版式继续入库。${current.fallbackReason || ""}`
      : retrying
        ? String(current.retryReason || "正在修正该页的拆解结果")
        : "";
  detailNode.textContent = detail;
  detailNode.classList.toggle("hidden", !detail);
  mount.classList.toggle("hidden", !pageStatuses.length && !detail && !active);
  // 中止后的面板不再自动清空：它现在带着"继续完成剩余页"与「保留已完成的 N 页并入库」两条
  // 出路，2.4 秒后消失等于把它们一起关掉（台账 P99）。用户点过才收。
}

function templateImportPageStatusSnapshot(items, limit = 18) {
  // Stored with the draft so the per-page拆解 result survives a reload; the
  // live panel is in-memory only and used to disappear on refresh.
  return (Array.isArray(items) ? items : [])
    .slice(0, limit)
    .map((item) => ({
      page: Number(item?.page) || 0,
      status: String(item?.status || ""),
      message: String(item?.message || "").slice(0, 200),
      image: String(item?.image || ""),
    }))
    .filter((item) => item.page > 0 && item.status);
}

function restoreTemplateImportProgressFromDraft(draft) {
  if (!draft) return false;
  if (hasTemplateImportProgress()) {
    // 实况面板（正在跑，或本次会话刚跑完还没被撤）只活在内存里。编辑区每次重渲染都会把面板
    // 节点复位成初始的 hidden，而"有进度就 return"让没有任何人重画它——于是任务还在跑、
    // 主按钮还是"停止拆解"，进度条却空了（现场报障）。属于这份草稿的实况在这里重画一遍。
    if (String(templateImportProgressState.draftId || "") === String(draft.id || "")) {
      updateTemplateImportProgress({});
    }
    return false;
  }
  const saved = templateImportPageStatusSnapshot(draft.templateImportPageStatuses);
  if (!saved.length) return false;
  const status = String(draft.templateImportStatus || "");
  const imported = status === "imported";
  // "pending" 只会由中止路径写入（importCurrentDraftToViralFactory 的 AbortError 分支）。
  // 恢复成 cancelled 而不是 failed：面板要靠它显示"继续完成剩余页"，而 failed 会被
  // recoveredTemplateForImportProgress 当成"其实已入库"的候选（中止时并没有入库）。
  const cancelled = status === "pending";
  const successful = saved.filter((item) => item.status === "success").length;
  updateTemplateImportProgress({
    draftId: String(draft.id || ""),
    stage: imported ? "completed" : cancelled ? "cancelled" : "failed",
    message: String(draft.templateImportError || "").trim()
      || (imported
        ? "模板已成功入库"
        : cancelled
          ? `模板拆解已中止，已保留 ${successful} 张成功页`
          : "模板拆解失败"),
    // 续跑要靠这个 id 从服务端的 pending 索引续做；它过去只在成功路径写入（P99）。
    templateId: String(draft.templateImportTemplateId || ""),
    pageStatuses: saved,
    totalImages: saved.length,
    analyzedImages: saved.filter((item) => ["success", "failed"].includes(item.status)).length,
  }, { reset: true });
  return true;
}

function templateImportRequestOptions(reportProgress, controller, submittedMessage = "") {
  return {
    signal: controller.signal,
    onProgress: reportProgress,
    onJobStart: (job) => {
      templateImportJobId = String(job.jobId || "");
      updateTemplateImportProgress({ jobId: templateImportJobId });
      reportProgress({ jobId: templateImportJobId });
      // 受理到"第一批进度"之间还有一段空白（服务端要先起 worker、读原图）。这段时间面板
      // 必须说明"任务已经在服务端了、正在等回话"，否则还是那句提交前的话。归属仍由
      // reportProgress 按草稿把关，这里只补一句文案。
      if (submittedMessage) reportProgress({ message: submittedMessage });
    },
  };
}

async function cancelTemplateImport() {
  if (!templateImportController) return;
  // 已经按过一次：收尾期间再点不该重新发请求，也不该让人以为"还能再按一次"。
  if (templateImportCancelling) return;
  const contentAnalysis = templateImportProgressState.stage === "content-analysis";
  const controller = templateImportController;
  const jobId = templateImportJobId;
  templateImportCancelling = true;
  updateTemplateImportProgress({
    stage: "cancelling",
    message: contentAnalysis ? "正在中止内容模板拆解" : "正在中止拆解",
  });
  // 先停前端这一侧：按下中止的那一刻界面就该停。原来先 await 那个请求，而它要等后端做
  // 状态转换与审计；后端正忙着跑这一轮拆解时，用户要干等几秒甚至更久，看起来就是
  // "点了没反应"，然后忍不住再点一次。
  controller.abort();
  if (state.currentSection === "rewrite") renderEditor();
  toast(contentAnalysis ? "已请求中止内容模板拆解…" : "已请求中止，正在等当前页收尾…", { duration: 4200 });
  try {
    if (jobId) await postBackend("/api/jobs/cancel", { jobId });
  } catch (error) {
    // 前端已经停了；这里失败只影响服务端那一份的收尾，如实说明，但不假装中止没发生。
    toast(`中止拆解请求未送达：${error.message}（界面已停止，服务端任务可能仍在收尾）`, {
      kind: "error",
      duration: 8000,
    });
  }
}

function recoveredTemplateForImportProgress(current = {}) {
  if (current.stage !== "failed") return false;
  const pageStatuses = Array.isArray(current.pageStatuses) ? current.pageStatuses : [];
  if (!pageStatuses.length || pageStatuses.some((item) => item?.status !== "success")) return false;
  const totalImages = Math.max(0, Number(current.totalImages) || pageStatuses.length);
  const recoveredTemplateId = pageStatuses
    .map((item) => String(item?.image || "").match(/\/api\/template-asset\/([^/]+)\//)?.[1] || "")
    .find(Boolean) || String(current.templateId || "");
  const recovered = (state.layoutTemplates || []).find((template) => {
    const quality = template?.params?.analysisQuality || {};
    const originalCount = (template?.originalImages || []).filter(Boolean).length;
    const renderedCount = (template?.renderedImages || []).filter(Boolean).length;
    return (!recoveredTemplateId || template.id === recoveredTemplateId)
      && quality.status === "passed"
      && originalCount === totalImages
      && renderedCount === totalImages;
  });
  return recovered || false;
}

function reconcileRecoveredTemplateImport() {
  const current = templateImportProgressState || {};
  const recovered = recoveredTemplateForImportProgress(current);
  if (!recovered) return false;
  const pageStatuses = Array.isArray(current.pageStatuses) ? current.pageStatuses : [];
  const totalImages = Math.max(0, Number(current.totalImages) || pageStatuses.length);
  updateTemplateImportProgress({
    stage: "completed",
    message: "模板已恢复入库，共 " + totalImages + " 页",
    percent: 100,
    templateId: recovered.id,
    totalImages,
    analyzedImages: totalImages,
    pageStatuses,
    failedStage: "",
  });
  return true;
}

async function retrySelectedTemplateImportPages() {
  if (!templateImportRetryReady(templateImportProgressState)) {
    toast("请等待所有页面拆解结束后再重试");
    return;
  }
  const templateId = String(templateImportProgressState.templateId || "");
  const draftId = String(templateImportProgressState.draftId || state.activeDraftId || "");
  const reportProgress = templateImportProgressReporter(draftId);
  const pageIndexes = $$('[data-template-retry-page]:checked').map((input) => Number(input.dataset.templateRetryPage));
  if (!templateId || !pageIndexes.length) {
    toast("请选择需要重试的失败页");
    return;
  }
  const button = $("#templateImportRetrySelectedBtn");
  const retryDraft = state.drafts.find((item) => String(item.id || "") === draftId);
  const retryPreviousStatus = retryDraft?.templateImportStatus || "imported";
  if (retryDraft) {
    retryDraft.templateImportStatus = "importing";
    retryDraft.templateImportError = "";
    saveState();
  }
  const done = setButtonLoading(button, "正在重试...");
  const controller = new AbortController();
  templateImportController = controller;
  templateImportCancelling = false;
  templateImportDraftId = draftId;
  templateImportJobId = "";
  try {
    const data = await postBackend(
      "/api/templates/retry-pages",
      { templateId, pageIndexes },
      templateImportRequestOptions(reportProgress, controller),
    );
    const template = data.template;
    if (template) {
      state.layoutTemplates = data.templates || state.layoutTemplates.map((item) => item.id === template.id ? template : item);
      const quality = template.params?.analysisQuality || {};
      reportProgress({
        stage: "completed",
        message: quality.failedPages?.length ? "所选页面重试完成，仍有失败页可继续选择" : "所有模板页均已拆解成功",
        percent: 100,
        templateId: template.id,
        totalImages: template.originalImages?.length || 0,
        pageStatuses: quality.pageStatuses || [],
      });
      if (retryDraft) {
        retryDraft.templateImportStatus = retryPreviousStatus === "ready" ? "ready" : "imported";
        retryDraft.templateImportCompletedAt = new Date().toISOString();
        retryDraft.templateImportError = quality.failedPages?.length ? "仍有失败页可继续选择" : "";
        saveState();
      }
      await loadViralLibraries({ recommend: false });
    }
  } catch (error) {
    if (retryDraft) {
      retryDraft.templateImportStatus = isAbortError(error) ? retryPreviousStatus : "failed";
      retryDraft.templateImportError = isAbortError(error) ? "" : (error.message || "模板页面重试失败");
      saveState();
    }
    if (!isAbortError(error)) toast(`手动重试失败：${error.message}`, { kind: "error", duration: 9000 });
  } finally {
    if (templateImportController === controller) {
      templateImportController = null;
      templateImportDraftId = "";
    }
    // 这一轮到此为止：无论中止还是跑完，都不该再显示「正在停止」。
    templateImportCancelling = false;
    templateImportJobId = "";
    done();
    updateTemplateImportProgress({});
    if (state.currentSection === "rewrite") renderEditor();
  }
}

// 「保留入库」：中止或部分失败之后，把已经拆解成功的页面单独入库。
//
// 服务端在拆解中止/部分失败时已经把成功页写进 pending 记录（提示语也是「已保留成功页面」），
// 但整份模板还带着失败页，直接入库会倒在合同与逐页校验上——此前只有「重试失败页」一条出路，
// 用户不想再等/再花钱的时候，那些已经拆好的页面就只能烂在那里。这里补上另一条出路：
// 只保留成功页，让服务端按这些页重建一套完整模板（不含任何 AI 调用，因此不计费）。
async function commitPartialTemplateImport() {
  const current = templateImportProgressState || {};
  if (!templateImportCommitReady(current)) {
    toast("当前没有可保留入库的已拆解页面");
    return;
  }
  const templateId = templateIdFromImportProgress(current);
  const pageStatuses = Array.isArray(current.pageStatuses) ? current.pageStatuses : [];
  const pageIndexes = pageStatuses
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => String(item?.status || "") === "success")
    .map(({ item, index }) => (Number(item?.page) > 0 ? Number(item.page) - 1 : index));
  if (!templateId || !pageIndexes.length) {
    toast("没有可保留入库的已拆解页面");
    return;
  }
  if (
    typeof window !== "undefined"
    && typeof window.confirm === "function"
    && !window.confirm(`把已拆解成功的 ${pageIndexes.length} 页保留入库？失败和未完成的页面不会进入模板库。`)
  ) return;
  const draftId = String(current.draftId || state.activeDraftId || "");
  const draft = state.drafts.find((item) => String(item.id || "") === draftId);
  const previousStatus = draft?.templateImportStatus || "failed";
  const button = $("#templateImportCommitBtn");
  const done = setButtonLoading(button, "正在保留入库...");
  if (draft) {
    draft.templateImportStatus = "importing";
    draft.templateImportError = "";
    saveState();
  }
  const controller = new AbortController();
  templateImportController = controller;
  templateImportCancelling = false;
  templateImportDraftId = draftId;
  templateImportJobId = "";
  const reportProgress = templateImportProgressReporter(draftId);
  try {
    const data = await postBackend(
      "/api/templates/commit-partial",
      { templateId, pageIndexes },
      templateImportRequestOptions(reportProgress, controller),
    );
    const template = data.template;
    if (!template) throw new Error("保留入库没有返回模板");
    state.layoutTemplates = data.templates
      || state.layoutTemplates.map((item) => (item.id === template.id ? template : item));
    state.activeLayoutTemplateId = template.id;
    const keptPageCount = (template.originalImages || []).filter(Boolean).length;
    reportProgress({
      stage: "completed",
      message: `已保留 ${keptPageCount} 页拆解结果入库`,
      percent: 100,
      templateId: template.id,
      totalImages: keptPageCount,
      analyzedImages: keptPageCount,
      pageStatuses: template.params?.analysisQuality?.pageStatuses || [],
    });
    if (draft) {
      draft.templateImportStatus = previousStatus === "ready" ? "ready" : "imported";
      draft.templateImportCompletedAt = new Date().toISOString();
      draft.templateImportError = "";
      saveState();
    }
    await loadViralLibraries({ recommend: false });
    toast(`已保留 ${keptPageCount} 页并加入模板库`);
  } catch (error) {
    if (draft) {
      draft.templateImportStatus = isAbortError(error) ? previousStatus : "failed";
      draft.templateImportError = isAbortError(error) ? "" : (error.message || "保留入库失败");
      saveState();
    }
    if (!isAbortError(error)) toast(`保留入库失败：${error.message}`, { kind: "error", duration: 9000 });
  } finally {
    if (templateImportController === controller) {
      templateImportController = null;
      templateImportDraftId = "";
    }
    // 这一轮到此为止：无论中止还是跑完，都不该再显示「正在停止」。
    templateImportCancelling = false;
    templateImportJobId = "";
    done();
    updateTemplateImportProgress({});
    if (state.currentSection === "rewrite") renderEditor();
  }
}

async function importCurrentDraftToViralFactory(buttonOverride = null) {
  // 正在跑的拆解只能被"它自己那份草稿"停下来。
  //
  // 这里过去只看"有没有在跑"，不看归属：在别的草稿上点「拆解并入库模板」时，那个按钮的
  // 文案明明是"开始拆解"（中止文案只在归属草稿上出现，见 drafts.js 的 isTemplateImportRunningForDraft），
  // 点下去却把别人正在跑的任务中止了——用户完全预期不到，而且没有任何确认。
  // 同一份草稿上再点仍然是停止：那是按钮当时承诺的动作。
  if (templateImportController) {
    // 归属只看草稿本身，不调 syncFormToDraft：那个函数会 saveState + 重渲染，而"拒绝"这条
    // 路径不该顺手把编辑器内容落盘（同一份草稿要停止时也不需要它）。
    const activeDraft = currentDraft();
    if (isTemplateImportRunningForDraft(activeDraft)) {
      await cancelTemplateImport();
      return;
    }
    const owner = state.drafts.find((item) => String(item.id || "") === String(templateImportDraftId || ""));
    const ownerLabel = owner ? draftTaskTitle(owner) : "另一份草稿";
    toast(`「${ownerLabel}」正在拆解，请等它结束，或切回该任务点「中止拆解」`, { kind: "warning", duration: 8000 });
    return;
  }
  // 入口曾经挂 data-requires-capability="viralAnalysis,templateGeneration"；现在只剩主按钮，
  // 就地把同一份能力清单检查补在动作上，否则缺能力的用户只会拿到后端报错。
  if (!capabilityEnabled("viralAnalysis") || !capabilityEnabled("templateGeneration")) {
    toast("模板入库当前不可用，请稍后再试", { kind: "error" });
    return;
  }
  const draft = syncFormToDraft() || currentDraft();
  if (!draft) {
    toast("请先导入或新建一篇笔记");
    return;
  }
  // 空草稿没有可拆解的内容：链接草稿（source=link）只要还没选模板就会走到这里，过去空
  // 草稿也能拆——实测在弹窗里勾上"选题/标题"确认后会真的发起一次拆解任务。入口的按钮
  // 已经跟着置灰，这里再拦一次，保证别的调用点也拦得住。
  if (!draftHasUsableContent(draft)) {
    const hint = typeof EMPTY_DRAFT_TEMPLATE_IMPORT_HINT === "string"
      ? EMPTY_DRAFT_TEMPLATE_IMPORT_HINT
      : "先导入笔记内容或加入图片，再拆解入库";
    toast(hint, { kind: "error", duration: 5000 });
    return;
  }
  const templateSelection = await chooseTemplatePageRoles(draft.images || []);
  if (!templateSelection) {
    clearTemplateImportProgress();
    return;
  }
  const templateKinds = templateSelection.templateKinds || selectedTemplateImportKinds();
  if (!templateKinds.length) {
    clearTemplateImportProgress();
    toast("请至少选择一种要入库的模板");
    return;
  }
  const button = buttonOverride || $("#rewriteBtn");
  templateImportDraftId = String(draft.id || "");
  draft.templateImportStatus = "importing";
  draft.templateImportStartedAt = new Date().toISOString();
  draft.templateImportError = "";
  draft.templateImportPageStatuses = [];
  saveState();
  renderEditor();
  const done = setButtonCancelable(button, "中止拆解", "中止当前模板入库任务");
  const reportProgress = templateImportProgressReporter(draft.id);
  const controller = new AbortController();
  templateImportController = controller;
  templateImportCancelling = false;
  templateImportJobId = "";
  button.classList.add("is-loading");
  button.setAttribute("aria-busy", "true");
  try {
    const hasImages = (draft.images || []).length > 0;
    const templatePageRoles = templateSelection.pageRoles;
    const needsContentTemplates = templateKinds.includes("topic") || templateKinds.includes("title");
    reportProgress({
      stage: needsContentTemplates ? "content-analysis" : "preparing",
      message: needsContentTemplates ? "正在生成所选内容模板" : "正在准备视觉模板",
      meta: "将按勾选结果生成选题、标题、封面或内页模板",
      percent: 2,
    }, { reset: true });
    // 中止文案只有一处来源：drafts.js 的渲染（"中止拆解"）。这里再写一次就会多出
    // 第二套说法（台账 P100，面板侧那套「停止拆解」已随按钮一起删掉）。
    const analysisOptions = templateImportRequestOptions(reportProgress, controller);
    analysisOptions.templateKinds = templateKinds;
    const analysis = needsContentTemplates
      ? await analyzeCurrentViralNote(analysisOptions)
      : null;
    if (needsContentTemplates && !analysis) {
      draft.templateImportStatus = "failed";
      draft.templateImportError = "内容模板分析失败";
      reportProgress({ stage: "failed", message: "内容模板分析失败", failedStage: "content-analysis" });
      return;
    }
    // 这一句必须说清"还在提交"。服务端受理一次拆解要跑计费预扣、任务落库与磁盘水位检查，
    // 机器忙的时候这段受理能有十几秒到一分钟：面板此前一直停在提交前那句话（"准备视觉模板"），
    // 用户看到的就是"点了没反应、一直卡在 4%"。
    reportProgress({
      stage: "preparing",
      message: needsContentTemplates ? "内容模板已完成，正在提交视觉拆解任务" : "正在提交视觉拆解任务",
      meta: hasImages && (templateKinds.includes("cover") || templateKinds.includes("inner")) ? "正在启动图片拆解任务" : "正在整理入库结果",
      percent: 4,
    });
    let visualTemplate = null;
    const visualKinds = templateKinds.filter((kind) => kind === "cover" || kind === "inner");
    if (hasImages && visualKinds.length) {
      visualTemplate = await generateViralVisualTemplate(null, templatePageRoles, {
        recommend: false,
        onProgress: reportProgress,
        requestOptions: templateImportRequestOptions(reportProgress, controller, "拆解任务已提交，正在等待服务器返回拆解进度"),
      });
      if (!visualTemplate?.id) {
        throw new Error(`${visualKinds.map((kind) => kind === "cover" ? "封面" : "内页").join("、")}视觉模板未生成，未写入模板库`);
      }
      const returnedRoles = new Set(visualTemplate.templatePageRoles || []);
      const missingKinds = visualKinds.filter((kind) => !returnedRoles.has(kind));
      if (missingKinds.length) {
        throw new Error(`${missingKinds.map((kind) => kind === "cover" ? "封面" : "内页").join("、")}模板未生成，未完成入库`);
      }
    }
    if (analysis) {
      await loadViralLibraries({ recommend: false, preferredAtomIds: (analysis.atoms || []).map((atom) => atom.id) });
    } else {
      await loadViralLibraries({ recommend: false });
    }
    reportProgress({
      stage: "completed",
      message: visualTemplate ? "视觉模板已成功入库" : "内容模板已成功入库",
      percent: 100,
      templateId: visualTemplate?.id || "",
      totalImages: visualTemplate?.originalImages?.length || 0,
      pageStatuses: visualTemplate?.params?.analysisQuality?.pageStatuses || templateImportProgressState.pageStatuses || [],
    });
    draft.templateImportStatus = "imported";
    draft.templateImportCompletedAt = new Date().toISOString();
    draft.templateImportTemplateId = visualTemplate?.id || "";
    draft.templateImportKinds = [...templateKinds];
    draft.templateImportError = "";
    saveState();
    renderEditor();
    const importedLabels = templateKinds.map((kind) => ({ topic: "选题", title: "标题", cover: "封面", inner: "内页" }[kind])).join("、");
    toast(visualTemplate || analysis ? `${importedLabels}模板已入库` : `${importedLabels}模板未生成`);
  } catch (error) {
    if (isAbortError(error)) {
      draft.templateImportStatus = "pending";
      draft.templateImportError = "";
      reportProgress({
        stage: "cancelled",
        message: "模板拆解已中止",
      });
      return;
    }
    draft.templateImportStatus = "failed";
    const failureDetail = templateImportFailureDetail(error);
    draft.templateImportError = failureDetail;
    const failedStage = templateImportProgressState.stage;
    const failureLabel = templateImportFailureLabel(failedStage);
    reportProgress({
      stage: "failed",
      message: `${failureLabel}：${failureDetail}`,
      failedStage,
    });
    toast(`${failureLabel}：${error.message}`, { kind: "error", duration: 9000 });
  } finally {
    if (templateImportController === controller) {
      templateImportController = null;
      templateImportDraftId = "";
    }
    // 这一轮到此为止：无论中止还是跑完，都不该再显示「正在停止」。
    templateImportCancelling = false;
    templateImportJobId = "";
    done();
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    const pageResult = templateImportPageStatusSnapshot(templateImportProgressState.pageStatuses);
    if (pageResult.length) draft.templateImportPageStatuses = pageResult;
    // 中止/失败也要留下 pending 模板 id：它是"继续完成剩余页"唯一的凭据（服务端
    // retry-pages 按 templateId 从 pending 索引续做）。过去只在成功路径写（台账 P99）。
    const pendingTemplateId = templateIdFromImportProgress(templateImportProgressState);
    if (pendingTemplateId) draft.templateImportTemplateId = pendingTemplateId;
    updateTemplateImportProgress({});
    saveState();
    renderEditor();
  }
}

function collectViralBrief(draft = currentDraft()) {
  const topicPackage = topicPackageFromItem(selectedViralLibraryItem("topic"));
  const promptJobs = draftPromptImageJobs(draft, { includeExcluded: false });
  const pureAi = knowledgeChannelForDraft(draft) === "pure-ai";
  const confirmedPageCount = Number(draft?.pageExpansionReview?.confirmedPageCount || draft?.requestedPageCount || 0) || 0;
  const requestedPageCount = pureAi ? Math.max(1, promptJobs.length) : confirmedPageCount;
  const innerPageEntries = promptJobs
    .filter((job) => job.contentPart === "inner")
    .map((job) => {
      const sourceKind = job.sourceKind === "cover" ? "cover" : "inner";
      const sourceItem = (state.viralLibraries[sourceKind] || []).find((item) => String(item.templateId) === String(job.templateId));
      const sourceTemplate = templateSourceForJobs(draft, job.templateId, sourceKind);
      return {
        orderKey: job.key,
        sourcePageKey: job.sourcePageKey || templatePageKey(job.templateId, job.pageIndex),
        templateId: job.templateId,
        templatePage: Number(job.pageIndex) + 1,
        label: job.label || `内页 ${job.pageIndex + 1}`,
        sourceKind,
        templateName: job.sourceTemplateName || sourceItem?.name || sourceTemplate?.name || "",
        isAdditional: Boolean(job.isAdditional),
        template: compactViralLibraryItem(sourceItem || sourceTemplate),
      };
    });
  return {
    topic: topicPackage?.topic || topicPackage?.name || "",
    audience: topicPackage?.audience || "",
    pain: topicPackage?.pain || "",
    benefit: topicPackage?.benefit || "",
    category: topicPackage?.category || "",
    scenario: topicPackage?.scenario || "",
    facts: (topicPackage?.verifiedFacts || []).join("\n"),
    sourceCorpusId: draft?.knowledge?.corpusId || draft?.sourceCorpusId || "",
    knowledgeBasis: draft?.knowledgeBasis || {},
    forbidden: (topicPackage?.forbidden || []).join("\n"),
    topicPackageId: topicPackage?.id || "",
    topicPackage: topicPackage?.id ? topicPackage : {},
    coverTemplateId: selectedViralLibraryItem("cover")?.templateId || "",
    innerTemplateId: selectedViralLibraryItem("inner")?.templateId || "",
    templateSetId: state.viralLibrarySelections.templateSetId || "",
    excludedTemplatePages: [...draftExcludedTemplatePageKeys(draft)],
    imagePageOrder: Array.isArray(draft?.imagePageOrder) ? [...draft.imagePageOrder] : [],
    innerPageOrder: promptJobs.filter((job) => job.contentPart === "inner").map((job) => job.key),
    innerPageEntries,
    requestedPageCount,
    // Pure AI must fit the pages the user selected. Grounded material may still
    // request expansion so source scope is not silently discarded.
    pageCountPolicy: pureAi ? "exact" : (confirmedPageCount > 0 ? "truncate" : "auto"),
  };
}

function filterDraftPlanExcludedPages(draft, plan) {
  return executionPlanForWrite(plan);
}

// The async job result is dropped entirely when it exceeds the server cap
// (XHS_ASYNC_RESULT_MAX_BYTES), and a missing plan used to degrade into NaN
// page counts plus a misleading "执行计划版本已失效".  Fail loudly with the real
// reason instead of inventing numbers.
function viralPlanFromJobResult(data) {
  const plan = data?.executionPlan || data?.recipe?.compiledOutput;
  if (plan && typeof plan === "object") return plan;
  const code = String(data?.resultErrorCode || "");
  if (data?.resultPersisted === false && code === "result_too_large") {
    throw new Error("生成结果超过服务端 1MB 上限未能返回，请减少页数或资料量后重试");
  }
  if (data?.resultPersisted === false) {
    throw new Error("生成结果未返回（" + (code || "result_unavailable") + "），请重试");
  }
  throw new Error("生成结果里没有执行计划，请重新搭建笔记内容");
}

function executionPlanForSeries(plan, execution) {
  if (!execution || !Array.isArray(execution.pageScript)) return plan;
  const seriesId = execution.seriesId || "";
  const pageScript = execution.pageScript.map((page, index) => ({ ...page, page: index + 1 }));
  const pageTasks = Array.isArray(execution.pageTasks) ? execution.pageTasks : [];
  const lockedPageCopies = Array.isArray(execution.lockedPageCopies) ? execution.lockedPageCopies : [];
  const sourceTaskSet = plan.pageTaskSet && typeof plan.pageTaskSet === "object" ? plan.pageTaskSet : {};
  const sourceTaskSeries = (sourceTaskSet.series || []).find((item) => String(item?.seriesId || "") === String(seriesId)) || {};
  const pageTaskSet = {
    ...sourceTaskSet,
    series: [{
      ...sourceTaskSeries,
      seriesId,
      seriesGroupId: execution.seriesGroupId || sourceTaskSeries.seriesGroupId || sourceTaskSet.seriesGroupId || "",
      seriesIndex: Number(execution.seriesIndex) || Number(sourceTaskSeries.seriesIndex) || 1,
      seriesCount: Number(execution.seriesCount) || Number(sourceTaskSeries.seriesCount) || 1,
      title: execution.title || sourceTaskSeries.title || "",
      tasks: pageTasks,
      status: execution.status || sourceTaskSeries.status || "planned",
    }],
    stats: {
      ...(sourceTaskSet.stats || {}),
      seriesCount: 1,
      taskCount: pageTasks.length,
      innerTaskCount: pageTasks.filter((task) => task?.pageRole === "inner").length,
    },
  };
  const sourceLockedSet = plan.lockedCopySet && typeof plan.lockedCopySet === "object" ? plan.lockedCopySet : {};
  const lockedCopySet = {
    ...sourceLockedSet,
    status: execution.status === "passed" ? "passed" : "failed",
    generationStatus: execution.generationStatus || (lockedPageCopies.length === pageTasks.length && pageTasks.length ? "complete" : "partial"),
    series: [execution],
    stats: {
      ...(sourceLockedSet.stats || {}),
      plannedSeriesCount: 1,
      completedSeriesCount: execution.generationStatus === "complete" || (lockedPageCopies.length === pageTasks.length && pageTasks.length) ? 1 : 0,
      failedSeriesCount: execution.status === "passed" ? 0 : 1,
      lockedPageCount: lockedPageCopies.length,
    },
  };
  const basePageCount = Math.max(1, Math.min(Number(plan.pageExpansion?.basePageCount) || 2, pageScript.length));
  const additionalPages = pageScript.slice(basePageCount).map((page, index) => ({
    id: `auto-${execution.seriesId || "series"}-${index + 1}`,
    templateId: page.templateId || "",
    pageIndex: Math.max(0, Number(page.templatePage || 1) - 1),
    sourceKind: "inner",
    sourcePageKey: page.sourcePageKey || templatePageKey(page.templateId, Math.max(0, Number(page.templatePage || 1) - 1)),
    label: page.pageLabel || `内页 ${index + basePageCount}`,
    orderKey: page.orderKey || `auto:${page.page}:${page.templateId}:${page.templatePage}`,
    isAdditional: true,
  }));
  return {
    ...plan,
    activeSeriesId: seriesId,
    pageTaskSet,
    lockedCopySet,
    seriesExecutions: [execution],
    pageScript,
    pageTasks,
    lockedPageCopies,
    generatedContent: execution.generatedContent || {},
    pageExpansion: {
      ...(plan.pageExpansion || {}),
      basePageCount,
      requiredPageCount: pageScript.length,
      expandedPageCount: pageScript.length,
      additionalPages,
      additionalTemplatePages: additionalPages,
      innerPageOrder: additionalPages.map((item) => item.orderKey),
    },
  };
}

function linkedSeriesData(data) {
  const plan = data?.executionPlan || data?.recipe?.compiledOutput || {};
  const executions = Array.isArray(plan.seriesExecutions) ? plan.seriesExecutions : [];
  if (!executions.length) return [{ data, plan, execution: null }];
  return executions.map((execution) => {
    const seriesPlan = executionPlanForSeries(plan, execution);
    return {
      execution,
      plan: seriesPlan,
      data: {
        ...data,
        executionPlan: seriesPlan,
        recipe: data.recipe ? { ...data.recipe, compiledOutput: seriesPlan } : data.recipe,
      },
    };
  });
}

function applyLinkedSeriesPlans(data, sourceDraft = null, { reuseSource = false } = {}) {
  const entries = linkedSeriesData(data);
  const groupId = data?.executionPlan?.seriesPlan?.seriesGroupId || data?.executionPlan?.lockedCopySet?.seriesGroupId || "";
  const originalActiveId = state.activeDraftId;
  const drafts = [];
  entries.forEach((entry, index) => {
    const seriesId = entry.execution?.seriesId || entry.plan.activeSeriesId || "";
    let draft = reuseSource && index === 0 ? sourceDraft : state.drafts.find((item) => (
      groupId && item.seriesGroupId === groupId && item.seriesId === seriesId
    ));
    if (!draft) {
      const brief = entry.data.recipe?.brief || {};
      draft = createDraft({
        source: "viral",
        origin: sourceDraft?.origin || "",
        images: [],
        title: brief.topic || "",
        body: brief.facts || "",
        // 与选题预览同一口径：「未分类」是详情里的展示兜底，不能当成标签落进草稿。
        tags: brief.category && !isUncategorizedCategory(brief.category) ? `#${brief.category.replace(/^#/, "")}` : "",
      });
      if (sourceDraft?.knowledge) draft.knowledge = JSON.parse(JSON.stringify(sourceDraft.knowledge));
      else if (state.pendingLocalKnowledge) draft.knowledge = JSON.parse(JSON.stringify(state.pendingLocalKnowledge));
      draft.knowledgeChannel = knowledgeChannelForDraft(sourceDraft);
      draft.knowledgeSource = knowledgeSourceForDraft(sourceDraft);
      draft.sourceImages = [...(sourceDraft?.images || [])];
    }
    applyViralPlanToDraft(draft, entry.data);
    draft.seriesGroupId = groupId;
    draft.seriesId = seriesId;
    draft.seriesIndex = Number(entry.execution?.seriesIndex) || index + 1;
    draft.seriesCount = Number(entry.execution?.seriesCount) || entries.length;
    drafts.push(draft);
  });
  if (drafts.length) setActiveDraftId(drafts[0].id);
  else setActiveDraftId(originalActiveId);
  saveState();
  renderAll();
  return drafts;
}

function applyViralPlanToDraft(draft, data) {
  if (!draft) return;
  const plan = viralPlanFromJobResult(data);
  const brief = data?.recipe?.brief || collectViralBrief(draft);
  const topicItem = selectedViralLibraryItem("topic");
  const titleItem = selectedViralLibraryItem("title");
  const coverItem = selectedViralLibraryItem("cover");
  const innerItem = selectedViralLibraryItem("inner");
  const previousJobs = draftImageJobs(draft, { includeExcluded: true });
  const expansion = plan.pageExpansion && typeof plan.pageExpansion === "object" ? plan.pageExpansion : {};
  const requiredPageCount = Math.max(0, Number(expansion.requiredPageCount) || 0);
  const basePageCount = Math.max(1, Number(expansion.basePageCount) || 1);
  const requestedPageCount = Math.max(0, Number(draft.pageExpansionReview?.confirmedPageCount || draft.requestedPageCount) || 0);
  const autoConfirmExpansion = draft.pageExpansionReview?.status === "replanning"
    && requestedPageCount >= basePageCount
    && requiredPageCount === requestedPageCount;
  if (requiredPageCount > basePageCount && draft.pageExpansionReview?.status !== "confirmed" && !autoConfirmExpansion) {
    draft.pageExpansionReview = {
      status: "pending",
      basePageCount,
      estimatedPageCount: requiredPageCount,
      minPageCount: basePageCount,
      maxPageCount: Math.min(MAX_DRAFT_TEMPLATE_PAGES, Number(expansion.maxPageCount) || MAX_DRAFT_TEMPLATE_PAGES),
      additionalPageCount: requiredPageCount - basePageCount,
      pages: expansionTemplatePagesForReview(draft, requiredPageCount - basePageCount),
      updatedAt: new Date().toISOString(),
    };
  } else if (requiredPageCount <= basePageCount) {
    delete draft.pageExpansionReview;
  } else if (autoConfirmExpansion) {
    draft.pageExpansionReview = {
      ...(draft.pageExpansionReview || {}),
      status: "confirmed",
      confirmedPageCount: requestedPageCount,
      confirmedAt: new Date().toISOString(),
    };
  }
  applyKnowledgePageExpansion(draft, plan);
  draft.executionPlan = filterDraftPlanExcludedPages(draft, plan);
  draft.pageCopyEdits = {};
  // 新计划 = 新文字，旧的「确认全部页面」签收据此作废，必须重新确认。
  delete draft.visualCopyReview;
  delete draft.executionPlanInvalidation;
  draft.imagePageOrder = Array.isArray(brief.imagePageOrder)
    ? [...brief.imagePageOrder]
    : Array.isArray(draft.imagePageOrder) ? [...draft.imagePageOrder] : [];
  draft.promptMode = {
    title: false,
    body: false,
    tags: false,
    cover: Boolean(plan.coverPrompt || plan.imagePrompt),
    inner: Boolean(plan.innerPrompt || plan.imagePrompt),
  };
  draft.generatedContentEdits = {};
  // Keep the user's current title/body/tags untouched until one-click
  // generation explicitly applies the newly built content.
  draft.sourceCorpusId = plan.sourceCorpusId || draft.knowledge?.corpusId || draft.sourceCorpusId || "";
  if (draft.knowledge?.decision === "grounded" && draft.knowledge.mode !== "local" && draft.sourceCorpusId) {
    draft.knowledge.corpusId = draft.sourceCorpusId;
  }
  draft.knowledgeBasis = plan.knowledgeBasis || draft.knowledgeBasis || {};
  draft.pageExpansion = plan.pageExpansion || draft.pageExpansion || {};
  draft.viralBrief = {
    ...brief,
    excludedTemplatePages: [...draftExcludedTemplatePageKeys(draft)],
    imagePageOrder: [...draft.imagePageOrder],
    innerPageOrder: Array.isArray(draft.innerPageOrder) ? [...draft.innerPageOrder] : [],
  };
  draft.viralRecipeId = data?.recipe?.id || "";
  draft.viralAtomIds = data?.recipe?.atomIds || [];
  // 发布列表的「保存表现」表单只对带生成记录的条目渲染（publish/list.js），
  // /api/viral/performance 也靠它把表现回流到模板原子统计。这个字段以前只落 recipe
  // 和 atom，generation 一直没落，表单等于永远不出现。
  draft.viralGenerationId = data?.generation?.id || "";
  draft.viralCategory = brief.category || "";
  draft.topicPackageId = plan.topicPackage?.id || brief.topicPackageId || "";
  draft.librarySelection = { ...(data?.recipe?.librarySelection || state.viralLibrarySelections) };
  draft.librarySnapshots = {
    topic: compactViralLibraryItem(topicItem),
    title: compactViralLibraryItem(titleItem),
    cover: compactViralLibraryItem(coverItem),
    inner: compactViralLibraryItem(innerItem),
  };
  draft.coverTemplateId = brief.coverTemplateId || coverItem?.templateId || "";
  draft.innerTemplateId = brief.innerTemplateId || innerItem?.templateId || "";
  draft.layoutTemplateId = draft.innerTemplateId;
  draft.templateSetId = brief.templateSetId || state.viralLibrarySelections.templateSetId || "";
  draft.templateReferences = {
    cover: { templateId: draft.coverTemplateId, images: coverItem?.referenceImages || [], aiImage: coverItem?.aiImage || {} },
    inner: { templateId: draft.innerTemplateId, images: innerItem?.referenceImages || [], aiImage: innerItem?.aiImage || {} },
  };
  // Re-run after template IDs are stored so auto pages can resolve their source references.
  applyKnowledgePageExpansion(draft, plan);
  if (!alignDraftImageJobsToExecutionPlan(draft, plan)) {
    invalidateDraftExecutionPlan(draft, "page-image-binding-mismatch");
  }
  reorderDraftAssetArrays(draft, previousJobs, draftImageJobs(draft, { includeExcluded: true }), { includeNewReferences: true });
  draft.templateRenderMode = "ai-pending";
  draft.imageGenerationError = "";
  draft.imageGenerationFailedLabels = [];
  draft.imageGenerationFailedJobKeys = [];
  saveState();
}

function viralPromptCanUsePureAi(message = "") {
  return /当前主题没有形成有效知识节点|没有形成有效知识节点|知识架构.*分页|资料不足.*知识|知识.*资料不足|知识图文不能在无参考资料|知识图文必须先确认可用参考资料|请选择本地资料或云端知识依据/.test(String(message || ""));
}

function viralPromptTaskBelongsToRewriteDraft(draft = currentDraft(), ownerDraftId = state.viralComposeSourceDraftId) {
  // 判据只看草稿本身，不看 state.activeWorkflow：setSection("rewrite") 并不设置那个
  // workflow，从低粉采集/生图等工具页切回编辑器时它还是 "tools"，于是"不是当前工作流"把
  // 正在跑的搭建判成不属于这份草稿——进度面板不显示，按钮还被 renderEditor 改回
  // 「搭建笔记内容」。用户看到的就是"点了确定什么都没发生"，而服务端任务在跑、在计费。
  return Boolean(
    draft
    && draftWorkflow(draft) === "original"
    && String(draft.id || "") === String(ownerDraftId || ""),
  );
}

function syncRewriteViralPromptTaskVisibility(draft = currentDraft()) {
  document.querySelectorAll('[data-viral-prompt-task="rewrite"]').forEach((panel) => {
    const ownerDraftId = panel.dataset.taskOwnerDraftId || state.viralComposeSourceDraftId;
    const belongsToDraft = viralPromptTaskBelongsToRewriteDraft(draft, ownerDraftId);
    const requestedVisible = panel.dataset.taskVisible === "true";
    panel.classList.toggle("hidden", !requestedVisible || !belongsToDraft);
  });
}

function syncRewriteViralPromptButton(draft = currentDraft()) {
  const button = $("#rewriteBtn");
  if (!button || !viralPromptBuildController || !usesFourLibraryCompose(draft)) return;
  const hasRunningTask = Boolean(viralPromptBuildController);
  const ownsRunningTask = Boolean(
    hasRunningTask
    && viralPromptTaskBelongsToRewriteDraft(draft),
  );
  // 这个按钮一律保持可点：冲突一律留到 composeViralDraft 入口,点下去给一句点名的提示。
  // 与主生成按钮同一口径（"灰着又没有任何说明，用户只能猜"）。自己持有搭建运行时
  // 文案变"停止搭建"，那是可点的取消入口，不是置灰。
  button.disabled = false;
  button.textContent = ownsRunningTask ? "停止搭建" : "按四库搭建内容";
  button.title = ownsRunningTask ? "停止当前笔记内容搭建" : "";
  button.setAttribute("aria-busy", String(ownsRunningTask));
  button.classList.toggle("is-loading", ownsRunningTask);
  button.classList.toggle("is-cancel-action", ownsRunningTask);
}

function updateViralPromptTask(progress = {}, {
  hide = false,
  error = false,
  allowPureAi = false,
  ownerDraftId = state.viralComposeSourceDraftId,
} = {}) {
  document.querySelectorAll("[data-viral-prompt-task]").forEach((panel) => {
    const requestedVisible = !hide;
    if (panel.dataset.viralPromptTask === "rewrite" && ownerDraftId) {
      panel.dataset.taskOwnerDraftId = String(ownerDraftId);
    }
    const panelOwnerDraftId = panel.dataset.taskOwnerDraftId || ownerDraftId;
    const belongsToDraft = panel.dataset.viralPromptTask !== "rewrite"
      || viralPromptTaskBelongsToRewriteDraft(currentDraft(), panelOwnerDraftId);
    const visible = requestedVisible && belongsToDraft;
    panel.dataset.taskVisible = String(requestedVisible);
    panel.classList.toggle("hidden", !visible);
    panel.classList.toggle("is-error", Boolean(error));
    panel.classList.toggle("is-complete", progress.stage === "completed" && !error);
    const pureAiButton = panel.querySelector("[data-viral-prompt-pure-ai]");
    if (pureAiButton) pureAiButton.classList.toggle("hidden", !visible || !allowPureAi);
    if (hide) return;
    const completedPages = Math.max(0, Number(progress.completedPages || 0));
    const totalPages = Math.max(0, Number(progress.totalPages || 0));
    const receivedChars = Math.max(0, Number(progress.receivedChars || 0));
    const details = [];
    if (totalPages) details.push(`逐页文案任务 ${completedPages}/${totalPages}`);
    if (receivedChars) details.push(`已接收 ${receivedChars} 字`);
    const message = String(progress.message || "正在搭建笔记内容").trim();
    const taskText = details.length ? `${message}（${details.join("，")}）` : message;
    const label = panel.querySelector("[data-viral-prompt-task-label]");
    if (label) label.textContent = taskText;
  });
}

async function retryViralPromptWithPureAi(event) {
  event?.preventDefault();
  if (viralPromptBuildController) return;
  const draft = currentDraft();
  const pureAiKnowledge = {
    decision: "fallback",
    mode: "pure-ai",
    selections: [],
    sources: [],
    stale: false,
    confirmedAt: new Date().toISOString(),
  };
  if (draft) draft.knowledge = pureAiKnowledge;
  else state.pendingLocalKnowledge = pureAiKnowledge;
  state.localUploadQueue = [];
  saveState();
  renderEditor();
  toast("已关闭本次资料参照，正在改用纯 AI 重新搭建");
  // 用户点「改用纯 AI 重新搭建」本身就是重跑的明确意图，不再叠一层覆盖确认（P118）。
  await composeViralDraft({
    preventDefault() {},
    currentTarget: $("#rewriteBtn"),
    // 这个按钮的文案已经写明要重新搭建，再问一遍是重复确认。
    recomposeConfirmed: true,
  });
}

// 编辑器主按钮的搭建阶段（#rewriteBtn）是整份替换：重跑会先用新分析
// 结果重置已确认页数、过滤掉自动追加页并重排内页/图片顺序，成功后整份替换
// executionPlan（标题、正文、标签提示词与逐页文字都在里面），失败路径不回滚这些重置。
// 所以已经搭建过的草稿再点一次必须先让用户确认；首次搭建不打扰。四库页那条入口
// （#composeViralBtn）原先在一个 `display:none !important` 的列里，已随该列一并移除——
// 现在「搭建笔记内容」只有编辑器这一条入口，`fromRewrite` 因此恒为 true。
const VIRAL_RECOMPOSE_CONFIRM_MESSAGE = "这篇笔记已经搭建过内容。重新搭建会重置已确认的页数、清掉自动追加页与图片顺序，并用新结果替换现有的标题、正文、标签提示词和逐页文字。确定重新搭建吗？";

// 搭建失败时把草稿还原成"动手之前"：只还原这段流程真的改过的那几个字段，其余不动。
// recipe 与 generation 是一对，必须一起还原，否则留下"新生成记录 + 旧配方"的错配。
function restoreViralPlanAfterFailedCompose(draft, previousPlan) {
  if (!draft || !previousPlan) return false;
  draft.executionPlan = previousPlan.executionPlan;
  draft.viralRecipeId = previousPlan.viralRecipeId;
  draft.viralGenerationId = previousPlan.viralGenerationId || "";
  draft.templateRenderMode = previousPlan.templateRenderMode;
  draft.imageGenerationError = previousPlan.imageGenerationError;
  return true;
}

function viralRecomposeNeedsConfirm({ draft, fromRewrite, expansionResume, event } = {}) {
  if (!fromRewrite || expansionResume) return false;
  // 纯 AI 重试与知识确认后的续跑都是用户已经表达过意图的重入，不能再问第二遍。
  if (event?.recomposeConfirmed === true) return false;
  return Boolean(draft?.executionPlan);
}

function confirmViralRecompose({ draft, fromRewrite, expansionResume, event, confirm } = {}) {
  if (!viralRecomposeNeedsConfirm({ draft, fromRewrite, expansionResume, event })) return true;
  const ask = typeof confirm === "function"
    ? confirm
    : (typeof window !== "undefined" && typeof window.confirm === "function" ? window.confirm : null);
  // 没有确认能力的环境（无 window 的静态/行为测试）不拦：宁可放行，也不静默卡死。
  if (typeof ask !== "function") return true;
  return ask(VIRAL_RECOMPOSE_CONFIRM_MESSAGE) !== false;
}

async function composeViralDraft(event) {
  event?.preventDefault();
  const expansionResume = Boolean(event?.expansionResume);
  const activeDraft = currentDraft();
  if (!expansionResume && activeDraft?.pageExpansionReview?.status === "pending") {
    if (knowledgeChannelForDraft(activeDraft) === "pure-ai") {
      activeDraft.requestedPageCount = 0;
      delete activeDraft.pageExpansionReview;
      saveState();
      renderEditor();
    } else {
      toast("请先确认页数，确认后会自动继续当前任务");
      return;
    }
  }
  if (viralPromptBuildController) {
    const selectedDraftId = String(currentDraft()?.id || "");
    const ownerDraftId = String(state.viralComposeSourceDraftId || "");
    if (selectedDraftId && selectedDraftId === ownerDraftId && !viralPromptBuildController.signal.aborted) {
      viralPromptBuildController.abort();
      toast("正在停止搭建笔记内容...");
    } else {
      toast("另一个原创任务正在搭建笔记内容，请切回该任务查看或停止");
    }
    return;
  }
  const fromRewrite = event?.currentTarget?.id === "rewriteBtn";
  const sourceDraft = currentDraft();
  if (!capabilityEnabled("viralCompose")) {
    toast("内容搭建当前不可用，请稍后再试", { kind: "error" });
    return;
  }
  // 生成图文/生图/模板拆解在跑时不能开始搭建：两边写的是同一份 executionPlan 与图序，
  // 同时跑会互相覆盖（现场：一个任务在生成，另一个在搭建）。判据与生成入口共用同一套
  // 每草稿任务注册表（draftGenerationBusy），不另造状态。这段必须排在上面"自己正在搭建"
  // 那段之后：点自己的搭建仍然是"停止"。
  if (sourceDraft && typeof draftGenerationBusy === "function" && draftGenerationBusy(sourceDraft.id)) {
    const elsewhereMessage = typeof aiTaskRunningElsewhereMessage === "function"
      ? aiTaskRunningElsewhereMessage(sourceDraft.id)
      : "";
    toast(elsewhereMessage || "当前草稿正在生成图文或图片，请等它结束或先点停止，再搭建笔记内容");
    return;
  }
  // 二次确认必须排在下面那段重置之前：重置在发请求前就把页数、自动追加页和图序改掉了，
  // 先重置再问等于问了个寂寞。
  if (!confirmViralRecompose({ draft: sourceDraft, fromRewrite, expansionResume, event })) return;
  // 用户点了「搭建笔记内容」＝这个槽里接下来装的是刚搭出来的新内容，上一次入库的拆解记录
  // 到此为止。排在确认之后：用户取消这次重新搭建时，上一份内容的拆解记录不该已经没了。
  if (fromRewrite) retireTemplateImportRecordForDraft(sourceDraft);
  if (!expansionResume && sourceDraft) {
    const priorReview = sourceDraft.pageExpansionReview;
    const hasPriorRequestedCount = Number(sourceDraft.requestedPageCount) > 0
      || priorReview?.status === "confirmed";
    if (hasPriorRequestedCount) {
      pageExpansionCancelBackups.delete(String(sourceDraft.id || ""));
      sourceDraft.requestedPageCount = 0;
      delete sourceDraft.pageExpansionReview;
      sourceDraft.additionalTemplatePages = draftAdditionalTemplatePages(sourceDraft)
        .filter((item) => item.autoGenerated !== true && !/^auto-(?:inner-|series[-_])/.test(String(item.id || "")));
      const basePageCount = Math.max(1, draftPromptImageJobs(sourceDraft, { includeExcluded: true }).length);
      sourceDraft.pageExpansion = {
        ...(sourceDraft.pageExpansion || {}),
        basePageCount,
        requiredPageCount: basePageCount,
        expandedPageCount: basePageCount,
        additionalPages: [],
        additionalTemplatePages: [],
      };
      sourceDraft.innerPageOrder = draftImageJobs(sourceDraft, { includeExcluded: true })
        .filter((job) => job.contentPart === "inner").map((job) => job.key);
      sourceDraft.imagePageOrder = draftImageJobs(sourceDraft, { includeExcluded: true }).map((job) => job.key);
      saveState();
    }
  }
  if (sourceDraft) {
    restoreViralSelectionsFromDraft(sourceDraft);
    const hasLoadedLibrary = Object.values(state.viralLibraries).some((items) => Array.isArray(items) && items.length);
    if (!hasLoadedLibrary || !selectedViralLibraryItem("topic")) {
      await loadViralLibraries({ recommend: false });
      restoreViralSelectionsFromDraft(sourceDraft);
    }
    // 选题/模板守卫必须排在 applyViralSelectionPreviewToDraft 之前：那次重算是按
    // 「新选择」重建图集的，缺选择时会把用户刚加进编辑器的图片清空，然后才弹提示。
    // 先丢图再提示对用户是纯损失；顺序反过来后守卫直接拦下，草稿完全不动。
    const requiredKinds = fromRewrite ? ["topic", "title", "cover", "inner"] : ["topic", "cover", "inner"];
    const missingKinds = requiredKinds.filter((kind) => !selectedViralLibraryItem(kind));
    if (missingKinds.length) {
      if (fromRewrite) beginViralComposeLibraryGuide(sourceDraft.id);
      if (missingKinds.includes("topic")) {
        if (fromRewrite) setSection("viral");
        setViralWorkbench("topic");
        toast("请先从选题库选择一个选题");
        return;
      }
      const nextKind = missingKinds[0];
      const group = viralLibraryGroupForKind(nextKind);
      state.viralActiveLibraryKinds[group] = nextKind;
      if (fromRewrite) setSection("viral");
      renderFourLibraryWorkbench();
      setViralWorkbench(group);
      toast(fromRewrite
        ? `请先选择${viralLibraryMeta[nextKind].label}，选择后将自动进入下一项`
        : `请先选择${missingKinds.map((kind) => viralLibraryMeta[kind].label).join("、")}`);
      return;
    }
    if (fromRewrite) {
      applyViralSelectionPreviewToDraft(sourceDraft);
      saveState();
      renderEditor();
    }
  }
  const brief = collectViralBrief(sourceDraft || currentDraft());
  if (!brief.topic) {
    if (fromRewrite) setSection("viral");
    setViralWorkbench("topic");
    toast("请先从选题库选择一个选题");
    return;
  }
  if (state.localUploadQueue.length) {
    try {
      updateViralPromptTask(
        { stage: "preparing", message: "正在临时读取本地参考资料", percent: 1 },
        { ownerDraftId: sourceDraft?.id || "" },
      );
      const prepared = await prepareSelectedLocalReferences(sourceDraft, brief.topic);
      if (!prepared) {
        updateViralPromptTask({}, { hide: true, ownerDraftId: sourceDraft?.id || "" });
        return;
      }
      if (sourceDraft) {
        saveState();
        renderEditor();
      }
    } catch (error) {
      updateViralPromptTask({}, { hide: true, ownerDraftId: sourceDraft?.id || "" });
      toast(`本地资料准备失败：${error.message}`, { kind: "error", duration: 7000 });
      return;
    }
  }
  if (sourceDraft && requireGroundedKnowledgeDecision(sourceDraft, () => composeViralDraft({
    preventDefault() {},
    currentTarget: $("#rewriteBtn"),
    // 资料口径确认后的续跑：用户在本轮点击时已经确认过重新搭建，这里不再问。
    recomposeConfirmed: true,
  }))) return;
  const atomIds = Object.values(state.viralSelections || {}).filter(Boolean);
  const button = $("#rewriteBtn");
  const previousGeneration = state.viralLastGeneration;
  const previousComposeSourceDraftId = state.viralComposeSourceDraftId;
  const previousDraftPlan = sourceDraft ? {
    executionPlan: sourceDraft.executionPlan,
    viralRecipeId: sourceDraft.viralRecipeId,
    viralGenerationId: sourceDraft.viralGenerationId,
    templateRenderMode: sourceDraft.templateRenderMode,
    imageGenerationError: sourceDraft.imageGenerationError,
  } : null;
  state.viralLastGeneration = null;
  if (fromRewrite && requiresViralTemplateFit(sourceDraft)) {
    sourceDraft.executionPlan = null;
    sourceDraft.viralRecipeId = "";
    sourceDraft.viralGenerationId = "";
    sourceDraft.templateRenderMode = "ai-pending";
    sourceDraft.imageGenerationError = "";
    saveState();
    renderEditor();
  }
  const controller = new AbortController();
  viralPromptBuildController = controller;
  state.viralComposeFromRewrite = fromRewrite;
  state.viralComposeSourceDraftId = sourceDraft?.id || null;
  const done = setButtonCancelable(button, "停止搭建", "停止当前笔记内容搭建");
  // 搭建写的是整份 executionPlan：登记进每草稿任务注册表（full 语义），生成图文、
  // 单区生成、生图与单图入口据此在搭建期间全部锁住，finally 里对称释放。登记语句
  // 必须待在下面的 try 里——它和 try 之间抛一次错，这份草稿的 full 计数就永远减不
  // 回去（生成入口被永久置灰），与 generate.js 里同款告诫一致。
  let composeGenerationRun = null;
  // 任务一旦提交就一定会在服务端跑完并计费，和浏览器后面能不能拿到结果无关。记下编号，
  // 失败时才能如实告诉用户"这次已经付出去了"，避免他再点一次等于再扣一次钱。
  let submittedJobId = "";
  const noteSubmittedJob = (submitted) => {
    submittedJobId = String(submitted?.jobId || "") || submittedJobId;
  };
  updateViralPromptTask(
    { stage: "preparing", message: "正在整理模板和内容规则" },
    { ownerDraftId: sourceDraft?.id || "" },
  );
  try {
    composeGenerationRun = sourceDraft && typeof beginDraftGenerationRun === "function"
      ? beginDraftGenerationRun(sourceDraft.id, "full")
      : null;
    // 登记之后立刻刷一次 editor：分区/图片入口的置灰只在 renderEditor 里算，不刷的话
    // 要等第一次进度回调才灰，中间那段窗口按钮仍是亮的。
    if (sourceDraft) renderEditor();
    const requestKnowledge = knowledgeForRequest(sourceDraft);
    const pureAiMode = requestKnowledge?.mode === "pure-ai";
    const planningData = await postBackend("/api/viral/combinations/save", {
      brief,
      atomIds,
      selection: state.viralLibrarySelections,
      advancedAtomOverrides: state.viralAdvancedOverrides,
      recipeName: `${brief.topic}组合配方`,
      knowledge: requestKnowledge,
      allowKnowledgeFallback: requestKnowledge?.decision === "fallback" && requestKnowledge?.mode === "pure-ai",
      planningOnly: !expansionResume,
    }, {
      signal: controller.signal,
      onProgress: (progress) => updateViralPromptTask(progress, { ownerDraftId: sourceDraft?.id || "" }),
      onJobStart: noteSubmittedJob,
      timeoutMinutes: 25,
      timeoutLabel: "逐页文案任务",
    });
    throwIfAborted(controller.signal);
    state.viralLastGeneration = planningData;
    const plannedExecution = viralPlanFromJobResult(planningData);
    const plannedBasePages = Math.max(1, Number(plannedExecution.pageExpansion?.basePageCount) || draftPromptImageJobs(sourceDraft, { includeExcluded: true }).length || 1);
    // Math.max(0, NaN) is NaN, and that NaN used to travel all the way into the
    // "分析为 NaN 页" banner, so every fallback is coerced before the max().
    const plannedInnerPages = Math.max(
      0,
      Number(plannedExecution.pagePlan?.stats?.innerPageCount)
        || Number(plannedExecution.seriesPlan?.stats?.totalInnerPages)
        || Math.max(0, (Number(plannedExecution.pageExpansion?.requiredPageCount) || 0) - 1),
    );
    const analyzedRequiredPages = Math.max(
      plannedBasePages,
      plannedInnerPages + 1,
      Number(plannedExecution.pageExpansion?.requiredPageCount) || 0,
    );
    const plannedSuggestedPages = Math.min(MAX_DRAFT_TEMPLATE_PAGES, analyzedRequiredPages);
    const pageCountMismatch = analyzedRequiredPages !== plannedBasePages;
    if (!expansionResume && !pureAiMode && pageCountMismatch) {
      if (fromRewrite) {
        pageExpansionCancelBackups.set(String(sourceDraft.id || ""), {
          previousGeneration,
          previousComposeSourceDraftId,
          previousDraftPlan,
        });
        sourceDraft.pageExpansionReview = {
          ...(sourceDraft.pageExpansionReview || {}),
          status: "pending",
          basePageCount: plannedBasePages,
          analyzedPageCount: analyzedRequiredPages,
          suggestedPageCount: plannedSuggestedPages,
          estimatedPageCount: analyzedRequiredPages,
          selectedPageCount: plannedBasePages,
          additionalPageCount: plannedSuggestedPages - plannedBasePages,
          pages: [],
          expansionDecision: "keep-current",
          resumeFromRewrite: true,
          updatedAt: new Date().toISOString(),
        };
        saveState();
        renderEditor();
      }
      updateViralPromptTask(
        { stage: "waiting-confirmation", message: `${knowledgeChannelForDraft(sourceDraft) === "pure-ai" ? "AI 内容规划" : "完整资料分析"}为 ${analyzedRequiredPages} 页，等待确认单篇页数`, totalPages: analyzedRequiredPages, completedPages: 0 },
        { ownerDraftId: sourceDraft?.id || "" },
      );
      toast(`${knowledgeChannelForDraft(sourceDraft) === "pure-ai" ? "AI 内容规划" : "完整资料分析"}需要 ${analyzedRequiredPages} 页，请确认单篇生成页数`);
      return;
    }
    let completedData = planningData;
    if (!expansionResume) {
      updateViralPromptTask(
        { stage: "generating", message: "页数一致，正在生成逐页文字", totalPages: plannedBasePages, completedPages: 0 },
        { ownerDraftId: sourceDraft?.id || "" },
      );
      const generationBrief = {
        ...brief,
        requestedPageCount: plannedBasePages,
        pageCountPolicy: pureAiMode ? "exact" : "truncate",
      };
      completedData = await postBackend("/api/viral/combinations/save", {
        brief: generationBrief,
        atomIds,
        selection: state.viralLibrarySelections,
        advancedAtomOverrides: state.viralAdvancedOverrides,
        recipeName: `${brief.topic}组合配方`,
        knowledge: requestKnowledge,
        allowKnowledgeFallback: pureAiMode,
        planningOnly: false,
      }, {
        signal: controller.signal,
        onProgress: (progress) => updateViralPromptTask(progress, { ownerDraftId: sourceDraft?.id || "" }),
        onJobStart: noteSubmittedJob,
        timeoutMinutes: 25,
        timeoutLabel: "逐页文案任务",
      });
      throwIfAborted(controller.signal);
      state.viralLastGeneration = completedData;
    }
    if (fromRewrite) {
      applyViralPlanToDraft(sourceDraft, completedData);
      pageExpansionCancelBackups.delete(String(sourceDraft.id || ""));
      saveState();
      renderEditor();
    }
    const plannedPages = completedData.executionPlan?.pageTaskSet?.stats?.taskCount || 0;
    updateViralPromptTask({
      stage: "completed",
      message: "笔记内容与逐页文字已生成，可检查修改",
      completedPages: plannedPages,
      totalPages: plannedPages,
    }, { ownerDraftId: sourceDraft?.id || "" });
    toast("已完成单篇笔记内容与逐页文字，可检查修改后再一键生图");
  } catch (error) {
    // 从上面那次 executionPlan = null 起，旧计划就已经离开草稿，而新计划要等两次调用都
    // 成功才会写回。以前只有"用户主动停止"这一条路径会还原，其余失败——任务超时、响应
    // 读不回来、状态接口一直读不到——都会把标题/正文/标签提示词和逐页文字永久留在"空
    // 计划"上。实测现场：服务端任务跑完并扣了费，浏览器什么都没拿到，用户的提示词没了。
    const restored = restoreViralPlanAfterFailedCompose(sourceDraft, previousDraftPlan);
    state.viralLastGeneration = previousGeneration;
    state.viralComposeSourceDraftId = previousComposeSourceDraftId;
    if (restored) {
      saveState();
      renderEditor();
    }
    if (isAbortError(error)) {
      updateViralPromptTask({}, { hide: true });
      toast("已停止搭建笔记内容，原计划已保留");
    } else {
      const message = `笔记内容搭建失败：${error.message}`;
      if (expansionResume && sourceDraft?.pageExpansionReview?.status === "confirmed") {
        sourceDraft.pageExpansionReview.status = "pending";
        sourceDraft.pageExpansionReview.updatedAt = new Date().toISOString();
        saveState();
        renderEditor();
      }
      updateViralPromptTask({ message }, {
        error: true,
        allowPureAi: viralPromptCanUsePureAi(error.message),
        ownerDraftId: sourceDraft?.id || "",
      });
      // 已经提交过的任务在服务端会继续跑并计费，"再点一次"就是再付一次钱——这句必须说出口。
      toast(
        submittedJobId
          ? `${message}（任务 ${submittedJobId} 已提交，可能仍在服务端运行并计费；请稍后刷新查看，先别重试）`
          : message,
        { kind: "error", duration: submittedJobId ? 12000 : 7000 },
      );
    }
  } finally {
    if (viralPromptBuildController === controller) viralPromptBuildController = null;
    endDraftGenerationRun(composeGenerationRun);
    done();
    renderEditor();
  }
}

function sendViralPlanToRewrite() {
  const data = state.viralLastGeneration;
  const plan = data?.executionPlan || data?.recipe?.compiledOutput || {};
  if (!plan.titlePrompt || !plan.bodyPrompt || !(plan.coverPrompt || plan.imagePrompt) || !(plan.innerPrompt || plan.imagePrompt)) {
    toast("请先搭建完整提示词");
    return;
  }
  const source = state.drafts.find((draft) => draft.id === state.viralComposeSourceDraftId) || currentDraft();
  const drafts = applyLinkedSeriesPlans(data, source, { reuseSource: false });
  if (!drafts.length) {
    toast("没有可放入改写页的单篇计划");
    return;
  }
  setSection("rewrite");
  toast("已创建单篇草稿，可继续生成和重试");
}

async function updateViralAtomStatus(atomId, status) {
  try {
    await postBackend("/api/viral/atoms/update", { atomId, changes: { status } });
    if (status === "archived") {
      Object.keys(state.viralSelections).forEach((type) => {
        if (state.viralSelections[type] === atomId) {
          delete state.viralSelections[type];
          delete state.viralAdvancedOverrides[type];
        }
      });
    }
    await loadViralAtoms();
    toast(status === "archived" ? "模板原子已归档" : status === "favorite" ? "模板原子已收藏" : "已取消收藏");
  } catch (error) {
    toast(`更新模板原子失败：${error.message}`);
  }
}

function draftUsesTemplateScope(draft, templateId, scope) {
  if (!draft) return false;
  if ((scope === "set" || scope === "cover") && draft.coverTemplateId === templateId) return true;
  if ((scope === "set" || scope === "inner") && draft.innerTemplateId === templateId) return true;
  return scope === "set" && draft.layoutTemplateId === templateId;
}

function clearLocalTemplateReferences(draft, templateId, scope) {
  let changed = false;
  if ((scope === "set" || scope === "cover") && draft.coverTemplateId === templateId) {
    draft.coverTemplateId = "";
    changed = true;
  }
  if ((scope === "set" || scope === "inner") && draft.innerTemplateId === templateId) {
    draft.innerTemplateId = "";
    changed = true;
  }
  if (scope === "set" && draft.layoutTemplateId === templateId) {
    draft.layoutTemplateId = "";
    draft.templateRenderImages = [];
    draft.templateRenderWatermarkReports = [];
    changed = true;
  }
  if (draft.viralTemplateSnapshot && typeof draft.viralTemplateSnapshot === "object") {
    ["cover", "inner"].forEach((part) => {
      if ((scope === "set" || scope === part) && draft.viralTemplateSnapshot?.[part]?.templateId === templateId) {
        draft.viralTemplateSnapshot[part] = {};
        changed = true;
      }
    });
  }
  if (changed && draft.templateSetId === templateId) draft.templateSetId = "";
  return changed;
}

function templateRoles(template) {
  const images = template?.originalImages || template?.renderedImages || [];
  return new Set(localTemplatePageRoles(template, Math.max(1, images.length)));
}

function removeLocalSceneImageTemplate(templateId) {
  const builtIn = sceneImageTemplates.some((item) => item.id === templateId);
  if (builtIn) {
    state.deletedSceneImageTemplateIds = [...new Set([...(state.deletedSceneImageTemplateIds || []), templateId])];
    delete state.imageTemplateOverrides[templateId];
  } else {
    state.customSceneImageTemplates = (state.customSceneImageTemplates || []).filter((item) => item.id !== templateId);
  }
  if (state.selectedImageTemplateId === templateId) {
    state.selectedImageTemplateId = "";
    state.selectedStyle = "";
    const prompt = $("#promptInput");
    if (prompt) prompt.value = "";
  }
}

async function openImageStudioTemplateDeleteModal(templateId) {
  const item = [...coverImageStudioTemplates(), ...editableSceneImageTemplates()].find((template) => template.id === templateId);
  if (!item) {
    toast("模板不存在或已经被删除");
    return;
  }
  if (item.mode === "cover" && item.templateId) {
    await openTemplateDeleteModal(item.templateId, "cover");
    return;
  }
  state.templateDeletion = {
    localScene: true,
    templateId: item.id,
    scope: "scene",
    templateName: item.name || "未命名场景模板",
  };
  $("#templateDeleteTitle").textContent = "删除场景模板";
  $("#templateDeleteName").textContent = state.templateDeletion.templateName;
  $("#templateDeleteImpact").textContent = "删除后，该模板不会再出现在 AI 生图的真实场景模板列表中。";
  // 顺序要紧：先设 className 再补 hidden，否则 className 会把 hidden 抹掉。
  $("#deleteTemplatePartBtn").className = "ghost-btn danger-btn";
  $("#deleteTemplatePartBtn").classList.add("hidden");
  $("#deleteTemplateSetBtn").className = "primary-btn danger-action";
  $("#deleteTemplateSetBtn").classList.remove("hidden");
  $("#deleteTemplateSetBtn").textContent = "确认删除模板";
  $("#templateDeleteWarning").textContent = "此操作不可撤销。删除后该模板不会再出现在 AI 生图的真实场景模板列表中；不会删除已经生成的笔记图片。";
  const modal = $("#templateDeleteModal");
  showModal(modal);
}

function closeTemplateDeleteModal() {
  const modal = $("#templateDeleteModal");
  if (!modal) return;
  hideModal(modal);
  state.templateDeletion = null;
}

async function openTemplateDeleteModal(templateId, scope = "cover") {
  const template = state.layoutTemplates.find((item) => item.id === templateId);
  if (!template) {
    toast("模板不存在或已经被删除");
    return;
  }
  const modal = $("#templateDeleteModal");
  const roles = templateRoles(template);
  // 四库卡片只删"当前这一半"：封面库只删封面、内页库只删内页，不再提供整套删除。
  if (scope !== "cover" && scope !== "inner") scope = roles.has("cover") ? "cover" : "inner";
  if (!roles.has(scope)) {
    toast(`该模板没有可删除的${scope === "cover" ? "封面" : "内页"}`);
    return;
  }
  const label = scope === "cover" ? "封面" : "内页";
  const localDrafts = state.drafts.filter((draft) => draftUsesTemplateScope(draft, templateId, scope));
  const remainingRoles = [...roles].filter((role) => role !== scope);
  const affectedCount = localDrafts.length;
  state.templateDeletion = { templateId, scope, templateName: template.name || template.sourceTitle || "未命名模板", onlyPart: true };
  $("#templateDeleteTitle").textContent = `删除${label}模板`;
  $("#templateDeleteName").textContent = state.templateDeletion.templateName;
  $("#templateDeleteImpact").textContent = affectedCount
    ? `有 ${affectedCount} 篇草稿正在引用该模板。删除后会清除失效引用，但保留已经生成的笔记图片。`
    : "当前没有草稿引用该模板。";
  $("#templateDeleteWarning").textContent = remainingRoles.length
    ? `此操作不可撤销。只删除${label}，模板的其余部分保留；不会删除导入原图或已经生成的笔记图片。`
    : `此操作不可撤销。该模板只剩${label}，删除后这份模板会从库里移除；不会删除导入原图或已经生成的笔记图片。`;
  const partButton = $("#deleteTemplatePartBtn");
  const setButton = $("#deleteTemplateSetBtn");
  partButton.textContent = `只删除${label}`;
  partButton.className = "primary-btn danger-action";
  partButton.classList.remove("hidden");
  // 整个弹窗只有一个危险动作，"整套删除"从这里下线（场景模板与批量删除仍在用它）。
  setButton.classList.add("hidden");
  showModal(modal);
}

function openBatchTemplateDeleteModal(kind) {
  if (kind !== "cover" && kind !== "inner") return;
  const selectedIds = new Set(state.visualLibraryDeleteSelections[kind] || []);
  const items = (state.viralLibraries[kind] || []).filter((item) => item.templateId && selectedIds.has(item.templateId));
  const templateIds = [...new Set(items.map((item) => item.templateId))];
  if (!templateIds.length) {
    toast("请先选择要删除的模板");
    return;
  }
  const label = kind === "cover" ? "封面" : "内页";
  const affectedDrafts = state.drafts.filter((draft) => templateIds.some((templateId) => draftUsesTemplateScope(draft, templateId, kind)));
  const names = items.map((item) => item.name || "未命名模板");
  state.templateDeletion = { batch: true, templateIds, scope: kind, templateName: `${templateIds.length} 个${label}模板` };
  $("#templateDeleteTitle").textContent = `删除所选${label}模板`;
  $("#templateDeleteName").textContent = names.length <= 2 ? names.join("、") : `${names.slice(0, 2).join("、")}等 ${names.length} 个模板`;
  $("#templateDeleteImpact").textContent = affectedDrafts.length
    ? `有 ${affectedDrafts.length} 篇草稿正在引用这些模板。删除后会清除失效引用，但保留已经生成的笔记图片。`
    : `将从${label}模板库删除所选 ${templateIds.length} 个模板。`;
  const partButton = $("#deleteTemplatePartBtn");
  const setButton = $("#deleteTemplateSetBtn");
  // 批量同样只删"当前这一库"这一半，整个弹窗只留一个危险动作。
  // 顺序要紧：className 赋值会清掉此前加的 hidden，必须先设 className 再补 hidden——
  // 原来反过来写，等于把 hidden 抹掉，批量弹窗里一直有两个按钮。
  partButton.className = "primary-btn danger-action";
  partButton.textContent = `只删除所选${label}`;
  partButton.classList.remove("hidden");
  setButton.className = "ghost-btn danger-btn";
  setButton.classList.add("hidden");
  $("#templateDeleteWarning").textContent = `此操作不可撤销。只删除所选${label}，模板的其余部分保留；若某份模板只剩这一部分，则会整份移除。不会删除导入原图或已经生成的笔记图片。`;
  const modal = $("#templateDeleteModal");
  showModal(modal);
}

function openImageStudioBatchDeleteModal(mode) {
  if (!["cover", "scene"].includes(mode)) return;
  const selectedIds = new Set(state.imageTemplateDeleteSelections[mode] || []);
  const items = imageStudioTemplates(mode).filter((item) => selectedIds.has(item.id));
  const targets = items
    .map((item) => ({ id: item.id, templateId: item.templateId || "", name: item.name || "未命名模板" }))
    .filter((item) => mode === "scene" || item.templateId);
  if (!targets.length) {
    toast("请先选择要删除的模板");
    return;
  }
  const label = mode === "scene" ? "场景" : "封面";
  const names = targets.map((item) => item.name);
  state.templateDeletion = {
    batch: true,
    imageStudioBatch: true,
    scope: mode,
    targets,
    templateName: `${targets.length} 个${label}模板`,
  };
  $("#templateDeleteTitle").textContent = `删除所选${label}模板`;
  $("#templateDeleteName").textContent = names.length <= 2 ? names.join("、") : `${names.slice(0, 2).join("、")}等 ${names.length} 个模板`;
  $("#templateDeleteImpact").textContent = mode === "scene"
    ? `删除后，所选 ${targets.length} 个模板不会再出现在 AI 生图的真实场景列表中。`
    : `将从封面库和 AI 生图工作区删除所选 ${targets.length} 个封面模板；已生成的图片会保留。`;
  // 顺序要紧：先设 className 再补 hidden，否则 className 会把 hidden 抹掉。
  $("#deleteTemplatePartBtn").className = "ghost-btn danger-btn";
  $("#deleteTemplatePartBtn").classList.add("hidden");
  $("#deleteTemplateSetBtn").className = "primary-btn danger-action";
  $("#deleteTemplateSetBtn").classList.remove("hidden");
  $("#deleteTemplateSetBtn").textContent = `确认删除 ${targets.length} 个模板`;
  $("#templateDeleteWarning").textContent = "此操作不可撤销。删除所选模板时会同时移除对应的模板脚本、预览与专属资源；不会删除导入原图或已经生成的笔记图片。";
  const modal = $("#templateDeleteModal");
  showModal(modal);
}

function clearDeletedTemplateSelections(templateId, scope) {
  const cover = selectedViralLibraryItem("cover");
  const inner = selectedViralLibraryItem("inner");
  if ((scope === "set" || scope === "cover") && cover?.templateId === templateId) {
    state.viralLibrarySelections.coverId = "";
  }
  if ((scope === "set" || scope === "inner") && inner?.templateId === templateId) {
    state.viralLibrarySelections.innerId = "";
  }
  if (state.viralLibrarySelections.templateSetId === templateId) {
    state.viralLibrarySelections.templateSetId = "";
  }
}

function applyTemplateDeletionResult(data, templateId, requestedScope) {
  const actualScope = data.deletedScope || requestedScope;
  clearDeletedTemplateSelections(templateId, actualScope);
  state.layoutTemplates = data.template
    ? state.layoutTemplates.map((item) => item.id === templateId ? data.template : item)
    : state.layoutTemplates.filter((item) => item.id !== templateId);
  state.activeLayoutTemplateId = state.layoutTemplates.some((item) => item.id === templateId)
    ? templateId
    : state.layoutTemplates[0]?.id || null;
  state.drafts.forEach((draft) => clearLocalTemplateReferences(draft, templateId, actualScope));
  const affectedKinds = actualScope === "set" ? ["cover", "inner"] : [actualScope];
  state.viralLibraries = {
    ...(state.viralLibraries || {}),
    ...Object.fromEntries(affectedKinds.map((kind) => [
      kind,
      (state.viralLibraries?.[kind] || []).filter((item) => item?.templateId !== templateId),
    ])),
  };
  return actualScope;
}

function finishTemplateDeletion() {
  saveState();
  closeTemplateDeleteModal();
  renderAll();
}

async function confirmTemplateDeletion(scopeOverride = "") {
  const pending = state.templateDeletion;
  if (!pending) return;
  const scope = scopeOverride || pending.scope;
  // 四库批量也走 part 按钮（只删当前这一库）；场景模板与 AI 生图批量才用 set。
  const button = pending.localScene || pending.imageStudioBatch || scope === "set" ? $("#deleteTemplateSetBtn") : $("#deleteTemplatePartBtn");
  const done = setButtonLoading(button, "正在删除...");
  let deletedCount = 0;
  try {
    if (pending.localScene) {
      removeLocalSceneImageTemplate(pending.templateId);
      finishTemplateDeletion();
      toast("场景模板已删除");
      return;
    }
    if (pending.imageStudioBatch) {
      if (pending.scope === "scene") {
        pending.targets.forEach((target) => {
          removeLocalSceneImageTemplate(target.id);
        });
        deletedCount = pending.targets.length;
      } else {
        const templateIds = [...new Set(pending.targets.map((target) => target.templateId).filter(Boolean))];
        const data = await postBackend("/api/templates/delete-batch", { templateIds, scope: "cover" });
        (data.results || []).forEach((result) => {
          applyTemplateDeletionResult(result, result.templateId, "cover");
        });
        deletedCount = data.results?.length || 0;
      }
      state.imageTemplateDeleteSelections[pending.scope] = [];
      finishTemplateDeletion();
      toast(`已删除 ${deletedCount} 个${pending.scope === "scene" ? "场景" : "封面"}模板`);
      return;
    }
    if (pending.batch) {
      const data = await postBackend("/api/templates/delete-batch", {
        templateIds: pending.templateIds,
        scope: pending.scope,
      });
      (data.results || []).forEach((result) => {
        applyTemplateDeletionResult(result, result.templateId, pending.scope);
      });
      deletedCount = data.results?.length || 0;
      state.visualLibraryDeleteSelections[pending.scope] = [];
      finishTemplateDeletion();
      toast(`已删除 ${deletedCount} 个${pending.scope === "cover" ? "封面" : "内页"}模板`);
      return;
    }
    const data = await deleteBackend(`/api/templates/${encodeURIComponent(pending.templateId)}?scope=${encodeURIComponent(scope)}`);
    const actualScope = applyTemplateDeletionResult(data, pending.templateId, scope);
    finishTemplateDeletion();
    // 四库卡片只会请求删"当前这一半"。若这份模板只剩这一半，后端会整份移除，
    // 这里要说清是"因为它只有这部分"，避免用户以为删多了。
    toast(actualScope === "set"
      ? (scope === "set" ? "整套模板已删除" : "该模板只剩这一部分，已整份移除")
      : `${actualScope === "cover" ? "封面" : "内页"}模板已删除`);
  } catch (error) {
    if (pending.batch && deletedCount) {
      finishTemplateDeletion();
      toast(`已删除 ${deletedCount} 个模板，其余删除失败：${error.message}`);
    } else {
      toast(`删除模板失败：${error.message}`);
    }
  } finally {
    done();
  }
}


// ----- js/tools/low-fan.js -----
const lowFanCollectionJobStorageKey = "xhs-low-fan-collection-job-v1";
const lowFanSuffixesStorageKey = "xhs-low-fan-suffixes-v1";
// The collector consumes its handoff within seconds; staying queued this long
// means the browser extension never took the job over.
// P104：采集由扩展驱动、页面侧看不到进度，过去要干等 45 秒才等到"插件未接管"。
// 现在 10 秒兜底；等待期间也有即时说明（见 renderLowFanCollection），不留空白。
const LOW_FAN_TAKEOVER_HINT_SECONDS = 10;
// 发布页同样探测不到扩展装没装（没有 externally_connectable），这段等待对没装的用户
// 就是纯空白。30 秒足够慢网下的正常接管，又不会让人干等 45 秒才看到指引。
const PUBLISH_TAKEOVER_HINT_SECONDS = 30;

// 采集任务轮询会把任务配置里的副词回填进表单；用户手动或 AI 重新生成过副词后，
// 不再让后台轮询覆盖，避免"换主词重新生成后又跳回旧任务存的副词"。
let lowFanSuffixEditedSinceJobStart = false;

// P104：点「开始采集」到服务端把新任务返回之间有一段空窗——实测任务 createdAt 比点击晚
// 12.8 秒、响应还要更晚。这段时间 state.lowFanCollection.job 仍是上一条任务，照旧渲染就会
// 把上一条任务的说明挂到这一次点击下面（实测读到过「插件未接管（已等待 956 秒）」），
// 用户面对的是一个不属于自己的加载中页面 + 一段讲别的任务的话。用这个标记顶掉旧任务，
// 让点击后立刻有属于这一次的说明；POST 回来（成功或失败）都由 startLowFanCollection 清掉。
let lowFanJobCreationPending = false;

function lowFanLines(value) {
  return [...new Set(String(value || "").split(/[\n,，]+/).map((item) => item.trim()).filter(Boolean))];
}

function normalizeLowFanSuffixes(values) {
  const source = Array.isArray(values) ? values : lowFanLines(values);
  const result = [];
  const seen = new Set();
  source.forEach((value) => {
    const suffix = String(value || "").replace(/^[#\s]+|[#\s]+$/g, "").replace(/\s+/g, " ").slice(0, 30);
    if (!suffix || seen.has(suffix.toLowerCase()) || result.length >= 20) return;
    seen.add(suffix.toLowerCase());
    result.push(suffix);
  });
  return result;
}

function saveLowFanSuffixes() {
  localStorage.setItem(tenantStorageKey(lowFanSuffixesStorageKey), JSON.stringify(state.lowFanCollection.suffixes));
}

function renderLowFanSuffixes() {
  const list = $("#lowFanSuffixList");
  if (!list) return;
  list.innerHTML = state.lowFanCollection.suffixes.map((suffix) => `<span class="low-fan-suffix-chip">
    <span title="${escapeHtml(suffix)}">${escapeHtml(suffix)}</span>
    <button type="button" data-remove-low-fan-suffix="${escapeHtml(suffix)}" title="删除 ${escapeHtml(suffix)}" aria-label="删除 ${escapeHtml(suffix)}"><i data-lucide="x" aria-hidden="true"></i></button>
  </span>`).join("");
  refreshIcons();
}

function setLowFanSuffixes(values) {
  state.lowFanCollection.suffixes = normalizeLowFanSuffixes(values);
  saveLowFanSuffixes();
  renderLowFanSuffixes();
}

function setLowFanSuffixComposer(open) {
  const composer = $(".low-fan-suffix-add");
  const input = $("#lowFanSuffixInput");
  composer?.classList.toggle("is-open", open);
  if (input) input.disabled = !open;
  if (open) {
    input?.focus();
  } else if (input) {
    input.value = "";
  }
}

function addLowFanSuffix() {
  const input = $("#lowFanSuffixInput");
  const composer = $(".low-fan-suffix-add");
  if (!composer?.classList.contains("is-open")) {
    setLowFanSuffixComposer(true);
    return;
  }
  const suffix = normalizeLowFanSuffixes([input?.value || ""])[0];
  if (!suffix) {
    input?.focus();
    return;
  }
  if (state.lowFanCollection.suffixes.some((item) => item.toLowerCase() === suffix.toLowerCase())) {
    toast("这个副词已经添加过了");
    input.select();
    return;
  }
  if (state.lowFanCollection.suffixes.length >= 20) return toast("最多添加 20 个副词", { kind: "error" });
  lowFanSuffixEditedSinceJobStart = true;
  setLowFanSuffixes([...state.lowFanCollection.suffixes, suffix]);
  setLowFanSuffixComposer(false);
}

function parseGeneratedLowFanSuffixes(text) {
  const parsed = parseJsonObject(text);
  const values = Array.isArray(parsed?.suffixes)
    ? parsed.suffixes
    : String(text || "").split(/[\n,，、]+/).map((item) => item.replace(/^\s*(?:[-*•]|\d+[.、)])\s*/, ""));
  return normalizeLowFanSuffixes(values).slice(0, 6);
}

// P92：过去要求"恰好 5 个"，AI 返 4/6 个或夹一个空项就整次抛错、新旧都拿不到。
// 现在 4-6 个都收（空项与重复由 normalizeLowFanSuffixes 过滤），把门槛降成"够用即可"。
const LOW_FAN_SUFFIX_MIN = 4;

async function generateLowFanSuffixes() {
  const seedTerms = lowFanLines($("#lowFanSeedTerms").value);
  if (!seedTerms.length) return toast("请先填写主词", { kind: "error" });
  if (!ensureAiConfigured("text")) return;
  const button = $("#generateLowFanSuffixesBtn");
  const original = button.innerHTML;
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  button.innerHTML = '<i data-lucide="loader-circle" aria-hidden="true"></i>';
  refreshIcons();
  try {
    await syncAiConfigBeforeGeneration();
    const text = await runTextAi(
      "low-fan-suffixes",
      `根据以下小红书搜索主词，生成 4 到 6 个适合扩展搜索的中文副词。副词应短、具体、彼此不重复，优先覆盖人群、场景、需求、痛点或内容形式；不要复述主词，不要带 #。只返回 JSON：{"suffixes":["副词1","副词2","副词3","副词4"]}。\n\n主词：${seedTerms.join("、")}`,
      { title: seedTerms.join("、"), body: "", tags: "", images: [] },
    );
    const suffixes = parseGeneratedLowFanSuffixes(text);
    if (suffixes.length < LOW_FAN_SUFFIX_MIN) {
      throw new Error(`AI 只返回了 ${suffixes.length} 个可用副词（至少需要 ${LOW_FAN_SUFFIX_MIN} 个），请重试`);
    }
    lowFanSuffixEditedSinceJobStart = true;
    // 只有在拿到可用结果时才动列表：失败分支保留用户原有的副词，不再"刷掉旧的又出不来新的"。
    setLowFanSuffixes(suffixes);
    setLowFanSuffixComposer(false);
    toast(`已生成 ${suffixes.length} 个对应副词`);
  } catch (error) {
    toast(`副词生成失败：${error.message}`, { kind: "error", duration: 5000 });
  } finally {
    button.disabled = false;
    button.removeAttribute("aria-busy");
    button.innerHTML = original;
    refreshIcons();
  }
}

function renderLowFanVisualSearch() {
  const search = state.lowFanVisualSearch;
  const drop = $("#lowFanVisualDrop");
  const preview = $("#lowFanVisualPreview");
  const placeholder = $(".low-fan-visual-placeholder");
  const clearButton = $("#clearLowFanVisualBtn");
  const button = $("#analyzeLowFanVisualBtn");
  if (!drop || !preview || !button) return;
  const hasImage = Boolean(search?.src);
  drop.classList.toggle("has-image", hasImage);
  preview.hidden = !hasImage;
  preview.src = hasImage ? search.src : "";
  if (placeholder) placeholder.hidden = hasImage;
  if (clearButton) clearButton.hidden = !hasImage;
  button.disabled = !hasImage || Boolean(search?.busy);
  button.setAttribute("aria-busy", search?.busy ? "true" : "false");
  const result = $("#lowFanVisualSearchResult");
  if (result) result.hidden = !search?.analysis;
  if (search?.analysis) {
    $("#lowFanVisualObject").textContent = search.analysis.object || "未能确认品类";
    $("#lowFanVisualKeywords").textContent = (search.analysis.keywords || []).join(" · ");
  }
  refreshIcons();
}

function parseLowFanVisualAnalysis(text) {
  const parsed = parseJsonObject(text) || {};
  const values = (Array.isArray(parsed.keywords) ? parsed.keywords : [])
    .concat(Array.isArray(parsed.searchTerms) ? parsed.searchTerms : [])
    .concat(lowFanLines(parsed.query || ""));
  const keywords = normalizeLowFanSuffixes(values).slice(0, 8);
  const object = String(parsed.object || parsed.category || keywords[0] || "").trim();
  return { object, keywords, query: String(parsed.query || keywords[0] || object).trim(), rationale: String(parsed.rationale || "").trim() };
}

async function analyzeLowFanVisualAndSearch() {
  const search = state.lowFanVisualSearch;
  if (!search?.src || search.busy) return;
  if (!ensureAiConfigured("text")) return;
  const button = $("#analyzeLowFanVisualBtn");
  const collectionWindow = openLowFanCollectorWindow();
  const originalButton = button?.innerHTML || "";
  if (button) {
    button.disabled = true;
    button.innerHTML = '<i data-lucide="loader-circle" aria-hidden="true"></i><span>正在识别图片...</span>';
    refreshIcons();
  }
  search.busy = true;
  renderLowFanVisualSearch();
  try {
    await syncAiConfigBeforeGeneration();
    const text = await runTextAi(
      "low-fan-image-search",
      "识别输入图片中的主要商品，并生成用于小红书搜索的关键词。只返回 JSON，不要解释：{\"object\":\"品类\",\"keywords\":[\"品类词\",\"材质词\",\"造型词\",\"场景词\"],\"query\":\"最适合搜索的一组词\",\"rationale\":\"一句话说明识别依据\"}。只描述图片明确可见的信息，不要猜品牌、价格、宝石真假、产地或功效。关键词要自然、具体、适合搜索爆款笔记。",
      { title: "图片识别", body: "", tags: "", images: [] },
      { imageInputs: [search.src] },
    );
    const analysis = parseLowFanVisualAnalysis(text);
    if (!analysis.object && !analysis.keywords.length) throw new Error("AI 未识别出可用搜索词，请换一张主体更清晰的图片");
    search.analysis = analysis;
    const terms = normalizeLowFanSuffixes([analysis.query || analysis.object]).slice(0, 1);
    $("#lowFanSeedTerms").value = terms.join("、");
    lowFanSuffixEditedSinceJobStart = true;
    setLowFanSuffixes(analysis.keywords.filter((item) => !terms.includes(item)).slice(0, 6));
    renderLowFanVisualSearch();
    toast(`已识别为${analysis.object || "目标单品"}，正在匹配低粉爆款`);
    await startLowFanCollection(null, collectionWindow);
  } catch (error) {
    toast(`识图搜索失败：${friendlyAiError(error)}`, { kind: "error", duration: 6000 });
  } finally {
    search.busy = false;
    if (button) {
      button.disabled = false;
      button.innerHTML = originalButton;
    }
    renderLowFanVisualSearch();
  }
}

async function replicateLowFanResult(resultId, button) {
  const item = state.lowFanCollection.results.find((entry) => String(entry.id || entry.noteUrl) === String(resultId));
  if (!item?.noteUrl) return toast("这条样本没有可用链接", { kind: "error" });
  const done = setButtonLoading(button, "导入中...");
  try {
    const job = await captureNoteLinks(item.noteUrl);
    const notes = job.results || [];
    const note = notes.find((entry) => entry.ok !== false) || {};
    const fields = normalizeImportedNoteFields(note.body ?? "", note.tags ?? "");
    const title = normalizeImportedNoteTitle(note.title ?? item.title ?? "", fields.body);
    createDraft({ source: "link", workflow: "replica", seed: title || "低粉爆款复刻", origin: item.noteUrl, title, body: fields.body, tags: fields.tags, images: normalizeImages(note.images).slice(0, 18) });
    setSection("rewrite");
    toast("已导入复刻任务，可继续调整文案与图片");
  } catch (error) {
    // 用户自己按的取消不是失败，中性提示即可。
    // 插件没装这条诊断自己就是完整的说法（含下一步），再套一层"复刻导入失败"只是把
    // 同一件事说两遍。
    toast(
      error?.noteCaptureNotice || error?.noteCaptureCancelled || error?.extensionMissing
        ? error.message
        : `复刻导入失败：${error.message}`,
      { kind: error?.noteCaptureCancelled ? "info" : "error" },
    );
  } finally {
    done();
  }
}

function lowFanStatusLabel(status) {
  return ({
    creating: "正在创建任务",
    queued: "等待扩展接管",
    running: "正在采集",
    paused: "已暂停",
    needs_login: "需要登录",
    needs_verification: "需要人工验证",
    completed: "采集完成",
    cancelled: "已取消",
    failed: "采集失败",
  })[status] || "暂无采集任务";
}

function lowFanStatusIcon(status) {
  return ({
    creating: "loader-circle",
    queued: "clock-3",
    running: "loader-circle",
    paused: "pause-circle",
    needs_login: "log-in",
    needs_verification: "shield-alert",
    completed: "circle-check",
    cancelled: "circle-x",
    failed: "triangle-alert",
  })[status] || "circle-dashed";
}

async function lowFanTaskUrl(job, resume = false) {
  const keyword = job?.progress?.currentKeyword || job?.keywords?.[job?.progress?.keywordIndex || 0] || job?.keywords?.[0] || "";
  const handoff = await postBackend("/api/extension/handoff", {
    purpose: "low-fan",
    payload: {
      taskId: job?.id || "",
      backendUrl: backendBaseUrl,
      resume: Boolean(resume),
    },
  });
  if (!handoff.handoff?.nonce || !handoff.handoff?.tenantId) throw new Error("低分采集交接响应无效");
  const url = new URL("https://www.xiaohongshu.com/search_result");
  url.searchParams.set("keyword", keyword);
  url.searchParams.set("source", "web_search_result_notes");
  url.hash = new URLSearchParams({
    handoff: handoff.handoff?.nonce || "",
    tenant: handoff.handoff?.tenantId || "",
    purpose: "low-fan",
    // Tells the extension which workstation signed this nonce, so it calls
    // back here instead of trying the production host first.
    ws: backendBaseUrl,
  }).toString();
  return url.toString();
}

function refocusWorkstation() {
  [0, 100, 400, 1000].forEach((delay) => {
    window.setTimeout(() => {
      try { window.focus(); } catch {}
    }, delay);
  });
}

function openLowFanCollectorWindow() {
  const collectionWindow = window.open("about:blank", "xhs-low-fan-collector");
  if (collectionWindow) {
    // Keep the collector tab alive while returning focus to the workstation.
    try { collectionWindow.blur(); } catch {}
    refocusWorkstation();
  }
  return collectionWindow;
}

function navigateLowFanCollectorWindow(collectionWindow, url) {
  if (!collectionWindow || collectionWindow.closed) return false;
  try {
    collectionWindow.location.replace(url);
  } catch {
    collectionWindow.location.href = url;
  }
  try { collectionWindow.blur(); } catch {}
  refocusWorkstation();
  return true;
}

function lowFanProgress(job) {
  if (!job) return 0;
  const progress = job.progress || {};
  if (job.status === "completed") return 100;
  if (progress.phase === "keyword") return 3;
  if (["search", "author"].includes(progress.phase)) {
    const totalKeywords = Math.max(1, job.keywords?.length || 1);
    const keywordProgress = Number(progress.keywordIndex || 0) / totalKeywords;
    const cardProgress = Number(progress.cardIndex || 0) / Math.max(1, progress.cardKeys?.length || 1) / totalKeywords;
    return Math.min(98, 5 + ((keywordProgress + cardProgress) * 93));
  }
  return 0;
}

function lowFanMetric(value) {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  if (number >= 10000) return `${(number / 10000).toFixed(number >= 100000 ? 0 : 1)}万`;
  return number.toLocaleString("zh-CN");
}

async function copyLowFanNoteUrl(button) {
  const url = String(button?.dataset.copyLowFanUrl || "").trim();
  if (!url) return toast("链接为空，暂时无法复制", { kind: "error" });
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      const input = document.createElement("textarea");
      input.value = url;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    const original = button.innerHTML;
    button.innerHTML = '<i data-lucide="check"></i><span>已复制</span>';
    button.disabled = true;
    if (window.lucide?.createIcons) window.lucide.createIcons({ attrs: { "stroke-width": 1.8 } });
    window.setTimeout(() => {
      button.innerHTML = original;
      button.disabled = false;
      if (window.lucide?.createIcons) window.lucide.createIcons({ attrs: { "stroke-width": 1.8 } });
    }, 1400);
  } catch (error) {
    toast(`复制失败：${error.message || "浏览器未授权剪贴板"}`, { kind: "error" });
  }
}

function splitDisplayedLowFanAuthor(item) {
  const author = String(item?.authorName || "").trim();
  const published = String(item?.publishedAgeText || item?.publishedAt || "").trim();
  if (published || !author) return { author, published };
  const match = author.match(/^(.*?)(刚刚|昨天|前天|\d+\s*(?:分钟前|小时前|天前|周前|个月前|月前|年前)|\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[-/.]\d{1,2})$/);
  return match && match[1].trim() ? { author: match[1].trim(), published: match[2].trim() } : { author, published };
}

function resetLowFanCollectionView() {
  window.clearTimeout(state.lowFanCollection.watcher);
  state.lowFanCollection.watcher = 0;
  state.lowFanCollection.job = null;
  state.lowFanCollection.results = [];
  state.lowFanCollection.counts = { total: 0, complete: 0, incomplete: 0 };
  lowFanJobCreationPending = false;
  // 清空任务时副词栏也一起清掉：只清结果、留下上一轮的副词，用户下一轮点「开始采集」
  // 会把这些旧副词原样提交（界面上看就是"清空之后旧副词又跟着新任务跑了"）。
  lowFanSuffixEditedSinceJobStart = false;
  setLowFanSuffixes([]);
  localStorage.removeItem(tenantStorageKey(lowFanCollectionJobStorageKey));
  renderLowFanCollection();
}

function renderLowFanCollection() {
  const collection = state.lowFanCollection;
  // P104：创建请求还没回来时不要拿上一条任务充数（见 lowFanJobCreationPending）。
  const creating = lowFanJobCreationPending;
  const job = creating ? null : collection.job;
  const statusNode = $("#lowFanJobStatus");
  if (!statusNode) return;
  const status = creating ? "creating" : (job?.status || "idle");
  statusNode.textContent = lowFanStatusLabel(status);
  const statusBadge = $("#lowFanStatusBadge");
  if (statusBadge) {
    statusBadge.dataset.status = status;
    statusBadge.innerHTML = `<i data-lucide="${lowFanStatusIcon(status)}" aria-hidden="true"></i><span id="lowFanJobStatus">${escapeHtml(lowFanStatusLabel(status))}</span>`;
  }
  $("#lowFanJobMeta").textContent = creating
    ? "正在把这次采集交给浏览器扩展…"
    : job
      ? `${job.mode === "strict" ? "严格低粉" : "快速采集"} · ${job.keywords?.length || 0} 个关键词 · ${job.resultsCount || collection.results.length} 条候选`
      : "请先填写主词并开始采集";
  // A queued job with no browser event means the extension never took over -
  // the one failure the page used to show as a silent "等待扩展接管" forever.
  // P104：等待本身也要有话说——点完"开始采集"到扩展接管之间的几秒里，用户面对的是
  // 一个我们不拥有、也无法自报状态的小红书标签页，页面侧必须立刻解释"需要扩展"。
  const waitingSeconds = job ? Math.floor((Date.now() - Date.parse(job.createdAt || "")) / 1000) : 0;
  const takeoverPending = Boolean(
    job
    && job.status === "queued"
    && !Number(job.eventCount || 0)
    && Number.isFinite(waitingSeconds),
  );
  const takeoverStalled = takeoverPending && waitingSeconds >= LOW_FAN_TAKEOVER_HINT_SECONDS;
  const progressMessage = $("#lowFanProgressMessage");
  progressMessage.textContent = creating
    // 这一段必须属于"这一次点击"：创建期间旧任务的一切（进度、说明）都不该出现。
    ? "正在创建采集任务…创建完成后由采集扩展接管小红书采集页；未安装「薯作台小红书采集助手」扩展时该页面会一直加载"
    : takeoverStalled
      ? `插件未接管（已等待 ${waitingSeconds} 秒）：请确认浏览器已安装「薯作台小红书采集助手」并在扩展页重新加载，且小红书标签页保持打开`
      : takeoverPending
        ? "已打开小红书采集页：装好「薯作台小红书采集助手」的话扩展会自动接管并开始采集；未安装扩展时该页面会一直加载"
        : job?.error || job?.progress?.message || "任务尚未启动";
  progressMessage.dataset.stalled = takeoverStalled ? "1" : "0";
  // 扩展接管后任务离开 queued，这段说明随之自动收起，不会误伤正常采集。
  progressMessage.dataset.takeoverPending = takeoverPending && !takeoverStalled ? "1" : "0";
  const progress = Math.round(lowFanProgress(job));
  $("#lowFanProgressBar").style.width = `${progress}%`;
  $("#lowFanProgressPercent").textContent = `${progress}%`;
  $("#lowFanCompleteCount").textContent = collection.counts.complete || 0;
  $("#lowFanIncompleteCount").textContent = collection.counts.incomplete || 0;
  $("#lowFanTotalCount").textContent = collection.counts.total || 0;

  const active = ["queued", "running"].includes(job?.status);
  const resumable = ["paused", "needs_login", "needs_verification"].includes(job?.status);
  $("#pauseLowFanCollectionBtn").disabled = !active;
  $("#cancelLowFanCollectionBtn").disabled = !job || ["completed", "cancelled", "failed"].includes(job.status);
  $("#resumeLowFanCollectionBtn").disabled = !resumable;
  const canExport = Boolean(job && collection.results.length);
  $("#exportLowFanCollectionBtn").disabled = !canExport;
  $("#exportLowFanCollectionBtnMirror")?.toggleAttribute("disabled", !canExport);
  $("#clearLowFanCollectionBtn")?.toggleAttribute("disabled", !job && !collection.results.length);

  $$('[data-low-fan-filter]').forEach((button) => button.classList.toggle("active", button.dataset.lowFanFilter === collection.filter));
  const rows = collection.results.filter((item) => collection.filter === "all"
    || (collection.filter === "complete" ? item.dataQuality === "complete" : item.dataQuality !== "complete"))
    .sort((left, right) => {
      const qualityOrder = Number(right.dataQuality === "complete") - Number(left.dataQuality === "complete");
      return qualityOrder || Number(right.likes || 0) - Number(left.likes || 0);
    });
  const body = $("#lowFanResultsBody");
  body.innerHTML = rows.map((item) => {
    const identity = splitDisplayedLowFanAuthor(item);
    const quality = item.dataQuality === "complete" ? "符合" : "不符合";
    const interaction = Number(item.likes || 0);
    return `<tr>
      <td><strong title="${escapeHtml(item.title || "")}">${escapeHtml(item.title || "未读取到标题")}</strong><small title="${escapeHtml(item.excludedReason || "")}">${escapeHtml(item.excludedReason || quality)}</small></td>
      <td>${escapeHtml(identity.author || "—")}</td>
      <td>${lowFanMetric(item.followers)}</td>
      <td>${escapeHtml(identity.published || "—")}</td>
      <td><strong>${lowFanMetric(interaction)}</strong><small>点赞量</small></td>
      <td><span class="low-fan-quality ${escapeHtml(item.dataQuality || "excluded")}">${escapeHtml(quality)}</span></td>
      <td title="${escapeHtml((item.matchedKeywords || []).join("、"))}">${escapeHtml((item.matchedKeywords || []).join("、") || "—")}</td>
      <td><div class="low-fan-link-actions"><a href="${escapeHtml(item.noteUrl || "#")}" target="_blank" rel="noreferrer">打开</a><button type="button" class="low-fan-copy-btn" data-copy-low-fan-url="${escapeHtml(item.noteUrl || "")}" title="复制笔记链接"><i data-lucide="copy"></i><span>复制</span></button><button type="button" class="low-fan-replicate-btn" data-replicate-low-fan="${escapeHtml(item.id || item.noteUrl || "")}" title="导入到复刻工作流"><i data-lucide="wand-sparkles"></i><span>复刻</span></button></div></td>
    </tr>`;
  }).join("");
  refreshIcons();
  $("#lowFanResultsEmpty").classList.toggle("hidden", Boolean(rows.length));
}

async function refreshLowFanCollection(jobId, keepWatching = true) {
  if (!jobId) return;
  if (state.lowFanCollection.job?.id === jobId && state.lowFanCollection.job.status === "cancelled") return;
  window.clearTimeout(state.lowFanCollection.watcher);
  try {
    const [jobData, resultData] = await Promise.all([
      getBackend(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(jobId)}`),
      getBackend(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(jobId)}/results`),
    ]);
    // A cancellation or a newer task can finish while this poll is in flight.
    if (localStorage.getItem(tenantStorageKey(lowFanCollectionJobStorageKey)) !== jobId
      || (state.lowFanCollection.job?.id === jobId && state.lowFanCollection.job.status === "cancelled")) return;
    const previousJobId = state.lowFanCollection.job?.id;
    state.lowFanCollection.job = jobData.job;
    if (previousJobId !== jobId) lowFanSuffixEditedSinceJobStart = false;
    // P92：扩展带回联想词时用任务值覆盖本地是"意图"，但服务端在联想词为空时也会标记
    // autocompleteCaptured 并把任务 suffixes 置空——无条件跟随会把用户已有的副词整表刷掉。
    // 只认非空结果：空的联想词不构成"替换"。
    if (jobData.job?.config?.autocompleteCaptured === true && !lowFanSuffixEditedSinceJobStart) {
      const capturedSuffixes = normalizeLowFanSuffixes(jobData.job.config.suffixes || []);
      if (capturedSuffixes.length && JSON.stringify(capturedSuffixes) !== JSON.stringify(state.lowFanCollection.suffixes)) {
        setLowFanSuffixes(capturedSuffixes);
      }
    }
    state.lowFanCollection.results = resultData.results || [];
    state.lowFanCollection.counts = resultData.counts || { total: 0, complete: 0, incomplete: 0 };
    renderLowFanCollection();
    const terminal = ["completed", "cancelled", "failed"].includes(jobData.job.status);
    if (keepWatching && !terminal) {
      state.lowFanCollection.watcher = window.setTimeout(() => refreshLowFanCollection(jobId), 2500);
    }
  } catch (error) {
    if (keepWatching) state.lowFanCollection.watcher = window.setTimeout(() => refreshLowFanCollection(jobId), 5000);
  }
}

async function startLowFanCollection(event, existingWindow = null) {
  event?.preventDefault();
  // 未登录时这一步会 401：过去那个 401 被显示成"创建采集任务失败：登录状态已失效"，
  // 还会顺手打开一个空白的小红书标签页。先判登录，再谈采集（见 auth/login-gate.js）。
  if (!(await requireSignedInForAction("爆款数据采集"))) return;
  const seedTerms = lowFanLines($("#lowFanSeedTerms").value);
  if (!seedTerms.length) return toast("请至少填写一个主词", { kind: "error" });
  // 采集全程由浏览器插件驱动：先等插件检测出结果，没装就当场拦住。顺序反过来就是
  // "先开采集页、先建任务，再祈祷插件接手"——没装的人拿到的是一条停在「等待扩展接管」
  // 的任务和一个永远加载的小红书页面，而原因（这台浏览器里没有插件）从头到尾不可见。
  // 这里最多多等一次本机消息往返（900ms 上限），仍在浏览器的用户手势有效期里，
  // 不影响下面那次 window.open。
  const extensionFailure = await requireCaptureExtension("采集");
  if (extensionFailure) {
    // 识图搜索那条路在更早的用户手势里已经开好了窗口，别把它留在屏幕上。
    if (existingWindow && !existingWindow.closed) existingWindow.close();
    toast(extensionFailure.message + extensionFailure.hint, { kind: "error", duration: 9000 });
    return;
  }
  const suffixes = state.lowFanCollection.suffixes;
  const useAutocomplete = $("#lowFanAutocomplete").checked;
  const maxKeywords = useAutocomplete ? 20 : Math.min(20, seedTerms.length * (suffixes.length + 1));
  const collectionWindow = existingWindow || openLowFanCollectorWindow();
  const button = $("#startLowFanCollectionBtn");
  const done = setButtonLoading(button, "正在创建任务...");
  // P104：任务记录要等 POST 回来才换过来（实测十几秒），先让面板说明这一次点击。
  lowFanJobCreationPending = true;
  renderLowFanCollection();
  try {
    const data = await postBackend("/api/viral/low-fan-collection/jobs", {
      seedTerms,
      mode: $("#lowFanStrictMode").checked ? "strict" : "quick",
      config: {
        seedTerms,
        suffixes,
        useAutocomplete,
        maxKeywords,
        maxCandidates: 50,
        maxAuthors: 50,
        maxDetailsPerKeyword: 7,
        // 发布时间只开放三档（近 3 个月 / 半年 / 一年），下拉里选出来的就是合法档位；
        // 后端还会再收敛一次（见 low_fan_collection.normalize_note_age_days），两边都不放行任意天数。
        maxAgeDays: Number($("#lowFanMaxAgeDays").value || 365),
        maxFollowers: Number($("#lowFanMaxFollowers").value || 5000),
        minLikes: Number($("#lowFanMinLikes").value || 300),
      },
    });
    state.lowFanCollection.job = data.job;
    state.lowFanCollection.results = [];
    state.lowFanCollection.counts = { total: 0, complete: 0, incomplete: 0 };
    localStorage.setItem(tenantStorageKey(lowFanCollectionJobStorageKey), data.job.id);
    // 真任务回来了：占位说明交给它自己的接管提示/进度。
    lowFanJobCreationPending = false;
    renderLowFanCollection();
    if (collectionWindow) collectionWindow.location.href = await lowFanTaskUrl(data.job);
    else toast("任务已创建，请允许浏览器打开小红书标签页", { kind: "error", duration: 5000 });
    refreshLowFanCollection(data.job.id);
    // P104：采集页自己不认人（扩展只匹配小红书域，页面上没有可问的 externally_connectable），
    // 页内只能报"接管没接管"。但工作站这边在开跑前已经问过插件在不在（上面的闸门），
    // 所以这句不再是"装没装都不知道"的模糊话：装好不等于会接管，接管还得插件在那张页面醒过来。
    toast("已打开小红书采集页：装好「薯作台小红书采集助手」的话扩展会自动接管并开始采集，否则该页面会一直加载", { duration: 6000 });
  } catch (error) {
    lowFanJobCreationPending = false;
    if (collectionWindow && !collectionWindow.closed) collectionWindow.close();
    toast(`创建采集任务失败：${error.message}`, { kind: "error", duration: 6000 });
    // 失败后不能把面板留在「正在创建…」：这一次没有任务，界面回到当前真实任务（可能仍是上一条）。
    renderLowFanCollection();
  } finally {
    done();
  }
}

async function pauseLowFanCollection() {
  const job = state.lowFanCollection.job;
  if (!job) return;
  try {
    const data = await postBackend(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(job.id)}/pause`, {});
    state.lowFanCollection.job = data.job;
    renderLowFanCollection();
    toast("采集任务已暂停");
  } catch (error) {
    toast(`暂停失败：${error.message}`, { kind: "error" });
  }
}

async function cancelLowFanCollection() {
  const job = state.lowFanCollection.job;
  if (!job) return;
  window.clearTimeout(state.lowFanCollection.watcher);
  state.lowFanCollection.watcher = 0;
  try {
    const data = await postBackend(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(job.id)}/cancel`, {});
    state.lowFanCollection.job = data.job || { ...job, status: "cancelled" };
    renderLowFanCollection();
    toast("采集任务已取消，已保留采集记录");
  } catch (error) {
    refreshLowFanCollection(job.id);
    toast(`取消失败：${error.message}`, { kind: "error" });
  }
}

async function clearLowFanCollection() {
  const job = state.lowFanCollection.job;
  if (!job && !state.lowFanCollection.results.length) return;
  const shouldCancel = job && !["completed", "cancelled", "failed"].includes(job.status);
  // 清空是破坏性且不可撤销的：任务还在跑时会被一并停掉，页面上的结果会被清掉，
  // 工作站本地那份任务指针也一起删（服务端记录还在，但界面没有入口再找回来）。
  // 与「清空当前笔记」保持同一套写法（window.confirm）。
  const confirmed = window.confirm(
    shouldCancel
      ? "清空采集结果？正在进行的采集任务会被停止，已采集的结果和副词栏会被从页面清掉。"
      : "清空采集结果？页面上的采集结果和副词栏会被清掉（服务端任务记录仍保留）。",
  );
  if (!confirmed) return;
  resetLowFanCollectionView();
  if (!shouldCancel) {
    toast("已清空采集结果");
    return;
  }
  try {
    await postBackend(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(job.id)}/cancel`, {});
    toast("已停止任务并清空采集结果");
  } catch (error) {
    toast(`结果已清空，后台任务停止失败：${error.message}`, { kind: "error", duration: 5000 });
  }
}

async function resumeLowFanCollection() {
  const job = state.lowFanCollection.job;
  if (!job) return;
  const button = $("#resumeLowFanCollectionBtn");
  const done = button ? setButtonLoading(button, "继续中...") : () => {};
  // 浏览器只允许在用户手势里开窗，所以窗口引用必须在任何 await 之前拿到；
  // 命名窗口（xhs-low-fan-collector）会复用已有的那一个采集标签页。
  const collectionWindow = openLowFanCollectorWindow();
  try {
    // 继续同样由插件在采集页里执行：没装插件时"已继续"是句假话——那张页面不会醒过来。
    const extensionFailure = await requireCaptureExtension("采集");
    if (extensionFailure) {
      if (collectionWindow && !collectionWindow.closed) collectionWindow.close();
      toast(extensionFailure.message + extensionFailure.hint, { kind: "error", duration: 9000 });
      return;
    }
    const data = await postBackend(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(job.id)}/events`, {
      type: "resume",
      eventId: `${job.id}:workstation-resume:${Date.now()}`,
      progress: { message: "任务已继续，正在当前页面接着采集" },
    });
    state.lowFanCollection.job = data.job;
    renderLowFanCollection();
    refreshLowFanCollection(job.id);
    // 只发一个 resume 事件，等于赌"采集页还活着、并且自己能醒来"。页面卡在一次
    // 永远不结束的加载、标签页被关掉、或者扩展刚重载过（工作器令牌随 session
    // 存储一起没了）时都赌输。这里把那个命名窗口直接导航到一份新的 handoff
    // （resume=true）：采集页据此从任务自己的进度接着跑，并顺带换一张新令牌。
    if (collectionWindow) {
      collectionWindow.location.href = await lowFanTaskUrl(job, true);
      toast("已继续，当前小红书页面会接着执行");
    } else {
      toast("已继续，但浏览器拦截了采集页；请允许弹窗后重试", { kind: "error", duration: 6000 });
    }
  } catch (error) {
    if (collectionWindow && !collectionWindow.closed) collectionWindow.close();
    toast(`继续失败：${error.message}`, { kind: "error" });
  } finally {
    done();
  }
}

// 服务端把中文文件名放在 RFC 5987 的 filename* 里，另给一个 ASCII 兜底 filename（P119）。
// 下载是 blob + <a download>，浏览器不会替我们采用服务端的名字，所以这里必须显式设置，
// 并按 P46 已确立的口径优先取 filename*。
function lowFanExportFilename(disposition, jobId) {
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch (_error) {
      /* 编码异常就落到 ASCII 兜底，别让改名失败连带整个导出失败 */
    }
  }
  const plain = /filename="([^"]+)"/.exec(disposition);
  return plain ? plain[1] : `低粉爆款采集-${jobId}.csv`;
}

async function exportLowFanCollection() {
  const job = state.lowFanCollection.job;
  if (!job) return;
  try {
    const response = await backendFetch(`/api/viral/low-fan-collection/jobs/${encodeURIComponent(job.id)}/export.csv`);
    // 失败报文（401/403/503…）统一在 HTTP 边界翻成人话：`接口返回 401` 那种话既没说是
    // 没登录还是没权限，也没告诉用户下一步做什么。
    if (!response.ok) throw backendError(await response.json().catch(() => ({})), response.status, response);
    const filename = lowFanExportFilename(response.headers.get("Content-Disposition") || "", job.id);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    toast(`已开始下载 ${filename}`);
    window.setTimeout(() => { link.remove(); URL.revokeObjectURL(url); }, 1000);
  } catch (error) {
    toast(`导出失败：${error.message}`, { kind: "error" });
  }
}

function restoreLowFanCollection() {
  const jobId = localStorage.getItem(tenantStorageKey(lowFanCollectionJobStorageKey));
  try {
    const savedSuffixes = JSON.parse(localStorage.getItem(tenantStorageKey(lowFanSuffixesStorageKey)) || "[]");
    state.lowFanCollection.suffixes = normalizeLowFanSuffixes(savedSuffixes);
  } catch {
    state.lowFanCollection.suffixes = [];
  }
  renderLowFanSuffixes();
  renderLowFanVisualSearch();
  renderLowFanCollection();
  if (jobId) refreshLowFanCollection(jobId);
}

function simulatePlugin(type) {
  if (type === "keyword") {
    createDraft({ source: "plugin", seed: "小红书虚拟资料 关键词采集", images: [sampleImages[0]] });
    toast("关键词采集已进入当前 AI 改写任务");
  } else if (type === "blogger") {
    createDraft({ source: "plugin", seed: "博主爆款标题与图片结构采集", images: [sampleImages[3]] });
    toast("博主采集已进入当前 AI 改写任务");
  } else {
    createDraft({ source: "plugin", seed: "单篇笔记采集", images: [sampleImages[3]] });
    toast("单篇笔记已进入 AI 改写页");
  }
  setSection("rewrite");
}

// 发布是逐条进行的（列表里每条一个「发布」按钮）：服务端建任务时按条目逐条 can_write，
// 批量多选既没有真正的并发能力，一条失败还会把整批拖下水——多选入口已按此移除。
// 一次发布三步：先落库（服务端才认识这条）→ 建发布任务 → 打开创作页交给扩展接管。
const PUBLISH_PREPARE_RETRY_ATTEMPTS = 3;
const PUBLISH_PREPARE_RETRY_DELAY_MS = 1200;

// 404 resource_not_found 与 5xx 属于"还没就绪"的瞬时失败（同一族判据见 client.js 的
// JOB_STATUS_RETRY_* 与扩展 content.js 的 withJobReadRetry）。真正的权限拒绝（403）
// 不重试：重试多少次都一样，只会让用户多等。
function isRetryablePublishPrepare(error) {
  const status = Number(error?.status || 0);
  if (status === 404) return String(error?.errorCode || "") === "resource_not_found";
  return [408, 425, 429, 500, 502, 503, 504].includes(status);
}

// 落库闸门的失败文案必须分档：这三种情况（没水合 / 已停写 / 写盘被拒）的处置完全不同。
// 「写盘被拒」以前一律回同一句兜底话术，把 404 resource_not_found、403、5xx 全糊在一起——
// 用户看到"还没保存到服务器"不知道去哪修，排障也拿不到真实状态码与 errorCode。
// 真实失败原因由 state.js 的 workspacePersistenceState().lastError 带出来。
function publishPersistenceFailureMessage(persistence) {
  // 未登录时水合永远不会成功：那句"还没同步完成，请刷新页面"里的"刷新"是做不到的
  // （见 auth/login-gate.js）。
  if (typeof loginGateSignedOut === "function" && loginGateSignedOut()) {
    return loginGateActionMessage("发布");
  }
  if (!persistence?.hydrated) return "工作台还没和服务器同步完成，请刷新页面后重新发布。";
  if (persistence?.blocked) return "自动保存已暂停（状态被其它窗口改过），请刷新页面后重新发布。";
  const status = Number(persistence?.lastError?.status || 0);
  const code = String(persistence?.lastError?.errorCode || "");
  if (status === 404 || code === "resource_not_found") {
    return "这条内容引用的草稿或模板在服务器上已经找不到了（通常是被删除过），请回到草稿重新「加入发布列表」后再发。";
  }
  if (status === 403 || code === "resource_identity_forbidden" || code === "resource_authorization_unavailable") {
    return "服务器认为当前组织或身份上下文还没就绪，拒绝了这次保存；请刷新页面（必要时重新登录）后再发布。";
  }
  if (status === 409 || code === "state_revision_conflict") {
    return "工作台状态已被其它窗口更新，请刷新页面后重新发布。";
  }
  if (status >= 500) {
    return `服务器保存这次内容时出错（HTTP ${status}${code ? " · " + code : ""}），请稍后重试；反复出现请把这条信息发给管理员。`;
  }
  const detail = String(persistence?.lastError?.message || "").trim();
  return detail
    ? `这条内容还没保存到服务器（${detail}），请在自动保存恢复正常后重试。`
    : "这条内容还没保存到服务器，请在自动保存恢复正常后重试。";
}

// 窗口名必须按发布目标区分：浏览器会复用同名窗口，"xhs-publish" 这种全站唯一的名字
// 让第二条发布直接导航掉第一条刚打开的接管页（连同它的一次性 handoff 一起销毁，
// 第一条就永久停在「已打开小红书发布页」）。一条内容一格窗口，同名重试就复用那一格。
function publishWindowName(itemIds) {
  const key = (Array.isArray(itemIds) ? itemIds : [itemIds])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean)
    .join("+");
  return key ? `xhs-publish-${key}` : "xhs-publish";
}

// 窗口必须在任何 await 之前同步打开，否则会被弹窗拦截；占位页让用户在落库+建任务的
// 这段时间里看到的不是一张白页。
function openPublishWindow(itemIds) {
  const popup = window.open("about:blank", publishWindowName(itemIds));
  if (!popup) return null;
  try {
    popup.document.title = "正在准备小红书发布任务";
    const note = popup.document.createElement("p");
    note.textContent = "正在准备小红书发布任务，马上带你去创作页…";
    note.style.cssText = "font:14px/1.7 system-ui,-apple-system,sans-serif;padding:24px;color:#444";
    popup.document.body.appendChild(note);
  } catch {
    // 占位页写不进去不影响主流程。
  }
  return popup;
}

function closePublishWindow(popup) {
  if (!popup || popup.closed) return;
  try {
    popup.close();
  } catch {
    // 用户已经自己关掉了。
  }
}

async function createPublishTask(items) {
  let lastError = null;
  for (let attempt = 1; attempt <= PUBLISH_PREPARE_RETRY_ATTEMPTS; attempt += 1) {
    try {
      const data = await postBackend("/api/publish/prepare", { items, workstationUrl: backendBaseUrl });
      // 建发布任务这次写入会推进服务端 stateRevision：把新值对齐到本地，否则紧接着的
      // 自动保存会拿旧 revision 去写，被判成"状态被其它窗口改过"而暂停，发布随即失败。
      applyBackendStateRevision(data);
      return data;
    } catch (error) {
      lastError = error;
      if (attempt >= PUBLISH_PREPARE_RETRY_ATTEMPTS || !isRetryablePublishPrepare(error)) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, PUBLISH_PREPARE_RETRY_DELAY_MS));
    }
  }
  throw lastError;
}

async function publishToXiaohongshu(itemIds = null, trigger = null) {
  const selectedIds = (Array.isArray(itemIds) ? itemIds : [itemIds])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean);
  // P121：这一条已经在准备发布任务了。同一条再点一次只会重复建任务、重复开窗，
  // 还会把正在准备的那一次顶掉——直接拒绝，并说清现在是什么状态。
  if (selectedIds.some((id) => publishAttemptInFlight(id))) {
    toast("这条内容正在准备发布任务，请等它完成后再试", { kind: "error", duration: 4000 });
    return;
  }
  let items = state.publish.filter((item) => selectedIds.includes(item.id));
  if (!items.length) {
    toast("这条内容已经不在发布列表里了，请刷新后重新加入", { kind: "error" });
    return;
  }
  if (items.some((item) => !(item.images || []).length)) {
    toast("小红书图文发布至少需要一张图片", { kind: "error", duration: 4500 });
    return;
  }
  // 发布条目在服务端是"逐条 can_read(它引用的草稿)"才写得进去的（server/backend.py 的
  // _check_state_record_references），草稿被删掉之后整批 /api/state 都会被 404 拒，
  // 用户看到的是"点发布 → 占位页开一下就关 + 原因：还没保存到服务器"。这里提前判定，
  // 直接把可照做的做法写到卡片上，也不去开那张注定要关掉的窗口。
  const orphanItem = items.find(
    (item) => item.draftId && !(state.drafts || []).some((draft) => draft.id === item.draftId),
  );
  if (orphanItem) {
    const reason = "这条内容对应的草稿已经不在工作台里了（可能被删除），请回到草稿重新「加入发布列表」后再发。";
    state.publish = state.publish.map((item) => (item.id === orphanItem.id
      ? { ...item, publishStatus: "failed", publishError: reason }
      : item));
    saveState();
    renderPublishList();
    toast(`「${orphanItem.title || "未命名内容"}」${reason}`, { kind: "error", duration: 7000 });
    return;
  }
  // 发布列表可能存着修复前加入的超限内容，也可能被手动改过：跳转前再判一次，
  // 不要让用户到了小红书发布页才发现填不进去。
  const overLimitItem = items
    .map((item) => ({ item, problems: xhsPublishLimitMessages(item) }))
    .find(({ problems }) => problems.length);
  if (overLimitItem) {
    toast(`「${overLimitItem.item.title || "未命名内容"}」${overLimitItem.problems.join("；")}，请修改后再发布`, { kind: "error", duration: 7000 });
    return;
  }
  const done = trigger ? setButtonLoading(trigger, "准备发布任务...") : () => {};
  updatePublishTakeoverHint(0, null);
  selectedIds.forEach((id) => publishAttemptsInFlight.add(id));
  applyPublishAttemptState(selectedIds);
  const publishWindow = openPublishWindow(selectedIds);
  try {
    // 先落库：条目要等这次 /api/state 写入才注册成 canonical 资源，抢在它前面建任务
    // 只会拿到 404「Resource not found」，刚打开的窗口随即被关掉（用户看到的就是"瞬间关闭"）。
    const persistence = await flushWorkspaceForPublish();
    if (!persistence.ok) throw new Error(publishPersistenceFailureMessage(persistence));
    items = state.publish.filter((item) => selectedIds.includes(item.id));
    if (!items.length) throw new Error("这条内容已经不在发布列表里了，请刷新后重新加入");
    const data = await createPublishTask(items);
    const taskId = data.task?.id || "";
    if (data.task && taskId) {
      // Retain the durable task locally until backend hydration refreshes it.
      const existingTasks = Array.isArray(state.publishTasks) ? state.publishTasks : [];
      state.publishTasks = [
        data.task,
        ...existingTasks.filter((task) => task?.id !== taskId),
      ].slice(0, 50);
    }
    state.publish = state.publish.map((item) => selectedIds.includes(item.id)
      ? { ...item, publishStatus: "opened", xhsPublishTaskId: taskId, publishError: "" }
      : item);
    saveState();
    renderPublishList();
    if (!publishWindow) {
      toast("发布任务已准备，请允许浏览器打开小红书发布页", { kind: "error", duration: 5000 });
    } else {
      publishWindow.location.href = data.taskUrl;
      watchXiaohongshuPublishTask(taskId);
      // 这里探测不到扩展装没装（扩展只匹配小红书域，没有 externally_connectable），
      // 所以只能说"装了会怎样、没装怎么办"，不能替用户断言"会自动填入内容"（P48）。
      // P63/P64：真正"没接管"的原因与处置，由下面的 updatePublishTakeoverHint 兜底说明。
      toast("已打开小红书发布页：装好「薯作台」发布助手的话会自动填入内容，否则请在发布页手动填写", { duration: 6000 });
    }
  } catch (error) {
    // 失败就不再留那张空标签页：占位页/创作页都已经没有意义，留着只会让用户以为
    // "发布了"。真正要说的话在列表的「原因：」和下面的 toast 里。
    closePublishWindow(publishWindow);
    // Record the reason on each selected item so the publish list can show what
    // went wrong together with a manual retry entry.
    state.publish = state.publish.map((item) => selectedIds.includes(item.id)
      ? { ...item, publishStatus: "failed", publishError: String(error?.message || "").slice(0, 200) }
      : item);
    saveState();
    renderPublishList();
    toast(`准备小红书发布失败：${error.message}`, { kind: "error", duration: 5000 });
  } finally {
    // 就地解锁，不整表重渲染：prepare 返回后 renderPublishList() 刚重建过按钮，
    // 重渲染一次会把「保存表现」表单里还没提交的输入一起抹掉。
    selectedIds.forEach((id) => publishAttemptsInFlight.delete(id));
    applyPublishAttemptState(selectedIds);
    done();
  }
}

// The publish takeover shares the collector's silent failure mode: the task is
// created, the page waits, and nothing says the extension never came. Publish
// items start as "ready" and only the extension moves them off it.
function updatePublishTakeoverHint(attempt = 0, task = null) {
  const node = $("#publishTakeoverMessage");
  if (!node) return;
  const items = Array.isArray(task?.items) ? task.items : [];
  const untouched = items.length > 0 && items.every((item) => String(item?.status || "") === "ready");
  const waitingSeconds = Math.max(0, Number(attempt) || 0) * 2;
  const stalled = untouched && waitingSeconds >= PUBLISH_TAKEOVER_HINT_SECONDS;
  node.hidden = !stalled;
  node.dataset.stalled = stalled ? "1" : "0";
  if (!stalled) {
    node.textContent = "";
    return;
  }
  // 提示要能解释"为什么没接管、现在能做什么"：小红书未登录时插件会停在登录页等登录
  // （不消费交接凭证，P63）；扩展被关掉/浏览器重开后本页的旧上下文已失效，只能由工作台
  // 重新发起一次发布（P64）。
  node.textContent = `插件未接管（已等待 ${waitingSeconds} 秒）：请确认浏览器已安装「薯作台」发布助手并在扩展页重新加载、小红书发布页保持打开；`
    + "安装与使用说明见右上角「看使用教程」里的「浏览器插件」一节。"
    + "如果小红书还没登录，请先在发布页登录（登录后会自动继续填入，交接凭证不会因此失效）；"
    + "如果刚重载过扩展、关过浏览器或改过插件，请回到本页点「重试」重新打开发布页（会重新签发一次交接凭证）；"
    + "如果你同时使用本地和线上工作站，请在插件弹窗里把「工作站地址」改成当前正在用的这一个。";
}

function watchXiaohongshuPublishTask(taskId, attempt = 0) {
  if (!taskId || attempt > 120) return;
  window.setTimeout(async () => {
    try {
      const data = await getBackend(`/api/publish/task/${encodeURIComponent(taskId)}`);
      const taskItems = data.task?.items || [];
      updatePublishTakeoverHint(attempt, data.task);
      let changed = false;
      state.publish = state.publish.map((item) => {
        const taskItem = taskItems.find((entry) => entry.id === item.id);
        if (!taskItem) return item;
        if (taskItem.status === "published" && item.publishStatus !== "published") {
          changed = true;
          return { ...item, publishStatus: "published", publishError: "" };
        }
        if (taskItem.status === "failed" && item.publishStatus !== "failed") {
          changed = true;
          return {
            ...item,
            publishStatus: "failed",
            publishError: String(taskItem.error || "").slice(0, 200),
          };
        }
        return item;
      });
      if (changed) {
        saveState();
        renderPublishList();
      }
      // Stop polling once every item reached a terminal state; a failed item
      // waits for the user's manual retry instead of spinning forever.
      const pending = taskItems.some((item) => !["published", "failed"].includes(String(item.status || "")));
      if (pending) watchXiaohongshuPublishTask(taskId, attempt + 1);
    } catch {
      if (attempt < 10) watchXiaohongshuPublishTask(taskId, attempt + 1);
    }
  }, 2000);
}

async function consumeExtensionHandoff() {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const nonce = (fragment.get("handoff") || "").trim();
  const tenantId = (fragment.get("tenant") || "").trim();
  if (!nonce || !tenantId) return;
  const clearFragment = () => {
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, "", cleanUrl);
  };
  // Remove the one-time nonce before any network wait so it cannot linger in
  // the address bar/history if the consume request hangs or the page crashes.
  clearFragment();
  try {
    const response = await fetch(`${backendBaseUrl}/api/extension/handoff/consume`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, nonce, purpose: "rewrite" }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) throw backendError(data, response.status, response);
    const payload = data.handoff?.payload || {};
    const importedFields = normalizeImportedNoteFields(payload.body ?? "", payload.tags ?? "");
    const images = normalizeImages(payload.images || []);
    const draft = createDraft({
      source: "plugin",
      seed: payload.title || "插件采集笔记",
      origin: payload.url || "",
      title: payload.title || undefined,
      body: importedFields.body,
      tags: importedFields.tags,
      images: images.length ? images : [sampleImages[3]],
    });
    draft.metrics = payload.metrics || {};
    saveState();
    setSection("rewrite");
  } catch (error) {
    toast(`插件交接失败：${error.message || "请重试"}`, { kind: "error", duration: 5000 });
  }
}

function openOriginalTemplateLibrary() {
  state.activeWorkflow = "original";
  setSection("viral");
  setViralWorkbench("topic");
}

function createOriginalDraftFromTemplateLibrary() {
  createDraft({ source: "blank", seed: "手动创建", images: [], workflow: "original" });
  openOriginalTemplateLibrary();
}


// ----- js/shell/tutorials.js -----
// 站内使用教程（3.x）：把 web/tutorial-assets 里那批图文真正挂到页面上。
//
// 为什么要有这一页：图文素材早在仓库里（01-09），但没有任何页面引用它们，
// 等于没有教程。这里只做**结构**：分段 + 步骤 + 配图；每一步的说明文案是可读的
// 一句话，正式的推广文案由业务给（换文案不用改代码，改这个数组即可）。
//
// 诚实的地方：注册/登录/充值三步目前没有截图，就按"无图步骤"渲染并显示一句
// 「配图待补」，而不是拿别的图冒充——缺什么一眼能看见。

const TUTORIAL_ASSET_ROOT = "/tutorial-assets/";

const TUTORIAL_SECTIONS = [
  {
    key: "start",
    title: "第一次使用",
    description: "从打开网站到拿到第一篇可发布的内容。",
    steps: [
      { title: "注册 / 登录", body: "用邮箱注册或登录；忘记密码可以在登录框里点「忘记密码」，用邮箱验证码重置。", image: null },
      { title: "开通会员或充值", body: "会员与加量包都在「开通会员」里；新手会先拿到试用额度，够跑通一遍流程。", image: null },
      {
        title: "认识工作台",
        body: "左侧是六条主线：快速笔记、原创笔记、AI 生图、知识库、低粉爆款采集、发布列表。",
        image: "01-flow-overview.png",
      },
      { title: "出第一篇", body: "选「快速笔记」导入内容 → 确认标题正文标签 → 生成结果 → 在发布列表里发布或导出。", image: null },
    ],
  },
  {
    key: "quick-note",
    title: "快速笔记：两种导入方式",
    description: "有链接就用链接，有图就用图生文案。",
    steps: [
      { title: "粘贴小红书链接", body: "把笔记链接贴进输入框，后端抓取正文后改写。抓取失败会说明原因（多半是没有登录或被小红书拦下）。", image: "02-link-entry.webp" },
      { title: "上传图片生成文案", body: "上传几张图，AI 给出标题、正文与标签。", image: "03-upload-entry.webp" },
    ],
  },
  {
    key: "image",
    title: "AI 生图",
    description: "写提示词 → 选模板 → 进编辑器微调。",
    steps: [
      { title: "写清楚要什么", body: "描述主体、场景与风格；越具体越省重抽。", image: "04-image-prompt.webp" },
      { title: "挑一个模板", body: "模板决定版式与字体，可以在生成前指定。", image: "05-image-templates.webp" },
      { title: "生成后进编辑器", body: "不满意的图直接在编辑器里换元素、改文字，不用整张重做。", image: "06-image-to-editor.webp" },
    ],
  },
  {
    key: "plugin",
    title: "浏览器插件",
    description: "插件只做两件事：接管小红书页面的采集，以及在发布时自动填内容。采集本身不扣积分。",
    steps: [
      {
        title: "按关键词采集",
        body: "在工作站「低粉爆款采集」填好主词并开始采集，插件会接管打开的小红书标签页逐条采集。",
        image: "09-plugin-keyword.webp",
      },
      {
        title: "接管图文发布",
        body: "在工作站把内容加入发布列表并点「发布」，插件会在小红书创作者页自动上传图片、填入标题正文与话题；最后一步由你点小红书官方发布按钮。",
        image: null,
      },
    ],
  },
  // 4.9「完成态」：一条线走完之后，成品在哪、能做什么、谁负责发布——过去分散在各工作区，
  // 没有一处讲清（验收方按 §3.2 的 4.9 提过）。这里是那一页说明。
  {
    key: "deliver",
    title: "成品能做什么（导出与发布）",
    description: "一条线走完之后：文案与图片怎么拿走、发布由谁完成。",
    steps: [
      {
        title: "复制文案",
        body: "草稿里的标题、正文、标签是三个可编辑框，直接选中复制即可；不需要先导出文件。",
        image: null,
      },
      {
        title: "下载图片",
        body: "在「AI 生图」的「已保存图片」里，每张图都有「下载」按钮；单张生成结果上也有。",
        image: null,
      },
      {
        title: "一次带走全部",
        body: "草稿区工具栏的「导出我的作品」会把本组织的草稿文案（每篇一个 markdown）、引用到的图片与一份 manifest 清单打包成 zip；万一有图片文件缺失，会如实记进清单而不是静默少打包。",
        image: null,
      },
      {
        title: "发布",
        body: "把内容加入「发布列表」→ 点「发布」→ 插件在小红书创作者页自动上传图片、填入标题正文与话题；最后一屏由你点小红书的官方发布按钮。也可以只把文案与图片导出，自己在小红书里发。",
        image: null,
      },
      {
        title: "责任边界",
        body: "平台不代你发布、也不替你审核内容：生成结果的质量与合规由你判断，发布账号与发布行为也在你这边。",
        image: null,
      },
    ],
  },
];

function tutorialStepMarkup(step) {
  const title = escapeHtml(step.title || "");
  const body = escapeHtml(step.body || "");
  const image = step.image
    ? `<img class="tutorial-step-image" src="${escapeHtml(TUTORIAL_ASSET_ROOT + step.image)}" alt="${title}" loading="lazy" />`
    : `<p class="tutorial-step-placeholder">配图待补</p>`;
  return `<li class="tutorial-step">${image}<div class="tutorial-step-copy"><strong>${title}</strong><p>${body}</p></div></li>`;
}

function renderTutorials() {
  const node = document.getElementById("tutorialsBody");
  if (!node) return;
  node.innerHTML = TUTORIAL_SECTIONS.map(
    (section) => `<section class="tutorial-section">
      <header><h3>${escapeHtml(section.title)}</h3><p>${escapeHtml(section.description)}</p></header>
      <ol class="tutorial-steps">${section.steps.map(tutorialStepMarkup).join("")}</ol>
    </section>`
  ).join("");
}

function openTutorialsModal() {
  const modal = document.getElementById("tutorialsModal");
  if (!modal) return;
  renderTutorials();
  if (typeof showModal === "function") showModal(modal);
}

function initTutorials() {
  document.getElementById("openTutorialsBtn")?.addEventListener("click", () => openTutorialsModal());
  document.getElementById("closeTutorialsBtn")?.addEventListener("click", () => {
    const modal = document.getElementById("tutorialsModal");
    if (modal && typeof hideModal === "function") hideModal(modal);
  });
}

// ----- js/shell/events.js -----
// 顶栏下拉：治理（团队/平台）与账号（AI 配置/退出）。点了面板里的按钮就收起，
// ── 移动端左侧抽屉：把顶部导航的 brand + 全部图标 + 文字收起进侧栏抽屉。
//    桌面端不显示（CSS 在 ≥820px 时把 .topbar-hamburger / .mobile-drawer 隐藏）。
//    点击抽屉里的任一导航项就走与 .global-nav-item 完全一致的工作流 → setSection → 关抽屉。
//    brand 项额外绑了一个「回到首页」的行为，避免移动端首页入口缺失。
function bindMobileDrawer() {
  const toggle = document.getElementById("mobileDrawerToggle");
  const drawer = document.getElementById("mobileDrawer");
  const backdrop = document.getElementById("mobileDrawerBackdrop");
  const brandBtn = document.getElementById("mobileDrawerBrand");
  if (!toggle || !drawer || !backdrop) return;

  const items = drawer.querySelectorAll(".mobile-drawer-item[data-section]");

  const setOpen = (open) => {
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    drawer.dataset.open = open ? "1" : "";
    backdrop.dataset.open = open ? "1" : "";
    backdrop.hidden = !open;
    toggle.setAttribute("aria-expanded", String(open));
    document.body.classList.toggle("mobile-drawer-open", open);
  };

  toggle.addEventListener("click", () => {
    setOpen(drawer.dataset.open !== "1");
  });
  backdrop.addEventListener("click", () => setOpen(false));

  brandBtn?.addEventListener("click", () => {
    setSection("home");
    setOpen(false);
  });

  items.forEach((button) => {
    if (button === brandBtn) return;
    button.addEventListener("click", () => {
      const workflow = button.dataset.globalWorkflow || "tools";
      state.activeWorkflow = workflow;
      // 原创笔记按钮：点击 toggle 四库子菜单显隐，不关抽屉让用户看到子菜单
      const subnav = button.nextElementSibling?.classList?.contains("mobile-drawer-subnav")
        ? button.nextElementSibling : null;
      if (subnav) {
        const willOpen = subnav.hidden;
        // 先关掉所有同类 subnav（一次只展开一个）
        drawer.querySelectorAll(".mobile-drawer-subnav").forEach((s) => { s.hidden = true; });
        subnav.hidden = !willOpen;
      }
      const firstEntry = !enteredWorkflowsThisSession.has(workflow);
      const section = ["replica", "original"].includes(workflow)
        ? (firstEntry ? button.dataset.section : lastSectionForWorkflow(workflow))
        : button.dataset.section;
      setSection(section);
      // 有子菜单的不关抽屉（让用户继续点四库），没子菜单的直接关
      if (!subnav) setOpen(false);
    });
  });

  // 四库模板子菜单按钮：走 .nav-item 统一事件代理（events.js:157）做 setSection + setViralWorkbench，
  // 这里只额外关抽屉——不然点了子菜单后抽屉不关，挡住刚切到的 viral section。
  drawer.querySelectorAll(".mobile-drawer-subitem").forEach((button) => {
    button.addEventListener("click", () => setOpen(false));
  });

  // Esc 关抽屉
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawer.dataset.open === "1") setOpen(false);
  });

  // 监听 setSection 同步抽屉里的 active 高亮（用 MutationObserver 抓 .section.active 变化）
  const sectionsRoot = document.querySelector(".app-shell") || document.body;
  const syncActive = () => {
    const activeId = document.querySelector(".section.active")?.id?.replace(/^section-/, "") || "";
    items.forEach((button) => {
      const targetSection = button.dataset.section;
      // 治理菜单项用 ID 单独匹配
      if (button.id === "mobileDrawerAuthMgmt" || button.id === "mobileDrawerAdminConsole") {
        button.classList.toggle("active", activeId === targetSection);
      } else if (button.dataset.globalWorkflow === "replica" || button.dataset.globalWorkflow === "original") {
        button.classList.toggle("active", activeId === targetSection);
      } else {
        button.classList.toggle("active", activeId === targetSection);
      }
    });
    // 治理组可见性：与治理下拉一致
    syncMobileGovernanceVisibility();
  };
  const observer = new MutationObserver(syncActive);
  observer.observe(sectionsRoot, { attributes: true, subtree: true, attributeFilter: ["class"] });
  // 兜底：登录态变化时刷新治理组可见性（登入/登出会切换 #authManagementNav 等的 hidden）
  setTimeout(syncActive, 0);
}

// 点击空白或 Esc 也收起。面板挂到 body 上：顶栏的 .global-nav 有 overflow: hidden，
// header 又有 backdrop-filter，留在原地会被裁掉/定位错，所以每次打开按触发器重新定位。
function bindTopbarMenu(triggerId, panelId) {
  const trigger = document.getElementById(triggerId);
  const panel = document.getElementById(panelId);
  if (!trigger || !panel) return;
  document.body.appendChild(panel);
  const place = () => {
    const rect = trigger.getBoundingClientRect();
    const width = panel.offsetWidth;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    panel.style.top = `${Math.round(rect.bottom + 6)}px`;
    panel.style.left = `${Math.round(left)}px`;
  };
  // 账号名在登录完成时就已经写进 DOM（见 session.js 的 syncAuthUi），这里只是打开菜单时
  // 兜底刷新一次：取不到就保持原值，绝不能用占位符覆盖已就绪的真实账号名。
  const refreshAccountName = async () => {
    if (!panel.querySelector("#accountName")) return;
    if (typeof refreshAccountNameText === "function") {
      await refreshAccountNameText(globalThis.xhsSpaAuth, panel.querySelector("#accountName"));
    }
  };
  const setOpen = (open) => {
    panel.classList.toggle("hidden", !open);
    trigger.setAttribute("aria-expanded", String(open));
    if (open) {
      void refreshAccountName();
      place();
    }
  };
  trigger.addEventListener("click", (event) => {
    event.stopPropagation();
    setOpen(panel.classList.contains("hidden"));
  });
  panel.addEventListener("click", (event) => {
    if (event.target.closest("button")) setOpen(false);
  });
  document.addEventListener("click", (event) => {
    if (panel.classList.contains("hidden")) return;
    if (event.target === trigger || trigger.contains(event.target) || panel.contains(event.target)) return;
    setOpen(false);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setOpen(false);
  });
  window.addEventListener("resize", () => setOpen(false));
}

function bindTopbarMenus() {
  bindTopbarMenu("governanceNavBtn", "governanceNavMenu");
  bindTopbarMenu("accountMenuBtn", "accountMenuPanel");
  if (typeof initAccountSettings === "function") initAccountSettings();
  if (typeof initMyOrganizations === "function") initMyOrganizations();
  if (typeof initPromptSettings === "function") initPromptSettings();
  if (typeof initOrgKnowledge === "function") initOrgKnowledge();
  if (typeof initOpsConfig === "function") initOpsConfig();
  if (typeof initTutorials === "function") initTutorials();
  // 治理下拉里的两个入口不再是 .global-nav-item，拿不到那套批量绑定，因此这里补上
  // 团队治理的路由（平台治理有自己的 handler，含登录与权限检查）。
  document.getElementById("authManagementNav")?.addEventListener("click", () => {
    state.activeWorkflow = "tools";
    setSection("auth-management");
  });
  document.getElementById("supportEntryBtn")?.addEventListener("click", () => openSupportModal());
}

function bindEvents() {
  window.addEventListener("resize", syncPreviewScale);
  window.visualViewport?.addEventListener("resize", syncPreviewScale);
  bindTopbarMenus();
  bindRenameOrganizationModal();
  window.addEventListener("focus", () => {
    if (state.currentSection === "viral") {
      loadLayoutTemplates();
    }
  });
  $$(".nav-item").forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.navGroupToggle) toggleNavGroup(button);
      if (button.dataset.workflow) state.activeWorkflow = button.dataset.workflow;
      if (button.dataset.viralNavKind) state.viralActiveWorkbench = button.dataset.viralNavKind;
      setSection(button.dataset.section);
      if (button.dataset.viralNavKind) setViralWorkbench(button.dataset.viralNavKind);
    });
  });
  $$(".global-nav-item").forEach((button) => {
    button.addEventListener("click", () => {
      const workflow = button.dataset.globalWorkflow || "tools";
      state.activeWorkflow = workflow;
      const firstEntry = !enteredWorkflowsThisSession.has(workflow);
      const section = ["replica", "original"].includes(workflow)
        ? (firstEntry ? button.dataset.section : lastSectionForWorkflow(workflow))
        : button.dataset.section;
      setSection(section);
    });
  });
  bindMobileDrawer();
  $("#homeBrandBtn")?.addEventListener("click", () => setSection("home"));
  $("#workspaceQuickAction")?.addEventListener("click", () => {
    if (!["replica", "original"].includes(state.activeWorkflow)) {
      state.activeWorkflow = draftWorkflow(currentDraft());
    }
    setSection("rewrite");
  });
  $$('[data-open-extension-tutorial]').forEach((button) => button.addEventListener("click", openExtensionTutorial));
  // 首页插件状态旁边的手动刷新：强制重问一次，用户不必刷新整个网页。
  $$("[data-refresh-extension-status]").forEach((button) => {
    button.addEventListener("click", () => {
      if (typeof refreshExtensionStatusFromButton === "function") void refreshExtensionStatusFromButton(button);
    });
  });
  $("#closeExtensionTutorialBtn")?.addEventListener("click", closeExtensionTutorial);
  $("#extensionTutorialModal")?.addEventListener("click", (event) => {
    if (event.target.id === "extensionTutorialModal") closeExtensionTutorial();
  });
  $$("[data-section-target]").forEach((button) => {
    button.addEventListener("click", () => {
      const intakeTabTarget = button.dataset.replicaIntakeTabTarget;
      if (button.dataset.sectionTarget === "intake" && intakeTabTarget) {
        $(`#section-intake [data-link-import-tab="${intakeTabTarget}"]`)?.click();
      }
      if (button.dataset.workflowTarget) state.activeWorkflow = button.dataset.workflowTarget;
      setSection(button.dataset.sectionTarget);
    });
  });
  const authManagementContent = $("#authManagementContent");
  authManagementContent?.addEventListener("click", async (event) => {
    if (event.target.closest("[data-governance-team-retry]")) {
      await loadAuthManagement(true);
      return;
    }
    const inviteButton = event.target.closest("[data-auth-management-go-invite]");
    if (inviteButton) {
      authManagementState.invitationFilter = "pending";
      authManagementState.tab = "invitations";
      authManagementState.error = "";
      authManagementState.notice = "";
      await loadAuthManagement(true);
      authManagementContent.querySelector("input[name=\"email\"]")?.focus();
      return;
    }
    const tab = event.target.closest("[data-auth-management-tab]");
    if (tab) {
      event.preventDefault();
      authManagementState.tab = tab.dataset.authManagementTab || "members";
      authManagementState.error = "";
      authManagementState.notice = "";
      // 强制重取：邀请列表与成员列表是两套缓存，切页签命中缓存的话，
      // 别人接受邀请之后这一页永远停在旧快照上，除非有人想起来去点「刷新」。
      await loadAuthManagement(true);
      return;
    }
    const copyInvitation = event.target.closest("[data-copy-invitation-link]");
    if (copyInvitation) {
      try {
        await navigator.clipboard.writeText(authManagementState.invitationLink);
        authManagementState.notice = "邀请链接已复制";
      } catch (_error) {
        authManagementState.error = "复制失败，请手动复制邀请链接";
      }
      renderAuthManagement();
      return;
    }
    const pageButton = event.target.closest("[data-auth-member-page]");
    if (pageButton) {
      const delta = pageButton.getAttribute("data-auth-member-page") === "next" ? 1 : -1;
      authManagementState.memberPage = Math.max(1, (Number(authManagementState.memberPage) || 1) + delta);
      renderAuthManagement();
      return;
    }
    if (event.target.closest("[data-auth-bulk-start], [data-auth-bulk-clear]")) {
      authManagementState.selected = new Set();
      authManagementState.bulkMode = Boolean(event.target.closest("[data-auth-bulk-start]"));
      authManagementState.error = "";
      authManagementState.notice = "";
      renderAuthManagement();
      authManagementContent.querySelector(authManagementState.bulkMode ? "[data-auth-bulk-clear]" : "[data-auth-bulk-start]")?.focus();
      return;
    }
    if (event.target.closest("[data-auth-bulk-apply]")) {
      openAuthBulkDialog("role");
      return;
    }
    if (event.target.closest("[data-auth-bulk-remove]")) {
      openAuthBulkDialog("remove");
      return;
    }
    const actionTarget = event.target.closest("[data-auth-remove-member], [data-auth-resend-invitation], [data-auth-revoke-invitation]");
    if (!actionTarget) return;
    const action = actionTarget.dataset.authRemoveMember !== undefined ? "remove-member"
      : actionTarget.dataset.authResendInvitation !== undefined ? "resend-invitation"
        : "revoke-invitation";
    actionTarget.disabled = true;
    try {
      await handleAuthManagementAction(action, actionTarget);
    } catch (error) {
      authManagementState.error = error.message || "权限操作失败";
      renderAuthManagement();
    } finally {
      actionTarget.disabled = false;
    }
  });
  // 重命名组织只是打开弹层，不改数据，所以不走上面的 mutation 分支。
  authManagementContent?.addEventListener("click", (event) => {
    if (event.target.closest("#renameOrganizationBtn")) openRenameOrganizationModal();
  });
  authManagementContent?.addEventListener("change", (event) => {
    const box = event.target.closest("[data-auth-member-select]");
    if (!box) return;
    const id = box.getAttribute("data-auth-member-select");
    const selected = authManagementState.selected instanceof Set
      ? authManagementState.selected
      : new Set();
    if (box.checked) selected.add(id);
    else selected.delete(id);
    authManagementState.selected = selected;
    renderAuthManagement();
    Array.from(authManagementContent.querySelectorAll("[data-auth-member-select]")).find((input) => input.dataset.authMemberSelect === id)?.focus();
  });
  authManagementContent?.addEventListener("input", (event) => {
    const email = event.target.closest('form[data-auth-form="invite"] input[name="email"]');
    if (email) authManagementState.invitationDraft.email = email.value;
    const search = event.target.closest("[data-auth-member-search]");
    if (!search) return;
    authManagementState.memberQuery = search.value;
    authManagementState.memberPage = 1;
    renderAuthManagement();
    const next = authManagementContent?.querySelector("[data-auth-member-search]");
    next?.focus();
    if (next && typeof next.value === "string") next.setSelectionRange(next.value.length, next.value.length);
  });
  authManagementContent?.addEventListener("change", async (event) => {
    const invitationFilter = event.target.closest("[data-auth-invitation-filter]");
    if (invitationFilter) {
      authManagementState.invitationFilter = invitationFilter.value || "pending";
      renderAuthManagement();
      authManagementContent.querySelector("[data-auth-invitation-filter]")?.focus();
      return;
    }
    const inviteRole = event.target.closest("[data-auth-invite-role]");
    if (inviteRole) {
      authManagementState.invitationDraft.role = inviteRole.value;
      const hint = authManagementContent.querySelector("[data-auth-role-hint]");
      if (hint) hint.textContent = authMemberRoleHint(inviteRole.value);
      return;
    }
    const role = event.target.closest("[data-auth-member-role]");
    if (!role) return;
    role.disabled = true;
    try {
      await handleAuthManagementAction("member-role", role);
    } catch (error) {
      authManagementState.error = error.message || "成员角色更新失败";
      renderAuthManagement();
    } finally {
      role.disabled = false;
    }
  });
  authManagementContent?.addEventListener("submit", async (event) => {
    const form = event.target.closest("form[data-auth-form]");
    if (!form) return;
    event.preventDefault();
    const submitter = event.submitter || form.querySelector("button[type=submit]");
    if (submitter) submitter.disabled = true;
    authManagementState.error = "";
    authManagementState.notice = "";
    try {
      const changed = await submitAuthManagementForm(form, submitter);
      renderAuthManagement();
      if (changed) await loadAuthManagement(true);
    } catch (error) {
      authManagementState.error = error.message || "权限操作失败";
      renderAuthManagement();
    } finally {
      if (submitter) submitter.disabled = false;
    }
  });
  $("#refreshAuthManagementBtn")?.addEventListener("click", async () => {
    authManagementState.loaded = {};
    authManagementState.error = "";
    authManagementState.notice = "";
    await loadAuthManagement(true);
  });
  $("#openBillingBtn")?.addEventListener("click", () => {
    loadBillingStatus().then(openBillingPanel);
  });
  $("#billingModalContent")?.addEventListener("click", async (event) => {
    // 先收掉收银台再开「我的组织」：两个弹层同开是叠罗汉，而且这一跳的语义就是"换个视角看"。
    if (event.target.closest("[data-billing-open-organizations]")) {
      closeBillingPanel();
      if (typeof openMyOrganizations === "function") openMyOrganizations();
      return;
    }
    const checkoutDismiss = event.target.closest("[data-billing-checkout-cancel],[data-billing-checkout-done]");
    if (checkoutDismiss) {
      closeBillingPanel();
      return;
    }
    const retry = event.target.closest("[data-billing-retry]");
    if (retry) {
      retry.disabled = true;
      try {
        await loadBillingStatus();
        openBillingPanel();
      } finally {
        retry.disabled = false;
      }
      return;
    }
    const qrRefresh = event.target.closest("[data-billing-qr-refresh]");
    if (qrRefresh) {
      await regenerateBillingQr(qrRefresh);
      return;
    }
    const buy = event.target.closest("[data-billing-buy]");
    if (!buy) return;
    buy.disabled = true;
    try {
      await createBillingOrder(buy.dataset.billingBuy);
    } catch (error) {
      toast(`创建支付订单失败：${error.message}`, { kind: "error", duration: 6000 });
    } finally {
      buy.disabled = false;
    }
  });
  $("#closeBillingBtn")?.addEventListener("click", () => {
    // 成功弹窗必须由用户点"完成"关闭，X 按钮此时不响应。
    if (!billingCheckoutSucceeded) closeBillingPanel();
  });
  $("#billingModal")?.addEventListener("click", (event) => {
    if (event.target.id === "billingModal" && !billingCheckoutSucceeded) closeBillingPanel();
  });
  $("#homeRecentDrafts")?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-home-draft-id]");
    if (!button) return;
    state.activeWorkflow = button.dataset.homeWorkflow === "replica" ? "replica" : "original";
    setActiveDraftId(button.dataset.homeDraftId);
    setSection("rewrite");
  });
  $("#homeViewTasksBtn")?.addEventListener("click", () => {
    if (!["replica", "original"].includes(state.activeWorkflow)) {
      state.activeWorkflow = state.drafts[0] ? draftWorkflow(state.drafts[0]) : "replica";
    }
    setSection("rewrite");
  });
  $("#openAiConfigBtn").addEventListener("click", openAiConfig);
  $("#parseLinksBtn").addEventListener("click", handleLinks);
  $("#originalImportBtn")?.addEventListener("click", handleOriginalLink);
  $$("[data-link-import-tab]").forEach((tab) => tab.addEventListener("click", () => {
    const shell = tab.closest(".link-import-shell");
    if (!shell) return;
    const target = tab.dataset.linkImportTab;
    shell.querySelectorAll("[data-link-import-tab]").forEach((item) => {
      const active = item === tab;
      item.classList.toggle("active", active);
      item.setAttribute("aria-selected", String(active));
    });
    shell.querySelectorAll("[data-link-import-panel]").forEach((panel) => {
      const active = panel.dataset.linkImportPanel === target;
      panel.classList.toggle("active", active);
      panel.hidden = !active;
    });
    if (shell.closest("#section-intake")) syncReplicaIntakeCopy(target);
  }));
  $("#imageInput")?.addEventListener("change", (event) => {
    handleImageInput(event.target.files, "#uploadPreview", "replica");
    event.target.value = "";
  });
  $("#folderInput")?.addEventListener("change", (event) => handleFolderInput(event.target.files, "replica"));
  $("#uploadToRewriteBtn")?.addEventListener("click", () => uploadToRewrite("#uploadPreview", "replica"));
  // 原创笔记的图片导入已下线（那一页只剩链接导入），图片入口只有快速笔记的「图生文案」。
  [
    ["#uploadPreview", "replica"],
  ].forEach(([selector, workflow]) => $(selector)?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-remove-pending-upload]");
    if (!button) return;
    removePendingUploadImage(workflow, Number(button.dataset.uploadIndex), selector);
  }));
  $$('[data-new-draft]').forEach((button) => button.addEventListener("click", () => {
    if (state.activeWorkflow === "original") return createOriginalDraftFromTemplateLibrary();
    createDraft({ source: "blank", seed: "手动创建", images: [], workflow: state.activeWorkflow });
    setSection("rewrite");
  }));
  $("#adminConsoleNav")?.addEventListener("click", () => {
    const auth = globalThis.xhsSpaAuth;
    if (auth?.configured) {
      auth.isAuthenticated?.().then((authenticated) => {
        if (!authenticated) {
          toast("请先登录后再打开管理后台", { kind: "error" });
          setSection("home");
          return;
        }
        setSection("admin-console");
      });
      return;
    }
    setSection("admin-console");
  });
  $("#refreshAdminConsoleBtn")?.addEventListener("click", refreshAdminConsole);
  $$('[data-workflow-intake]').forEach((button) => button.addEventListener("click", () => {
    setSection(state.activeWorkflow === "replica" ? "intake" : "original");
  }));
  $$('[data-workflow-template-library]').forEach((button) => button.addEventListener("click", openOriginalTemplateLibrary));
  $("#deleteAllDraftsBtn").addEventListener("click", requestDeleteAllDrafts);
  $("#closeDeleteAllDraftsBtn").addEventListener("click", closeDeleteAllDraftsModal);
  $("#cancelDeleteAllDraftsBtn").addEventListener("click", closeDeleteAllDraftsModal);
  $("#confirmDeleteAllDraftsBtn").addEventListener("click", deleteAllDrafts);
  $("#deleteAllDraftsModal").addEventListener("click", (event) => {
    if (event.target.id === "deleteAllDraftsModal") closeDeleteAllDraftsModal();
  });

  $("#draftList").addEventListener("click", (event) => {
    const deleteButton = event.target.closest("[data-delete-draft-id]");
    if (deleteButton) {
      event.stopPropagation();
      deleteDraft(deleteButton.dataset.deleteDraftId);
      return;
    }
    const item = event.target.closest("[data-draft-id]");
    if (!item) return;
    state.previewImageIndex = 0;
    setActiveDraftId(item.dataset.draftId);
    state.activeDraftId = item.dataset.draftId;
    restoreViralSelectionsFromDraft(currentDraft());
    saveState();
    renderEditor();
  });
  $("#draftList").addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.target.closest("button")) return;
    const item = event.target.closest("[data-draft-id]");
    if (!item) return;
    event.preventDefault();
    state.previewImageIndex = 0;
    setActiveDraftId(item.dataset.draftId);
    state.activeDraftId = item.dataset.draftId;
    restoreViralSelectionsFromDraft(currentDraft());
    saveState();
    renderEditor();
  });

  $("#clearDraftBtn").addEventListener("click", clearCurrentRewriteDraft);

  ["draftTitle", "draftBody", "draftTags"].forEach((id) => {
    const field = $(`#${id}`);
    field.addEventListener("input", () => {
      resizeRewriteContentField(field);
      syncFormToDraft();
    });
  });

  $("#titleAiBtn").addEventListener("click", () => toggleDraftAiBlock("title"));
  $("#bodyAiBtn").addEventListener("click", () => toggleDraftAiBlock("body"));
  $("#tagsAiBtn").addEventListener("click", () => toggleDraftAiBlock("tags"));
  $("#rewriteBtn").addEventListener("click", () => {
    const draft = currentDraft();
    if (viralPromptBuildController) {
      if (viralPromptTaskBelongsToRewriteDraft(draft)) {
        composeViralDraft({ preventDefault() {}, currentTarget: $("#rewriteBtn") });
        return;
      }
    }
    if (rewriteGenerationController) return rewriteDraft();
    // Branch on the runtime import owner, not on the persisted templateImportStatus:
    // a stale "importing" flag sent this click into the template-import flow while
    // the button still read 「生成 1 份完整图文」.
    if (isTemplateDecompositionDraft(draft) || isTemplateImportRunningForDraft(draft)) {
      importCurrentDraftToViralFactory($("#rewriteBtn"));
      return;
    }
    if (isTemplateImportAwaitingSelection(draft)) {
      openTemplateLibraryForDraft();
      return;
    }

    if (usesFourLibraryCompose(draft)) {
      if (hasReadyViralExecutionPlan(draft)) {
        rewriteDraft();
      } else {
        composeViralDraft({ preventDefault() {}, currentTarget: $("#rewriteBtn") });
      }
      return;
    }

    rewriteDraft();
  });
  // 模板入库没有独立入口：编辑区只保留主按钮（#rewriteBtn），拆解类草稿上它自己会
  // 变成「拆解并入库模板」，避免同一页出现两个同义按钮。中止同理——面板里那个
  // 「停止拆解」与主按钮是同一个动作（都调 cancelTemplateImport），两处并存时既会出现
  // 「中止」/「停止」两种说法，又能绕过主按钮的"只能停自己的任务"归属检查（台账 P100），
  // 已整条移除。
  $("#templateImportRetrySelectedBtn").addEventListener("click", retrySelectedTemplateImportPages);
  $("#cancelPageExpansionReviewBtn")?.addEventListener("click", cancelPageExpansionReview);
  $("#keepCurrentPageCountBtn")?.addEventListener("click", keepCurrentPageCount);
  $("#useSuggestedPageCountBtn")?.addEventListener("click", useSuggestedPageCount);
  $("#templateImportSelectAllFailed").addEventListener("change", (event) => {
    $$('[data-template-retry-page]').forEach((input) => { input.checked = event.target.checked; });
  });
  // 中止/部分失败之后，把已经拆解成功的页面单独入库（不想再重试失败页时的另一条出路）。
  $("#templateImportCommitBtn")?.addEventListener("click", commitPartialTemplateImport);
  $("#polishImageBtn").addEventListener("click", () => toggleDraftAiBlock("image"));
  $("#replicaVisualPrompt")?.addEventListener("input", (event) => {
    const draft = currentDraft();
    if (!isReplicaDraft(draft)) return;
    draft.imageEditPrompts = { cover: event.target.value, inner: event.target.value };
    draft.imagePromptCustomized = { cover: true, inner: true };
    draft.promptSnapshot = { ...(draft.promptSnapshot || {}), imagePrompt: event.target.value };
    saveState();
  });
  $("#editorForm").addEventListener("input", (event) => {
    const visualCopyInput = event.target.closest("[data-visual-copy-field]");
    if (visualCopyInput) {
      updateVisualPageCopy(visualCopyInput);
      return;
    }
    const input = event.target.closest("[data-block-prompt]");
    if (input) updateDraftAiBlockPrompt(input);
  });
  $("#editorForm").addEventListener("click", (event) => {
    const visualCopyAction = event.target.closest("[data-visual-copy-action]");
    if (visualCopyAction) {
      if (visualCopyAction.dataset.visualCopyAction === "unlock") unlockVisualCopyReview();
      else confirmVisualCopyReview();
      return;
    }
    const button = event.target.closest("[data-generate-ai-block]");
    if (!button) return;
    const block = button.dataset.generateAiBlock;
    if (block === "title") improveTitle();
    else if (block === "body") improveBody();
    else if (block === "tags") improveTags();
    else if (block === "cover" || block === "inner" || block === "image") generateVisualRole(block);
  });
  $("#viralDraftPlan").addEventListener("input", (event) => {
    const input = event.target.closest("[data-draft-execution-prompt]");
    if (input) updateDraftExecutionPrompt(input);
  });
  $("#viralDraftPlan").addEventListener("click", (event) => {
    const button = event.target.closest("[data-retry-viral-images]");
    if (button) retryViralDraftImages(button);
  });
  $("#rewriteLibrarySelections").addEventListener("click", (event) => {
    const detailButton = event.target.closest("[data-template-detail-kind]");
    if (detailButton) {
      openRewriteTemplateDetail(detailButton.dataset.templateDetailKind);
      return;
    }
    const libraryButton = event.target.closest("[data-open-library-kind]");
    if (libraryButton) {
      setSection("viral");
      setViralWorkbench(libraryButton.dataset.openLibraryKind);
    }
  });
  $("#closeRewriteTemplateDetailBtn").addEventListener("click", closeRewriteTemplateDetail);
  $("#dismissRewriteTemplateDetailBtn").addEventListener("click", closeRewriteTemplateDetail);
  $("#rewriteTemplateDetailModal").addEventListener("click", (event) => {
    if (event.target.id === "rewriteTemplateDetailModal") closeRewriteTemplateDetail();
  });
  $("#closeInnerPagePickerBtn").addEventListener("click", closeInnerPagePicker);
  $("#cancelInnerPagePickerBtn").addEventListener("click", closeInnerPagePicker);
  $("#confirmInnerPagePickerBtn").addEventListener("click", addSelectedInnerPages);
  $("#innerPagePickerModal").addEventListener("click", (event) => {
    if (event.target.id === "innerPagePickerModal") {
      closeInnerPagePicker();
      return;
    }
    const sourceButton = event.target.closest("[data-image-picker-source]");
    if (sourceButton) selectImagePickerSource(sourceButton.dataset.imagePickerSource);
  });
  $("#innerPagePickerGrid").addEventListener("click", (event) => {
    const item = event.target.closest("[data-inner-page-picker-key]");
    if (item) toggleInnerPagePickerItem(item.dataset.innerPagePickerKey);
  });
  $("#innerPageTemplateLibrary").addEventListener("click", (event) => {
    const item = event.target.closest("[data-image-picker-template-id]");
    if (item) selectInnerPagePickerTemplate(item.dataset.imagePickerTemplateId);
  });
  $("#imagePickerUploadInput").addEventListener("change", (event) => handleImagePickerUpload(event.target.files));
  $("#imagePickerUploadGrid").addEventListener("click", (event) => {
    const removeButton = event.target.closest("[data-remove-image-picker-upload]");
    if (removeButton) removeImagePickerUpload(removeButton.dataset.removeImagePickerUpload);
  });
  $("#singleImageEditorForm").addEventListener("submit", submitSingleImageEdit);
  $("#closeSingleImageEditorBtn").addEventListener("click", closeSingleImageEditor);
  $("#cancelSingleImageEditorBtn").addEventListener("click", closeSingleImageEditor);
  $("#singleImageEditorModal").addEventListener("click", (event) => {
    if (event.target.id === "singleImageEditorModal") closeSingleImageEditor();
  });
  $("#singleImageReferenceInput").addEventListener("change", async (event) => {
    try {
      await appendSingleImageReferences(event.target.files);
    } catch (error) {
      toast(`参考图读取失败：${error.message}`, { kind: "error" });
    } finally {
      event.target.value = "";
    }
  });
  $("#singleImageReferenceList").addEventListener("click", (event) => {
    const button = event.target.closest("[data-remove-single-image-reference]");
    if (button) removeSingleImageReference(Number(button.dataset.removeSingleImageReference));
  });
  $("#rewriteTemplateDetailBody").addEventListener("click", (event) => {
    const previewButton = event.target.closest("[data-library-preview-image]");
    if (!previewButton) return;
    const gallery = previewButton.closest(".selected-template-gallery");
    const pages = gallery ? [...gallery.querySelectorAll("[data-library-preview-image]")] : [previewButton];
    const index = Math.max(0, pages.indexOf(previewButton));
    openImageViewerSet(pages.map((page) => page.dataset.libraryPreviewImage), index, previewButton.dataset.previewTitle || "模板预览");
  });
  $("#rewriteTemplateDetailLibraryBtn").addEventListener("click", (event) => {
    const kind = event.currentTarget.dataset.openLibraryKind || "topic";
    closeRewriteTemplateDetail();
    setSection("viral");
    setViralWorkbench(kind);
  });
  document.addEventListener("keydown", (event) => {
    const modal = $("#rewriteTemplateDetailModal");
    if (event.key === "Escape" && modal && !modal.classList.contains("hidden")) closeRewriteTemplateDetail();
    const innerPicker = $("#innerPagePickerModal");
    if (event.key === "Escape" && innerPicker && !innerPicker.classList.contains("hidden")) closeInnerPagePicker();
    const singleImageEditor = $("#singleImageEditorModal");
    if (event.key === "Escape" && singleImageEditor && !singleImageEditor.classList.contains("hidden")) closeSingleImageEditor();
    const extensionTutorial = $("#extensionTutorialModal");
    if (event.key === "Escape" && extensionTutorial && !extensionTutorial.classList.contains("hidden")) closeExtensionTutorial();
  });
  $("#draftImages").addEventListener("click", (event) => {
    const retryFailedButton = event.target.closest("[data-retry-failed-draft-images]");
    if (retryFailedButton) {
      retryFailedDraftImages(retryFailedButton);
      return;
    }
    if (event.target.closest("[data-add-expansion-page]")) {
      addExpansionReviewPage();
      return;
    }
    const removeExpansionButton = event.target.closest("[data-remove-expansion-page]");
    if (removeExpansionButton) {
      removeExpansionReviewPage(removeExpansionButton.dataset.removeExpansionPage);
      return;
    }
    if (event.target.closest("[data-open-inner-page-picker]")) {
      openInnerPagePicker();
      return;
    }
    const removePageButton = event.target.closest("[data-remove-draft-page]");
    if (removePageButton) {
      removeDraftTemplatePageByKey(removePageButton.dataset.removeDraftPage);
      return;
    }
    const regenerateJobButton = event.target.closest("[data-regenerate-draft-job]");
    if (regenerateJobButton) {
      regenerateDraftImage(-1, regenerateJobButton, regenerateJobButton.dataset.regenerateDraftJob);
      return;
    }
    const regenerateButton = event.target.closest("[data-regenerate-draft-image]");
    if (regenerateButton) {
      regenerateDraftImage(Number(regenerateButton.dataset.regenerateDraftImage), regenerateButton);
      return;
    }
    const generateSingleButton = event.target.closest("[data-generate-single-draft-image]");
    if (generateSingleButton) {
      generateSingleDraftImage(Number(generateSingleButton.dataset.generateSingleDraftImage), generateSingleButton);
      return;
    }
    const editSingleButton = event.target.closest("[data-edit-single-draft-image]");
    if (editSingleButton) {
      openSingleImageEditor(Number(editSingleButton.dataset.editSingleDraftImage));
      return;
    }
    const removeButton = event.target.closest("[data-remove-draft-image]");
    if (removeButton) {
      removeDraftImage(Number(removeButton.dataset.removeDraftImage));
      return;
    }
    if (draftImageDrag.suppressClick) {
      draftImageDrag.suppressClick = false;
      return;
    }
    const button = event.target.closest("[data-draft-preview-image]")
      || event.target.closest("[data-draft-image-target-key]")?.querySelector("[data-draft-preview-image]");
    if (button) {
      const previewButtons = [...event.currentTarget.querySelectorAll("[data-draft-preview-image]")];
      const previewItems = previewButtons.reduce((items, item) => {
        const src = item.dataset.draftPreviewImage;
        if (src && !items.some((entry) => entry.src === src)) {
          items.push({ src, label: item.dataset.draftImageLabel || "", key: item.dataset.draftImageKey || "" });
        }
        return items;
      }, []);
      const images = previewItems.map((item) => item.src);
      const index = images.indexOf(button.dataset.draftPreviewImage);
      openImageViewerSet(images, Math.max(0, index), "图片预览", {
        draftId: currentDraft()?.id || "",
        draftImageLabels: previewItems.map((item) => item.label),
        draftImageKeys: previewItems.map((item) => item.key),
      });
    }
  });
  $("#imageGenerationErrorStatus").addEventListener("click", (event) => {
    const button = event.target.closest("[data-retry-failed-draft-images]");
    if (button) retryFailedDraftImages(button);
  });
  $("#draftImages").addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    const card = event.target.closest("[data-draft-image-target-key]");
    if (!card || !card.dataset.draftImageDragKey || event.target.closest(".draft-image-actions")) return;
    // On phones, swiping the image scrolls; only its grip/label starts sorting.
    if (event.pointerType !== "mouse" && window.matchMedia("(max-width: 640px)").matches
      && card.closest("#section-rewrite.original-mode") && !event.target.closest(".draft-image-card-label")) return;
    const draft = currentDraft();
    // 搭建笔记内容（原创笔记）同样按"开始那一刻的图序"落计划：搭建一开始 executionPlan 被置空，
    // 拖动的失效标记记不上，结束时又会把 brief 里的图序写回草稿——期间拖图只会白拖或让页与图错位。
    // 所以和生成中一样先拒绝，并说明原因。
    const viralBuilding = typeof viralPromptBuildController !== "undefined"
      && Boolean(viralPromptBuildController)
      && typeof viralPromptTaskBelongsToRewriteDraft === "function"
      && viralPromptTaskBelongsToRewriteDraft(draft);
    if (viralBuilding) {
      toast("正在搭建笔记内容，完成后再调整图片顺序");
      return;
    }
    if ((state.rewriteGeneration?.active && String(state.rewriteGeneration.ownerDraftId || "") === String(draft.id || "")) || state.regeneratingDraftImageKey) {
      toast("图片生成中，完成后再调整图片顺序");
      return;
    }
    cleanupDraftImageDrag();
    draftImageDrag.board = event.currentTarget;
    draftImageDrag.card = card;
    draftImageDrag.handle = card;
    draftImageDrag.pointerId = event.pointerId;
    draftImageDrag.startX = event.clientX;
    draftImageDrag.startY = event.clientY;
    draftImageDrag.pressActive = true;
    draftImageDrag.longPressFired = false;
    draftImageDrag.suppressClick = false;
    state.draggedDraftImageKey = card.dataset.draftImageDragKey || "";
    draftImageDrag.longPressTimer = setTimeout(() => {
      if (!draftImageDrag.pressActive || draftImageDrag.active) return;
      draftImageDrag.longPressFired = true;
      draftImageDrag.suppressClick = true;
      draftImageDrag.card?.setPointerCapture?.(draftImageDrag.pointerId);
      activateDraftImagePointerDrag(draftImageDrag.startX, draftImageDrag.startY);
    }, 400);
  });
  $("#draftImages").addEventListener("pointermove", (event) => {
    if (draftImageDrag.pointerId !== event.pointerId || !draftImageDrag.card || !draftImageDrag.pressActive) return;
    if (!draftImageDrag.active) {
      const distance = Math.hypot(event.clientX - draftImageDrag.startX, event.clientY - draftImageDrag.startY);
      if (distance < 6) return;
      clearTimeout(draftImageDrag.longPressTimer);
      draftImageDrag.longPressTimer = null;
      if (event.pointerType !== "mouse") {
        cleanupDraftImageDrag();
        return;
      }
      draftImageDrag.suppressClick = true;
      draftImageDrag.card.setPointerCapture?.(event.pointerId);
      activateDraftImagePointerDrag(event.clientX, event.clientY);
    }
    event.preventDefault();
    updateDraftImagePointerPreview(event.clientX, event.clientY);
    const card = document.elementFromPoint(event.clientX, event.clientY)?.closest("[data-draft-image-target-key]");
    if (card && card !== draftImageDrag.card) {
      const rect = card.getBoundingClientRect();
      const placeAfter = event.clientX >= rect.left + rect.width / 2;
      draftImageDrag.targetKey = card.dataset.draftImageTargetKey || "";
      draftImageDrag.placeAfter = placeAfter;
      positionDraftImageDropSlot(draftImageDrag.board, card, placeAfter);
    }
    const boardRect = draftImageDrag.board.getBoundingClientRect();
    if (event.clientX < boardRect.left + 40) draftImageDrag.board.scrollLeft -= 14;
    else if (event.clientX > boardRect.right - 40) draftImageDrag.board.scrollLeft += 14;
  });
  $("#draftImages").addEventListener("pointerup", (event) => {
    if (draftImageDrag.pointerId !== event.pointerId) return;
    if (draftImageDrag.active && state.draggedDraftImageKey && draftImageDrag.targetKey) {
      event.preventDefault();
      const draft = syncFormToDraft() || currentDraft();
      const moved = moveDraftImage(
        draft,
        state.draggedDraftImageKey,
        draftImageDrag.targetKey,
        draftImageDrag.placeAfter
      );
      cleanupDraftImageDrag();
      if (moved) {
        saveState();
        renderEditor();
        toast("图片顺序已调整，发布时会沿用当前顺序");
      }
      return;
    }
    cleanupDraftImageDrag();
  });
  $("#draftImages").addEventListener("pointercancel", () => {
    cleanupDraftImageDrag();
    draftImageDrag.suppressClick = false;
  });
  $("#draftImages").addEventListener("contextmenu", (event) => {
    if (event.target.closest("[data-draft-image-drag-key]")) event.preventDefault();
  });
  $("#singleImageEditorForm")?.addEventListener("submit", submitSingleImageEdit);
  $("#cancelSingleImageEditorBtn")?.addEventListener("click", () => closeSingleImageEditor());
  $("#singleImageEditorModal")?.addEventListener("click", (event) => {
    if (event.target.id === "singleImageEditorModal") closeSingleImageEditor();
    const remove = event.target.closest("[data-remove-single-image-reference]");
    if (remove) removeSingleImageReference(Number(remove.dataset.removeSingleImageReference));
  });
  $("#singleImageReferenceInput")?.addEventListener("change", async (event) => {
    await appendSingleImageReferences(event.target.files);
    event.target.value = "";
  });
  document.addEventListener("keydown", (event) => {
    const modal = $("#singleImageEditorModal");
    if (event.key === "Escape" && modal && !modal.classList.contains("hidden")) closeSingleImageEditor();
  });
  $$('[data-viral-prompt-pure-ai]').forEach((button) => button.addEventListener("click", retryViralPromptWithPureAi));
  $("#viralCoverTemplateFile").addEventListener("change", importSingleCoverTemplate);
  $("#viralInnerTemplateFile").addEventListener("change", importSingleInnerTemplate);
  $("#imageSceneTemplateFile").addEventListener("change", importSingleSceneTemplate);
  Object.entries(viralLibraryGroups).forEach(([group, config]) => {
    $(`#${config.shelfId}`).addEventListener("click", (event) => {
      if (handleTemplateGalleryEvent(event)) return;
      if (event.target.closest("[data-clear-topic-filters]")) {
        clearTopicPackageFilters();
        return;
      }
      if (event.target.closest("[data-open-local-topic-generator]")) {
        openLocalTopicGenerator();
        return;
      }
      const clearButton = event.target.closest("[data-clear-library-kind]");
      if (clearButton) {
        clearViralLibraryItem(clearButton.dataset.clearLibraryKind);
        return;
      }
      const editVisualButton = event.target.closest("[data-toggle-visual-library-edit]");
      if (editVisualButton) {
        toggleVisualLibraryEdit(editVisualButton.dataset.toggleVisualLibraryEdit);
        return;
      }
      const editTextButton = event.target.closest("[data-toggle-text-library-edit]");
      if (editTextButton) {
        toggleTextLibraryEdit(editTextButton.dataset.toggleTextLibraryEdit);
        return;
      }
      const addTextButton = event.target.closest("[data-add-text-template]");
      if (addTextButton) {
        if (addTextButton.dataset.addTextTemplate === "topic") openTopicPackageEditor(null);
        else openTitleTemplateEditor(null);
        return;
      }
      const addVisualButton = event.target.closest("[data-add-visual-template]");
      if (addVisualButton) {
        openVisualTemplateFilePicker(addVisualButton.dataset.addVisualTemplate);
        return;
      }
      const deleteSelectedButton = event.target.closest("[data-delete-selected-visual-templates]");
      if (deleteSelectedButton) {
        openBatchTemplateDeleteModal(deleteSelectedButton.dataset.deleteSelectedVisualTemplates);
        return;
      }
      const editVisualTemplateButton = event.target.closest("[data-manage-edit-visual-template]");
      if (editVisualTemplateButton) {
        const kind = editVisualTemplateButton.dataset.templateEditScope;
        const item = (state.viralLibraries[kind] || []).find((candidate) => candidate.templateId === editVisualTemplateButton.dataset.manageEditVisualTemplate);
        const editorItem = visualLibraryItemForImageEditor(item, kind);
        if (editorItem) openImageTemplateEditor(editorItem);
        return;
      }
      const deleteVisualButton = event.target.closest("[data-manage-delete-visual-template]");
      if (deleteVisualButton) {
        openTemplateDeleteModal(deleteVisualButton.dataset.manageDeleteVisualTemplate, deleteVisualButton.dataset.templateDeleteScope);
        return;
      }
      const editCard = event.target.closest("[data-toggle-visual-template-selection]");
      if (editCard) {
        toggleVisualTemplateDeleteSelection(group, editCard.dataset.toggleVisualTemplateSelection);
        return;
      }
      const editTopicButton = event.target.closest("[data-edit-topic-package]");
      if (editTopicButton) {
        openTopicPackageEditor((state.viralLibraries.topic || []).find((item) => item.id === editTopicButton.dataset.editTopicPackage));
        return;
      }
      const editTitleButton = event.target.closest("[data-edit-title-template]");
      if (editTitleButton) {
        openTitleTemplateEditor((state.viralLibraries.title || []).find((item) => item.id === editTitleButton.dataset.editTitleTemplate));
        return;
      }
      const archiveTitleButton = event.target.closest("[data-archive-title-template]");
      if (archiveTitleButton) {
        archiveTitleTemplate(archiveTitleButton.dataset.archiveTitleTemplate);
        return;
      }
      const copyTopicButton = event.target.closest("[data-copy-topic-package]");
      if (copyTopicButton) {
        copyTopicPackage(copyTopicButton.dataset.copyTopicPackage);
        return;
      }
      const archiveTopicButton = event.target.closest("[data-archive-topic-package]");
      if (archiveTopicButton) {
        archiveTopicPackage(archiveTopicButton.dataset.archiveTopicPackage);
        return;
      }
      const explicitSelectButton = event.target.closest("button[data-select-library-item][data-library-kind]");
      if (explicitSelectButton) {
        selectViralLibraryItem(explicitSelectButton.dataset.libraryKind, explicitSelectButton.dataset.selectLibraryItem);
        return;
      }
      if (event.target.closest(".topic-card-more, .library-card-details, .visual-template-edit-controls")) return;
      const card = event.target.closest(".library-item-card[data-select-library-item][data-library-kind]");
      if (!card) return;
      selectViralLibraryItem(card.dataset.libraryKind, card.dataset.selectLibraryItem);
    });
    $(`#${config.shelfId}`).addEventListener("input", (event) => {
      if (!event.target.matches("[data-topic-package-search]")) return;
      state.viralTopicQuery = event.target.value;
      filterVisibleTopicPackageCards();
    });
    $(`#${config.shelfId}`).addEventListener("change", (event) => {
      if (event.target.matches("[data-toggle-all-visual-templates]")) {
        toggleAllVisualTemplateDeleteSelections(event.target.dataset.toggleAllVisualTemplates, event.target.checked);
        return;
      }
      if (event.target.matches("[data-topic-package-track]")) {
        state.viralTopicTrack = event.target.value;
        filterVisibleTopicPackageCards();
        return;
      }
      if (event.target.matches("[data-topic-package-format]")) {
        state.viralTopicFormat = event.target.value;
        filterVisibleTopicPackageCards();
      }
    });
    $(`#${config.shelfId}`).addEventListener("keydown", (event) => {
      if (!event.target.matches(".library-item-card[data-select-library-item]")) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectViralLibraryItem(event.target.dataset.libraryKind, event.target.dataset.selectLibraryItem);
    });
  });
  // 四库引导：顶部步骤条与货架标题栏的"下一步"都在 #section-viral 内，走一次事件委托，
  // 免得每个货架各绑一遍（货架是动态重渲染的）。
  $("#section-viral").addEventListener("click", (event) => {
    const routeStep = event.target.closest("[data-viral-route-kind]");
    if (routeStep) {
      setViralWorkbench(routeStep.dataset.viralRouteKind);
      return;
    }
    if (event.target.closest("[data-viral-route-action]")) {
      if (!selectedViralLibraryItem("topic")) {
        toast("先选一个选题，才能搭建笔记内容");
        setViralWorkbench("topic");
        return;
      }
      setSection("rewrite");
      return;
    }
    const nextStep = event.target.closest("[data-viral-next-kind]");
    if (nextStep) {
      setViralWorkbench(nextStep.dataset.viralNextKind);
      return;
    }
    if (event.target.closest("[data-viral-next-action]")) {
      setSection("rewrite");
    }
  });

  $("#viralAtomLibrary").addEventListener("change", (event) => {
    const input = event.target.closest("[data-viral-atom-type]");
    if (!input) return;
    state.viralSelections[input.dataset.viralAtomType] = input.value;
    state.viralAdvancedOverrides[input.dataset.viralAtomType] = input.value;
    renderViralAtomLibrary();
  });
  $("#viralAtomLibrary").addEventListener("click", (event) => {
    const button = event.target.closest("[data-atom-status]");
    if (!button) return;
    event.preventDefault();
    updateViralAtomStatus(button.dataset.atomId, button.dataset.atomStatus);
  });
  $("#viralAtomSearch").addEventListener("input", (event) => {
    state.viralAtomQuery = event.target.value;
    renderViralAtomLibrary();
  });
  $("#viralAtomTypeFilter").addEventListener("change", (event) => {
    state.viralAtomTypeFilter = event.target.value;
    renderViralAtomLibrary();
  });
  $("#previewImages").addEventListener("pointerdown", startPreviewImageDrag);
  $("#previewImages").addEventListener("pointermove", movePreviewImageDrag);
  $("#previewImages").addEventListener("pointerup", finishPreviewImageDrag);
  $("#previewImages").addEventListener("pointercancel", () => {
    previewDrag = null;
  });
  $("#sendPublishBtn").addEventListener("click", sendToPublish);

  $("[data-group=\"count\"]").addEventListener("click", (event) => {
    const button = event.target.closest("button");
    if (!button) return;
    state.selectedCount = Number(button.dataset.value);
    $$('[data-group="count"] button').forEach((node) => node.classList.toggle("active", node === button));
    syncSegmentedIndicator(event.currentTarget, true);
  });

  $("#styleGrid").addEventListener("click", (event) => {
    const addButton = event.target.closest("[data-add-image-template]");
    if (addButton) {
      openImageTemplateAddFlow(addButton.dataset.addImageTemplate);
      return;
    }
    const deleteButton = event.target.closest("[data-delete-image-template]");
    if (deleteButton) {
      openImageStudioTemplateDeleteModal(deleteButton.dataset.deleteImageTemplate);
      return;
    }
    const editButton = event.target.closest("[data-edit-image-template]");
    if (editButton) {
      const item = imageStudioTemplates().find((template) => template.id === editButton.dataset.editImageTemplate);
      if (item) openImageTemplateEditor(item);
      return;
    }
    const editSelectionCard = event.target.closest("[data-toggle-image-template-selection]");
    if (editSelectionCard) {
      setImageTemplateDeleteSelection(state.imageStudioMode, editSelectionCard.dataset.toggleImageTemplateSelection);
      return;
    }
    const card = event.target.closest("[data-image-template-id]");
    if (!card) return;
    selectImageStudioTemplate(card.dataset.imageTemplateId);
  });

  $("#imageTemplateShelfActions").addEventListener("click", (event) => {
    const toggleButton = event.target.closest("[data-toggle-image-template-edit]");
    if (toggleButton) {
      toggleImageTemplateEdit(toggleButton.dataset.toggleImageTemplateEdit || state.imageStudioMode);
      return;
    }
    const addButton = event.target.closest("[data-add-image-template]");
    if (addButton) {
      openImageTemplateAddFlow(addButton.dataset.addImageTemplate);
      return;
    }
    const deleteButton = event.target.closest("[data-delete-selected-image-templates]");
    if (deleteButton) {
      openImageStudioBatchDeleteModal(deleteButton.dataset.deleteSelectedImageTemplates);
    }
  });
  $("#imageTemplateShelfActions").addEventListener("change", (event) => {
    const checkbox = event.target.closest("[data-toggle-all-image-templates]");
    if (checkbox) setAllImageTemplateDeleteSelections(checkbox.dataset.toggleAllImageTemplates, checkbox.checked);
  });

  $$("[data-image-studio-mode]").forEach((button) => {
    button.addEventListener("click", () => setImageStudioMode(button.dataset.imageStudioMode));
  });
  $$("[data-image-generation-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      setImageGenerationMode(button.dataset.imageGenerationMode);
    });
  });
  $("#imageTemplateSelection").addEventListener("click", (event) => {
    if (event.target.closest("[data-clear-image-template]")) clearImageStudioTemplate();
  });
  $("#referenceInput").addEventListener("change", async (event) => {
    try {
      await appendImageReferences(event.target.files, "general");
    } catch (error) {
      toast(`参考图读取失败：${error.message}`);
    } finally {
      event.target.value = "";
    }
  });
  $("#imageReferencePreview").addEventListener("click", (event) => {
    const button = event.target.closest("[data-remove-image-reference]");
    if (!button) return;
    state.imageReferences.splice(Number(button.dataset.removeImageReference), 1);
    closeImageReferenceMentionMenu();
    renderImageStudioSelection();
  });
  $("#replacementReferenceInput").addEventListener("change", async (event) => {
    try {
      await appendImageReferences(event.target.files, "replacement");
    } catch (error) {
      toast(`替换参考图读取失败：${error.message}`);
    } finally {
      event.target.value = "";
    }
  });
  $("#replacementReferencePreview").addEventListener("click", (event) => {
    const button = event.target.closest("[data-remove-replacement-reference]");
    if (!button) return;
    state.replacementReferences.splice(Number(button.dataset.removeReplacementReference), 1);
    closeImageReferenceMentionMenu();
    renderImageStudioSelection();
  });

  bindImageReferenceMentionInputs();
  $("#optimizePromptBtn").addEventListener("click", optimizePrompt);
  $("#generateImageBtn").addEventListener("click", generateImages);
  // 生成按钮的可用性跟着现场字段走：缺什么就灰掉并说明（P69）。
  ["#imageContentInput", "#promptInput", "#replacementTargetInput", "#replacementDescriptionInput"].forEach((selector) => {
    $(selector)?.addEventListener("input", syncGenerateImageButtonState);
  });
  $("#stopImageGenerationBtn").addEventListener("click", stopImageGeneration);
  $("#openSavedImageLibraryBtn").addEventListener("click", () => setSavedImageLibraryOpen(true));
  $("#closeSavedImageLibraryBtn").addEventListener("click", () => setSavedImageLibraryOpen(false));
  $("#closeImageAssignmentBtn").addEventListener("click", closeImageAssignment);
  $("#cancelImageAssignmentBtn").addEventListener("click", closeImageAssignment);
  $("#confirmImageAssignmentBtn").addEventListener("click", confirmImageAssignment);
  $("#imageAssignmentModal").addEventListener("click", (event) => {
    if (event.target.id === "imageAssignmentModal") closeImageAssignment();
    const roleOption = event.target.closest("[data-image-assignment-role]");
    if (roleOption && state.imageAssignment) {
      const role = roleOption.dataset.imageAssignmentRole === "cover" ? "cover" : "inner";
      state.imageAssignment.role = role;
      const input = roleOption.querySelector("input[name=\"imageAssignmentRole\"]");
      if (input) input.checked = true;
      $$('[data-image-assignment-role]').forEach((option) => {
        const selected = option.dataset.imageAssignmentRole === role;
        option.classList.toggle("selected", selected);
        option.setAttribute("aria-checked", String(selected));
      });
      return;
    }
    const workflowButton = event.target.closest("[data-image-assignment-workflow]");
    if (workflowButton && state.imageAssignment) {
      state.imageAssignment.workflow = workflowButton.dataset.imageAssignmentWorkflow;
      state.imageAssignment.draftId = "";
      renderImageAssignmentModal();
      return;
    }
    const taskButton = event.target.closest("[data-image-assignment-draft]");
    if (taskButton && state.imageAssignment) {
      state.imageAssignment.draftId = taskButton.dataset.imageAssignmentDraft;
      renderImageAssignmentModal();
    }
  });
  $("#savedImageLibraryModal").addEventListener("click", (event) => {
    if (event.target.id === "savedImageLibraryModal") setSavedImageLibraryOpen(false);
  });
  const handleGeneratedImageClick = async (event) => {
    const deleteLatestButton = event.target.closest("[data-delete-latest-generated-id]");
    if (deleteLatestButton) {
      const latestImage = state.latestGeneratedImages.find((item) => item.id === deleteLatestButton.dataset.deleteLatestGeneratedId);
      if (!removeLatestGeneratedImage(deleteLatestButton.dataset.deleteLatestGeneratedId)) return;
      renderGeneratedGrid();
      try {
        const result = await postBackend("/api/assets/delete", { url: latestImage?.src || "" });
        toast(result.deleted ? "本次生成图片已删除" : "图片已移除；因仍被任务使用，文件已保留");
      } catch (error) {
        if (latestImage) state.latestGeneratedImages.push(latestImage);
        renderGeneratedGrid();
        toast(`删除图片失败：${error.message}`, { kind: "error" });
      }
      return;
    }
    const downloadButton = event.target.closest("[data-download-generated-id]");
    if (downloadButton) {
      downloadGeneratedImage(
        downloadButton.dataset.downloadGeneratedId,
        Number(downloadButton.dataset.downloadGeneratedIndex) || 0,
      );
      return;
    }
    const saveButton = event.target.closest("[data-save-generated-id]");
    if (saveButton) {
      const status = saveGeneratedImage(saveButton.dataset.saveGeneratedId);
      if (status === "missing") return;
      if (status === "duplicate") {
        toast("这张图片已经保存过了");
        return;
      }
      try {
        await persistGeneratedImage(state.generatedImages.find((item) => item.src === generatedImageById(saveButton.dataset.saveGeneratedId)?.src));
        saveState();
        renderGeneratedGrid();
        toast("图片已保存到独立图片区");
      } catch (error) {
        state.generatedImages = state.generatedImages.filter((item) => item.id !== saveButton.dataset.saveGeneratedId);
        renderGeneratedGrid();
        toast(`保存图片失败：${error.message}`, { kind: "error" });
      }
      return;
    }
    const deleteButton = event.target.closest("[data-delete-generated-id]");
    if (deleteButton) {
      const savedImage = state.generatedImages.find((item) => item.id === deleteButton.dataset.deleteGeneratedId);
      if (!removeGeneratedImage(deleteButton.dataset.deleteGeneratedId)) return;
      try {
        await deleteSavedGeneratedImage(savedImage);
        saveState();
        renderGeneratedGrid();
        toast("图片已从素材库删除");
      } catch (error) {
        if (savedImage) state.generatedImages.unshift(savedImage);
        renderGeneratedGrid();
        toast(`删除图库图片失败：${error.message}`, { kind: "error" });
      }
      return;
    }
    const assignmentButton = event.target.closest("[data-open-generated-assignment]");
    if (assignmentButton) {
      openImageAssignment(assignmentButton.dataset.openGeneratedAssignment);
      return;
    }
    const previewButton = event.target.closest("[data-preview-generated-id]");
    if (previewButton) {
      const collection = previewButton.dataset.generatedScope === "latest" ? state.latestGeneratedImages : state.generatedImages;
      const previewIndex = collection.findIndex((item) => item.id === previewButton.dataset.previewGeneratedId);
      if (previewIndex < 0) return;
      openImageViewerSet(
        collection.map((item) => item.src),
        previewIndex,
        previewButton.dataset.generatedScope === "latest" ? "本次生成" : "已保存图片",
      );
      return;
    }
  };
  $("#latestGeneratedGrid").addEventListener("click", handleGeneratedImageClick);
  $("#generatedGrid").addEventListener("click", handleGeneratedImageClick);
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !$("#imageAssignmentModal").classList.contains("hidden")) {
      closeImageAssignment();
      return;
    }
    if (event.key === "Escape" && !$("#savedImageLibraryModal").classList.contains("hidden")) {
      setSavedImageLibraryOpen(false);
    }
  });

  $("#lowFanCollectionForm").addEventListener("submit", startLowFanCollection);
  $("#generateLowFanSuffixesBtn").addEventListener("click", generateLowFanSuffixes);
  $("#addLowFanSuffixBtn").addEventListener("click", addLowFanSuffix);
  $("#lowFanSuffixInput").addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addLowFanSuffix();
    } else if (event.key === "Escape") {
      setLowFanSuffixComposer(false);
    }
  });
  $("#lowFanSuffixInput").addEventListener("blur", () => window.setTimeout(() => {
    const composer = $(".low-fan-suffix-add");
    if (composer && !composer.contains(document.activeElement)) setLowFanSuffixComposer(false);
  }, 0));
  $("#lowFanSuffixList").addEventListener("click", (event) => {
    const button = event.target.closest("[data-remove-low-fan-suffix]");
    if (!button) return;
    setLowFanSuffixes(state.lowFanCollection.suffixes.filter((suffix) => suffix !== button.dataset.removeLowFanSuffix));
  });
  $("#lowFanResultsBody").addEventListener("click", (event) => {
    const button = event.target.closest("[data-copy-low-fan-url]");
    if (button) copyLowFanNoteUrl(button);
    const replicateButton = event.target.closest("[data-replicate-low-fan]");
    if (replicateButton) replicateLowFanResult(replicateButton.dataset.replicateLowFan, replicateButton);
  });
  $("#lowFanVisualSearchInput")?.addEventListener("change", async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast("请选择图片文件", { kind: "error" });
    try {
      state.lowFanVisualSearch = { src: await readImageAsDataUrl(file), fileName: file.name, analysis: null, busy: false };
      renderLowFanVisualSearch();
    } catch (error) {
      toast(`读取图片失败：${error.message}`, { kind: "error" });
    }
  });
  $("#clearLowFanVisualBtn")?.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    state.lowFanVisualSearch = { src: "", fileName: "", analysis: null, busy: false };
    renderLowFanVisualSearch();
  });
  $("#analyzeLowFanVisualBtn")?.addEventListener("click", analyzeLowFanVisualAndSearch);
  $("#pauseLowFanCollectionBtn").addEventListener("click", pauseLowFanCollection);
  $("#cancelLowFanCollectionBtn").addEventListener("click", cancelLowFanCollection);
  $("#resumeLowFanCollectionBtn").addEventListener("click", resumeLowFanCollection);
  $("#exportLowFanCollectionBtn").addEventListener("click", exportLowFanCollection);
  $("#exportLowFanCollectionBtnMirror")?.addEventListener("click", exportLowFanCollection);
  $("#clearLowFanCollectionBtn")?.addEventListener("click", clearLowFanCollection);
  $$('[data-low-fan-filter]').forEach((button) => button.addEventListener("click", () => {
    state.lowFanCollection.filter = button.dataset.lowFanFilter || "all";
    renderLowFanCollection();
  }));
  $("#simulatePluginNoteBtn")?.addEventListener("click", () => simulatePlugin("note"));
  $("#simulateKeywordBtn")?.addEventListener("click", () => simulatePlugin("keyword"));
  $("#simulateBloggerBtn")?.addEventListener("click", () => simulatePlugin("blogger"));
  $("#exportWorksBtn")?.addEventListener("click", () => void exportWorks());
  // 发布列表的批量能力只有删除：勾选、全选、删除所选；发布仍然是逐条的。
  $("#publishList").addEventListener("change", (event) => {
    const selectBox = event.target.closest("[data-publish-select]");
    if (!selectBox) return;
    setPublishSelection(selectBox.dataset.publishSelect, selectBox.checked, selectBox.closest(".publish-item"));
  });
  $("#publishSelectAll")?.addEventListener("change", (event) => selectAllPublish(event.target.checked));
  $("#bulkDeletePublishBtn")?.addEventListener("click", () => removePublishItems([...publishSelectedIds]));
  // 发布/重试/删除都是这一条的事，逐条按钮是唯一的发布入口。
  // 触发按钮一并传进去，loading 态才落在用户刚点的那颗按钮上。
  $("#publishList").addEventListener("click", (event) => {
    const editButton = event.target.closest("[data-edit-publish]");
    if (editButton) {
      const publishItem = (state.publish || []).find((item) => item.id === editButton.dataset.editPublish);
      const draftId = publishItem?.draftId;
      const draft = draftId ? (state.drafts || []).find((d) => d.id === draftId) : null;
      if (!draft) {
        toast("原草稿已删除，无法返回编辑", { kind: "error", duration: 5000 });
        return;
      }
      // 回到 AI 改写页并把这条草稿设为活跃：发布列表里的内容改动不再回写草稿，
      // 想再改只能在编辑页动手。保持与 #draftList 点击一致的状态切换。
      state.activeWorkflow = draftWorkflow(draft);
      state.previewImageIndex = 0;
      setActiveDraftId(draft.id);
      restoreViralSelectionsFromDraft(draft);
      saveState();
      setSection("rewrite");
      renderEditor();
      return;
    }
    const deleteButton = event.target.closest("[data-delete-publish]");
    if (deleteButton) {
      removePublishItems([deleteButton.dataset.deletePublish]);
      return;
    }
    const publishButton = event.target.closest("[data-publish-xhs]");
    if (publishButton) {
      publishToXiaohongshu([publishButton.dataset.publishXhs], publishButton);
      return;
    }
    const retryButton = event.target.closest("[data-retry-publish]");
    if (retryButton) {
      publishToXiaohongshu([retryButton.dataset.retryPublish], retryButton);
      return;
    }
    const performanceButton = event.target.closest("[data-save-performance]");
    if (performanceButton) savePublishPerformance(performanceButton.dataset.savePerformance);
  });
  $("#closeImageViewerBtn").addEventListener("click", closeImageViewer);
  $("#imageViewerPrevBtn").addEventListener("click", () => moveImageViewer(-1));
  $("#imageViewerNextBtn").addEventListener("click", () => moveImageViewer(1));
  $("#imageViewerRegenerateBtn").addEventListener("click", () => {
    const viewer = state.imageViewer || {};
    const pageKey = viewer.draftImageKeys?.[viewer.index] || viewer.draftImageLabels?.[viewer.index] || "";
    if (pageKey) regenerateDraftImage(-1, $("#imageViewerRegenerateBtn"), pageKey);
  });
  $("#imageViewerZoomOutBtn").addEventListener("click", () => setImageViewerZoom(-0.25));
  $("#imageViewerFitBtn").addEventListener("click", () => setImageViewerZoom(0));
  $("#imageViewerZoomInBtn").addEventListener("click", () => setImageViewerZoom(0.25));
  $("#imageViewerDots").addEventListener("click", (event) => {
    const dot = event.target.closest("[data-image-viewer-dot]");
    if (!dot) return;
    state.imageViewer.index = Number(dot.dataset.imageViewerDot || 0);
    renderImageViewer();
  });
  $("#imageViewerModal").addEventListener("click", (event) => {
    if (event.target.id === "imageViewerModal") closeImageViewer();
  });
  document.addEventListener("keydown", (event) => {
    const modal = $("#imageViewerModal");
    if (!modal || modal.classList.contains("hidden")) return;
    if (event.key === "Escape") closeImageViewer();
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveImageViewer(-1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      moveImageViewer(1);
    }
  });
  $("#cancelTemplateDeleteBtn").addEventListener("click", closeTemplateDeleteModal);
  $("#closeTemplateDeleteBtn").addEventListener("click", closeTemplateDeleteModal);
  $("#deleteTemplatePartBtn").addEventListener("click", () => confirmTemplateDeletion(state.templateDeletion?.scope || ""));
  $("#deleteTemplateSetBtn").addEventListener("click", () => confirmTemplateDeletion(state.templateDeletion?.batch ? state.templateDeletion.scope : "set"));
  $("#templateDeleteModal").addEventListener("click", (event) => {
    if (event.target.id === "templateDeleteModal") closeTemplateDeleteModal();
  });
  $("#confirmTemplateRoleBtn").addEventListener("click", () => {
    const templateKinds = selectedTemplateImportKinds();
    const pageRoles = selectedTemplatePageRoles();
    const selectionError = templateRoleSelectionError(templateKinds, pageRoles);
    if (selectionError) {
      toast(selectionError, { kind: "error", duration: 6500 });
      return;
    }
    closeTemplateRoleDialog({
      pageRoles,
      templateKinds,
    });
  });
  $("#templateRoleModal").addEventListener("click", (event) => {
    const button = event.target.closest("[data-set-all-template-roles]");
    if (button) setAllTemplatePageRoles(button.dataset.setAllTemplateRoles);
  });
  $("#templateRoleModal").addEventListener("change", (event) => {
    if (event.target.matches("[data-template-import-kind]")) syncTemplateVisualImportOptions();
  });
  $("#cancelTemplateRoleBtn").addEventListener("click", () => closeTemplateRoleDialog());
  $("#closeTemplateRoleBtn").addEventListener("click", () => closeTemplateRoleDialog());
  $("#templateRoleModal").addEventListener("click", (event) => {
    if (event.target.id === "templateRoleModal") closeTemplateRoleDialog();
  });
  $("#topicPackageForm").addEventListener("submit", saveTopicPackageEditor);
  $("#closeTopicPackageBtn").addEventListener("click", closeTopicPackageEditor);
  $("#cancelTopicPackageBtn").addEventListener("click", closeTopicPackageEditor);
  $("#topicPackageModal").addEventListener("click", (event) => {
    if (event.target.id === "topicPackageModal") closeTopicPackageEditor();
  });
  $("#autoCompleteTopicPackageBtn")?.addEventListener("click", autoCompleteTopicPackage);
  $("#topicPackageExamples")?.addEventListener("click", (event) => {
    const chip = event.target.closest("[data-topic-example]");
    if (chip) fillTopicExample(chip.dataset.topicExample);
  });
  $("#titleTemplateForm").addEventListener("submit", saveTitleTemplateEditor);
  $("#autoTitleFormulaBtn")?.addEventListener("click", autoGenerateTitleFormula);
  $("#closeTitleTemplateBtn").addEventListener("click", closeTitleTemplateEditor);
  $("#cancelTitleTemplateBtn").addEventListener("click", closeTitleTemplateEditor);
  $("#titleTemplateModal").addEventListener("click", (event) => {
    if (event.target.id === "titleTemplateModal") closeTitleTemplateEditor();
  });
  $("#imageTemplateForm").addEventListener("submit", saveImageTemplateEditor);
  $("#closeImageTemplateBtn").addEventListener("click", closeImageTemplateEditor);
  $("#cancelImageTemplateBtn").addEventListener("click", closeImageTemplateEditor);
  $("#imageTemplateModal").addEventListener("click", (event) => {
    if (event.target.id === "imageTemplateModal") closeImageTemplateEditor();
  });
  $("#closeAiConfigBtn").addEventListener("click", () => closeAiConfig());
  $("#cancelAiConfigBtn").addEventListener("click", () => closeAiConfig());
  $("#aiConfigModal").addEventListener("click", (event) => {
    if (event.target.id === "aiConfigModal") closeAiConfig();
  });
  if (document.documentElement.dataset.aiConfigKeyboardBound !== "1") {
    document.documentElement.dataset.aiConfigKeyboardBound = "1";
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const modal = document.querySelector("#aiConfigModal:not(.hidden)");
      if (!modal) return;
      event.preventDefault();
      closeAiConfig();
    });
  }
  $("#saveAiConfigBtn").addEventListener("click", async () => {
    collectAiConfigForm();
    try {
      await syncAiConfigToBackend({ includeApiKeyChanges: true });
      closeAiConfig({ discardChanges: false });
      renderEditor();
      toast("AI 配置已保存到本地后端");
    } catch (error) {
      toast(`AI 配置保存失败：${error.message}`);
    }
  });
  $$('[data-ai-config-view]').forEach((button) => {
    button.addEventListener("click", () => setAiConfigView(button.dataset.aiConfigView));
  });
  $$('[data-edit-api-key]').forEach((button) => {
    button.addEventListener("click", () => {
      const type = button.dataset.editApiKey;
      const input = $(`#${type}ApiKey`);
      const isEditing = ["editing", "editing-existing"].includes(input.dataset.keyState);
      setApiKeyEditing(type, !isEditing);
    });
  });
  $$('[data-confirm-api-key]').forEach((button) => {
    button.addEventListener("click", () => confirmApiKeyEditing(button.dataset.confirmApiKey));
  });
  $$(".sensitive-key-field").forEach((input) => {
    input.addEventListener("input", () => {
      const type = input.id.replace("ApiKey", "");
      if (input.dataset.keyState === "editing-existing" && input.value !== input.dataset.existingDisplay) {
        input.dataset.keyState = "editing";
        input.type = "password";
        input.title = "正在输入新密钥，保存后仅显示脱敏值";
      }
      const confirmButton = $(`[data-confirm-api-key="${type}"]`);
      if (confirmButton) confirmButton.disabled = input.readOnly;
    });
    ["copy", "cut", "contextmenu", "dragstart"].forEach((eventName) => {
      input.addEventListener(eventName, (event) => event.preventDefault());
    });
    input.addEventListener("keydown", (event) => {
      if ((event.ctrlKey || event.metaKey) && ["c", "x"].includes(event.key.toLowerCase())) {
        event.preventDefault();
      }
      if (event.key === "Enter" && !input.readOnly) {
        const type = input.id.replace("ApiKey", "");
        const confirmButton = $(`[data-confirm-api-key="${type}"]`);
        if (confirmButton && !confirmButton.disabled) {
          event.preventDefault();
          confirmApiKeyEditing(type);
        }
      }
    });
  });
  $("#fetchTextModelsBtn").addEventListener("click", () => fetchAiModels("text"));
  $("#fetchImageModelsBtn").addEventListener("click", () => fetchAiModels("image"));
  $("#testTextModelBtn").addEventListener("click", () => testAiModel("text"));
  $("#testImageModelBtn").addEventListener("click", requestImageModelTest);
  $("#closeImageModelTestConfirmBtn").addEventListener("click", closeImageModelTestConfirm);
  $("#cancelImageModelTestConfirmBtn").addEventListener("click", closeImageModelTestConfirm);
  $("#imageModelTestConfirmModal").addEventListener("click", (event) => {
    if (event.target.id === "imageModelTestConfirmModal") closeImageModelTestConfirm();
  });
  $("#confirmImageModelTestBtn").addEventListener("click", () => {
    closeImageModelTestConfirm();
    testAiModel("image");
  });
  $("#openImageModelTestResultBtn").addEventListener("click", () => {
    // 受保护图片在等待水合期间 src 上是占位图，不能拿它去开大图（P86 同类）。
    const image = $("#imageModelTestImage");
    const imageUrl = image ? protectedImageDisplaySource(image) : "";
    if (imageUrl) openImageViewerSet([imageUrl], 0, "生图 API 测试结果");
  });
  ["text", "image"].forEach((type) => {
    const input = $(`#${type}Model`);
    const trigger = $(`#${type}ModelSuggestionsBtn`);
    const menu = $(`#${type}ModelSuggestionMenu`);
    input.addEventListener("input", () => {
      state.aiConfig[type].model = modelValue(type);
      setModelSuggestionMenuOpen(type, true, { filter: true });
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setModelSuggestionMenuOpen(type, true);
      }
      if (event.key === "Escape") setModelSuggestionMenuOpen(type, false);
    });
    trigger.addEventListener("click", () => {
      const isOpen = menu.dataset.open === "true";
      setModelSuggestionMenuOpen(type, !isOpen, { filter: false });
      if (!isOpen) input.focus();
    });
    menu.addEventListener("click", (event) => {
      const option = event.target.closest("[data-model-suggestion]");
      if (!option) return;
      setModelValue(type, option.dataset.modelSuggestion);
      state.aiConfig[type].model = modelValue(type);
      setModelSuggestionMenuOpen(type, false);
      input.focus();
    });
  });
  document.addEventListener("click", (event) => {
    if (event.target.closest(".model-combobox")) return;
    ["text", "image"].forEach((type) => setModelSuggestionMenuOpen(type, false));
  });

  $("#refreshKnowledgeBtn").addEventListener("click", loadKnowledgeWorkspace);
  $$('[data-reverse-topic-source]').forEach((button) => button.addEventListener("click", () => setReverseTopicSource(button.dataset.reverseTopicSource)));
  $$('[data-reverse-topic-mode]').forEach((button) => button.addEventListener("click", () => setReverseTopicMode(button.dataset.reverseTopicMode)));
  $("#generateReverseTopicBtn").addEventListener("click", () => generateReverseTopic(state.reverseTopicGeneration?.mode));
  $("#clearLocalTopicSourcesBtn").addEventListener("click", () => {
    state.reverseTopicUploadQueue = [];
    state.reverseTopicCloudSelection = [];
    renderReverseTopicSelection();
  });
  $("#addLocalTopicSourceBtn").addEventListener("click", () => $("#localTopicGeneratorUploadInput").click());
  $("#refreshReverseTopicCloudBtn").addEventListener("click", () => loadReverseTopicCloudDocuments());
  $("#localTopicGeneratorUploadInput").addEventListener("change", (event) => {
    addLocalReferenceFiles(event.target.files, "reverse");
    event.target.value = "";
  });
  ["closeLocalTopicGeneratorBtn", "cancelLocalTopicGeneratorBtn"].forEach((id) => $(`#${id}`).addEventListener("click", closeLocalTopicGenerator));
  $("#localTopicGeneratorModal").addEventListener("click", (event) => {
    if (event.target.id === "localTopicGeneratorModal") return closeLocalTopicGenerator();
  });
  $("#addLocalReferenceBtn")?.addEventListener("click", () => $("#localReferenceUploadInput")?.click());
  $("#localReferenceUploadInput")?.addEventListener("change", (event) => {
    addLocalReferenceFiles(event.target.files);
    event.target.value = "";
  });
  $("#knowledgeEvidence")?.addEventListener("input", (event) => {
    const start = event.target.closest("[data-local-page-start]");
    if (start) {
      start.value = updateLocalReferencePageField(start.dataset.localPageStart, Number(start.dataset.localPageIndex || 0), "start", start.value);
      return;
    }
    const end = event.target.closest("[data-local-page-end]");
    if (end) end.value = updateLocalReferencePageField(end.dataset.localPageEnd, Number(end.dataset.localPageIndex || 0), "end", end.value);
  });
  $("#knowledgeEvidence")?.addEventListener("click", (event) => {
    if (event.target.closest("[data-local-upload-dropzone]")) return $("#localReferenceUploadInput")?.click();
    const mode = event.target.closest("[data-local-page-mode]");
    if (mode) {
      updateLocalReferencePageMode(mode.dataset.localUploadId, mode.dataset.localPageMode);
      renderLocalUploadQueue();
      renderKnowledgeEvidence(currentDraft());
      return;
    }
    const addRange = event.target.closest("[data-local-page-add]");
    if (addRange) return addLocalReferencePageRange(addRange.dataset.localPageAdd);
    const removeRange = event.target.closest("[data-local-page-remove]");
    if (removeRange) return removeLocalReferencePageRange(removeRange.dataset.localPageRemove, Number(removeRange.dataset.localPageIndex || 0));
    const remove = event.target.closest("[data-remove-local-upload]");
    if (remove) removeLocalReferenceFile(remove.dataset.removeLocalUpload);
  });
  $("#knowledgeEvidence")?.addEventListener("dragover", (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    event.currentTarget.classList.add("is-dragging");
  });
  $("#knowledgeEvidence")?.addEventListener("dragleave", (event) => {
    if (event.currentTarget === event.target || !event.currentTarget.contains(event.relatedTarget)) event.currentTarget.classList.remove("is-dragging");
  });
  $("#knowledgeEvidence")?.addEventListener("drop", (event) => {
    if (!event.dataTransfer?.files?.length) return;
    event.preventDefault();
    event.currentTarget.classList.remove("is-dragging");
    addLocalReferenceFiles(event.dataTransfer.files);
  });
  $("#localTopicSourceList").addEventListener("click", (event) => {
    if (event.target.closest('[data-local-upload-dropzone="reverse"]')) return $("#localTopicGeneratorUploadInput").click();
    const mode = event.target.closest("[data-local-page-mode]");
    if (mode) {
      updateLocalReferencePageMode(mode.dataset.localUploadId, mode.dataset.localPageMode);
      refreshLocalUploadSurfaces();
      return;
    }
    const addRange = event.target.closest("[data-local-page-add]");
    if (addRange) return addLocalReferencePageRange(addRange.dataset.localPageAdd);
    const removeRange = event.target.closest("[data-local-page-remove]");
    if (removeRange) return removeLocalReferencePageRange(removeRange.dataset.localPageRemove, Number(removeRange.dataset.localPageIndex || 0));
    const removeLocal = event.target.closest("[data-remove-local-upload]");
    if (removeLocal) return removeLocalReferenceFile(removeLocal.dataset.removeLocalUpload);
  });
  $("#localTopicSourceList").addEventListener("input", (event) => {
    const start = event.target.closest("[data-local-page-start]");
    if (start) {
      start.value = updateLocalReferencePageField(start.dataset.localPageStart, Number(start.dataset.localPageIndex || 0), "start", start.value);
      return;
    }
    const end = event.target.closest("[data-local-page-end]");
    if (end) end.value = updateLocalReferencePageField(end.dataset.localPageEnd, Number(end.dataset.localPageIndex || 0), "end", end.value);
  });
  $("#localTopicSourceList").addEventListener("dragover", (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    event.currentTarget.classList.add("is-dragging");
  });
  $("#localTopicSourceList").addEventListener("dragleave", (event) => {
    if (event.currentTarget === event.target || !event.currentTarget.contains(event.relatedTarget)) event.currentTarget.classList.remove("is-dragging");
  });
  $("#localTopicSourceList").addEventListener("drop", (event) => {
    if (!event.dataTransfer?.files?.length) return;
    event.preventDefault();
    event.currentTarget.classList.remove("is-dragging");
    addLocalReferenceFiles(event.dataTransfer.files, "reverse");
  });
  $("#reverseTopicCloudList").addEventListener("change", (event) => {
    const input = event.target.closest("[data-reverse-topic-cloud-id]");
    if (!input) return;
    const id = String(input.dataset.reverseTopicCloudId || "");
    state.reverseTopicCloudSelection = input.checked ? [id] : [];
    renderReverseTopicSelection();
  });
  $("#knowledgeSearchInput").addEventListener("input", debounce(loadKnowledgeWorkspace, 250));
  $("#knowledgeStatusFilter").addEventListener("change", loadKnowledgeWorkspace);
  $("#knowledgeCategoryFilter").addEventListener("change", loadKnowledgeWorkspace);
  $("#knowledgeFolderFilter").addEventListener("change", loadKnowledgeWorkspace);
  $("#reindexAllKnowledgeBtn").addEventListener("click", reindexAllKnowledge);
  $("#cancelReindexAllKnowledgeBtn")?.addEventListener("click", cancelReindexAllKnowledge);
  $("#openKnowledgeUploadBtn").addEventListener("click", openKnowledgeUploadModal);
  $("#knowledgeFileInput").addEventListener("change", (event) => addKnowledgeUploadFiles(event.target.files));
  $("#knowledgeFolderInput").addEventListener("change", (event) => addKnowledgeUploadFiles(event.target.files));
  $("#startKnowledgeUploadBtn").addEventListener("click", () => startKnowledgeBatchUpload());
  $("#clearKnowledgeUploadBtn").addEventListener("click", clearKnowledgeUploadQueue);
  $("#retryKnowledgeUploadBtn").addEventListener("click", retryKnowledgeUploadFailures);
  $("#dismissKnowledgeUploadProgressBtn").addEventListener("click", () => $("#knowledgeUploadProgress").classList.add("hidden"));
  ["closeKnowledgeUploadBtn", "cancelKnowledgeUploadBtn"].forEach((id) => $(`#${id}`).addEventListener("click", () => hideModal($("#knowledgeUploadModal"))));
  $("#knowledgeUploadQueue").addEventListener("click", (event) => {
    const remove = event.target.closest("[data-remove-knowledge-upload]");
    if (!remove) return;
    state.knowledgeUploadQueue = (state.knowledgeUploadQueue || []).filter((item) => item.id !== remove.dataset.removeKnowledgeUpload);
    renderKnowledgeUploadQueue();
  });
  const knowledgeDropzone = $("#knowledgeUploadDropzone");
  ["dragenter", "dragover"].forEach((type) => knowledgeDropzone.addEventListener(type, (event) => {
    event.preventDefault();
    knowledgeDropzone.classList.add("is-dragging");
  }));
  ["dragleave", "drop"].forEach((type) => knowledgeDropzone.addEventListener(type, (event) => {
    event.preventDefault();
    knowledgeDropzone.classList.remove("is-dragging");
  }));
  knowledgeDropzone.addEventListener("drop", (event) => addKnowledgeUploadFiles(event.dataTransfer?.files));
  knowledgeDropzone.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); $("#knowledgeFileInput").click(); }
  });
  $("#newKnowledgeNoteBtn").addEventListener("click", () => openKnowledgeNote());
  $("#newKnowledgeFolderBtn").addEventListener("click", () => openKnowledgeFolderEditor());
  $("#syncKnowledgeFoldersBtn").addEventListener("click", syncKnowledgeDirectories);
  $("#knowledgeFolderForm").addEventListener("submit", saveKnowledgeFolder);
  $("#knowledgeMoveForm").addEventListener("submit", moveKnowledgeDocument);
  ["closeKnowledgeFolderBtn", "cancelKnowledgeFolderBtn"].forEach((id) => $(`#${id}`).addEventListener("click", () => hideModal($("#knowledgeFolderModal"))));
  ["closeKnowledgeMoveBtn", "cancelKnowledgeMoveBtn"].forEach((id) => $(`#${id}`).addEventListener("click", () => hideModal($("#knowledgeMoveModal"))));
  $(".knowledge-folder-panel").addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-toggle-knowledge-folder]");
    if (toggle) return toggleKnowledgeFolder(toggle.dataset.toggleKnowledgeFolder);
    const filter = event.target.closest("[data-knowledge-folder-filter]");
    if (filter) {
      $("#knowledgeFolderFilter").value = filter.dataset.knowledgeFolderFilter || "";
      return loadKnowledgeWorkspace();
    }
    const edit = event.target.closest("[data-edit-knowledge-folder]");
    if (edit) return openKnowledgeFolderEditor((state.knowledgeFolders || []).find((item) => item.id === edit.dataset.editKnowledgeFolder));
    const remove = event.target.closest("[data-delete-knowledge-folder]");
    if (remove) deleteKnowledgeFolder(remove.dataset.deleteKnowledgeFolder);
  });
  $("#knowledgeNoteForm").addEventListener("submit", saveKnowledgeNote);
  ["closeKnowledgeNoteBtn", "cancelKnowledgeNoteBtn"].forEach((id) => $(`#${id}`).addEventListener("click", () => hideModal($("#knowledgeNoteModal"))));
  $("#closeKnowledgeChunksBtn").addEventListener("click", () => hideModal($("#knowledgeChunksModal")));
  $("#knowledgeDocumentRows").addEventListener("click", (event) => {
    const chunks = event.target.closest("[data-knowledge-chunks]");
    if (chunks) return showKnowledgeChunks(chunks.dataset.knowledgeChunks);
    const edit = event.target.closest("[data-edit-knowledge-note]");
    if (edit) return openKnowledgeNote(state.knowledgeDocuments.find((item) => item.id === edit.dataset.editKnowledgeNote));
    const move = event.target.closest("[data-move-knowledge-document]");
    if (move) return openKnowledgeMove(move.dataset.moveKnowledgeDocument, move.dataset.moveKnowledgeFolder || "");
    const reindex = event.target.closest("[data-reindex-knowledge]");
    if (reindex) return reindexKnowledgeDocument(reindex.dataset.reindexKnowledge);
    const remove = event.target.closest("[data-delete-knowledge]");
    if (remove) deleteKnowledgeDocument(remove.dataset.deleteKnowledge);
  });
  $("#runKnowledgeSearchBtn").addEventListener("click", runKnowledgeReviewSearch);
  $("#knowledgeReviewQuery").addEventListener("keydown", (event) => {
    if (event.key === "Enter") { event.preventDefault(); runKnowledgeReviewSearch(); }
  });
  $("#knowledgeReviewResults").addEventListener("change", (event) => {
    const input = event.target.closest("[data-knowledge-select]");
    if (!input) return;
    const id = input.dataset.knowledgeSelect;
    const review = state.knowledgeReview;
    if (input.checked) {
      const item = review.results.find((result) => result.chunkId === id);
      if (item) review.selected[id] = { ...item, overrideText: item.content || "" };
    } else delete review.selected[id];
    renderKnowledgeReviewResults();
  });
  $("#knowledgeReviewResults").addEventListener("input", (event) => {
    const textarea = event.target.closest("[data-knowledge-override]");
    if (textarea && state.knowledgeReview.selected[textarea.dataset.knowledgeOverride]) {
      state.knowledgeReview.selected[textarea.dataset.knowledgeOverride].overrideText = textarea.value;
    }
  });
  $("#confirmKnowledgeBtn").addEventListener("click", () => confirmKnowledgeDecision("grounded"));
  $("#knowledgeFallbackBtn").addEventListener("click", () => confirmKnowledgeDecision("fallback"));
  $("#closeKnowledgeReviewBtn").addEventListener("click", closeKnowledgeReview);
  document.addEventListener("click", async (event) => {
    const evidence = event.target.closest("#knowledgeEvidence");
    if (evidence) {
      const channelToggle = event.target.closest("[data-toggle-knowledge-channel]");
      if (channelToggle) return toggleKnowledgeChannel();
      const toggle = event.target.closest("[data-toggle-knowledge-evidence]");
      if (toggle) {
        state.knowledgeEvidenceCollapsed = toggle.getAttribute("aria-expanded") === "true";
        renderKnowledgeEvidence(currentDraft());
        return;
      }
      const sourceButton = event.target.closest("[data-knowledge-source]");
      if (sourceButton) return setKnowledgeSource(sourceButton.dataset.knowledgeSource);
      if (event.target.closest("[data-run-knowledge-search-inline]")) return runKnowledgeReviewSearch();
      if (event.target.closest("[data-confirm-knowledge-inline]")) return confirmKnowledgeDecision("grounded");
      if (event.target.closest("[data-leave-cloud-knowledge]")) return leaveCloudKnowledgeSource();
      if (event.target.closest("[data-knowledge-evidence-add-file]")) return $("#localReferenceUploadInput")?.click();
      if (event.target.closest("[data-confirm-evidence-local]")) return confirmEvidenceLocalFiles();
    }
    if (event.target.closest("[data-review-knowledge]")) openKnowledgeReview(currentDraft());
    if (event.target.closest("[data-open-knowledge-config]")) {
      // 未登录时"你没有权限配置"是假的：他不是权限不够，是没登录。先请登录，别把人送去
      // 问管理员（见 auth/login-gate.js）。
      if (!(await requireSignedInForAction("云端知识库配置"))) return;
      // 云端知识库是**组织级**配置，入口在「团队治理 → 知识库连接」，和成员管理同一批人。
      // 普通成员拿不到配置面板，直接说清该谁做。
      if (typeof canConfigureOrgAi === "function" && canConfigureOrgAi()) {
        openOrgKnowledge();
        return;
      }
      toast("云端知识库由组织管理员配置，请联系管理员开通", { duration: 5200 });
    }
  });
}


// ----- js/rewrite/original-mobile.js -----
/* Mobile-only arrangement; move existing nodes so editors and actions retain state. */
(() => {
  const section = document.getElementById("section-rewrite");
  const layout = section?.querySelector(".rewrite-layout");
  if (!layout) return;
  const media = window.matchMedia("(max-width: 640px)");
  const editor = layout.querySelector(".editor-panel");
  const preview = layout.querySelector(".preview-panel");
  const composition = document.getElementById("rewriteLibrarySelections");
  const actions = layout.querySelector(".draft-rail-end-actions");
  const images = document.getElementById("previewImages");
  const compositionHome = document.createComment("original composition position");
  const actionsHome = document.createComment("original task actions position");
  composition.before(compositionHome);
  actions.before(actionsHome);

  const views = document.createElement("div");
  views.className = "original-mobile-views";
  views.setAttribute("role", "group");
  views.setAttribute("aria-label", "原创笔记视图");
  views.innerHTML = '<button type="button" data-mobile-view="edit" aria-pressed="true">编辑</button><button type="button" data-mobile-view="preview" aria-pressed="false">预览</button>';
  const more = document.createElement("details");
  more.className = "original-mobile-more";
  more.innerHTML = '<summary aria-label="更多任务操作">更多</summary>';
  let active = false;
  let view = "edit";
  let frame = 0;

  function fitPreviewImage() {
    if (!active) return;
    const index = Number(images.style.getPropertyValue("--preview-index")) || 0;
    const image = images.querySelectorAll("img")[index];
    const ratio = image?.naturalWidth && image?.naturalHeight
      ? `${image.naturalWidth} / ${image.naturalHeight}` : "1 / 1";
    if (preview.style.getPropertyValue("--mobile-image-ratio") !== ratio) {
      preview.style.setProperty("--mobile-image-ratio", ratio);
    }
  }

  function refreshVisibleContent() {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      if (typeof resizeRewriteContentFields === "function") resizeRewriteContentFields();
      fitPreviewImage();
    });
  }

  function selectView(next) {
    view = next;
    section.dataset.mobileView = view;
    views.querySelectorAll("button").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.mobileView === view));
    });
    more.open = false;
    refreshVisibleContent();
  }

  function syncMobileLayout() {
    const enabled = media.matches && section.classList.contains("original-mode");
    if (enabled !== active) {
      active = enabled;
      if (active) {
        editor.before(views, composition);
        actionsHome.after(more);
        more.append(actions);
        selectView("edit");
      } else {
        compositionHome.after(composition);
        actionsHome.after(actions);
        more.remove();
        views.remove();
        delete section.dataset.mobileView;
        preview.style.removeProperty("--mobile-image-ratio");
      }
    }
    if (section.classList.contains("active")) refreshVisibleContent();
  }

  views.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-mobile-view]");
    if (button) selectView(button.dataset.mobileView);
  });
  more.addEventListener("click", (event) => {
    if (event.target.closest("button")) more.open = false;
  });
  document.addEventListener("click", (event) => {
    if (more.open && !more.contains(event.target)) more.open = false;
  });
  more.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      more.open = false;
      more.querySelector("summary").focus();
    }
  });
  media.addEventListener("change", syncMobileLayout);
  window.addEventListener("resize", () => {
    if (active) refreshVisibleContent();
  });
  new MutationObserver(syncMobileLayout).observe(section, { attributes: true, attributeFilter: ["class"] });
  new MutationObserver(fitPreviewImage).observe(images, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "src"] });
  images.addEventListener("load", fitPreviewImage, true);
  syncMobileLayout();
})();

// ----- js/shell/boot.js -----
// 插件检测放在最前面，而且不挂在登录链上：它不需要登录、不发任何网络请求，只是问一句
// 已经注入本页的桥接脚本（见 core/extension-status.js）。
// 以前它挂在登录链的末端：登录落定 → 切租户 → /api/config → /api/state → 计费 →
// /api/extension/package → 再探测。本地这些往返是亚毫秒级，看不出问题；服务端每一个都是
// 一次真实往返（实测 fs03 单个接口 0.19~0.52s，还要加上 Logto 换令牌与前几次水合），
// 于是首页那行「检测中…」要挂到二十几秒后才出结果。探测本身从来没慢过——它只是被排在了
// 整条链的最后。
void detectCaptureExtension();
// 切走很久再回来时重问一次（不弹提示），别让状态行拿几小时前的结论替插件背书。
armExtensionDetectionRefresh();
loadState();
loadAiConfig();
bindEvents();
bindSpaAuthUi();
bindAdminConsolePager();
bindAdminConsoleEditor();
bindAdminConsoleExport();
bindExtensionDownload();
armCloseGuard();
window.addEventListener("pagehide", flushStateOnPageHide, { capture: true });
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") flushStateOnPageHide();
});
restoreLowFanCollection();
renderAll();
startProtectedImageHydration();
// 全站图片加载态：<img> 解码完成前显示骨架屏、失败时显示文案，替代浏览器自带的破图占位。
startImageStates();
loadBillingStatus();
const requestedSection = new URLSearchParams(window.location.search).get("section");
setSection(requestedSection && workspaceSections[requestedSection] ? requestedSection : "home");
window.requestAnimationFrame(() => {
  syncSegmentedIndicators(document, false);
  syncPreviewScale();
});
window.lucide?.createIcons({ attrs: { "stroke-width": 1.8 } });
// 租户读接口必须等登录闸门有结论：游客确认未登录就跳过，避免 /api/config|/state|/templates|/atoms
// 连打 401；登录落定后由 syncAuthUi 再拉一轮（见 auth/session.js）。
void (async () => {
  const gate = typeof whenLoginGateReady === "function"
    ? await whenLoginGateReady()
    : { active: false, signedIn: true };
  if (gate.active && gate.known && !gate.signedIn) return;
  loadAiConfigFromBackend();
  mergeSavedImageLibraryFromBackend();
  loadLayoutTemplates();
  loadViralAtoms();
})();
consumeExtensionHandoff();

if (typeof acceptOrganizationInvitation === "function") {
  globalThis.acceptOrganizationInvitation = acceptOrganizationInvitation;
}
